import React, { useState, useEffect, useCallback, useRef } from "react";
import ReportingDashboard from './ReportingDashboard';
import { flushSync } from "react-dom";

const APP_VERSION = "3.9.163";
const APP_BUILD = "2026.06.20";

// Sanitize Jira score fields - old localStorage data may store objects instead of primitives
function sanitizeScoreField(v) {
  if (v == null) return null;
  if (typeof v === "object") return String(v.value ?? v.name ?? "");
  return v;
}
function sanitizeTasks(tasks) {
  if (!Array.isArray(tasks)) return [];
  return tasks.map(t => ({
    ...t,
    jiraEffort: sanitizeScoreField(t.jiraEffort),
    jiraValue:  sanitizeScoreField(t.jiraValue),
    jiraScore:  sanitizeScoreField(t.jiraScore),
  }));
}

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #1e2b33;
    --surface: #263540;
    --surface2: #2e3f4d;
    --border: #3a5060;
    --green: #47ac4c;
    --red: #e32529;
    --amber: #F1901E;
    --grey-dark: #36454f;
    --grey-light: #D3D5D7;
    --text: #f0f4f7;
    --text-muted: #8fa8b8;
    --text-dim: #4a6070;
    --outlook: #0078d4;
    --jira: #0052cc;
  }

  /* ── Light mode overrides ── */
  .light-mode {
    --bg: #f0f4f7;
    --surface: #ffffff;
    --surface2: #e8edf2;
    --border: #c8d6e0;
    --text: #1e2b33;
    --text-muted: #4a6070;
    --text-dim: #8fa8b8;
    --grey-dark: #c8d6e0;
    --grey-light: #4a6070;
  }
  .light-mode body { background: var(--bg); color: var(--text); }
  .light-mode .task-card { box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
  .light-mode .modal { box-shadow: 0 8px 32px rgba(0,0,0,0.15); }
  .light-mode .detail-panel { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
  .light-mode .nav-item:hover { background: var(--surface2); }
  .light-mode .search-box { background: #fff; }
  .light-mode .form-input { background: #fff; color: #1a1a1a; }
  .light-mode .form-select { background: #fff; color: #1a1a1a; }
  .light-mode .notes-area { background: #fff; color: #1a1a1a; }
  .light-mode .badge.outlook { background: #e8f4fd; }
  .light-mode .badge.jira { background: #e8f0fd; }
  .light-mode .kanban-card { box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
  .light-mode .impl-sidebar { border-right-color: var(--border); }
  .light-mode .impl-project-item { border-color: var(--border); }
  .light-mode .impl-project-item:hover { background: var(--surface2); }
  .light-mode .gantt-header { background: var(--surface2); }

  body { background: var(--bg); color: var(--text); font-family: 'Inter', sans-serif; min-height: 100vh; }

  /* ── New shell layout: topbar + sidebar + content ── */
  .app { display: flex; flex-direction: column; min-height: 100vh; width: 100%; }

  /* ── Top bar ── */
  .header { display: flex; align-items: center; gap: 10px; padding: 0 16px; height: 48px; background: var(--surface); border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .header-title { font-family: 'Poppins', sans-serif; font-weight: 700; font-size: 16px; letter-spacing: -0.3px; color: var(--text); white-space: nowrap; display: flex; align-items: center; gap: 8px; }
  .header-title .brand-c365 { color: var(--green); }
  .header-title .brand-sub { font-size: 12px; font-weight: 400; color: var(--text-muted); letter-spacing: 0.5px; text-transform: uppercase; }
  .header-spacer { flex: 1; }
  .header-badges { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .badge { display: flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; border: 1px solid; cursor: pointer; transition: all 0.15s; font-family: 'Inter', sans-serif; }
  .badge:hover { opacity: 0.85; }
  .badge.outlook { color: var(--outlook); border-color: var(--outlook); background: #0078d415; }
  .badge.jira { color: #4c9aff; border-color: #4c9aff; background: #0052cc15; }
  .badge.disconnected { opacity: 0.4; }
  .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .dot.pulse { animation: pulse 2s infinite; }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

  /* ── Body below topbar: sidebar + content ── */
  .main { display: flex; flex: 1; min-height: 0; overflow: hidden; transition: padding-right 0.2s ease; }
  .main.detail-open { padding-right: 396px; }

  /* ── Mobile responsive layout ── */
  @media (max-width: 900px) {
    .sidebar { display: none; width: 210px; }
    .sidebar.mobile-open { display: flex; position: fixed; inset: 0; z-index: 200; background: var(--surface); width: 260px; padding-top: 60px; overflow-y: auto; box-shadow: 4px 0 20px rgba(0,0,0,0.4); }
    .detail-panel { position: fixed !important; inset: 0 !important; width: 100% !important; z-index: 200; border-radius: 0; box-shadow: none !important; }
    .main.detail-open { padding-right: 0 !important; }
    .mobile-overlay { position: fixed; inset: 0; background: #00000060; z-index: 199; }
    .header { flex-wrap: wrap; gap: 6px; padding: 0 10px; }
    .header-badges { gap: 4px; }
    .badge { font-size: 10px; padding: 3px 7px; }
    .task-card { padding: 10px 12px; }
    .task-title { font-size: 13px; }
    .search-box { font-size: 14px; }
    .nav-item { padding: 10px 12px; font-size: 14px; }
    .nav-sub-item { padding: 9px 12px 9px 28px; font-size: 13px; }
    .btn { padding: 7px 12px; font-size: 12px; }
    .toolbar { flex-wrap: wrap; }
    .kanban-board { grid-template-columns: 1fr 1fr; }
    .popout-modal { border-radius: 0; max-height: 100vh; height: 100vh; }
  }
  @media (max-width: 600px) {
    .kanban-board { grid-template-columns: 1fr; }
    .header-badges .badge:not(.outlook):not(.jira) { display: none; }
  }

  /* Mobile nav toggle button */
  .mobile-nav-btn { display: none; background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 5px 10px; color: var(--text); font-size: 18px; cursor: pointer; }
  @media (max-width: 900px) { .mobile-nav-btn { display: flex; align-items: center; } }
  .mobile-back-btn { display: none; background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 8px 14px; color: var(--text); font-size: 13px; cursor: pointer; margin-bottom: 8px; }
  @media (max-width: 900px) { .mobile-back-btn { display: block; } }
  .sidebar.mobile-open .mobile-close-sidebar { display: block; }
  .mobile-close-sidebar { display: none; position: absolute; top: 14px; right: 14px; background: none; border: 1px solid var(--border); border-radius: 4px; padding: 4px 10px; color: var(--text-muted); cursor: pointer; font-size: 16px; }
  .main.full-width { padding-left: 0; }
  .main.detail-open.full-width { padding-right: 0; }

  /* ── Content area ── */
  .task-panel { flex: 1; display: flex; flex-direction: column; gap: 14px; min-width: 0; overflow-y: auto; padding: 12px 16px; background: var(--bg); }

  .sidebar { display: flex; flex-direction: column; width: 210px; flex-shrink: 0; background: var(--surface); border-right: 1px solid var(--border); overflow-y: auto; overflow-x: hidden; }
  .sidebar-section { font-size: 10px; font-weight: 600; letter-spacing: 1.2px; text-transform: uppercase; color: var(--text-dim); padding: 12px 12px 4px; }
  .nav-item { display: flex; align-items: center; gap: 8px; padding: 7px 12px; cursor: pointer; font-size: 13px; color: var(--text-muted); transition: all 0.15s; border-left: 2px solid transparent; margin: 1px 0; }
  .nav-item:hover { background: var(--surface2); color: var(--text); }
  .nav-item.active { background: var(--surface2); color: var(--green); border-left-color: var(--green); }
  .nav-item .count { margin-left: auto; font-size: 10px; background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 1px 7px; color: var(--text-muted); min-width: 18px; text-align: center; }
  .nav-item.active .count { border-color: var(--green); color: var(--green); background: rgba(71,172,76,0.1); }
  .nav-icon { font-size: 13px; width: 16px; text-align: center; flex-shrink: 0; }
  .nav-collapse-btn { background: none; border: none; color: var(--text-dim); cursor: pointer; font-size: 11px; padding: 0 2px; margin-left: 2px; transition: color 0.15s; flex-shrink: 0; }
  .nav-collapse-btn:hover { color: var(--green); }
  .nav-manual-toggle { display: flex; align-items: center; gap: 6px; padding: 6px 10px; font-size: 10px; color: var(--text-muted); cursor: pointer; border-radius: 4px; transition: all 0.15s; }
  .nav-manual-toggle:hover { color: var(--text); }
  .nav-manual-toggle input { accent-color: var(--green); cursor: pointer; }

  /* task-panel defined above with layout rules */
  .toolbar { display: flex; gap: 10px; align-items: center; }
  .search-box { flex: 1; background: var(--surface); border: 1px solid var(--border); border-radius: 5px; padding: 9px 14px; color: var(--text); font-family: 'Inter', sans-serif; font-size: 13px; outline: none; transition: border-color 0.15s; }
  .search-box:focus { border-color: var(--green); }
  .search-box::placeholder { color: var(--text-dim); }

  .btn { padding: 8px 16px; border-radius: 5px; border: 1px solid var(--border); background: var(--surface); color: var(--text); font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
  .btn:hover { border-color: var(--green); color: var(--green); }
  .btn.primary { background: var(--green); border-color: var(--green); color: #fff; }
  .btn.primary:hover { background: #3d9442; }
  .btn.danger { background: var(--red); border-color: var(--red); color: #fff; }

  .task-list { display: flex; flex-direction: column; gap: 6px; }
  .task-card { background: var(--surface); border: 1px solid var(--border); border-radius: 6px; padding: 12px 16px; cursor: pointer; transition: all 0.15s; position: relative; overflow: hidden; }
  .task-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--accent-source, var(--border)); }
  .task-card:hover { border-color: var(--green)60; background: var(--surface2); }
  .task-card.selected { border-color: var(--green); background: var(--surface2); }
  .task-card.done { opacity: 0.4; }
  .task-card-row { display: flex; align-items: flex-start; gap: 10px; }
  .task-check { width: 16px; height: 16px; border-radius: 3px; border: 1.5px solid var(--text-dim); flex-shrink: 0; margin-top: 2px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
  .task-check:hover { border-color: var(--green); }
  .task-check.checked { background: var(--green); border-color: var(--green); color: #fff; font-size: 10px; }
  .task-body { flex: 1; min-width: 0; }
  .task-title { font-size: 13px; line-height: 1.4; color: var(--text); margin-bottom: 6px; font-weight: 500; }
  .task-title.done { text-decoration: line-through; color: var(--text-muted); }
  .task-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

  .tag { font-size: 10px; padding: 2px 7px; border-radius: 3px; letter-spacing: 0.3px; border: 1px solid; font-weight: 600; font-family: 'Inter', sans-serif; }
  .tag.outlook { color: #47ac4c; border-color: #47ac4c50; background: #47ac4c15; }
  .tag.jira { color: #F1901E; border-color: #F1901E50; background: #F1901E15; }
  .tag.manual { color: #D3D5D7; border-color: #D3D5D760; background: #D3D5D715; }
  .tag.priority-high { color: var(--red); border-color: #e3252930; background: #e3252908; }
  .tag.priority-medium { color: var(--amber); border-color: #F1901E30; background: #F1901E08; }
  .tag.priority-low { color: var(--green); border-color: #47ac4c30; background: #47ac4c08; }
  .tag.status { color: var(--text-muted); border-color: var(--border); }
  .tag.due-tag { color: #47ac4c; border-color: #47ac4c50; background: #47ac4c15; }
  .tag.due-today { color: #F1901E; border-color: #F1901E50; background: #F1901E15; }
  .tag.overdue-tag { color: #e32529; border-color: #e3252950; background: #e3252915; font-weight: 700; }

  /* ── Detail Panel ── */
  .detail-panel { background: var(--surface); border: 1px solid var(--border); border-left: 1px solid var(--border); border-radius: 0; padding: 20px; display: flex; flex-direction: column; gap: 14px; position: fixed; top: 0; right: 0; width: 380px; height: 100vh; overflow-y: auto; z-index: 50; box-shadow: -4px 0 20px rgba(0,0,0,0.3); transition: transform 0.2s ease; }
  .detail-panel.empty { align-items: center; justify-content: center; min-height: 200px; color: var(--text-dim); font-size: 12px; text-align: center; }
  .detail-section-label { font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 5px; font-weight: 600; }
  .detail-title { font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 600; line-height: 1.4; color: var(--text); }
  .detail-field { display: flex; flex-direction: column; gap: 4px; }
  .detail-row-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 600px) { .detail-row-2col { grid-template-columns: 1fr; } }
  .detail-value { font-size: 12px; color: var(--text); background: var(--surface2); border: 1px solid var(--border); border-radius: 4px; padding: 6px 10px; }
  .detail-desc { font-size: 12px; color: var(--text-muted); line-height: 1.6; background: var(--surface2); border: 1px solid var(--border); border-radius: 4px; padding: 8px 10px; min-height: 60px; white-space: pre-wrap; word-break: break-word; overflow-wrap: break-word; }
  .detail-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .detail-actions .btn { flex: 1; text-align: center; font-size: 11px; }

  .notes-area { width: 100%; background: var(--surface2); border: 1px solid var(--border); border-radius: 4px; padding: 8px 10px; color: var(--text); font-family: 'Inter', sans-serif; font-size: 12px; resize: vertical; min-height: 80px; outline: none; transition: border-color 0.15s; }
  .notes-area:focus { border-color: var(--green); }
  .notes-area::placeholder { color: var(--text-dim); }
  .notes-save-row { display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px; }

  /* ── Config Modal ── */
  .overlay { position: fixed; inset: 0; background: #00000099; backdrop-filter: blur(4px); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .modal { background: var(--surface); border: 1px solid var(--border); border-top: 3px solid var(--green); border-radius: 8px; padding: 28px; width: 100%; max-width: 520px; display: flex; flex-direction: column; gap: 20px; animation: slideUp 0.2s ease; }
  @keyframes slideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  .modal-title { font-family: 'Poppins', sans-serif; font-size: 18px; font-weight: 700; }
  .modal-section { display: flex; flex-direction: column; gap: 10px; }
  .modal-section-title { font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); font-weight: 600; display: flex; align-items: center; gap: 8px; }
  .modal-section-title::after { content: ''; flex: 1; height: 1px; background: var(--border); }
  .form-row { display: flex; flex-direction: column; gap: 4px; }
  .form-label { font-size: 11px; color: var(--text-muted); font-weight: 500; }
  .form-input { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 8px 12px; color: var(--text); font-family: 'Inter', sans-serif; font-size: 12px; outline: none; transition: border-color 0.15s; width: 100%; }
  input[type="date"] { background: #ffffff; color: #1a1a1a; border: 1px solid var(--border); border-radius: 5px; padding: 6px 10px; font-family: 'Inter', sans-serif; font-size: 12px; outline: none; cursor: pointer; }
  input[type="date"]:focus { border-color: var(--green); }
  input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; }
  .form-input:focus { border-color: var(--green); }
  .form-input::placeholder { color: var(--text-dim); }
  .modal-footer { display: flex; gap: 10px; justify-content: flex-end; }

  .new-task-panel { background: var(--surface); border: 1px solid var(--green)50; border-radius: 6px; padding: 16px; display: flex; flex-direction: column; gap: 12px; animation: slideUp 0.2s ease; }
  .new-task-row { display: flex; gap: 10px; }
  .form-select { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 8px 10px; color: var(--text); font-family: 'Inter', sans-serif; font-size: 12px; outline: none; cursor: pointer; }

  .empty-state { text-align: center; padding: 48px 20px; color: var(--text-dim); font-size: 13px; line-height: 1.8; }

  /* ── Sub-filter nav items ── */
  .nav-sub { display: flex; flex-direction: column; margin: 0 0 2px 0; }
  .nav-sub-item { display: flex; align-items: center; gap: 8px; padding: 5px 12px 5px 28px; cursor: pointer; font-size: 11px; color: var(--text-muted); transition: all 0.15s; border-left: 2px solid transparent; }
  .nav-sub-item:hover { background: var(--surface2); color: var(--text); }
  .nav-sub-item.active { background: var(--surface2); color: var(--green); border-left-color: var(--green); }
  .nav-sub-item .count { margin-left: auto; font-size: 10px; background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 1px 6px; color: var(--text-muted); }

  /* ── View toggle ── */
  .view-toggle { display: flex; gap: 4px; }
  .view-btn { padding: 6px 10px; border-radius: 4px; border: 1px solid var(--border); background: var(--surface); color: var(--text-muted); font-size: 11px; cursor: pointer; transition: all 0.15s; }
  .view-btn:hover { border-color: var(--green); color: var(--green); }
  .view-btn.active { background: var(--green); border-color: var(--green); color: #fff; }

  /* ── Kanban board ── */
  .kanban-board { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; align-items: start; }
  .kanban-col { background: var(--surface); border: 1px solid var(--border); border-radius: 6px; display: flex; flex-direction: column; min-height: 200px; }
  .kanban-col-header { padding: 10px 12px; border-bottom: 1px solid var(--border); font-size: 11px; font-weight: 600; letter-spacing: 0.8px; text-transform: uppercase; display: flex; align-items: center; justify-content: space-between; }
  .kanban-col-header .col-count { font-size: 10px; background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 1px 7px; color: var(--text-muted); }
  .kanban-cards { display: flex; flex-direction: column; gap: 6px; padding: 8px; }
  .kanban-card { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 10px 12px; cursor: pointer; transition: all 0.15s; font-size: 12px; }
  .kanban-card:hover { border-color: var(--green)60; }
  .kanban-card.selected { border-color: var(--green); }
  .kanban-card-title { font-weight: 500; line-height: 1.4; margin-bottom: 6px; color: var(--text); }
  .kanban-card-meta { display: flex; gap: 4px; flex-wrap: wrap; }
  .kanban-col-to-start .kanban-col-header { color: var(--text-muted); border-top: 3px solid var(--text-dim); border-radius: 6px 6px 0 0; }
  .kanban-col-in-progress .kanban-col-header { color: #4c9aff; border-top: 3px solid #4c9aff; border-radius: 6px 6px 0 0; }
  .kanban-col-waiting .kanban-col-header { color: var(--amber); border-top: 3px solid var(--amber); border-radius: 6px 6px 0 0; }
  .kanban-col-done .kanban-col-header { color: var(--green); border-top: 3px solid var(--green); border-radius: 6px 6px 0 0; }
  .empty-state .icon { font-size: 36px; margin-bottom: 12px; opacity: 0.3; }

  /* ── Implementations & Projects ── */
  .impl-layout { display: flex; gap: 0; min-height: 0; width: 100%; align-items: stretch; }
  .impl-sidebar { width: 200px; flex-shrink: 0; border-right: 1px solid var(--border); display: flex; flex-direction: column; gap: 2px; padding: 8px 0; overflow-y: auto; }
  .impl-sidebar-title { font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 8px 12px 4px; }
  .impl-project-item { display: flex; flex-direction: column; gap: 2px; padding: 8px 12px; cursor: pointer; border-radius: 4px; margin: 0 4px; transition: all 0.15s; border: 1px solid transparent; }
  .impl-project-item:hover { background: var(--surface); }
  .impl-project-item.active { background: var(--surface2); border-color: var(--green)30; }
  .impl-project-name { font-size: 12px; font-weight: 600; color: var(--text); }
  .impl-project-meta { font-size: 10px; color: var(--text-muted); }
  .impl-progress-bar { height: 4px; background: var(--border); border-radius: 2px; overflow: hidden; margin-top: 4px; }
  .impl-progress-fill { height: 100%; background: var(--green); border-radius: 2px; transition: width 0.3s; }
  .impl-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0; overflow-y: auto; overflow-x: auto; }
  .impl-header { padding: 8px 20px 0; border-bottom: 1px solid var(--border); display: flex; flex-direction: column; gap: 4px; }
  .impl-header-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .impl-title { font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 700; color: var(--text); }
  .impl-tabs { display: flex; gap: 0; align-items: center; justify-content: space-between; }
  .impl-tabs-left { display: flex; gap: 0; }
  .impl-tabs-right { display: flex; gap: 6px; align-items: center; padding-bottom: 4px; }
  .impl-tab { padding: 6px 14px; font-size: 12px; font-weight: 500; cursor: pointer; border-bottom: 2px solid transparent; color: var(--text-muted); transition: all 0.15s; display: inline-flex; align-items: center; gap: 4px; line-height: 1; }
  .impl-tab:hover { color: var(--text); }
  .impl-tab.active { color: var(--green); border-bottom-color: var(--green); }
  .impl-body { flex: 1; overflow-y: auto; overflow-x: auto; padding: 0; display: flex; flex-direction: column; gap: 0; width: 100%; box-sizing: border-box; }
  .impl-section { display: flex; flex-direction: column; gap: 6px; width: 100%; }
  .impl-section-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .impl-section-title { font-size: 11px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: var(--text-muted); }
  .impl-task-row { display: grid; grid-template-columns: 3fr 90px 110px 90px 28px 90px 90px 2fr 100px; gap: 8px; align-items: start; padding: 10px 12px; background: var(--surface); border: 1px solid var(--border); border-radius: 4px; font-size: 12px; min-height: 48px; width: 100%; box-sizing: border-box; }
  .impl-task-row:hover { border-color: var(--green)50; }
  .impl-task-row.header { background: var(--surface2); font-weight: 600; color: var(--text-muted); font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; text-align: center; }
  .impl-task-name { font-weight: 500; color: var(--text); }
  .impl-status-badge { padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: 600; text-align: center; white-space: nowrap; }
  .impl-status-not-started { color: var(--text-muted); background: var(--surface2); border: 1px solid var(--border); }
  .impl-status-not-required { color: var(--text-dim); background: var(--surface2); border: 1px solid var(--border); font-style: italic; }
  .impl-status-in-progress { color: #4c9aff; background: #0052cc15; border: 1px solid #4c9aff40; }
  .impl-status-behind { color: var(--red); background: #e3252915; border: 1px solid #e3252940; }
  .impl-status-complete { color: var(--green); background: #47ac4c15; border: 1px solid #47ac4c40; }
  .impl-dashboard-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .impl-stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 6px; padding: 14px 16px; display: flex; flex-direction: column; gap: 4px; }
  .impl-stat-value { font-family: 'Poppins', sans-serif; font-size: 28px; font-weight: 700; }
  .impl-stat-label { font-size: 10px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.8px; }
  .impl-kanban { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
  .impl-kanban-col { background: var(--surface); border: 1px solid var(--border); border-radius: 6px; display: flex; flex-direction: column; min-height: 120px; }
  .impl-kanban-col-header { padding: 8px 12px; border-bottom: 1px solid var(--border); font-size: 11px; font-weight: 600; letter-spacing: 0.5px; display: flex; align-items: center; justify-content: space-between; }
  .impl-kanban-card { margin: 6px 8px; background: var(--surface2); border: 1px solid var(--border); border-radius: 4px; padding: 8px 10px; font-size: 11px; cursor: grab; user-select: none; }
  .impl-kanban-card:active { cursor: grabbing; }
  .impl-kanban-card-title { font-weight: 500; color: var(--text); margin-bottom: 4px; }
  .impl-kanban-card-meta { font-size: 10px; color: var(--text-muted); }
  .impl-gantt { overflow-x: auto; position: relative; }
  .impl-gantt-row { display: flex; align-items: center; border-bottom: 1px solid var(--border)50; min-height: 36px; height: 36px; }
  .impl-gantt-label { width: 200px; flex-shrink: 0; font-size: 11px; padding: 4px 8px; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .impl-gantt-track { flex: 1; position: relative; height: 32px; }
  .impl-gantt-bar { position: absolute; height: 16px; top: 8px; border-radius: 3px; background: var(--green); opacity: 0.85; }
  .impl-gantt-bar.behind { background: var(--red); }
  .impl-gantt-bar.in-progress { background: #4c9aff; }
  .impl-gantt-bar.complete { background: var(--green); }
  .impl-gantt-bar.not-started { background: var(--border); }
  .impl-gantt-milestone { position: absolute; width: 12px; height: 12px; top: 10px; transform: rotate(45deg); background: var(--amber); }
  .impl-gantt-header { display: flex; border-bottom: 2px solid var(--border); margin-bottom: 4px; }
  .impl-gantt-header-label { width: 200px; flex-shrink: 0; font-size: 10px; color: var(--text-muted); padding: 4px 8px; font-weight: 600; }
  .impl-gantt-months { flex: 1; display: flex; }
  .impl-gantt-month { font-size: 9px; color: var(--text-muted); text-align: center; border-left: 1px solid var(--border)30; padding: 4px 0; }
  .impl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; color: var(--text-dim); text-align: center; gap: 12px; }
  .impl-empty-icon { font-size: 40px; opacity: 0.3; }

  .syncing { animation: spin 1s linear infinite; display: inline-block; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  .toast { position: fixed; bottom: 24px; right: 24px; background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 10px 18px; font-size: 12px; color: var(--text); z-index: 200; animation: slideUp 0.2s ease; font-family: 'Inter', sans-serif; }
  .toast.success { border-color: var(--green); color: var(--green); }
  .toast.error { border-color: var(--red); color: var(--red); }

  /* ── Popout Modal ── */
  .popout-overlay { position: fixed; inset: 0; background: #00000099; backdrop-filter: blur(4px); z-index: 150; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .popout-modal { background: var(--surface); border: 1px solid var(--border); border-top: 3px solid var(--green); border-radius: 8px; width: 100%; max-width: 800px; max-height: 90vh; overflow-y: auto; padding: 28px; display: flex; flex-direction: column; gap: 16px; animation: slideUp 0.2s ease; }
  .popout-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
  .popout-title { font-family: 'Poppins', sans-serif; font-size: 20px; font-weight: 700; color: var(--text); line-height: 1.4; flex: 1; }
  .popout-close { background: none; border: 1px solid var(--border); color: var(--text-muted); border-radius: 4px; padding: 4px 10px; cursor: pointer; font-size: 16px; flex-shrink: 0; }
  .popout-close:hover { border-color: var(--red); color: var(--red); }
  .btn.danger { background: var(--red); color: #fff; border-color: var(--red); }
  .btn.danger:hover { opacity: 0.85; }
  .editable-title { font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 600; color: var(--text); background: var(--surface2); border: 1px solid var(--border); border-radius: 4px; padding: 4px 8px; width: 100%; outline: none; line-height: 1.4; }
  .editable-title:focus { border-color: var(--green); }
  .note-image { max-width: 100%; border-radius: 4px; margin-top: 6px; border: 1px solid var(--border); cursor: pointer; }
  .note-image:hover { border-color: var(--green); }
  .popout-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .popout-full { grid-column: 1 / -1; }

  /* ── Notes history ── */
  .note-entry { background: var(--surface2); border: 1px solid var(--border); border-radius: 4px; padding: 8px 10px; margin-bottom: 6px; }
  .note-entry-date { font-size: 10px; color: var(--text-muted); margin-bottom: 4px; font-weight: 600; }
  .note-entry-text { font-size: 12px; color: var(--text); line-height: 1.6; white-space: pre-wrap; word-break: break-word; overflow-wrap: break-word; }
  .note-entry-text ul { padding-left: 16px; margin: 4px 0; }
  .note-entry-text li { margin: 2px 0; }

  /* ── Tags ── */
  .tag-input-row { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .tag-chip { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; border: 1px solid; cursor: default; }
  .tag-chip .remove { color: inherit; font-size: 11px; margin-left: 2px; opacity: 0.6; cursor: pointer; }
  .tag-chip .remove:hover { opacity: 1; }
  .tag-add-input { background: var(--surface2); border: 1px solid var(--border); border-radius: 12px; padding: 2px 10px; color: var(--text); font-size: 11px; outline: none; width: 100px; }
  .tag-add-input:focus { border-color: var(--green); }

  /* ── Subtasks ── */
  .subtask-item { display: flex; align-items: center; gap: 8px; padding: 5px 0; border-bottom: 1px solid var(--border)30; }
  .subtask-check { width: 14px; height: 14px; border-radius: 3px; border: 1.5px solid var(--text-dim); flex-shrink: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 9px; transition: all 0.15s; }
  .subtask-check.checked { background: var(--green); border-color: var(--green); color: #fff; }
  .subtask-text { flex: 1; font-size: 12px; color: var(--text); }
  .subtask-text.done { text-decoration: line-through; color: var(--text-muted); }
  .subtask-status { font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 3px; border: 1px solid; white-space: nowrap; cursor: pointer; user-select: none; }
  .subtask-status:hover { opacity: 0.8; }
  .subtask-remove { background: none; border: none; color: var(--text-dim); cursor: pointer; font-size: 14px; padding: 0 4px; }
  .subtask-remove:hover { color: var(--red); }
  .subtask-add-row { display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap; }
  .subtask-extra-row { display: flex; gap: 6px; margin-top: 4px; flex-wrap: wrap; align-items: center; }
  .subtask-meta { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; margin-top: 2px; }
  .subtask-badge { cursor: pointer; user-select: none; }
  .subtask-badge:hover { opacity: 0.8; border-color: var(--green) !important; color: var(--green) !important; }
  .inline-subtasks { margin-top: 8px; padding: 8px 10px; background: var(--surface2); border-radius: 4px; border: 1px solid var(--border); }
  .inline-subtask-row { display: flex; align-items: center; gap: 8px; padding: 5px 0; border-bottom: 1px solid var(--border)30; }
  .inline-subtask-row:last-child { border-bottom: none; }
  .inline-subtask-check { width: 13px; height: 13px; border-radius: 3px; border: 1.5px solid var(--text-dim); flex-shrink: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 8px; transition: all 0.15s; }
  .inline-subtask-check.checked { background: var(--green); border-color: var(--green); color: #fff; }
  .inline-subtask-text { font-size: 11px; color: var(--text); flex: 1; line-height: 1.4; }
  .inline-subtask-text.done { text-decoration: line-through; color: var(--text-muted); }
  .inline-subtask-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 3px; align-items: center; }
  .note-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  .note-table th { background: var(--surface); color: var(--green); font-weight: 600; text-align: left; padding: 5px 8px; border: 1px solid var(--border); font-family: 'Inter', sans-serif; }
  .note-table td { padding: 5px 8px; border: 1px solid var(--border); color: var(--text); vertical-align: top; }
  .note-table tr:nth-child(even) td { background: var(--surface2); }
  .note-table tr:hover td { background: #47ac4c12; }

  /* ── Action Log ── */
  .action-log-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  .action-log-table th { background: var(--surface2); color: var(--green); font-weight: 600; text-align: left; padding: 7px 8px; border: 1px solid var(--border); font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; white-space: nowrap; position: sticky; top: 0; z-index: 2; }
  .action-log-table td { padding: 5px 6px; border: 1px solid var(--border); color: var(--text); vertical-align: top; }
  .action-log-table tr:nth-child(even) td { background: var(--surface2); }
  .action-log-table tr:hover td { background: #47ac4c10; }
  .action-log-input { background: transparent; border: none; color: var(--text); font-family: 'Inter', sans-serif; font-size: 11px; width: 100%; outline: none; padding: 2px 0; resize: none; min-height: 20px; }
  .action-log-input:focus { background: var(--surface); border-radius: 2px; padding: 2px 4px; }
  .action-log-select { background: transparent; border: none; color: var(--text); font-family: 'Inter', sans-serif; font-size: 11px; width: 100%; outline: none; cursor: pointer; padding: 2px 0; }
  .action-log-select:focus { background: var(--surface); }
  .action-log-badge { display: inline-block; padding: 2px 7px; border-radius: 3px; font-size: 10px; font-weight: 600; white-space: nowrap; }
  .al-open { color: #4c9aff; background: #0052cc15; border: 1px solid #4c9aff40; }
  .al-customer-pending { color: var(--amber); background: #F1901E15; border: 1px solid #F1901E40; }
  .al-closed { color: var(--green); background: #47ac4c15; border: 1px solid #47ac4c40; }
  .al-low { color: var(--green); }
  .al-medium { color: var(--amber); }
  .al-high { color: var(--red); font-weight: 700; }
  .al-critical { color: #fff; background: var(--red); border-radius: 3px; padding: 1px 5px; font-weight: 700; }

  /* ── Risk Register ── */
  .risk-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  .risk-table th { background: var(--surface2); color: var(--green); font-weight: 600; text-align: left; padding: 7px 8px; border: 1px solid var(--border); font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; white-space: nowrap; position: sticky; top: 0; z-index: 2; }
  .risk-table td { padding: 5px 6px; border: 1px solid var(--border); color: var(--text); vertical-align: top; }
  .risk-table tr:nth-child(even) td { background: var(--surface2); }
  .risk-table tr:hover td { background: #47ac4c10; }
  .risk-score-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; text-align: center; min-width: 28px; }
  .risk-input { background: transparent; border: none; color: var(--text); font-family: 'Inter', sans-serif; font-size: 11px; width: 100%; outline: none; padding: 2px 0; resize: none; min-height: 20px; }
  .risk-input:focus { background: var(--surface); border-radius: 2px; padding: 2px 4px; }
  .risk-num-input { background: var(--surface2); border: 1px solid var(--border); border-radius: 3px; color: var(--text); font-family: 'Inter', sans-serif; font-size: 11px; width: 38px; text-align: center; outline: none; padding: 3px 4px; }
  .risk-num-input:focus { border-color: var(--green); }
  .risk-select { background: transparent; border: none; color: var(--text); font-family: 'Inter', sans-serif; font-size: 11px; width: 100%; outline: none; cursor: pointer; padding: 2px 0; }
  .risk-select:focus { background: var(--surface); }

  /* ── Module Setup ── */
  .ms-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px; }
  .ms-table th { background: var(--surface2); color: var(--green); font-weight: 600; text-align: center; padding: 6px 6px; border-top: 1px solid var(--border); border-right: 1px solid var(--border); border-bottom: 2px solid #47ac4c40; font-size: 9px; letter-spacing: 0.4px; text-transform: uppercase; position: sticky; top: 0; z-index: 2; white-space: nowrap; }
  .ms-table thead tr:first-child th:first-child { border-left: 1px solid var(--border); border-top-left-radius: 5px; }
  .ms-table thead tr:first-child th:last-child { border-top-right-radius: 5px; }
  .ms-table thead tr:last-child th:first-child { border-left: 1px solid var(--border); border-bottom-left-radius: 5px; }
  .ms-table thead tr:last-child th:last-child { border-bottom-right-radius: 5px; }
  .ms-table thead tr:last-child th { border-bottom: 2px solid #47ac4c50; }
  .ms-table th.wrap { white-space: normal; word-break: break-word; vertical-align: middle; min-height: 52px; height: 52px; line-height: 1.3; }
  .ms-table th.left { text-align: left; }
  .ms-table td { padding: 4px 6px; border-right: 1px solid var(--border); border-bottom: 1px solid var(--border); color: var(--text); vertical-align: middle; }
  .ms-table td:first-child { border-left: 1px solid var(--border); }
  .ms-table tr:last-child td:first-child { border-bottom-left-radius: 4px; }
  .ms-table tr:last-child td:last-child { border-bottom-right-radius: 4px; }
  .ms-table tr:nth-child(even) td { background: var(--surface2); }
  .ms-table tr:hover td { background: #47ac4c08; }
  .ms-section-header { display: flex; align-items: center; gap: 10px; padding: 5px 10px; background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; font-size: 10px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 2px; }
  .ms-group-header { background: var(--surface2) !important; color: var(--text-muted); font-size: 9px; font-weight: 700; text-align: center; letter-spacing: 0.8px; text-transform: uppercase; border-bottom: 2px solid var(--green)30 !important; }
  .ms-input { background: transparent; border: none; color: var(--text); font-family: 'Inter', sans-serif; font-size: 11px; width: 100%; outline: none; padding: 1px 0; }
  .ms-input:focus { background: var(--surface); border-radius: 2px; padding: 1px 3px; }
  .ms-select { background: transparent; border: none; color: var(--text); font-family: 'Inter', sans-serif; font-size: 11px; width: 100%; outline: none; cursor: pointer; padding: 1px 0; text-align: center; }
  .ms-select:focus { background: var(--surface); }
  .ms-num { background: var(--surface2); border: 1px solid var(--border); border-radius: 3px; color: var(--text); font-size: 11px; width: 46px; text-align: center; outline: none; padding: 2px 2px; font-family: 'Inter', sans-serif; }
  .ms-num:focus { border-color: var(--green); }
  .ms-yes { color: var(--green); font-weight: 700; }
  .ms-no { color: var(--text-dim); }
  .ms-jira-key { color: #4c9aff; font-size: 10px; font-weight: 600; white-space: nowrap; }

  /* ── Client Hub ── */
  .ch-shell { display: flex; flex-direction: column; height: 100%; background: var(--bg); }
  .ch-topbar { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: var(--surface); border-bottom: 1px solid var(--border); flex-shrink: 0; flex-wrap: wrap; }
  .ch-title { font-size: 15px; font-weight: 600; color: var(--text); white-space: nowrap; }
  .ch-client-select { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 6px 10px; color: var(--text); font-size: 13px; outline: none; min-width: 240px; font-family: 'Inter', sans-serif; }
  .ch-client-select:focus { border-color: var(--green); }
  .ch-tabs { display: flex; border-bottom: 1px solid var(--border); background: var(--surface); flex-shrink: 0; padding: 0 16px; }
  .ch-tab { padding: 9px 16px; font-size: 12px; font-weight: 500; cursor: pointer; border-bottom: 2px solid transparent; color: var(--text-muted); transition: all 0.15s; white-space: nowrap; }
  .ch-tab:hover { color: var(--text); }
  .ch-tab.active { color: var(--green); border-bottom-color: var(--green); }
  .ch-body { flex: 1; overflow-y: auto; padding: 16px; }
  .ch-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; color: var(--text-dim); font-size: 13px; gap: 8px; }
  .ch-card { background: var(--surface); border: 1px solid var(--border); border-radius: 7px; padding: 12px 14px; margin-bottom: 8px; }
  .ch-card:hover { border-color: var(--border); }
  .ch-entry { background: var(--surface); border: 1px solid var(--border); border-radius: 7px; padding: 12px 14px; margin-bottom: 8px; cursor: pointer; border-left: 3px solid var(--border); transition: all 0.15s; }
  .ch-entry:hover { background: var(--surface2); }
  .ch-entry.type-email { border-left-color: var(--outlook); }
  .ch-entry.type-teams { border-left-color: #6264a7; }
  .ch-entry.type-phone { border-left-color: var(--green); }
  .ch-entry.type-meeting { border-left-color: var(--amber); }
  .ch-entry.type-note { border-left-color: var(--text-dim); }
  .ch-entry.type-document { border-left-color: #4c9aff; }
  .ch-entry-top { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
  .ch-entry-subject { font-size: 13px; font-weight: 500; color: var(--text); flex: 1; }
  .ch-entry-date { font-size: 11px; color: var(--text-dim); white-space: nowrap; }
  .ch-entry-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .ch-entry-preview { font-size: 12px; color: var(--text-muted); margin-top: 4px; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .ch-type-badge { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 3px; border: 1px solid; white-space: nowrap; }
  .ch-type-email { color: var(--outlook); border-color: #0078d440; background: #0078d412; }
  .ch-type-teams { color: #8b8dc7; border-color: #6264a740; background: #6264a712; }
  .ch-type-phone { color: var(--green); border-color: #47ac4c40; background: #47ac4c12; }
  .ch-type-meeting { color: var(--amber); border-color: #F1901E40; background: #F1901E12; }
  .ch-type-note { color: var(--text-muted); border-color: var(--border); background: var(--surface2); }
  .ch-type-document { color: #4c9aff; border-color: #4c9aff40; background: #4c9aff12; }
  .ch-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
  .ch-form-row.single { grid-template-columns: 1fr; }
  .ch-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 5px; display: block; }
  .ch-input { width: 100%; background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 7px 10px; color: var(--text); font-size: 13px; outline: none; font-family: 'Inter', sans-serif; }
  .ch-input:focus { border-color: var(--green); }
  .ch-select { width: 100%; background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 7px 10px; color: var(--text); font-size: 13px; outline: none; font-family: 'Inter', sans-serif; cursor: pointer; }
  .ch-select:focus { border-color: var(--green); }
  .ch-textarea { width: 100%; background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 8px 10px; color: var(--text); font-size: 13px; outline: none; font-family: 'Inter', sans-serif; resize: vertical; min-height: 100px; line-height: 1.5; }
  .ch-textarea:focus { border-color: var(--green); }
  .ch-upload-zone { border: 1px dashed var(--border); border-radius: 7px; padding: 20px; text-align: center; cursor: pointer; transition: all 0.15s; margin-bottom: 10px; }
  .ch-upload-zone:hover { border-color: var(--green); background: rgba(71,172,76,0.05); }
  .ch-upload-zone p { font-size: 13px; color: var(--text-muted); margin-top: 6px; }
  .ch-upload-zone span { font-size: 11px; color: var(--text-dim); }
  .ch-doc-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: var(--surface); border: 1px solid var(--border); border-radius: 6px; margin-bottom: 6px; }
  .ch-doc-icon { font-size: 20px; flex-shrink: 0; }
  .ch-doc-info { flex: 1; min-width: 0; }
  .ch-doc-name { font-size: 13px; font-weight: 500; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ch-doc-meta { font-size: 11px; color: var(--text-dim); margin-top: 2px; }
  .ch-ai-box { background: var(--surface); border: 1px solid var(--border); border-radius: 7px; padding: 14px 16px; margin-bottom: 12px; }
  .ch-ai-input-row { display: flex; gap: 8px; margin-top: 10px; }
  .ch-ai-answer { background: var(--surface2); border-radius: 5px; padding: 12px 14px; margin-top: 12px; font-size: 13px; color: var(--text); line-height: 1.65; border-left: 3px solid var(--green); }
  .ch-ai-answer-claude { background: var(--surface2); border-radius: 5px; padding: 12px 14px; margin-top: 12px; font-size: 13px; color: var(--text); line-height: 1.65; border-left: 3px solid #c47ef0; white-space: pre-wrap; }
  .ch-ai-claude-label { font-size: 10px; font-weight: 600; color: #c47ef0; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 6px; display: flex; align-items: center; gap: 5px; }
  .ch-ai-source { font-size: 10px; color: var(--text-dim); margin-top: 8px; font-style: italic; }
  .ch-suggested { display: flex; flex-direction: column; gap: 6px; }
  .ch-suggested-q { font-size: 12px; color: #4c9aff; cursor: pointer; padding: 5px 8px; border-radius: 4px; transition: background 0.15s; }
  .ch-suggested-q:hover { background: var(--surface2); }
  .ch-health-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 14px; }
  .ch-health-card { background: var(--surface); border: 1px solid var(--border); border-radius: 7px; padding: 14px; text-align: center; }
  .ch-health-val { font-size: 26px; font-weight: 700; color: var(--text); font-family: 'Poppins', sans-serif; }
  .ch-health-lbl { font-size: 10px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; margin-top: 4px; }
  .ch-activity-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--border)50; font-size: 12px; color: var(--text-muted); }
  .ch-activity-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; }
  .ch-dot-green { background: var(--green); }
  .ch-dot-amber { background: var(--amber); }
  .ch-dot-red { background: var(--red); }
  .ch-filter-row { display: flex; gap: 6px; margin-bottom: 12px; flex-wrap: wrap; }
  .ch-filter-btn { font-size: 11px; padding: 4px 12px; border-radius: 20px; border: 1px solid var(--border); background: var(--surface2); color: var(--text-muted); cursor: pointer; transition: all 0.15s; }
  .ch-filter-btn:hover, .ch-filter-btn.active { background: rgba(71,172,76,0.1); border-color: var(--green); color: var(--green); }
  .ch-delete-btn { background: none; border: none; color: var(--text-dim); cursor: pointer; font-size: 14px; padding: 2px 6px; border-radius: 3px; flex-shrink: 0; }
  .ch-delete-btn:hover { color: var(--red); background: rgba(227,37,41,0.1); }

  /* ── Roadmap ── */
  .rm-shell { display: flex; flex-direction: column; height: 100%; background: var(--bg); overflow: hidden; }
  .rm-topbar { display: flex; align-items: center; gap: 10px; padding: 10px 16px; background: var(--surface); border-bottom: 1px solid var(--border); flex-shrink: 0; flex-wrap: wrap; }
  .rm-title { font-size: 15px; font-weight: 600; color: var(--text); }
  .rm-view-toggle { display: flex; border: 1px solid var(--border); border-radius: 5px; overflow: hidden; }
  .rm-view-btn { font-size: 12px; padding: 5px 14px; background: var(--surface2); color: var(--text-muted); border: none; cursor: pointer; border-right: 1px solid var(--border); }
  .rm-view-btn:last-child { border-right: none; }
  .rm-view-btn.active { background: var(--green); color: #fff; font-weight: 600; }
  .rm-filters { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .rm-select { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 5px 8px; color: var(--text); font-size: 12px; outline: none; }
  .rm-body { flex: 1; overflow: auto; padding: 0; }

  /* Board view */
  .rm-board { display: flex; gap: 0; height: 100%; align-items: flex-start; overflow-x: auto; padding: 14px; }
  .rm-lane { flex-shrink: 0; width: 240px; background: var(--surface); border: 1px solid var(--border); border-radius: 8px; margin-right: 12px; display: flex; flex-direction: column; max-height: calc(100vh - 160px); }
  .rm-lane-header { padding: 10px 12px 8px; border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .rm-lane-title { font-size: 12px; font-weight: 700; color: var(--text); }
  .rm-lane-meta { font-size: 10px; color: var(--text-dim); margin-top: 2px; }
  .rm-lane-cards { flex: 1; overflow-y: auto; padding: 8px; display: flex; flex-direction: column; gap: 6px; min-height: 60px; }
  .rm-lane-cards.drag-over { background: rgba(71,172,76,0.07); border-radius: 5px; }
  .rm-card { background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 9px 10px; cursor: grab; border-left: 3px solid var(--border); transition: box-shadow 0.15s; user-select: none; }
  .rm-card:active { cursor: grabbing; }
  .rm-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.2); border-color: var(--border); }
  .rm-card.dragging { opacity: 0.4; }
  .rm-card.s-deployed { border-left-color: var(--green); }
  .rm-card.s-inprogress { border-left-color: var(--amber); }
  .rm-card.s-inuat { border-left-color: #a78bfa; }
  .rm-card.s-pendingqa { border-left-color: var(--outlook); }
  .rm-card.s-todo { border-left-color: var(--text-dim); }
  .rm-card-key { font-size: 10px; font-weight: 700; color: var(--teal,#00d4aa); font-family: monospace; margin-bottom: 3px; }
  .rm-card-title { font-size: 12px; color: var(--text); font-weight: 500; line-height: 1.4; margin-bottom: 5px; }
  .rm-card-meta { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .rm-badge { font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 3px; white-space: nowrap; }
  .rm-badge-status { background: var(--surface); border: 1px solid var(--border); color: var(--text-muted); }
  .rm-badge-owner { background: rgba(71,172,76,0.1); border: 1px solid rgba(71,172,76,0.3); color: var(--green); }
  .rm-badge-customer { background: rgba(0,120,212,0.1); border: 1px solid rgba(0,120,212,0.3); color: #4c9aff; }
  .rm-add-lane-btn { flex-shrink: 0; width: 200px; height: 60px; background: var(--surface)50; border: 1px dashed var(--border); border-radius: 8px; color: var(--text-dim); font-size: 13px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; }
  .rm-add-lane-btn:hover { border-color: var(--green); color: var(--green); background: rgba(71,172,76,0.05); }

  /* Gantt view */
  .rm-gantt { min-width: 100%; }
  .rm-gantt-header { display: flex; position: sticky; top: 0; background: var(--surface); border-bottom: 1px solid var(--border); z-index: 10; }
  .rm-gantt-label-col { width: 380px; min-width: 380px; flex-shrink: 0; padding: 8px 12px; font-size: 11px; font-weight: 600; color: var(--text-muted); border-right: 1px solid var(--border); }
  .rm-gantt-months { display: flex; flex: 1; }
  .rm-gantt-month { flex: 1; min-width: 80px; text-align: center; padding: 8px 4px; font-size: 11px; font-weight: 600; color: var(--text-muted); border-right: 1px solid var(--border)50; }
  .rm-gantt-row { display: flex; border-bottom: 1px solid var(--border)40; min-height: 36px; align-items: center; }
  .rm-gantt-row:hover { background: var(--surface2); }
  .rm-gantt-row-label { width: 380px; min-width: 380px; flex-shrink: 0; padding: 6px 12px; border-right: 1px solid var(--border); cursor: pointer; }
  .rm-gantt-row-key { font-size: 10px; font-weight: 700; color: var(--teal,#00d4aa); font-family: monospace; }
  .rm-gantt-row-title { font-size: 11px; color: var(--text); font-weight: 500; white-space: normal; overflow: hidden; word-break: break-word; line-height: 1.3; }
  .rm-gantt-timeline { flex: 1; position: relative; height: 36px; }
  .rm-gantt-bar { position: absolute; height: 20px; top: 8px; border-radius: 3px; min-width: 4px; display: flex; align-items: center; padding: 0 5px; font-size: 9px; font-weight: 600; color: #fff; overflow: hidden; white-space: nowrap; cursor: pointer; }
  .rm-gantt-today { position: absolute; top: 0; bottom: 0; width: 1px; background: var(--red); opacity: 0.6; z-index: 5; }
  .rm-gantt-today-label::before { content: 'Today'; position: absolute; top: 2px; left: 3px; font-size: 9px; color: var(--red); font-weight: 600; white-space: nowrap; }

  /* Card detail modal */
  .rm-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(3px); z-index: 300; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .rm-modal { background: var(--surface); border: 1px solid var(--border); border-radius: 8px; width: 100%; max-width: 600px; max-height: 88vh; display: flex; flex-direction: column; animation: slideUp 0.2s ease; }
  .rm-modal-header { padding: 14px 18px 10px; border-bottom: 1px solid var(--border); display: flex; align-items: flex-start; gap: 10px; flex-shrink: 0; }
  .rm-modal-body { flex: 1; overflow-y: auto; padding: 16px 18px; display: flex; flex-direction: column; gap: 12px; }
  .rm-modal-footer { padding: 12px 18px; border-top: 1px solid var(--border); display: flex; gap: 8px; justify-content: flex-end; flex-shrink: 0; }
  .rm-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .rm-field-row.single { grid-template-columns: 1fr; }
  .rm-field { display: flex; flex-direction: column; gap: 4px; }
  .rm-field label { font-size: 10px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; }
  .rm-input { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 7px 10px; color: var(--text); font-size: 13px; outline: none; font-family: inherit; width: 100%; }
  .rm-input:focus { border-color: var(--green); }
  .rm-textarea { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 7px 10px; color: var(--text); font-size: 13px; outline: none; font-family: inherit; width: 100%; resize: vertical; min-height: 70px; line-height: 1.5; }
  .rm-textarea:focus { border-color: var(--green); }
  .rm-select-input { background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 7px 10px; color: var(--text); font-size: 13px; outline: none; font-family: inherit; width: 100%; cursor: pointer; }
  .rm-select-input:focus { border-color: var(--green); }

  /* Grid/spreadsheet view */
  .rm-grid { width: 100%; border-collapse: collapse; font-size: 12px; }
  .rm-grid thead { position: sticky; top: 0; z-index: 10; }
  .rm-grid th { background: var(--surface); border: 1px solid var(--border); padding: 7px 10px; text-align: left; font-size: 10px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap; cursor: pointer; user-select: none; }
  .rm-grid th:hover { color: var(--text); }
  .rm-grid th.sorted { color: var(--green); }
  .rm-grid td { border: 1px solid var(--border)60; padding: 0; vertical-align: middle; }
  .rm-grid tr:hover td { background: var(--surface2); }
  .rm-grid tr.selected td { background: rgba(71,172,76,0.07); }
  .rm-grid-cell { padding: 6px 9px; display: block; min-height: 32px; display: flex; align-items: center; cursor: pointer; }
  .rm-grid-cell:hover { background: var(--surface2); }
  .rm-grid-cell.editing { padding: 0; }
  .rm-grid-cell input, .rm-grid-cell select { width: 100%; background: var(--surface); border: none; border-bottom: 2px solid var(--green); padding: 6px 9px; color: var(--text); font-size: 12px; outline: none; font-family: inherit; height: 32px; }
  .rm-grid-key { font-family: monospace; font-weight: 700; color: var(--teal,#00d4aa); font-size: 11px; }
  .rm-grid-status { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 3px; white-space: nowrap; }
  .rm-grid-wrap { overflow: auto; height: 100%; }
  .rm-grid-add-row { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-top: 1px solid var(--border); color: var(--text-dim); font-size: 12px; cursor: pointer; background: var(--surface); }
  .rm-grid-add-row:hover { color: var(--green); }
  .rm-drag-handle { cursor: grab; color: var(--text-dim); padding: 0 8px; font-size: 14px; user-select: none; flex-shrink: 0; }
  .rm-drag-handle:hover { color: var(--text); }
  .rm-grid tr.drag-over-top td { border-top: 2px solid var(--green); }
  .rm-grid tr.drag-over-bottom td { border-bottom: 2px solid var(--green); }
  .rm-grid tr.row-dragging { opacity: 0.35; }
  .ch-popout-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(3px); z-index: 300; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .ch-popout { background: var(--surface); border: 1px solid var(--border); border-radius: 8px; width: 100%; max-width: 680px; max-height: 85vh; display: flex; flex-direction: column; animation: slideUp 0.2s ease; }
  .ch-popout-header { padding: 16px 18px 12px; border-bottom: 1px solid var(--border); display: flex; align-items: flex-start; gap: 10px; flex-shrink: 0; }
  .ch-popout-title { flex: 1; font-size: 14px; font-weight: 600; color: var(--text); line-height: 1.4; }
  .ch-popout-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 18px; border-bottom: 1px solid var(--border); background: var(--surface2); flex-shrink: 0; }
  .ch-popout-body { flex: 1; overflow-y: auto; padding: 16px 18px; }
  .ch-popout-content { font-size: 13px; color: var(--text); line-height: 1.7; white-space: pre-wrap; word-break: break-word; }
  .ch-popout-footer { padding: 12px 18px; border-top: 1px solid var(--border); display: flex; gap: 8px; justify-content: flex-end; flex-shrink: 0; }
  /* Pin button */
  .ch-pin-btn { background: none; border: none; cursor: pointer; font-size: 15px; padding: 2px 5px; border-radius: 3px; flex-shrink: 0; opacity: 0.4; transition: opacity 0.15s; }
  .ch-pin-btn:hover, .ch-pin-btn.pinned { opacity: 1; }
  .ch-entry.pinned { border-top: 2px solid gold; }
  /* Client summary card */
  .ch-summary-bar { display: flex; align-items: center; gap: 16px; padding: 10px 16px; background: var(--surface2); border-bottom: 1px solid var(--border); flex-wrap: wrap; flex-shrink: 0; }
  .ch-summary-stat { display: flex; flex-direction: column; align-items: center; min-width: 60px; }
  .ch-summary-val { font-size: 18px; font-weight: 700; color: var(--text); font-family: 'Poppins', sans-serif; }
  .ch-summary-lbl { font-size: 10px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.06em; }
  .ch-summary-divider { width: 1px; height: 32px; background: var(--border); }
  .ch-summary-last { font-size: 12px; color: var(--text-muted); flex: 1; }
  /* Search bar */
  .ch-search-bar { display: flex; align-items: center; gap: 8px; padding: 8px 16px; background: var(--surface); border-bottom: 1px solid var(--border); flex-shrink: 0; }
  .ch-search-input { flex: 1; background: var(--surface2); border: 1px solid var(--border); border-radius: 5px; padding: 6px 10px; color: var(--text); font-size: 13px; outline: none; font-family: 'Inter', sans-serif; }
  .ch-search-input:focus { border-color: var(--green); }
  /* Jira link tag */
  .ch-jira-tag { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 3px; background: rgba(76,154,255,0.15); border: 1px solid rgba(76,154,255,0.3); color: #4c9aff; font-family: monospace; cursor: pointer; }
  .ch-jira-tag:hover { background: rgba(76,154,255,0.25); }
  /* Actions extractor */
  .ch-actions-list { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
  .ch-action-row { display: flex; align-items: flex-start; gap: 10px; background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 10px 12px; }
  .ch-action-row input[type=checkbox] { accent-color: var(--green); margin-top: 2px; flex-shrink: 0; width: 15px; height: 15px; }
  .ch-action-info { flex: 1; min-width: 0; }
  .ch-action-text { font-size: 13px; color: var(--text); margin-bottom: 3px; }
  .ch-action-meta { font-size: 11px; color: var(--text-dim); display: flex; gap: 10px; flex-wrap: wrap; }
  .ch-action-priority { font-size: 10px; font-weight: 600; padding: 1px 7px; border-radius: 3px; }
  .ch-pri-high { background: rgba(227,37,41,0.15); color: var(--red); }
  .ch-pri-medium { background: rgba(241,144,30,0.15); color: var(--amber); }
  .ch-pri-low { background: rgba(71,172,76,0.15); color: var(--green); }
`;

// ─── Proxy ────────────────────────────────────────────────────────────────────
// Auto-detect production vs development for proxy URL
// In production, call proxy directly on port 3001 (bypasses IIS which does not have ARR)
// In dev, proxy runs on localhost:3001
const PROXY = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/proxy?url="
  : "/api/proxy?url=";

const FILES_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/files"
  : "/api/files";

// Direct proxy URL for XHR/multipart uploads - bypasses IIS ARR which strips custom headers
// Uses the proxy port directly so X-Atlassian-Token is preserved
const PROXY_DIRECT = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/proxy?url="
  : "https://tasks.c365cloud.co.uk:3001/proxy?url=";

// Shared projects API - same origin as proxy
const PROJECTS_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/projects"
  : "/api/projects";

// Client Hub API - shared communications store
const CLIENT_HUB_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/clienthub"
  : "/api/clienthub";

// Client Hub - Ask Claude endpoint
const CLIENT_HUB_ASK_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/clienthub/ask"
  : "/api/clienthub/ask";

// Roadmap API
const ROADMAP_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/roadmap"
  : "/api/roadmap";

// Team members API
const TEAM_MEMBERS_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/team-members"
  : "/api/team-members";

// Manual tasks API (shared server storage)
const MANUAL_TASKS_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/manual-tasks"
  : "/api/manual-tasks";

// Client Hub - Extract actions endpoint
const CLIENT_HUB_EXTRACT_API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3001/api/clienthub/extract-actions"
  : "/api/clienthub/extract-actions";


// ─── Manager accounts ─────────────────────────────────────────────────────────
// These users get the Manager View toggle in the header
const MANAGER_NAMES = ["Heather Allen", "Richard Foster"];

function isManager(config) {
  // Check if current user's Jira display name is a manager
  const name = localStorage.getItem("taskflow_jira_displayname") || "";
  return MANAGER_NAMES.some(m => name.toLowerCase() === m.toLowerCase());
}

// ─── Azure AD OAuth (PKCE) ───────────────────────────────────────────────────
const MS_CLIENT_ID = "4c585289-b626-4650-9912-c34a6a16b94f";
const MS_SCOPES = "Tasks.ReadWrite Mail.ReadWrite Mail.Send Calendars.Read User.Read offline_access";
const MS_REDIRECT = window.location.origin;

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array)).replace(/[+/=]/g, (c) => ({"+":"-","/":"_","=":""})[c]);
}

async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/[+/=]/g, (c) => ({"+":"-","/":"_","=":""})[c]);
}

async function startMsLogin() {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  localStorage.setItem("pkce_verifier", verifier);
  const params = new URLSearchParams({
    client_id: MS_CLIENT_ID,
    response_type: "code",
    redirect_uri: MS_REDIRECT,
    scope: MS_SCOPES,
    response_mode: "query",
    code_challenge: challenge,
    code_challenge_method: "S256",
    prompt: "select_account",  // Show account picker; use consent only when scopes change
  });
  window.location.href = `https://login.microsoftonline.com/0c5d7ee9-5595-4077-9d3c-fc031c2d194f/oauth2/v2.0/authorize?${params}`;
}

async function handleMsCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");
  if (!code) return null;
  const verifier = localStorage.getItem("pkce_verifier");
  if (!verifier) return null;
  window.history.replaceState(null, "", window.location.pathname);
  try {
    const res = await fetch("https://login.microsoftonline.com/0c5d7ee9-5595-4077-9d3c-fc031c2d194f/oauth2/v2.0/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        client_id: MS_CLIENT_ID,
        grant_type: "authorization_code",
        code,
        redirect_uri: MS_REDIRECT,
        code_verifier: verifier,
        scope: MS_SCOPES,
      }),
    });
    const data = await res.json();
    if (data.access_token) {
      const expiry = Date.now() + (data.expires_in || 3600) * 1000;
      localStorage.setItem("taskflow_ms_token", data.access_token);
      localStorage.setItem("taskflow_ms_token_expiry", expiry.toString());
      if (data.refresh_token) localStorage.setItem("taskflow_ms_refresh", data.refresh_token);
      localStorage.removeItem("pkce_verifier");
      return data.access_token;
    }
  } catch (e) {
    console.error("MS token exchange failed:", e);
  }
  return null;
}

async function refreshMsToken() {
  const refresh = localStorage.getItem("taskflow_ms_refresh");
  if (!refresh) return null;
  try {
    const res = await fetch("https://login.microsoftonline.com/0c5d7ee9-5595-4077-9d3c-fc031c2d194f/oauth2/v2.0/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        client_id: MS_CLIENT_ID,
        grant_type: "refresh_token",
        refresh_token: refresh,
        scope: MS_SCOPES,
      }),
    });
    const data = await res.json();
    if (data.access_token) {
      const expiry = Date.now() + (data.expires_in || 3600) * 1000;
      localStorage.setItem("taskflow_ms_token", data.access_token);
      localStorage.setItem("taskflow_ms_token_expiry", expiry.toString());
      if (data.refresh_token) localStorage.setItem("taskflow_ms_refresh", data.refresh_token);
      return data.access_token;
    }
  } catch (e) {
    console.error("MS token refresh failed:", e);
  }
  return null;
}

function getSavedMsToken() {
  const token = localStorage.getItem("taskflow_ms_token");
  const expiry = parseInt(localStorage.getItem("taskflow_ms_token_expiry") || "0");
  if (token && Date.now() < expiry - 60000) return token;
  return null;
}

// Get a valid token - tries refresh if current one is expired
async function getValidMsToken() {
  const token = getSavedMsToken();
  if (token) return token;
  // Token expired - try refresh
  const refreshed = await refreshMsToken();
  return refreshed;
}
function proxyUrl(url) { return PROXY + encodeURIComponent(url); }

// Upload a file to the proxy server for storage.
// Returns { id, name, url, size, type } on success.
async function uploadTaskFile(file, taskId) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const base64 = ev.target.result.split(",")[1];
        const res = await fetch(FILES_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fileName: file.name, mimeType: file.type, base64, taskId }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); reject(new Error(e.error || "Upload failed")); return; }
        resolve(await res.json());
      } catch (e) { reject(e); }
    };
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

// Delete a file from the proxy server.
async function deleteTaskFile(fileId) {
  try {
    await fetch(FILES_API + "/" + fileId, { method: "DELETE" });
  } catch {}
}

// File type helpers
function fileIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (["pdf"].includes(ext)) return "📄";
  if (["doc","docx"].includes(ext)) return "📝";
  if (["xls","xlsx","csv"].includes(ext)) return "📊";
  if (["ppt","pptx"].includes(ext)) return "📑";
  if (["jpg","jpeg","png","gif","webp"].includes(ext)) return "🖼";
  if (["zip","rar","7z"].includes(ext)) return "📦";
  if (["txt"].includes(ext)) return "📃";
  return "📎";
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

// SLA thresholds in business days by priority (SUPPORT tickets)
const SLA_DAYS = { critical: 1, high: 3, medium: 10, low: 20 };

// Check if a SUPPORT ticket has had no update for 2+ calendar days
// Used for the "No Reply" sub-view
const NO_REPLY_DAYS = 2;
const NO_REPLY_STATUSES = [
  "new", "in progress", "response", "reopened",
  "triage", "triage response", "triage waiting information", "tier 3 triage"
];

function isNoReply(task) {
  if (!task.jiraKey || !task.jiraKey.startsWith("SUPPORT-")) return false;
  if (task.done) return false;
  if (!task.jiraUpdated) return false;
  // Only flag statuses where a reply is expected
  const status = (task.jiraStatus || "").toLowerCase();
  if (!NO_REPLY_STATUSES.some(s => status.includes(s))) return false;
  // Check days since last update
  const updated = new Date(task.jiraUpdated);
  const now = new Date();
  const diffMs = now - updated;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays >= NO_REPLY_DAYS;
}

function businessDaysSince(isoDate) {
  if (!isoDate) return null;
  const start = new Date(isoDate);
  const now = new Date();
  let count = 0;
  const d = new Date(start);
  while (d < now) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) count++;
  }
  return count;
}

function getSLAInfo(task) {
  // Only for SUPPORT tickets (jiraKey starts with SUPPORT-)
  if (!task.jiraKey || !task.jiraKey.startsWith("SUPPORT-")) return null;
  // Don't show SLA for done/closed statuses
  const doneStatuses = ["done","closed","resolved","complete","qa completed"];
  if (doneStatuses.some(s => (task.jiraStatus || "").toLowerCase().includes(s))) return null;
  const priority = (task.priority || "medium").toLowerCase();
  const threshold = SLA_DAYS[priority] || SLA_DAYS.medium;
  const days = businessDaysSince(task.jiraUpdated || task.createdAt);
  if (days === null) return null;
  const remaining = threshold - days;
  const breached = remaining < 0;
  const warning = remaining <= Math.ceil(threshold * 0.2); // last 20% = warning
  return { days, threshold, remaining, breached, warning };
}

// Extract Jira issue key from a URL or plain key string
// e.g. "https://c365online.atlassian.net/browse/SUPPORT-12345" → "SUPPORT-12345"
function extractJiraKeyFromLink(link) {
  if (!link) return null;
  // Match /browse/KEY-123 pattern
  const browseMatch = link.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)/i);
  if (browseMatch) return browseMatch[1].toUpperCase();
  // Match plain key e.g. "SUPPORT-123"
  const plainMatch = link.match(/^([A-Z][A-Z0-9]+-\d+)$/i);
  if (plainMatch) return plainMatch[1].toUpperCase();
  return null;
}

// Map a Jira status name to a subtask status
function jiraStatusToSubtaskStatus(jiraStatus) {
  if (!jiraStatus) return null;
  const s = jiraStatus.toLowerCase();
  // Completed states
  if (s.includes("done") || s.includes("closed") || s.includes("resolved") ||
      s.includes("complete") || s.includes("deployed") || s.includes("live") ||
      s.includes("released")) return "Done";
  // Waiting / blocked states (check before "in progress" so "in uat"/"pending qa" land here)
  if (s.includes("wait") || s.includes("hold") || s.includes("pending") ||
      s.includes("block") || s.includes("response") || s.includes("uat") ||
      s.includes("qa") || s.includes("approval") || s.includes("ready for")) return "Waiting Response";
  // Active states
  if (s.includes("progress") || s.includes("review") || s.includes("triage") ||
      s.includes("development") || s.includes("dev") || s.includes("testing") ||
      s.includes("rework") || s.includes("build")) return "In Progress";
  // To-do states
  if (s.includes("to do") || s.includes("backlog") || s.includes("open") ||
      s.includes("new") || s.includes("selected")) return "To Start";
  // Unknown status - do NOT force to "To Start"; signal caller to leave status alone
  return null;
}


// ─── Recurring task helpers ──────────────────────────────────────────────────
const RECUR_OPTIONS = [
  { value: "", label: "No repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
];

function nextRecurDate(dueDate, recur) {
  if (!dueDate || !recur) return null;
  const d = new Date(dueDate);
  if (recur === "daily")     d.setDate(d.getDate() + 1);
  if (recur === "weekly")    d.setDate(d.getDate() + 7);
  if (recur === "biweekly")  d.setDate(d.getDate() + 14);
  if (recur === "monthly")   d.setMonth(d.getMonth() + 1);
  return d.toISOString().split("T")[0];
}


// ─── Project health RAG calculation ─────────────────────────────────────────
function getProjectRAG(project, tasks) {
  const allTasks = [
    ...(project.tasks || []),
    ...(project.modules || []).flatMap(m => m.tasks || []),
  ];
  const total    = allTasks.length;
  const complete = allTasks.filter(t => t.status === "Complete").length;
  const pct      = total > 0 ? Math.round(complete / total * 100) : 0;

  // Check for overdue tasks (finish date in past, not complete)
  const today = new Date().toISOString().split("T")[0];
  const overdue = allTasks.filter(t => t.finish && t.finish < today && t.status !== "Complete").length;

  // Check linked Jira tickets for SLA breaches
  const jiraKeys = allTasks.map(t => t.jiraKey).filter(Boolean);
  const slaBreaches = jiraKeys.length > 0
    ? (tasks || []).filter(t => jiraKeys.includes(t.jiraKey) && getSLAInfo(t)?.breached).length
    : 0;

  // RAG logic
  if (overdue > 0 || slaBreaches > 0 || pct < 30) return { rag: "R", label: "At Risk", colour: "#e32529" };
  if (pct < 70) return { rag: "A", label: "In Progress", colour: "#F1901E" };
  if (pct === 100) return { rag: "G", label: "Complete", colour: "#47ac4c" };
  return { rag: "G", label: "On Track", colour: "#47ac4c" };
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`;
}

function capitalise(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const TAG_COLOURS = [
  { color: "#47ac4c", border: "#47ac4c50", bg: "#47ac4c15" },
  { color: "#e32529", border: "#e3252950", bg: "#e3252915" },
  { color: "#F1901E", border: "#F1901E50", bg: "#F1901E15" },
  { color: "#D3D5D7", border: "#D3D5D760", bg: "#D3D5D720" },
  { color: "#8fa8b8", border: "#8fa8b850", bg: "#8fa8b815" },
];

function getTagColour(tag) {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLOURS[Math.abs(hash) % TAG_COLOURS.length];
}

function TagChip({ tag, onRemove }) {
  const c = getTagColour(tag);
  return (
    <span className="tag-chip" style={{ color: c.color, borderColor: c.border, background: c.bg }}>
      {tag}
      {onRemove && <span className="remove" onClick={onRemove}>{"✕"}</span>}
    </span>
  );
}

// ─── Outlook API ──────────────────────────────────────────────────────────────
// Lists to skip - these contain flagged emails which we manage ourselves
const SKIP_OUTLOOK_LISTS = ["flagged email", "flagged emails", "email flag"];

async function fetchOutlookTasks(config) {
  const { msToken } = config;
  if (!msToken) return [];
  try {
    const listsRes = await fetch("https://graph.microsoft.com/v1.0/me/todo/lists", {
      headers: { Authorization: `Bearer ${msToken}` },
    });
    if (!listsRes.ok) throw new Error("Outlook auth failed");
    const lists = await listsRes.json();
    if (!lists.value?.length) return [];

    // Skip flagged email lists to avoid duplicating email-converted tasks
    const filteredLists = lists.value.filter(list => {
      const name = list.displayName?.toLowerCase() || "";
      return !SKIP_OUTLOOK_LISTS.some(skip => name.includes(skip));
    });

    const allTasks = await Promise.all(
      filteredLists.map(async (list) => {
        const tasksRes = await fetch(
          `https://graph.microsoft.com/v1.0/me/todo/lists/${list.id}/tasks?$top=50&$filter=status ne 'completed'`,
          { headers: { Authorization: `Bearer ${msToken}` } }
        );
        const tasks = await tasksRes.json();
        return (tasks.value || []).map((t) => ({
          id: `outlook-${t.id}`,
          title: t.title,
          source: "outlook",
          status: t.status === "completed" ? "done" : "open",
          done: t.status === "completed",
          priority: t.importance === "high" ? "high" : t.importance === "low" ? "low" : "medium",
          due: t.dueDateTime?.dateTime?.split("T")[0] || null,
          description: t.body?.content || "",
          rawId: t.id,
          listId: list.id,
          listName: list.displayName,
        }));
      })
    );
    return allTasks.flat();
  } catch (e) {
    return { error: e.message };
  }
}

async function fetchOutlookEmails(config) {
  const { msToken } = config;
  if (!msToken) return [];
  try {
    const folderRes = await fetch(
      "https://graph.microsoft.com/v1.0/me/mailFolders/inbox/messages?$top=20&$filter=isRead eq false&$select=id,subject,from,receivedDateTime,bodyPreview,flag&$orderby=receivedDateTime desc",
      { headers: { Authorization: `Bearer ${msToken}` } }
    );
    if (!folderRes.ok) throw new Error("Outlook emails failed");
    const data = await folderRes.json();
    return data.value || [];
  } catch (e) {
    return [];
  }
}

async function fetchFlaggedEmails(config) {
  const { msToken } = config;
  if (!msToken) return [];
  try {
    // $filter on flag/flagStatus cannot be combined with $orderby on /me/messages - causes 400.
    // Search inbox and sentitems folders separately (most flagged emails live here).
    const folders = ["inbox", "sentitems"];
    const results = await Promise.all(folders.map(async (folder) => {
      try {
        const res = await fetch(
          `https://graph.microsoft.com/v1.0/me/mailFolders/${folder}/messages?$top=50&$filter=flag/flagStatus eq 'flagged'&$select=id,subject,from,receivedDateTime,bodyPreview,flag`,
          { headers: { Authorization: `Bearer ${msToken}` } }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.value || [];
      } catch { return []; }
    }));
    // Merge, deduplicate by id, sort by receivedDateTime descending
    const seen = new Set();
    const all = results.flat().filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    }).sort((a, b) => new Date(b.receivedDateTime) - new Date(a.receivedDateTime));
    return mapFlaggedEmails(all);
  } catch (e) {
    return [];
  }
}

function mapFlaggedEmails(emails) {
  return emails.map(email => ({
    id: `flagged-${email.id}`,
    title: email.subject || "(No subject)",
    source: "outlook",
    status: "open",
    done: false,
    priority: "medium",
    due: null,
    description: email.bodyPreview || "",
    senderName: email.from?.emailAddress?.name || email.from?.emailAddress?.address || "",
    senderEmail: email.from?.emailAddress?.address || "",
    receivedDate: email.receivedDateTime ? new Date(email.receivedDateTime).toLocaleDateString("en-GB") : "",
    isFlaggedEmail: true,
    rawEmailId: email.id,
  }));
}

async function createOutlookTask(config, title, description = "") {
  const { msToken } = config;
  const listsRes = await fetch("https://graph.microsoft.com/v1.0/me/todo/lists", {
    headers: { Authorization: `Bearer ${msToken}` },
  });
  const lists = await listsRes.json();
  const listId = lists.value?.[0]?.id;
  if (!listId) throw new Error("No task list found");
  const res = await fetch(`https://graph.microsoft.com/v1.0/me/todo/lists/${listId}/tasks`, {
    method: "POST",
    headers: { Authorization: `Bearer ${msToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ title, body: { content: description, contentType: "text" } }),
  });
  if (!res.ok) throw new Error("Failed to create task");
  return res.json();
}

async function completeOutlookTask(config, listId, taskId) {
  const { msToken } = config;
  await fetch(`https://graph.microsoft.com/v1.0/me/todo/lists/${listId}/tasks/${taskId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${msToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ status: "completed" }),
  });
}

// ─── Jira API (via proxy) ─────────────────────────────────────────────────────
// Custom field IDs for waiting states
// Full Affected Clients list - matches Jira custom field options
const JIRA_CLIENT_LIST = [
  "All Clients",
  "54NORTH HOMES","ABBEY HEALTHCARE","AGINCARE","ANCHOR","ASPIRE HOUSING LTD",
  "BE ONE HOMES","BELIEVE HOUSING LTD","BERNESLAI HOMES","BEYOND HOUSING",
  "BRISTOL CITY COUNCIL","BROADLAND HOUSING GROUP","BROMSGROVE DISTRICT HOUSING TRUST",
  "BUPA","BUPA - CARE HOMES","BUPA - CARE SERVICES","BUPA - CARE SERVICES - MAINTENANCE",
  "BUPA - CORPORATE","BUPA - CROMWELL HOSPITAL","BUPA - DENTAL CARE","BUPA - HEALTH & SAFETY",
  "BUPA - RICHMOND CARE VILLAGES","BUPA - SUPPLIER","BUPA NEW ZEALAND","C365CLOUD",
  "CARTREFI CONWY","CENTRAL ENGLAND CO-OP","CENTRAL STATISTICS OFFICE CSO","CHRISTIAN ACTION",
  "COLCHESTER BOROUGH HOMES LTD","COMMUNITY GATEWAY","COMMUNITY HOUSING","CO-OPERATIVE GROUP LIMITED",
  "COVENTRY CITY COUNCIL","CYSON CYMRU","DONCASTER COUNCIL","DUDLEY METROPOLITAN BOROUGH COUNCIL",
  "DWP DEPARTMENT FOR WORK AND PENSION","EAST CHESHIRE NHS","EAST HERTS COUNCIL","EAST OF ENGLAND CO-OP",
  "ELYSIUM HEALTHCARE","EPPING FOREST DISTRICT COUNCIL","FLAGSHIP GROUP",
  "FOREIGN, COMMONWEALTH & DEVELOPMENT OFFICE","FRAMEWORK HOUSING GROUP","GATESHEAD COUNCIL",
  "GATEWAY HOUSING ASSOCIATION","GREENSQUAREACCORD","GROSVENOR LTD","HABINTEG","HARINGEY COUNCIL",
  "HARROGATE NHS","HARROW COUNCIL","HC-ONE LTD","HESLEY GROUP","HILLINGDON COUNCIL","HOME GROUP LTD",
  "HONEYCOMB GROUP","HOUSING FOR WOMEN","IDS","INCLUSION HOUSING","INCOMMUNITIES","IRWELL VALLEY",
  "ISLINGTON & SHOREDITCH HOUSING ASSOCIATION","ISLINGTON COUNCIL","JOHNNIE JOHNSON HOUSING",
  "KARBON HOMES","LIVV HOUSING GROUP","MAGENTA LIVING","MARSTON'S PLC","MCVITIE'S","MIDLAND HEART",
  "NORTHAMPTONSHIRE PARTNERSHIP HOMES","NORTHERN POWERGRID NPG","NOTTINGHAM CITY COUNCIL",
  "NOTTINGHAMSHIRE NHS","NRS","NTW NHS","OCS GROUP UK","ONE MANCHESTER","ONE VISION HOUSING",
  "ONWARD HOMES","ORBIT","PARTNERS FOUNDATION","PLACES FOR PEOPLE","PROGRESS HOUSING GROUP",
  "RACING HOMES","RADIUS HOUSING","REDBRIDGE LONDON BOROUGH COUNCIL","RIVERSIDE",
  "ROCHDALE BOROUGHWIDE HOUSING","ROYAL BRITISH VETERANS ENTERPRISE","SAINSBURY'S","SANCTUARY",
  "SANDWELL COUNCIL","SELLAFIELD LTD","SENTINEL","SHAL HOUSING","SHROPSHIRE TOWNS AND RURAL HOUSING",
  "SOHA HOUSING","SOLIHULL COMMUNITY HOUSING","SOMERSET COUNCIL","SOUTH TEES NHS",
  "SOUTH TYNESIDE  & SUNDERLAND NHS FT","SOUTHAMPTON CITY COUNCIL","SOVEREIGN NETWORK GROUP",
  "SPRING HOUSING","ST LEGER HOMES","STOCKPORT HOMES","THIRTEEN","TORUS","TRENT & DOVE HOUSING",
  "UNITED UTILITIES GROUP PLC","UNITY HOUSING","WALSALL HOUSING GROUP","WATMOS",
  "WEST LANCASHIRE BOROUGH COUNCIL","WIGAN COUNCIL","WOKING BOROUGH COUNCIL","WOLVERHAMPTON HOMES",
  "WOMENS PIONEER HOUSING","YOUR HOUSING GROUP (YHG)","UNKNOWN",
];

const JIRA_FIELD_SDM = "customfield_10208";
const JIRA_FIELD_PC = "customfield_10211";
const JIRA_FIELD_AFFECTED_CLIENTS = "customfield_10435"; // "Affected Clients" field
// Jira Queue page custom fields (confirmed with Phil / verified at build; editable in Config later)
const JQ_FIELDS = {
  sdm: "customfield_10208",            // SDM (user picker)
  pc: "customfield_10211",             // Project Coordinator (user picker)
  affectedClients: "customfield_10435",// Affected Clients (multi)
  reason: "customfield_10464",         // Reason for SDM/PC Review (shared SDM+PC)
  propertyRef: "customfield_10097",    // Property Reference (MIF)
  mifType: "customfield_12398",        // MIF Type (MIF)
  formName: "customfield_12399",       // Form Name (MIF)
  complianceModule: "customfield_12397", // Compliance Module (MIF)
};
// Canonical status names (one real status each; email wording varied)
const JQ_STATUS = { sdm: "Waiting on SDM", pc: "Waiting on PC", edSignoff: "Waiting for Sign Off", onHold: "On Hold" };
// People to hide from the SDM/PC picker lists (by display name, case-insensitive).
const JQ_EXCLUDED_PEOPLE = ["bailey dawson"];
const jqExcluded = (name) => JQ_EXCLUDED_PEOPLE.includes(String(name || "").trim().toLowerCase());
const JIRA_WAITING_STATUSES = ["Waiting on SDM", "Waiting on PC"];

function extractJiraDescription(desc) {
  if (!desc || !desc.content) return "";
  const lines = [];
  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (node.type === "text") lines.push(node.text);
      else if (node.type === "hardBreak") lines.push("\n");
      else if (node.content) walk(node.content);
    }
  };
  walk(desc.content);
  return lines.join("").trim();
}

// Extract plain text from a Jira comment ADF body
function extractJiraCommentText(body) {
  if (!body || !body.content) return "";
  const lines = [];
  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (node.type === "text") {
        // Skip @mention nodes - they're just noise (accountIds)
        lines.push(node.text);
      } else if (node.type === "mention") {
        lines.push(`@${node.attrs?.text || "user"}`);
      } else if (node.type === "hardBreak") {
        lines.push(" ");
      } else if (node.content) {
        walk(node.content);
      }
    }
  };
  walk(body.content);
  return lines.join("").replace(/\s+/g, " ").trim();
}

async function fetchJiraCurrentUserAccountId(config) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  if (!jiraUrl || !jiraEmail || !jiraToken) return null;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  try {
    const res = await fetch(proxyUrl(`${baseUrl}/rest/api/3/myself`), {
      headers: { Authorization: `Basic ${auth}`, Accept: "application/json" },
    });
    if (res.ok) { const me = await res.json(); return me.accountId || null; }
  } catch {}
  return null;
}

async function fetchJiraIssues(config) {
  const { jiraUrl, jiraEmail, jiraToken, jiraProject } = config;
  if (!jiraUrl || !jiraEmail || !jiraToken) return [];

  const auth    = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");

  // ── Step 1: Resolve who is logged in ──────────────────────────────────────
  // We use accountId explicitly in every query. currentUser() is unreliable for
  // custom user-picker fields (SDM/PC) and can cross-contaminate results between
  // users sharing a Jira instance. If we cannot confirm identity, abort entirely.
  let accountId = null;
  try {
    const r = await fetch(proxyUrl(`${baseUrl}/rest/api/3/myself`), {
      headers: { Authorization: `Basic ${auth}`, Accept: "application/json" },
    });
    if (r.ok) {
      const me = await r.json();
      accountId = me.accountId || null;
      if (me.displayName) {
        try { localStorage.setItem("taskflow_jira_displayname", me.displayName); } catch {}
      }
      try {
        if (accountId) localStorage.setItem("taskflow_jira_accountid", accountId);
        if (me.emailAddress) localStorage.setItem("taskflow_jira_email", me.emailAddress);
      } catch {}
      // Register in the shared user registry now that identity is known, so roadmap
      // owners can find this person in the share picker. (Doing it here rather than only
      // on mount avoids a race where the registry write fires before Jira has resolved.)
      try {
        const regBase = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:3001" : "";
        fetch(`${regBase}/api/users/register`, { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountId: accountId || "", name: me.displayName || "", email: me.emailAddress || "" }) }).catch(() => {});
      } catch {}
      console.log("[Jira] accountId resolved:", accountId, me.displayName);
    } else {
      console.error("[Jira] /myself failed:", r.status, "- aborting sync");
      return [];
    }
  } catch (e) {
    console.error("[Jira] /myself threw:", e.message, "- aborting sync");
    return [];
  }

  if (!accountId) {
    console.error("[Jira] accountId is null - aborting sync");
    return [];
  }

  // ── Step 2: Build the four JQL queries ────────────────────────────────────
  const projectPrefix = jiraProject ? `project="${jiraProject}" AND ` : "";
  const notDone       = "statusCategory != Done AND issuetype != Epic";
  // For block/status views, do NOT filter by statusCategory - some statuses
  // (e.g. ED Awaiting Sign Off, Waiting External) are mapped to the Done category in Jira
  // but still represent actionable work items that need to appear in these views.
  const notEpic       = "issuetype != Epic";

  // Q1 - Assigned to me
  const jqlAssigned = `${projectPrefix}assignee="${accountId}" AND ${notDone} ORDER BY updated DESC`;

  // Q2 - @mentioned in a comment
  // ScriptRunner issueFunction not available on all Jira instances - use comment ~ only
  const jqlMentionVariants = [
    `comment ~ "${accountId}" AND ${notDone} ORDER BY updated DESC`,
  ];

  // Q3 - SDM custom field matches me
  const jqlSDM = `cf[10208] = "${accountId}" AND status = "Waiting on SDM" AND ${notDone} ORDER BY updated DESC`;

  // Q4 - Project Co-ordinator custom field matches me
  const jqlPC  = `cf[10211] = "${accountId}" AND status = "Waiting on PC" AND ${notDone} ORDER BY updated DESC`;

  // Q5–Q8 - SDM or PC on issues in specific blocking statuses
  // ── SUPPORT direct statuses (shown on the SUPPORT card itself) ──────────────
  const SUPPORT_STATUSES = [
    "Triage", "Triage Waiting Information", "Triage Response", "Tier 3 Triage",
    "New", "In Progress", "QA", "QA Completed", "On Hold", "Response", "Reopened",
    "With Development Team",
  ];
  // ── Dev card statuses (shown when SUPPORT card = "With Development Team") ───
  // These appear on the linked DEVOPS/ED/TEMPL/TEMP2A card
  const DEV_STATUSES = [
    "For Review", "For Review - In Progress", "More Info Required", "Not Approved",
    "Not Required", "On Hold", "Blocked", "Backlog", "Sprint Assigned", "To Do",
    "In Progress", "QA", "Rework", "Response", "Waiting Internal", "Waiting External",
    "To Publish", "Waiting for Sign Off",
  ];
  // Legacy statuses kept for backward compat
  const BLOCK_STATUSES = [...new Set([...SUPPORT_STATUSES, ...DEV_STATUSES,
    "Ed on Hold", "Awaiting Internal", "In Query", "In Query External", "ED Awaiting Sign Off"])];

  // Query SUPPORT cards by direct status (assigned to me as SDM or PC)
  const supportStatusQueries = SUPPORT_STATUSES.map(s => ({
    status: s,
    type: "support",
    jql: `assignee = "${accountId}" AND status = "${s}" AND ${notEpic} ORDER BY updated DESC`,
  }));

  // Also query cards where I'm SDM/PC for some key statuses
  const blockQueries = [
    ...["Blocked", "On Hold", "Ed on Hold", "Awaiting Internal", "In Query",
        "Waiting Internal", "Waiting External", "In Query External",
        "Waiting for Sign Off", "ED Awaiting Sign Off"].map(s => ({
      status: s,
      type: "block",
      jql: `(cf[10208] = "${accountId}" OR cf[10211] = "${accountId}") AND status = "${s}" AND ${notEpic} ORDER BY updated DESC`,
    })),
  ];

  // ── Step 3: Execute queries ────────────────────────────────────────────────
  const fields = [
    "summary","status","priority","duedate","description","issuetype",
    "assignee","updated","comment",
    JIRA_FIELD_SDM, JIRA_FIELD_PC, JIRA_FIELD_AFFECTED_CLIENTS,
    "customfield_11129","customfield_11130","customfield_11131",
    "customfield_10207", // "Ticket created" checkbox - Yes when dev card raised
    "issuelinks",        // needed to find linked DEVOPS/ED/TEMPL/TEMP2A cards
  ].join(",");

  const runJql = async (jql, label) => {
    try {
      // Use GET /rest/api/3/search with jql as an encoded query param.
      // Build the query string manually - encode only the JQL value, keep other
      // params (fields, maxResults) as plain strings so the proxy passes them through cleanly.
      const target = `${baseUrl}/rest/api/3/search/jql?jql=${encodeURIComponent(jql)}&fields=${fields}&maxResults=50&startAt=0`;
      const r = await fetch(proxyUrl(target), {
        headers: { Authorization: `Basic ${auth}`, Accept: "application/json" },
      });
      if (!r.ok) {
        const txt = await r.text();
        console.warn(`[Jira] ${label} query failed ${r.status}:`, txt.substring(0, 120));
        return [];
      }
      const data = await r.json();
      const issues = data.issues || [];
      console.log(`[Jira] ${label}: ${issues.length} issues`);
      return issues;
    } catch (e) {
      console.warn(`[Jira] ${label} threw:`, e.message);
      return [];
    }
  };

  // Run Q1, Q3, Q4 + blocking status queries in parallel; Q2 mention tries variants in sequence
  const [assignedIssues, sdmIssues, pcIssues, ...blockIssueGroups] = await Promise.all([
    runJql(jqlAssigned, "Assigned"),
    runJql(jqlSDM,      "SDM"),
    runJql(jqlPC,       "PC"),
    ...blockQueries.map(q => runJql(q.jql, q.status)),
  ]);
  // Map each blocking status to its result set
  const blockIssuesByStatus = {};
  BLOCK_STATUSES.forEach((s, i) => { blockIssuesByStatus[s] = blockIssueGroups[i] || []; });
  const allBlockIssues = Object.values(blockIssuesByStatus).flat();

  // ── Query SUPPORT cards by direct status (assigned to me) ──────────────────
  const supportStatusResults = await Promise.all(
    supportStatusQueries.map(q => runJql(q.jql, `Support-${q.status}`))
  );
  const supportIssuesByStatus = {};
  SUPPORT_STATUSES.forEach((s, i) => { supportIssuesByStatus[s] = supportStatusResults[i] || []; });

  // ── For "With Development Team" cards, fetch linked dev card statuses ────────
  // customfield_10207 = "Yes" confirms a dev card was raised.
  // The linked card key comes from issuelinks (outward/inward).
  const devCardStatusMap = {}; // SUPPORT key → dev card status string
  // Collect ALL "With Development Team" issues - from both direct status queries
  // AND from assigned/mentioned queries (so devCardStatus is populated for all of them)
  const withDevIssues = [
    ...(supportIssuesByStatus["With Development Team"] || []),
    ...assignedIssues.filter(i => i.fields.status?.name?.toLowerCase() === "with development team"),
    ...sdmIssues.filter(i => i.fields.status?.name?.toLowerCase() === "with development team"),
    ...pcIssues.filter(i => i.fields.status?.name?.toLowerCase() === "with development team"),
  ].filter((i, idx, arr) => arr.findIndex(x => x.key === i.key) === idx); // dedupe

  if (withDevIssues.length > 0) {
    const devKeySet = new Set();
    const supportToDevKey = {}; // SUPPORT key → dev card key
    withDevIssues.forEach(issue => {
      const links = issue.fields.issuelinks || [];
      links.forEach(lnk => {
        const linked = lnk.outwardIssue || lnk.inwardIssue;
        if (!linked) return;
        const k = linked.key;
        // DEVOPS, ED, TEMPL, TEMP2A, CLOUDDEV cards
        if (/^(DEVOPS|ED|TEMPL|TEMP2A|CLOUDDEV)-\d+/.test(k)) {
          devKeySet.add(k);
          supportToDevKey[issue.key] = k;
        }
      });
    });
    if (devKeySet.size > 0) {
      try {
        const devJql = "issueKey in (" + [...devKeySet].map(k => '"' + k + '"').join(",") + ")";
        const devR = await fetch(
          proxyUrl(`${baseUrl}/rest/api/3/search/jql?jql=${encodeURIComponent(devJql)}&fields=status&maxResults=100`),
          { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
        );
        if (devR.ok) {
          const devData = await devR.json();
          const devStatusByKey = {};
          (devData.issues || []).forEach(i => { devStatusByKey[i.key] = i.fields.status?.name || ""; });
          Object.entries(supportToDevKey).forEach(([supKey, devKey]) => {
            devCardStatusMap[supKey] = devStatusByKey[devKey] || "";
          });
          console.log("[DevCard] mapped", Object.keys(devCardStatusMap).length, "SUPPORT→dev statuses");
        }
      } catch (e) { console.warn("[Jira] dev card status fetch error:", e.message); }
    }
  }

  // Mention: try variants until one returns results
  let mentionedIssues = [];
  for (const [i, jql] of jqlMentionVariants.entries()) {
    const results = await runJql(jql, `Mention-v${i + 1}`);
    if (results.length > 0) { mentionedIssues = results; break; }
  }

  // ── Step 4: Filter mention results to latest-comment-only ─────────────────
  // Only surface a mentioned issue if the MOST RECENT comment contains our accountId.
  // This prevents old @mentions from cluttering the list.
  if (mentionedIssues.length > 0) {
    const filtered = await Promise.all(mentionedIssues.map(async (issue) => {
      try {
        // Use the comment field already fetched if present (maxResults=1 newest)
        const comments = issue.fields.comment?.comments || [];
        if (comments.length > 0) {
          const latest = comments[comments.length - 1];
          const body   = JSON.stringify(latest.body || "");
          return body.includes(accountId) ? issue : null;
        }
        // Fallback: fetch comments separately
        const cr = await fetch(
          proxyUrl(`${baseUrl}/rest/api/3/issue/${issue.key}/comment?maxResults=1&orderBy=-created`),
          { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
        );
        if (!cr.ok) return null;
        const cd   = await cr.json();
        const last = cd.comments?.[0];
        if (!last) return null;
        return JSON.stringify(last.body || "").includes(accountId) ? issue : null;
      } catch { return null; }
    }));
    mentionedIssues = filtered.filter(Boolean);
    console.log(`[Jira] Mention after latest-comment filter: ${mentionedIssues.length} issues`);
  }

  // ── Step 5: Build key sets for accurate flag stamping ─────────────────────
  const assignedKeys  = new Set(assignedIssues.map(i => i.key));
  const mentionedKeys = new Set(mentionedIssues.map(i => i.key));
  const sdmKeys       = new Set(sdmIssues.map(i => i.key));
  const pcKeys        = new Set(pcIssues.map(i => i.key));
  // Block status key sets - one per status
  const blockKeysByStatus = {};
  BLOCK_STATUSES.forEach(s => { blockKeysByStatus[s] = new Set((blockIssuesByStatus[s] || []).map(i => i.key)); });

  // ── Step 6: Merge, deduplicate, map to task objects ───────────────────────
  const seen = new Set();
  const allSupportStatusIssues = Object.values(supportIssuesByStatus).flat();
  const allIssues = [
    ...assignedIssues, ...mentionedIssues, ...sdmIssues, ...pcIssues,
    ...allBlockIssues, ...allSupportStatusIssues,
  ].filter(i => { if (seen.has(i.key)) return false; seen.add(i.key); return true; });

  return allIssues.map((i) => {
    const sdmField = i.fields[JIRA_FIELD_SDM];
    const pcField  = i.fields[JIRA_FIELD_PC];
    const sdmName  = sdmField?.displayName || sdmField?.name || null;
    const pcName   = pcField?.displayName  || pcField?.name  || null;
    const status   = i.fields.status.name;

    // Flags - derived purely from fresh API data, never persisted from prior sync
    const isAssigned  = assignedKeys.has(i.key);
    const isMentioned = mentionedKeys.has(i.key);
    const isSDM       = sdmKeys.has(i.key);
    const isPC        = pcKeys.has(i.key);

    return {
      id:           `jira-${i.id}`,
      title:        `[${i.key}] ${i.fields.summary}`,
      source:       "jira",
      jiraStatus:   status,
      status:       (() => {
        const s = status.toLowerCase();
        if (s.includes("done") || s.includes("closed") || s.includes("resolved") || s.includes("complete")) return "Done";
        if (s.includes("progress") || s.includes("review") || s.includes("selected") || s.includes("development") || s.includes("testing")) return "In Progress";
        if (s.includes("block") || s.includes("impediment")) return "In Progress";
        if (s === "waiting on sdm" || s === "waiting on pc") return "Waiting Response";
        if (s === "with development team") return "Waiting Response";
        if (s.includes("wait") || s.includes("hold") || s.includes("pending")) return "Waiting Response";
        return "To Start";
      })(),
      done:         false, // done state is managed locally only - never from Jira status
      priority:     (i.fields.priority?.name || "Medium").toLowerCase(),
      jiraPriority: i.fields.priority?.name || null,
      due:          i.fields.duedate || null,
      description:  extractJiraDescription(i.fields.description),
      jiraKey:      i.key,
      jiraUrl:      `${baseUrl}/browse/${i.key}`,
      jiraUpdated:  i.fields.updated || null,
      assignee:     i.fields.assignee?.displayName || null,
      sdm:          sdmName,
      pc:           pcName,
      // Categorisation flags - used by sidebar sub-filters
      assignedToMe: isAssigned,
      mentioned:    isMentioned,
      isSDM:        isSDM,
      isPC:         isPC,
      // blockStatus - set if this issue was pulled via a blocking status query (SDM or PC)
      blockStatus:  BLOCK_STATUSES.find(s => blockKeysByStatus[s].has(i.key)) || null,
      // supportStatus - set if this SUPPORT card was pulled via a direct status query (assigned to me)
      supportStatus: SUPPORT_STATUSES.find(s => (supportIssuesByStatus[s] || []).some(x => x.key === i.key)) || null,
      // devCardStatus - for "With Development Team" cards, the status of the linked dev card
      devCardStatus: devCardStatusMap[i.key] || null,
      // waitingOn/waitingRole - only flag on exact Jira status match
      waitingOn:    (isSDM && status === "Waiting on SDM") ? sdmName
                  : (isPC  && status === "Waiting on PC")  ? pcName : null,
      waitingRole:  (isSDM && status === "Waiting on SDM") ? "SDM"
                  : (isPC  && status === "Waiting on PC")  ? "PC"  : null,
      jiraEffort: (() => { const v = i.fields.customfield_11129; if (v == null) return null; if (typeof v === "object") return v.value ?? v.name ?? JSON.stringify(v); return v; })(),
      jiraValue:  (() => { const v = i.fields.customfield_11130; if (v == null) return null; if (typeof v === "object") return v.value ?? v.name ?? JSON.stringify(v); return v; })(),
      jiraScore:  (() => { const v = i.fields.customfield_11131; if (v == null) return null; if (typeof v === "object") return v.value ?? v.name ?? JSON.stringify(v); return v; })(),
      affectedClient: (() => {
        const v = i.fields[JIRA_FIELD_AFFECTED_CLIENTS];
        if (v == null) return null;
        if (Array.isArray(v)) return v.map(x => typeof x === "object" ? (x.value ?? x.name ?? x) : x).join(", ");
        if (typeof v === "object") return v.value ?? v.name ?? null;
        return String(v);
      })(),
      // Latest comment - extracted from the comment field fetched with the issue
      latestComment: (() => {
        const comments = i.fields.comment?.comments || [];
        if (comments.length === 0) return null;
        const last = comments[comments.length - 1];
        const text = extractJiraCommentText(last.body);
        if (!text) return null;
        return {
          author: last.author?.displayName || "Unknown",
          text,
          date: last.created ? new Date(last.created).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "",
        };
      })(),
    };
  });
}


async function fetchJiraTransitions(config, issueKey) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  const res = await fetch(proxyUrl(`${baseUrl}/rest/api/2/issue/${issueKey}/transitions`), {
    headers: { Authorization: `Basic ${auth}`, Accept: "application/json" },
  });
  const data = await res.json();
  return data.transitions || [];
}


async function transitionJiraIssue(config, issueKey, transitionNameOrId, isId = false) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  let transitionId = transitionNameOrId;
  if (!isId) {
    const transRes = await fetch(proxyUrl(`${baseUrl}/rest/api/2/issue/${issueKey}/transitions`), {
      headers: { Authorization: `Basic ${auth}`, Accept: "application/json" },
    });
    const trans = await transRes.json();
    const t = trans.transitions?.find((x) => x.name.toLowerCase().includes(transitionNameOrId.toLowerCase()));
    if (!t) throw new Error(`No transition "${transitionNameOrId}" found`);
    transitionId = t.id;
  }
  await fetch(proxyUrl(`${baseUrl}/rest/api/2/issue/${issueKey}/transitions`), {
    method: "POST",
    headers: { Authorization: `Basic ${auth}`, "Content-Type": "application/json" },
    body: JSON.stringify({ transition: { id: transitionId } }),
  });
}

async function fetchJiraComments(config, issueKey) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  try {
    const res = await fetch(
      proxyUrl(`${baseUrl}/rest/api/3/issue/${issueKey}/comment?maxResults=50&orderBy=-created`),
      { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data.comments || []).map(c => ({
      id: c.id,
      author: c.author?.displayName || "Unknown",
      date: c.created ? new Date(c.created).toLocaleString("en-GB", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "",
      text: (() => {
        if (!c.body?.content) return "";
        const lines = [];
        const walk = (nodes) => { for (const n of nodes || []) { if (n.type === "text") lines.push(n.text); else if (n.type === "hardBreak") lines.push("\n"); else if (n.content) walk(n.content); } };
        walk(c.body.content);
        return lines.join("").trim();
      })(),
    }));
  } catch { return []; }
}

async function updateJiraIssue(config, issueKey, fields) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  const res = await fetch(proxyUrl(`${baseUrl}/rest/api/3/issue/${issueKey}`), {
    method: "PUT",
    headers: { Authorization: `Basic ${auth}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ fields }),
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.errors ? Object.values(err.errors).join(", ") : `Update failed (${res.status})`);
  }
}

async function addJiraComment(config, issueKey, commentOrSegments) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  // Accept either a plain string or an array of ADF segments (text + mention nodes)
  const body = Array.isArray(commentOrSegments)
    ? buildAdfBody(commentOrSegments)
    : { type: "doc", version: 1, content: [{ type: "paragraph", content: [{ type: "text", text: commentOrSegments }] }] };
  const res = await fetch(proxyUrl(`${baseUrl}/rest/api/3/issue/${issueKey}/comment`), {
    method: "POST",
    headers: { Authorization: `Basic ${auth}`, Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error("Failed to post comment to Jira");
}


// Search Jira users by query string - used for @ mention picker
async function searchJiraUsers(config, query) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  if (!jiraUrl || !jiraEmail || !jiraToken || !query.trim()) return [];
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
  try {
    const res = await fetch(
      proxyUrl(`${baseUrl}/rest/api/3/user/search?query=${encodeURIComponent(query)}&maxResults=8`),
      { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (Array.isArray(data) ? data : []).filter(u => u.accountId && u.displayName && u.accountType !== "app");
  } catch { return []; }
}

// Build ADF document body from segments - supports text and mention nodes
// segments: array of { type: "text", text } | { type: "mention", accountId, displayName }
function buildAdfBody(segments) {
  const content = [];
  let buffer = "";
  const flushBuffer = () => {
    if (buffer) {
      // Split on newlines to create paragraph breaks
      const lines = buffer.split("\n");
      lines.forEach((line, i) => {
        if (i > 0) content.push({ type: "hardBreak" });
        if (line) content.push({ type: "text", text: line });
      });
      buffer = "";
    }
  };
  for (const seg of segments) {
    if (seg.type === "text") {
      buffer += seg.text;
    } else if (seg.type === "mention") {
      flushBuffer();
      content.push({
        type: "mention",
        attrs: { id: seg.accountId, text: `@${seg.displayName}`, accessLevel: "" },
      });
    }
  }
  flushBuffer();
  return { type: "doc", version: 1, content: [{ type: "paragraph", content }] };
}

// Fetch allowed values for a Jira custom field (select/multi-select)
async function fetchJiraFieldOptions(config, fieldId) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  if (!jiraUrl || !jiraEmail || !jiraToken) return JIRA_CLIENT_LIST;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");

  // Start with the full hardcoded list as the base - this guarantees the authoritative
  // Affected Clients set is always present. Then supplement with any new entries from
  // Jira's field options that are not already in the list.
  const base = new Set(JIRA_CLIENT_LIST);

  try {
    // Try the field context options endpoint first
    const res = await fetch(
      proxyUrl(`${baseUrl}/rest/api/3/customField/${fieldId}/option?maxResults=100&startAt=0`),
      { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
    );
    if (res.ok) {
      const data = await res.json();
      (data.values || []).forEach(v => {
        const label = v.value || v.optionValue || v.name;
        if (label) base.add(label.trim());
      });
    } else {
      // Fallback: derive from issues in search results
      const r2 = await fetch(
        proxyUrl(`${baseUrl}/rest/api/3/search/jql?jql=cf[10435] is not EMPTY&fields=${fieldId}&maxResults=100`),
        { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
      );
      if (r2.ok) {
        const d2 = await r2.json();
        (d2.issues || []).forEach(i => {
          const v = i.fields[fieldId];
          if (!v) return;
          const vals = Array.isArray(v) ? v : [v];
          vals.forEach(x => {
            const label = typeof x === "object" ? (x.value || x.name) : x;
            if (label) base.add(label.trim());
          });
        });
      }
    }
  } catch { /* silently fall back to hardcoded list */ }

  return [...base].sort();
}

async function addJiraAttachment(config, issueKey, file, onProgress) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");

  // Read file as base64
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  if (onProgress) onProgress(10);

  // Send to proxy's dedicated attach endpoint as JSON
  // Use /api/proxy?attach=1 to piggyback on the existing working ARR route
  const attachUrl = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "http://localhost:3001/api/attach"
    : "/api/proxy?attach=1";

  const res = await fetch(attachUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jiraUrl: baseUrl,
      auth,
      issueKey,
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      base64,
    }),
  });

  if (onProgress) onProgress(90);

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `Attachment failed (${res.status})`);
  }

  if (onProgress) onProgress(100);
  return res.json();
}

async function createJiraIssue(config, summary, description, affectedClient, category, ticketType) {
  const { jiraUrl, jiraEmail, jiraToken } = config;
  if (!jiraUrl || !jiraEmail || !jiraToken) {
    throw new Error("Jira not fully configured - ensure URL, email and token are set in Config");
  }
  const auth = btoa(`${jiraEmail}:${jiraToken}`);
  const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");

  const CREATE_PROJECT = "SUPPORT";
  const CREATE_STATUS  = "Triage";

  // Build description combining all fields
  const fullDescription = [
    description,
    affectedClient ? "Affected Client: " + affectedClient : "",
    category       ? "Category: " + category : "",
    ticketType     ? "Ticket Type: " + ticketType : "",
  ].filter(Boolean).join("\n\n");

  const baseFields = {
    project: { key: CREATE_PROJECT },
    summary,
    issuetype: { name: "General request" },
    description: {
      type: "doc", version: 1,
      content: [{ type: "paragraph", content: [{ type: "text", text: fullDescription }] }]
    },
  };

  // Try with Affected Clients custom field first, fall back to without if rejected
  const attempts = [
    // Attempt 1: with custom field
    { ...baseFields, ...(affectedClient ? { [JIRA_FIELD_AFFECTED_CLIENTS]: [{ value: affectedClient }] } : {}) },
    // Attempt 2: without custom field (if field value not valid in this Jira instance)
    baseFields,
  ];

  let created = null;
  let lastError = null;

  for (const fields of attempts) {
    const res = await fetch(proxyUrl(`${baseUrl}/rest/api/3/issue`), {
      method: "POST",
      headers: { Authorization: `Basic ${auth}`, Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ fields }),
    });
    if (res.ok) {
      created = await res.json();
      break;
    }
    // Read error but continue to next attempt
    const err = await res.json().catch(() => ({}));
    lastError = err?.errors ? Object.values(err.errors).join(", ") : `Failed to create Jira issue (${res.status})`;
    console.warn("[Jira] Create attempt failed:", lastError);
    // Only retry on 400/403 - other errors are fatal
    if (res.status !== 400 && res.status !== 403) break;
  }

  if (!created) throw new Error(lastError || "Failed to create Jira issue");

  // Transition to Triage status
  try {
    const transRes = await fetch(proxyUrl(`${baseUrl}/rest/api/2/issue/${created.key}/transitions`), {
      headers: { Authorization: `Basic ${auth}`, Accept: "application/json" },
    });
    if (transRes.ok) {
      const transData = await transRes.json();
      const triageTrans = transData.transitions?.find(t => t.name.toLowerCase() === CREATE_STATUS.toLowerCase());
      if (triageTrans) {
        await fetch(proxyUrl(`${baseUrl}/rest/api/2/issue/${created.key}/transitions`), {
          method: "POST",
          headers: { Authorization: `Basic ${auth}`, Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ transition: { id: triageTrans.id } }),
        });
      }
    }
  } catch (e) {
    console.warn("[Jira] Could not transition to Triage:", e.message);
  }

  return created;
}

// Check if email tasks/subtasks have been replied to
// Returns a map of { [emailId]: { replied: true, repliedAt: isoString } }
// Known domain acronyms that should always be fully uppercase
const DOMAIN_ACRONYMS = new Set(["nhs", "hmrc", "dvla", "hsbc", "bupa", "bnp", "ibm", "llp", "llc", "plc", "ltd"]);

function formatEmailDomain(email) {
  if (!email?.includes("@")) return "";
  const d = email.split("@")[1].split(".")[0].toLowerCase();
  if (DOMAIN_ACRONYMS.has(d)) return d.toUpperCase();
  return d.charAt(0).toUpperCase() + d.slice(1);
}

async function checkEmailReplies(config, emailTasks) {
  const { msToken } = config;
  if (!msToken || !emailTasks.length) return {};

  // Get current user's email address to identify replies from self
  let myEmail = "";
  try {
    const meRes = await fetch("https://graph.microsoft.com/v1.0/me?$select=mail,userPrincipalName",
      { headers: { Authorization: `Bearer ${msToken}` } });
    if (meRes.ok) {
      const me = await meRes.json();
      myEmail = (me.mail || me.userPrincipalName || "").toLowerCase();
    }
  } catch {}

  const results = {};

  // Check each email task - batch to avoid hammering Graph
  for (const task of emailTasks.slice(0, 50)) {
    const emailId = task.rawId || task.id.replace("email-", "");
    if (!emailId || task.replied) continue; // skip already-replied tasks

    try {
      // First get the conversationId for this message
      const msgRes = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages/${emailId}?$select=conversationId,receivedDateTime`,
        { headers: { Authorization: `Bearer ${msToken}` } }
      );
      if (!msgRes.ok) continue;
      const msg = await msgRes.json();
      if (!msg.conversationId) continue;

      // Find messages in this conversation sent by me after the original
      const receivedAt = msg.receivedDateTime || "";
      const threadRes = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages?$filter=conversationId eq '${msg.conversationId}'&$select=from,sentDateTime,isDraft&$orderby=sentDateTime asc&$top=20`,
        { headers: { Authorization: `Bearer ${msToken}` } }
      );
      if (!threadRes.ok) continue;
      const thread = await threadRes.json();
      const messages = thread.value || [];

      // Look for a message sent from me, after the original received time, that isn't a draft
      const reply = messages.find(m => {
        if (m.isDraft) return false;
        const fromAddr = (m.from?.emailAddress?.address || "").toLowerCase();
        if (fromAddr !== myEmail) return false;
        return !receivedAt || new Date(m.sentDateTime) > new Date(receivedAt);
      });

      if (reply) {
        results[task.id] = {
          replied: true,
          repliedAt: reply.sentDateTime,
        };
        console.log(`[Sync] Reply detected for task "${task.title}" at ${reply.sentDateTime}`);
      }
    } catch (e) {
      console.warn(`[Sync] Reply check failed for ${emailId}:`, e.message);
    }
  }

  // Also check email subtasks across all passed tasks (manual tasks with ✉ subtasks)
  for (const task of emailTasks) {
    // Include subtasks with stEmailId OR legacy ✉ subtasks without it
    // Include done ones too so we can detect new replies that should reopen them
    const subtasks = (task.subtasks || []).filter(s =>
      s.stEmailId || s.text?.startsWith("✉ ")
    );
    console.log(`[Sync] Checking ${subtasks.length} email subtask(s) on task "${task.title?.substring(0,40)}"`);
    for (const st of subtasks) {
      try {
        let conversationId = null;
        let receivedDateTime = null;
        let emailUnflagged = false;

        if (st.stEmailId) {
          // New style - we have the message ID directly
          const msgRes = await fetch(
            `https://graph.microsoft.com/v1.0/me/messages/${st.stEmailId}?$select=conversationId,receivedDateTime`,
            { headers: { Authorization: `Bearer ${msToken}` } }
          );
          if (!msgRes.ok) continue;
          const msg = await msgRes.json();
          conversationId = msg.conversationId;
          receivedDateTime = msg.receivedDateTime;
        } else {
          // Legacy style - extract subject from text: "✉ Name | Domain | Subject"
          const parts = st.text.replace(/^✉ /, "").split(" | ");
          const subject = parts.slice(2).join(" | ") || parts[parts.length - 1];
          if (!subject) continue;

          // Strip RE:/FW: prefixes and search with contains for flexibility
          const cleanSubject = subject.replace(/^(RE|FW|FWD):\s*/i, "").trim();
          const searchRes = await fetch(
            `https://graph.microsoft.com/v1.0/me/messages?$search="subject:${cleanSubject.replace(/"/g, "")}"&$select=conversationId,receivedDateTime,flag&$top=5`,
            { headers: { Authorization: `Bearer ${msToken}`, ConsistencyLevel: "eventual" } }
          );
          if (!searchRes.ok) continue;
          const searchData = await searchRes.json();
          const found = (searchData.value || [])[0];
          if (!found) {
            console.log(`[Sync] Could not find message for subtask "${st.text?.substring(0,50)}" by subject`);
            continue;
          }
          conversationId = found.conversationId;
          receivedDateTime = found.receivedDateTime;
          // Check flag status from the found message
          const flagStatus = found.flag?.flagStatus || "";
          emailUnflagged = flagStatus === "notFlagged" || flagStatus === "complete";
          console.log(`[Sync] Found message by subject for "${subject?.substring(0,40)}" - flag: ${flagStatus}`);
        }

        if (!conversationId) continue;

        // Check flag status of the original email (new-style subtasks with stEmailId)
        if (st.stEmailId) {
          try {
            const flagRes = await fetch(
              `https://graph.microsoft.com/v1.0/me/messages/${st.stEmailId}?$select=flag`,
              { headers: { Authorization: `Bearer ${msToken}` } }
            );
            if (flagRes.ok) {
              const flagData = await flagRes.json();
              const flagStatus = flagData.flag?.flagStatus || "";
              emailUnflagged = flagStatus === "notFlagged" || flagStatus === "complete";
              console.log(`[Sync] Flag status for subtask "${st.text?.substring(0,30)}": ${flagStatus}`);
            }
          } catch {}
        }

        const threadRes = await fetch(
          `https://graph.microsoft.com/v1.0/me/messages?$filter=conversationId eq '${conversationId}'&$select=from,sentDateTime,isDraft&$top=20`,
          { headers: { Authorization: `Bearer ${msToken}` } }
        );
        if (!threadRes.ok) continue;
        const thread = await threadRes.json();
        const messages = thread.value || [];

        const reply = messages.find(m => {
          if (m.isDraft) return false;
          return (m.from?.emailAddress?.address || "").toLowerCase() === myEmail &&
            (!receivedDateTime || new Date(m.sentDateTime) > new Date(receivedDateTime));
        });

        if (reply) {
          if (st.done) {
            // Subtask was done - only reopen if latest external message arrived AFTER doneAt
            const latestMessage = messages
              .sort((a, b) => new Date(b.sentDateTime) - new Date(a.sentDateTime))[0];
            const latestFrom = (latestMessage?.from?.emailAddress?.address || "").toLowerCase();
            const latestTime = latestMessage ? new Date(latestMessage.sentDateTime) : null;
            const doneTime = st.doneAt ? new Date(st.doneAt) : null;
            // Only reopen if: latest msg not from me AND arrived after user marked done
            if (latestMessage && latestFrom !== myEmail && latestTime && (!doneTime || latestTime > doneTime)) {
              results[`${task.id}:${st.id}`] = { newReply: true, repliedAt: latestMessage.sentDateTime };
              console.log(`[Sync] New reply from ${latestFrom} at ${latestMessage.sentDateTime} on done subtask - flagging newReply`);
            } else if (latestMessage && latestFrom !== myEmail && doneTime && latestTime <= doneTime) {
              console.log(`[Sync] External msg at ${latestMessage.sentDateTime} but doneAt ${st.doneAt} is newer - keeping done`);
            }
          } else {
            results[`${task.id}:${st.id}`] = { replied: true, repliedAt: reply.sentDateTime, unflagged: emailUnflagged };
            console.log(`[Sync] Reply detected on subtask "${st.text?.substring(0,40)}" at ${reply.sentDateTime}${emailUnflagged ? " (unflagged → Done)" : " (flagged → In Progress)"}`);
          }
        } else if (emailUnflagged && !st.done) {
          results[`${task.id}:${st.id}`] = { replied: false, unflagged: true };
          console.log(`[Sync] Subtask "${st.text?.substring(0,40)}" unflagged → Done`);
        } else if (!st.done) {
          console.log(`[Sync] No reply, still flagged: "${st.text?.substring(0,40)}" (${messages.length} msgs, myEmail: ${myEmail})`);
        } else if (st.done) {
          // Done subtask - only reopen if latest external message arrived AFTER doneAt
          const latestMessage = messages.sort((a, b) => new Date(b.sentDateTime) - new Date(a.sentDateTime))[0];
          const latestFrom = (latestMessage?.from?.emailAddress?.address || "").toLowerCase();
          const latestTime = latestMessage ? new Date(latestMessage.sentDateTime) : null;
          const doneTime = st.doneAt ? new Date(st.doneAt) : null;
          if (latestMessage && latestFrom !== myEmail && latestTime && (!doneTime || latestTime > doneTime)) {
            results[`${task.id}:${st.id}`] = { newReply: true, repliedAt: latestMessage.sentDateTime };
            console.log(`[Sync] New reply on done subtask - flagging newReply "${st.text?.substring(0,40)}"`);
          } else {
            console.log(`[Sync] Done subtask - no reopen needed for "${st.text?.substring(0,40)}" (doneAt: ${st.doneAt}, latest: ${latestMessage?.sentDateTime})`);
          }
        }
      } catch (e) {
        console.warn(`[Sync] Subtask reply check error:`, e.message);
      }
    }
  }

  return results;
}

async function flagOutlookEmail(config, emailId) {
  const { msToken } = config;
  const res = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${emailId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${msToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ flag: { flagStatus: "flagged" }, isRead: true }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Flag failed: ${res.status}`);
  }
}

// Look up a Jira user's email by display name
async function lookupJiraUserEmail(config, displayName) {
  if (!displayName) return null;
  // Merge config team members with server-loaded list
  const teamMembers = [
    ...(config.teamMembers || []),
    ...(window.__c365teamMembers__ || []),
  ];
  const match = teamMembers.find(m =>
    m.name?.toLowerCase() === displayName.toLowerCase() ||
    m.name?.toLowerCase().includes(displayName.toLowerCase())
  );
  if (match?.email) {
    console.log("[Email] Found email for", displayName, ":", match.email);
    return match.email;
  }
  // Fallback: if displayName looks like an email, use it directly
  if (displayName.includes("@")) return displayName;
  console.warn("[Email] No email found for", displayName, "- ensure team_members.json is on the server");
  return null;
}

// Send an email via Outlook Graph API
async function sendOutlookEmail(config, { to, subject, body }) {
  const { msToken } = config;
  if (!msToken) { console.warn("[Email] sendOutlookEmail: no msToken"); return false; }
  if (!to) { console.warn("[Email] sendOutlookEmail: no recipient address"); return false; }
  try {
    console.log("[Email] Sending to:", to, "Subject:", subject);
    const res = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
      method: "POST",
      headers: { Authorization: `Bearer ${msToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        message: {
          subject,
          body: { contentType: "HTML", content: body },
          toRecipients: [{ emailAddress: { address: to } }],
        },
        saveToSentItems: true,
      }),
    });
    if (res.status === 202) {
      console.log("[Email] Sent successfully to", to);
      return true;
    } else {
      const err = await res.json().catch(() => ({}));
      console.warn("[Email] sendMail failed:", res.status, err?.error?.message);
      return false;
    }
  } catch (e) {
    console.warn("[Email] sendOutlookEmail error:", e.message);
    return false;
  }
}

// Strip HTML email body to clean plain text
function htmlEmailToPlainText(html, contentType) {
  if (!html) return "";
  if (contentType === "text") return html.trim();
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/tr>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const CH_ATTACHMENT_LIMIT = 20 * 1024 * 1024; // 20 MB

// Fetch full email thread + attachments from Graph API
// Returns { bodyText, attachments: [{ fileName, fileSize, fileUrl, fileMime }], skipped: [] }
async function fetchFullEmail(config, emailId) {
  const { msToken } = config;

  // First fetch the message to get conversationId and body
  const [msgRes, attListRes] = await Promise.all([
    fetch(`https://graph.microsoft.com/v1.0/me/messages/${emailId}?$select=body,subject,from,receivedDateTime,toRecipients,conversationId`,
      { headers: { Authorization: `Bearer ${msToken}`, Accept: "application/json" } }),
    fetch(`https://graph.microsoft.com/v1.0/me/messages/${emailId}/attachments`,
      { headers: { Authorization: `Bearer ${msToken}`, Accept: "application/json" } }),
  ]);

  if (!msgRes.ok) throw new Error(`Graph message fetch ${msgRes.status}`);
  const msgData = await msgRes.json();

  // Fetch all messages in the conversation thread
  let threadText = "";
  if (msgData.conversationId) {
    try {
      const threadRes = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages?$filter=conversationId eq '${msgData.conversationId}'&$select=body,subject,from,receivedDateTime,toRecipients&$orderby=receivedDateTime asc&$top=50`,
        { headers: { Authorization: `Bearer ${msToken}`, Accept: "application/json" } }
      );
      if (threadRes.ok) {
        const threadData = await threadRes.json();
        const messages = threadData.value || [];
        console.log(`[ClientHub] Thread has ${messages.length} message(s) for conversationId ${msgData.conversationId}`);

        const parts = messages.map(m => {
          const from = m.from?.emailAddress?.name || m.from?.emailAddress?.address || "Unknown";
          const to = (m.toRecipients || []).map(r => r.emailAddress?.name || r.emailAddress?.address).join(", ");
          const date = m.receivedDateTime
            ? new Date(m.receivedDateTime).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
            : "";
          const body = htmlEmailToPlainText(m.body?.content || "", m.body?.contentType || "text");
          return `────────────────────────\nFrom: ${from}\nTo: ${to}\nDate: ${date}\n\n${body}`;
        });
        threadText = parts.join("\n\n");
      }
    } catch (e) {
      console.warn("[ClientHub] Thread fetch failed, falling back to single message:", e.message);
    }
  }

  // Fall back to single message body if thread fetch failed
  if (!threadText) {
    threadText = htmlEmailToPlainText(msgData.body?.content || "", msgData.body?.contentType || "text");
  }

  // Parse attachment list - only fileAttachments, skip inline images
  const attList = attListRes.ok ? ((await attListRes.json()).value || []) : [];
  console.log(`[ClientHub] ${attList.length} attachment(s) found on email ${emailId}`, attList.map(a => `${a.name} (${a["@odata.type"]}, ${a.size} bytes, inline:${a.isInline})`));

  const realAtts = attList.filter(a =>
    !a.isInline &&
    (a["@odata.type"] === "#microsoft.graph.fileAttachment" || !a["@odata.type"]?.includes("item"))
  );
  const toFetch = realAtts.filter(a => a.size <= CH_ATTACHMENT_LIMIT);
  const skipped = realAtts.filter(a => a.size > CH_ATTACHMENT_LIMIT).map(a => a.name);

  if (toFetch.length === 0) {
    console.log(`[ClientHub] No eligible attachments to save (${realAtts.length} real, ${skipped.length} oversized)`);
    return { bodyText: threadText, attachments: [], skipped };
  }

  // Fetch each attachment individually
  const savedAttachments = [];
  for (const att of toFetch) {
    try {
      console.log(`[ClientHub] Fetching attachment: ${att.name} (${att.id})`);
      const attRes = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages/${emailId}/attachments/${att.id}`,
        { headers: { Authorization: `Bearer ${msToken}`, Accept: "application/json" } }
      );
      if (!attRes.ok) {
        console.warn(`[ClientHub] Attachment fetch failed: ${attRes.status} for ${att.name}`);
        continue;
      }
      const attData = await attRes.json();
      if (!attData.contentBytes) {
        console.warn(`[ClientHub] No contentBytes for ${att.name} - type: ${attData["@odata.type"]}`);
        continue;
      }
      console.log(`[ClientHub] Saving ${att.name} to /api/files (${att.size} bytes)`);

      const saveRes = await fetch(FILES_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: att.name || attData.name,
          mimeType: att.contentType || attData.contentType || "application/octet-stream",
          base64: attData.contentBytes,
          taskId: `clienthub-email-${emailId}`,
        }),
      });
      if (saveRes.ok) {
        const saved = await saveRes.json();
        console.log(`[ClientHub] Saved attachment: ${saved.url}`);
        savedAttachments.push({
          fileName: att.name || attData.name,
          fileSize: att.size,
          fileMime: att.contentType || attData.contentType,
          fileUrl: saved.url,
          fileId: saved.id,
        });
      } else {
        const errText = await saveRes.text().catch(() => "");
        console.warn(`[ClientHub] /api/files save failed ${saveRes.status}: ${errText}`);
      }
    } catch (e) {
      console.warn(`[ClientHub] Attachment "${att.name}" error:`, e.message);
    }
  }

  console.log(`[ClientHub] Saved ${savedAttachments.length}/${toFetch.length} attachments`);
  return { bodyText: threadText, attachments: savedAttachments, skipped };
}

async function updateOutlookTaskDue(config, listId, taskId, dueDate) {
  const { msToken } = config;
  const body = dueDate
    ? { dueDateTime: { dateTime: `${dueDate}T00:00:00`, timeZone: "UTC" } }
    : { dueDateTime: null };
  const res = await fetch(
    `https://graph.microsoft.com/v1.0/me/todo/lists/${listId}/tasks/${taskId}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${msToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error("Failed to update due date");
}

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_TASKS = [];

// ─── Components ───────────────────────────────────────────────────────────────
function Tag({ className, children }) {
  return <span className={`tag ${className}`}>{children}</span>;
}

function TaskCard({ task, selected, onClick, onToggle, onToggleSubtask, bulkChecked, onBulkToggle, highlightAssignee, markAssignedOutBy }) {
  const today = new Date().toISOString().split("T")[0];
  const isOverdue = task.due && !task.done && task.due < today;
  const sourceColor = task.source === "outlook" ? "#0078d4" : task.source === "jira" ? "#0052cc" : "#334155";
  const [expanded, setExpanded] = useState(!!highlightAssignee || !!markAssignedOutBy);
  const subtasks = task.subtasks || [];
  const completedCount = subtasks.filter(s => s.done).length;

  // Priority border colour - left accent bar shows priority for manual/outlook tasks
  const accentColor = task.source === "jira"
    ? sourceColor
    : task.priority === "high"   ? "var(--red)"
    : task.priority === "low"    ? "var(--green)"
    : sourceColor;

  return (
    <div className={`task-card ${selected ? "selected" : ""} ${task.done ? "done" : ""}`}
      style={{ "--accent-source": accentColor }}
      onClick={onClick}>
      <div className="task-card-row" style={{ alignItems: "center" }}>
        {/* Drag handle - visual only, actual drag on outer wrapper */}
        {task.source === "manual" && (
          <span style={{ color: "var(--text-dim)", fontSize: 14, cursor: "grab", userSelect: "none", flexShrink: 0, padding: "0 2px", marginTop: 1 }}
            title="Drag to reorder">⠿</span>
        )}
        {/* Bulk select checkbox */}
        <input type="checkbox"
          checked={!!bulkChecked}
          onChange={e => { e.stopPropagation(); onBulkToggle && onBulkToggle(); }}
          onClick={e => e.stopPropagation()}
          style={{ accentColor: "var(--green)", cursor: "pointer", flexShrink: 0, width: 13, height: 13, marginTop: 1 }}
          title="Select for bulk action" />

        <div className={`task-check ${task.done ? "checked" : ""}`} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          {task.done && "✓"}
        </div>
        <div className="task-body">
          <div className={`task-title ${task.done ? "done" : ""}`}>{task.title}</div>
          {(task.senderName || task.receivedDate) && (
            <div style={{ display: "flex", gap: 12, marginBottom: 5, fontSize: 11, color: "var(--text-muted)", flexWrap: "wrap", alignItems: "center" }}>
              {task.senderName && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "var(--green)" }}>✉</span> {task.senderName}
                </span>
              )}
              {task.receivedDate && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "var(--amber)" }}>🕐</span> {task.receivedDate}
                </span>
              )}
              {task.replied && (
                <span style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(71,172,76,0.15)", border: "1px solid var(--green)", color: "var(--green)", borderRadius: 3, padding: "1px 7px", fontWeight: 600, fontSize: 10 }}>
                  ✓ Replied {task.repliedAt ? new Date(task.repliedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : ""}
                </span>
              )}
            </div>
          )}
          <div className="task-meta">
            <Tag className={task.source}>{task.source}</Tag>
            {task.source !== "jira" && <Tag className={`priority-${task.priority}`}>{capitalise(task.priority)}</Tag>}
            {task.source === "jira" && <Tag className="status">{task.jiraStatus || task.status}</Tag>}
            {task.source === "jira" && (() => {
              const sla = getSLAInfo(task);
              if (!sla) return null;
              if (sla.breached) return (
                <span className="tag" style={{ color: "#e32529", borderColor: "#e3252950", background: "#e3252915", fontWeight: 700, fontSize: 9 }}
                  title={`SLA breached - ${Math.abs(sla.remaining)} business day(s) overdue (${sla.threshold}d threshold)`}>
                  🚨 SLA -{Math.abs(sla.remaining)}d
                </span>
              );
              if (sla.warning) return (
                <span className="tag" style={{ color: "#F1901E", borderColor: "#F1901E50", background: "#F1901E15", fontWeight: 700, fontSize: 9 }}
                  title={`SLA warning - ${sla.remaining} business day(s) remaining (${sla.threshold}d threshold)`}>
                  ⚠ SLA {sla.remaining}d
                </span>
              );
              return null; // within SLA, no badge needed
            })()}
            {task.waitingOn && (
              <span className="tag" style={{ color: "#F1901E", borderColor: "#F1901E50", background: "#F1901E10", fontWeight: 700 }}>
                ⏳ Waiting on {task.waitingRole}: {task.waitingOn}
              </span>
            )}
            {isNoReply(task) && (
              <span className="tag" style={{ color: "#e32529", borderColor: "#e3252950", background: "#e3252915", fontWeight: 700, fontSize: 9 }}
                title="No update in 2+ days">
                💬 No reply {task.jiraUpdated ? Math.floor((new Date() - new Date(task.jiraUpdated)) / (1000*60*60*24)) + "d" : ""}
              </span>
            )}
            {task.mentioned && (
              <span className="tag" style={{ color: "#47ac4c", borderColor: "#47ac4c50", background: "#47ac4c10", fontWeight: 700 }}>
                💬 Mentioned
              </span>
            )}
            {task.recur && task.source === "manual" && (
              <span className="tag" style={{ color: "#a78bfa", borderColor: "#a78bfa50", background: "#a78bfa10", fontSize: 9, fontWeight: 600 }}>
                🔁 {RECUR_OPTIONS.find(o => o.value === task.recur)?.label || task.recur}
              </span>
            )}
            {task.isFlaggedEmail && (
              <span className="tag" style={{ color: "#e32529", borderColor: "#e3252950", background: "#e3252910", fontWeight: 700 }}>
                🚩 Flagged
              </span>
            )}
            {task.webLinkJiraStatus && task.source === "manual" && (
              <span className="tag" style={{ color: "#4c9aff", borderColor: "#4c9aff50", background: "#4c9aff10", fontWeight: 600, fontSize: 9 }}>
                🔷 {extractJiraKeyFromLink(task.webLink)} · {task.webLinkJiraStatus}
              </span>
            )}
            {task.webLinkJiraAssignee && task.source === "manual" && task.webLinkJiraStatus && (
              <span className="tag" style={{ color: "var(--text-muted)", borderColor: "var(--border)", background: "transparent", fontSize: 9 }}>
                👤 {task.webLinkJiraAssignee}
              </span>
            )}
            {task.due && !isOverdue && (
              <span className={`tag ${task.due === today ? "due-today" : "due-tag"}`}>
                {task.due === today ? "⚡ Due Today" : `Due ${formatDate(task.due)}`}
              </span>
            )}
            {task.due && isOverdue && <span className="tag overdue-tag">⚠ Overdue {formatDate(task.due)}</span>}
            {(task.assignedTo) && (
              <span className="tag" style={{ color: "#47ac4c", borderColor: "#47ac4c50", background: "#47ac4c10", fontSize: 9, fontWeight: 600 }}>
                👤 {task.assignedTo}
              </span>
            )}
            {(task.tags || []).map((tag) => {
              const c = getTagColour(tag);
              return <span key={tag} className="tag-chip" style={{ color: c.color, borderColor: c.border, background: c.bg, fontSize: 9, padding: "1px 6px" }}>{tag}</span>;
            })}
            {task.source === "jira" && task.jiraEffort != null && (
              <span className="tag" style={{ color: "#f59e0b", borderColor: "#f59e0b50", background: "#f59e0b15", fontSize: 9, fontWeight: 700 }}>
                ⚡ {String(task.jiraEffort)}
              </span>
            )}
            {task.source === "jira" && task.jiraValue != null && (
              <span className="tag" style={{ color: "#60a5fa", borderColor: "#60a5fa50", background: "#60a5fa15", fontSize: 9, fontWeight: 700 }}>
                💎 {String(task.jiraValue)}
              </span>
            )}
            {task.source === "jira" && task.jiraPriority != null && (
              <span className="tag" style={{ color: "#f472b6", borderColor: "#f472b650", background: "#f472b615", fontSize: 9, fontWeight: 700 }}>
                🎯 {task.jiraPriority}
              </span>
            )}
            {task.source === "jira" && task.jiraScore != null && (
              <span className="tag" style={{ color: "#fbbf24", borderColor: "#fbbf2450", background: "#fbbf2410", fontSize: 9, fontWeight: 700 }}>
                ★ {String(task.jiraScore)}
              </span>
            )}
            {task.source === "jira" && (task.affectedClient || task.jiraKey?.split("-")[0]) && (
              <span className="tag" style={{ color: "#34d399", borderColor: "#34d39950", background: "#34d39915", fontSize: 9, fontWeight: 600 }}>
                🏢 {task.affectedClient || task.jiraKey?.split("-")[0]}
              </span>
            )}
            {subtasks.length > 0 && (
              <span
                className="tag status subtask-badge"
                onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
                style={{ color: expanded ? "var(--green)" : undefined, borderColor: expanded ? "var(--green)" : undefined }}
                title={expanded ? "Click to collapse subtasks" : "Click to expand subtasks"}
              >
                {expanded ? "▾" : "▸"} {completedCount}/{subtasks.length} ✓
              </span>
            )}
          </div>
          {task.source === "jira" && task.latestComment && (
            <div style={{ marginTop: 6, padding: "6px 8px", background: "var(--surface2)", borderRadius: 4, border: "1px solid var(--border)", borderLeft: "3px solid #47ac4c60" }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ color: "var(--green)", fontWeight: 600 }}>💬 {task.latestComment.author}</span>
                <span>{task.latestComment.date}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.45, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                {task.latestComment.text}
              </div>
            </div>
          )}
          {expanded && subtasks.length > 0 && (
            <div className="inline-subtasks" onClick={e => e.stopPropagation()}>
              {subtasks.map(s => {
                const today = new Date().toISOString().split("T")[0];
                const isOverdue = s.due && !s.done && s.due < today;
                const isDueToday = s.due && s.due === today;
                return (
                  <div key={s.id} className="inline-subtask-row" style={{ alignItems: "flex-start",
                    ...(highlightAssignee && s.assignedTo === highlightAssignee && !s.done ? { background: "rgba(71,172,76,0.08)", borderLeft: "2px solid var(--green)", paddingLeft: 6, marginLeft: -8, borderRadius: 3 } : {}),
                    ...(markAssignedOutBy && s.assignedTo && s.assignedTo !== markAssignedOutBy && !s.done ? { background: "rgba(196,126,240,0.08)", borderLeft: "2px solid #c47ef0", paddingLeft: 6, marginLeft: -8, borderRadius: 3 } : {}) }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span className={`inline-subtask-text ${s.done ? "done" : ""}`}>
                        {markAssignedOutBy && s.assignedTo && s.assignedTo !== markAssignedOutBy && !s.done && (
                          <span style={{ color: "#c47ef0", fontWeight: 700, marginRight: 4 }}>(S)</span>
                        )}
                        {s.text}
                        {s.replied && !s.newReply && (
                          <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 600, background: "rgba(71,172,76,0.15)", border: "1px solid var(--green)", color: "var(--green)", borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>✓ Replied</span>
                        )}
                        {s.newReply && (
                          <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 600, background: "rgba(241,144,30,0.15)", border: "1px solid var(--amber)", color: "var(--amber)", borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>💬 New reply</span>
                        )}
                      </span>
                      {/* Status + date + link all on the same meta line */}
                      <div className="inline-subtask-meta">
                        <span style={{
                          fontSize: 8, fontWeight: 600, padding: "1px 5px", borderRadius: 3,
                          border: "1px solid", flexShrink: 0,
                          ...(s.status === "Done" || s.done ? { color: "var(--green)", borderColor: "#47ac4c50", background: "#47ac4c15" }
                            : s.status === "In Progress"      ? { color: "#4c9aff",       borderColor: "#4c9aff50", background: "#4c9aff15" }
                            : s.status === "Waiting Response" ? { color: "var(--amber)",  borderColor: "#F1901E50", background: "#F1901E15" }
                            : { color: "var(--text-dim)", borderColor: "var(--border)", background: "var(--surface2)" }),
                          cursor: "pointer", whiteSpace: "nowrap",
                        }} onClick={() => onToggleSubtask && onToggleSubtask(task.id, s.id)}
                        title="Click to cycle status">
                          {s.status || (s.done ? "Done" : "To Start")}
                        </span>
                        {s.due && (
                          <span className={`tag ${isOverdue ? "overdue-tag" : isDueToday ? "due-today" : "due-tag"}`} style={{ fontSize: 9 }}>
                            {isOverdue ? "⚠ " : isDueToday ? "⚡ " : "📅 "}{formatDate(s.due)}
                          </span>
                        )}
                        {s.assignedTo && (
                          <span className="tag" style={{ fontSize: 8, color: "#47ac4c", borderColor: "#47ac4c50", background: "#47ac4c10", fontWeight: 600 }}>👤 {s.assignedTo}</span>
                        )}
                        {(s.notes || []).length > 0 && (
                          <span className="tag" style={{ fontSize: 8, color: "#4c9aff", borderColor: "#4c9aff50", background: "#4c9aff10" }}>💬 {(s.notes || []).length}</span>
                        )}
                        {s.link && (
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <a href={s.link} target="_blank" rel="noreferrer"
                              style={{ color: "var(--green)", fontSize: 9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}
                              onClick={e => e.stopPropagation()}>
                              {extractJiraKeyFromLink(s.link) ? "🔷 " + extractJiraKeyFromLink(s.link) : "🔗 " + s.link.replace(/^https?:\/\//, "").substring(0, 30)}
                            </a>
                            {s.jiraLinkStatus && (
                              <span style={{ fontSize: 7, fontWeight: 700, padding: "1px 4px", borderRadius: 2,
                                background: "#4c9aff20", color: "#4c9aff", border: "1px solid #4c9aff40", whiteSpace: "nowrap" }}>
                                {s.jiraLinkStatus}
                              </span>
                            )}
                            {s.jiraLinkAssignee && (
                              <span style={{ fontSize: 7, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                                👤 {s.jiraLinkAssignee}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Parse TSV text that may contain quoted multi-line cells (RFC 4180 style).
// Excel pastes quoted cells when the cell contains newlines.
// Returns array of string arrays (rows of cells), or null if not TSV.
function parseTSV(text) {
  if (!text || !text.includes("\t")) return null;
  const rows = [];
  let row = [];
  let cell = "";
  let inQuote = false;
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (inQuote) {
      if (ch === '"') {
        // Check for escaped quote ""
        if (i + 1 < text.length && text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuote = false; i++; continue;
      }
      cell += ch; i++; continue;
    }
    if (ch === '"') { inQuote = true; i++; continue; }
    if (ch === "\t") { row.push(cell.trim()); cell = ""; i++; continue; }
    if (ch === "\n") {
      row.push(cell.trim()); cell = "";
      if (row.some(c => c.length > 0)) rows.push(row);
      row = []; i++;
      // Skip \r after \n
      if (i < text.length && text[i] === "\r") i++;
      continue;
    }
    if (ch === "\r") { i++; continue; }
    cell += ch; i++;
  }
  // flush final cell/row
  row.push(cell.trim());
  if (row.some(c => c.length > 0)) rows.push(row);
  if (rows.length < 2) return null;
  // Validate: all rows should have the same column count as the header row
  const colCount = rows[0].length;
  if (colCount < 2) return null;
  const valid = rows.every(r => r.length === colCount);
  if (!valid) {
    // Tolerate rows that are short by padding with empty strings
    return rows.map(r => {
      while (r.length < colCount) r.push("");
      return r.slice(0, colCount);
    });
  }
  return rows;
}

// Detect if text looks like tabular data.
// Handles:
//   1. Tab-separated values (TSV) - pasted from Excel/Word tables, including quoted multi-line cells
//   2. Repeating newline-block patterns (legacy detection)
// Returns { headers, rows } if tabular, null otherwise
function detectTable(text) {
  if (!text || text.trim().length === 0) return null;

  // ── Strategy 1: TSV with quoted multi-line cell support ─────────────────────
  const tsv = parseTSV(text);
  if (tsv && tsv.length >= 2) {
    const headers = tsv[0];
    const rows = tsv.slice(1);
    return { headers, rows };
  }

  // ── Strategy 2: Repeating newline-block patterns ────────────────────────────
  const lines = text.split("\n").map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length < 4) return null;

  for (let blockSize = 2; blockSize <= 6; blockSize++) {
    if (lines.length % blockSize !== 0) continue;
    const numRows = lines.length / blockSize;
    if (numRows < 2) continue;

    const firstItems = [];
    for (let r = 0; r < numRows; r++) {
      firstItems.push(lines[r * blockSize]);
    }

    const looksLikeIds = firstItems.every(item => item.length < 20 && /^[A-Z0-9]/.test(item));
    if (looksLikeIds) {
      const headers = [];
      for (let col = 0; col < blockSize; col++) {
        const colValues = [];
        for (let r = 0; r < numRows; r++) colValues.push(lines[r * blockSize + col]);
        const allDates = colValues.every(v => /\d{2}\/\d{2}\/\d{4}/.test(v));
        const allPostcodes = colValues.every(v => /^[A-Z]{1,2}\d{1,2}\s?\d[A-Z]{2}$/i.test(v.trim()));
        const allShortIds = colValues.every(v => /^[A-Z]+\d+$/.test(v.trim()) && v.length < 12);
        if (col === 0 && allShortIds) headers.push("Ref");
        else if (allDates) headers.push("Date");
        else if (allPostcodes) headers.push("Postcode");
        else if (col === 1) headers.push("Address");
        else if (col === blockSize - 1 && allDates) headers.push("Date");
        else headers.push(`Column ${col + 1}`);
      }
      const rows = [];
      for (let r = 0; r < numRows; r++) {
        rows.push(lines.slice(r * blockSize, r * blockSize + blockSize));
      }
      return { headers, rows };
    }
  }
  return null;
}

function WebLinkField({ task, onUpdateTask }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(task.webLink || "");

  useEffect(() => {
    setVal(task.webLink || "");
    setEditing(false);
  }, [task.id]);

  const save = () => {
    const trimmed = val.trim();
    onUpdateTask(task.id, {
      webLink: trimmed,
      // Clear Jira live data when link is removed
      ...(trimmed === "" ? { webLinkJiraStatus: null, webLinkJiraAssignee: null } : {}),
    });
    setEditing(false);
  };

  const jiraKey = extractJiraKeyFromLink(task.webLink);
  const isJiraLink = !!jiraKey;

  if (!editing && !task.webLink) {
    return (
      <div className="detail-field">
        <div className="detail-section-label">Web Link (inc Jira card URL)</div>
        <button className="btn" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => setEditing(true)}>+ Add link</button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="detail-field">
        <div className="detail-section-label">Web Link (inc Jira card URL)</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            className="form-input"
            style={{ fontSize: 11, flex: 1 }}
            placeholder="https://c365online.atlassian.net/browse/PROJ-123"
            value={val}
            onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            autoFocus
          />
          <button className="btn primary" style={{ fontSize: 11, padding: "4px 10px" }} onClick={save}>✓</button>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-field">
      <div className="detail-section-label">Web Link (inc Jira card URL)</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <a href={task.webLink} target="_blank" rel="noreferrer"
          style={{ color: isJiraLink ? "#4c9aff" : "var(--green)", fontSize: 11, flex: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: isJiraLink ? 600 : 400 }}>
          {isJiraLink ? "🔷 " + jiraKey : "🔗 " + task.webLink}
        </a>
        {/* Live Jira status from sync */}
        {task.webLinkJiraStatus && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 3,
            background: "#4c9aff20", color: "#4c9aff", border: "1px solid #4c9aff40", whiteSpace: "nowrap" }}>
            ↺ {task.webLinkJiraStatus}
          </span>
        )}
        {/* Assignee from Jira */}
        {task.webLinkJiraAssignee && (
          <span style={{ fontSize: 9, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            👤 {task.webLinkJiraAssignee}
          </span>
        )}
        <button className="btn" style={{ fontSize: 10, padding: "2px 8px", flexShrink: 0 }} onClick={() => setEditing(true)}>✎</button>
      </div>
    </div>
  );
}

function NoteEntry({ note, onDelete }) {
  const table = detectTable(note.text);
  return (
    <div className="note-entry" style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div className="note-entry-date" style={{ margin: 0 }}>📅 {note.date}{note.author ? <span style={{ color: "var(--green)", fontWeight: 600, marginLeft: 6 }}>· {note.author}</span> : null}</div>
        {onDelete && (
          <button
            onClick={onDelete}
            title="Delete this note"
            style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 13, padding: "0 2px", lineHeight: 1, flexShrink: 0 }}
            onMouseEnter={e => e.target.style.color = "var(--red)"}
            onMouseLeave={e => e.target.style.color = "var(--text-dim)"}>
            🗑
          </button>
        )}
      </div>
      {table ? (
        <table className="note-table">
          <thead>
            <tr>{table.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i}>{row.map((cell, j) => <td key={j} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: 300 }}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="note-entry-text">
          {note.text.split("\n").map((line, i) => {
            if (line.startsWith("- ") || line.startsWith("* ")) {
              return (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", margin: "2px 0" }}>
                  <span style={{ color: "var(--green)", fontSize: 14, lineHeight: 1.4, flexShrink: 0 }}>•</span>
                  <span>{line.substring(2)}</span>
                </div>
              );
            }
            return line.trim() ? <div key={i} style={{ margin: "2px 0" }}>{line}</div> : <div key={i} style={{ height: 4 }} />;
          })}
        </div>
      )}
      {(note.images || []).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {(note.images || []).map((img, i) => (
            <div key={i} style={{ position: "relative", cursor: "zoom-in" }}
              onClick={() => {
                window.dispatchEvent(new CustomEvent("c365-lightbox", { detail: { src: img } }));
              }}>
              <img src={img} alt="note attachment"
                style={{ width: 90, height: 70, objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)", display: "block" }}
                onError={e => { e.target.style.display = "none"; }}
              />
              <div style={{ position: "absolute", bottom: 2, right: 3, fontSize: 9, color: "#fff", background: "rgba(0,0,0,0.5)", borderRadius: 2, padding: "1px 3px" }}>🔍</div>
            </div>
          ))}
        </div>
      )}
      {(note.files || []).length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
          {(note.files || []).map((f, i) => (
            <a key={i} href={f.url} download={f.name} target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 4, padding: "6px 10px", textDecoration: "none", transition: "border-color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "var(--green)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
              <span style={{ fontSize: 18 }}>{fileIcon(f.name)}</span>
              <span style={{ flex: 1, fontSize: 11, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              {f.size && <span style={{ fontSize: 10, color: "var(--text-muted)", flexShrink: 0 }}>{formatFileSize(f.size)}</span>}
              <span style={{ fontSize: 10, color: "var(--green)", flexShrink: 0, fontWeight: 600 }}>⬇ Download</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// MentionTextarea - textarea with @ user picker for Jira comments
function MentionTextarea({ value, onChange, onSegmentsChange, config, isJira, placeholder }) {
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionAt, setMentionAt] = useState(-1); // cursor position of @ sign
  const [segments, setSegments] = useState([]); // for ADF posting
  const [searching, setSearching] = useState(false);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimer = useRef(null);
  const justSelected = useRef(false); // suppress dropdown re-open after user selection

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    onChange(val);

    if (!isJira) return;

    // If a user was just selected from the dropdown, suppress re-opening
    if (justSelected.current) {
      justSelected.current = false;
      setMentionOpen(false);
      setMentionResults([]);
      return;
    }

    // Detect @ trigger - find last @ before cursor with no space after it
    const textBeforeCursor = val.substring(0, pos);
    const atMatch = textBeforeCursor.match(/@([\w.]*)$/);
    if (atMatch) {
      const query = atMatch[1];
      setMentionAt(textBeforeCursor.lastIndexOf("@"));
      setMentionQuery(query);
      setMentionOpen(true);
      // Debounce search
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(async () => {
        if (query.length < 1) { setMentionResults([]); return; }
        setSearching(true);
        const results = await searchJiraUsers(config, query);
        setMentionResults(results);
        setSearching(false);
      }, 250);
    } else {
      setMentionOpen(false);
      setMentionResults([]);
    }
  };

  const selectUser = (user) => {
    const val = value;
    const before = val.substring(0, mentionAt);
    const after = val.substring(mentionAt + 1 + mentionQuery.length);
    const newVal = before + `@${user.displayName}` + after;
    justSelected.current = true; // prevent handleChange re-opening dropdown
    onChange(newVal);
    setMentionOpen(false);
    setMentionResults([]);

    // Build segments for ADF - split on all @mentions we've inserted
    // Re-parse the new value to find all @DisplayName tokens we've confirmed
    const newSegments = [...(segments.filter(s => s.type === "mention" && val.includes(`@${s.displayName}`))),
      { type: "mention", accountId: user.accountId, displayName: user.displayName }
    ];
    setSegments(newSegments);
    onSegmentsChange && onSegmentsChange(newVal, newSegments);

    // Restore focus
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + user.displayName.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (mentionOpen && mentionResults.length > 0) {
      if (e.key === "Escape") { setMentionOpen(false); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); return; }
    }
    // Auto-continue bullet points on Enter
    if (e.key === "Enter") {
      const val = e.target.value;
      const pos = e.target.selectionStart;
      const lineStart = val.lastIndexOf("\n", pos - 1) + 1;
      const currentLine = val.substring(lineStart, pos);
      if (currentLine.startsWith("- ") || currentLine.startsWith("* ")) {
        e.preventDefault();
        const newVal = val.substring(0, pos) + "\n- " + val.substring(pos);
        onChange(newVal);
        setTimeout(() => {
          if (textareaRef.current) textareaRef.current.setSelectionRange(pos + 3, pos + 3);
        }, 0);
      }
    }
  };

  // Build final segments for posting - reconcile text + confirmed mentions
  const buildSegments = () => {
    if (segments.length === 0) return null; // plain text, no mentions
    let remaining = value;
    const result = [];
    const confirmedMentions = segments.filter(s => value.includes(`@${s.displayName}`));
    if (confirmedMentions.length === 0) return null;
    for (const mention of confirmedMentions) {
      const token = `@${mention.displayName}`;
      const idx = remaining.indexOf(token);
      if (idx === -1) continue;
      if (idx > 0) result.push({ type: "text", text: remaining.substring(0, idx) });
      result.push({ type: "mention", accountId: mention.accountId, displayName: mention.displayName });
      remaining = remaining.substring(idx + token.length);
    }
    if (remaining) result.push({ type: "text", text: remaining });
    return result;
  };

  // Expose buildSegments via ref-like callback
  useEffect(() => {
    onSegmentsChange && onSegmentsChange(value, buildSegments());
  }, [value, segments]);

  return (
    <div style={{ position: "relative" }}>
      <textarea
        ref={textareaRef}
        className="notes-area"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {isJira && (
        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
          Type <strong style={{ color: "var(--green)" }}>@name</strong> to mention a Jira user
        </div>
      )}
      {mentionOpen && (
        <div ref={dropdownRef} style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 999,
          background: "var(--surface2)", border: "1px solid var(--green)", borderRadius: 5,
          boxShadow: "0 4px 16px #0008", maxHeight: 220, overflowY: "auto",
        }}>
          {searching && (
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-muted)" }}>Searching...</div>
          )}
          {!searching && mentionResults.length === 0 && mentionQuery.length > 0 && (
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-muted)" }}>No users found</div>
          )}
          {mentionResults.map(user => (
            <div key={user.accountId}
              onMouseDown={(e) => { e.preventDefault(); selectUser(user); }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)", fontSize: 12 }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
            >
              {user.avatarUrls?.["24x24"] && (
                <img src={user.avatarUrls["24x24"]} alt="" style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0 }} />
              )}
              <div>
                <div style={{ fontWeight: 600, color: "var(--text)" }}>{user.displayName}</div>
                {user.emailAddress && <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{user.emailAddress}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskDetailContent({ task, onComplete, onCompleteFlagged, onCreateOutlook, onSaveNote, onSaveDue, onTransition, onStatusChange, onUpdateTask, onDelete, onPromoteSubtask, onSaveTemplate, onCreateInJira, compact, jiraAssignees = [], config = {}, allManualTasks = [] }) {
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [newSubtask, setNewSubtask] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleVal, setTitleVal] = useState("");
  const [noteImages, setNoteImages] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [noteFiles, setNoteFiles] = useState([]); // pending file attachments for current note
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [allComments, setAllComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [editingJiraFields, setEditingJiraFields] = useState(false);
  const [jiraEditSummary, setJiraEditSummary] = useState("");
  const [jiraEditDesc, setJiraEditDesc] = useState("");
  const [jiraEditClient, setJiraEditClient] = useState("");
  const [savingJiraEdit, setSavingJiraEdit] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [noteSegments, setNoteSegments] = useState(null); // ADF segments for @mentions
  const [subtaskAllDonePrompt, setSubtaskAllDonePrompt] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskText, setEditingSubtaskText] = useState("");
  const [subtasksCollapsed, setSubtasksCollapsed] = useState(false);
  const [newSubtaskDue, setNewSubtaskDue] = useState("");
  const [newSubtaskLink, setNewSubtaskLink] = useState("");
  const [newSubtaskAssignee, setNewSubtaskAssignee] = useState("");
  const [expandedSubtaskId, setExpandedSubtaskId] = useState(null);
  const [subtaskNoteText, setSubtaskNoteText] = useState("");

  useEffect(() => {
    setNote("");
    setDue(task?.due || "");
    setEditingTitle(false);
    setTitleVal(task?.title || "");
    setNoteImages([]);
    setNoteSegments(null);
    setLightboxSrc(null);
    setNoteFiles([]);
    setShowAllComments(false);
    setAllComments([]);
    setEditingJiraFields(false);
  }, [task?.id]);

  // Listen for lightbox events fired from NoteEntry image thumbnails
  useEffect(() => {
    const handler = (e) => setLightboxSrc(e.detail.src);
    window.addEventListener("c365-lightbox", handler);
    return () => window.removeEventListener("c365-lightbox", handler);
  }, []);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        // Compress image to max 800px wide and JPEG quality 0.75 before storing
        // This keeps base64 size manageable for localStorage
        const img = new Image();
        img.onload = () => {
          const MAX = 800;
          const scale = img.width > MAX ? MAX / img.width : 1;
          const canvas = document.createElement("canvas");
          canvas.width  = Math.round(img.width  * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const compressed = canvas.toDataURL("image/jpeg", 0.75);
          setNoteImages(prev => [...prev, compressed]);
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  if (!task) return null;

  const today = new Date().toISOString().split("T")[0];

  // Lightbox overlay - renders over everything when an image is clicked
  const lightbox = lightboxSrc ? (
    <div
      onClick={() => setLightboxSrc(null)}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out",
        padding: 20,
      }}>
      <img src={lightboxSrc} alt="full size"
        style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 6, boxShadow: "0 8px 40px rgba(0,0,0,0.8)" }}
        onClick={e => e.stopPropagation()} />
      <button onClick={() => setLightboxSrc(null)}
        style={{ position: "fixed", top: 20, right: 24, background: "none", border: "1px solid #ffffff60", color: "#fff", borderRadius: 4, padding: "4px 12px", cursor: "pointer", fontSize: 18, fontWeight: 700 }}>
        ✕
      </button>
      <a href={lightboxSrc} download="image.jpg"
        style={{ position: "fixed", bottom: 24, right: 24, background: "var(--green)", color: "#fff", borderRadius: 4, padding: "6px 14px", fontSize: 12, fontWeight: 600, textDecoration: "none" }}
        onClick={e => e.stopPropagation()}>
        ⬇ Download
      </a>
    </div>
  ) : null;
  const isOverdue = task.due && !task.done && task.due < today;
  const notes = task.localNotes || [];
  const tags = task.tags || [];
  const subtasks = task.subtasks || [];

  const handleSaveNote = async () => {
    if (!note.trim() && noteImages.length === 0 && noteFiles.length === 0) return;
    setSavingNote(true);
    setUploadingFiles(noteFiles.length > 0);
    // Upload file attachments to server first, store returned metadata
    const uploadedFiles = [];
    for (const file of noteFiles) {
      try {
        const result = await uploadTaskFile(file, task.id);
        uploadedFiles.push(result);
      } catch (e) {
        console.warn("[Files] Upload failed for", file.name, e.message);
      }
    }
    setUploadingFiles(false);
    const date = new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const author = localStorage.getItem("taskflow_jira_displayname") || "";
    const newNote = { text: note, date, author, images: noteImages, files: uploadedFiles };
    onUpdateTask(task.id, { localNotes: [...notes, newNote] });
    if (note.trim()) {
      if (task.source === "jira") await onSaveNote(task, noteSegments || note);
      else onSaveNote(task, note);
    }
    setNote("");
    setNoteImages([]);
    setNoteFiles([]);
    setNoteSegments(null);
    setSavingNote(false);
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    const updated = [...new Set([...tags, newTag.trim()])];
    onUpdateTask(task.id, { tags: updated });
    setNewTag("");
  };

  const removeTag = (tag) => {
    onUpdateTask(task.id, { tags: tags.filter(t => t !== tag) });
  };

  const addSubtask = async () => {
    if (!newSubtask.trim()) return;
    const assignee = newSubtaskAssignee || "";
    const newSt = { id: Date.now(), text: newSubtask.trim(), done: false, status: "To Start", due: newSubtaskDue || null, link: newSubtaskLink.trim() || null, assignedTo: assignee };
    const updated = [...subtasks, newSt];
    onUpdateTask(task.id, { subtasks: updated });
    setNewSubtask("");
    setNewSubtaskDue("");
    setNewSubtaskLink("");
    setNewSubtaskAssignee("");
    // Notify assignee if set and not self
    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
    if (assignee && assignee !== myName && config.msToken) {
      try {
        const email = await lookupJiraUserEmail(config, assignee);
        if (email) await sendOutlookEmail(config, {
          to: email,
          subject: `Subtask assigned to you: ${newSt.text.substring(0, 55)}`,
          body: `
            <p>Hi ${assignee.split(" ")[0]},</p>
            <p>${myName || "Someone"} has assigned a subtask to you in the C365Cloud Task Manager.</p>
            <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
              <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Subtask:</td><td style="padding:6px 12px">${newSt.text}</td></tr>
              <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Parent task:</td><td style="padding:6px 12px">${task.title}</td></tr>
              ${newSt.due ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Due:</td><td style="padding:6px 12px">${newSt.due}</td></tr>` : ""}
            </table>
            <p style="color:#888;font-size:12px">This subtask has been logged in the C365Cloud Task Manager.</p>
          `,
        });
      } catch (err) { console.warn("[Subtask] Email failed:", err.message); }
    }
  };

  const subtaskStatusStyle = (status) => {
    if (status === "Done")             return { color: "var(--green)",   borderColor: "#47ac4c50", background: "#47ac4c15" };
    if (status === "In Progress")      return { color: "#4c9aff",        borderColor: "#4c9aff50", background: "#4c9aff15" };
    if (status === "Waiting Response") return { color: "var(--amber)",   borderColor: "#F1901E50", background: "#F1901E15" };
    return { color: "var(--text-muted)", borderColor: "var(--border)", background: "var(--surface2)" }; // To Start
  };

  const cycleSubtaskStatus = (id) => {
    const order = ["To Start", "In Progress", "Waiting Response", "Done"];
    const updated = subtasks.map(s => {
      if (s.id !== id) return s;
      const currentIdx = order.indexOf(s.status || "To Start");
      const nextStatus = order[(currentIdx + 1) % order.length];
      return { ...s, status: nextStatus, done: nextStatus === "Done" };
    });
    onUpdateTask(task.id, { subtasks: updated });
    const allDone = updated.every(s => s.done);
    if (allDone && updated.length > 0 && !task.done) setSubtaskAllDonePrompt(true);
  };

  const setSubtaskStatus = (id, status) => {
    const updated = subtasks.map(s => s.id === id ? { ...s, status, done: status === "Done" } : s);
    onUpdateTask(task.id, { subtasks: updated });
    const allDone = updated.every(s => s.done);
    if (allDone && updated.length > 0 && !task.done) setSubtaskAllDonePrompt(true);
  };

  const toggleSubtask = (id) => {
    const now = new Date().toISOString();
    const updated = subtasks.map(s => s.id === id
      ? { ...s, done: !s.done, doneAt: !s.done ? now : null, newReply: false }
      : s);
    onUpdateTask(task.id, { subtasks: updated });
    // Check if all subtasks are now done
    const allDone = updated.every(s => s.done);
    if (allDone && updated.length > 0 && !task.done) {
      setSubtaskAllDonePrompt(true);
    }
  };

  const removeSubtask = (id) => {
    onUpdateTask(task.id, { subtasks: subtasks.filter(s => s.id !== id) });
  };

  const addSubtaskNote = async (subtaskId, text) => {
    if (!text.trim()) return;
    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
    const date = new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const note = { text: text.trim(), date, author: myName };
    const sub = subtasks.find(s => s.id === subtaskId);
    const updated = subtasks.map(s => s.id === subtaskId ? { ...s, notes: [...(s.notes || []), note] } : s);
    onUpdateTask(task.id, { subtasks: updated });
    setSubtaskNoteText("");
    // Notify the subtask assignee if it's someone other than the note author
    if (sub?.assignedTo && sub.assignedTo !== myName && config.msToken) {
      try {
        const email = await lookupJiraUserEmail(config, sub.assignedTo);
        if (email) await sendOutlookEmail(config, {
          to: email,
          subject: `New comment on subtask: ${sub.text?.substring(0, 50)}`,
          body: `
            <p>Hi ${sub.assignedTo.split(" ")[0]},</p>
            <p>${myName || "Someone"} added a comment to a subtask assigned to you.</p>
            <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
              <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Subtask:</td><td style="padding:6px 12px">${sub.text}</td></tr>
              <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Parent task:</td><td style="padding:6px 12px">${task.title}</td></tr>
              <tr><td style="padding:6px 12px;font-weight:bold;color:#555;vertical-align:top">Comment:</td>
              <td style="padding:6px 12px"><div style="background:#f5f5f5;border-left:3px solid #1B3A6B;padding:8px 12px;font-size:13px">${(note.text||"").replace(/\n/g,"<br>")}</div>
              <span style="font-size:11px;color:#888">${note.date} by ${myName}</span></td></tr>
            </table>
            <p style="color:#888;font-size:12px">This comment was added in the C365Cloud Task Manager.</p>
          `,
        });
      } catch (err) { console.warn("[Subtask note] Email failed:", err.message); }
    }
  };

  const deleteSubtaskNote = (subtaskId, noteIdx) => {
    const updated = subtasks.map(s => s.id === subtaskId ? { ...s, notes: (s.notes || []).filter((_, i) => i !== noteIdx) } : s);
    onUpdateTask(task.id, { subtasks: updated });
  };

  const saveSubtaskEdit = (id) => {
    if (!editingSubtaskText.trim()) return;
    const updated = subtasks.map(s => s.id === id ? { ...s, text: editingSubtaskText.trim() } : s);
    onUpdateTask(task.id, { subtasks: updated });
    setEditingSubtaskId(null);
    setEditingSubtaskText("");
  };

  const completedSubtasks = subtasks.filter(s => s.done).length;

  // Export this task + subtasks to Excel
  const ensureXLSX = () => new Promise((resolve, reject) => {
    if (window.XLSX) { resolve(window.XLSX); return; }
    const script = document.createElement("script");
    script.src = "https://cdn.sheetjs.com/xlsx-0.20.2/package/dist/xlsx.full.min.js";
    script.onload = () => resolve(window.XLSX);
    script.onerror = () => reject(new Error("Could not load the Excel library"));
    document.head.appendChild(script);
  });

  const exportTaskToExcel = async () => {
    try {
      const XLSX = await ensureXLSX();
      // Helper: merge a notes array into one date-differentiated cell
      const mergeNotes = (notes) => (notes || [])
        .map(n => `[${n.date || ""}${n.author ? " - " + n.author : ""}] ${n.text || ""}`)
        .join("\n\n");

      const header = [
        "Row Type", "Jira Key", "Title", "Status", "Jira Status", "Assignee",
        "Created By", "Due Date", "Priority", "Done", "Web Link",
        "Task Comments", "Subtask Comments",
      ];

      const rows = [header];

      // Parent task row
      rows.push([
        "Task",
        task.jiraKey || "",
        task.title || "",
        task.jiraStatus || task.status || "",
        task.jiraStatus || "",
        task.assignedTo || task.assignee || "",
        task.createdBy || "",
        task.due || "",
        task.priority || "",
        task.done ? "Yes" : "No",
        task.webLink || task.jiraUrl || "",
        mergeNotes(task.localNotes),   // Task Comments column
        "",                            // Subtask Comments (blank on parent row)
      ]);

      // One row per subtask
      (subtasks || []).forEach(s => {
        rows.push([
          "Subtask",
          extractJiraKeyFromLink(s.link) || "",
          s.text || "",
          s.status || (s.done ? "Done" : "To Start"),
          s.jiraLinkStatus || "",
          s.assignedTo || "",
          "",                          // subtasks don't carry createdBy
          s.due || "",
          "",                          // subtasks don't carry priority
          s.done ? "Yes" : "No",
          s.link || "",
          "",                          // Task Comments (blank on subtask rows)
          mergeNotes(s.notes),         // Subtask Comments column
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 10 }, { wch: 14 }, { wch: 50 }, { wch: 16 }, { wch: 16 }, { wch: 18 },
        { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 40 },
        { wch: 60 }, { wch: 60 },
      ];
      // Enable wrap on the two comment columns so line breaks show
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let r = range.s.r; r <= range.e.r; r++) {
        [11, 12].forEach(colIdx => {
          const cell = ws[XLSX.utils.encode_cell({ r, c: colIdx })];
          if (cell) cell.s = { alignment: { wrapText: true, vertical: "top" } };
        });
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Task Export");
      const safeName = (task.title || "task").replace(/[^a-z0-9]/gi, "_").substring(0, 60);
      XLSX.writeFile(wb, `${safeName}.xlsx`);
    } catch (e) {
      console.warn("[Export] Failed:", e.message);
      alert("Could not export to Excel: " + e.message);
    }
  };

  return (
    <>
      {lightbox}
      {task.source !== "jira" && !editingTitle ? (
        <div style={{ marginTop: -4, marginBottom: 2 }}>
          <button className="btn" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => { setTitleVal(task.title); setEditingTitle(true); }}>✎ Rename task</button>
        </div>
      ) : null}
      {task.source !== "jira" && editingTitle ? (
        <div className="detail-field">
          <div className="detail-section-label">Task Name</div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="editable-title"
              value={titleVal}
              onChange={e => setTitleVal(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") { onUpdateTask(task.id, { title: titleVal }); setEditingTitle(false); }
                if (e.key === "Escape") setEditingTitle(false);
              }}
              autoFocus
            />
            <button className="btn primary" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => { onUpdateTask(task.id, { title: titleVal }); setEditingTitle(false); }}>✓</button>
            <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => setEditingTitle(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
      <div className="detail-row-2col">
      <div className="detail-field">
        <div className="detail-section-label">Source</div>
        <div className="detail-value">{task.source === "outlook" ? "📧 Microsoft Outlook" : task.source === "jira" ? "🔷 Jira" : "✏️ Manual"}</div>
      </div>
      <WebLinkField task={task} onUpdateTask={onUpdateTask} />
      </div>
      <div className="detail-row-2col">
      {task.source === "manual" && task.createdBy ? (
        <div className="detail-field">
          <div className="detail-section-label">Created by</div>
          <div className="detail-value" style={{ fontSize: 12, color: "var(--text-muted)" }}>👤 {task.createdBy}</div>
        </div>
      ) : <div />}
      {(task.source === "manual" || task.source === "outlook" || task.assignee) ? (
        <div className="detail-field">
          <div className="detail-section-label">Assignee</div>
          {task.source === "jira" ? (
            <div className="detail-value">👤 {task.assignee || "Unassigned"}</div>
          ) : (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <select
                style={{ flex: 1, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: "6px 10px", color: "var(--text)", fontSize: 12, outline: "none", fontFamily: "inherit" }}
                value={task.assignedTo || task.assignee || ""}
                onChange={async (e) => {
                  const newAssignee = e.target.value;
                  const prev = task.assignedTo || task.assignee || "";
                  onUpdateTask(task.id, { assignedTo: newAssignee });
                  // Send notification email if assigning to someone else
                  if (newAssignee && newAssignee !== prev && config.msToken) {
                    try {
                      const email = await lookupJiraUserEmail(config, newAssignee);
                      if (email) {
                        await sendOutlookEmail(config, {
                          to: email,
                          subject: `Task assigned to you: ${task.title?.substring(0, 60)}`,
                          body: `
                            <p>Hi ${newAssignee.split(" ")[0]},</p>
                            <p>A task has been assigned to you in the C365Cloud Task Manager.</p>
                            <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
                              <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Task:</td><td style="padding:6px 12px">${task.title}</td></tr>
                              ${task.due ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Due:</td><td style="padding:6px 12px">${task.due}</td></tr>` : ""}
                              ${task.priority ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Priority:</td><td style="padding:6px 12px">${task.priority}</td></tr>` : ""}
                              ${task.description ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555;vertical-align:top">Description:</td><td style="padding:6px 12px">${task.description.replace(/\n/g, "<br>")}</td></tr>` : ""}
                              ${(task.localNotes || []).length > 0 ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555;vertical-align:top">Notes:</td><td style="padding:6px 12px">${(task.localNotes || []).map(n => `<div style="margin-bottom:6px;padding:6px 8px;background:#f5f5f5;border-left:3px solid #1B3A6B;font-size:13px">${(n.text||"").replace(/\n/g,"<br>")}<span style="font-size:11px;color:#888;display:block;margin-top:3px">${n.date||""}${n.author ? " · " + n.author : ""}</span></div>`).join("")}</td></tr>` : ""}
                            </table>
                            <p style="color:#888;font-size:12px">This task has been logged in the C365Cloud Task Manager.</p>
                          `,
                        });
                      }
                    } catch (e) { console.warn("[Assign] Email notification failed:", e.message); }
                  }
                }}>
                <option value="">- Unassigned -</option>
                {jiraAssignees.map(name => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
          )}
        </div>
      ) : <div />}
      </div>
      {task.source === "jira" && task.jiraUrl && task.jiraUrl !== "#" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="btn" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => window.open(task.jiraUrl, "_blank")}>
            🔷 Open in Jira ↗
          </button>
          {!editingJiraFields && (
            <button className="btn" style={{ fontSize: 11, padding: "5px 12px" }}
              onClick={() => {
                setJiraEditSummary(task.title.replace(/^\[[^\]]+\]\s*/, ""));
                setJiraEditDesc(task.description || "");
                setJiraEditClient(task.affectedClient || "");
                setEditingJiraFields(true);
              }}>✎ Edit Ticket</button>
          )}
          {!editingJiraFields && (
            <button className="btn" style={{ fontSize: 11, padding: "5px 12px" }}
              onClick={() => setShowImageUpload(v => !v)}>
              📷 {showImageUpload ? "Cancel" : "Send Image"}
            </button>
          )}
        </div>
      )}
      {task.source === "jira" && task.jiraKey && showImageUpload && !editingJiraFields && (
        <JiraImageUpload
          issueKey={task.jiraKey}
          onClose={() => setShowImageUpload(false)}
        />
      )}
      {task.source === "jira" && editingJiraFields && (
        <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="detail-section-label">Edit Jira Ticket [{task.jiraKey}]</div>
          <div className="form-row">
            <label className="form-label">Summary</label>
            <input className="form-input" value={jiraEditSummary} onChange={e => setJiraEditSummary(e.target.value)} placeholder="Ticket summary" />
          </div>
          <div className="form-row">
            <label className="form-label">Description</label>
            <textarea className="notes-area" value={jiraEditDesc} onChange={e => setJiraEditDesc(e.target.value)} placeholder="Description" style={{ minHeight: 60 }} />
          </div>
          <div className="form-row">
            <label className="form-label">Affected Client</label>
            <input className="form-input" value={jiraEditClient} onChange={e => setJiraEditClient(e.target.value)} placeholder="Client name" />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn primary" style={{ fontSize: 11 }} disabled={savingJiraEdit}
              onClick={async () => {
                setSavingJiraEdit(true);
                try {
                  const fields = {
                    summary: jiraEditSummary.trim(),
                    description: {
                      type: "doc", version: 1,
                      content: [{ type: "paragraph", content: [{ type: "text", text: jiraEditDesc.trim() || " " }] }]
                    },
                  };
                  if (jiraEditClient.trim()) fields[JIRA_FIELD_AFFECTED_CLIENTS] = [{ value: jiraEditClient.trim() }];
                  await updateJiraIssue(window.__c365config__ || {}, task.jiraKey, fields);
                  onUpdateTask(task.id, {
                    title: `[${task.jiraKey}] ${jiraEditSummary.trim()}`,
                    description: jiraEditDesc.trim(),
                    affectedClient: jiraEditClient.trim() || task.affectedClient,
                  });
                  setEditingJiraFields(false);
                  if (window.__c365showToast__) window.__c365showToast__(`[${task.jiraKey}] updated in Jira`);
                } catch(e) {
                  if (window.__c365showToast__) window.__c365showToast__(e.message, "error");
                }
                setSavingJiraEdit(false);
              }}>
              {savingJiraEdit ? "Saving…" : "💾 Save to Jira"}
            </button>
            <button className="btn" style={{ fontSize: 11 }} onClick={() => setEditingJiraFields(false)}>Cancel</button>
          </div>
        </div>
      )}
      {task.source === "jira" && (() => {
        const sla = getSLAInfo(task);
        if (!sla) return null;
        return (
          <div className="detail-field">
            <div className="detail-section-label">SLA Status</div>
            <div style={{
              background: sla.breached ? "#e3252915" : sla.warning ? "#F1901E15" : "#47ac4c15",
              border: `1px solid ${sla.breached ? "#e3252950" : sla.warning ? "#F1901E50" : "#47ac4c50"}`,
              borderRadius: 4, padding: "8px 12px",
              display: "flex", alignItems: "center", gap: 12
            }}>
              <span style={{ fontSize: 20 }}>{sla.breached ? "🚨" : sla.warning ? "⚠️" : "✅"}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: sla.breached ? "var(--red)" : sla.warning ? "var(--amber)" : "var(--green)" }}>
                  {sla.breached ? `Breached by ${Math.abs(sla.remaining)} business day(s)` : sla.warning ? `${sla.remaining} business day(s) remaining` : `Within SLA - ${sla.remaining} day(s) remaining`}
                </div>
                <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                  {sla.days} business days elapsed · {sla.threshold}d threshold for {task.priority || "medium"} priority
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {task.source === "jira" && (task.jiraEffort != null || task.jiraValue != null || task.jiraScore != null || task.jiraPriority != null) && (
        <div className="detail-field">
          <div className="detail-section-label">Jira Scores</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, width: "100%", flexWrap: "wrap" }}>
              {task.jiraEffort != null && (
                <div style={{ flex: 1, minWidth: 70, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, padding: "6px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>Effort</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b" }}>{String(task.jiraEffort)}</div>
                </div>
              )}
              {task.jiraValue != null && (
                <div style={{ flex: 1, minWidth: 70, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, padding: "6px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>Value</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#60a5fa" }}>{String(task.jiraValue)}</div>
                </div>
              )}
              {task.jiraPriority != null && (
                <div style={{ flex: 1, minWidth: 70, background: "var(--surface2)", border: "1px solid #f472b640", borderRadius: 4, padding: "6px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>Priority</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#f472b6" }}>{task.jiraPriority}</div>
                </div>
              )}
            </div>
            {task.jiraScore != null && (
              <div style={{ width: "100%", background: "var(--surface2)", border: "1px solid #fbbf2440", borderRadius: 4, padding: "6px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>Overall Score</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#fbbf24" }}>{String(task.jiraScore)}</div>
              </div>
            )}
          </div>
        </div>
      )}
      {task.mentioned && (
        <div className="detail-field">
          <div className="detail-section-label">Mention</div>
          <div className="detail-value" style={{ color: "#47ac4c" }}>💬 You were @mentioned in a comment</div>
        </div>
      )}
      {task.sdm && (
        <div className="detail-field">
          <div className="detail-section-label">SDM</div>
          <div className="detail-value" style={{ color: task.waitingRole === "SDM" ? "#F1901E" : "inherit" }}>
            {task.waitingRole === "SDM" ? "⏳ " : ""}{task.sdm}
          </div>
        </div>
      )}
      {task.pc && (
        <div className="detail-field">
          <div className="detail-section-label">Project Coordinator</div>
          <div className="detail-value" style={{ color: task.waitingRole === "PC" ? "#F1901E" : "inherit" }}>
            {task.waitingRole === "PC" ? "⏳ " : ""}{task.pc}
          </div>
        </div>
      )}
      {task.senderName && (
        <div className="detail-field">
          <div className="detail-section-label">From</div>
          <div className="detail-value">{task.senderName}{task.senderEmail ? ` <${task.senderEmail}>` : ""}
            {task.replied && (
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, background: "rgba(71,172,76,0.15)", border: "1px solid var(--green)", color: "var(--green)", borderRadius: 3, padding: "1px 7px" }}>
                ✓ Replied {task.repliedAt ? new Date(task.repliedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}
              </span>
            )}
          </div>
        </div>
      )}
      {task.receivedDate && (
        <div className="detail-field">
          <div className="detail-section-label">Received</div>
          <div className="detail-value">{task.receivedDate}</div>
        </div>
      )}

      {task.source === "jira" && task.latestComment && (
        <div className="detail-field">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div className="detail-section-label" style={{ margin: 0 }}>
              {showAllComments ? "All Comments" : "Latest Comment"}
            </div>
            <button className="btn" style={{ fontSize: 9, padding: "1px 8px" }}
              onClick={async () => {
                if (!showAllComments && allComments.length === 0) {
                  setLoadingComments(true);
                  const comments = await fetchJiraComments(window.__c365config__ || {}, task.jiraKey);
                  setAllComments(comments);
                  setLoadingComments(false);
                }
                setShowAllComments(v => !v);
              }}>
              {loadingComments ? "Loading…" : showAllComments ? "▲ Collapse" : "▼ Show all"}
            </button>
          </div>
          {showAllComments ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
              {allComments.map(c => (
                <div key={c.id} style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderLeft: "3px solid #47ac4c60", borderRadius: 4, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: "var(--green)", fontWeight: 600 }}>💬 {c.author}</span>
                    <span>{c.date}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{c.text}</div>
                </div>
              ))}
              {allComments.length === 0 && !loadingComments && <div style={{ fontSize: 11, color: "var(--text-dim)", fontStyle: "italic" }}>No comments</div>}
            </div>
          ) : (
            <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderLeft: "3px solid #47ac4c60", borderRadius: 4, padding: "8px 10px" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ color: "var(--green)", fontWeight: 600 }}>💬 {task.latestComment.author}</span>
                <span>{task.latestComment.date}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{task.latestComment.text}</div>
            </div>
          )}
        </div>
      )}

      <div className="detail-row-2col">
      {task.source !== "jira" ? (
        <div className="detail-field">
          <div className="detail-section-label">Priority</div>
          <select className="form-select" style={{ background: "#fff", color: "#1a1a1a", width: "100%" }}
            value={task.priority || "medium"}
            onChange={e => onUpdateTask(task.id, { priority: e.target.value })}>
            <option value="high">▲ High</option>
            <option value="medium">● Medium</option>
            <option value="low">▼ Low</option>
          </select>
        </div>
      ) : <div />}
      <div className="detail-field">
        <div className="detail-section-label">Tags</div>
        <div className="tag-input-row">
          {tags.map((tag) => (
            <TagChip key={tag} tag={tag} onRemove={() => removeTag(tag)} />
          ))}
          <input
            className="tag-add-input"
            placeholder="+ Add tag"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addTag()}
          />
        </div>
      </div>
      </div>

      <div className="detail-row-2col">
      {task.source === "manual" ? (
        <div className="detail-field">
          <div className="detail-section-label">Start Date</div>
          <input type="date" className="form-input" value={task.startDate || ""} onChange={(e) => onUpdateTask(task.id, { startDate: e.target.value || null })} style={{ width: "100%" }} />
        </div>
      ) : <div />}
      <div className="detail-field">
        <div className="detail-section-label">Due Date</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" className="form-input" value={due} onChange={(e) => setDue(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
          <button className="btn" style={{ fontSize: 11, padding: "6px 10px", flexShrink: 0 }} onClick={() => onSaveDue(task, due)}>Save</button>
        </div>
        {isOverdue && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 4 }}>⚠ Overdue</div>}
      </div>
      </div>

      {task.source === "manual" && (
        <div className="detail-field">
          <div className="detail-section-label">Repeat</div>
          <select className="form-select" value={task.recur || ""} onChange={e => onUpdateTask(task.id, { recur: e.target.value })} style={{ background: "#fff", color: "#1a1a1a" }}>
            {RECUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {task.recur && task.due && <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 3 }}>Next: {formatDate(nextRecurDate(task.due, task.recur))}</div>}
        </div>
      )}

      <div className="detail-field">
        <div className="detail-section-label">Status</div>
        {task.source === "jira" ? (
          <select className="form-select" style={{ background: "#fff", color: "#1a1a1a" }}
            value=""
            onChange={async (e) => {
              if (!e.target.value) return;
              const [id, name] = e.target.value.split("|");
              try { await onTransition(task, id, name); e.target.value = ""; } catch {}
            }}>
            <option value="">{task.jiraStatus || task.status || "Current status"}{(task.transitions || []).length > 0 ? " (change…)" : ""}</option>
            {(task.transitions || []).map((t) => (
              <option key={t.id} value={`${t.id}|${t.name}`}>→ {t.name}</option>
            ))}
          </select>
        ) : (
          <select className="form-select" style={{ background: "#fff", color: "#1a1a1a" }} value={task.status} onChange={(e) => onStatusChange(task, e.target.value)}>
            <option value="To Start">To Start</option>
            <option value="In Progress">In Progress</option>
            <option value="Waiting Response">Waiting Response</option>
            <option value="Done">Done</option>
          </select>
        )}
      </div>

      {task.source === "manual" && (
        <div className="detail-field">
          <div className="detail-section-label">Depends on (predecessor)</div>
          <select className="form-select" style={{ background: "#fff", color: "#1a1a1a" }}
            value={task.dependsOn || ""}
            onChange={(e) => onUpdateTask(task.id, { dependsOn: e.target.value || null })}>
            <option value="">- None -</option>
            {(allManualTasks || []).filter(mt => mt.id !== task.id).map(mt => (
              <option key={mt.id} value={mt.id}>{mt.title}</option>
            ))}
          </select>
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 3 }}>This task starts after the selected task. Shown as a link on the Gantt.</div>
        </div>
      )}

      {task.description && <div className="detail-field"><div className="detail-section-label">Description</div><div className="detail-desc">{task.description}</div></div>}

      {subtaskAllDonePrompt && (
        <div style={{ position: "fixed", inset: 0, background: "#00000070", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSubtaskAllDonePrompt(false)}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--green)", borderRadius: 8, padding: 24, maxWidth: 340, width: "90%", boxShadow: "0 8px 32px #000a" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: 16, marginBottom: 8, color: "var(--text)" }}>All subtasks complete!</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 18 }}>Would you like to mark the whole task as done?</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setSubtaskAllDonePrompt(false)}>Not yet</button>
              <button className="btn primary" onClick={() => { setSubtaskAllDonePrompt(false); onComplete(); }}>✓ Complete Task</button>
            </div>
          </div>
        </div>
      )}
      <div className="detail-field">
        <div className="detail-section-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Subtasks {subtasks.length > 0 && `(${completedSubtasks}/${subtasks.length})`}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn" style={{ fontSize: 9, padding: "1px 7px" }}
              onClick={exportTaskToExcel} title="Download this task and its subtasks as an Excel file">
              📊 Export
            </button>
            {subtasks.length > 0 && (
              <button className="btn" style={{ fontSize: 9, padding: "1px 7px" }}
                onClick={() => setSubtasksCollapsed(v => !v)}>
                {subtasksCollapsed ? "▶ Show" : "▼ Hide"}
              </button>
            )}
          </div>
        </div>
        {!subtasksCollapsed && subtasks.map(s => (
          <div key={s.id} className="subtask-item" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 28 }}>
              {editingSubtaskId === s.id ? (
                <input
                  className="form-input"
                  style={{ flex: 1, fontSize: 11, padding: "2px 6px" }}
                  value={editingSubtaskText}
                  onChange={e => setEditingSubtaskText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveSubtaskEdit(s.id); if (e.key === "Escape") { setEditingSubtaskId(null); } }}
                  autoFocus
                />
              ) : (
                <span className={`subtask-text ${s.done ? "done" : ""}`} style={{ flex: 1 }}>
                  {s.text}
                  {s.replied && !s.newReply && (
                    <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 600, background: "rgba(71,172,76,0.15)", border: "1px solid var(--green)", color: "var(--green)", borderRadius: 3, padding: "1px 5px" }}>
                      ✓ Replied
                    </span>
                  )}
                  {s.newReply && (
                    <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 600, background: "rgba(241,144,30,0.15)", border: "1px solid var(--amber)", color: "var(--amber)", borderRadius: 3, padding: "1px 5px" }}>
                      💬 New reply
                    </span>
                  )}
                </span>
              )}
              {editingSubtaskId === s.id ? (
                <button className="btn primary" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => saveSubtaskEdit(s.id)}>✓</button>
              ) : (
                <button className="btn" style={{ fontSize: 10, padding: "2px 6px", flexShrink: 0 }} title="Edit subtask" onClick={() => { setEditingSubtaskId(s.id); setEditingSubtaskText(s.text); }}>✎</button>
              )}
              <button
                className="btn"
                style={{ fontSize: 10, padding: "2px 6px", flexShrink: 0, color: expandedSubtaskId === s.id ? "var(--green)" : undefined }}
                title="Add/edit due date & link"
                onClick={() => { setExpandedSubtaskId(expandedSubtaskId === s.id ? null : s.id); setSubtaskNoteText(""); }}
              >⋯</button>
              <button className="btn" style={{ fontSize: 10, padding: "2px 6px", color: "var(--amber)", borderColor: "var(--amber)", flexShrink: 0 }} title="Promote to main task" onClick={() => onPromoteSubtask && onPromoteSubtask(task.id, s.id, s.text)}>↑ Task</button>
              <button className="subtask-remove" onClick={() => removeSubtask(s.id)}>{"✕"}</button>
            </div>
            {/* Status + date + link on the same meta row below the title */}
            {expandedSubtaskId !== s.id && (
              <div className="subtask-meta" style={{ paddingLeft: 0, gap: 6 }}>
                {extractJiraKeyFromLink(s.link) && s.jiraLinkStatus ? (
                  <span
                    title="Live status from linked Jira card"
                    style={{
                      ...subtaskStatusStyle(s.status || (s.done ? "Done" : "To Start")),
                      fontSize: 9, fontWeight: 600,
                      border: "1px solid",
                      borderRadius: 3,
                      padding: "3px 6px",
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                    }}>
                    🔷 {s.jiraLinkStatus}
                  </span>
                ) : (
                  <select
                    value={s.status || (s.done ? "Done" : "To Start")}
                    onChange={e => { e.stopPropagation(); setSubtaskStatus(s.id, e.target.value); }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      ...subtaskStatusStyle(s.status || (s.done ? "Done" : "To Start")),
                      fontSize: 9, fontWeight: 600,
                      border: "1px solid",
                      borderRadius: 3,
                      padding: "2px 4px",
                      cursor: "pointer",
                      width: 90,
                      flexShrink: 0,
                    }}>
                    <option value="To Start">To Start</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Waiting Response">Waiting</option>
                    <option value="Done">Done</option>
                  </select>
                )}
                {s.due && (
                  <span className="tag due-tag" style={{ fontSize: 9 }}>📅 {formatDate(s.due)}</span>
                )}
                {s.assignedTo && (
                  <span className="tag" style={{ fontSize: 9, color: "#47ac4c", borderColor: "#47ac4c50", background: "#47ac4c10", fontWeight: 600 }}>👤 {s.assignedTo}</span>
                )}
                {(s.notes || []).length > 0 && (
                  <span className="tag" style={{ fontSize: 9, color: "#4c9aff", borderColor: "#4c9aff50", background: "#4c9aff10", cursor: "pointer" }}
                    onClick={() => { setExpandedSubtaskId(expandedSubtaskId === s.id ? null : s.id); setSubtaskNoteText(""); }}>
                    💬 {(s.notes || []).length}
                  </span>
                )}
                {s.link && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <a href={s.link} target="_blank" rel="noreferrer"
                      style={{ color: extractJiraKeyFromLink(s.link) ? "#4c9aff" : "var(--green)", fontSize: 10, fontWeight: extractJiraKeyFromLink(s.link) ? 600 : 400 }}>
                      {extractJiraKeyFromLink(s.link) ? "🔷 " + extractJiraKeyFromLink(s.link) : "🔗 " + (s.link.length > 40 ? s.link.substring(0, 40) + "…" : s.link)}
                    </a>
                    {s.jiraLinkStatus && (
                      <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
                        background: "#4c9aff20", color: "#4c9aff", border: "1px solid #4c9aff40" }}
                        title="Live Jira status - updates on sync">
                        ↺ {s.jiraLinkStatus}
                      </span>
                    )}
                    {s.jiraLinkAssignee && (
                      <span style={{ fontSize: 8, color: "var(--text-muted)" }}>👤 {s.jiraLinkAssignee}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {expandedSubtaskId === s.id && (
              <div className="subtask-extra-row" style={{ paddingLeft: 22, width: "100%", flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: "0 0 auto" }}>
                  <label style={{ fontSize: 9, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Start</label>
                  <input
                    type="date"
                    className="form-input"
                    style={{ fontSize: 11, width: 130 }}
                    value={s.startDate || ""}
                    title="Start date"
                    onChange={e => {
                      const updated = subtasks.map(x => x.id === s.id ? { ...x, startDate: e.target.value || null } : x);
                      onUpdateTask(task.id, { subtasks: updated });
                    }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: "0 0 auto" }}>
                  <label style={{ fontSize: 9, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Due</label>
                  <input
                    type="date"
                    className="form-input"
                    style={{ fontSize: 11, width: 130 }}
                    value={s.due || ""}
                    title="Due date"
                    onChange={e => {
                      const updated = subtasks.map(x => x.id === s.id ? { ...x, due: e.target.value || null } : x);
                      onUpdateTask(task.id, { subtasks: updated });
                    }}
                  />
                </div>
                <input
                  className="form-input"
                  style={{ fontSize: 11, flex: 1, minWidth: 120 }}
                  placeholder="https://..."
                  value={s.link || ""}
                  onChange={e => {
                    const updated = subtasks.map(x => x.id === s.id ? { ...x, link: e.target.value || null } : x);
                    onUpdateTask(task.id, { subtasks: updated });
                  }}
                />
                <select
                  className="form-input"
                  style={{ fontSize: 11, flex: "0 0 auto", width: 150 }}
                  value={s.assignedTo || ""}
                  onChange={async e => {
                    const newAssignee = e.target.value;
                    const prev = s.assignedTo || "";
                    const updated = subtasks.map(x => x.id === s.id ? { ...x, assignedTo: newAssignee } : x);
                    onUpdateTask(task.id, { subtasks: updated });
                    // Notify on assignment to someone else
                    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
                    if (newAssignee && newAssignee !== prev && newAssignee !== myName && config.msToken) {
                      try {
                        const email = await lookupJiraUserEmail(config, newAssignee);
                        if (email) await sendOutlookEmail(config, {
                          to: email,
                          subject: `Subtask assigned to you: ${s.text?.substring(0, 55)}`,
                          body: `
                            <p>Hi ${newAssignee.split(" ")[0]},</p>
                            <p>${myName || "Someone"} has assigned a subtask to you in the C365Cloud Task Manager.</p>
                            <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
                              <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Subtask:</td><td style="padding:6px 12px">${s.text}</td></tr>
                              <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Parent task:</td><td style="padding:6px 12px">${task.title}</td></tr>
                              ${s.due ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Due:</td><td style="padding:6px 12px">${s.due}</td></tr>` : ""}
                            </table>
                            <p style="color:#888;font-size:12px">This subtask has been logged in the C365Cloud Task Manager.</p>
                          `,
                        });
                      } catch (err) { console.warn("[Subtask] Email failed:", err.message); }
                    }
                  }}>
                  <option value="">👤 Assign subtask…</option>
                  {(jiraAssignees || []).map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                <select
                  className="form-input"
                  style={{ fontSize: 11, flex: "0 0 auto", width: 170 }}
                  value={s.dependsOn || ""}
                  title="This subtask starts after the selected subtask"
                  onChange={e => {
                    const updated = subtasks.map(x => x.id === s.id ? { ...x, dependsOn: e.target.value || null } : x);
                    onUpdateTask(task.id, { subtasks: updated });
                  }}>
                  <option value="">🔗 Depends on…</option>
                  {subtasks.filter(x => x.id !== s.id).map(x => (
                    <option key={x.id} value={x.id}>{x.text?.substring(0, 40)}</option>
                  ))}
                </select>
                <button className="btn primary" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => setExpandedSubtaskId(null)}>Done</button>
              </div>
            )}
            {/* Subtask discussion thread */}
            {expandedSubtaskId === s.id && (
              <div style={{ paddingLeft: 22, width: "100%", marginTop: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  💬 Subtask discussion
                </div>
                {(s.notes || []).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 8 }}>
                    {(s.notes || []).map((n, ni) => (
                      <div key={ni} style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: "6px 9px", position: "relative" }}>
                        <div style={{ fontSize: 9, color: "var(--text-dim)", marginBottom: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span>📅 {n.date}{n.author ? <span style={{ color: "var(--green)", fontWeight: 600, marginLeft: 5 }}>· {n.author}</span> : null}</span>
                          <button onClick={() => deleteSubtaskNote(s.id, ni)} title="Delete comment"
                            style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 11, padding: 0, lineHeight: 1 }}>✕</button>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text)", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>{n.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    className="form-input"
                    style={{ flex: 1, fontSize: 11 }}
                    placeholder="Add a comment to this subtask…"
                    value={expandedSubtaskId === s.id ? subtaskNoteText : ""}
                    onChange={e => setSubtaskNoteText(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addSubtaskNote(s.id, subtaskNoteText); }}
                  />
                  <button className="btn primary" style={{ fontSize: 10, padding: "2px 10px" }}
                    onClick={() => addSubtaskNote(s.id, subtaskNoteText)}>Comment</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!subtasksCollapsed && (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            <div className="subtask-add-row">
              <input
                className="form-input"
                placeholder="Add subtask…"
                value={newSubtask}
                onChange={e => setNewSubtask(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addSubtask()}
                style={{ flex: 1, fontSize: 12 }}
              />
              <button className="btn" style={{ fontSize: 11 }} onClick={addSubtask}>Add</button>
            </div>
            <div className="subtask-add-row" style={{ marginTop: 0 }}>
              <input
                type="date"
                className="form-input"
                style={{ fontSize: 11, flex: "0 0 auto", width: 140 }}
                value={newSubtaskDue}
                onChange={e => setNewSubtaskDue(e.target.value)}
                title="Optional due date for new subtask"
              />
              <input
                className="form-input"
                style={{ fontSize: 11, flex: 1 }}
                placeholder="Link (optional)"
                value={newSubtaskLink}
                onChange={e => setNewSubtaskLink(e.target.value)}
              />
              <select
                className="form-input"
                style={{ fontSize: 11, flex: "0 0 auto", width: 150 }}
                value={newSubtaskAssignee}
                onChange={e => setNewSubtaskAssignee(e.target.value)}
                title="Assign this subtask to a team member">
                <option value="">👤 Assignee…</option>
                {(jiraAssignees || []).map(name => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="detail-field">
        <div className="detail-section-label">Notes {task.source === "jira" ? "- syncs to Jira as comment" : ""}</div>
        <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4 }}>Tip: start a line with <strong style={{color:"var(--green)"}}>-</strong> to create a bullet point. For Jira comments, type <strong style={{color:"var(--green)"}}>@</strong> to mention a user.</div>
        {notes.map((n, i) => (
          <NoteEntry key={i} note={n} onDelete={() => {
            if (window.confirm("Delete this note?")) {
              onUpdateTask(task.id, { localNotes: notes.filter((_, j) => j !== i) });
            }
          }} />
        ))}
        <MentionTextarea
          value={note}
          onChange={setNote}
          onSegmentsChange={(val, segs) => setNoteSegments(segs)}
          config={window.__c365config__ || {}}
          isJira={task.source === "jira"}
          placeholder="Add a note... (start a line with - for bullet points)"
        />
        {noteImages.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {noteImages.map((img, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={img} alt="preview" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)" }} />
                <button onClick={() => setNoteImages(prev => prev.filter((_, j) => j !== i))}
                  style={{ position: "absolute", top: -4, right: -4, background: "var(--red)", border: "none", color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 9, cursor: "pointer", lineHeight: 1, padding: 0 }}>{"✕"}</button>
              </div>
            ))}
          </div>
        )}
        {/* Pending file attachments preview */}
        {noteFiles.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
            {noteFiles.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, padding: "4px 8px", fontSize: 11 }}>
                <span>{fileIcon(f.name)}</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text)" }}>{f.name}</span>
                <span style={{ color: "var(--text-muted)", fontSize: 10, flexShrink: 0 }}>{formatFileSize(f.size)}</span>
                <button onClick={() => setNoteFiles(prev => prev.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 13, padding: "0 2px", flexShrink: 0 }}>{"✕"}</button>
              </div>
            ))}
          </div>
        )}
        <div className="notes-save-row" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <label style={{ cursor: "pointer", padding: "5px 10px", border: "1px solid var(--border)", borderRadius: 4, fontSize: 11, color: "var(--text-muted)", background: "var(--surface2)", whiteSpace: "nowrap" }}
            title="Attach image">
            🖼 Image
            <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImageUpload} />
          </label>
          <label style={{ cursor: "pointer", padding: "5px 10px", border: "1px solid var(--border)", borderRadius: 4, fontSize: 11, color: "var(--text-muted)", background: "var(--surface2)", whiteSpace: "nowrap" }}
            title="Attach file (PDF, Word, Excel, etc.)">
            📎 File
            <input type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.zip"
              multiple style={{ display: "none" }}
              onChange={e => setNoteFiles(prev => [...prev, ...Array.from(e.target.files || [])])} />
          </label>
          <button className="btn primary" style={{ fontSize: 11, flex: 1 }} onClick={handleSaveNote} disabled={savingNote || uploadingFiles}>
            {uploadingFiles ? "Uploading…" : savingNote ? "Saving..." : task.source === "jira" ? "💬 Post to Jira" : "Save Note"}
          </button>
        </div>
      </div>

      <div className="detail-actions">
        {!task.done && (task.source === "manual" || task.id?.startsWith("email-")) && <button className="btn primary" onClick={onComplete}>✓ Mark Done</button>}
        {!task.done && task.source === "jira" && <button className="btn primary" onClick={onComplete}>✓ Mark Done</button>}
        {!task.done && task.source === "outlook" && !task.isFlaggedEmail && <button className="btn primary" onClick={onComplete}>✓ Done in Outlook</button>}
        {!task.done && task.source === "outlook" && task.isFlaggedEmail && <button className="btn primary" onClick={onCompleteFlagged}>✓ Done in Outlook</button>}
        {task.source === "jira" && <button className="btn" onClick={onCreateOutlook}>+ Outlook Task</button>}
        {(task.source === "manual" || task.id?.startsWith("email-") || task.id?.startsWith("ch-action-")) && onCreateInJira && (
          <button className="btn" style={{ borderColor: "#4c9aff", color: "#4c9aff" }} onClick={() => onCreateInJira(task)}>◆ Create in Jira</button>
        )}
        {task.source === "manual" && onSaveTemplate && (
          <button className="btn" style={{ fontSize: 10 }} onClick={() => onSaveTemplate(task)} title="Save this task as a reusable template">📋 Save Template</button>
        )}
        <button className="btn danger" onClick={() => onDelete(task)}>🗑 Delete</button>
      </div>
    </>
  );
}

function JiraImageUpload({ issueKey, onClose }) {
  const fileInputRef = React.useRef();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});

  const handlePaste = useCallback((e) => {
    // Only intercept paste if no text input/textarea/contenteditable is focused
    const active = document.activeElement;
    const isTextFocused = active && (
      active.tagName === "INPUT" || active.tagName === "TEXTAREA" ||
      active.isContentEditable || active.getAttribute("contenteditable") === "true"
    );
    if (isTextFocused) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const imgs = Array.from(items)
      .filter(i => i.kind === "file" && i.type.startsWith("image/"))
      .map(i => i.getAsFile()).filter(Boolean);
    if (imgs.length) {
      e.preventDefault();
      e.stopPropagation();
      setFiles(prev => [...prev, ...imgs]);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("paste", handlePaste, true); // capture phase
    return () => document.removeEventListener("paste", handlePaste, true);
  }, [handlePaste]);

  const addFiles = (fileList) => {
    const valid = Array.from(fileList).filter(f => f.type.startsWith("image/") || f.type === "application/pdf");
    if (valid.length) setFiles(prev => [...prev, ...valid]);
  };

  const send = async () => {
    if (!files.length) return;
    setUploading(true);
    let allOk = true;
    for (const file of files) {
      try {
        setProgress(prev => ({ ...prev, [file.name]: 10 }));
        await addJiraAttachment(window.__c365config__ || {}, issueKey, file, (pct) => {
          setProgress(prev => ({ ...prev, [file.name]: pct }));
        });
        setProgress(prev => ({ ...prev, [file.name]: 100 }));
      } catch (e) {
        allOk = false;
        setProgress(prev => ({ ...prev, [file.name]: -1 }));
        if (window.__c365showToast__) window.__c365showToast__(file.name + ": " + e.message, "error");
      }
    }
    setUploading(false);
    if (allOk) {
      if (window.__c365showToast__) window.__c365showToast__("✓ " + files.length + " file" + (files.length > 1 ? "s" : "") + " attached to " + issueKey);
      onClose();
    }
  };

  return (
    <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 6, padding: 12, display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
      <div className="detail-section-label">📷 Send image / file to {issueKey}</div>
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        style={{ border: "2px dashed var(--border)", borderRadius: 6, padding: "16px 12px", textAlign: "center", cursor: "pointer", color: "var(--text-muted)", fontSize: 12 }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "#3266ad"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
      >
        📂 Click to browse · drag & drop · or paste (Ctrl+V)
        <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf"
          style={{ display: "none" }}
          onChange={e => addFiles(e.target.files)} />
      </div>
      {files.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {files.map((f, i) => {
            const pct = progress[f.name];
            const isUploading = pct > 0 && pct < 100;
            const done = pct === 100;
            const failed = pct === -1;
            const previewUrl = f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
            return (
              <div key={i} style={{ background: "var(--surface)", border: "1px solid " + (failed ? "var(--red)" : done ? "var(--green)" : "var(--border)"), borderRadius: 5, padding: "6px 8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {previewUrl
                    ? <img src={previewUrl} alt={f.name} style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 3, flexShrink: 0 }} />
                    : <span style={{ fontSize: 20, flexShrink: 0 }}>📄</span>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: failed ? "var(--red)" : done ? "var(--green)" : "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {done ? "✓ " : failed ? "✕ " : ""}{f.name}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{(f.size / 1024).toFixed(0)} KB</div>
                  </div>
                  {!isUploading && !done && (
                    <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14, padding: 0 }}>✕</button>
                  )}
                  {isUploading && <span style={{ fontSize: 10, color: "#4c9aff" }}>{pct}%</span>}
                </div>
                {(isUploading || done) && (
                  <div style={{ height: 3, background: "var(--border)", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (pct || 0) + "%", background: done ? "var(--green)" : "#4c9aff", borderRadius: 2, transition: "width 0.2s" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn primary" style={{ fontSize: 11, background: "#0052cc", borderColor: "#0052cc" }}
          disabled={!files.length || uploading} onClick={send}>
          {uploading ? "Uploading…" : "📤 Send to Jira" + (files.length > 0 ? " (" + files.length + " file" + (files.length > 1 ? "s" : "") + ")" : "")}
        </button>
        <button className="btn" style={{ fontSize: 11 }} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function DetailPanel({ task, onComplete, onCompleteFlagged, onCreateOutlook, onSaveNote, onSaveDue, onTransition, onStatusChange, onUpdateTask, onDelete, onPopout, onPromoteSubtask, onMobileClose, onSaveTemplate, onCreateInJira, jiraAssignees = [], config = {}, allManualTasks = [] }) {
  if (!task) return null;
  return (
    <div className="detail-panel">
      {onMobileClose && (
        <button onClick={onMobileClose} className="mobile-back-btn">← Back to list</button>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, flexShrink: 0 }}>
        <div style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>Task Detail</div>
        <div style={{ display: "flex", gap: 6 }}>
          <button style={{ background: "none", border: "1px solid var(--border)", color: "var(--text-muted)", borderRadius: 4, padding: "3px 9px", cursor: "pointer", fontSize: 14, lineHeight: 1 }}
            onClick={onPopout} title="Open in full pop-out view">⤢</button>
          <button style={{ background: "none", border: "1px solid var(--border)", color: "var(--text-muted)", borderRadius: 4, padding: "3px 9px", cursor: "pointer", fontSize: 14, lineHeight: 1, fontWeight: 700 }}
            onClick={() => { if(window.__c365closeDetail__) window.__c365closeDetail__(); }} title="Close panel">{"✕"}</button>
        </div>
      </div>
      <div className="popout-header" style={{ paddingTop: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="detail-title" style={{ fontSize: 14 }}>{task.title}</div>
        </div>
      </div>
      <TaskDetailContent
        task={task}
        onComplete={onComplete}
        onCompleteFlagged={onCompleteFlagged}
        onCreateOutlook={onCreateOutlook}
        onSaveNote={onSaveNote}
        onSaveDue={onSaveDue}
        onTransition={onTransition}
        onStatusChange={onStatusChange}
        onUpdateTask={onUpdateTask}
        onDelete={onDelete}
        onPromoteSubtask={onPromoteSubtask}
        onSaveTemplate={onSaveTemplate}
        onCreateInJira={onCreateInJira}
        jiraAssignees={jiraAssignees}
        config={config}
        allManualTasks={allManualTasks}
        compact={true}
      />
    </div>
  );
}

function PopoutModal({ task, onClose, onComplete, onCompleteFlagged, onCreateOutlook, onSaveNote, onSaveDue, onTransition, onStatusChange, onUpdateTask, onDelete, onPromoteSubtask, onCreateInJira, jiraAssignees = [], config = {}, allManualTasks = [] }) {
  if (!task) return null;
  return (
    <div className="popout-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="popout-modal">
        <div className="popout-header">
          <div className="popout-title">{task.title}</div>
          <button className="popout-close" onClick={onClose} title="Close">{"✕"}</button>
        </div>
        <TaskDetailContent
          task={task}
          onComplete={onComplete}
          onCompleteFlagged={onCompleteFlagged}
          onCreateOutlook={onCreateOutlook}
          onSaveNote={onSaveNote}
          onSaveDue={onSaveDue}
          onTransition={onTransition}
          onStatusChange={onStatusChange}
          onUpdateTask={onUpdateTask}
          onDelete={onDelete}
          onPromoteSubtask={onPromoteSubtask}
          onCreateInJira={onCreateInJira}
          jiraAssignees={jiraAssignees}
          config={config}
          allManualTasks={allManualTasks}
        />
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <button className="btn" style={{ padding: "10px 28px", fontSize: 13, fontWeight: 600 }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function ConfigModal({ config, onSave, onClose }) {
  const [form, setForm] = useState(config);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">⚙ Configure Connections</div>
        <div className="modal-section">
          <div className="modal-section-title">Microsoft / Outlook</div>
          {getSavedMsToken() ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 12, color: "var(--accent-green)" }}>✓ Connected to Microsoft</div>
              <button className="btn" style={{ fontSize: 11 }} onClick={() => {
                localStorage.removeItem("taskflow_ms_token");
                localStorage.removeItem("taskflow_ms_token_expiry");
                set("msToken", "");
              }}>Disconnect</button>
            </div>
          ) : (
            <button className="btn primary" onClick={() => startMsLogin()}>
              Sign in with Microsoft
            </button>
          )}
          <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.6 }}>
            Click "Sign in with Microsoft" to connect your Outlook tasks and emails. Your login persists automatically.
          </div>
        </div>
        <div className="modal-section">
          <div className="modal-section-title">Jira</div>
          <div className="form-row">
            <label className="form-label">Jira URL</label>
            <input className="form-input" type="text" placeholder="https://yourcompany.atlassian.net" value={form.jiraUrl || ""} onChange={(e) => set("jiraUrl", e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Email Address</label>
            <input className="form-input" type="email" placeholder="you@yourcompany.com" value={form.jiraEmail || ""} onChange={(e) => set("jiraEmail", e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">API Token</label>
            <input className="form-input" type="password" placeholder="Your Jira API token" value={form.jiraToken || ""} onChange={(e) => set("jiraToken", e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Project Key (optional)</label>
            <input className="form-input" type="text" placeholder="COMP (leave blank for all projects)" value={form.jiraProject || ""} onChange={(e) => set("jiraProject", e.target.value)} />
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.6 }}>
            Create an API token at <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer" style={{ color: "var(--accent-blue)" }}>Atlassian Account Security</a>.
          </div>
        </div>
        <div className="modal-section">
          <div className="modal-section-title">👥 Team Members (for task assignment emails)</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
            Add your team members' names and email addresses so assignment notifications can be sent. One per line, format: <strong>Name | email@company.com</strong>
          </div>
          <textarea
            style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: "8px 10px", color: "var(--text)", fontSize: 12, outline: "none", fontFamily: "monospace", resize: "vertical", minHeight: 100, lineHeight: 1.6 }}
            value={(form.teamMembers || []).map(m => `${m.name} | ${m.email}`).join("\n")}
            onChange={e => {
              const members = e.target.value.split("\n").map(line => {
                const parts = line.split("|").map(s => s.trim());
                return parts.length >= 2 ? { name: parts[0], email: parts[1] } : null;
              }).filter(Boolean);
              set("teamMembers", members);
            }}
            placeholder={"Holly Yates | h.yates@compliance365.co.uk\nJames Carter | j.carter@compliance365.co.uk\nKamran Shalam | k.shalam@compliance365.co.uk"}
          />
          <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 4 }}>
            {(form.teamMembers || []).length} member{(form.teamMembers || []).length !== 1 ? "s" : ""} configured
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => onSave(form)}>Save & Connect</button>
        </div>
      </div>
    </div>
  );
}


// ─── Implementations & Projects ───────────────────────────────────────────────
const IMPL_STATUSES = ["Not Started", "In Progress", "Behind", "Complete", "Not Required"];

const BASE_TASKS = [
  { name: "Contract Award",                        owner: "Customer",      section: "Key Milestones",            depLabel: "" },
  { name: "Contract Sign",                         owner: "Customer",      section: "Key Milestones",            depLabel: "1" },
  { name: "Project Kick-off Meeting",              owner: "C365/Customer", section: "Key Milestones",            depLabel: "2" },
  { name: "URL Configuration",                     owner: "C365",          section: "Base System Configuration", depLabel: "" },
  { name: "System Setup",                          owner: "C365",          section: "Base System Configuration", depLabel: "4" },
  { name: "Property List Sent",                    owner: "Customer",      section: "Base System Configuration", depLabel: "" },
  { name: "Property List Loaded",                  owner: "C365",          section: "Base System Configuration", depLabel: "6" },
  { name: "Deploy Standard Compliance Dashboards", owner: "C365",          section: "Dashboard Setup",           depLabel: "" },
  { name: "Customer Provides Example Dashboards",  owner: "Customer",      section: "Dashboard Setup",           depLabel: "" },
  { name: "Configuration of Custom Dashboards",    owner: "C365",          section: "Dashboard Setup",           depLabel: "9" },
  { name: "Deployment of Dashboards",              owner: "C365",          section: "Dashboard Setup",           depLabel: "" },
  { name: "Handover to C365 BAU Support",          owner: "C365",          section: "Go-Live",                   depLabel: "" },
  { name: "Project Board Review and Acceptance",   owner: "Customer/C365", section: "Go-Live",                   depLabel: "" },
];

// Sections that must always render at the bottom of the plan, after all modules
const BOTTOM_SECTIONS = ["Go-Live"];

// Modules that do NOT generate a Module Setup row (they have their own task templates)
const NO_MODULE_SETUP = ["cafm helpdesk", "property sync", "api", "direct upload"];

// Next working day (skips Sat/Sun)
function nextWorkingDay(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
}

const MODULE_TASKS = [
  { name: "Module Configuration Documents Provided", owner: "Customer",      depLabel: "" },
  { name: "Module Setup",                            owner: "C365",          depLabel: "1" },
  { name: "Provide examples of supplier certificates", owner: "Customer",    depLabel: "" },
  { name: "Setup Extraction Templates",              owner: "C365",          depLabel: "3" },
  { name: "Historical Documents/Action Upload",      owner: "Customer",      depLabel: "" },
  { name: "Create Mobile Form/s for module",         owner: "C365",          depLabel: "" },
  { name: "Supplier Setup for Module",               owner: "C365",          depLabel: "" },
  { name: "Module Released to Customer",             owner: "C365",          depLabel: "" },
  { name: "End User Training",                       owner: "C365",          depLabel: "" },
  { name: "Module Go-Live",                          owner: "Customer",      depLabel: "" },
];

// CAFM Helpdesk module - different task list with its own dependencies
// depLabel is 1-based index within the module's own task list
const CAFM_HELPDESK_TASKS = [
  { name: "Confirm Job Types",                            owner: "Customer",      depLabel: "" },
  { name: "Confirm Contractors",                          owner: "Customer",      depLabel: "" },
  { name: "Setup Job Types and Contractors",              owner: "C365",          depLabel: "1,2" },
  { name: "Confirm Preferred Contractors",                owner: "Customer",      depLabel: "" },
  { name: "Setup Preferred Contractor Matrix",            owner: "C365",          depLabel: "4" },
  { name: "Work Flow Workshop",                           owner: "C365/Customer", depLabel: "" },
  { name: "Configuration of Work Flows",                  owner: "Customer",      depLabel: "6" },
  { name: "Mobile App Workshop",                          owner: "C365/Customer", depLabel: "" },
  { name: "Configuration of Mobile App",                  owner: "C365",          depLabel: "8" },
  { name: "Finance System Integration Workshop",          owner: "C365/Customer", depLabel: "" },
  { name: "Development of Finance System Integration",    owner: "C365",          depLabel: "10" },
  { name: "Configuration of Reporting",                   owner: "C365",          depLabel: "" },
  { name: "End User Training",                            owner: "C365/Customer", depLabel: "" },
  { name: "Full Go-Live",                                 owner: "C365/Customer", depLabel: "" },
];

function makeId() { return "t_" + Math.random().toString(36).substr(2, 9); }

// ── Risk Register default entries ────────────────────────────────────────────
const DEFAULT_RISKS = [
  { riskId: "RI01", description: "Staff availability (COVID/Holidays/Other commitments)", likelihood: 4, impact: 3, mitigation: "Teams meetings and project team with deputies to cover key areas", mitigatedLikelihood: 3, mitigatedImpact: 1, status: "", comments: "" },
  { riskId: "RI02", description: "Suppliers to the customer don't engage in the process", likelihood: 3, impact: 4, mitigation: "Early engagement of the supply chain by the customer and C365", mitigatedLikelihood: 1, mitigatedImpact: 2, status: "", comments: "" },
  { riskId: "RI03", description: "Collation of historical documentation and data for importing into C365 is in a format that C365 can import and extract", likelihood: 5, impact: 5, mitigation: "C365 provide early view of the data required and ensure the project plan reflects the agreed timescales.", mitigatedLikelihood: 3, mitigatedImpact: 2, status: "", comments: "Working with PM to collect the documents that are digital and providing proformas for the rest of the data" },
  { riskId: "RI04", description: "Historical documentation isn't named correctly or hasn't got the UPRN within the document", likelihood: 5, impact: 5, mitigation: "Early assessment of documentation to identify any potential issues and plan resource in to address these to mitigate the impact on the project", mitigatedLikelihood: 3, mitigatedImpact: 1, status: "", comments: "" },
  { riskId: "RI05", description: "Unexpected changes to scope of project impacting the delivery timescales", likelihood: 3, impact: 4, mitigation: "The customer has detailed the requirements in the contract.", mitigatedLikelihood: 0, mitigatedImpact: 0, status: "", comments: "" },
  { riskId: "RI06", description: "Data transfer from the customer becoming insecure exposing company and or personal data to unauthorised individuals", likelihood: 3, impact: 5, mitigation: "All data transfer to be completed via ShareFile with access controlled by the PC.", mitigatedLikelihood: 1, mitigatedImpact: 5, status: "", comments: "" },
  { riskId: "RI07", description: "Access to the C365 mobile app and data to unauthorised personal", likelihood: 2, impact: 5, mitigation: "Access to the mobile app is control via Helpdesk request from Authorised Customer staff", mitigatedLikelihood: 1, mitigatedImpact: 5, status: "", comments: "" },
  { riskId: "RI08", description: "Unauthorised access to the API for the customer, exposing data to 3rd parties", likelihood: 1, impact: 4, mitigation: "Security keys and processes for accessing the API is controlled and only members of the customer's team that require the details have been provided the required keys. This was instructed by the project manager at the customer", mitigatedLikelihood: 1, mitigatedImpact: 3, status: "", comments: "" },
  { riskId: "RI09", description: "Managing user access to C365 Cloud for the customer to account for joiners, movers and leavers resulting in unauthorised access to the system", likelihood: 3, impact: 5, mitigation: "The customer is planning to implement SSO, this ties the login to the customers AD environment, ensuring that changes to users are managed by the customer's internal processes", mitigatedLikelihood: 2, mitigatedImpact: 3, status: "", comments: "" },
];

function riskScoreLabel(score) {
  if (score >= 15) return { label: "Critical", color: "#e32529", bg: "#e3252918" };
  if (score >= 10) return { label: "Major",    color: "#f97316", bg: "#f9731618" };
  if (score >= 5)  return { label: "Moderate", color: "#F1901E", bg: "#F1901E18" };
  return                  { label: "Minor",    color: "#47ac4c", bg: "#47ac4c18" };
}


// ── Project template library ─────────────────────────────────────────────────
const PROJECT_TEMPLATE_STORE_KEY = "taskflow_project_templates";

function getProjectTemplates() {
  try { return JSON.parse(localStorage.getItem(PROJECT_TEMPLATE_STORE_KEY) || "[]"); } catch { return []; }
}

function saveProjectTemplate(project, name) {
  const templates = getProjectTemplates();
  const tmpl = {
    id: Date.now(),
    name: name.trim(),
    savedAt: new Date().toISOString(),
    // Strip runtime/client-specific data
    tasks: (project.tasks || []).map(t => ({ ...t, id: "t_" + Math.random().toString(36).substring(2,7), status: "Not Started", start: "", finish: "", milestone: "", jiraKey: null, actualFinish: "" })),
    modules: (project.modules || []).map(m => ({ ...m, tasks: (m.tasks || []).map(t => ({ ...t, id: "t_" + Math.random().toString(36).substring(2,7), status: "Not Started", start: "", finish: "", milestone: "", jiraKey: null })) })),
    moduleSetup: (project.moduleSetup || []).map(r => ({ ...r, id: "ms_" + Math.random().toString(36).substring(2,7), jiraKey: "" })),
    mobileForms: (project.mobileForms || []).map(r => ({ ...r, id: "mf_" + Math.random().toString(36).substring(2,7), jiraKey: "" })),
    templates: (project.templates || []).map(r => ({ ...r, id: "tp_" + Math.random().toString(36).substring(2,7), jiraKey: "", supportKey: "" })),
    risks: project.risks || [],
  };
  const updated = [...templates, tmpl];
  try { localStorage.setItem(PROJECT_TEMPLATE_STORE_KEY, JSON.stringify(updated)); } catch {}
  return updated;
}

function deleteProjectTemplate(id) {
  const templates = getProjectTemplates().filter(t => t.id !== id);
  try { localStorage.setItem(PROJECT_TEMPLATE_STORE_KEY, JSON.stringify(templates)); } catch {}
  return templates;
}

function createProjectFromTemplate(clientName, sdm, pc) {
  const baseTasks = BASE_TASKS.map(t => ({
    id: makeId(), name: t.name, owner: t.owner, section: t.section,
    status: "Not Started", start: "", finish: "", milestone: "",
    depLabel: t.depLabel || "",
    jiraKey: null, jiraId: null, deps: [],
  }));
  return {
    id: "proj_" + Date.now(),
    clientName, sdm, pc,
    createdAt: new Date().toISOString(),
    tasks: baseTasks,
    modules: [],
    actionLog: [],
    riskRegister: DEFAULT_RISKS.map(r => ({ ...r, id: makeId(), dateRaised: new Date().toISOString().split("T")[0] })),
    moduleSetup: [],
    mobileForms: [],
    templates: [],
  };
}

const PROPERTY_SYNC_TASKS = [
  { name: "Project Mandate Sent to Customer",  owner: "C365",          depLabel: "" },
  { name: "Completed Mandate Received",        owner: "Customer",      depLabel: "" },
  { name: "Refine Requirements",               owner: "Customer",      depLabel: "" },
  { name: "Signoff Mandate",                   owner: "C365/Customer", depLabel: "2,3" },
  { name: "Build Property Sync",               owner: "C365",          depLabel: "" },
  { name: "Testing",                           owner: "C365",          depLabel: "" },
  { name: "Deployment",                        owner: "C365",          depLabel: "" },
  { name: "Customer Signoff",                  owner: "Customer",      depLabel: "" },
];

const DIRECT_UPLOAD_TASKS = [
  { name: "Provide C365 Specification",        owner: "C365",          depLabel: "" },
  { name: "Customer Confirm Required Fields",  owner: "Customer",      depLabel: "1" },
  { name: "Provide Example CSV",               owner: "Customer",      depLabel: "" },
  { name: "Build Direct Upload",               owner: "C365",          depLabel: "2,3" },
  { name: "Testing",                           owner: "C365",          depLabel: "" },
  { name: "Deployment",                        owner: "C365",          depLabel: "" },
  { name: "Customer Signoff",                  owner: "Customer",      depLabel: "" },
];

const API_TASKS = [
  { name: "Provide API Swagger Hub Link",      owner: "C365",          depLabel: "" },
  { name: "Setup Customer URL",                owner: "C365",          depLabel: "" },
  { name: "Complete Property Sync",            owner: "C365",          depLabel: "" },
  { name: "Testing",                           owner: "C365",          depLabel: "" },
  { name: "Build Additional Links",            owner: "C365",          depLabel: "" },
  { name: "Customer Signoff",                  owner: "Customer",      depLabel: "" },
];

function addModuleToProject(project, moduleName) {
  const key = moduleName.trim().toLowerCase();
  const templateTasks =
    key === "cafm helpdesk"   ? CAFM_HELPDESK_TASKS  :
    key === "property sync"   ? PROPERTY_SYNC_TASKS  :
    key === "direct upload"   ? DIRECT_UPLOAD_TASKS  :
    key === "api"             ? API_TASKS             :
    MODULE_TASKS;
  const tasks = templateTasks.map(t => ({
    id: makeId(), name: t.name, owner: t.owner, section: moduleName,
    status: "Not Started", start: "", finish: "", milestone: "",
    depLabel: t.depLabel || "", comments: "",
    jiraKey: null, jiraId: null, deps: [],
  }));
  return {
    ...project,
    modules: [...project.modules, { name: moduleName, tasks }],
  };
}

function calcProgress(project) {
  const allTasks = [
    ...project.tasks,
    ...project.modules.flatMap(m => m.tasks),
  ];
  if (!allTasks.length) return 0;
  const done = allTasks.filter(t => t.status === "Complete").length;
  return Math.round((done / allTasks.length) * 100);
}

function statusClass(status) {
  if (status === "Complete")      return "impl-status-complete";
  if (status === "In Progress")   return "impl-status-in-progress";
  if (status === "Behind")        return "impl-status-behind";
  if (status === "Not Required")  return "impl-status-not-required";
  return "impl-status-not-started";
}


// ─── ImplementationsView component ────────────────────────────────────────────
function ImplementationsView({ projects, onUpdateProjects, config, jiraConnected, showToast, sync, onRaiseJira, tasks }) {
  const [selectedProjectId, setSelectedProjectId] = useState(projects[0]?.id || null);
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [projectNameVal, setProjectNameVal] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showNewProject, setShowNewProject] = useState(false);

  // Migrate depLabel and comments for projects that predate these fields
  useEffect(() => {
    const DEP_LABELS = { "Contract Sign": "1", "Project Kick-off Meeting": "2", "System Setup": "4", "Property List Loaded": "6" };
    const needsMigration = projects.some(proj =>
      proj.tasks.some(t => (DEP_LABELS[t.name] && !t.depLabel) || t.comments === undefined)
    );
    if (needsMigration) {
      onUpdateProjects(projects.map(proj => ({
        ...proj,
        tasks: proj.tasks.map(t => ({
          ...t,
          depLabel: t.depLabel ?? (DEP_LABELS[t.name] || ""),
          comments: t.comments ?? "",
        })),
        modules: proj.modules.map(m => ({
          ...m,
          tasks: m.tasks.map(t => ({
            ...t,
            depLabel: t.depLabel ?? "",
            comments: t.comments ?? "",
          })),
        })),
      })));
    }
  }, []); // eslint-disable-line

  // Auto-set status to "Behind" for overdue tasks - runs at most once per session
  // stored in a module-level flag so remounts don't re-trigger
  useEffect(() => {
    if (window.__implOverdueChecked) return;
    window.__implOverdueChecked = true;
    const todayStr = new Date().toISOString().split("T")[0];
    const needsUpdate = projects.some(proj =>
      proj.tasks.some(t => t.finish && t.finish < todayStr && t.status !== "Complete" && t.status !== "Behind") ||
      proj.modules.some(m => m.tasks.some(t => t.finish && t.finish < todayStr && t.status !== "Complete" && t.status !== "Behind"))
    );
    if (!needsUpdate) return;
    onUpdateProjects(projects.map(proj => ({
      ...proj,
      tasks: proj.tasks.map(t =>
        t.finish && t.finish < todayStr && t.status !== "Complete" && t.status !== "Behind"
          ? { ...t, status: "Behind" } : t
      ),
      modules: proj.modules.map(m => ({
        ...m,
        tasks: m.tasks.map(t =>
          t.finish && t.finish < todayStr && t.status !== "Complete" && t.status !== "Behind"
            ? { ...t, status: "Behind" } : t
        ),
      })),
    })));
  }, []); // eslint-disable-line
  const [newProject, setNewProject] = useState({ clientName: "", sdm: "", pc: "", sdmAccountId: "", pcAccountId: "" });
  const [editingProjectPeople, setEditingProjectPeople] = useState(false);
  const [editPeople, setEditPeople] = useState({ sdm: "", pc: "", sdmAccountId: "", pcAccountId: "" });
  const [showAddModule, setShowAddModule] = useState(false);
  const [newModuleName, setNewModuleName] = useState("");
  const [editingTask, setEditingTask] = useState(null);



  const project = projects.find(p => p.id === selectedProjectId);

  const updateProject = (updated) => {
    // Check if project just hit 100% - show celebration toast
    const oldProgress = calcProgress(projects.find(p => p.id === updated.id) || {});
    const newProgress = calcProgress(updated);
    if (newProgress === 100 && oldProgress < 100) {
      showToast(`🎉 ${updated.clientName} is 100% complete!`);
    }
    onUpdateProjects(projects.map(p => p.id === updated.id ? updated : p));
  };

  // Returns the effective finish date for a task:
  // If actual finish (milestone) is set AND is after planned finish, use actual finish.
  // This ensures dependent tasks start 1 business day after the actual completion, not planned.
  const effectiveFinish = (task) => {
    const planned = task.finish || null;
    const actual  = task.milestone || null;
    if (actual && planned && actual > planned) return actual;
    if (actual && !planned) return actual;
    return planned;
  };

  const applyDepDates = (tasks, changedId, newFinish) => {
    if (!newFinish) return tasks;
    const changedIdx = tasks.findIndex(t => t.id === changedId);
    if (changedIdx === -1) return tasks;
    const changedDepNum = String(changedIdx + 1);
    return tasks.map(t => {
      if (!t.depLabel) return t;
      // Support comma-separated deps e.g. "1,2"
      const depNums = t.depLabel.split(",").map(s => s.trim());
      if (!depNums.includes(changedDepNum)) return t;
      // For multi-deps, find the latest effective finish among all parent tasks
      // Effective finish = actual finish if later than planned, otherwise planned finish
      let latestFinish = newFinish;
      depNums.forEach(num => {
        const idx = parseInt(num, 10) - 1;
        if (idx >= 0 && idx < tasks.length && idx !== changedIdx) {
          const parentEff = effectiveFinish(tasks[idx]);
          if (parentEff && parentEff > latestFinish) latestFinish = parentEff;
        }
      });
      // Also check the changed task itself for actual finish override
      const changedTask = tasks[changedIdx];
      const changedEff = effectiveFinish({ ...changedTask, finish: newFinish });
      if (changedEff > latestFinish) latestFinish = changedEff;
      // Only set start if ALL parent deps have a finish date
      const allParentsHaveFinish = depNums.every(num => {
        const idx = parseInt(num, 10) - 1;
        if (idx < 0 || idx >= tasks.length) return false;
        if (idx === changedIdx) return true; // this is the one we just set
        return !!(tasks[idx].finish || tasks[idx].milestone);
      });
      if (!allParentsHaveFinish) return t;
      const nwd = nextWorkingDay(latestFinish);
      if (!t.start || t.start < nwd) return { ...t, start: nwd };
      return t;
    });
  };

  const updateTask = (taskId, updates, moduleIndex = null) => {
    if (!project) return;
    // Auto-complete: if actual finish (milestone) is set to today or before, mark Complete
    if (updates.milestone && updates.milestone !== "") {
      const today = new Date().toISOString().split("T")[0];
      // Convert DD-MM-YYYY to YYYY-MM-DD for comparison if needed
      let milestoneIso = updates.milestone;
      if (/^\d{2}-\d{2}-\d{4}$/.test(milestoneIso)) {
        const [d, m, y] = milestoneIso.split("-");
        milestoneIso = `${y}-${m}-${d}`;
      }
      if (milestoneIso <= today && !updates.status) {
        updates = { ...updates, status: "Complete" };
      }
    }
    if (moduleIndex === null) {
      let tasks = project.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t);
      if (updates.finish) tasks = applyDepDates(tasks, taskId, updates.finish);
      // If actual finish is set and is later than planned finish, re-propagate deps
      if (updates.milestone) {
        const updatedTask = tasks.find(t => t.id === taskId);
        const eff = effectiveFinish(updatedTask);
        if (eff) tasks = applyDepDates(tasks, taskId, eff);
      }
      updateProject({ ...project, tasks });
    } else {
      const modules = project.modules.map((m, i) => {
        if (i !== moduleIndex) return m;
        let tasks = m.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t);
        if (updates.finish) tasks = applyDepDates(tasks, taskId, updates.finish);
        return { ...m, tasks };
      });
      updateProject({ ...project, modules });
    }
  };

  const handleAddProject = () => {
    if (!newProject.clientName.trim()) return;
    const templates = getProjectTemplates();
    let baseProj = createProjectFromTemplate(newProject.clientName.trim(), newProject.sdm.trim(), newProject.pc.trim());
    if (templates.length > 0) {
      const tmplChoice = window.confirm(`Create from a saved template? (OK = choose template, Cancel = blank project)`);
      if (tmplChoice) {
        const names = templates.map((t, i) => `${i + 1}. ${t.name}`).join("\n");
        const num = parseInt(window.prompt("Choose template:\n" + names, "1"));
        if (!isNaN(num) && templates[num - 1]) {
          const tmpl = templates[num - 1];
          baseProj = { ...baseProj, tasks: tmpl.tasks || [], modules: tmpl.modules || [],
            moduleSetup: tmpl.moduleSetup || [], mobileForms: tmpl.mobileForms || [],
            templates: tmpl.templates || [], risks: tmpl.risks || [] };
        }
      }
    }
    const proj = {
      ...baseProj,
      createdByAccountId: localStorage.getItem("taskflow_jira_accountid") || "",
      sdmAccountId: newProject.sdmAccountId || "",
      pcAccountId: newProject.pcAccountId || "",
    };
    onUpdateProjects([...projects, proj]);
    setSelectedProjectId(proj.id);
    setNewProject({ clientName: "", sdm: "", pc: "", sdmAccountId: "", pcAccountId: "" });
    setShowNewProject(false);
  };

  const handleAddModule = () => {
    if (!newModuleName.trim() || !project) return;
    const name = newModuleName.trim();
    let updatedProject = addModuleToProject(project, name);
    // Auto-add a Module Setup row for compliance modules (not infrastructure/integration modules)
    if (!NO_MODULE_SETUP.includes(name.toLowerCase())) {
      const newRow = {
        id: makeId(),
        category: name, compliance: "", supplier: "",
        freqUnit: "Month", red: "", amber: "", green: "",
        assetised: "No",
        uploadTemplate: "No", uploadOnsite: "No", uploadHistorical: "No",
        alertOnAction: "No", alertOverdue: "No",
        historicDocsRequired: "No", supplierSetup: "No", moduleLive: "No",
        jiraKey: "", jiraId: "",
      };
      updatedProject = { ...updatedProject, moduleSetup: [...(updatedProject.moduleSetup || []), newRow] };
    }
    updateProject(updatedProject);
    setNewModuleName("");
    setShowAddModule(false);
  };

  const handleRemoveModule = (moduleIndex) => {
    if (!project) return;
    updateProject({ ...project, modules: project.modules.filter((_, i) => i !== moduleIndex) });
  };

  const handleRaiseJira = (task, moduleIndex = null, moduleSetupRowId = null, explicitUpdateFn = null) => {
    if (!jiraConnected) { showToast("Connect Jira first", "error"); return; }
    // Mobile Forms - explicit updateFn passed directly
    if (explicitUpdateFn) {
      if (onRaiseJira) onRaiseJira(task, null, project.clientName, explicitUpdateFn);
      return;
    }
    // Module Setup rows - specialised updateFn that writes back to moduleSetup array
    if (moduleSetupRowId) {
      const updateModuleSetupJira = (taskId, updates) => {
        updateProject({
          ...project,
          moduleSetup: (project.moduleSetup || []).map(r =>
            r.id === moduleSetupRowId ? { ...r, jiraKey: updates.jiraKey || r.jiraKey } : r
          ),
        });
      };
      if (onRaiseJira) onRaiseJira(task, null, project.clientName, updateModuleSetupJira);
      return;
    }
    if (onRaiseJira) onRaiseJira(task, moduleIndex, project.clientName, updateTask);
  };


  const handleDownloadExcel = () => {
    if (!project) return;
    const allTasks = [
      ...project.tasks.map(t => ({ ...t, module: t.section })),
      ...project.modules.flatMap(m => m.tasks.map(t => ({ ...t, module: m.name }))),
    ];
    // Group by section for better Excel layout
    const sections = [...new Set(allTasks.map(t => t.module))];
    const wsData = [
      [project.clientName + " - Implementation Plan"],
      ["Generated: " + new Date().toLocaleDateString("en-GB")],
      [],
      ["Section/Module", "Task", "Owner", "Status", "Start", "Planned Finish", "Actual Finish", "Comments", "Jira Key", "Jira Status", "% Complete"],
    ];
    sections.forEach(sec => {
      const secTasks = allTasks.filter(t => t.module === sec);
      wsData.push([sec, "", "", "", "", "", "", "", "", ""]);
      secTasks.forEach(t => {
        const fmt = (d) => d ? d.split("-").reverse().join("-") : "";
        wsData.push([
          "", t.name, t.owner, t.status,
          fmt(t.start), fmt(t.finish), fmt(t.milestone),
          t.comments || "",
          t.jiraKey || "", t.jiraStatus || "", t.status === "Complete" ? 100 : t.status === "In Progress" ? 50 : 0,
        ]);
      });
      wsData.push([]);
    });

    // ── Action Log sheet ─────────────────────────────────────────────────────
    const logEntries = project.actionLog || [];
    const fmt = (d) => d ? d.split("-").reverse().join("-") : "";
    const alData = [
      [project.clientName + " - Action Log"],
      ["Generated: " + new Date().toLocaleDateString("en-GB")],
      [],
      ["#", "C365 Ticket Ref", "Date Raised", "Raised By", "Module", "Description", "Priority", "Assigned To", "Status", "Audit Trail", "Target Date", "Updated Date", "Date Closed"],
    ];
    logEntries.forEach(e => {
      alData.push([
        e.logNumber,
        e.ticketRef || "",
        fmt(e.dateRaised),
        e.raisedBy || "",
        e.module || "",
        e.description || "",
        e.priority || "Medium",
        e.assignedTo || "",
        e.status || "Open",
        e.auditTrail || "",
        fmt(e.targetDate),
        fmt(e.updatedDate),
        fmt(e.dateClosed),
      ]);
    });

    try {
      const XLSX = window.XLSX || require("xlsx");

      // Plan sheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [{ wch: 28 }, { wch: 40 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 30 }, { wch: 12 }, { wch: 20 }, { wch: 12 }];

      // Action Log sheet
      const wsAl = XLSX.utils.aoa_to_sheet(alData);
      wsAl["!cols"] = [{ wch: 6 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 20 }, { wch: 40 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 40 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];

      // Risk Register sheet
      const risks = project.riskRegister || [];
      const rrData = [
        [project.clientName + " - Risk Register"],
        ["Generated: " + new Date().toLocaleDateString("en-GB")],
        [],
        ["Risk ID", "Date Raised", "Description", "Likelihood (1-5)", "Impact (1-5)", "Risk Score", "Score Label", "Mitigation / Control", "Mitigated Likelihood", "Mitigated Impact", "Mitigation Score", "Mit. Score Label", "Status", "Comments"],
      ];
      risks.forEach(r => {
        const score = (parseInt(r.likelihood, 10) || 0) * (parseInt(r.impact, 10) || 0);
        const mitScore = (parseInt(r.mitigatedLikelihood, 10) || 0) * (parseInt(r.mitigatedImpact, 10) || 0);
        rrData.push([
          r.riskId,
          r.dateRaised ? r.dateRaised.split("-").reverse().join("-") : "",
          r.description || "",
          r.likelihood || 0,
          r.impact || 0,
          score,
          riskScoreLabel(score).label,
          r.mitigation || "",
          r.mitigatedLikelihood || 0,
          r.mitigatedImpact || 0,
          mitScore,
          mitScore > 0 ? riskScoreLabel(mitScore).label : "",
          r.status || "Open",
          r.comments || "",
        ]);
      });
      const wsRr = XLSX.utils.aoa_to_sheet(rrData);
      wsRr["!cols"] = [{ wch: 8 }, { wch: 12 }, { wch: 50 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 50 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 40 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Implementation Plan");
      XLSX.utils.book_append_sheet(wb, wsAl, "Action Log");
      XLSX.utils.book_append_sheet(wb, wsRr, "Risk Register");

      // Module Setup sheet
      const msRows = project.moduleSetup || [];
      const msData = [
        [project.clientName + " - Module Setup"],
        ["Generated: " + new Date().toLocaleDateString("en-GB")],
        [],
        ["Category", "Compliance", "Supplier", "Frequency Unit", "RAG RED", "RAG AMBER", "RAG GREEN", "Assetised", "Upload Template", "Upload OnSite", "Upload Historical/Untemplated", "Alert On Action Creation", "Alert Compliance Overdue", "Historic Docs Upload Required", "Supplier Setup & Engaged", "Module Fully Live", "Jira Key"],
      ];
      msRows.forEach(r => {
        msData.push([
          r.category || "", r.compliance || "", r.supplier || "",
          r.freqUnit || "Month", r.red || "", r.amber || "", r.green || "",
          r.assetised || "No",
          r.uploadTemplate || "No", r.uploadOnsite || "No", r.uploadHistorical || "No",
          r.alertOnAction || "No", r.alertOverdue || "No",
          r.historicDocsRequired || "No", r.supplierSetup || "No", r.moduleLive || "No",
          r.jiraKey || "",
        ]);
      });
      const wsMs = XLSX.utils.aoa_to_sheet(msData);
      wsMs["!cols"] = [{ wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 14 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsMs, "Module Setup");

      // Mobile Forms sheet
      const mfRows = project.mobileForms || [];
      const mfData = [
        [project.clientName + " - Mobile Forms"],
        ["Generated: " + new Date().toLocaleDateString("en-GB")],
        [],
        ["Ticket Ref", "Compliance Type", "Form - Despatch Needed", "Form - Development Type", "Form - Example Provided", "Form - Form Built", "Form - Customer Signoff", "Form Status", "Output - Example Provided", "Output - Output Built", "Output - Customer Signoff", "Output Status", "Workflow - Despatch Created", "Workflow - Import Created", "Workflow - Workflow Complete", "Workflow - End to End Test", "Workflow - Customer Signoff", "Go-Live Status"],
      ];
      mfRows.forEach(r => {
        mfData.push([
          r.jiraKey || "", r.complianceType || "",
          r.formDespatch || "No", r.formDevType || "", r.formExampleProvided || "No", r.formBuilt || "No", r.formCustomerSignoff || "No",
          r.jiraStatus || "",
          r.outExampleProvided || "No", r.outBuilt || "No", r.outCustomerSignoff || "No",
          r.jiraStatus || "",
          r.wfDespatchCreated || "No", r.wfImportCreated || "No", r.wfComplete || "No", r.wfEndToEnd || "No", r.wfCustomerSignoff || "No",
          r.goLiveStatus || "",
        ]);
      });
      const wsMf = XLSX.utils.aoa_to_sheet(mfData);
      wsMf["!cols"] = [{ wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, wsMf, "Mobile Forms");

      // Templates sheet
      const tmplRows = project.templates || [];
      const tmplData = [
        [project.clientName + " - Templates"],
        ["Generated: " + new Date().toLocaleDateString("en-GB")],
        [],
        ["Jira Ref (SUPPORT)", "Compliance Type", "Contractor", "Examples Provided", "ED Card", "ED Status", "TEMPL Card", "Template Status", "Doc Upload Setup", "Route Created Module", "Comments"],
      ];
      tmplRows.forEach(r => {
        tmplData.push([
          r.supportKey || "", r.complianceType || "", r.contractor || "",
          r.examplesProvided || "", r.edKey || "", r.edStatus || "",
          r.templKey || "", r.templStatus || "Waiting on ED",
          r.docUploadSetup || "No", r.routeCreated || "No", r.comments || "",
        ]);
      });
      const wsTmpl = XLSX.utils.aoa_to_sheet(tmplData);
      wsTmpl["!cols"] = [{ wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, wsTmpl, "Templates");

      XLSX.writeFile(wb, project.clientName.replace(/[^a-z0-9]/gi, "_") + "_Implementation_Plan.xlsx");
      showToast("Excel plan downloaded");
    } catch {
      // Fallback to CSV of plan only
      const csv = wsData.map(r => r.map(c => String(c)).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = project.clientName.replace(/[^a-z0-9]/gi, "_") + "_Implementation_Plan.csv";
      a.click();
      URL.revokeObjectURL(url);
      showToast("Plan downloaded as CSV (Action Log requires Excel)");
    }
  };

  const progress = project ? calcProgress(project) : 0;
  const allTasks = project ? [
    ...project.tasks,
    ...project.modules.flatMap(m => m.tasks),
  ] : [];
  const statusCounts = IMPL_STATUSES.reduce((acc, s) => ({ ...acc, [s]: allTasks.filter(t => t.status === s).length }), {});

  // CommentsCell - uncontrolled textarea to avoid re-render-per-keystroke
  const CommentsCell = React.memo(({ taskId, moduleIndex, initialValue, onSave }) => {
    const ref = React.useRef(null);
    React.useEffect(() => {
      if (ref.current) {
        ref.current.value = initialValue;
        ref.current.style.height = "auto";
        ref.current.style.height = Math.max(26, ref.current.scrollHeight) + "px";
      }
    }, [taskId]); // only reset when task changes, not on every keystroke
    return (
      <textarea
        ref={ref}
        placeholder="Add comment…"
        defaultValue={initialValue}
        style={{ fontSize: 11, width: "100%", background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 3, padding: "3px 6px", resize: "none", minHeight: 26, fontFamily: "Inter, sans-serif", lineHeight: 1.5, boxSizing: "border-box", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}
        onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.max(26, e.target.scrollHeight) + "px"; }}
        onBlur={e => onSave(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSave(e.target.value); e.target.blur(); } }}
      />
    );
  });

  const SmartDateInput = ({ value, onChange }) => {
    const [editing, setEditing] = React.useState(false);
    const [text, setText] = React.useState("");
    const toDisplay = (iso) => iso ? iso.split("-").reverse().join("-") : "";
    const commit = (val) => {
      setEditing(false);
      if (!val.trim()) { onChange(""); return; }
      const dmy = val.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      const ymd = val.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
      if (dmy) {
        const iso = dmy[3] + "-" + dmy[2].padStart(2,"0") + "-" + dmy[1].padStart(2,"0");
        if (!isNaN(new Date(iso).getTime())) onChange(iso);
      } else if (ymd) {
        const iso = ymd[1] + "-" + ymd[2].padStart(2,"0") + "-" + ymd[3].padStart(2,"0");
        if (!isNaN(new Date(iso).getTime())) onChange(iso);
      }
    };
    if (editing) {
      return <input autoFocus style={{ fontSize: 11, width: "100%", background: "#fff", color: "#1a1a1a", border: "2px solid var(--green)", borderRadius: 3, padding: "4px 6px" }}
        value={text} placeholder="DD-MM-YYYY"
        onChange={e => setText(e.target.value)}
        onBlur={e => commit(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") commit(e.target.value); if (e.key === "Escape") setEditing(false); }} />;
    }
    return (
      <div style={{ fontSize: 11, width: "100%", background: "#fff", color: value ? "#1a1a1a" : "#aaa", border: "1px solid var(--border)", borderRadius: 3, padding: "3px 4px", cursor: "pointer", minHeight: 26, display: "flex", alignItems: "center", justifyContent: "space-between", boxSizing: "border-box" }}
        onClick={() => { setText(toDisplay(value)); setEditing(true); }}>
        <span>{toDisplay(value) || "DD-MM-YYYY"}</span>
        {value && <span style={{ fontSize: 9, color: "#aaa" }} onClick={e => { e.stopPropagation(); onChange(""); }}>{"✕"}</span>}
      </div>
    );
  };

  const TaskRow = ({ task, moduleIndex = null }) => {
    const todayStr = new Date().toISOString().split("T")[0];
    const inThreeDays = new Date(); inThreeDays.setDate(inThreeDays.getDate() + 3);
    const threeDaysStr = inThreeDays.toISOString().split("T")[0];
    const isComplete = task.status === "Complete";
    const isBehind   = task.status === "Behind" || (!isComplete && task.finish && task.finish < todayStr);
    const isDueSoon  = !isComplete && !isBehind && task.finish && task.finish <= threeDaysStr && task.finish >= todayStr;
    const rowBg = isComplete ? "#47ac4c12" : isBehind ? "#e3252912" : isDueSoon ? "#F1901E12" : "var(--surface)";
    const rowBorder = isComplete ? "#47ac4c40" : isBehind ? "#e3252940" : isDueSoon ? "#F1901E40" : "var(--border)";
    const nameColor = isComplete ? "var(--green)" : isBehind ? "var(--red)" : isDueSoon ? "var(--amber)" : "var(--text)";
    return (
    <div className="impl-task-row" style={{ background: rowBg, borderColor: rowBorder }}>
      <span className="impl-task-name" style={{ color: nameColor, fontWeight: isComplete || isBehind ? 600 : 500 }}>
        {isComplete ? "✓ " : isBehind ? "⚠ " : isDueSoon ? "⏰ " : ""}{task.name}
      </span>
      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{task.owner}</span>
      <select
        style={{ fontSize: 11, background: "#fff", color: "#1a1a1a", border: "1px solid var(--border)", borderRadius: 3, padding: "4px 4px", width: "100%", minWidth: 0 }}
        value={task.status}
        onChange={e => updateTask(task.id, { status: e.target.value }, moduleIndex)}>
        {IMPL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <SmartDateInput value={task.start || ""} onChange={v => updateTask(task.id, { start: v }, moduleIndex)} />
      <span style={{ fontSize: 10, color: task.depLabel ? "var(--amber)" : "var(--text-dim)", textAlign: "center", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0 }}>
        {task.depLabel || ""}
      </span>
      <SmartDateInput value={task.finish || ""} onChange={v => updateTask(task.id, { finish: v }, moduleIndex)} />
      <SmartDateInput value={task.milestone || ""} onChange={v => updateTask(task.id, { milestone: v }, moduleIndex)} />
      <CommentsCell taskId={task.id} moduleIndex={moduleIndex} initialValue={task.comments || ""} onSave={(v) => updateTask(task.id, { comments: v }, moduleIndex)} />
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {task.jiraKey ? (
          <>
            <a href={"https://" + (config.jiraUrl || "").replace(/^https?:\/\//, "") + "/browse/" + task.jiraKey}
              target="_blank" rel="noreferrer"
              style={{ fontSize: 11, color: "#4c9aff", fontWeight: 600, whiteSpace: "nowrap" }}>
              🔷 {task.jiraKey}
            </a>
            <JiraStatusBadge status={task.jiraStatus} />
          </>
        ) : (
          <button className="btn" style={{ fontSize: 10, padding: "3px 8px", whiteSpace: "nowrap" }}
            onClick={() => handleRaiseJira(task, moduleIndex)}>
            + Jira
          </button>
        )}
      </div>
    </div>
    );
  };

  const [dashFilter, setDashFilter] = useState(null);
  const renderDashboard = () => {
    const filteredTasks = dashFilter ? allTasks.filter(t => t.status === dashFilter) : null;
    const statCards = [
      { label: "Not Started", color: "var(--text-muted)", status: "Not Started" },
      { label: "In Progress", color: "#4c9aff",           status: "In Progress" },
      { label: "Behind",      color: "var(--red)",        status: "Behind" },
      { label: "Complete",    color: "var(--green)",      status: "Complete" },
    ];
    return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, padding: "12px 16px" }}>
      <div className="impl-dashboard-grid">
        <div className="impl-stat-card">
          <div className="impl-stat-value" style={{ color: "var(--green)" }}>{progress}%</div>
          <div className="impl-stat-label">Overall Progress</div>
          <div className="impl-progress-bar" style={{ marginTop: 8 }}>
            <div className="impl-progress-fill" style={{ width: progress + "%" }} />
          </div>
        </div>
        {statCards.map(sc => (
          <div key={sc.status} className="impl-stat-card"
            onClick={() => setDashFilter(dashFilter === sc.status ? null : sc.status)}
            style={{ cursor: "pointer", borderColor: dashFilter === sc.status ? sc.color : "var(--border)", transition: "all 0.15s" }}
            title={"Click to filter tasks by: " + sc.label}>
            <div className="impl-stat-value" style={{ color: sc.color }}>{statusCounts[sc.status] || 0}</div>
            <div className="impl-stat-label">{sc.label}</div>
            {dashFilter === sc.status && (
              <div style={{ fontSize: 9, color: sc.color, marginTop: 2 }}>▼ Filtering - click to clear</div>
            )}
          </div>
        ))}
      </div>
      <div>
        <div className="impl-section-title" style={{ marginBottom: 8 }}>Task Status Breakdown</div>
        {[...new Set(allTasks.map(t => t.section))].map(section => {
          const sectionTasks = allTasks.filter(t => t.section === section);
          const done = sectionTasks.filter(t => t.status === "Complete").length;
          const pct = Math.round((done / sectionTasks.length) * 100);
          return (
            <div key={section} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 180, fontSize: 11, color: "var(--text)", flexShrink: 0 }}>{section}</div>
              <div style={{ flex: 1, height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ height: "100%", width: pct + "%", background: "var(--green)", borderRadius: 4 }} />
              </div>
              <div style={{ width: 36, fontSize: 10, color: "var(--text-muted)", textAlign: "right" }}>{pct}%</div>
            </div>
          );
        })}
      </div>
      <div>
        <div className="impl-section-title" style={{ marginBottom: 8 }}>⚠ Behind / Overdue Tasks</div>
        {allTasks.filter(t => t.status === "Behind" || (t.finish && t.finish < new Date().toISOString().split("T")[0] && t.status !== "Complete")).length === 0 ? (
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>No tasks behind schedule ✓</div>
        ) : allTasks.filter(t => t.status === "Behind" || (t.finish && t.finish < new Date().toISOString().split("T")[0] && t.status !== "Complete")).map(t => (
          <div key={t.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "5px 8px", background: "#e3252910", border: "1px solid #e3252930", borderRadius: 4, marginBottom: 4, fontSize: 11 }}>
            <span style={{ color: "var(--red)" }}>⚠</span>
            <span style={{ flex: 1, color: "var(--text)" }}>{t.name}</span>
            <span style={{ color: "var(--text-muted)" }}>{t.owner}</span>
            <span className={"impl-status-badge " + statusClass(t.status)}>{t.status}</span>
          </div>
        ))}
      </div>
      {dashFilter && (
        <div>
          <div className="impl-section-title" style={{ marginBottom: 8 }}>
            Filtering: <span style={{ color: "var(--green)" }}>{dashFilter}</span>
            <button className="btn" style={{ fontSize: 9, padding: "1px 8px", marginLeft: 10 }} onClick={() => setDashFilter(null)}>✕ Clear</button>
          </div>
          {filteredTasks.map(t => (
            <div key={t.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 4, marginBottom: 4, fontSize: 11 }}>
              <span style={{ flex: 1, color: "var(--text)", fontWeight: 500 }}>{t.name}</span>
              <span style={{ color: "var(--text-muted)" }}>{t.section}</span>
              <span style={{ color: "var(--text-muted)" }}>{t.owner}</span>
              {t.start && <span style={{ color: "var(--text-muted)", fontSize: 10 }}>▶ {t.start}</span>}
              {t.finish && <span style={{ color: "var(--text-muted)", fontSize: 10 }}>⬛ {t.finish}</span>}
              {t.jiraKey && <span style={{ color: "#4c9aff", fontSize: 10 }}>🔷 {t.jiraKey}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  const renderPlan = () => {
    const allSections = [...new Set(project.tasks.map(t => t.section))];
    const topSections = allSections.filter(s => !BOTTOM_SECTIONS.includes(s));
    const bottomSections = allSections.filter(s => BOTTOM_SECTIONS.includes(s));
    return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%", boxSizing: "border-box", overflowX: "hidden", padding: "8px 8px" }}>
      <div className="impl-task-row header" style={{ alignItems: "center" }}>
        <span style={{ textAlign: "left" }}>Task</span>
        <span style={{ textAlign: "left" }}>Owner</span>
        <span style={{ textAlign: "center" }}>Status</span>
        <span style={{ textAlign: "center" }}>Start</span>
        <span style={{ textAlign: "center", fontSize: 9 }}>D</span>
        <span style={{ textAlign: "center" }}>Planned<br/>Finish</span>
        <span style={{ textAlign: "center" }}>Actual<br/>Finish</span>
        <span style={{ textAlign: "center" }}>Comments</span>
        <span style={{ textAlign: "center" }}>Jira</span>
      </div>
      {topSections.filter(s => s !== "Dashboard Setup").map(section => (
        <div key={section} className="impl-section">
          <div className="impl-section-title">{section}</div>
          {project.tasks.filter(t => t.section === section).map(t => <TaskRow key={t.id} task={t} />)}
        </div>
      ))}
      {/* Dashboard Setup rendered above modules */}
      {allSections.includes("Dashboard Setup") && (
        <div className="impl-section">
          <div className="impl-section-title">Dashboard Setup</div>
          {project.tasks.filter(t => t.section === "Dashboard Setup").map(t => <TaskRow key={t.id} task={t} />)}
        </div>
      )}
      {project.modules.map((mod, mi) => (
        <div key={mod.name} className="impl-section">
          <div className="impl-section-header">
            <div className="impl-section-title">📋 {mod.name}</div>
            <button className="btn" style={{ fontSize: 10, padding: "2px 8px", color: "var(--red)", borderColor: "var(--red)" }}
              onClick={() => handleRemoveModule(mi)}>Remove</button>
          </div>
          {mod.tasks.map(t => <TaskRow key={t.id} task={t} moduleIndex={mi} />)}
        </div>
      ))}
      {bottomSections.filter(s => s !== "Dashboard Setup").map(section => (
        <div key={section} className="impl-section">
          <div className="impl-section-title" style={{ color: "var(--green)" }}>🏁 {section}</div>
          {project.tasks.filter(t => t.section === section).map(t => <TaskRow key={t.id} task={t} />)}
        </div>
      ))}
    </div>
    );
  };

  const [dragOverCol, setDragOverCol] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const renderKanban = () => {
    const headerColors = {
      "Not Started": "var(--text-dim)",
      "In Progress": "#4c9aff",
      "Behind": "var(--red)",
      "Complete": "var(--green)",
      "Not Required": "#4a6070",
    };
    const kanbanStatuses = ["Not Started", "In Progress", "Behind", "Complete", "Not Required"];
    return (
      <div className="impl-kanban" style={{ padding: "12px 16px" }}>
        {kanbanStatuses.map(status => {
          const col = allTasks.filter(t => t.status === status);
          const isOver = dragOverCol === status;
          return (
            <div
              key={status}
              className="impl-kanban-col"
              style={{ outline: isOver ? "2px solid var(--green)" : "none", transition: "outline 0.1s" }}
              onDragOver={e => { e.preventDefault(); setDragOverCol(status); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
              onDrop={e => {
                e.preventDefault();
                setDragOverCol(null);
                const taskId = e.dataTransfer.getData("taskId");
                const moduleIdx = e.dataTransfer.getData("moduleIdx");
                if (!taskId) return;
                const modIdx = moduleIdx === "" ? null : parseInt(moduleIdx, 10);
                updateTask(taskId, { status }, modIdx);
                setDraggingId(null);
              }}
            >
              <div className="impl-kanban-col-header" style={{ color: headerColors[status] || "var(--text-muted)", borderTop: "3px solid " + (headerColors[status] || "var(--text-dim)"), borderRadius: "6px 6px 0 0" }}>
                {status}
                <span style={{ fontSize: 10, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10, padding: "1px 7px", color: "var(--text-muted)" }}>{col.length}</span>
              </div>
              {isOver && col.length === 0 && (
                <div style={{ margin: 8, padding: "12px 8px", border: "2px dashed var(--green)", borderRadius: 4, fontSize: 11, color: "var(--green)", textAlign: "center", opacity: 0.7 }}>
                  Drop here
                </div>
              )}
              {col.map(t => {
                // Find moduleIndex for this task
                let modIdx = null;
                project.modules.forEach((m, i) => { if (m.tasks.some(mt => mt.id === t.id)) modIdx = i; });
                const isDragging = draggingId === t.id;
                return (
                  <div
                    key={t.id}
                    className="impl-kanban-card"
                    draggable
                    onDragStart={e => {
                      e.dataTransfer.setData("taskId", t.id);
                      e.dataTransfer.setData("moduleIdx", modIdx === null ? "" : String(modIdx));
                      e.dataTransfer.effectAllowed = "move";
                      setDraggingId(t.id);
                    }}
                    onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                    style={{
                      cursor: "grab",
                      opacity: isDragging ? 0.4 : 1,
                      borderColor: isDragging ? "var(--green)" : undefined,
                      transition: "opacity 0.15s, border-color 0.15s",
                    }}
                  >
                    <div className="impl-kanban-card-title">{t.name}</div>
                    <div className="impl-kanban-card-meta">{t.section} · {t.owner}</div>
                    {t.jiraKey && <div style={{ fontSize: 10, color: "#4c9aff", marginTop: 4 }}>🔷 {t.jiraKey}</div>}
                    {/* Quick-move buttons as fallback */}
                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 6 }}>
                      {kanbanStatuses.filter(s => s !== status).map(s => (
                        <button key={s} className="btn"
                          style={{ fontSize: 9, padding: "1px 6px", flex: 1 }}
                          onClick={() => updateTask(t.id, { status: s }, modIdx)}>
                          → {s === "Not Started" ? "To Do" : s === "In Progress" ? "In Progress" : s === "Behind" ? "Behind" : "Complete"}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const [ganttShowAll, setGanttShowAll] = React.useState(false);

  const renderGantt = () => {
    const dated = allTasks.filter(t => t.start && t.finish);
    const undated = ganttShowAll ? allTasks.filter(t => !t.start || !t.finish) : [];
    if (!dated.length && !undated.length) return (
      <div className="impl-empty">
        <div className="impl-empty-icon">📅</div>
        <div>Add start and finish dates to tasks to see the Gantt chart.</div>
      </div>
    );

    const allDates = dated.flatMap(t => [t.start, t.finish, t.milestone].filter(Boolean));
    // Snap to Monday for start, Friday for end to align with weeks
    const rawMin = new Date(Math.min(...allDates.map(d => new Date(d))));
    const rawMax = new Date(Math.max(...allDates.map(d => new Date(d))));
    // Pad by 1 week each side
    const minDate = new Date(rawMin); minDate.setDate(minDate.getDate() - ((minDate.getDay() + 6) % 7)); // snap to Monday
    const maxDate = new Date(rawMax); maxDate.setDate(maxDate.getDate() + (7 - maxDate.getDay()) % 7 + 7); // snap past Sunday

    const totalDays = Math.ceil((maxDate - minDate) / 86400000);
    const PX_PER_DAY = 18; // pixels per day - compact enough for long projects, parent has overflowX:auto
    const LABEL_W = 180;   // task label column width
    const totalW = totalDays * PX_PER_DAY;

    const toX = (d) => Math.round((new Date(d) - minDate) / 86400000) * PX_PER_DAY;

    // Build week headers
    const weeks = [];
    const wCur = new Date(minDate);
    while (wCur < maxDate) {
      weeks.push(new Date(wCur));
      wCur.setDate(wCur.getDate() + 7);
    }

    // Build month groupings for the top row
    const months = [];
    let mCur = new Date(minDate); mCur.setDate(1);
    while (mCur <= maxDate) {
      const mStart = new Date(Math.max(mCur, minDate));
      const mEnd = new Date(mCur); mEnd.setMonth(mEnd.getMonth() + 1);
      const daysInRange = Math.min((mEnd - mStart) / 86400000, (maxDate - mStart) / 86400000);
      months.push({ label: mCur.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }), width: Math.round(daysInRange) * PX_PER_DAY });
      mCur.setMonth(mCur.getMonth() + 1);
    }

    const todayStr = new Date().toISOString().split("T")[0];
    const todayX = toX(todayStr);

    // dependency arrows - same pairs as before
    const awardIdx   = dated.findIndex(t => t.name === "Contract Award");
    const signIdx    = dated.findIndex(t => t.name === "Contract Sign");
    const kickoffIdx = dated.findIndex(t => t.name === "Project Kick-off Meeting");
    const rowH = 36;

    return (
      <div>
      <div style={{ padding: "8px 8px 0", display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 4 }}>
        <button className={`btn ${ganttShowAll ? "primary" : ""}`} style={{ fontSize: 11 }}
          onClick={() => setGanttShowAll(v => !v)}>
          {ganttShowAll ? "☑ Showing all tasks" : "☐ Show tasks without dates"}
        </button>
      </div>
      <div style={{ padding: "0 8px 0", overflowX: "auto", overflowY: "visible" }}>
        <div style={{ minWidth: LABEL_W + totalW, position: "relative" }}>
          {/* Month header row */}
          <div style={{ display: "flex", borderBottom: "1px solid var(--border)", marginBottom: 0 }}>
            <div style={{ width: LABEL_W, flexShrink: 0, fontSize: 10, color: "var(--text-muted)", padding: "3px 8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Task</div>
            <div style={{ display: "flex", flex: 1 }}>
              {months.map((m, i) => (
                <div key={i} style={{ width: m.width, flexShrink: 0, fontSize: 9, color: "var(--text-muted)", textAlign: "center", borderLeft: "1px solid var(--border)30", padding: "3px 0", fontWeight: 700, background: "var(--surface2)", letterSpacing: 0.3 }}>
                  {m.label}
                </div>
              ))}
            </div>
          </div>
          {/* Week header row */}
          <div style={{ display: "flex", borderBottom: "2px solid var(--border)", background: "var(--surface2)" }}>
            <div style={{ width: LABEL_W, flexShrink: 0 }} />
            <div style={{ position: "relative", width: totalW, flexShrink: 0 }}>
              {weeks.map((w, i) => (
                <div key={i} style={{
                  position: "absolute", left: toX(w.toISOString().split("T")[0]),
                  width: 7 * PX_PER_DAY, borderLeft: "1px solid var(--border)40",
                  fontSize: 8, color: "var(--text-dim)", padding: "2px 3px", lineHeight: 1.4,
                  whiteSpace: "nowrap", overflow: "hidden"
                }}>
                  {w.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}
                </div>
              ))}
              {/* Today line */}
              {todayX >= 0 && todayX <= totalW && (
                <div style={{ position: "absolute", left: todayX, top: 0, bottom: 0, width: 2, background: "var(--green)", opacity: 0.7, zIndex: 3 }} title="Today" />
              )}
            </div>
          </div>

          {/* Task rows + dependency arrows */}
          <div style={{ position: "relative" }}>
            {/* Dependency SVG overlay */}
            {(() => {
              const pairs = [[awardIdx, signIdx], [signIdx, kickoffIdx]]
                .filter(([a, b]) => a !== -1 && b !== -1 && dated[a]?.finish && dated[b]?.start);
              if (!pairs.length) return null;
              return (
                <svg style={{ position: "absolute", left: LABEL_W, top: 0, width: totalW, height: dated.length * rowH, pointerEvents: "none", zIndex: 5, overflow: "visible" }}>
                  <defs>
                    <marker id="msArrow2" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="0">
                      <path d="M0,0 L0,6 L6,3 z" fill="#4c9aff" />
                    </marker>
                  </defs>
                  {pairs.map(([fi, ti], i) => {
                    const fromX = toX(dated[fi].finish) + PX_PER_DAY;
                    const toBarX = toX(dated[ti].start);
                    const y1 = fi * rowH + rowH / 2;
                    const y2 = ti * rowH + rowH / 2;
                    // Always step right from source, then drop/rise, then always arrive from left.
                    // If target starts before our elbow, extend elbow further right so final
                    // segment always travels left→right and arrowhead always points right.
                    const elbowX = Math.max(fromX + 16, toBarX + 20);
                    const arrowTip = elbowX + 2; // final segment ends just past elbow → always rightward
                    // Draw: source → elbow (horizontal) → drop (vertical) → tip (horizontal, always right)
                    // Then draw a separate fixed right-pointing arrowhead at arrowTip,y2
                    return (
                      <g key={i}>
                        <polyline
                          points={`${fromX},${y1} ${elbowX},${y1} ${elbowX},${y2} ${arrowTip},${y2}`}
                          fill="none" stroke="#4c9aff" strokeWidth="1.5" />
                        {/* Fixed right-pointing triangle arrowhead - always points right regardless of path direction */}
                        <polygon
                          points={`${arrowTip},${y2 - 4} ${arrowTip + 7},${y2} ${arrowTip},${y2 + 4}`}
                          fill="#4c9aff" />
                      </g>
                    );
                  })}
                </svg>
              );
            })()}

            {/* Today vertical line across rows */}
            {todayX >= 0 && todayX <= totalW && (
              <div style={{ position: "absolute", left: LABEL_W + todayX, top: 0, bottom: 0, width: 2, background: "var(--green)", opacity: 0.25, zIndex: 2, pointerEvents: "none" }} />
            )}

            {dated.map(t => {
              const barLeft = toX(t.start);
              const barWidth = Math.max(toX(t.finish) - barLeft, PX_PER_DAY / 2);
              const milX = t.milestone ? toX(t.milestone) : null;
              const barColor = t.status === "Complete" ? "var(--green)"
                : t.status === "Behind" || (t.finish && t.finish < todayStr && t.status !== "Complete") ? "var(--red)"
                : t.status === "In Progress" ? "#4c9aff"
                : "var(--border)";
              return (
                <div key={t.id} style={{ display: "flex", alignItems: "center", borderBottom: "1px solid var(--border)30", height: rowH, minHeight: rowH }}>
                  <div style={{ width: LABEL_W, flexShrink: 0, fontSize: 11, padding: "0 8px", color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={t.name}>{t.name}</div>
                  <div style={{ position: "relative", width: totalW, flexShrink: 0, height: rowH }}>
                    {weeks.map((w, i) => (
                      <div key={i} style={{ position: "absolute", left: toX(w.toISOString().split("T")[0]), top: 0, bottom: 0, width: 1, background: "var(--border)", opacity: 0.15 }} />
                    ))}
                    <div style={{ position: "absolute", top: 10, left: barLeft, width: barWidth, height: 16, background: barColor, borderRadius: 3, opacity: 0.88, overflow: "hidden" }}
                      title={`${t.status}: ${t.start} → ${t.finish}`}>
                      {barWidth > 40 && (
                        <span style={{ position: "absolute", left: 4, top: 1, fontSize: 8, color: "#fff", whiteSpace: "nowrap", fontWeight: 600, lineHeight: "14px" }}>
                          {t.start} → {t.finish}
                        </span>
                      )}
                    </div>
                    {milX !== null && (
                      <div style={{ position: "absolute", left: milX - 6, top: 12, width: 12, height: 12, transform: "rotate(45deg)", background: "var(--amber)", zIndex: 4 }}
                        title={"Actual Finish: " + t.milestone} />
                    )}
                  </div>
                </div>
              );
            })}
            {/* Undated tasks - grey placeholder rows shown when "Show tasks without dates" is on */}
            {undated.map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", borderBottom: "1px solid var(--border)20", height: rowH, minHeight: rowH, opacity: 0.6 }}>
                <div style={{ width: LABEL_W, flexShrink: 0, fontSize: 11, padding: "0 8px", color: "var(--text-dim)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={t.name}>{t.name}</div>
                <div style={{ flex: 1, height: 16, margin: "0 8px", background: "transparent", border: "1px dashed var(--border)", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 9, color: "var(--text-dim)" }}>No dates set</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    );
  };

  // ── Action Log ───────────────────────────────────────────────────────────────
  const addWorkingDays = (dateStr, days) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d.toISOString().split("T")[0];
  };

  const isoToDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
  };

  const displayToIso = (disp) => {
    if (!disp) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(disp)) return disp;
    if (/^\d{2}-\d{2}-\d{4}$/.test(disp)) {
      const [d, m, y] = disp.split("-");
      return `${y}-${m}-${d}`;
    }
    return disp;
  };

  const updateActionLog = (logId, updates) => {
    if (!project) return;
    const actionLog = (project.actionLog || []).map(e =>
      e.id === logId ? { ...e, ...updates, updatedDate: new Date().toISOString().split("T")[0] } : e
    );
    updateProject({ ...project, actionLog });
  };

  const addActionLogEntry = () => {
    if (!project) return;
    const today = new Date().toISOString().split("T")[0];
    const existing = project.actionLog || [];
    const nextNum = existing.length > 0
      ? Math.max(...existing.map(e => parseInt(e.logNumber, 10) || 0)) + 1
      : 1;
    const newEntry = {
      id: makeId(),
      logNumber: String(nextNum).padStart(3, "0"),
      ticketRef: "",
      dateRaised: today,
      raisedBy: "",
      module: "",
      description: "",
      priority: "Medium",
      assignedTo: "",
      status: "Open",
      auditTrail: "",
      targetDate: addWorkingDays(today, 3),
      updatedDate: today,
      dateClosed: "",
    };
    updateProject({ ...project, actionLog: [...existing, newEntry] });
  };

  const deleteActionLogEntry = (logId) => {
    if (!project) return;
    updateProject({ ...project, actionLog: (project.actionLog || []).filter(e => e.id !== logId) });
  };

  const priorityClass = (p) => {
    if (p === "Low") return "al-low";
    if (p === "High") return "al-high";
    if (p === "Critical") return "al-critical";
    return "al-medium";
  };

  const statusClass2 = (s) => {
    if (s === "Closed") return "al-closed";
    if (s === "Customer Pending") return "al-customer-pending";
    return "al-open";
  };

  const renderActionLog = () => {
    const entries = project.actionLog || [];
    const allModules = [...new Set(project.tasks.map(t => t.section)), ...project.modules.map(m => m.name)];
    return (
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10, width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn primary" style={{ fontSize: 11 }} onClick={addActionLogEntry}>+ Add Entry</button>
        </div>
        {entries.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-dim)", fontSize: 12, fontStyle: "italic" }}>
            No action log entries yet. Click + Add Entry to begin.
          </div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
            <table className="action-log-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th style={{ width: 90 }}>Ticket Ref</th>
                  <th style={{ width: 82 }}>Date Raised</th>
                  <th style={{ width: 100 }}>Raised By</th>
                  <th style={{ width: 110 }}>Module</th>
                  <th>Description</th>
                  <th style={{ width: 72 }}>Priority</th>
                  <th style={{ width: 110 }}>Assigned To</th>
                  <th style={{ width: 95 }}>Status</th>
                  <th>Audit Trail</th>
                  <th style={{ width: 82 }}>Target Date</th>
                  <th style={{ width: 82 }}>Updated</th>
                  <th style={{ width: 82 }}>Closed</th>
                  <th style={{ width: 30 }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    {/* Log Number - read only */}
                    <td style={{ textAlign: "center", color: "var(--text-muted)", fontWeight: 700, fontSize: 10 }}>{e.logNumber}</td>
                    {/* C365 Ticket Ref */}
                    <td style={{ whiteSpace: "nowrap" }}>
                      {e.ticketRef ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <a href={`${config.jiraUrl ? config.jiraUrl.replace(/\/+$/, "") : "https://c365online.atlassian.net"}/browse/${e.ticketRef}`}
                            target="_blank" rel="noreferrer"
                            style={{ color: "#4c9aff", fontSize: 11, fontWeight: 600 }}>
                            🔷 {e.ticketRef}
                          </a>
                          <button className="btn" style={{ fontSize: 9, padding: "1px 5px" }}
                            onClick={() => updateActionLog(e.id, { ticketRef: "" })}>{"✕"}</button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input className="action-log-input" style={{ width: 70 }} placeholder="Key…"
                            value={e.ticketRef || ""}
                            onChange={v => updateActionLog(e.id, { ticketRef: v.target.value })} />
                          <button className="btn" style={{ fontSize: 9, padding: "1px 6px", color: "#4c9aff", borderColor: "#4c9aff", whiteSpace: "nowrap" }}
                            onClick={() => {
                              const logId = e.id;
                              const task = {
                                id: `actionlog-${logId}`,
                                name: e.description || "Action Log Item",
                                section: "Action Log",
                                owner: e.raisedBy || "C365",
                              };
                              const updateFn = (taskId, updates) => {
                                if (updates.jiraKey) updateActionLog(logId, { ticketRef: updates.jiraKey });
                              };
                              if (onRaiseJira) onRaiseJira(task, null, project.clientName, updateFn);
                            }}>+ Jira</button>
                        </div>
                      )}
                    </td>
                    {/* Date Raised - read only */}
                    <td style={{ color: "var(--text-muted)", fontSize: 10, whiteSpace: "nowrap" }}>{isoToDisplay(e.dateRaised)}</td>
                    {/* Raised By */}
                    <td><input className="action-log-input" value={e.raisedBy || ""} onChange={v => updateActionLog(e.id, { raisedBy: v.target.value })} /></td>
                    {/* Module */}
                    <td>
                      <select className="action-log-select" value={e.module || ""} onChange={v => updateActionLog(e.id, { module: v.target.value })}>
                        <option value="">-</option>
                        {allModules.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </td>
                    {/* Description */}
                    <td><textarea className="action-log-input" value={e.description || ""} rows={2}
                      onChange={v => updateActionLog(e.id, { description: v.target.value })} style={{ resize: "vertical" }} /></td>
                    {/* Priority */}
                    <td>
                      <select className="action-log-select" value={e.priority || "Medium"} onChange={v => updateActionLog(e.id, { priority: v.target.value })}>
                        {["Low","Medium","High","Critical"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <span className={`action-log-badge ${priorityClass(e.priority || "Medium")}`}>{e.priority || "Medium"}</span>
                    </td>
                    {/* Assigned To */}
                    <td><input className="action-log-input" value={e.assignedTo || ""} onChange={v => updateActionLog(e.id, { assignedTo: v.target.value })} /></td>
                    {/* Status */}
                    <td>
                      <select className="action-log-select" value={e.status || "Open"}
                        onChange={v => {
                          const updates = { status: v.target.value };
                          if (v.target.value === "Closed") updates.dateClosed = new Date().toISOString().split("T")[0];
                          else updates.dateClosed = "";
                          updateActionLog(e.id, updates);
                        }}>
                        {["Open","Customer Pending","Closed"].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <span className={`action-log-badge ${statusClass2(e.status || "Open")}`}>{e.status || "Open"}</span>
                    </td>
                    {/* Audit Trail */}
                    <td><textarea className="action-log-input" value={e.auditTrail || ""} rows={2}
                      onChange={v => updateActionLog(e.id, { auditTrail: v.target.value })} style={{ resize: "vertical" }} /></td>
                    {/* Target Date */}
                    <td>
                      <SmartDateInput value={e.targetDate || ""} onChange={v => updateActionLog(e.id, { targetDate: displayToIso(v) })} />
                    </td>
                    {/* Updated Date - read only, auto-set on any change */}
                    <td style={{ color: "var(--text-muted)", fontSize: 10, whiteSpace: "nowrap" }}>{isoToDisplay(e.updatedDate)}</td>
                    {/* Date Closed - read only, auto-set when status → Closed */}
                    <td style={{ color: e.dateClosed ? "var(--green)" : "var(--text-dim)", fontSize: 10, whiteSpace: "nowrap" }}>{isoToDisplay(e.dateClosed)}</td>
                    {/* Delete */}
                    <td style={{ textAlign: "center" }}>
                      <button onClick={() => deleteActionLogEntry(e.id)}
                        style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 13, lineHeight: 1 }}
                        title="Delete entry">{"✕"}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Risk Register ────────────────────────────────────────────────────────────
  const updateRisk = (riskId, updates) => {
    if (!project) return;
    const riskRegister = (project.riskRegister || []).map(r =>
      r.id === riskId ? { ...r, ...updates } : r
    );
    updateProject({ ...project, riskRegister });
  };

  const addRiskEntry = () => {
    if (!project) return;
    const existing = project.riskRegister || [];
    const nextNum = existing.length > 0
      ? Math.max(...existing.map(r => parseInt((r.riskId || "RI00").replace(/\D/g, ""), 10) || 0)) + 1
      : existing.length + 1;
    const newRisk = {
      id: makeId(),
      riskId: "RI" + String(nextNum).padStart(2, "0"),
      dateRaised: new Date().toISOString().split("T")[0],
      description: "",
      likelihood: 1, impact: 1,
      mitigation: "",
      mitigatedLikelihood: 1, mitigatedImpact: 1,
      status: "", comments: "",
    };
    updateProject({ ...project, riskRegister: [...existing, newRisk] });
  };

  const deleteRisk = (riskId) => {
    if (!project) return;
    updateProject({ ...project, riskRegister: (project.riskRegister || []).filter(r => r.id !== riskId) });
  };

  const RiskTextarea = React.memo(({ value, onChange }) => {
    const ref = React.useRef(null);
    React.useEffect(() => {
      if (ref.current) {
        ref.current.style.height = "auto";
        ref.current.style.height = Math.max(22, ref.current.scrollHeight) + "px";
      }
    }, [value]);
    return (
      <textarea
        ref={ref}
        className="risk-input"
        defaultValue={value}
        rows={1}
        style={{ resize: "none", overflow: "hidden", display: "block" }}
        onBlur={e => onChange(e.target.value)}
        onInput={e => {
          e.target.style.height = "auto";
          e.target.style.height = Math.max(22, e.target.scrollHeight) + "px";
        }}
      />
    );
  });

  const renderRiskRegister = () => {
    const risks = project.riskRegister || [];
    return (
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10, width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn primary" style={{ fontSize: 11 }} onClick={addRiskEntry}>+ Add Risk</button>
        </div>
        {risks.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-dim)", fontSize: 12, fontStyle: "italic" }}>
            No risks recorded. Click + Add Risk to begin.
          </div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
            <table className="risk-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>Risk ID</th>
                  <th style={{ width: 82 }}>Date Raised</th>
                  <th>Description</th>
                  <th style={{ width: 40, textAlign: "center" }}>L</th>
                  <th style={{ width: 40, textAlign: "center" }}>I</th>
                  <th style={{ width: 70, textAlign: "center" }}>Score</th>
                  <th>Mitigation / Control</th>
                  <th style={{ width: 40, textAlign: "center" }}>ML</th>
                  <th style={{ width: 40, textAlign: "center" }}>MI</th>
                  <th style={{ width: 70, textAlign: "center" }}>Mit. Score</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th>Comments</th>
                  <th style={{ width: 30 }}></th>
                </tr>
              </thead>
              <tbody>
                {risks.map(r => {
                  const score = (parseInt(r.likelihood, 10) || 0) * (parseInt(r.impact, 10) || 0);
                  const mitScore = (parseInt(r.mitigatedLikelihood, 10) || 0) * (parseInt(r.mitigatedImpact, 10) || 0);
                  const scoreMeta = riskScoreLabel(score);
                  const mitMeta = mitScore > 0 ? riskScoreLabel(mitScore) : null;
                  return (
                    <tr key={r.id}>
                      {/* Risk ID */}
                      <td style={{ fontWeight: 700, fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{r.riskId}</td>
                      {/* Date Raised */}
                      <td style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                        {r.dateRaised ? r.dateRaised.split("-").reverse().join("-") : ""}
                      </td>
                      {/* Description */}
                      <td>
                        <RiskTextarea value={r.description || ""} onChange={v => updateRisk(r.id, { description: v })} />
                      </td>
                      {/* Likelihood */}
                      <td style={{ textAlign: "center" }}>
                        <input className="risk-num-input" type="number" min={0} max={5} value={r.likelihood ?? ""}
                          onChange={e => updateRisk(r.id, { likelihood: parseInt(e.target.value, 10) || 0 })} />
                      </td>
                      {/* Impact */}
                      <td style={{ textAlign: "center" }}>
                        <input className="risk-num-input" type="number" min={0} max={5} value={r.impact ?? ""}
                          onChange={e => updateRisk(r.id, { impact: parseInt(e.target.value, 10) || 0 })} />
                      </td>
                      {/* Risk Score */}
                      <td style={{ textAlign: "center" }}>
                        <span className="risk-score-badge" style={{ color: scoreMeta.color, background: scoreMeta.bg, border: `1px solid ${scoreMeta.color}50` }}>
                          {score}<br />
                          <span style={{ fontSize: 9, fontWeight: 400 }}>{scoreMeta.label}</span>
                        </span>
                      </td>
                      {/* Mitigation */}
                      <td>
                        <RiskTextarea value={r.mitigation || ""} onChange={v => updateRisk(r.id, { mitigation: v })} />
                      </td>
                      {/* Mitigated Likelihood */}
                      <td style={{ textAlign: "center" }}>
                        <input className="risk-num-input" type="number" min={0} max={5} value={r.mitigatedLikelihood ?? ""}
                          onChange={e => updateRisk(r.id, { mitigatedLikelihood: parseInt(e.target.value, 10) || 0 })} />
                      </td>
                      {/* Mitigated Impact */}
                      <td style={{ textAlign: "center" }}>
                        <input className="risk-num-input" type="number" min={0} max={5} value={r.mitigatedImpact ?? ""}
                          onChange={e => updateRisk(r.id, { mitigatedImpact: parseInt(e.target.value, 10) || 0 })} />
                      </td>
                      {/* Mitigation Score */}
                      <td style={{ textAlign: "center" }}>
                        {mitScore > 0 && mitMeta ? (
                          <span className="risk-score-badge" style={{ color: mitMeta.color, background: mitMeta.bg, border: `1px solid ${mitMeta.color}50` }}>
                            {mitScore}<br />
                            <span style={{ fontSize: 9, fontWeight: 400 }}>{mitMeta.label}</span>
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-dim)", fontSize: 10 }}>-</span>
                        )}
                      </td>
                      {/* Status */}
                      <td>
                        <select className="risk-select" value={r.status || ""}
                          onChange={e => updateRisk(r.id, { status: e.target.value })}>
                          <option value="">Open</option>
                          <option value="Mitigated">Mitigated</option>
                          <option value="Accepted">Accepted</option>
                          <option value="Closed">Closed</option>
                          <option value="Escalated">Escalated</option>
                        </select>
                      </td>
                      {/* Comments */}
                      <td>
                        <RiskTextarea value={r.comments || ""} onChange={v => updateRisk(r.id, { comments: v })} />
                      </td>
                      {/* Delete */}
                      <td style={{ textAlign: "center" }}>
                        <button onClick={() => deleteRisk(r.id)}
                          style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 13 }}
                          title="Delete risk">{"✕"}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Module Setup ──────────────────────────────────────────────────────────────
  const updateModuleSetupRow = (rowId, updates) => {
    if (!project) return;
    const moduleSetup = (project.moduleSetup || []).map(r => r.id === rowId ? { ...r, ...updates } : r);
    updateProject({ ...project, moduleSetup });
  };

  const addModuleSetupRow = () => {
    if (!project) return;
    const newRow = {
      id: makeId(),
      category: "", compliance: "", supplier: "",
      freqUnit: "Month", red: "", amber: "", green: "",
      assetised: "No",
      uploadTemplate: "No", uploadOnsite: "No", uploadHistorical: "No",
      alertOnAction: "No", alertOverdue: "No",
      historicDocsRequired: "No", supplierSetup: "No", moduleLive: "No",
      jiraKey: "", jiraId: "",
    };
    updateProject({ ...project, moduleSetup: [...(project.moduleSetup || []), newRow] });
  };

  const deleteModuleSetupRow = (rowId) => {
    if (!project) return;
    updateProject({ ...project, moduleSetup: (project.moduleSetup || []).filter(r => r.id !== rowId) });
  };

  const JiraStatusBadge = ({ status }) => {
    if (!status) return null;
    const s = status.toLowerCase();
    const color = s.includes("done") || s.includes("complete") || s.includes("closed") || s.includes("resolved")
      ? "var(--green)"
      : s.includes("progress") || s.includes("review") || s.includes("development") || s.includes("testing")
      ? "#4c9aff"
      : s.includes("block") || s.includes("behind") || s.includes("overdue")
      ? "var(--red)"
      : s.includes("hold") || s.includes("wait") || s.includes("pending")
      ? "var(--amber)"
      : "var(--text-muted)";
    return (
      <span style={{ display: "block", fontSize: 9, color, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", marginTop: 2, letterSpacing: 0.2 }}>
        {status}
      </span>
    );
  };

  const YNSelect = ({ value, onChange }) => (
    <select className="ms-select" value={value || "No"} onChange={e => onChange(e.target.value)}>
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  );

  const YNCell = ({ value }) => (
    <span className={value === "Yes" ? "ms-yes" : "ms-no"}>{value || "No"}</span>
  );

  const renderModuleSetup = () => {
    const rows = project.moduleSetup || [];
    // Group by category - only named categories (rows without a category sit at the bottom ungrouped)
    const categories = [...new Set(rows.filter(r => r.category).map(r => r.category))];
    const uncategorisedRows = rows.filter(r => !r.category);

    const addRowForCategory = (cat) => {
      const newRow = {
        id: makeId(),
        category: cat, compliance: "", supplier: "",
        freqUnit: "Month", red: "", amber: "", green: "",
        assetised: "No",
        uploadTemplate: "No", uploadOnsite: "No", uploadHistorical: "No",
        alertOnAction: "No", alertOverdue: "No",
        historicDocsRequired: "No", supplierSetup: "No", moduleLive: "No",
        jiraKey: "", jiraId: "",
      };
      updateProject({ ...project, moduleSetup: [...rows, newRow] });
    };

    const renderRow = (r) => (
      <tr key={r.id}>
        <td><input className="ms-input" value={r.category || ""} onChange={e => updateModuleSetupRow(r.id, { category: e.target.value })} placeholder="e.g. Electrical" /></td>
        <td><input className="ms-input" value={r.compliance || ""} onChange={e => updateModuleSetupRow(r.id, { compliance: e.target.value })} placeholder="e.g. EICR" /></td>
        <td><input className="ms-input" value={r.supplier || ""} onChange={e => updateModuleSetupRow(r.id, { supplier: e.target.value })} placeholder="e.g. Supplier A" /></td>
        <td style={{ textAlign: "center" }}>
          <select className="ms-select" value={r.freqUnit || "Month"} onChange={e => updateModuleSetupRow(r.id, { freqUnit: e.target.value })}>
            {["Day","Week","Month","Year"].map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </td>
        <td style={{ textAlign: "center" }}><input className="ms-num" type="number" min={0} value={r.red ?? ""} onChange={e => updateModuleSetupRow(r.id, { red: e.target.value })} style={{ borderColor: "#e3252940" }} /></td>
        <td style={{ textAlign: "center" }}><input className="ms-num" type="number" min={0} value={r.amber ?? ""} onChange={e => updateModuleSetupRow(r.id, { amber: e.target.value })} style={{ borderColor: "#F1901E40" }} /></td>
        <td style={{ textAlign: "center" }}><input className="ms-num" type="number" min={0} value={r.green ?? ""} onChange={e => updateModuleSetupRow(r.id, { green: e.target.value })} style={{ borderColor: "#47ac4c40" }} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.assetised} onChange={v => updateModuleSetupRow(r.id, { assetised: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.uploadTemplate} onChange={v => {
          updateModuleSetupRow(r.id, { uploadTemplate: v });
          if (v === "Yes") {
            const complianceVal = r.compliance || "";
            const alreadyExists = (project.templates || []).some(t => t.complianceType === complianceVal && complianceVal !== "");
            if (!alreadyExists) {
              const newRow = { id: Date.now() + Math.random(), complianceType: complianceVal, contractor: "", examplesProvided: "", edStatus: "Not Started", templateStatus: "Not Started", docUploadSetup: "No", routeCreated: "No", comments: "", jiraKey: "" };
              updateProject({ ...project, moduleSetup: (project.moduleSetup || []).map(r2 => r2.id === r.id ? { ...r2, uploadTemplate: v } : r2), templates: [...(project.templates || []), newRow] });
            }
          }
        }} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.uploadOnsite} onChange={v => {
          updateModuleSetupRow(r.id, { uploadOnsite: v });
          if (v === "Yes") {
            const complianceVal = r.compliance || "";
            const alreadyExists = (project.mobileForms || []).some(f => f.complianceType === complianceVal && complianceVal !== "");
            if (!alreadyExists) {
              const newRow = { id: Date.now() + Math.random(), complianceType: complianceVal, formDevType: "", formExampleProvided: "No", formBuilt: "No", formDespatch: "No", formCustomerSignoff: "No", comments: "", jiraKey: "" };
              updateProject({ ...project, moduleSetup: (project.moduleSetup || []).map(r2 => r2.id === r.id ? { ...r2, uploadOnsite: v } : r2), mobileForms: [...(project.mobileForms || []), newRow] });
            }
          }
        }} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.uploadHistorical} onChange={v => updateModuleSetupRow(r.id, { uploadHistorical: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.alertOnAction} onChange={v => updateModuleSetupRow(r.id, { alertOnAction: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.alertOverdue} onChange={v => updateModuleSetupRow(r.id, { alertOverdue: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.historicDocsRequired} onChange={v => updateModuleSetupRow(r.id, { historicDocsRequired: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.supplierSetup} onChange={v => updateModuleSetupRow(r.id, { supplierSetup: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.moduleLive} onChange={v => updateModuleSetupRow(r.id, { moduleLive: v })} /></td>
        <td style={{ textAlign: "center" }}>
          {r.jiraKey ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <span className="ms-jira-key">🔷 {r.jiraKey}</span>
              <JiraStatusBadge status={r.jiraStatus} />
            </div>
          ) : (
            <button className="btn" style={{ fontSize: 9, padding: "2px 7px", color: "#4c9aff", borderColor: "#4c9aff40" }}
              onClick={() => handleRaiseJira({ id: r.id, name: (r.category || "Module") + " - " + (r.compliance || "Setup"), section: "Module Setup", owner: "C365" }, null, r.id)}>
              + Jira
            </button>
          )}
        </td>
        <td style={{ textAlign: "center" }}>
          <button onClick={() => deleteModuleSetupRow(r.id)}
            style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 13 }} title="Delete row">{"✕"}</button>
        </td>
      </tr>
    );

    const thead = (
      <thead>
        <tr>
          <th className="left" rowSpan={2} style={{ minWidth: 75 }}>Category</th>
          <th className="left" rowSpan={2} style={{ minWidth: 100 }}>Compliance</th>
          <th className="left" rowSpan={2} style={{ minWidth: 100 }}>Supplier</th>
          <th colSpan={4} style={{ borderBottom: "1px solid var(--border)" }}>Frequency of Inspection</th>
          <th rowSpan={2} style={{ minWidth: 50 }}>Assetised</th>
          <th colSpan={3} style={{ borderBottom: "1px solid var(--border)" }}>Upload Type</th>
          <th colSpan={2} style={{ borderBottom: "1px solid var(--border)" }}>Email Alerts</th>
          <th colSpan={3} style={{ borderBottom: "1px solid var(--border)" }}>Final Setup</th>
          <th rowSpan={2} style={{ minWidth: 70 }}>Jira</th>
          <th rowSpan={2} style={{ width: 24 }}></th>
        </tr>
        <tr>
          <th className="wrap" style={{ minWidth: 60 }}>Unit</th>
          <th className="wrap" style={{ minWidth: 50, color: "var(--red)" }}>RED</th>
          <th className="wrap" style={{ minWidth: 50, color: "var(--amber)" }}>AMBER</th>
          <th className="wrap" style={{ minWidth: 50, color: "var(--green)" }}>GREEN</th>
          <th className="wrap" style={{ minWidth: 80 }}>Template</th>
          <th className="wrap" style={{ minWidth: 44 }}>OnSite</th>
          <th className="wrap" style={{ minWidth: 44 }}>Hist / Untempl</th>
          <th className="wrap" style={{ minWidth: 38 }}>On Action Creation</th>
          <th className="wrap" style={{ minWidth: 38 }}>Compliance Overdue</th>
          <th className="wrap" style={{ minWidth: 38 }}>Historic Docs Upload Required</th>
          <th className="wrap" style={{ minWidth: 52 }}>Supplier Setup &amp; Engaged</th>
          <th className="wrap" style={{ minWidth: 48 }}>Module Fully Live</th>
        </tr>
      </thead>
    );

    return (
      <div style={{ padding: "12px 8px", display: "flex", flexDirection: "column", gap: 10, width: "100%", boxSizing: "border-box" }}>
        {rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-dim)", fontSize: 12, fontStyle: "italic" }}>
            No module setup entries yet. Add a compliance module from the Plan tab to get started.
          </div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
            <table className="ms-table">
              {thead}
              <tbody>
                {categories.map(cat => {
                  const catRows = rows.filter(r => r.category === cat);
                  return (
                    <React.Fragment key={cat}>
                      {/* Plan-style section header spanning all 18 columns */}
                      <tr>
                        <td colSpan={18} style={{ padding: 0, border: "none", paddingTop: 8 }}>
                          <div className="ms-section-header">
                            <span style={{ color: "var(--green)", fontSize: 11 }}>📋</span>
                            <span>{cat}</span>
                            <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 9, fontWeight: 400, color: "var(--text-dim)" }}>{catRows.length} row{catRows.length !== 1 ? "s" : ""}</span>
                              <button className="btn" style={{ fontSize: 9, padding: "2px 9px" }}
                                onClick={() => addRowForCategory(cat)}>+ Add Row</button>
                            </span>
                          </div>
                        </td>
                      </tr>
                      {catRows.map(r => renderRow(r))}
                    </React.Fragment>
                  );
                })}
                {uncategorisedRows.map(r => renderRow(r))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Templates ─────────────────────────────────────────────────────────────────
  const updateTemplate = (rowId, updates) => {
    if (!project) return;
    const templates = (project.templates || []).map(r => r.id === rowId ? { ...r, ...updates } : r);
    updateProject({ ...project, templates });
  };

  const addTemplateRow = () => {
    if (!project) return;
    const newRow = {
      id: makeId(),
      supportKey: "", edKey: "", templKey: "",
      complianceType: "", contractor: "", examplesProvided: "",
      edStatus: "", templStatus: "Waiting on ED",
      docUploadSetup: "No", routeCreated: "No", comments: "",
    };
    updateProject({ ...project, templates: [...(project.templates || []), newRow] });
  };

  const deleteTemplateRow = (rowId) => {
    if (!project) return;
    updateProject({ ...project, templates: (project.templates || []).filter(r => r.id !== rowId) });
  };

  const renderTemplates = () => {
    const rows = project.templates || [];
    // Determine template status display
    const getTemplStatus = (r) => {
      if (r.templStatus && r.templStatus !== "Waiting on ED") return r.templStatus;
      if (r.edStatus) {
        const edDone = r.edStatus.toLowerCase().includes("done") || r.edStatus.toLowerCase().includes("complete") || r.edStatus.toLowerCase().includes("closed");
        if (!edDone) return "Waiting on ED";
        if (!r.templKey) return "Waiting on TEMPL";
        return r.templStatus || r.edStatus;
      }
      return "Waiting on ED";
    };

    const statusColour = (status) => {
      if (!status) return "var(--text-dim)";
      const s = status.toLowerCase();
      if (s.includes("done") || s.includes("complete") || s.includes("closed")) return "var(--green)";
      if (s.includes("waiting")) return "var(--amber)";
      if (s.includes("progress") || s.includes("review")) return "#4c9aff";
      return "var(--text-muted)";
    };

    return (
      <div style={{ padding: "12px 8px", display: "flex", flexDirection: "column", gap: 10, width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn primary" style={{ fontSize: 11 }} onClick={addTemplateRow}>+ Add Row</button>
        </div>
        {rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-dim)", fontSize: 12, fontStyle: "italic" }}>
            No template entries yet. Click + Add Row to begin.
          </div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
            <table className="ms-table">
              <thead>
                <tr>
                  <th className="left" style={{ minWidth: 110 }}>Jira Ref Number</th>
                  <th className="left" style={{ minWidth: 100 }}>Compliance Type</th>
                  <th className="left" style={{ minWidth: 90 }}>Contractor</th>
                  <th style={{ minWidth: 60 }}>Examples Provided</th>
                  <th style={{ minWidth: 90 }}>ED Status</th>
                  <th style={{ minWidth: 90 }}>Template Status</th>
                  <th style={{ minWidth: 52 }}>Doc Upload Setup</th>
                  <th style={{ minWidth: 52 }}>Route Created Module</th>
                  <th style={{ minWidth: 120 }}>Comments</th>
                  <th style={{ width: 24 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const templStatusVal = getTemplStatus(r);
                  const edDone = r.edStatus && (r.edStatus.toLowerCase().includes("done") || r.edStatus.toLowerCase().includes("complete") || r.edStatus.toLowerCase().includes("closed"));
                  return (
                    <tr key={r.id}>
                      {/* Jira Ref - + Jira button, then shows SUPPORT key + linked keys */}
                      <td>
                        {r.supportKey ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <a href={"https://" + (config.jiraUrl || "").replace(/^https?:\/\//, "") + "/browse/" + r.supportKey}
                              target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "#4c9aff", fontWeight: 600, whiteSpace: "nowrap" }}>
                              🔷 {r.supportKey}
                            </a>
                            {r.edKey && (
                              <a href={"https://" + (config.jiraUrl || "").replace(/^https?:\/\//, "") + "/browse/" + r.edKey}
                                target="_blank" rel="noreferrer" style={{ fontSize: 9, color: edDone ? "var(--green)" : "var(--amber)", whiteSpace: "nowrap" }}>
                                🔗 {r.edKey}
                              </a>
                            )}
                            {r.templKey && (
                              <a href={"https://" + (config.jiraUrl || "").replace(/^https?:\/\//, "") + "/browse/" + r.templKey}
                                target="_blank" rel="noreferrer" style={{ fontSize: 9, color: "var(--green)", whiteSpace: "nowrap" }}>
                                📄 {r.templKey}
                              </a>
                            )}
                          </div>
                        ) : (
                          <button className="btn" style={{ fontSize: 9, padding: "2px 7px", color: "#4c9aff", borderColor: "#4c9aff40" }}
                            onClick={() => {
                              const updateFn = (taskId, updates) => {
                                updateProject({
                                  ...project,
                                  templates: (project.templates || []).map(t =>
                                    t.id === r.id ? { ...t, supportKey: updates.jiraKey || t.supportKey } : t
                                  ),
                                });
                              };
                              handleRaiseJira({ id: r.id, name: (r.complianceType || "Template") + " - " + (r.contractor || ""), section: "Templates", owner: "C365" }, null, null, updateFn);
                            }}>
                            + Jira
                          </button>
                        )}
                      </td>
                      {/* Compliance Type */}
                      <td><input className="ms-input" value={r.complianceType || ""} onChange={e => updateTemplate(r.id, { complianceType: e.target.value })} placeholder="e.g. EICR" /></td>
                      {/* Contractor */}
                      <td><input className="ms-input" value={r.contractor || ""} onChange={e => updateTemplate(r.id, { contractor: e.target.value })} placeholder="e.g. Supplier A" /></td>
                      {/* Number of Examples Provided */}
                      <td style={{ textAlign: "center" }}>
                        <input className="ms-num" type="number" min={0} value={r.examplesProvided ?? ""} onChange={e => updateTemplate(r.id, { examplesProvided: e.target.value })} style={{ width: 48 }} />
                      </td>
                      {/* ED Status - from linked ED- card */}
                      <td style={{ textAlign: "center" }}>
                        {r.edKey ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                            <span style={{ fontSize: 9, color: "var(--text-dim)" }}>{r.edKey}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: statusColour(r.edStatus) }}>
                              {r.edStatus || "-"}
                            </span>
                          </div>
                        ) : (
                          <span style={{ fontSize: 10, color: "var(--text-dim)" }}>No ED card</span>
                        )}
                      </td>
                      {/* Template Status - from linked TEMPL- card or derived */}
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                          {r.templKey && <span style={{ fontSize: 9, color: "var(--text-dim)" }}>{r.templKey}</span>}
                          <span style={{ fontSize: 10, fontWeight: 600, color: statusColour(templStatusVal) }}>
                            {templStatusVal}
                          </span>
                        </div>
                      </td>
                      {/* Doc Upload Setup */}
                      <td style={{ textAlign: "center" }}><YNSelect value={r.docUploadSetup} onChange={v => updateTemplate(r.id, { docUploadSetup: v })} /></td>
                      {/* Route Created Module */}
                      <td style={{ textAlign: "center" }}><YNSelect value={r.routeCreated} onChange={v => updateTemplate(r.id, { routeCreated: v })} /></td>
                      {/* Comments */}
                      <td><input className="ms-input" value={r.comments || ""} onChange={e => updateTemplate(r.id, { comments: e.target.value })} /></td>
                      {/* Delete */}
                      <td style={{ textAlign: "center" }}>
                        <button onClick={() => deleteTemplateRow(r.id)}
                          style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 13 }} title="Delete row">{"✕"}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Mobile Forms ──────────────────────────────────────────────────────────────
  const updateMobileForm = (rowId, updates) => {
    if (!project) return;
    const mobileForms = (project.mobileForms || []).map(r => r.id === rowId ? { ...r, ...updates } : r);
    updateProject({ ...project, mobileForms });
  };

  const addMobileFormRow = (complianceType = "") => {
    if (!project) return;
    const newRow = {
      id: makeId(),
      complianceType,
      formDespatch: "No", formDevType: "", formExampleProvided: "No", formBuilt: "No", formCustomerSignoff: "No",
      outExampleProvided: "No", outBuilt: "No", outCustomerSignoff: "No",
      wfDespatchCreated: "No", wfImportCreated: "No", wfComplete: "No", wfEndToEnd: "No", wfCustomerSignoff: "No",
      goLiveStatus: "With C365",
      jiraKey: "", jiraStatus: "",
    };
    updateProject({ ...project, mobileForms: [...(project.mobileForms || []), newRow] });
  };

  const deleteMobileFormRow = (rowId) => {
    if (!project) return;
    updateProject({ ...project, mobileForms: (project.mobileForms || []).filter(r => r.id !== rowId) });
  };

  const updateMobileFormJira = (rowId) => ({
    id: rowId,
    updateFn: (taskId, updates) => {
      updateProject({
        ...project,
        mobileForms: (project.mobileForms || []).map(r =>
          r.id === rowId ? { ...r, jiraKey: updates.jiraKey || r.jiraKey } : r
        ),
      });
    }
  });

  const renderMobileForms = () => {
    const rows = project.mobileForms || [];

    const mfThead = (
      <thead>
        <tr>
          <th className="left" rowSpan={2} style={{ minWidth: 90 }}>Ticket Ref</th>
          <th className="left" rowSpan={2} style={{ minWidth: 110 }}>Compliance Type</th>
          <th colSpan={6} style={{ borderBottom: "1px solid var(--border)" }}>Form</th>
          <th colSpan={4} style={{ borderBottom: "1px solid var(--border)" }}>Output</th>
          <th colSpan={5} style={{ borderBottom: "1px solid var(--border)" }}>Workflow</th>
          <th rowSpan={2} style={{ minWidth: 80 }}>Go-Live</th>
          <th rowSpan={2} style={{ width: 24 }}></th>
        </tr>
        <tr>
          <th className="wrap" style={{ minWidth: 52 }}>Despatch Needed</th>
          <th className="wrap" style={{ minWidth: 64 }}>Development Type</th>
          <th className="wrap" style={{ minWidth: 44 }}>Example Provided</th>
          <th className="wrap" style={{ minWidth: 44 }}>Form Built</th>
          <th className="wrap" style={{ minWidth: 44 }}>Customer Signoff</th>
          <th className="wrap" style={{ minWidth: 80 }}>Form Status</th>
          <th className="wrap" style={{ minWidth: 44 }}>Example Provided</th>
          <th className="wrap" style={{ minWidth: 44 }}>Output Built</th>
          <th className="wrap" style={{ minWidth: 44 }}>Customer Signoff</th>
          <th className="wrap" style={{ minWidth: 80 }}>Output Status</th>
          <th className="wrap" style={{ minWidth: 44 }}>Despatch Created</th>
          <th className="wrap" style={{ minWidth: 44 }}>Import Created</th>
          <th className="wrap" style={{ minWidth: 44 }}>Workflow Complete</th>
          <th className="wrap" style={{ minWidth: 44 }}>End to End Test</th>
          <th className="wrap" style={{ minWidth: 44 }}>Customer Signoff</th>
        </tr>
      </thead>
    );

    const renderMfRow = (r) => (
      <tr key={r.id}>
        {/* Ticket Ref - jiraKey or + Jira button */}
        <td>
          {r.jiraKey ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span className="ms-jira-key">🔷 {r.jiraKey}</span>
              <JiraStatusBadge status={r.jiraStatus} />
            </div>
          ) : (
            <button className="btn" style={{ fontSize: 9, padding: "2px 7px", color: "#4c9aff", borderColor: "#4c9aff40" }}
              onClick={() => {
                const h = updateMobileFormJira(r.id);
                handleRaiseJira({ id: h.id, name: (r.complianceType || "Mobile Form") + " - Form Build", section: "Mobile Forms", owner: "C365" }, null, null, h.updateFn);
              }}>
              + Jira
            </button>
          )}
        </td>
        {/* Compliance Type */}
        <td><input className="ms-input" value={r.complianceType || ""} onChange={e => updateMobileForm(r.id, { complianceType: e.target.value })} placeholder="e.g. EICR" /></td>
        {/* Form group */}
        <td style={{ textAlign: "center" }}><YNSelect value={r.formDespatch} onChange={v => updateMobileForm(r.id, { formDespatch: v })} /></td>
        <td><input className="ms-input" value={r.formDevType || ""} onChange={e => updateMobileForm(r.id, { formDevType: e.target.value })} placeholder="Existing / New" /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.formExampleProvided} onChange={v => updateMobileForm(r.id, { formExampleProvided: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.formBuilt} onChange={v => updateMobileForm(r.id, { formBuilt: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.formCustomerSignoff} onChange={v => updateMobileForm(r.id, { formCustomerSignoff: v })} /></td>
        {/* Form Status - Jira status */}
        <td style={{ textAlign: "center" }}>
          {r.jiraStatus ? <JiraStatusBadge status={r.jiraStatus} /> : <span style={{ color: "var(--text-dim)", fontSize: 10 }}>-</span>}
        </td>
        {/* Output group */}
        <td style={{ textAlign: "center" }}><YNSelect value={r.outExampleProvided} onChange={v => updateMobileForm(r.id, { outExampleProvided: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.outBuilt} onChange={v => updateMobileForm(r.id, { outBuilt: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.outCustomerSignoff} onChange={v => updateMobileForm(r.id, { outCustomerSignoff: v })} /></td>
        {/* Output Status - also Jira status */}
        <td style={{ textAlign: "center" }}>
          {r.jiraStatus ? <JiraStatusBadge status={r.jiraStatus} /> : <span style={{ color: "var(--text-dim)", fontSize: 10 }}>-</span>}
        </td>
        {/* Workflow group */}
        <td style={{ textAlign: "center" }}><YNSelect value={r.wfDespatchCreated} onChange={v => updateMobileForm(r.id, { wfDespatchCreated: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.wfImportCreated} onChange={v => updateMobileForm(r.id, { wfImportCreated: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.wfComplete} onChange={v => updateMobileForm(r.id, { wfComplete: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.wfEndToEnd} onChange={v => updateMobileForm(r.id, { wfEndToEnd: v })} /></td>
        <td style={{ textAlign: "center" }}><YNSelect value={r.wfCustomerSignoff} onChange={v => updateMobileForm(r.id, { wfCustomerSignoff: v })} /></td>
        {/* Go-Live Status */}
        <td style={{ textAlign: "center" }}>
          <select className="ms-select" value={r.goLiveStatus || "With C365"} onChange={e => updateMobileForm(r.id, { goLiveStatus: e.target.value })}>
            <option value="With C365">With C365</option>
            <option value="With Client">With Client</option>
            <option value="Live">Live</option>
          </select>
        </td>
        {/* Delete */}
        <td style={{ textAlign: "center" }}>
          <button onClick={() => deleteMobileFormRow(r.id)}
            style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: 13 }} title="Delete row">{"✕"}</button>
        </td>
      </tr>
    );

    return (
      <div style={{ padding: "12px 8px", display: "flex", flexDirection: "column", gap: 10, width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn primary" style={{ fontSize: 11 }} onClick={() => addMobileFormRow()}>+ Add Row</button>
        </div>
        {rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-dim)", fontSize: 12, fontStyle: "italic" }}>
            No mobile form entries yet. Click + Add Row to begin.
          </div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
            <table className="ms-table">
              {mfThead}
              <tbody>
                {rows.map(r => renderMfRow(r))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const [projectSearch, setProjectSearch] = React.useState("");
  const filteredProjects = projectSearch.trim()
    ? projects.filter(p => p.clientName.toLowerCase().includes(projectSearch.toLowerCase()) ||
        (p.sdm || "").toLowerCase().includes(projectSearch.toLowerCase()) ||
        (p.pc || "").toLowerCase().includes(projectSearch.toLowerCase()))
    : projects;

  return (
    <div className="impl-layout">
      {/* Project list sidebar */}
      <div className="impl-sidebar">
        <div className="impl-sidebar-title">Projects</div>
        <div style={{ padding: "0 8px 6px" }}>
          <input className="search-box" placeholder="Search projects…" value={projectSearch}
            onChange={e => setProjectSearch(e.target.value)}
            style={{ fontSize: 11, padding: "6px 10px", width: "100%", boxSizing: "border-box" }} />
        </div>
        {filteredProjects.length === 0 && (
          <div style={{ fontSize: 11, color: "var(--text-dim)", padding: "8px 10px", fontStyle: "italic" }}>No projects match</div>
        )}
        {filteredProjects.map(p => (
          <div key={p.id} className={"impl-project-item " + (p.id === selectedProjectId ? "active" : "")}
            onClick={() => { setSelectedProjectId(p.id); setActiveTab("dashboard"); }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div className="impl-project-name">{p.clientName}</div>
              {(() => {
                const rag = getProjectRAG(p, []);
                return (
                  <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 2,
                    background: rag.colour, color: "#fff", flexShrink: 0 }} title={rag.label}>
                    {rag.rag}
                  </span>
                );
              })()}
            </div>
            <div className="impl-project-meta">{calcProgress(p)}% complete</div>
            <div className="impl-progress-bar">
              <div className="impl-progress-fill" style={{ width: calcProgress(p) + "%" }} />
            </div>
          </div>
        ))}
        <div style={{ padding: "8px 8px 0" }}>
          <button className="btn" style={{ width: "100%", fontSize: 11 }} onClick={() => setShowNewProject(true)}>+ New Project</button>
        </div>
      </div>

      {/* Main project area */}
      <div className="impl-main">
        {!project ? (
          <div className="impl-empty">
            <div className="impl-empty-icon">🏗</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No project selected.<br />Create a new project to get started.</div>
            <button className="btn primary" onClick={() => setShowNewProject(true)}>+ New Project</button>
          </div>
        ) : (
          <>
            <div className="impl-header">
              <div className="impl-header-top">
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editingProjectName ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input className="form-input" value={projectNameVal}
                        onChange={e => setProjectNameVal(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && projectNameVal.trim()) {
                            updateProject({ ...project, clientName: projectNameVal.trim() });
                            setEditingProjectName(false);
                          }
                          if (e.key === "Escape") setEditingProjectName(false);
                        }}
                        style={{ fontSize: 16, fontWeight: 700, flex: 1 }} autoFocus />
                      <button className="btn primary" style={{ fontSize: 11 }}
                        onClick={() => { if (projectNameVal.trim()) { updateProject({ ...project, clientName: projectNameVal.trim() }); setEditingProjectName(false); } }}>✓</button>
                      <button className="btn" style={{ fontSize: 11 }} onClick={() => setEditingProjectName(false)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div className="impl-title">{project.clientName}</div>
                      <button className="btn" style={{ fontSize: 9, padding: "1px 7px", color: "var(--text-muted)" }}
                        onClick={() => { setProjectNameVal(project.clientName); setEditingProjectName(true); }}
                        title="Edit project name">✎</button>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                    {project.sdm && <span>SDM: {project.sdm}</span>}
                    {project.pc  && <span>PC: {project.pc}</span>}
                    <button className="btn" style={{ fontSize: 9, padding: "1px 7px", color: "var(--text-muted)" }}
                      onClick={() => { setEditPeople({ sdm: project.sdm || "", pc: project.pc || "", sdmAccountId: project.sdmAccountId || "", pcAccountId: project.pcAccountId || "" }); setEditingProjectPeople(true); }}>✎ Edit SDM/PC</button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {(() => {
                    const rag = getProjectRAG(project, tasks);
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 4,
                          background: rag.colour, color: "#fff" }} title={rag.label}>
                          {rag.rag} - {rag.label}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: progress === 100 ? "#fbbf24" : "var(--green)" }}>
                          {progress === 100 ? "🎉" : ""}{progress}% complete
                        </span>
                      </div>
                    );
                  })()}
                  <button className="btn" style={{ fontSize: 10, padding: "3px 10px" }}
                    title="Save this project as a reusable template"
                    onClick={() => {
                      const name = window.prompt("Template name:", project.clientName + " Template");
                      if (!name) return;
                      saveProjectTemplate(project, name);
                      showToast(`Project template "${name}" saved`);
                    }}>📐 Save Template</button>
                  <button className="btn" style={{ fontSize: 10, padding: "3px 10px" }}
                    title="Generate printable onboarding checklist"
                    onClick={() => generateOnboardingChecklist(project)}>📋 Checklist</button>
                  <button className="btn" style={{ fontSize: 10, padding: "3px 10px" }}
                    onClick={() => {
                      if (!window.confirm(`Duplicate "${project.clientName}"? A copy will be created with all tasks reset to Not Started.`)) return;
                      const now = Date.now();
                      const idMap = {};
                      const remapId = (old) => { if (!idMap[old]) idMap[old] = "t_" + now + "_" + Math.random().toString(36).substring(2,7); return idMap[old]; };
                      const duped = {
                        ...project,
                        id: "proj_" + now,
                        clientName: project.clientName + " (Copy)",
                        createdAt: new Date().toISOString(),
                        createdByAccountId: localStorage.getItem("taskflow_jira_accountid") || "",
                        tasks: project.tasks.map(t => ({ ...t, id: remapId(t.id), status: "Not Started", start: "", finish: "", milestone: "", jiraKey: null })),
                        modules: project.modules.map(m => ({ ...m, tasks: m.tasks.map(t => ({ ...t, id: remapId(t.id), status: "Not Started", start: "", finish: "", milestone: "", jiraKey: null })) })),
                        actionLog: [],
                        moduleSetup: (project.moduleSetup || []).map(r => ({ ...r, id: "ms_" + Math.random().toString(36).substring(2,9), jiraKey: "" })),
                        mobileForms: (project.mobileForms || []).map(r => ({ ...r, id: "mf_" + Math.random().toString(36).substring(2,9), jiraKey: "" })),
                        templates: (project.templates || []).map(r => ({ ...r, id: "tp_" + Math.random().toString(36).substring(2,9), jiraKey: "", supportKey: "" })),
                      };
                      onUpdateProjects([...projects, duped]);
                      setSelectedProjectId(duped.id);
                      showToast(`"${duped.clientName}" created`);
                    }}>⧉ Duplicate</button>
                  <button className="btn danger" style={{ fontSize: 10, padding: "3px 10px" }}
                    onClick={async () => {
                      if (!window.confirm("Delete this project? This cannot be undone.")) return;
                      // Remove from local state immediately
                      onUpdateProjects(projects.filter(p => p.id !== project.id));
                      setSelectedProjectId(null);
                      // Delete from server - tombstones the ID so it never syncs back
                      try {
                        const jiraAccountId = localStorage.getItem("taskflow_jira_accountid") || "";
                        await fetch(`${PROJECTS_API}/${project.id}`, {
                          method: "DELETE",
                          headers: { "Content-Type": "application/json", "X-User-AccountId": jiraAccountId },
                        });
                      } catch (e) {
                        console.warn("[Projects] server delete failed:", e.message);
                      }
                      // Also remove from localStorage cache so it can't be re-merged on next load
                      try {
                        const cached = JSON.parse(localStorage.getItem("taskflow_projects") || "[]");
                        localStorage.setItem("taskflow_projects", JSON.stringify(cached.filter(p => p.id !== project.id)));
                      } catch {}
                    }}>
                    Delete
                  </button>
                </div>
              </div>
              <div className="impl-tabs">
                <div className="impl-tabs-left">
                  {["dashboard","plan","kanban","gantt","actionlog","riskregister","modulesetup","mobileforms","templates"].map(tab => (
                    <div key={tab} className={"impl-tab " + (activeTab === tab ? "active" : "")}
                      onClick={() => setActiveTab(tab)}>
                      {tab === "dashboard" ? "📊 Dashboard" : tab === "plan" ? "📋 Plan" : tab === "kanban" ? "⊞ Kanban" : tab === "gantt" ? "📅 Gantt" : tab === "actionlog" ? "📋 Action Log" : tab === "riskregister" ? "⚠ Risk Register" : tab === "modulesetup" ? "⚙ Module Setup" : tab === "mobileforms" ? "📱 Mobile Forms" : "📄 Templates"}
                    </div>
                  ))}
                </div>
                {activeTab === "plan" && (
                  <div className="impl-tabs-right">
                    <button className="btn" style={{ fontSize: 10, padding: "3px 10px" }} onClick={handleDownloadExcel}>⬇ Excel Plan</button>
                    <button className="btn" style={{ fontSize: 10, padding: "3px 10px" }} onClick={() => setShowAddModule(true)}>+ Add Module</button>
                  </div>
                )}
              </div>
            </div>
            <div className="impl-body">
              {activeTab === "dashboard"    && renderDashboard()}
              {activeTab === "plan"         && renderPlan()}
              {activeTab === "kanban"       && renderKanban()}
              {activeTab === "gantt"        && renderGantt()}
              {activeTab === "actionlog"    && renderActionLog()}
              {activeTab === "riskregister" && renderRiskRegister()}
              {activeTab === "modulesetup"  && renderModuleSetup()}
              {activeTab === "mobileforms"  && renderMobileForms()}
              {activeTab === "templates"    && renderTemplates()}
            </div>
          </>
        )}
      </div>

      {/* Edit SDM/PC Modal */}
      {editingProjectPeople && project && (() => {
        // UserPicker2 - fixed controlled input (no revert bug) + filtered to SUPPORT project assignable users only
        const UserPicker2 = ({ label, fieldKey }) => {
          // inputVal drives the text box independently from editPeople[fieldKey]
          // This prevents the revert-on-last-letter bug caused by value={q || fallback}
          const [inputVal, setInputVal] = React.useState(editPeople[fieldKey] || "");
          const [results, setResults] = React.useState([]);
          const [searching, setSearching] = React.useState(false);
          const selected = editPeople[fieldKey + "AccountId"] ? editPeople[fieldKey] : null;

          const search = async (val) => {
            setInputVal(val);
            if (val.length < 2) { setResults([]); return; }
            setSearching(true);
            try {
              const { jiraUrl, jiraEmail, jiraToken } = config;
              const auth = btoa(`${jiraEmail}:${jiraToken}`);
              const base = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
              // Search users assignable to the SUPPORT project - this is the correct
              // scope for SDM/PC fields which are restricted to internal C365 staff.
              // /user/search is avoided as it returns all users including clients/bots.
              const r = await fetch(
                proxyUrl(`${base}/rest/api/3/user/assignable/search?project=SUPPORT&query=${encodeURIComponent(val)}&maxResults=10`),
                { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
              );
              if (r.ok) {
                const d = await r.json();
                setResults((Array.isArray(d) ? d : []).filter(u => u.accountId && u.displayName && u.accountType !== "app"));
              }
            } catch {}
            setSearching(false);
          };

          return (
            <div className="form-row">
              <label className="form-label">{label}</label>
              <div style={{ position: "relative" }}>
                {selected ? (
                  // Confirmed selection - show chip with ✕ to clear
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--surface2)", border: "1px solid var(--green)", borderRadius: 4 }}>
                    <span style={{ fontSize: 12, color: "var(--green)", flex: 1 }}>✓ {selected}</span>
                    <button style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14 }}
                      onClick={() => {
                        setEditPeople(p => ({ ...p, [fieldKey]: "", [fieldKey + "AccountId"]: "" }));
                        setInputVal("");
                        setResults([]);
                      }}>{"✕"}</button>
                  </div>
                ) : (
                  // Search input - fully controlled by inputVal, never falls back to editPeople
                  <>
                    <input className="form-input"
                      placeholder={`Search ${label}…`}
                      value={inputVal}
                      onChange={e => search(e.target.value)}
                      onBlur={() => setTimeout(() => setResults([]), 200)} />
                    {searching && <div style={{ fontSize: 10, color: "var(--text-muted)", padding: "2px 0" }}>Searching…</div>}
                    {results.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, zIndex: 999, maxHeight: 200, overflowY: "auto" }}>
                        {results.map(u => (
                          <div key={u.accountId}
                            style={{ padding: "7px 10px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid var(--border)30" }}
                            onMouseDown={() => {
                              setEditPeople(p => ({ ...p, [fieldKey]: u.displayName, [fieldKey + "AccountId"]: u.accountId }));
                              setInputVal(u.displayName);
                              setResults([]);
                            }}>
                            {u.displayName}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        };
        return (
          <div className="overlay" onClick={e => e.target === e.currentTarget && setEditingProjectPeople(false)}>
            <div className="modal" style={{ maxWidth: 440 }}>
              <div className="modal-title">✎ Edit Project People</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: -8 }}>{project.clientName}</div>
              <div className="modal-section">
                <UserPicker2 label="SDM" fieldKey="sdm" />
                <UserPicker2 label="Project Coordinator" fieldKey="pc" />
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => { setEditingProjectPeople(false); setEditPeople({ sdm: "", pc: "", sdmAccountId: "", pcAccountId: "" }); }}>Cancel</button>
                <button className="btn primary" onClick={() => {
                  const updates = {};
                  if (editPeople.sdm && editPeople.sdmAccountId) { updates.sdm = editPeople.sdm; updates.sdmAccountId = editPeople.sdmAccountId; }
                  if (editPeople.pc  && editPeople.pcAccountId)  { updates.pc  = editPeople.pc;  updates.pcAccountId  = editPeople.pcAccountId; }
                  if (Object.keys(updates).length > 0) {
                    updateProject({ ...project, ...updates });
                    showToast("Project people updated");
                  }
                  setEditingProjectPeople(false);
                  setEditPeople({ sdm: "", pc: "", sdmAccountId: "", pcAccountId: "" });
                }}>Save</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* New Project Modal */}
      {showNewProject && (() => {
        const UserPicker = ({ label, fieldKey }) => {
          const [q, setQ] = React.useState("");
          const [results, setResults] = React.useState([]);
          const [searching, setSearching] = React.useState(false);
          const selected = newProject[fieldKey] || "";
          const search = async (v) => {
            setQ(v);
            if (v.length < 2) { setResults([]); return; }
            setSearching(true);
            try { const r = await searchJiraUsers(config, v); setResults(r.slice(0, 6)); } catch { setResults([]); }
            setSearching(false);
          };
          return (
            <div className="form-row">
              <label className="form-label">{label}</label>
              <div style={{ position: "relative" }}>
                {selected ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--surface2)", border: "1px solid var(--green)", borderRadius: 4 }}>
                    <span style={{ fontSize: 12, color: "var(--green)", flex: 1 }}>✓ {selected}</span>
                    <button style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14 }}
                      onClick={() => {
                        const accountIdField = fieldKey === "sdm" ? "sdmAccountId" : "pcAccountId";
                        setNewProject(f => ({ ...f, [fieldKey]: "", [accountIdField]: "" }));
                        setQ("");
                      }}>{"✕"}</button>
                  </div>
                ) : (
                  <>
                    <input className="form-input" placeholder={"Type to search " + label + "…"}
                      value={q}
                      onChange={e => search(e.target.value)}
                      onBlur={() => setTimeout(() => setResults([]), 200)} />
                    {searching && <div style={{ fontSize: 11, color: "var(--text-muted)", padding: "4px 0" }}>Searching…</div>}
                    {results.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, zIndex: 200, maxHeight: 160, overflowY: "auto" }}>
                        {results.map(u => (
                          <div key={u.accountId} style={{ padding: "6px 10px", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}
                            onMouseDown={() => {
                              const accountIdField = fieldKey === "sdm" ? "sdmAccountId" : "pcAccountId";
                              setNewProject(f => ({ ...f, [fieldKey]: u.displayName, [accountIdField]: u.accountId || "" }));
                              setQ(""); setResults([]);
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}>
                            {u.avatarUrls?.["24x24"] && <img src={u.avatarUrls["24x24"]} style={{ width: 20, height: 20, borderRadius: "50%" }} alt="" />}
                            <span>{u.displayName}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        };
        return (
          <div className="overlay" onClick={e => e.target === e.currentTarget && setShowNewProject(false)}>
            <div className="modal">
              <div className="modal-title">🏗 New Implementation Project</div>
              <div className="modal-section">
                <div className="form-row">
                  <label className="form-label">Client Name *</label>
                  <input className="form-input" placeholder="Client name…" autoFocus
                    value={newProject.clientName} onChange={e => setNewProject(f => ({ ...f, clientName: e.target.value }))} />
                </div>
                <UserPicker label="SDM" fieldKey="sdm" />
                <UserPicker label="Project Coordinator" fieldKey="pc" />
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setShowNewProject(false)}>Cancel</button>
                <button className="btn primary" onClick={handleAddProject}
                  disabled={!newProject.clientName.trim()}>Create Project</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Module Modal */}
      {showAddModule && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowAddModule(false)}>
          <div className="modal">
            <div className="modal-title">+ Add Compliance Module</div>
            <div className="modal-section">
              <div className="form-row">
                <label className="form-label">Module Name *</label>
                <input className="form-input" placeholder="e.g. Gas, Electrical, Fire…" autoFocus
                  value={newModuleName} onChange={e => setNewModuleName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleAddModule()} />
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                This will create the standard 10 compliance module tasks for {newModuleName || "this module"}.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => { setShowAddModule(false); setNewModuleName(""); }}>Cancel</button>
              <button className="btn primary" onClick={handleAddModule}
                disabled={!newModuleName.trim()}>Add Module</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
const KANBAN_COLS = [
  { id: "To Start", label: "To Start", cls: "kanban-col-to-start" },
  { id: "In Progress", label: "In Progress", cls: "kanban-col-in-progress" },
  { id: "Waiting Response", label: "Waiting Response", cls: "kanban-col-waiting" },
  { id: "Done", label: "Done", cls: "kanban-col-done" },
];

function KanbanBoard({ tasks, selected, onSelect, onToggle, onStatusChange }) {
  const [expandedIds, setExpandedIds] = React.useState({});
  const toggleExpand = (id, e) => { if (e) e.stopPropagation(); setExpandedIds(prev => ({ ...prev, [id]: !prev[id] })); };
  const SUB_COLOUR = { "To Start": "#4a6070", "In Progress": "#F1901E", "Waiting Response": "#fbbf24", "Done": "#47ac4c" };
  return (
    <div className="kanban-board">
      {KANBAN_COLS.map(col => {
        const colTasks = tasks.filter(t => {
          if (col.id === "Done") return t.done;
          return !t.done && (t.status === col.id || (!t.status && col.id === "To Start"));
        });
        return (
          <div key={col.id} className={`kanban-col ${col.cls}`}>
            <div className="kanban-col-header">
              {col.label}
              <span className="col-count">{colTasks.length}</span>
            </div>
            <div className="kanban-cards">
              {colTasks.length === 0 && (
                <div style={{ fontSize: 11, color: "var(--text-dim)", padding: "8px 4px", textAlign: "center", fontStyle: "italic" }}>Empty</div>
              )}
              {colTasks.map(t => (
                <div key={t.id} className={`kanban-card ${selected === t.id ? "selected" : ""}`} onClick={() => onSelect(t.id)}>
                  <div className="kanban-card-title">{t.title}</div>
                  <div className="kanban-card-meta">
                    <span className={`tag priority-${t.priority}`}>{capitalise(t.priority)}</span>
                    {(t.tags||[]).map(tag => {
                      const c = getTagColour(tag);
                      return <span key={tag} className="tag-chip" style={{ color: c.color, borderColor: c.border, background: c.bg, fontSize: 9, padding: "1px 6px" }}>{tag}</span>;
                    })}
                    {t.due && <span className="tag due-tag" style={{ fontSize: 9 }}>📅 {formatDate(t.due)}</span>}
                    {(t.subtasks||[]).length > 0 && (
                      <span className="tag status" style={{ fontSize: 9, cursor: "pointer", color: "#2E75B6" }} onClick={(e) => toggleExpand(t.id, e)} title={expandedIds[t.id] ? "Hide subtasks" : "Show subtasks"}>
                        {expandedIds[t.id] ? "▼" : "▶"} {(t.subtasks||[]).filter(s=>s.done).length}/{(t.subtasks||[]).length} ✓
                      </span>
                    )}
                  </div>
                  {expandedIds[t.id] && (t.subtasks||[]).length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3, borderTop: "1px solid var(--border)", paddingTop: 6 }}>
                      {(t.subtasks||[]).map(s => {
                        const sc = SUB_COLOUR[s.done ? "Done" : (s.status || "To Start")] || "#4a6070";
                        return (
                          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10 }}>
                            <span style={{ color: sc, flexShrink: 0 }}>{s.done ? "☑" : "☐"}</span>
                            <span style={{ flex: 1, minWidth: 0, textDecoration: s.done ? "line-through" : "none", color: s.done ? "var(--text-dim)" : "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.text}>{s.text}</span>
                            {s.assignedTo && <span style={{ fontSize: 8, color: "#47ac4c", flexShrink: 0 }}>👤 {s.assignedTo.split(" ")[0]}</span>}
                            {s.due && <span style={{ fontSize: 8, color: "var(--text-dim)", flexShrink: 0 }}>📅 {formatDate(s.due)}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {col.id !== "Done" && (
                    <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {KANBAN_COLS.filter(c => c.id !== col.id && c.id !== "Done").map(c => (
                        <button key={c.id} className="btn" style={{ fontSize: 9, padding: "2px 6px", flex: 1 }}
                          onClick={(e) => { e.stopPropagation(); onStatusChange(t, c.id); }}>
                          → {c.label === "Waiting Response" ? "Waiting" : c.label}
                        </button>
                      ))}
                      <button className="btn primary" style={{ fontSize: 9, padding: "2px 6px" }}
                        onClick={(e) => { e.stopPropagation(); onToggle(t); }}>✓</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ClientView({ tasks, selected, onSelect, onToggle, onToggleSubtask }) {
  const [expandedStatuses, setExpandedStatuses] = useState({});

  // Group by client, then by actual Jira status within each client
  const groups = {};
  tasks.forEach(t => {
    const client = t.affectedClient || t.jiraKey?.split("-")[0] || "Unknown";
    if (!groups[client]) groups[client] = {};
    const status = t.jiraStatus || t.status || "Unknown";
    if (!groups[client][status]) groups[client][status] = [];
    groups[client][status].push(t);
  });
  const sortedClients = Object.keys(groups).sort();

  const toggleStatus = (key) => setExpandedStatuses(v => ({ ...v, [key]: !v[key] }));

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        <div className="icon">🏢</div>
        No active Jira tasks found.<br />
        Tasks appear here grouped by client once synced.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {sortedClients.map(client => {
        const statuses = Object.keys(groups[client]).sort();
        const totalTasks = statuses.reduce((n, s) => n + groups[client][s].length, 0);
        return (
          <div key={client}>
            <div style={{
              fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase",
              color: "var(--text)", padding: "8px 0 10px", borderBottom: "2px solid var(--green)40",
              marginBottom: 10, display: "flex", alignItems: "center", gap: 8
            }}>
              🏢 {client}
              <span style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10, padding: "1px 8px", fontSize: 10, color: "var(--text-muted)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                {totalTasks} task{totalTasks !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {statuses.map(status => {
                const statusKey = `${client}||${status}`;
                const isCollapsed = expandedStatuses[statusKey] === false;
                const statusTasks = groups[client][status];
                return (
                  <div key={status}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", background: "var(--surface2)", borderRadius: 4, cursor: "pointer", marginBottom: 6, border: "1px solid var(--border)" }}
                      onClick={() => toggleStatus(statusKey)}
                    >
                      <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{isCollapsed ? "▶" : "▼"}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, flex: 1 }}>{status}</span>
                      <span style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "1px 7px", fontSize: 10, color: "var(--text-muted)" }}>
                        {statusTasks.length}
                      </span>
                    </div>
                    {!isCollapsed && (
                      <div className="task-list" style={{ paddingLeft: 8 }}>
                        {statusTasks.map(t => (
                          <TaskCard key={t.id} task={t} selected={selected === t.id}
                            onClick={() => onSelect(t.id)}
                            onToggle={() => onToggle(t)}
                            onToggleSubtask={onToggleSubtask} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function generateWeeklyDigest(tasks, projects, activityLog) {
  const today = new Date();
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
  const fmtDate = d => new Date(d).toLocaleDateString("en-GB");
  const weekStr = `${fmtDate(weekAgo)} – ${fmtDate(today)}`;

  const completed = tasks.filter(t => t.done);
  const overdue   = tasks.filter(t => !t.done && t.due && t.due < today.toISOString().split("T")[0]);
  const dueThis   = tasks.filter(t => !t.done && t.due && t.due >= today.toISOString().split("T")[0] && t.due <= new Date(today.getTime() + 7*24*60*60*1000).toISOString().split("T")[0]);
  const jiraProgressed = tasks.filter(t => t.source === "jira" && !t.done && t.jiraUpdated && new Date(t.jiraUpdated) >= weekAgo);
  const recentActivity = (activityLog || []).filter(e => {
    try { return new Date(e.ts.split(", ")[0].split("/").reverse().join("-")) >= weekAgo; } catch { return false; }
  }).slice(0, 20);
  const myProjects = projects || [];

  const row = (label, val, colour) => `<tr><td style="padding:6px 12px;border-bottom:1px solid #e0e0e0;font-weight:600;width:180px;color:#555">${label}</td><td style="padding:6px 12px;border-bottom:1px solid #e0e0e0;color:${colour||"#222"};font-weight:700;font-size:18px">${val}</td></tr>`;
  const taskRow = t => `<tr style="border-bottom:1px solid #f0f0f0"><td style="padding:5px 12px;font-size:12px;color:#222;max-width:300px">${t.title.substring(0,80)}</td><td style="padding:5px 12px;font-size:11px;color:#888">${t.source}</td><td style="padding:5px 12px;font-size:11px;color:${t.due && t.due < today.toISOString().split("T")[0] ? "#e32529" : "#555"}">${t.due ? fmtDate(t.due) : "-"}</td></tr>`;
  const actRow = e => `<tr style="border-bottom:1px solid #f5f5f5"><td style="padding:4px 12px;font-size:11px;color:#888;white-space:nowrap">${e.ts}</td><td style="padding:4px 12px;font-size:11px;font-weight:600;color:${e.action==="Completed"?"#47ac4c":e.action==="Created"?"#4c9aff":e.action==="Note added"?"#a78bfa":"#555"}">${e.action}</td><td style="padding:4px 12px;font-size:11px;color:#222;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${e.title}</td><td style="padding:4px 12px;font-size:10px;color:#999;max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${e.detail||""}</td></tr>`;

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Weekly Digest ${weekStr}</title>
  <style>body{font-family:Arial,sans-serif;color:#222;max-width:900px;margin:0 auto;padding:24px}
  h1{color:#1e2b33;border-bottom:3px solid #47ac4c;padding-bottom:8px}h2{color:#1e2b33;margin-top:24px;font-size:14px;text-transform:uppercase;letter-spacing:0.5px;border-left:4px solid #47ac4c;padding-left:8px}
  table{width:100%;border-collapse:collapse;margin-bottom:16px}th{background:#1e2b33;color:#fff;padding:6px 12px;text-align:left;font-size:11px}
  @media print{@page{size:A4;margin:15mm}}</style></head><body>
  <div style="display:flex;align-items:center;gap:16px;margin-bottom:8px">
    <div style="background:#47ac4c;color:#fff;padding:4px 12px;border-radius:4px;font-weight:700;font-size:12px">C365CLOUD</div>
    <div style="color:#888;font-size:12px">CONFIDENTIAL - INTERNAL USE ONLY</div>
  </div>
  <h1>📋 Weekly Task Digest</h1>
  <p style="color:#555;font-size:13px">Period: <strong>${weekStr}</strong> &nbsp;|&nbsp; Generated: <strong>${fmtDate(today)}</strong></p>
  <h2>Summary</h2>
  <table><tbody>
    ${row("Tasks Completed",completed.length,"#47ac4c")}
    ${row("Currently Overdue",overdue.length,"#e32529")}
    ${row("Due This Week",dueThis.length,"#F1901E")}
    ${row("Jira Progressed",jiraProgressed.length,"#4c9aff")}
    ${row("Active Projects",myProjects.length,"#1e2b33")}
  </tbody></table>
  ${completed.length > 0 ? `<h2>✅ Completed Tasks (${completed.length})</h2><table><thead><tr><th>Task</th><th>Source</th><th>Due</th></tr></thead><tbody>${completed.map(taskRow).join("")}</tbody></table>` : ""}
  ${overdue.length > 0 ? `<h2>⚠️ Overdue Tasks (${overdue.length})</h2><table><thead><tr><th>Task</th><th>Source</th><th>Due</th></tr></thead><tbody>${overdue.map(taskRow).join("")}</tbody></table>` : ""}
  ${dueThis.length > 0 ? `<h2>📅 Due This Week (${dueThis.length})</h2><table><thead><tr><th>Task</th><th>Source</th><th>Due</th></tr></thead><tbody>${dueThis.map(taskRow).join("")}</tbody></table>` : ""}
  ${recentActivity.length > 0 ? `<h2>📋 Activity Log</h2><table><thead><tr><th>Time</th><th>Action</th><th>Task</th><th>Detail</th></tr></thead><tbody>${recentActivity.map(actRow).join("")}</tbody></table>` : ""}
  <p style="margin-top:32px;font-size:10px;color:#aaa;text-align:center">C365Cloud Task Manager - Weekly Digest - ${fmtDate(today)}</p>
  <script>window.print();</script></body></html>`;

  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}



// ─── Client Dashboard View ───────────────────────────────────────────────────
function ClientDashboard({ tasks, allJiraTasks, managerMode, onSelectTask, onNavigate, config, jiraConnected, selectedTask }) {
  const [clientFilter, setClientFilter] = React.useState("");
  const [sdmFilter, setSdmFilter] = React.useState("");
  const [sortBy, setSortBy] = React.useState("client"); // client | overdue | noReply | total
  const [expandedClient, setExpandedClient] = React.useState(null);
  const [loadingAll, setLoadingAll] = React.useState(false);
  const [managerTasks, setManagerTasks] = React.useState([]);

  const today = new Date().toISOString().split("T")[0];

  // In manager mode fetch all SUPPORT tickets, otherwise use passed tasks
  const sourceTasks = managerMode && managerTasks.length > 0 ? managerTasks : tasks;

  // Only Jira tasks for client view
  const jiraTasks = sourceTasks.filter(t => t.source === "jira" && !t.done);

  // Fetch all SUPPORT tickets for manager mode
  const fetchAllForManager = React.useCallback(async () => {
    if (!jiraConnected || !config) return;
    setLoadingAll(true);
    try {
      const { jiraUrl, jiraEmail, jiraToken } = config;
      const auth = btoa(`${jiraEmail}:${jiraToken}`);
      const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
      const jql = "project = SUPPORT AND statusCategory != Done ORDER BY updated DESC";
      const fields = "summary,status,priority,assignee,customfield_10208,customfield_10211,customfield_10435,updated,duedate";
      const res = await fetch(
        proxyUrl(`${baseUrl}/rest/api/3/search/jql?jql=${encodeURIComponent(jql)}&fields=${fields}&maxResults=200`),
        { headers: { Authorization: `Basic ${auth}`, Accept: "application/json" } }
      );
      if (res.ok) {
        const data = await res.json();
        const mapped = (data.issues || []).map(i => ({
          id: "mgr-" + i.id,
          title: "[" + i.key + "] " + i.fields.summary,
          source: "jira",
          jiraKey: i.key,
          jiraStatus: i.fields.status?.name || "",
          jiraUrl: `${baseUrl}/browse/${i.key}`,
          jiraUpdated: i.fields.updated || null,
          due: i.fields.duedate || null,
          priority: (i.fields.priority?.name || "Medium").toLowerCase(),
          assignee: i.fields.assignee?.displayName || null,
          sdm: i.fields.customfield_10208?.displayName || null,
          pc: i.fields.customfield_10211?.displayName || null,
          affectedClient: (() => {
            const v = i.fields.customfield_10435;
            if (!v) return null;
            if (Array.isArray(v)) return v.map(x => typeof x === "object" ? (x.value ?? x.name) : x).join(", ");
            if (typeof v === "object") return v.value ?? v.name ?? null;
            return String(v);
          })(),
          done: false,
        }));
        setManagerTasks(mapped);
      }
    } catch(e) { console.warn("Manager fetch error:", e.message); }
    setLoadingAll(false);
  }, [config, jiraConnected]);

  React.useEffect(() => {
    if (managerMode) fetchAllForManager();
    else setManagerTasks([]);
  }, [managerMode]);

  // Group by client
  const clientMap = {};
  jiraTasks.forEach(t => {
    const client = t.affectedClient || (t.jiraKey ? t.jiraKey.split("-")[0] : "Unknown");
    if (!clientMap[client]) clientMap[client] = {
      tasks: [], overdue: 0, noReply: 0, slaBreach: 0,
      sdms: new Set(), pcs: new Set(), statuses: {}
    };
    clientMap[client].tasks.push(t);
    if (t.due && t.due < today) clientMap[client].overdue++;
    if (isNoReply(t)) clientMap[client].noReply++;
    const sla = getSLAInfo(t);
    if (sla?.breached) clientMap[client].slaBreach++;
    if (t.sdm) clientMap[client].sdms.add(t.sdm);
    if (t.pc) clientMap[client].pcs.add(t.pc);
    const s = t.jiraStatus || "Unknown";
    clientMap[client].statuses[s] = (clientMap[client].statuses[s] || 0) + 1;
  });

  // Get all SDMs and PCs for filters
  const allSdms = [...new Set(jiraTasks.map(t => t.sdm).filter(Boolean))].sort();
  const allPcs  = [...new Set(jiraTasks.map(t => t.pc).filter(Boolean))].sort();

  // Filter and sort
  let clients = Object.entries(clientMap)
    .filter(([name]) => !clientFilter || name.toLowerCase().includes(clientFilter.toLowerCase()))
    .filter(([, d]) => !sdmFilter || [...d.sdms].some(s => s === sdmFilter) || [...d.pcs].some(p => p === sdmFilter));

  clients.sort((a, b) => {
    if (sortBy === "overdue")  return b[1].overdue - a[1].overdue;
    if (sortBy === "noReply")  return b[1].noReply - a[1].noReply;
    if (sortBy === "total")    return b[1].tasks.length - a[1].tasks.length;
    if (sortBy === "sla")      return b[1].slaBreach - a[1].slaBreach;
    return a[0].localeCompare(b[0]);
  });

  // Manager summary tables
  const sdmSummary = {};
  const pcSummary  = {};
  if (managerMode) {
    jiraTasks.forEach(t => {
      [["sdm", t.sdm, sdmSummary], ["pc", t.pc, pcSummary]].forEach(([role, name, map]) => {
        if (!name) return;
        if (!map[name]) map[name] = { total: 0, overdue: 0, noReply: 0, slaBreach: 0 };
        map[name].total++;
        if (t.due && t.due < today) map[name].overdue++;
        if (isNoReply(t)) map[name].noReply++;
        const sla = getSLAInfo(t);
        if (sla?.breached) map[name].slaBreach++;
      });
    });
  }

  const StatPill = ({ value, colour, label }) => value > 0 ? (
    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10,
      background: colour + "20", color: colour, border: `1px solid ${colour}50`,
      whiteSpace: "nowrap" }} title={label}>
      {value} {label}
    </span>
  ) : null;

  const totalOpen    = jiraTasks.length;
  const totalOverdue = jiraTasks.filter(t => t.due && t.due < today).length;
  const totalNoReply = jiraTasks.filter(t => isNoReply(t)).length;
  const totalSla     = jiraTasks.filter(t => getSLAInfo(t)?.breached).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
          {managerMode ? "👔 Manager View - All Clients" : "🏢 My Clients"}
          {loadingAll && <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 10 }}>Loading…</span>}
        </div>
        {managerMode && (
          <button className="btn" style={{ fontSize: 11 }} onClick={fetchAllForManager} disabled={loadingAll}>↻ Refresh</button>
        )}
      </div>

      {/* Summary stat tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        {[
          { label: "Open Tickets",  value: totalOpen,    colour: "#4c9aff" },
          { label: "Overdue",       value: totalOverdue, colour: "#e32529" },
          { label: "No Reply 2d+",  value: totalNoReply, colour: "#F1901E" },
          { label: "SLA Breached",  value: totalSla,     colour: "#e32529" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--surface)", border: `1px solid ${s.colour}30`,
            borderTop: `3px solid ${s.colour}`, borderRadius: 6, padding: "10px 12px", textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.colour, fontFamily: "Poppins, sans-serif" }}>{s.value}</div>
            <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Manager SDM/PC breakdown tables */}
      {managerMode && Object.keys(sdmSummary).length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[["SDM Breakdown", sdmSummary], ["PC Breakdown", pcSummary]].map(([title, map]) => (
            <div key={title} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
              <div style={{ background: "var(--surface2)", padding: "6px 12px", fontSize: 10, fontWeight: 700,
                color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>{title}</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--surface2)" }}>
                    {["Name","Open","Overdue","No Reply","SLA"].map(h => (
                      <th key={h} style={{ padding: "4px 10px", fontSize: 9, color: "var(--text-muted)",
                        textAlign: h === "Name" ? "left" : "center", fontWeight: 600, textTransform: "uppercase",
                        letterSpacing: 0.5, borderBottom: "1px solid var(--border)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(map).sort((a,b) => b[1].total - a[1].total).map(([name, d], i) => (
                    <tr key={name} style={{ background: i % 2 === 0 ? "transparent" : "var(--surface2)" }}
                      onClick={() => setSdmFilter(sdmFilter === name ? "" : name)}
                      style={{ cursor: "pointer", background: sdmFilter === name ? "var(--green)15" : i % 2 === 0 ? "transparent" : "var(--surface2)" }}>
                      <td style={{ padding: "5px 10px", fontSize: 11, color: "var(--text)", fontWeight: sdmFilter === name ? 700 : 400 }}>{name}</td>
                      <td style={{ padding: "5px 10px", fontSize: 11, textAlign: "center", color: "#4c9aff", fontWeight: 600 }}>{d.total}</td>
                      <td style={{ padding: "5px 10px", fontSize: 11, textAlign: "center", color: d.overdue > 0 ? "#e32529" : "var(--text-muted)", fontWeight: d.overdue > 0 ? 700 : 400 }}>{d.overdue || "-"}</td>
                      <td style={{ padding: "5px 10px", fontSize: 11, textAlign: "center", color: d.noReply > 0 ? "#F1901E" : "var(--text-muted)", fontWeight: d.noReply > 0 ? 700 : 400 }}>{d.noReply || "-"}</td>
                      <td style={{ padding: "5px 10px", fontSize: 11, textAlign: "center", color: d.slaBreach > 0 ? "#e32529" : "var(--text-muted)", fontWeight: d.slaBreach > 0 ? 700 : 400 }}>{d.slaBreach || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Filter / sort bar */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input className="search-box" placeholder="Filter by client…" value={clientFilter}
          onChange={e => setClientFilter(e.target.value)}
          style={{ flex: "1 1 160px", maxWidth: 220, fontSize: 11, padding: "6px 10px" }} />
        <select className="form-select" value={sdmFilter} onChange={e => setSdmFilter(e.target.value)}
          style={{ fontSize: 11, flex: "1 1 140px", maxWidth: 180 }}>
          <option value="">All SDMs / PCs</option>
          {[...new Set([...allSdms, ...allPcs])].sort().map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <div style={{ display: "flex", gap: 4 }}>
          {[["client","A–Z"],["total","Most tickets"],["overdue","Overdue"],["noReply","No reply"],["sla","SLA"]].map(([id, label]) => (
            <button key={id} className={`btn ${sortBy === id ? "primary" : ""}`}
              style={{ fontSize: 9, padding: "4px 8px" }}
              onClick={() => setSortBy(id)}>{label}</button>
          ))}
        </div>
        <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{clients.length} client{clients.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Client cards grid */}
      {clients.length === 0 ? (
        <div className="empty-state"><div className="icon">🏢</div>No clients match your filter.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {clients.map(([client, d]) => {
            const isExpanded = expandedClient === client;
            const hasIssues = d.overdue > 0 || d.noReply > 0 || d.slaBreach > 0;
            const cardBorder = d.slaBreach > 0 ? "#e32529" : d.overdue > 0 ? "#e32529" : d.noReply > 0 ? "#F1901E" : "var(--border)";
            const hasSelection = d.tasks.some(t => t.id === selectedTask?.id);
            return (
              <div key={client} style={{
                background: hasSelection ? "rgba(71,172,76,0.08)" : "var(--surface)",
                border: `1px solid ${hasSelection ? "var(--green)" : cardBorder}`,
                borderTop: `3px solid ${hasSelection ? "var(--green)" : cardBorder}`,
                borderRadius: 6, overflow: "hidden", transition: "background 0.2s, border-color 0.2s, box-shadow 0.15s",
                boxShadow: hasSelection ? "0 0 0 1px var(--green)40" : "none",
              }}>
                {/* Card header */}
                <div style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
                  onClick={() => setExpandedClient(isExpanded ? null : client)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: hasSelection ? "var(--green)" : "var(--text)", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={client}>
                      🏢 {client}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                      <span style={{ fontSize: 9, color: "#4c9aff", fontWeight: 700 }}>{d.tasks.length} open</span>
                      <StatPill value={d.overdue}   colour="#e32529" label="overdue" />
                      <StatPill value={d.noReply}   colour="#F1901E" label="no reply" />
                      <StatPill value={d.slaBreach} colour="#e32529" label="SLA breach" />
                    </div>
                  </div>
                  <span style={{ color: "var(--text-dim)", fontSize: 12, flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</span>
                </div>

                {/* SDM / PC line */}
                {([...d.sdms].length > 0 || [...d.pcs].length > 0) && (
                  <div style={{ padding: "0 14px 8px", fontSize: 10, color: "var(--text-muted)", display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {[...d.sdms].map(s => <span key={s}>👤 SDM: {s}</span>)}
                    {[...d.pcs].map(p => <span key={p}>👤 PC: {p}</span>)}
                  </div>
                )}

                {/* Status breakdown bar */}
                <div style={{ padding: "0 14px 10px", display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {Object.entries(d.statuses).map(([status, count]) => (
                    <span key={status} style={{ fontSize: 8, fontWeight: 600, padding: "1px 5px",
                      borderRadius: 3, background: "var(--surface2)", color: "var(--text-muted)",
                      border: "1px solid var(--border)" }}>{status} ({count})</span>
                  ))}
                </div>

                {/* Expanded ticket list */}
                {isExpanded && (
                  <div style={{ borderTop: "1px solid var(--border)", maxHeight: 280, overflowY: "auto" }}>
                    {d.tasks.map(t => {
                      const isSelected = t.id === selectedTask?.id;
                      return (
                        <div key={t.id} onClick={() => onSelectTask(t.id)}
                          style={{
                            padding: "7px 14px",
                            borderBottom: "1px solid var(--border)20",
                            cursor: "pointer",
                            display: "flex", alignItems: "flex-start", gap: 8,
                            background: isSelected ? "rgba(71,172,76,0.18)" : "transparent",
                            borderLeft: isSelected ? "3px solid var(--green)" : "3px solid transparent",
                            transition: "background 0.15s",
                          }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: isSelected ? "var(--green)" : "var(--text)", overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: isSelected ? 700 : 500 }}>
                              {t.title}
                            </div>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
                              <span style={{ fontSize: 9, color: "var(--text-muted)" }}>{t.jiraStatus}</span>
                              {t.sdm && <span style={{ fontSize: 9, color: "var(--text-muted)" }}>· SDM: {t.sdm}</span>}
                              {t.due && <span style={{ fontSize: 9, color: t.due < today ? "#e32529" : "var(--text-muted)" }}>· 📅 {formatDate(t.due)}</span>}
                              {isNoReply(t) && <span style={{ fontSize: 9, color: "#F1901E", fontWeight: 700 }}>· 💬 No reply</span>}
                            </div>
                          </div>
                          {getSLAInfo(t)?.breached && <span style={{ fontSize: 8, color: "#e32529", fontWeight: 700, flexShrink: 0 }}>🚨SLA</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// ─── Generate printable client onboarding checklist ──────────────────────────
function generateOnboardingChecklist(project) {
  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB") : "-";
  const progress = Math.round(
    (project.tasks || []).filter(t => t.status === "Complete").length /
    Math.max((project.tasks || []).length, 1) * 100
  );

  const taskRows = (tasks) => (tasks || []).map(t => `
    <tr style="border-bottom:1px solid #eee">
      <td style="padding:5px 8px;font-size:11px">${t.name || ""}</td>
      <td style="padding:5px 8px;font-size:11px;text-align:center">
        <span style="display:inline-block;width:14px;height:14px;border:1.5px solid #333;border-radius:2px;${t.status === "Complete" ? "background:#47ac4c" : ""}"></span>
      </td>
      <td style="padding:5px 8px;font-size:11px;color:#666">${t.status || "Not Started"}</td>
      <td style="padding:5px 8px;font-size:11px;color:#666">${t.owner || "-"}</td>
      <td style="padding:5px 8px;font-size:11px;color:#666">${fmtDate(t.finish)}</td>
      <td style="padding:5px 8px;font-size:11px;color:#666">${t.jiraKey || "-"}</td>
    </tr>`).join("");

  const moduleRows = (project.modules || []).map(m => `
    <tr style="background:#f5f5f5">
      <td colspan="6" style="padding:6px 8px;font-size:11px;font-weight:700;color:#1e2b33">${m.name || m.type || "Module"}</td>
    </tr>
    ${taskRows(m.tasks)}`).join("");

  const moduleSetup = (project.moduleSetup || []).map(r => `
    <tr style="border-bottom:1px solid #eee">
      <td style="padding:5px 8px;font-size:11px">${r.complianceType || ""}</td>
      <td style="padding:5px 8px;font-size:11px;text-align:center">${r.onsite === "Yes" ? "✓" : ""}</td>
      <td style="padding:5px 8px;font-size:11px;text-align:center">${r.template === "Yes" ? "✓" : ""}</td>
      <td style="padding:5px 8px;font-size:11px;color:#666">${r.jiraKey || "-"}</td>
    </tr>`).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <title>Onboarding Checklist - ${project.clientName}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #222; max-width: 1000px; margin: 0 auto; padding: 20px; font-size: 12px; }
    h1 { color: #1e2b33; border-bottom: 3px solid #47ac4c; padding-bottom: 8px; font-size: 20px; }
    h2 { color: #1e2b33; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 20px; border-left: 4px solid #47ac4c; padding-left: 8px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    th { background: #1e2b33; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
    .progress-bar { background: #e0e0e0; border-radius: 4px; height: 8px; }
    .progress-fill { background: #47ac4c; height: 8px; border-radius: 4px; width: ${progress}%; }
    @media print { @page { size: A4; margin: 15mm } body { padding: 0 } }
  </style></head><body>
  <div style="display:flex;align-items:center;gap:12px;margin-bottom:8px">
    <div style="background:#47ac4c;color:#fff;padding:3px 10px;border-radius:3px;font-weight:700;font-size:11px">C365CLOUD</div>
    <div style="color:#888;font-size:11px">Client Onboarding Checklist - CONFIDENTIAL</div>
  </div>
  <h1>📋 ${project.clientName}</h1>
  <table style="margin-bottom:12px;border:none"><tbody>
    <tr><td style="padding:3px 8px;font-weight:600;color:#555;width:120px">SDM</td><td style="padding:3px 8px">${project.sdm || "-"}</td>
        <td style="padding:3px 8px;font-weight:600;color:#555;width:120px">PC</td><td style="padding:3px 8px">${project.pc || "-"}</td></tr>
    <tr><td style="padding:3px 8px;font-weight:600;color:#555">Progress</td>
        <td colspan="3" style="padding:3px 8px">
          <div style="display:flex;align-items:center;gap:8px">
            <div class="progress-bar" style="flex:1"><div class="progress-fill"></div></div>
            <span style="font-weight:700;color:#47ac4c">${progress}%</span>
          </div>
        </td></tr>
    <tr><td style="padding:3px 8px;font-weight:600;color:#555">Generated</td><td style="padding:3px 8px">${new Date().toLocaleDateString("en-GB")}</td></tr>
  </tbody></table>

  <h2>Implementation Plan</h2>
  <table><thead><tr><th>Task</th><th>Done</th><th>Status</th><th>Owner</th><th>Finish</th><th>Jira</th></tr></thead>
  <tbody>${taskRows(project.tasks)}${moduleRows}</tbody></table>

  ${project.moduleSetup?.length ? `
  <h2>Module Setup</h2>
  <table><thead><tr><th>Compliance Type</th><th>Onsite</th><th>Template</th><th>Jira</th></tr></thead>
  <tbody>${moduleSetup}</tbody></table>` : ""}

  <p style="margin-top:24px;font-size:9px;color:#aaa;text-align:center">
    C365Cloud Task Manager - Client Onboarding Checklist - ${project.clientName} - ${new Date().toLocaleDateString("en-GB")}
  </p>
  <script>window.print();</script></body></html>`;

  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}



// ─── Dev Ticket Completion Feed ──────────────────────────────────────────────
// Shows tickets from DEVOPS, CLOUDDEV, ED, TEMPL, TEMP2A that have moved to Done
// No personal activity logging - purely Jira status-based

function SharedActivityFeed({ devCompletedTickets }) {
  const [filterProject, setFilterProject] = React.useState("");
  const [filterAssignee, setFilterAssignee] = React.useState("");
  const [sortBy, setSortBy] = React.useState("updated");
  const [maxCards, setMaxCards] = React.useState(100);
  const [showSettings, setShowSettings] = React.useState(false);
  const [viewMode, setViewMode] = React.useState("list"); // "list" | "chart"
  const [settingDays, setSettingDays]   = React.useState(() => parseInt(localStorage.getItem("taskflow_dev_days")  || "30"));
  const [settingLimit, setSettingLimit] = React.useState(() => parseInt(localStorage.getItem("taskflow_dev_limit") || "500"));

  const tickets = devCompletedTickets || [];
  const allProjects  = [...new Set(tickets.map(t => t.project))].sort();
  const allAssignees = [...new Set(tickets.map(t => t.assignee))].filter(Boolean).sort();

  const filtered = tickets
    .filter(t => (!filterProject  || t.project  === filterProject))
    .filter(t => (!filterAssignee || t.assignee === filterAssignee))
    .sort((a, b) => {
      if (sortBy === "project")  return a.project.localeCompare(b.project);
      if (sortBy === "assignee") return a.assignee.localeCompare(b.assignee);
      return new Date(b.updated) - new Date(a.updated);
    });

  const projectColour = (proj) => {
    const map = {
      DEVOPS:   "#4c9aff",
      CLOUDDEV: "#a78bfa",
      ED:       "#F1901E",
      TEMPL:    "#47ac4c",
      TEMP2A:   "#fbbf24",
    };
    return map[proj] || "var(--text-muted)";
  };

  // Build chart data - count by assignee per project
  const chartData = React.useMemo(() => {
    const spaces = filterProject ? [filterProject] : [...new Set(tickets.map(t => t.project))].sort();
    const assignees = [...new Set(tickets.map(t => t.assignee))].filter(Boolean).sort();
    // Per assignee, count per space
    const rows = assignees.map(assignee => {
      const counts = {};
      spaces.forEach(sp => {
        counts[sp] = tickets.filter(t => t.assignee === assignee && t.project === sp).length;
      });
      const total = Object.values(counts).reduce((s, v) => s + v, 0);
      return { assignee, counts, total };
    }).filter(r => r.total > 0).sort((a, b) => b.total - a.total);
    return { spaces, rows };
  }, [tickets, filterProject]);

  const fmtTs = (ts) => {
    try {
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1)  return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24)  return `${diffHrs}h ago`;
      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays < 7)  return `${diffDays}d ago`;
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    } catch { return ts; }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
          🔔 Team Activity - Dev Tickets Completed
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 5, overflow: "hidden" }}>
            <button className={`btn ${viewMode === "list" ? "primary" : ""}`}
              style={{ fontSize: 10, padding: "4px 10px", borderRadius: 0, border: "none" }}
              onClick={() => setViewMode("list")}>☰ List</button>
            <button className={`btn ${viewMode === "chart" ? "primary" : ""}`}
              style={{ fontSize: 10, padding: "4px 10px", borderRadius: 0, border: "none", borderLeft: "1px solid var(--border)" }}
              onClick={() => setViewMode("chart")}>📊 Chart</button>
          </div>
          <button className={`btn ${showSettings ? "primary" : ""}`}
            style={{ fontSize: 10, padding: "4px 10px" }}
            onClick={() => setShowSettings(v => !v)}
            title="Configure time horizon and result limit">
            ⚙ Settings
          </button>
          {/* Count badge - updates when filters change */}
          <span style={{
            background: (!filterProject && !filterAssignee) ? "var(--green)20" : "#4c9aff20",
            color: (!filterProject && !filterAssignee) ? "var(--green)" : "#4c9aff",
            border: `1px solid ${(!filterProject && !filterAssignee) ? "var(--green)" : "#4c9aff"}40`,
            borderRadius: 12, padding: "3px 12px", fontSize: 12, fontWeight: 700,
          }}>
            {filtered.length === tickets.length
              ? `${tickets.length} ticket${tickets.length !== 1 ? "s" : ""} · last ${settingDays} days`
              : `${filtered.length} of ${tickets.length} ticket${tickets.length !== 1 ? "s" : ""}`}
          </span>
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 6,
          padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>
            ⚙ Activity Feed Settings
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {/* Time horizon */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600 }}>Time horizon (days fetched from Jira)</div>
              <div style={{ display: "flex", gap: 4 }}>
                {[7, 14, 30, 60, 90].map(d => (
                  <button key={d} className={`btn ${settingDays === d ? "primary" : ""}`}
                    style={{ fontSize: 10, padding: "4px 10px" }}
                    onClick={() => {
                      setSettingDays(d);
                      localStorage.setItem("taskflow_dev_days", String(d));
                    }}>{d}d</button>
                ))}
              </div>
              <div style={{ fontSize: 9, color: "var(--text-dim)" }}>Takes effect on next Sync (S key)</div>
            </div>
            {/* Max results from Jira */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600 }}>Max tickets fetched from Jira</div>
              <div style={{ display: "flex", gap: 4 }}>
                {[100, 250, 500, 1000].map(n => (
                  <button key={n} className={`btn ${settingLimit === n ? "primary" : ""}`}
                    style={{ fontSize: 10, padding: "4px 10px" }}
                    onClick={() => {
                      setSettingLimit(n);
                      localStorage.setItem("taskflow_dev_limit", String(n));
                    }}>{n}</button>
                ))}
              </div>
              <div style={{ fontSize: 9, color: "var(--text-dim)" }}>Takes effect on next Sync (S key)</div>
            </div>
            {/* Cards shown on screen */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600 }}>Cards shown in list</div>
              <div style={{ display: "flex", gap: 4 }}>
                {[25, 50, 100, 250, 500].map(n => (
                  <button key={n} className={`btn ${maxCards === n ? "primary" : ""}`}
                    style={{ fontSize: 10, padding: "4px 10px" }}
                    onClick={() => setMaxCards(n)}>{n}</button>
                ))}
              </div>
              <div style={{ fontSize: 9, color: "var(--text-dim)" }}>Applies immediately to current data</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "var(--surface)",
            borderRadius: 4, border: "1px solid var(--border)", flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, color: "var(--text-muted)", flex: 1 }}>
              Fetching <b>{settingDays} days</b> / max <b>{settingLimit} tickets</b> from Jira · showing <b>{maxCards} cards</b>
            </span>
            <button className="btn primary" style={{ fontSize: 11, padding: "4px 14px", whiteSpace: "nowrap" }}
              onClick={() => { setShowSettings(false); if (window.__c365sync__) window.__c365sync__(); }}>
              ↻ Resync now
            </button>
          </div>
        </div>
      )}

      {/* Space legend */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["DEVOPS","CLOUDDEV","ED","TEMPL","TEMP2A"].map(p => (
          <span key={p} style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
            background: projectColour(p) + "20", color: projectColour(p),
            border: `1px solid ${projectColour(p)}50`, cursor: "pointer",
            outline: filterProject === p ? `2px solid ${projectColour(p)}` : "none" }}
            onClick={() => setFilterProject(filterProject === p ? "" : p)}>
            {p}
          </span>
        ))}
        {filterProject && <button className="btn" style={{ fontSize: 9, padding: "2px 8px" }} onClick={() => setFilterProject("")}>✕ Clear</button>}
      </div>

      {/* Filter + sort bar */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <select className="form-select" style={{ fontSize: 11 }} value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">All assignees</option>
          {allAssignees.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <div style={{ display: "flex", gap: 4 }}>
          {[["updated","Most recent"],["project","Project"],["assignee","Assignee"]].map(([id, label]) => (
            <button key={id} className={`btn ${sortBy === id ? "primary" : ""}`}
              style={{ fontSize: 10, padding: "4px 8px" }}
              onClick={() => setSortBy(id)}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── Chart view ── */}
      {viewMode === "chart" && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "16px 20px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>
            Tasks completed by assignee {filterProject ? `- ${filterProject}` : "- all spaces"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 16 }}>
            Last {settingDays} days · {tickets.length} tickets total
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            {chartData.spaces.map(sp => (
              <span key={sp} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text-muted)" }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: projectColour(sp), display: "inline-block" }} />
                {sp}
              </span>
            ))}
          </div>

          {chartData.rows.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text-dim)", fontSize: 12, padding: "20px 0" }}>No data to display</div>
          ) : (() => {
            const maxVal = Math.max(...chartData.rows.map(r => r.total), 1);
            const barH = 22;
            const gap = 6;
            const labelW = 120;
            const chartW = 520;
            const totalH = chartData.rows.length * (barH + gap) + 20;

            return (
              <div style={{ overflowX: "auto" }}>
                <svg width={labelW + chartW + 60} height={totalH} style={{ display: "block" }}>
                  {chartData.rows.map((row, i) => {
                    const y = i * (barH + gap) + 10;
                    let xOffset = 0;
                    return (
                      <g key={row.assignee}>
                        {/* Assignee label */}
                        <text x={labelW - 8} y={y + barH / 2 + 4} textAnchor="end"
                          style={{ fontSize: 11, fill: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}>
                          {row.assignee.split(" ").slice(0, 2).join(" ")}
                        </text>
                        {/* Stacked bars per space */}
                        {chartData.spaces.map(sp => {
                          const count = row.counts[sp] || 0;
                          if (!count) return null;
                          const w = Math.max((count / maxVal) * chartW, 2);
                          const rect = (
                            <g key={sp}>
                              <rect x={labelW + xOffset} y={y} width={w} height={barH}
                                fill={projectColour(sp)} opacity={0.85} rx={2} />
                              {w > 18 && (
                                <text x={labelW + xOffset + w / 2} y={y + barH / 2 + 4}
                                  textAnchor="middle"
                                  style={{ fontSize: 10, fill: "#fff", fontWeight: 700, fontFamily: "Inter, sans-serif" }}>
                                  {count}
                                </text>
                              )}
                            </g>
                          );
                          xOffset += w;
                          return rect;
                        })}
                        {/* Total label */}
                        <text x={labelW + xOffset + 6} y={y + barH / 2 + 4}
                          style={{ fontSize: 11, fill: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}>
                          {row.total}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>
            );
          })()}

          {/* Summary table below chart */}
          <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Summary</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: "5px 10px", color: "var(--text-muted)", fontWeight: 600, fontSize: 11 }}>Assignee</th>
                    {chartData.spaces.map(sp => (
                      <th key={sp} style={{ textAlign: "center", padding: "5px 10px", color: projectColour(sp), fontWeight: 700, fontSize: 11 }}>{sp}</th>
                    ))}
                    <th style={{ textAlign: "center", padding: "5px 10px", color: "var(--text)", fontWeight: 700, fontSize: 11 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.rows.map((row, i) => (
                    <tr key={row.assignee} style={{ borderBottom: "1px solid var(--border)40", background: i % 2 === 0 ? "transparent" : "var(--surface2)20" }}>
                      <td style={{ padding: "5px 10px", color: "var(--text)" }}>{row.assignee}</td>
                      {chartData.spaces.map(sp => (
                        <td key={sp} style={{ textAlign: "center", padding: "5px 10px", color: row.counts[sp] ? projectColour(sp) : "var(--text-dim)", fontWeight: row.counts[sp] ? 600 : 400 }}>
                          {row.counts[sp] || "-"}
                        </td>
                      ))}
                      <td style={{ textAlign: "center", padding: "5px 10px", color: "var(--text)", fontWeight: 700 }}>{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {viewMode === "list" && tickets.length === 0 ? (
        <div className="empty-state">
          <div className="icon">🔔</div>
          No completed dev tickets found in the last 30 days.<br />
          Sync to check for new completions across DEVOPS, CLOUDDEV, ED, TEMPL and TEMP2A.
        </div>
      ) : viewMode === "list" && filtered.length === 0 ? (
        <div className="empty-state"><div className="icon">🔍</div>No tickets match your filter.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {filtered.length > maxCards && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", padding: "6px 10px", background: "var(--surface2)",
              borderRadius: 4, border: "1px solid var(--border)", textAlign: "center" }}>
              Showing {maxCards} of {filtered.length} tickets - increase limit in ⚙ Settings
            </div>
          )}
          {filtered.slice(0, maxCards).map((t, i) => (
            <div key={t.id || i} style={{
              display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px",
              background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 5,
              borderLeft: `3px solid ${projectColour(t.project)}` }}>
              {/* Project badge */}
              <div style={{ width: 60, flexShrink: 0, display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 3,
                  background: projectColour(t.project) + "20", color: projectColour(t.project),
                  border: `1px solid ${projectColour(t.project)}40` }}>
                  {t.project}
                </span>
                <a href={`https://c365online.atlassian.net/browse/${t.key}`} target="_blank" rel="noreferrer"
                  style={{ fontSize: 10, color: projectColour(t.project), fontWeight: 600, textDecoration: "none" }}
                  onClick={e => e.stopPropagation()}>
                  {t.key}
                </a>
              </div>
              {/* Ticket info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", marginBottom: 3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.summary}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: "var(--green)" }}>
                    ✓ {t.status}
                  </span>
                  {t.updated && (
                    <span style={{ fontSize: 9, fontWeight: 600, color: "var(--green)" }}>
                      📅 {new Date(t.updated).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  )}
                  {t.assignee && (
                    <span style={{ fontSize: 9, color: "var(--text-muted)" }}>👤 {t.assignee}</span>
                  )}
                  {t.affectedClient && (
                    <span style={{ fontSize: 9, color: "var(--text-muted)" }}>🏢 {t.affectedClient}</span>
                  )}
                </div>
              </div>
              {/* Relative timestamp top right */}
              <div style={{ fontSize: 9, color: "var(--text-dim)", flexShrink: 0, whiteSpace: "nowrap" }}>
                {fmtTs(t.updated)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Cross-Project Gantt ──────────────────────────────────────────────────────
function CrossProjectGantt({ projects }) {
  const [monthRange, setMonthRange] = React.useState(6);
  const [filterProject, setFilterProject] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");

  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  // Collect all tasks with dates across all projects
  const allItems = [];
  projects.forEach(p => {
    const addTask = (t, section) => {
      if (!t.start && !t.finish && !t.milestone) return;
      allItems.push({
        id: p.id + t.id,
        projectName: p.clientName,
        projectId: p.id,
        taskName: t.name || "",
        section: section || "",
        start: t.start || t.finish || t.milestone,
        finish: t.finish || t.start || t.milestone,
        milestone: t.milestone || null,
        status: t.status || "Not Started",
        sdm: p.sdm || "",
        pc: p.pc || "",
      });
    };
    (p.tasks || []).forEach(t => addTask(t, "Plan"));
    (p.modules || []).flatMap(m => (m.tasks || []).forEach(t => addTask(t, m.name || m.type || "Module")));
  });

  // Filter
  const filtered = allItems.filter(i =>
    (!filterProject || i.projectId === filterProject) &&
    (!filterStatus  || i.status === filterStatus)
  );

  // Date range - today to today + monthRange months
  const rangeStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const rangeEnd   = new Date(today.getFullYear(), today.getMonth() + monthRange, 0);
  const totalDays  = Math.ceil((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24));

  const pct = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const days = Math.ceil((d - rangeStart) / (1000 * 60 * 60 * 24));
    return Math.max(0, Math.min(100, (days / totalDays) * 100));
  };

  // Generate month headers
  const months = [];
  for (let i = 0; i < monthRange; i++) {
    const d = new Date(rangeStart.getFullYear(), rangeStart.getMonth() + i, 1);
    months.push(d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }));
  }

  const statusColour = (s) => {
    if (s === "Complete") return "var(--green)";
    if (s === "In Progress") return "#4c9aff";
    if (s === "Blocked" || s === "At Risk") return "var(--red)";
    return "var(--border)";
  };

  const todayPct = pct(todayStr);
  const uniqueProjects = [...new Map(projects.map(p => [p.id, p])).values()];
  const uniqueStatuses = [...new Set(allItems.map(i => i.status))].sort();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
          📊 Cross-Project Gantt
          <span style={{ fontSize: 10, fontWeight: 400, color: "var(--text-muted)", marginLeft: 8 }}>{filtered.length} tasks across {projects.length} projects</span>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <select className="form-select" style={{ fontSize: 11 }} value={filterProject} onChange={e => setFilterProject(e.target.value)}>
            <option value="">All projects</option>
            {uniqueProjects.map(p => <option key={p.id} value={p.id}>{p.clientName}</option>)}
          </select>
          <select className="form-select" style={{ fontSize: 11 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {[3,6,12].map(m => (
            <button key={m} className={`btn ${monthRange === m ? "primary" : ""}`} style={{ fontSize: 10, padding: "4px 8px" }}
              onClick={() => setMonthRange(m)}>{m}m</button>
          ))}
        </div>
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
        {/* Month headers */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)", background: "var(--surface2)" }}>
          <div style={{ width: 280, flexShrink: 0, padding: "6px 10px", fontSize: 10, fontWeight: 600, color: "var(--text-muted)" }}>Task</div>
          <div style={{ flex: 1, display: "flex", position: "relative" }}>
            {months.map((m, i) => (
              <div key={i} style={{ flex: 1, padding: "6px 4px", fontSize: 9, fontWeight: 600, color: "var(--text-muted)",
                textAlign: "center", borderLeft: "1px solid var(--border)30", textTransform: "uppercase", letterSpacing: 0.5 }}>{m}</div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div style={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-dim)", fontSize: 12 }}>
              No tasks with dates found. Add start/finish dates in the Plan tab of your projects.
            </div>
          ) : filtered.map((item, i) => {
            const startPct = pct(item.start);
            const endPct   = pct(item.finish);
            const width    = Math.max(0.5, (endPct || 0) - (startPct || 0));
            const isMilestone = !!item.milestone && item.start === item.finish;
            const isOverdue = item.finish < todayStr && item.status !== "Complete";
            return (
              <div key={item.id} style={{ display: "flex", borderBottom: "1px solid var(--border)20",
                background: i % 2 === 0 ? "transparent" : "var(--surface2)" }}>
                <div style={{ width: 280, flexShrink: 0, padding: "5px 10px", display: "flex", flexDirection: "column", gap: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.taskName}
                  </div>
                  <div style={{ fontSize: 9, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    🏢 {item.projectName} {item.section ? `· ${item.section}` : ""}
                  </div>
                </div>
                <div style={{ flex: 1, position: "relative", height: 36 }}>
                  {/* Today line */}
                  {todayPct !== null && todayPct >= 0 && todayPct <= 100 && (
                    <div style={{ position: "absolute", left: `${todayPct}%`, top: 0, bottom: 0,
                      width: 1, background: "var(--red)", opacity: 0.6, zIndex: 2 }} />
                  )}
                  {/* Bar or milestone */}
                  {startPct !== null && (
                    isMilestone ? (
                      <div style={{ position: "absolute", left: `${startPct}%`, top: "50%",
                        transform: "translate(-50%, -50%) rotate(45deg)",
                        width: 10, height: 10, background: "#fbbf24", zIndex: 1 }} title={item.taskName} />
                    ) : (
                      <div style={{
                        position: "absolute",
                        left: `${startPct}%`,
                        width: `${width}%`,
                        top: "50%", transform: "translateY(-50%)",
                        height: 14, borderRadius: 3,
                        background: isOverdue ? "var(--red)" : statusColour(item.status),
                        opacity: item.status === "Complete" ? 0.6 : 0.85,
                        zIndex: 1, minWidth: 4,
                      }} title={`${item.taskName}: ${item.start} → ${item.finish} (${item.status})`} />
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 9, color: "var(--text-muted)" }}>
        {[["var(--green)","Complete"],["#4c9aff","In Progress"],["var(--border)","Not Started"],["var(--red)","Overdue/At Risk"],["#fbbf24","◆ Milestone"]].map(([c,l]) => (
          <span key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 12, height: 8, borderRadius: 2, background: c, flexShrink: 0 }} />{l}
          </span>
        ))}
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 1, height: 12, background: "var(--red)", opacity: 0.6 }} />Today
        </span>
      </div>
    </div>
  );
}


// ─── SDM/PC Workload View ────────────────────────────────────────────────────
function WorkloadView({ tasks, projects }) {
  const [tab, setTab] = React.useState("sdm"); // sdm | pc
  const [sortBy, setSortBy] = React.useState("total");
  const today = new Date().toISOString().split("T")[0];

  const jiraTasks = tasks.filter(t => t.source === "jira" && !t.done);

  // Build SDM and PC maps
  const buildMap = (field) => {
    const map = {};
    jiraTasks.forEach(t => {
      const name = t[field];
      if (!name) return;
      if (!map[name]) map[name] = { name, total: 0, overdue: 0, noReply: 0, slaBreach: 0, waiting: 0, clients: new Set(), projects: 0 };
      map[name].total++;
      if (t.due && t.due < today) map[name].overdue++;
      if (isNoReply(t)) map[name].noReply++;
      const sla = getSLAInfo(t);
      if (sla?.breached) map[name].slaBreach++;
      if (t.waitingOn) map[name].waiting++;
      if (t.affectedClient) map[name].clients.add(t.affectedClient);
    });
    // Add project counts
    projects.forEach(p => {
      const name = field === "sdm" ? p.sdm : p.pc;
      if (!name || !map[name]) return;
      map[name].projects++;
    });
    return map;
  };

  const sdmMap = buildMap("sdm");
  const pcMap  = buildMap("pc");
  const activeMap = tab === "sdm" ? sdmMap : pcMap;

  const rows = Object.values(activeMap).sort((a, b) => {
    if (sortBy === "total")    return b.total    - a.total;
    if (sortBy === "overdue")  return b.overdue  - a.overdue;
    if (sortBy === "noReply")  return b.noReply  - a.noReply;
    if (sortBy === "sla")      return b.slaBreach - a.slaBreach;
    if (sortBy === "waiting")  return b.waiting  - a.waiting;
    if (sortBy === "projects") return b.projects - a.projects;
    return a.name.localeCompare(b.name);
  });

  const Cell = ({ value, warn, danger }) => (
    <td style={{ padding: "7px 12px", fontSize: 11, textAlign: "center",
      color: value === 0 ? "var(--text-dim)" : danger && value > 0 ? "#e32529" : warn && value > 0 ? "#F1901E" : "var(--text)",
      fontWeight: value > 0 && (danger || warn) ? 700 : 400 }}>
      {value === 0 ? "-" : value}
    </td>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
          👥 SDM / PC Workload
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className={`btn ${tab === "sdm" ? "primary" : ""}`} style={{ fontSize: 11 }} onClick={() => setTab("sdm")}>SDM</button>
          <button className={`btn ${tab === "pc" ? "primary" : ""}`}  style={{ fontSize: 11 }} onClick={() => setTab("pc")}>Project Coordinator</button>
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        {[
          { label: `${tab.toUpperCase()}s tracked`, value: rows.length, colour: "#4c9aff" },
          { label: "Total open tickets",  value: rows.reduce((n,r) => n + r.total, 0),    colour: "var(--text-muted)" },
          { label: "Overdue across all",  value: rows.reduce((n,r) => n + r.overdue, 0),  colour: "#e32529" },
          { label: "SLA breaches",        value: rows.reduce((n,r) => n + r.slaBreach, 0), colour: "#e32529" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--surface)", border: "1px solid var(--border)",
            borderTop: `3px solid ${s.colour}`, borderRadius: 6, padding: "10px 12px", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.colour }}>{s.value}</div>
            <div style={{ fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Sort bar */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "var(--text-muted)", alignSelf: "center", marginRight: 4 }}>Sort by:</span>
        {[["total","Open"],["overdue","Overdue"],["noReply","No Reply"],["sla","SLA"],["waiting","Waiting"],["projects","Projects"],["name","Name"]].map(([id, label]) => (
          <button key={id} className={`btn ${sortBy === id ? "primary" : ""}`} style={{ fontSize: 9, padding: "3px 8px" }}
            onClick={() => setSortBy(id)}>{label}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              {["Name","Open Tickets","Overdue","No Reply 2d+","SLA Breach","Waiting","Clients","Projects"].map(h => (
                <th key={h} style={{ padding: "6px 12px", fontSize: 9, color: "var(--text-muted)", textAlign: h === "Name" ? "left" : "center",
                  fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "var(--text-dim)", fontSize: 12 }}>
                No {tab.toUpperCase()} data found - sync Jira to populate.
              </td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.name} style={{ background: i % 2 === 0 ? "transparent" : "var(--surface2)",
                borderBottom: "1px solid var(--border)20" }}>
                <td style={{ padding: "7px 12px", fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{r.name}</td>
                <Cell value={r.total} />
                <Cell value={r.overdue}   danger />
                <Cell value={r.noReply}   warn />
                <Cell value={r.slaBreach} danger />
                <Cell value={r.waiting}   warn />
                <td style={{ padding: "7px 12px", fontSize: 11, textAlign: "center", color: "var(--text-muted)" }}>{r.clients.size}</td>
                <td style={{ padding: "7px 12px", fontSize: 11, textAlign: "center", color: "var(--text-muted)" }}>{r.projects || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Jira Ticket Age Report ───────────────────────────────────────────────────
function TicketAgeReport({ tasks }) {
  const [sortBy, setSortBy] = React.useState("age");
  const [filterSdm, setFilterSdm] = React.useState("");

  const today = new Date();
  const supportTasks = tasks.filter(t =>
    t.source === "jira" && !t.done && t.jiraKey && t.jiraKey.startsWith("SUPPORT-")
  );

  const withAge = supportTasks.map(t => {
    const updated = t.jiraUpdated ? new Date(t.jiraUpdated) : null;
    const created = t.createdAt ? new Date(t.createdAt) : null;
    const ref = updated || created;
    const ageDays = ref ? Math.floor((today - ref) / (1000 * 60 * 60 * 24)) : null;
    const sla = getSLAInfo(t);
    return { ...t, ageDays, sla };
  });

  const sorted = [...withAge].sort((a, b) => {
    if (sortBy === "age")     return (b.ageDays || 0) - (a.ageDays || 0);
    if (sortBy === "sla")     return (b.sla?.breached ? 1 : 0) - (a.sla?.breached ? 1 : 0);
    if (sortBy === "client")  return (a.affectedClient || "").localeCompare(b.affectedClient || "");
    if (sortBy === "status")  return (a.jiraStatus || "").localeCompare(b.jiraStatus || "");
    if (sortBy === "priority") {
      const order = { critical: 0, high: 1, medium: 2, low: 3 };
      return (order[a.priority] ?? 2) - (order[b.priority] ?? 2);
    }
    return 0;
  }).filter(t => !filterSdm || t.sdm === filterSdm || t.pc === filterSdm);

  const allPeople = [...new Set(withAge.flatMap(t => [t.sdm, t.pc].filter(Boolean)))].sort();

  const ageColour = (days) => {
    if (days === null) return "var(--text-muted)";
    if (days >= 10) return "#e32529";
    if (days >= 5)  return "#F1901E";
    return "var(--green)";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
          🕐 Ticket Age Report
          <span style={{ fontSize: 10, fontWeight: 400, color: "var(--text-muted)", marginLeft: 8 }}>{sorted.length} open SUPPORT tickets</span>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <select className="form-select" style={{ fontSize: 11 }} value={filterSdm} onChange={e => setFilterSdm(e.target.value)}>
            <option value="">All SDMs / PCs</option>
            {allPeople.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {[["age","Oldest first"],["sla","SLA breach"],["client","Client"],["status","Status"],["priority","Priority"]].map(([id, label]) => (
            <button key={id} className={`btn ${sortBy === id ? "primary" : ""}`} style={{ fontSize: 10, padding: "4px 8px" }}
              onClick={() => setSortBy(id)}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              {["Ticket","Client","Status","SDM","PC","Priority","Age","SLA"].map(h => (
                <th key={h} style={{ padding: "6px 10px", fontSize: 9, color: "var(--text-muted)", textAlign: "left",
                  fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
                  borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "var(--text-dim)", fontSize: 12 }}>No open SUPPORT tickets</td></tr>
            )}
            {sorted.map((t, i) => (
              <tr key={t.id} style={{ background: i % 2 === 0 ? "transparent" : "var(--surface2)",
                borderBottom: "1px solid var(--border)20" }}>
                <td style={{ padding: "6px 10px" }}>
                  <a href={t.jiraUrl} target="_blank" rel="noreferrer"
                    style={{ fontSize: 11, color: "#4c9aff", fontWeight: 600, textDecoration: "none" }}>
                    {t.jiraKey}
                  </a>
                </td>
                <td style={{ padding: "6px 10px", fontSize: 11, color: "var(--text)", maxWidth: 120,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.affectedClient || "-"}
                </td>
                <td style={{ padding: "6px 10px" }}>
                  <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                    background: "var(--surface2)", color: "var(--text-muted)", border: "1px solid var(--border)" }}>
                    {t.jiraStatus}
                  </span>
                </td>
                <td style={{ padding: "6px 10px", fontSize: 10, color: "var(--text-muted)" }}>{t.sdm || "-"}</td>
                <td style={{ padding: "6px 10px", fontSize: 10, color: "var(--text-muted)" }}>{t.pc  || "-"}</td>
                <td style={{ padding: "6px 10px" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color:
                    t.priority === "high" || t.priority === "critical" ? "var(--red)" :
                    t.priority === "medium" ? "var(--amber)" : "var(--green)" }}>
                    {t.priority || "-"}
                  </span>
                </td>
                <td style={{ padding: "6px 10px", fontSize: 11, fontWeight: 700, color: ageColour(t.ageDays) }}>
                  {t.ageDays !== null ? `${t.ageDays}d` : "-"}
                </td>
                <td style={{ padding: "6px 10px" }}>
                  {t.sla?.breached
                    ? <span style={{ fontSize: 9, fontWeight: 700, color: "#e32529" }}>🚨 -{Math.abs(t.sla.remaining)}d</span>
                    : t.sla?.warning
                    ? <span style={{ fontSize: 9, fontWeight: 700, color: "#F1901E" }}>⚠ {t.sla.remaining}d</span>
                    : <span style={{ fontSize: 9, color: "var(--text-dim)" }}>OK</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Calendar View ────────────────────────────────────────────────────────────
function CalendarView({ tasks, projects, onSelectTask, includeSubtasks }) {
  const [calYear, setCalYear] = React.useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = React.useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = React.useState(null);
  const [calFilter, setCalFilter] = React.useState("all"); // all | tasks | projects | outlook | jira

  const today = new Date().toISOString().split("T")[0];
  const monthName = new Date(calYear, calMonth, 1).toLocaleString("en-GB", { month: "long", year: "numeric" });

  // Build event map: date string → array of events
  const eventMap = React.useMemo(() => {
    const map = {};
    const add = (dateStr, ev) => {
      if (!dateStr) return;
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(ev);
    };

    // Tasks with due dates (and start dates)
    tasks.forEach(t => {
      if (t.done) return;
      if (calFilter !== "all" && calFilter !== "tasks" && calFilter !== t.source) return;
      const colour = t.source === "jira" ? "#4c9aff" : t.source === "outlook" ? "#0078d4" : "#47ac4c";
      if (t.startDate) add(t.startDate, { type: "task-start", id: t.id, title: "▶ " + t.title, colour, source: t.source, priority: t.priority });
      if (t.due) add(t.due, { type: "task", id: t.id, title: t.title, colour, source: t.source, priority: t.priority });
      // Subtasks with start/due dates
      if (includeSubtasks) {
        (t.subtasks || []).forEach(s => {
          if (s.done) return;
          if (s.startDate) add(s.startDate, { type: "subtask-start", id: t.id, title: "▶ ↳ " + s.text, colour: "#8ab4e8", source: "subtask" });
          if (s.due) add(s.due, { type: "subtask", id: t.id, title: "↳ " + s.text, colour: "#8ab4e8", source: "subtask" });
        });
      }
    });

    // Project milestones and task dates
    if (calFilter === "all" || calFilter === "projects") {
      projects.forEach(p => {
        [...(p.tasks || []), ...(p.modules || []).flatMap(m => m.tasks || [])].forEach(t => {
          if (t.finish) add(t.finish, { type: "milestone", id: p.id + t.id, title: `[${p.clientName}] ${t.name}`, colour: "#F1901E", source: "project" });
          if (t.milestone) add(t.milestone, { type: "milestone", id: p.id + t.id + "m", title: `★ [${p.clientName}] ${t.name}`, colour: "#fbbf24", source: "project" });
        });
      });
    }

    return map;
  }, [tasks, projects, calFilter, includeSubtasks]);

  // Build calendar grid
  const firstDay = new Date(calYear, calMonth, 1);
  const lastDay  = new Date(calYear, calMonth + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Monday=0
  const totalDays = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const toIso = d => `${calYear}-${String(calMonth + 1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;

  const dayEvents = selectedDay ? (eventMap[toIso(selectedDay)] || []) : [];

  const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  const filterBtns = [
    { id: "all", label: "All" },
    { id: "tasks", label: "Tasks" },
    { id: "jira", label: "Jira" },
    { id: "outlook", label: "Outlook" },
    { id: "projects", label: "Projects" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn" onClick={() => {
            if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
            else setCalMonth(m => m - 1);
            setSelectedDay(null);
          }}>◀</button>
          <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text)", minWidth: 160, textAlign: "center" }}>{monthName}</span>
          <button className="btn" onClick={() => {
            if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
            else setCalMonth(m => m + 1);
            setSelectedDay(null);
          }}>▶</button>
          <button className="btn" style={{ fontSize: 11 }} onClick={() => { setCalYear(new Date().getFullYear()); setCalMonth(new Date().getMonth()); setSelectedDay(null); }}>Today</button>
        </div>
        {/* Filter buttons */}
        <div style={{ display: "flex", gap: 4 }}>
          {filterBtns.map(f => (
            <button key={f.id} className={`btn ${calFilter === f.id ? "primary" : ""}`}
              style={{ fontSize: 10, padding: "4px 10px" }}
              onClick={() => setCalFilter(f.id)}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Calendar grid + side panel */}
      <div style={{ display: "grid", gridTemplateColumns: selectedDay ? "1fr 260px" : "1fr", gap: 12, alignItems: "start" }}>
        {/* Grid */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
          {/* Day of week headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: "var(--surface2)", borderBottom: "1px solid var(--border)" }}>
            {DOW.map(d => (
              <div key={d} style={{ padding: "6px 0", textAlign: "center", fontSize: 10, fontWeight: 700, color: "var(--text-muted)", letterSpacing: 0.5, textTransform: "uppercase" }}>{d}</div>
            ))}
          </div>
          {/* Weeks */}
          {Array.from({ length: cells.length / 7 }, (_, wi) => (
            <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: wi < cells.length / 7 - 1 ? "1px solid var(--border)30" : "none" }}>
              {cells.slice(wi * 7, wi * 7 + 7).map((d, di) => {
                const iso = d ? toIso(d) : null;
                const isToday = iso === today;
                const isSelected = iso && selectedDay === d;
                const evs = iso ? (eventMap[iso] || []) : [];
                const isWeekend = di === 5 || di === 6;
                return (
                  <div key={di}
                    onClick={() => d && setSelectedDay(isSelected ? null : d)}
                    style={{
                      minHeight: 72, padding: "4px 6px", cursor: d ? "pointer" : "default",
                      background: isSelected ? "var(--surface2)" : isToday ? "var(--green)10" : isWeekend ? "var(--bg)" : "transparent",
                      borderLeft: di > 0 ? "1px solid var(--border)30" : "none",
                      outline: isSelected ? "2px solid var(--green)" : "none",
                      outlineOffset: -2,
                      transition: "background 0.1s",
                    }}>
                    {d && (
                      <>
                        <div style={{
                          fontSize: 11, fontWeight: isToday ? 800 : 500,
                          color: isToday ? "var(--green)" : isWeekend ? "var(--text-dim)" : "var(--text)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: isToday ? 20 : "auto", height: isToday ? 20 : "auto",
                          background: isToday ? "var(--green)" : "transparent",
                          color: isToday ? "#fff" : isWeekend ? "var(--text-dim)" : "var(--text)",
                          borderRadius: isToday ? "50%" : 0, marginBottom: 2,
                        }}>{d}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {evs.slice(0, 3).map((ev, i) => (
                            <div key={i} style={{
                              fontSize: 9, lineHeight: "14px", padding: "0 3px",
                              background: ev.colour + "25", color: ev.colour,
                              borderLeft: `2px solid ${ev.colour}`, borderRadius: "0 2px 2px 0",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              fontWeight: 600, maxWidth: "100%",
                            }} title={ev.title}>{ev.title.replace(/^\[[^\]]+\]\s*/, "").substring(0, 18)}</div>
                          ))}
                          {evs.length > 3 && (
                            <div style={{ fontSize: 8, color: "var(--text-muted)", paddingLeft: 3 }}>+{evs.length - 3} more</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Day detail side panel */}
        {selectedDay && (
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)", borderBottom: "1px solid var(--border)", paddingBottom: 6, marginBottom: 2 }}>
              {new Date(calYear, calMonth, selectedDay).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            {dayEvents.length === 0
              ? <div style={{ fontSize: 11, color: "var(--text-dim)", fontStyle: "italic" }}>Nothing scheduled</div>
              : dayEvents.map((ev, i) => (
                <div key={i}
                  style={{ padding: "8px 10px", background: "var(--surface2)", borderRadius: 4, borderLeft: `3px solid ${ev.colour}`, cursor: ev.id && ev.type === "task" ? "pointer" : "default" }}
                  onClick={() => ev.type === "task" && onSelectTask && onSelectTask(ev.id)}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{ev.title}</div>
                  <div style={{ fontSize: 9, color: ev.colour, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>
                    {ev.type === "milestone" ? "📍 Project milestone" : ev.source === "jira" ? "🔷 Jira" : ev.source === "outlook" ? "📧 Outlook" : "✏ Manual"}
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, color: "var(--text-muted)" }}>
        {[["#4c9aff","Jira task"],["#0078d4","Outlook task"],["#47ac4c","Manual task"],["#F1901E","Project deadline"],["#fbbf24","Project milestone"]].map(([c,l]) => (
          <span key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: c, flexShrink: 0 }} />{l}
          </span>
        ))}
      </div>
    </div>
  );
}

function DashboardView({ tasks, projects, config, jiraConnected, msConnected, onNavigate, onSelect, onOpenJiraQueue }) {
  const today = new Date().toISOString().split("T")[0];
  const accountId = localStorage.getItem("taskflow_jira_accountid") || "";
  const myName = localStorage.getItem("taskflow_jira_displayname") || "";

  // A Jira card is "with me for next steps" only when it is genuinely actionable by me:
  //  - assigned to me, OR
  //  - I am the SDM and it is Waiting on SDM, OR
  //  - I am the PC and it is Waiting on PC.
  // Being the reporter/raiser or merely mentioned does NOT count.
  const jiraActionable = (t) => {
    if (t.source !== "jira" || t.done) return false;
    const assignedToMe = t.assignedToMe === true ||
      (t.assigneeAccountId && t.assigneeAccountId === accountId) ||
      (t.assignee && myName && t.assignee === myName);
    const sdmTurn = t.isSDM && t.jiraStatus === "Waiting on SDM";
    const pcTurn  = t.isPC  && t.jiraStatus === "Waiting on PC";
    return assignedToMe || sdmTurn || pcTurn;
  };

  // For the date sections we include manual/outlook tasks as normal, but Jira cards
  // ONLY when they are actionable by me (so we don't flood the dashboard with tickets
  // that are really sitting with dev, the client, or someone else).
  const relevant = (t) => {
    if (t.done) return false;
    if (t.source === "jira") return jiraActionable(t);
    return true; // manual + outlook tasks are always the user's own
  };

  const dueToday     = tasks.filter(t => relevant(t) && t.due === today);
  const overdue      = tasks.filter(t => relevant(t) && t.due && t.due < today);
  // In-date: not overdue and not complete (due today or in the future, or no due date but active)
  const inDate       = tasks.filter(t => relevant(t) && (!t.due || t.due >= today));
  // Jira Awaiting Action: strictly the cards on my desk right now
  const jiraAwaiting = tasks.filter(jiraActionable);
  const myProjects   = projects.filter(p =>
    p.sdmAccountId === accountId || p.pcAccountId === accountId ||
    p.createdByAccountId === accountId
  );

  const [expandedSections, setExpandedSections] = React.useState({});
  const Section = ({ title, count, colour, items, viewId, emptyMsg }) => {
    const isExpanded = expandedSections[title];
    const shown = isExpanded ? items : items.slice(0, 4);
    return (
    <div id={"dash-section-" + title} style={{ background: "var(--surface)", border: `1px solid ${colour}40`, borderRadius: 6, padding: 16, borderTop: `3px solid ${colour}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ background: colour + "20", color: colour, border: `1px solid ${colour}50`, borderRadius: 10, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{count}</span>
          {count > 4 && <button className="btn" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => setExpandedSections(p => ({ ...p, [title]: !p[title] }))}>{isExpanded ? "Show less" : "View all →"}</button>}
        </div>
      </div>
      {items.length === 0
        ? <div style={{ fontSize: 11, color: "var(--text-dim)", fontStyle: "italic" }}>{emptyMsg}</div>
        : (
          <div style={isExpanded ? { maxHeight: 340, overflowY: "auto" } : undefined}>
            {shown.map(t => (
              <div key={t.id || t.clientName}
                style={{ padding: "6px 0", borderBottom: "1px solid var(--border)30", cursor: t.id ? "pointer" : "default", fontSize: 12 }}
                onClick={t.id ? () => onSelect(t.id) : (viewId ? () => onNavigate(viewId) : undefined)}>
                <div style={{ color: "var(--text)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.source === "jira" && <span style={{ color: "#4c9aff", marginRight: 4 }}>◆</span>}
                  {t.source === "outlook" && <span style={{ color: "#0078d4", marginRight: 4 }}>✉</span>}
                  {t.title || t.clientName}
                </div>
                {(t.due || t.jiraStatus) && (
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                    {t.due && <span style={{ color: t.due < today ? "var(--red)" : "var(--amber)" }}>📅 {formatDate(t.due)}</span>}
                    {t.jiraStatus && <span style={{ marginLeft: 8 }}>{t.jiraStatus}</span>}
                    {t.jiraKey && <span style={{ marginLeft: 8, fontFamily: "monospace", color: "#4c9aff" }}>{t.jiraKey}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      }
    </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 18, fontWeight: 700, color: "var(--text)", paddingBottom: 8, borderBottom: "2px solid var(--green)" }}>
        📊 My Dashboard
        <span style={{ fontSize: 11, fontWeight: 400, color: "var(--text-muted)", marginLeft: 12 }}>{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
      </div>

      {/* Summary stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {[
          { label: "In Date",      value: inDate.length,       colour: "#47ac4c", section: "✅ In Date" },
          { label: "Due Today",     value: dueToday.length,     colour: "#F1901E", section: "⚡ Due Today" },
          { label: "Overdue",       value: overdue.length,      colour: "#e32529", section: "⚠ Overdue" },
          { label: "Jira Awaiting", value: jiraAwaiting.length, colour: "#4c9aff", section: "🔷 Jira Awaiting Action" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--surface)", border: `1px solid ${s.colour}40`, borderRadius: 6, padding: 14, textAlign: "center", cursor: "pointer", borderTop: `3px solid ${s.colour}` }}
            onClick={() => {
              if (s.label === "Jira Awaiting" && onOpenJiraQueue) { onOpenJiraQueue(); return; }
              setExpandedSections(p => ({ ...p, [s.section]: true }));
              const el = document.getElementById("dash-section-" + s.section);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.colour, fontFamily: "Poppins, sans-serif" }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Detail sections */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Section title="✅ In Date" count={inDate.length} colour="#47ac4c" items={inDate} viewId="manual" emptyMsg="No active tasks in date" />
        <Section title="⚠ Overdue" count={overdue.length} colour="#e32529" items={overdue} viewId="manual" emptyMsg="No overdue tasks!" />
        <Section title="⚡ Due Today" count={dueToday.length} colour="#F1901E" items={dueToday} viewId="manual" emptyMsg="Nothing due today - great!" />
        <Section title="🔷 Jira Awaiting Action" count={jiraAwaiting.length} colour="#4c9aff" items={jiraAwaiting} viewId="jira-queue" emptyMsg="No Jira tickets awaiting your action" />
        <Section title="🏗 My Projects" count={myProjects.length} colour="#47ac4c"
          items={myProjects.map(p => ({ id: null, clientName: p.clientName, title: p.clientName, jiraStatus: `${Math.round(p.tasks.filter(t=>t.status==="Complete").length / Math.max(p.tasks.length,1) * 100)}% complete` }))}
          viewId="implementations" emptyMsg="No projects assigned to you" />
      </div>
    </div>
  );
}

// ─── Client Hub ──────────────────────────────────────────────────────────────

const CH_TYPES = [
  { value: "email",    label: "Email",          badge: "ch-type-email" },
  { value: "teams",    label: "Teams message",  badge: "ch-type-teams" },
  { value: "phone",    label: "Phone call",     badge: "ch-type-phone" },
  { value: "meeting",  label: "Meeting note",   badge: "ch-type-meeting" },
  { value: "note",     label: "Note",           badge: "ch-type-note" },
  { value: "document", label: "Document",       badge: "ch-type-document" },
];

function chTypeBadge(type) {
  const t = CH_TYPES.find(x => x.value === type) || CH_TYPES[4];
  return <span className={`ch-type-badge ${t.badge}`}>{t.label}</span>;
}

function chTypeIcon(type) {
  if (type === "email")    return "✉";
  if (type === "teams")    return "💬";
  if (type === "phone")    return "📞";
  if (type === "meeting")  return "📋";
  if (type === "document") return "📄";
  return "📝";
}

function formatChDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7)  return `${diff} days ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
  } catch { return dateStr; }
}

// Keyword-based AI query - no external API needed
function clientHubAIQuery(entries, question) {
  if (!entries.length) return { answer: "No communications have been logged for this client yet.", sources: [] };
  if (!question.trim()) return { answer: "", sources: [] };

  const q = question.toLowerCase();
  const keywords = q.replace(/[^a-z0-9 ]/g, "").split(/\s+/).filter(w => w.length > 3 &&
    !["what","when","where","which","this","that","have","with","from","about","does","were","they","their","there"].includes(w));

  // Score each entry by keyword matches
  const scored = entries.map(e => {
    const text = [e.subject, e.content, e.contactNames, e.loggedBy].join(" ").toLowerCase();
    const score = keywords.reduce((s, kw) => s + (text.split(kw).length - 1), 0);
    return { entry: e, score, text };
  }).filter(x => x.score > 0).sort((a, b) => b.score - a.score);

  // Intent detection
  const isRecent    = /recent|latest|last|new|current/.test(q);
  const isSummary   = /summar|overview|tell me about|who is|relationship/.test(q);
  const isOutstand  = /outstand|unresolved|open|pending|concern|issue|problem/.test(q);
  const isContact   = /contact|who|person|speak|spoke|key people/.test(q);
  const isMeeting   = /meet|minute|agreed|decision|call/.test(q);

  let answer = "";
  const sources = scored.slice(0, 5).map(x => x.entry);

  if (isSummary) {
    const types = [...new Set(entries.map(e => e.type))];
    const contacts = [...new Set(entries.flatMap(e => (e.contactNames||"").split(",").map(s=>s.trim())).filter(Boolean))];
    const latest = entries.sort((a,b) => new Date(b.date||b.createdAt) - new Date(a.date||a.createdAt))[0];
    answer = `There ${entries.length === 1 ? "is" : "are"} ${entries.length} communication${entries.length !== 1 ? "s" : ""} logged for this client, covering: ${types.join(", ")}. ` +
      (contacts.length ? `Key contacts recorded: ${contacts.slice(0,5).join(", ")}. ` : "") +
      (latest ? `Most recent activity: ${latest.subject} (${formatChDate(latest.date||latest.createdAt)}).` : "");
  } else if (isContact) {
    const contacts = [...new Set(entries.flatMap(e => (e.contactNames||"").split(",").map(s=>s.trim())).filter(Boolean))];
    answer = contacts.length
      ? `Key contacts recorded for this client: ${contacts.join(", ")}.`
      : "No specific contact names have been recorded yet. Add them when logging communications.";
  } else if (isRecent && !scored.length) {
    const recent = [...entries].sort((a,b) => new Date(b.date||b.createdAt) - new Date(a.date||a.createdAt)).slice(0,3);
    answer = `Most recent activity: ${recent.map(e => `${e.subject} (${formatChDate(e.date||e.createdAt)})`).join("; ")}.`;
    sources.push(...recent);
  } else if (isOutstand) {
    const notes = scored.filter(x => x.entry.type !== "document");
    answer = notes.length
      ? `Based on logged communications, the following may be relevant to outstanding items: ${notes.slice(0,3).map(x => x.entry.subject).join("; ")}.`
      : "No communications matching outstanding concerns were found. Check the timeline for recent activity.";
  } else if (isMeeting) {
    const meetings = entries.filter(e => e.type === "meeting" || (e.subject||"").toLowerCase().includes("meeting") || (e.subject||"").toLowerCase().includes("minute"));
    answer = meetings.length
      ? `${meetings.length} meeting record${meetings.length !== 1 ? "s" : ""} found. Most recent: "${meetings.sort((a,b)=>new Date(b.date)-new Date(a.date))[0].subject}" on ${formatChDate(meetings[0].date)}.`
      : "No meeting notes found for this client yet.";
  } else if (scored.length) {
    const top = scored.slice(0, 3);
    answer = `Found ${scored.length} relevant communication${scored.length !== 1 ? "s" : ""}. Most relevant: ${top.map(x => `"${x.entry.subject}" (${formatChDate(x.entry.date||x.entry.createdAt)})`).join("; ")}.`;
  } else {
    answer = `No communications found matching "${question}". Try broader terms, or check the Timeline tab to browse all entries.`;
  }

  return { answer, sources };
}

// ─── Project Hub ──────────────────────────────────────────────────────────────
// A personal, project-based workspace parallel to Client Hub. The user creates
// their own projects (optionally tagged to a client) and logs emails, meetings,
// notes and documents against each. Reuses the Client Hub server storage via a
// namespaced key ("__PROJ__<name>") so it gets persistence, file handling and AI
// extraction for free. The project index is kept in localStorage.
const PROJECT_HUB_INDEX_KEY = "taskflow_project_hub_index";
const projKey = (name) => "__PROJ__" + name;

function ProjectHubView() {
  const [projects, setProjects] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(PROJECT_HUB_INDEX_KEY) || "[]"); } catch { return []; }
  });
  const [selected, setSelected] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("timeline");
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [viewEntry, setViewEntry] = React.useState(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const [showNewProject, setShowNewProject] = React.useState(false);
  const [newProjName, setNewProjName] = React.useState("");
  const [newProjClient, setNewProjClient] = React.useState("");
  const [aiQuestion, setAiQuestion] = React.useState("");
  const [aiResult, setAiResult] = React.useState(null);
  const [claudeResult, setClaudeResult] = React.useState(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [extracting, setExtracting] = React.useState(false);
  const [extractedActions, setExtractedActions] = React.useState(null);
  const [selectedActions, setSelectedActions] = React.useState({});

  const [form, setForm] = React.useState({
    type: "email", subject: "", content: "", contactNames: "",
    loggedBy: localStorage.getItem("taskflow_jira_displayname") || "", date: new Date().toISOString().split("T")[0],
  });
  const [docFile, setDocFile] = React.useState(null);
  const [docUploading, setDocUploading] = React.useState(false);
  const [dragActive, setDragActive] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(null); // { done, total, current }
  const [transcribingId, setTranscribingId] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const persistIndex = (list) => {
    setProjects(list);
    try { localStorage.setItem(PROJECT_HUB_INDEX_KEY, JSON.stringify(list)); } catch {}
  };

  React.useEffect(() => {
    if (!selected) { setEntries([]); return; }
    setLoading(true);
    fetch(`${CLIENT_HUB_API}/${encodeURIComponent(projKey(selected.name))}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setEntries(Array.isArray(data) ? data : []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [selected]);

  const createProject = () => {
    const name = newProjName.trim();
    if (!name) return;
    if (projects.some(p => p.name.toLowerCase() === name.toLowerCase())) { alert("A project with that name already exists."); return; }
    const proj = { name, client: newProjClient || "", createdAt: new Date().toISOString() };
    persistIndex([...projects, proj]);
    setSelected(proj); setActiveTab("timeline");
    setShowNewProject(false); setNewProjName(""); setNewProjClient("");
  };

  const renameProject = () => {
    if (!selected) return;
    const newName = window.prompt("Rename project:", selected.name);
    if (!newName || !newName.trim() || newName.trim() === selected.name) return;
    const nn = newName.trim();
    if (projects.some(p => p.name.toLowerCase() === nn.toLowerCase())) { alert("That name is taken."); return; }
    (async () => {
      for (const e of entries) {
        await fetch(CLIENT_HUB_API, { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...e, id: undefined, clientName: projKey(nn) }) }).catch(() => {});
      }
      for (const e of entries) {
        await fetch(`${CLIENT_HUB_API}/${encodeURIComponent(projKey(selected.name))}/${e.id}`, { method: "DELETE" }).catch(() => {});
      }
      persistIndex(projects.map(p => p.name === selected.name ? { ...p, name: nn } : p));
      setSelected({ ...selected, name: nn });
    })();
  };

  const deleteProject = () => {
    if (!selected) return;
    if (!window.confirm(`Delete project "${selected.name}" and all ${entries.length} logged item(s)? This cannot be undone.`)) return;
    (async () => {
      for (const e of entries) {
        await fetch(`${CLIENT_HUB_API}/${encodeURIComponent(projKey(selected.name))}/${e.id}`, { method: "DELETE" }).catch(() => {});
      }
      persistIndex(projects.filter(p => p.name !== selected.name));
      setSelected(null); setEntries([]);
    })();
  };

  const setEditClient = () => {
    if (!selected) return;
    const c = window.prompt("Tag this project to a client (leave blank for none):", selected.client || "");
    if (c === null) return;
    persistIndex(projects.map(p => p.name === selected.name ? { ...p, client: c.trim() } : p));
    setSelected({ ...selected, client: c.trim() });
  };

  const saveEntry = async (entry) => {
    setSaving(true);
    try {
      const res = await fetch(CLIENT_HUB_API, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...entry, clientName: projKey(selected.name) }) });
      if (res.ok) { const saved = await res.json(); setEntries(prev => [saved, ...prev]); return true; }
    } catch {} finally { setSaving(false); }
    return false;
  };

  const deleteEntry = async (id) => {
    try {
      await fetch(`${CLIENT_HUB_API}/${encodeURIComponent(projKey(selected.name))}/${id}`, { method: "DELETE" });
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch {}
    setDeleteConfirm(null);
  };

  const togglePin = async (entry) => {
    const updated = { ...entry, pinned: !entry.pinned };
    try {
      await fetch(`${CLIENT_HUB_API}/${encodeURIComponent(projKey(selected.name))}/${entry.id}/pin`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pinned: updated.pinned }) });
    } catch {}
    setEntries(prev => prev.map(e => e.id === entry.id ? updated : e));
    if (viewEntry?.id === entry.id) setViewEntry(updated);
  };

  const handleLogSubmit = async () => {
    if (!form.subject.trim()) { alert("Subject is required."); return; }
    const entry = {
      type: form.type, subject: form.subject.trim(), content: form.content.trim(),
      contactNames: form.contactNames.trim(), loggedBy: form.loggedBy.trim(),
      date: form.date, createdAt: new Date().toISOString(), projectClient: selected.client || "",
    };
    const ok = await saveEntry(entry);
    if (ok) {
      setForm({ type: "email", subject: "", content: "", contactNames: "", loggedBy: form.loggedBy, date: new Date().toISOString().split("T")[0] });
      setActiveTab("timeline");
    } else alert("Could not save. Check the server connection.");
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setDocUploading(true);
    let okCount = 0, failCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ done: i, total: files.length, current: file.name });
      try {
        const b64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result.split(",")[1]);
          r.onerror = reject; r.readAsDataURL(file);
        });
        const entry = {
          type: "document", subject: file.name, fileName: file.name,
          fileData: b64, fileMime: file.type, fileSize: file.size,
          date: new Date().toISOString().split("T")[0], createdAt: new Date().toISOString(),
          loggedBy: form.loggedBy, projectClient: selected.client || "",
        };
        const ok = await saveEntry(entry);
        if (ok) okCount++; else failCount++;
      } catch { failCount++; }
    }
    setUploadProgress(null);
    setDocUploading(false);
    setDocFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setActiveTab("documents");
    if (failCount) alert(`${okCount} uploaded, ${failCount} failed.`);
  };

  const handleDocUpload = () => uploadFiles(fileInputRef.current?.files);

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const extractActions = async (entry) => {
    if (!entry.content) return;
    setExtracting(true); setExtractedActions(null); setSelectedActions({});
    try {
      const res = await fetch(CLIENT_HUB_EXTRACT_API, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: entry.content, fileName: entry.fileName || entry.subject, clientName: selected.name, userNames: [form.loggedBy].filter(Boolean) }) });
      const data = await res.json();
      const actions = data.actions || [];
      setExtractedActions(actions);
      const sel = {}; actions.forEach((a, i) => { sel[i] = true; }); setSelectedActions(sel);
    } catch { setExtractedActions([]); }
    setExtracting(false);
  };

  const transcribeImage = async (entry) => {
    setTranscribingId(entry.id);
    try {
      const res = await fetch(`${CLIENT_HUB_API}/transcribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientName: projKey(selected.name), entryId: entry.id }),
      });
      const data = await res.json();
      if (data.content) {
        setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, content: data.content, transcribed: true } : e));
      } else {
        alert("Could not read image: " + (data.error || "unknown error"));
      }
    } catch (e) { alert("Read image failed: " + e.message); }
    setTranscribingId(null);
  };

  const typeIcon = (t) => t === "email" ? "✉" : t === "meeting" ? "👥" : t === "document" ? "📄" : "📝";
  const fmtD = (d) => { try { return new Date(d).toLocaleDateString("en-GB"); } catch { return d; } };

  const filteredEntries = React.useMemo(() => {
    let list = entries;
    if (typeFilter !== "all") list = list.filter(e => e.type === typeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        (e.subject || "").toLowerCase().includes(q) ||
        (e.content || "").toLowerCase().includes(q) ||
        (e.contactNames || "").toLowerCase().includes(q));
    }
    return [...list.filter(e => e.pinned), ...list.filter(e => !e.pinned)];
  }, [entries, typeFilter, searchQuery]);
  const docEntries = entries.filter(e => e.type === "document");

  const tabs = [
    { id: "timeline", label: "📅 Timeline" },
    { id: "log", label: "✏ Log communication" },
    { id: "documents", label: "📄 Documents" },
    { id: "ai", label: "🔍 Ask AI" },
  ];

  if (!selected) {
    return (
      <div className="ch-shell">
        <div className="ch-topbar">
          <span className="ch-title">🗂 Project Hub</span>
          <button className="btn primary" style={{ fontSize: 12 }} onClick={() => setShowNewProject(true)}>+ New project</button>
        </div>
        <div style={{ padding: 24 }}>
          {projects.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text-dim)", padding: 40 }}>
              No projects yet. Create one to start logging emails, meetings, notes and documents for a piece of work.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
              {projects.map(p => (
                <div key={p.name} onClick={() => { setSelected(p); setActiveTab("timeline"); }}
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16, cursor: "pointer", borderTop: "3px solid #2E75B6" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>🗂 {p.name}</div>
                  {p.client && <div style={{ fontSize: 11, color: "#4c9aff" }}>🏢 {p.client}</div>}
                  <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 6 }}>Created {fmtD(p.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showNewProject && (
          <div className="overlay" onClick={e => e.target === e.currentTarget && setShowNewProject(false)}>
            <div className="rm-modal" style={{ maxWidth: 440 }}>
              <div className="rm-modal-header"><span className="rm-title">New project</span>
                <button className="btn" onClick={() => setShowNewProject(false)}>✕</button></div>
              <div className="rm-modal-body">
                <div className="rm-field">
                  <label>Project name</label>
                  <input className="rm-input" autoFocus value={newProjName} onChange={e => setNewProjName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") createProject(); }} placeholder="e.g. BUPA Bulk Download Feature" />
                </div>
                <div className="rm-field">
                  <label>Client (optional)</label>
                  <select className="rm-select-input" value={newProjClient} onChange={e => setNewProjClient(e.target.value)}>
                    <option value="">- None -</option>
                    {JIRA_CLIENT_LIST.filter(c => c !== "All Clients").map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="rm-modal-footer">
                <button className="btn" onClick={() => setShowNewProject(false)}>Cancel</button>
                <button className="btn primary" onClick={createProject}>Create project</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ch-shell">
      <div className="ch-topbar">
        <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => { setSelected(null); setEntries([]); }}>← Projects</button>
        <span className="ch-title">🗂 {selected.name}</span>
        {selected.client && <span style={{ fontSize: 12, color: "#4c9aff" }}>🏢 {selected.client}</span>}
        <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{entries.length} item{entries.length !== 1 ? "s" : ""}</span>
        {loading && <span style={{ fontSize: 12, color: "var(--text-dim)" }}>Loading…</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button className="btn" style={{ fontSize: 11 }} onClick={setEditClient} title="Tag to a client">🏢 Client</button>
          <button className="btn" style={{ fontSize: 11 }} onClick={renameProject}>✎ Rename</button>
          <button className="btn danger" style={{ fontSize: 11 }} onClick={deleteProject}>🗑 Delete</button>
        </div>
      </div>

      <div className="ch-tabs">
        {tabs.map(t => (
          <div key={t.id} className={`ch-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</div>
        ))}
      </div>

      <div className="ch-body">
        {activeTab === "timeline" && (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <input className="form-input" placeholder="Search…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
              <select className="form-input" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ width: 140 }}>
                <option value="all">All types</option>
                <option value="email">Emails</option>
                <option value="meeting">Meetings</option>
                <option value="note">Notes</option>
                <option value="document">Documents</option>
              </select>
            </div>
            {filteredEntries.length === 0 ? (
              <div style={{ textAlign: "center", color: "var(--text-dim)", padding: 30 }}>No entries yet. Use "Log communication" to add one.</div>
            ) : filteredEntries.map(e => (
              <div key={e.id} className="ch-entry" onClick={() => setViewEntry(e)} style={{ cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>{typeIcon(e.type)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {e.pinned && <span title="Pinned">📌</span>}
                    <span style={{ fontWeight: 600, color: "var(--text)" }}>{e.subject}</span>
                  </div>
                  {e.content && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.content}</div>}
                  <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 3 }}>
                    {fmtD(e.date || e.createdAt)}{e.loggedBy ? ` · ${e.loggedBy}` : ""}{e.contactNames ? ` · ${e.contactNames}` : ""}
                  </div>
                </div>
                <button className="btn" style={{ fontSize: 10, padding: "2px 6px" }} onClick={ev => { ev.stopPropagation(); togglePin(e); }}>{e.pinned ? "Unpin" : "Pin"}</button>
                <button className="btn danger" style={{ fontSize: 10, padding: "2px 6px" }} onClick={ev => { ev.stopPropagation(); setDeleteConfirm(e.id); }}>Del</button>
              </div>
            ))}
          </div>
        )}

        {activeTab === "log" && (
          <div style={{ maxWidth: 640 }}>
            <div className="rm-field">
              <label>Type</label>
              <select className="rm-select-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="email">✉ Email</option>
                <option value="meeting">👥 Meeting</option>
                <option value="note">📝 Note</option>
              </select>
            </div>
            <div className="rm-field"><label>Subject</label>
              <input className="rm-input" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Brief subject / title" /></div>
            <div className="rm-field"><label>Detail / content</label>
              <textarea className="rm-input" style={{ minHeight: 140, resize: "vertical" }} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Paste the email, meeting notes, or note here…" /></div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div className="rm-field" style={{ flex: 1, minWidth: 160 }}><label>Contacts</label>
                <input className="rm-input" value={form.contactNames} onChange={e => setForm({ ...form, contactNames: e.target.value })} placeholder="Names, comma separated" /></div>
              <div className="rm-field" style={{ flex: 1, minWidth: 160 }}><label>Logged by</label>
                <input className="rm-input" value={form.loggedBy} onChange={e => setForm({ ...form, loggedBy: e.target.value })} /></div>
              <div className="rm-field"><label>Date</label>
                <input type="date" className="rm-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={{ colorScheme: "dark" }} /></div>
            </div>
            <button className="btn primary" onClick={handleLogSubmit} disabled={saving}>{saving ? "Saving…" : "Save entry"}</button>
          </div>
        )}

        {activeTab === "documents" && (
          <div>
            <div
              onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
              onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
              onDrop={handleDrop}
              onClick={() => !docUploading && fileInputRef.current?.click()}
              style={{
                marginBottom: 16, padding: "26px 16px", borderRadius: 8, textAlign: "center", cursor: docUploading ? "default" : "pointer",
                border: `2px dashed ${dragActive ? "#2E75B6" : "var(--border)"}`,
                background: dragActive ? "rgba(46,117,182,0.08)" : "var(--surface)",
                transition: "all 0.15s",
              }}>
              <input type="file" ref={fileInputRef} multiple accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.eml,.msg,.jpg,.jpeg,.png,.gif,.webp,.heic"
                onChange={e => uploadFiles(e.target.files)} style={{ display: "none" }} />
              {docUploading ? (
                <div style={{ fontSize: 13, color: "var(--text)" }}>
                  ⏳ Uploading{uploadProgress ? ` ${uploadProgress.done + 1} of ${uploadProgress.total}` : ""}…
                  {uploadProgress?.current && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{uploadProgress.current}</div>}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 26, marginBottom: 6 }}>⭱</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Drag &amp; drop files here, or click to browse</div>
                  <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>Multiple files supported. PDF, Word, Excel, CSV, text, emails (.eml preferred), and images (JPG/PNG, read on demand).</div>
                </>
              )}
            </div>
            {docEntries.length === 0 ? (
              <div style={{ textAlign: "center", color: "var(--text-dim)", padding: 30 }}>No documents uploaded yet.</div>
            ) : docEntries.map(e => {
              const isImage = /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(e.fileName || "");
              return (
              <div key={e.id} className="ch-entry" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>{isImage ? "🖼" : "📄"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "var(--text)" }}>{e.fileName || e.subject}</div>
                  <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 3 }}>
                    {fmtD(e.date || e.createdAt)}{e.loggedBy ? ` · ${e.loggedBy}` : ""}
                    {isImage && (e.transcribed || e.content ? <span style={{ color: "#47ac4c", marginLeft: 6 }}>· transcribed</span> : <span style={{ color: "var(--amber)", marginLeft: 6 }}>· not read yet</span>)}
                  </div>
                </div>
                {e.fileUrl && <a className="btn" style={{ fontSize: 10, padding: "2px 8px" }} href={e.fileUrl} target="_blank" rel="noreferrer">Open</a>}
                {isImage && <button className="btn" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => transcribeImage(e)} disabled={transcribingId === e.id}>{transcribingId === e.id ? "Reading…" : (e.transcribed || e.content ? "🔁 Re-read" : "👁 Read image")}</button>}
                {e.content && <button className="btn" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => setViewEntry(e)}>View text</button>}
                {e.content && <button className="btn" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => extractActions(e)} disabled={extracting}>{extracting ? "…" : "✦ Actions"}</button>}
                <button className="btn danger" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => setDeleteConfirm(e.id)}>Del</button>
              </div>
            );})}
            {extractedActions && (
              <div style={{ marginTop: 16, padding: 14, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: "var(--text)" }}>Extracted actions</div>
                {extractedActions.length === 0 ? <div style={{ fontSize: 12, color: "var(--text-dim)" }}>No actions found.</div>
                  : extractedActions.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, color: "var(--text-muted)", padding: "4px 0", borderBottom: "1px solid var(--border)30" }}>
                      • {a.action || a.text || JSON.stringify(a)}{a.owner ? ` (${a.owner})` : ""}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "ai" && (
          <>
            <div className="ch-ai-box">
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>Ask about {selected.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
                Searches across all emails, meetings, notes and documents logged for this project.
              </div>
              <div className="ch-ai-input-row" style={{ display: "flex", gap: 8 }}>
                <input className="ch-input" style={{ flex: 1 }} type="text" placeholder="e.g. What was agreed on scope?"
                  value={aiQuestion}
                  onChange={e => { setAiQuestion(e.target.value); setClaudeResult(null); }}
                  onKeyDown={e => { if (e.key === "Enter") { setClaudeResult(null); setAiResult(clientHubAIQuery(entries, aiQuestion)); } }} />
                <button className="btn primary" title="Keyword search" onClick={() => { setClaudeResult(null); setAiResult(clientHubAIQuery(entries, aiQuestion)); }}>
                  🔍 Search
                </button>
                <button className="btn" style={{ background: "#2d1f4e", borderColor: "#c47ef0", color: "#c47ef0", whiteSpace: "nowrap" }}
                  title="Ask Claude AI for a detailed answer"
                  disabled={aiLoading || !aiQuestion.trim()}
                  onClick={async () => {
                    if (!aiQuestion.trim()) return;
                    setAiLoading(true); setClaudeResult(null); setAiResult(null);
                    try {
                      const res = await fetch(CLIENT_HUB_ASK_API, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ question: aiQuestion, clientName: projKey(selected.name), entries }),
                      });
                      const data = await res.json();
                      setClaudeResult(data.error ? { error: data.error } : { answer: data.answer });
                    } catch (e) { setClaudeResult({ error: e.message }); }
                    setAiLoading(false);
                  }}>
                  {aiLoading ? "⏳ Thinking…" : "✦ Ask Claude"}
                </button>
              </div>

              {aiResult && !claudeResult && (
                <div className="ch-ai-answer">
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--green)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>🔍 Keyword search result</div>
                  {aiResult.answer}
                  {aiResult.sources && aiResult.sources.length > 0 && (
                    <div className="ch-ai-source">Based on: {aiResult.sources.map(s => s.subject).join("; ")}</div>
                  )}
                </div>
              )}

              {claudeResult && (
                <div className="ch-ai-answer-claude">
                  <div className="ch-ai-claude-label">✦ Claude AI</div>
                  {claudeResult.error ? <span style={{ color: "var(--red)" }}>Error: {claudeResult.error}</span> : claudeResult.answer}
                </div>
              )}

              {aiLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 12, color: "#c47ef0" }}>
                  <span>✦</span> Claude is reading the project history…
                </div>
              )}
            </div>

            <div className="ch-card">
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Suggested questions</div>
              <div className="ch-suggested">
                {[
                  "Summarise the current status of this project",
                  "What has been agreed so far?",
                  "What are the outstanding actions or decisions?",
                  "Who are the key people involved?",
                  "What was discussed in the most recent meeting?",
                  "Are there any risks or blockers noted?",
                ].map(q => (
                  <div key={q} className="ch-suggested-q" onClick={() => { setAiQuestion(q); setClaudeResult(null); setAiResult(clientHubAIQuery(entries, q)); }}>
                    → {q}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(196,126,240,0.07)", border: "1px solid rgba(196,126,240,0.2)", borderRadius: 5, fontSize: 11, color: "#c47ef0" }}>
                ✦ Use <strong>Ask Claude</strong> for detailed analysis - it reads all logged items and gives a full answer. <strong>Search</strong> does a quick keyword match.
              </div>
            </div>
          </>
        )}
      </div>

      {viewEntry && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setViewEntry(null)}>
          <div className="rm-modal" style={{ maxWidth: 640 }}>
            <div className="rm-modal-header">
              <span className="rm-title">{typeIcon(viewEntry.type)} {viewEntry.subject}</span>
              <button className="btn" onClick={() => setViewEntry(null)}>✕</button>
            </div>
            <div className="rm-modal-body">
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginBottom: 10 }}>
                {fmtD(viewEntry.date || viewEntry.createdAt)}{viewEntry.loggedBy ? ` · ${viewEntry.loggedBy}` : ""}{viewEntry.contactNames ? ` · ${viewEntry.contactNames}` : ""}
              </div>
              <div style={{ whiteSpace: "pre-wrap", fontSize: 13, color: "var(--text)", lineHeight: 1.5 }}>{viewEntry.content || "(no content)"}</div>
              {viewEntry.fileUrl && <a className="btn" style={{ marginTop: 12, display: "inline-block" }} href={viewEntry.fileUrl} target="_blank" rel="noreferrer">Open document</a>}
            </div>
            <div className="rm-modal-footer">
              <button className="btn" onClick={() => togglePin(viewEntry)}>{viewEntry.pinned ? "Unpin" : "📌 Pin"}</button>
              <button className="btn danger" onClick={() => { setDeleteConfirm(viewEntry.id); setViewEntry(null); }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div className="rm-modal" style={{ maxWidth: 360 }}>
            <div className="rm-modal-body">
              <div style={{ fontSize: 14, color: "var(--text)", marginBottom: 14 }}>Delete this entry?</div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="btn danger" onClick={() => deleteEntry(deleteConfirm)}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientHubView({ jiraConnected }) {
  const [selectedClient, setSelectedClient] = React.useState("");
  const [activeTab, setActiveTab]           = React.useState("timeline");
  const [entries, setEntries]               = React.useState([]);
  const [loading, setLoading]               = React.useState(false);
  const [saving, setSaving]                 = React.useState(false);
  const [typeFilter, setTypeFilter]         = React.useState("all");
  const [aiQuestion, setAiQuestion]         = React.useState("");
  const [aiResult, setAiResult]             = React.useState(null);
  const [claudeResult, setClaudeResult]     = React.useState(null);
  const [claudeLoading, setClaudeLoading]   = React.useState(false);
  const [deleteConfirm, setDeleteConfirm]   = React.useState(null);
  const [viewEntry, setViewEntry]           = React.useState(null);
  const [searchQuery, setSearchQuery]       = React.useState("");
  const [extracting, setExtracting]         = React.useState(false);
  const [extractedActions, setExtractedActions] = React.useState(null);
  const [selectedActions, setSelectedActions]   = React.useState({});
  const [creatingTasks, setCreatingTasks]       = React.useState(false);
  const [composingEmail, setComposingEmail]     = React.useState(false);
  const [myActionsOnly, setMyActionsOnly]       = React.useState(true);
  const [myNames, setMyNames]                   = React.useState("Phil Smith, Philip Smith, PS"); // entry open in popout

  // Log form state
  const [form, setForm] = React.useState({
    type: "email", subject: "", content: "", contactNames: "",
    loggedBy: "", date: new Date().toISOString().split("T")[0],
  });
  const [docFile, setDocFile]       = React.useState(null);
  const [docUploading, setDocUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);

  // Load entries when client changes
  React.useEffect(() => {
    if (!selectedClient) { setEntries([]); return; }
    setLoading(true);
    fetch(`${CLIENT_HUB_API}/${encodeURIComponent(selectedClient)}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setEntries(Array.isArray(data) ? data : []); })
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const togglePin = async (entry) => {
    const updated = { ...entry, pinned: !entry.pinned };
    try {
      await fetch(`${CLIENT_HUB_API}/${encodeURIComponent(selectedClient)}/${entry.id}/pin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinned: updated.pinned }),
      });
    } catch {}
    setEntries(prev => prev.map(e => e.id === entry.id ? updated : e));
    if (viewEntry?.id === entry.id) setViewEntry(updated);
  };

  const extractActions = async (entry) => {
    if (!entry.content) return;
    setExtracting(true);
    setExtractedActions(null);
    setSelectedActions({});
    try {
      const userNamesList = myNames.split(",").map(s => s.trim()).filter(Boolean);
      const res = await fetch(CLIENT_HUB_EXTRACT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: entry.content,
          fileName: entry.fileName || entry.subject,
          clientName: selectedClient,
          userNames: userNamesList,
        }),
      });
      const data = await res.json();
      if (data.actions) {
        setExtractedActions(data.actions);
        // Auto-select only my actions if myActionsOnly is on, otherwise select all
        const sel = {};
        data.actions.forEach((a, i) => {
          sel[i] = myActionsOnly ? !!a.isMyAction : true;
        });
        setSelectedActions(sel);
      } else {
        setExtractedActions([]);
      }
    } catch (e) {
      setExtractedActions([]);
    }
    setExtracting(false);
  };

  const sendEmailToClient = () => {
    const subject = encodeURIComponent(`RE: ${selectedClient}`);
    window.open(`mailto:?subject=${subject}&body=`, "_blank");
  };

  const saveEntry = async (entry) => {
    setSaving(true);
    try {
      const res = await fetch(CLIENT_HUB_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry),
      });
      if (res.ok) {
        const saved = await res.json();
        setEntries(prev => [saved, ...prev]);
        return true;
      }
    } catch {}
    finally { setSaving(false); }
    return false;
  };

  const deleteEntry = async (id) => {
    try {
      await fetch(`${CLIENT_HUB_API}/${encodeURIComponent(selectedClient)}/${id}`, { method: "DELETE" });
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch {}
    setDeleteConfirm(null);
  };

  const handleLogSubmit = async () => {
    if (!selectedClient || !form.subject.trim()) return;
    const entry = { ...form, clientName: selectedClient, createdAt: new Date().toISOString() };
    const ok = await saveEntry(entry);
    if (ok) {
      setForm({ type: "email", subject: "", content: "", contactNames: "", loggedBy: "", date: new Date().toISOString().split("T")[0] });
      setActiveTab("timeline");
    }
  };

  const handleDocUpload = async (files) => {
    const fileList = Array.from(files || []);
    if (!fileList.length || !selectedClient) return;
    setDocUploading(true);
    let saved = 0;
    for (const file of fileList) {
      try {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async (ev) => {
            try {
              const base64 = ev.target.result.split(",")[1];
              const entry = {
                type: "document",
                subject: file.name,
                content: `Document uploaded: ${file.name} (${(file.size/1024).toFixed(0)} KB)`,
                contactNames: "",
                loggedBy: form.loggedBy || "",
                date: new Date().toISOString().split("T")[0],
                clientName: selectedClient,
                createdAt: new Date().toISOString(),
                fileName: file.name,
                fileSize: file.size,
                fileMime: file.type,
                fileData: base64,
              };
              const ok = await saveEntry(entry);
              if (ok) saved++;
              resolve();
            } catch (e) { reject(e); }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } catch (e) {
        console.warn(`[ClientHub] Upload failed for ${file.name}:`, e.message);
      }
    }
    setDocUploading(false);
    setDocFile(null);
    if (saved > 0) setActiveTab("timeline");
  };

  const filteredEntries = React.useMemo(() => {
    let list = typeFilter === "all" ? entries : entries.filter(e => e.type === typeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        (e.subject || "").toLowerCase().includes(q) ||
        (e.content || "").toLowerCase().includes(q) ||
        (e.contactNames || "").toLowerCase().includes(q) ||
        (e.loggedBy || "").toLowerCase().includes(q)
      );
    }
    // Pinned entries float to top
    return [...list.filter(e => e.pinned), ...list.filter(e => !e.pinned)];
  }, [entries, typeFilter, searchQuery]);
  const docEntries = entries.filter(e => e.type === "document");

  // Health stats
  const last30 = entries.filter(e => {
    const d = new Date(e.date || e.createdAt);
    return (Date.now() - d) < 30 * 86400000;
  });
  const sentimentWords = entries.map(e => (e.content||"").toLowerCase()).join(" ");
  const posWords = (sentimentWords.match(/\b(good|great|happy|pleased|positive|excellent|progress|resolved|thank|appreciate)\b/g)||[]).length;
  const negWords = (sentimentWords.match(/\b(concern|issue|problem|unhappy|frustrated|delay|escalate|complaint|overdue)\b/g)||[]).length;
  const sentiment = negWords > posWords ? { label: "Needs attention", cls: "ch-dot-amber" }
    : negWords > 2              ? { label: "Mixed",           cls: "ch-dot-amber" }
    : posWords > 2              ? { label: "Positive",        cls: "ch-dot-green" }
    :                             { label: "Neutral",          cls: "ch-dot-green" };

  const tabs = [
    { id: "timeline", label: "📅 Timeline" },
    { id: "log",      label: "✏ Log communication" },
    { id: "documents",label: "📄 Documents" },
    { id: "ai",       label: "🔍 Ask AI" },
    { id: "health",   label: "💚 Relationship health" },
  ];

  return (
    <div className="ch-shell">
      {/* Top bar */}
      <div className="ch-topbar">
        <span className="ch-title">💬 Client Intelligence Hub</span>
        <select className="ch-client-select" value={selectedClient} onChange={e => { setSelectedClient(e.target.value); setActiveTab("timeline"); setAiResult(null); }}>
          <option value="">- Select a client -</option>
          {JIRA_CLIENT_LIST.filter(c => c !== "All Clients" && c !== "UNKNOWN").map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {selectedClient && <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{entries.length} communication{entries.length !== 1 ? "s" : ""} logged</span>}
        {loading && <span style={{ fontSize: 12, color: "var(--text-dim)" }}>Loading…</span>}
      </div>

      {/* ── Summary bar ── */}
      {selectedClient && (
        <div className="ch-summary-bar">
          <div className="ch-summary-stat">
            <div className="ch-summary-val">{entries.length}</div>
            <div className="ch-summary-lbl">Total</div>
          </div>
          <div className="ch-summary-divider" />
          <div className="ch-summary-stat">
            <div className="ch-summary-val">{last30.length}</div>
            <div className="ch-summary-lbl">Last 30d</div>
          </div>
          <div className="ch-summary-divider" />
          <div className="ch-summary-stat">
            <div className="ch-summary-val">{docEntries.length}</div>
            <div className="ch-summary-lbl">Docs</div>
          </div>
          <div className="ch-summary-divider" />
          <div className="ch-summary-stat">
            <div className="ch-summary-val" style={{ fontSize: 13, color: negWords > posWords ? "var(--amber)" : "var(--green)" }}>{sentiment.label}</div>
            <div className="ch-summary-lbl">Sentiment</div>
          </div>
          {entries.length > 0 && (
            <>
              <div className="ch-summary-divider" />
              <div className="ch-summary-last">
                Last contact: {formatChDate([...entries].sort((a,b) => new Date(b.date||b.createdAt) - new Date(a.date||a.createdAt))[0]?.date || "")}
                {(() => { const contacts = [...new Set(entries.flatMap(e => (e.contactNames||"").split(",").map(s=>s.trim())).filter(Boolean))]; return contacts.length ? ` · Key contacts: ${contacts.slice(0,3).join(", ")}` : ""; })()}
              </div>
            </>
          )}
          <button className="btn" style={{ fontSize: 11, padding: "4px 10px", marginLeft: "auto", whiteSpace: "nowrap" }} onClick={sendEmailToClient}>
            ✉ Send email
          </button>
        </div>
      )}

      {!selectedClient ? (
        <div className="ch-empty" style={{ marginTop: 60 }}>
          <span style={{ fontSize: 32 }}>🏢</span>
          <div style={{ fontWeight: 600, color: "var(--text-muted)" }}>Select a client to get started</div>
          <div style={{ fontSize: 12 }}>All communications, documents and notes are stored by client and shared across your team.</div>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="ch-tabs">
            {tabs.map(t => (
              <div key={t.id} className={`ch-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</div>
            ))}
          </div>

          <div className="ch-body">

            {/* ── Timeline ── */}
            {activeTab === "timeline" && (
              <>
                <div className="ch-search-bar">
                  <input className="ch-search-input" type="text" placeholder="🔍 Search communications…"
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                  {searchQuery && <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => setSearchQuery("")}>Clear</button>}
                </div>
                <div className="ch-filter-row" style={{ padding: "10px 16px 0" }}>
                  {["all","email","teams","phone","meeting","note","document"].map(f => (
                    <button key={f} className={`ch-filter-btn ${typeFilter === f ? "active" : ""}`} onClick={() => setTypeFilter(f)}>
                      {f === "all" ? "All" : CH_TYPES.find(x=>x.value===f)?.label || f}
                    </button>
                  ))}
                </div>
                {filteredEntries.length === 0 ? (
                  <div className="ch-empty">
                    <span style={{ fontSize: 28 }}>📭</span>
                    <div>No communications logged yet for {selectedClient}.</div>
                    <button className="btn primary" style={{ fontSize: 12, marginTop: 8 }} onClick={() => setActiveTab("log")}>+ Log first communication</button>
                  </div>
                ) : filteredEntries.map(e => (
                  <div key={e.id} className={`ch-entry type-${e.type}${e.pinned ? " pinned" : ""}`} onClick={() => setViewEntry(e)} style={{ cursor: "pointer" }}>
                    <div className="ch-entry-top">
                      {chTypeBadge(e.type)}
                      <span className="ch-entry-subject">{e.subject}</span>
                      <span className="ch-entry-date">{formatChDate(e.date || e.createdAt)}</span>
                      <button className={`ch-pin-btn${e.pinned ? " pinned" : ""}`} title={e.pinned ? "Unpin" : "Pin to top"}
                        onClick={ev => { ev.stopPropagation(); togglePin(e); }}>📌</button>
                      <button className="ch-delete-btn" title="Delete" onClick={ev => { ev.stopPropagation(); setDeleteConfirm(e.id); }}>✕</button>
                    </div>
                    <div className="ch-entry-meta">
                      {e.contactNames && <span style={{ fontSize: 11, color: "var(--text-dim)" }}>👤 {e.contactNames}</span>}
                      {e.loggedBy && <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Logged by {e.loggedBy}</span>}
                      {e.jiraKey && <span className="ch-jira-tag" onClick={ev => { ev.stopPropagation(); window.open(`https://c365.atlassian.net/browse/${e.jiraKey}`, "_blank"); }}>{e.jiraKey}</span>}
                    </div>
                    {e.content && <div className="ch-entry-preview">{e.content}</div>}
                    {deleteConfirm === e.id && (
                      <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }} onClick={ev => ev.stopPropagation()}>
                        <span style={{ fontSize: 12, color: "var(--red)" }}>Delete this entry?</span>
                        <button className="btn danger" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => deleteEntry(e.id)}>Delete</button>
                        <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* ── Log Communication ── */}
            {activeTab === "log" && (
              <div className="ch-card">
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginBottom: 14 }}>Log a communication for {selectedClient}</div>
                <div className="ch-form-row">
                  <div>
                    <label className="ch-label">Type</label>
                    <select className="ch-select" value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}>
                      {CH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="ch-label">Date</label>
                    <input type="date" className="ch-input" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} />
                  </div>
                </div>
                <div className="ch-form-row single">
                  <div>
                    <label className="ch-label">Subject / title</label>
                    <input type="text" className="ch-input" placeholder="Brief description of the communication" value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} />
                  </div>
                </div>
                <div className="ch-form-row single">
                  <div>
                    <label className="ch-label">Content / notes</label>
                    <textarea className="ch-textarea" placeholder="Paste email content, Teams message, call notes, or a summary…" value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} />
                  </div>
                </div>
                <div className="ch-form-row">
                  <div>
                    <label className="ch-label">Contact name(s) at client</label>
                    <input type="text" className="ch-input" placeholder="e.g. Sarah Mitchell, James Carter" value={form.contactNames} onChange={e => setForm(f => ({...f, contactNames: e.target.value}))} />
                  </div>
                  <div>
                    <label className="ch-label">Logged by</label>
                    <input type="text" className="ch-input" placeholder="Your name" value={form.loggedBy} onChange={e => setForm(f => ({...f, loggedBy: e.target.value}))} />
                  </div>
                </div>
                <div className="ch-form-row">
                  <div>
                    <label className="ch-label">Linked Jira ticket (optional)</label>
                    <input type="text" className="ch-input" placeholder="e.g. SUPPORT-12345" value={form.jiraKey || ""} onChange={e => setForm(f => ({...f, jiraKey: e.target.value.toUpperCase()}))} />
                  </div>
                  <div />
                </div>
                <button className="btn primary" style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
                  disabled={saving || !form.subject.trim()} onClick={handleLogSubmit}>
                  {saving ? "Saving…" : "✓ Save communication"}
                </button>
              </div>
            )}

            {/* ── Documents ── */}
            {activeTab === "documents" && (
              <>
                <div
                  className="ch-upload-zone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); if (e.dataTransfer.files.length) handleDocUpload(e.dataTransfer.files); }}
                >
                  <div style={{ fontSize: 28 }}>☁</div>
                  <p>{docUploading ? "Uploading…" : "Drop files here or click to upload"}</p>
                  <span>PDF, Word (.docx), Excel (.xlsx), text files - multiple files supported</span>
                  <input ref={fileInputRef} type="file" style={{ display: "none" }} accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.eml,.msg,.jpg,.jpeg,.png,.gif,.webp,.heic" multiple
                    onChange={e => { if (e.target.files.length) handleDocUpload(e.target.files); }} />
                </div>
                {docEntries.length === 0 ? (
                  <div className="ch-empty"><span style={{ fontSize: 24 }}>📂</span><div>No documents uploaded yet.</div></div>
                ) : docEntries.map(e => (
                  <div key={e.id} className="ch-doc-row">
                    <span className="ch-doc-icon">{fileIcon(e.fileName || e.subject)}</span>
                    <div className="ch-doc-info">
                      <div className="ch-doc-name">{e.fileName || e.subject}</div>
                      <div className="ch-doc-meta">
                        {e.loggedBy && `Uploaded by ${e.loggedBy} · `}
                        {formatChDate(e.date || e.createdAt)}
                        {e.fileSize ? ` · ${(e.fileSize/1024).toFixed(0)} KB` : ""}
                        {e.content && e.content.includes("Attachment from email:") ? " · 📧 from email" : ""}
                      </div>
                    </div>
                    {e.fileUrl && (
                      <a href={e.fileUrl} download={e.fileName || e.subject}
                        style={{ fontSize: 11, color: "var(--outlook)", textDecoration: "none", padding: "4px 8px",
                          border: "1px solid var(--outlook)40", borderRadius: 4, whiteSpace: "nowrap", flexShrink: 0 }}>
                        ⬇ Download
                      </a>
                    )}
                    <button className="ch-delete-btn" onClick={() => deleteEntry(e.id)}>✕</button>
                  </div>
                ))}
              </>
            )}

            {/* ── AI Query ── */}
            {activeTab === "ai" && (
              <>
                <div className="ch-ai-box">
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>Ask about {selectedClient}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
                    Searches across all logged communications, notes, and documents for this client.
                  </div>
                  <div className="ch-ai-input-row">
                    <input className="ch-input" style={{ flex: 1 }} type="text" placeholder="e.g. What are their outstanding concerns?"
                      value={aiQuestion} onChange={e => { setAiQuestion(e.target.value); setClaudeResult(null); }}
                      onKeyDown={e => { if (e.key === "Enter") { const r = clientHubAIQuery(entries, aiQuestion); setAiResult(r); setClaudeResult(null); }}} />
                    <button className="btn primary" title="Keyword search" onClick={() => { const r = clientHubAIQuery(entries, aiQuestion); setAiResult(r); setClaudeResult(null); }}>
                      🔍 Search
                    </button>
                    <button className="btn" style={{ background: "#2d1f4e", borderColor: "#c47ef0", color: "#c47ef0", whiteSpace: "nowrap" }}
                      title="Ask Claude AI for a detailed answer"
                      disabled={claudeLoading || !aiQuestion.trim()}
                      onClick={async () => {
                        if (!aiQuestion.trim()) return;
                        setClaudeLoading(true);
                        setClaudeResult(null);
                        setAiResult(null);
                        try {
                          const res = await fetch(CLIENT_HUB_ASK_API, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ question: aiQuestion, clientName: selectedClient, entries }),
                          });
                          const data = await res.json();
                          if (data.error) setClaudeResult({ error: data.error });
                          else setClaudeResult({ answer: data.answer });
                        } catch (e) {
                          setClaudeResult({ error: e.message });
                        }
                        setClaudeLoading(false);
                      }}>
                      {claudeLoading ? "⏳ Thinking…" : "✦ Ask Claude"}
                    </button>
                  </div>

                  {/* Keyword result */}
                  {aiResult && !claudeResult && (
                    <div className="ch-ai-answer">
                      <div style={{ fontSize: 10, fontWeight: 600, color: "var(--green)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>🔍 Keyword search result</div>
                      {aiResult.answer}
                      {aiResult.sources.length > 0 && (
                        <div className="ch-ai-source">Based on: {aiResult.sources.map(s => s.subject).join("; ")}</div>
                      )}
                    </div>
                  )}

                  {/* Claude AI result */}
                  {claudeResult && (
                    <div className="ch-ai-answer-claude">
                      <div className="ch-ai-claude-label">✦ Claude AI</div>
                      {claudeResult.error
                        ? <span style={{ color: "var(--red)" }}>Error: {claudeResult.error}</span>
                        : claudeResult.answer
                      }
                    </div>
                  )}

                  {claudeLoading && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 12, color: "#c47ef0" }}>
                      <span style={{ animation: "pulse 1.5s infinite" }}>✦</span> Claude is reading the client history…
                    </div>
                  )}
                </div>

                <div className="ch-card">
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Suggested questions</div>
                  <div className="ch-suggested">
                    {[
                      "Summarise our relationship with this client",
                      "What are their outstanding concerns?",
                      "Who are the key contacts at this client?",
                      "What was discussed in the most recent meeting?",
                      "Are there any unresolved issues or complaints?",
                      "What has been agreed with this client recently?",
                    ].map(q => (
                      <div key={q} className="ch-suggested-q" onClick={() => {
                        setAiQuestion(q);
                        setClaudeResult(null);
                        const r = clientHubAIQuery(entries, q);
                        setAiResult(r);
                      }}>
                        → {q}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(196,126,240,0.07)", border: "1px solid rgba(196,126,240,0.2)", borderRadius: 5, fontSize: 11, color: "#c47ef0" }}>
                    ✦ Use <strong>Ask Claude</strong> for detailed analysis - it reads all logged communications and gives a full answer.
                  </div>
                </div>
              </>
            )}

            {/* ── Relationship Health ── */}
            {activeTab === "health" && (
              <>
                <div className="ch-health-grid">
                  <div className="ch-health-card">
                    <div className="ch-health-val">{last30.length}</div>
                    <div className="ch-health-lbl">Comms (30 days)</div>
                  </div>
                  <div className="ch-health-card">
                    <div className="ch-health-val">{entries.length}</div>
                    <div className="ch-health-lbl">Total logged</div>
                  </div>
                  <div className="ch-health-card">
                    <div className="ch-health-val" style={{ fontSize: 18, color: negWords > posWords ? "var(--amber)" : "var(--green)" }}>{sentiment.label}</div>
                    <div className="ch-health-lbl">Sentiment (keyword)</div>
                  </div>
                </div>
                <div className="ch-card">
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 10 }}>Recent activity</div>
                  {entries.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--text-dim)", fontStyle: "italic" }}>No activity logged yet.</div>
                  ) : [...entries].sort((a,b) => new Date(b.date||b.createdAt) - new Date(a.date||a.createdAt)).slice(0, 8).map(e => (
                    <div key={e.id} className="ch-activity-row">
                      <span className={`ch-activity-dot ${e.type === "document" ? "ch-dot-green" : e.type === "email" || e.type === "teams" ? "ch-dot-green" : "ch-dot-amber"}`} />
                      <div>
                        <span style={{ color: "var(--text)" }}>{e.subject}</span>
                        <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-dim)" }}>{formatChDate(e.date || e.createdAt)}</span>
                        {e.loggedBy && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--text-dim)" }}>· {e.loggedBy}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ch-card" style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 10 }}>Communication breakdown</div>
                  {CH_TYPES.map(t => {
                    const count = entries.filter(e => e.type === t.value).length;
                    if (!count) return null;
                    const pct = Math.round(count / entries.length * 100);
                    return (
                      <div key={t.value} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                        <span style={{ width: 90, fontSize: 12, color: "var(--text-muted)" }}>{t.label}</span>
                        <div style={{ flex: 1, background: "var(--surface2)", borderRadius: 3, height: 8, overflow: "hidden" }}>
                          <div style={{ width: pct + "%", height: "100%", background: "var(--green)", borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 11, color: "var(--text-dim)", width: 28, textAlign: "right" }}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

          </div>
        </>
      )}

      {/* ── Entry popout ── */}
      {viewEntry && (
        <div className="ch-popout-overlay" onClick={e => e.target === e.currentTarget && setViewEntry(null)}>
          <div className="ch-popout">
            <div className="ch-popout-header">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  {chTypeBadge(viewEntry.type)}
                  {viewEntry.pinned && <span style={{ fontSize: 11, color: "gold" }}>📌 Pinned</span>}
                  {viewEntry.jiraKey && <span className="ch-jira-tag" onClick={() => window.open(`https://c365.atlassian.net/browse/${viewEntry.jiraKey}`, "_blank")}>{viewEntry.jiraKey}</span>}
                </div>
                <div className="ch-popout-title">{viewEntry.subject}</div>
              </div>
              <button onClick={() => setViewEntry(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 0 0 10px", flexShrink: 0 }}>✕</button>
            </div>
            <div className="ch-popout-meta">
              {viewEntry.date && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>📅 {new Date(viewEntry.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>}
              {viewEntry.contactNames && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>👤 {viewEntry.contactNames}</span>}
              {viewEntry.loggedBy && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Logged by {viewEntry.loggedBy}</span>}
              {viewEntry.fileSize && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>📦 {(viewEntry.fileSize / 1024).toFixed(0)} KB</span>}
            </div>
            <div className="ch-popout-body">
              {/* File open/download buttons */}
              {(viewEntry.fileUrl || viewEntry.fileData) && (
                <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                  {/* Open inline - PDF and images */}
                  {viewEntry.fileUrl && [".pdf", ".txt", ".png", ".jpg", ".jpeg"].some(ext => (viewEntry.fileName || "").toLowerCase().endsWith(ext)) && (
                    <a href={viewEntry.fileUrl} target="_blank" rel="noopener noreferrer"
                      className="btn primary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", fontSize: 12 }}>
                      👁 Open file
                    </a>
                  )}
                  {/* Open from base64 - for older entries without fileUrl */}
                  {!viewEntry.fileUrl && viewEntry.fileData && [".pdf", ".txt", ".png", ".jpg", ".jpeg"].some(ext => (viewEntry.fileName || viewEntry.subject || "").toLowerCase().endsWith(ext)) && (
                    <button className="btn primary" style={{ fontSize: 12 }} onClick={() => {
                      const mime = viewEntry.fileMime || "application/octet-stream";
                      const blob = new Blob([Uint8Array.from(atob(viewEntry.fileData), c => c.charCodeAt(0))], { type: mime });
                      window.open(URL.createObjectURL(blob), "_blank");
                    }}>👁 Open file</button>
                  )}
                  {/* Download from server URL */}
                  {viewEntry.fileUrl && (
                    <a href={viewEntry.fileUrl} download={viewEntry.fileName || viewEntry.subject}
                      className="btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", fontSize: 12 }}>
                      ⬇ Download {viewEntry.fileName || "file"}
                    </a>
                  )}
                  {/* Download from base64 - for older entries without fileUrl */}
                  {!viewEntry.fileUrl && viewEntry.fileData && (
                    <button className="btn" style={{ fontSize: 12 }} onClick={() => {
                      const mime = viewEntry.fileMime || "application/octet-stream";
                      const blob = new Blob([Uint8Array.from(atob(viewEntry.fileData), c => c.charCodeAt(0))], { type: mime });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = viewEntry.fileName || viewEntry.subject || "file";
                      a.click();
                    }}>⬇ Download {viewEntry.fileName || "file"}</button>
                  )}
                </div>
              )}

              {viewEntry.type === "document" && !viewEntry.content && !viewEntry.fileUrl ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>{fileIcon(viewEntry.fileName || viewEntry.subject)}</div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No content extracted - download the file to view it</div>
                </div>
              ) : (
                <div className="ch-popout-content">{viewEntry.content || <span style={{ color: "var(--text-dim)", fontStyle: "italic" }}>No content recorded.</span>}</div>
              )}

              {/* Extract actions button - show for any entry with substantial content */}
              {viewEntry.content && viewEntry.content.length > 50 && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                  {!extractedActions && !extracting && (
                    <button className="btn" style={{ fontSize: 12, borderColor: "#c47ef0", color: "#c47ef0" }}
                      onClick={() => extractActions(viewEntry)}>
                      ✦ Extract action items with Claude
                    </button>
                  )}
                  {extracting && <div style={{ fontSize: 12, color: "#c47ef0" }}>✦ Claude is reading the document…</div>}
                  {extractedActions !== null && !extracting && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>
                        {extractedActions.length === 0 ? "No action items found" : `${extractedActions.length} action item${extractedActions.length !== 1 ? "s" : ""} found - select to create as tasks:`}
                      </div>
                      {extractedActions.length > 0 && (
                        <>
                          <div className="ch-actions-list">
                            {extractedActions.map((a, i) => (
                              <div key={i} className="ch-action-row">
                                <input type="checkbox" checked={!!selectedActions[i]}
                                  onChange={e => setSelectedActions(prev => ({...prev, [i]: e.target.checked}))} />
                                <div className="ch-action-info">
                                  <div className="ch-action-text">{a.action}</div>
                                  <div className="ch-action-meta">
                                    {a.assignee && <span>👤 {a.assignee}</span>}
                                    {a.dueDate && <span>📅 {a.dueDate}</span>}
                                    <span className={`ch-action-priority ch-pri-${a.priority || "medium"}`}>{a.priority || "medium"}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <button className="btn primary" style={{ marginTop: 10, fontSize: 12 }}
                            disabled={creatingTasks || !Object.values(selectedActions).some(Boolean)}
                            onClick={() => {
                              // We need to pass addTask up - we'll use a custom event
                              const count = Object.values(selectedActions).filter(Boolean).length;
                              const tasks = extractedActions
                                .filter((_, i) => selectedActions[i])
                                .map(a => ({
                                  id: `ch-action-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                                  title: `[${selectedClient}] ${a.action}`,
                                  source: "manual",
                                  status: "To Start",
                                  done: false,
                                  priority: a.priority === "high" ? "high" : a.priority === "low" ? "low" : "medium",
                                  due: a.dueDate || null,
                                  description: `Action from Client Hub - ${selectedClient}\nAssignee: ${a.assignee || "Unassigned"}\nSource: ${viewEntry?.subject || ""}`,
                                  tags: ["client-hub"],
                                  createdAt: new Date().toISOString(),
                                }));
                              window.dispatchEvent(new CustomEvent("ch-create-tasks", { detail: { tasks } }));
                              setExtractedActions(null);
                              setSelectedActions({});
                              setViewEntry(null);
                            }}>
                            {creatingTasks ? "Creating…" : `✓ Create ${Object.values(selectedActions).filter(Boolean).length} task${Object.values(selectedActions).filter(Boolean).length !== 1 ? "s" : ""}`}
                          </button>
                          <button className="btn" style={{ marginTop: 10, marginLeft: 8, fontSize: 12 }}
                            onClick={() => { setExtractedActions(null); setSelectedActions({}); }}>
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="ch-popout-footer">
              <button className={`ch-pin-btn${viewEntry.pinned ? " pinned" : ""}`} style={{ opacity: 1, fontSize: 13, padding: "5px 10px", border: "1px solid var(--border)", borderRadius: 4, color: viewEntry.pinned ? "gold" : "var(--text-muted)" }}
                onClick={() => togglePin(viewEntry)}>
                📌 {viewEntry.pinned ? "Unpin" : "Pin"}
              </button>
              <div style={{ flex: 1 }} />
              <button className="btn danger" style={{ fontSize: 12 }} onClick={() => { deleteEntry(viewEntry.id); setViewEntry(null); }}>🗑 Delete</button>
              <button className="btn" style={{ fontSize: 12 }} onClick={() => setViewEntry(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Standalone Extract Actions Modal ────────────────────────────────────────

function StandaloneExtractModal({ onClose, onCreateTasks, jiraAssignees = [], config = {} }) {
  const [step, setStep]             = React.useState("upload"); // upload | extracting | results
  const [context, setContext]       = React.useState("");
  const [contextType, setContextType] = React.useState("client"); // client | department | board | other
  const [docContent, setDocContent] = React.useState("");
  const [docName, setDocName]       = React.useState("");
  const [myNames, setMyNames]       = React.useState("Phil Smith, Philip Smith, PS");
  const [myActionsOnly, setMyActionsOnly] = React.useState(true);
  const [actions, setActions]       = React.useState(null);
  const [selected, setSelected]     = React.useState({});
  const [error, setError]           = React.useState("");
  const fileRef                     = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setDocName(file.name);
    setError("");
    const ext = file.name.split(".").pop().toLowerCase();
    if (["txt","csv","md"].includes(ext)) {
      const text = await file.text();
      setDocContent(text);
    } else if (["pdf","docx","xlsx","xls","doc"].includes(ext)) {
      // Send to proxy for extraction
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(",")[1];
        try {
          const res = await fetch(CLIENT_HUB_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              clientName: "__extract_temp__",
              type: "document",
              subject: file.name,
              content: "",
              fileName: file.name,
              fileSize: file.size,
              fileMime: file.type,
              fileData: base64,
              loggedBy: "",
              date: new Date().toISOString().split("T")[0],
              createdAt: new Date().toISOString(),
            }),
          });
          if (res.ok) {
            const saved = await res.json();
            setDocContent(saved.content || "");
            // Clean up the temp entry
            if (saved.id) {
              fetch(`${CLIENT_HUB_API}/__extract_temp__/${saved.id}`, { method: "DELETE" }).catch(() => {});
            }
          }
        } catch (e) { setError("Could not extract text from file: " + e.message); }
      };
      reader.readAsDataURL(file);
    } else {
      setError("Unsupported file type. Please use PDF, Word, Excel, or text files.");
    }
  };

  const handlePaste = (e) => {
    const text = e.target.value;
    setDocContent(text);
    if (!docName) setDocName("Pasted content");
  };

  const extract = async () => {
    if (!docContent.trim()) { setError("Please upload a file or paste content first."); return; }
    setStep("extracting");
    setError("");
    try {
      const userNamesList = myNames.split(",").map(s => s.trim()).filter(Boolean);
      const res = await fetch(CLIENT_HUB_EXTRACT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: docContent,
          fileName: docName,
          clientName: context || contextType,
          userNames: userNamesList,
        }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setStep("upload"); return; }
      const acts = data.actions || [];
      setActions(acts);
      const sel = {};
      acts.forEach((a, i) => { sel[i] = myActionsOnly ? !!a.isMyAction : true; });
      setSelected(sel);
      setStep("results");
    } catch (e) { setError(e.message); setStep("upload"); }
  };

  const [parentTaskName, setParentTaskName] = React.useState("");
  const [assigneeOverrides, setAssigneeOverrides] = React.useState({}); // { [actionIndex]: displayName }
  const [sending, setSending] = React.useState(false);

  const createTasks = async () => {
    const toCreate = (actions || []).filter((_, i) => selected[i]);
    const taskTitle = parentTaskName.trim() || (context ? `[${context}] Actions from ${docName}` : `Actions from ${docName}`);
    setSending(true);

    const parentTask = {
      id: `extract-${Date.now()}`,
      title: taskTitle,
      source: "manual",
      status: "To Start",
      done: false,
      priority: "medium",
      due: toCreate.find(a => a.dueDate)?.dueDate || null,
      description: `Extracted from: ${docName}${context ? `\nContext: ${context}` : ""}\n${toCreate.length} action item${toCreate.length !== 1 ? "s" : ""} extracted`,
      subtasks: toCreate.map((a, si) => {
        const assignee = assigneeOverrides[actions.indexOf(a)] ?? a.assignee ?? "";
        return {
          id: `st-${Date.now()}-${si}`,
          text: a.action,
          done: false,
          due: a.dueDate || null,
          status: "To Start",
          assignedTo: assignee,
          note: assignee ? `Assignee: ${assignee}` : "",
        };
      }),
      tags: ["extracted", ...(context ? [context] : [])],
      createdAt: new Date().toISOString(),
    };

    onCreateTasks([parentTask]);

    // Send notification emails for actions assigned to others
    if (config.msToken) {
      const myDisplayName = config.msDisplayName || "";
      const notifyActions = toCreate.filter((a, i) => {
        const assignee = assigneeOverrides[actions.indexOf(a)] ?? a.assignee ?? "";
        return assignee && assignee !== myDisplayName;
      });

      for (const a of notifyActions) {
        const assignee = assigneeOverrides[actions.indexOf(a)] ?? a.assignee ?? "";
        try {
          const email = await lookupJiraUserEmail(config, assignee);
          if (email) {
            await sendOutlookEmail(config, {
              to: email,
              subject: `Action assigned to you: ${a.action.substring(0, 60)}`,
              body: `
                <p>Hi ${assignee.split(" ")[0]},</p>
                <p>An action has been assigned to you from <strong>${docName}</strong>${context ? ` (${context})` : ""}.</p>
                <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
                  <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Action:</td><td style="padding:6px 12px">${a.action}</td></tr>
                  ${a.dueDate ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Due date:</td><td style="padding:6px 12px">${a.dueDate}</td></tr>` : ""}
                  <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Task:</td><td style="padding:6px 12px">${taskTitle}</td></tr>
                  ${context ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Context:</td><td style="padding:6px 12px">${context}</td></tr>` : ""}
                </table>
                <p style="color:#888;font-size:12px">This action has been logged in the C365Cloud Task Manager.</p>
              `,
            });
          }
        } catch (e) { console.warn("[Extract] Email notification failed:", e.message); }
      }
    }

    setSending(false);
  };

  const contextLabels = { client: "Client", department: "Department", board: "Board meeting", other: "Other" };

  return (
    <div className="popout-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="popout-modal" style={{ maxWidth: 580 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 18 }}>✦</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>Extract Action Items</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Upload a document or paste content - Claude will extract actions and create tasks</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        {step === "extracting" && (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#c47ef0" }}>
            <div style={{ fontSize: 28, marginBottom: 12, animation: "pulse 1.5s infinite" }}>✦</div>
            <div style={{ fontSize: 14 }}>Claude is reading the document…</div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 6 }}>This may take a few seconds</div>
          </div>
        )}

        {step === "upload" && (<>
          {/* Context */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Context type</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              {Object.entries(contextLabels).map(([k, v]) => (
                <button key={k} className={`btn ${contextType === k ? "primary" : ""}`}
                  style={{ fontSize: 12, padding: "4px 12px" }}
                  onClick={() => setContextType(k)}>{v}</button>
              ))}
            </div>
            <input style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: "7px 10px", color: "var(--text)", fontSize: 13, outline: "none", fontFamily: "inherit" }}
              placeholder={`${contextLabels[contextType]} name (optional - used to prefix task titles)`}
              value={context} onChange={e => setContext(e.target.value)} />
          </div>

          {/* My name */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>My name variants</label>
            <input style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: "7px 10px", color: "var(--text)", fontSize: 13, outline: "none", fontFamily: "inherit" }}
              value={myNames} onChange={e => setMyNames(e.target.value)}
              placeholder="e.g. Phil Smith, Philip Smith, PS" />
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
              <input type="checkbox" checked={myActionsOnly} onChange={e => setMyActionsOnly(e.target.checked)} style={{ accentColor: "var(--green)" }} />
              Only create tasks for actions assigned to me
            </label>
          </div>

          {/* File upload */}
          <div style={{ border: "1px dashed var(--border)", borderRadius: 7, padding: 16, textAlign: "center", cursor: "pointer", marginBottom: 10, transition: "all 0.15s" }}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>☁</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{docName ? `✓ ${docName}` : "Drop a file here or click to upload"}</div>
            <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>PDF, Word (.docx), Excel (.xlsx), text files</div>
            <input ref={fileRef} type="file" style={{ display: "none" }} accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
              onChange={e => handleFile(e.target.files[0])} />
          </div>

          <div style={{ textAlign: "center", fontSize: 11, color: "var(--text-dim)", margin: "8px 0" }}>- or paste content below -</div>

          <textarea style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: "8px 10px", color: "var(--text)", fontSize: 12, outline: "none", fontFamily: "inherit", resize: "vertical", minHeight: 80, lineHeight: 1.5 }}
            placeholder="Paste meeting minutes, email threads, notes…"
            value={docContent} onChange={handlePaste} />

          {error && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 8 }}>⚠ {error}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            <button className="btn" style={{ fontSize: 12 }} onClick={onClose}>Cancel</button>
            <button className="btn primary" style={{ fontSize: 12, background: "#3d1f5e", borderColor: "#c47ef0", color: "#c47ef0" }}
              disabled={!docContent.trim()} onClick={extract}>
              ✦ Extract with Claude
            </button>
          </div>
        </>)}

        {step === "results" && actions !== null && (<>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
              {actions.length === 0 ? "No action items found" : `${actions.length} action item${actions.length !== 1 ? "s" : ""} found`}
            </div>
            {actions.length > 0 && myActionsOnly && (
              <span style={{ fontSize: 11, color: "var(--green)", background: "rgba(71,172,76,0.1)", border: "1px solid rgba(71,172,76,0.3)", borderRadius: 3, padding: "1px 7px" }}>
                {actions.filter(a => a.isMyAction).length} assigned to you
              </span>
            )}
            <button className="btn" style={{ fontSize: 10, padding: "2px 8px", marginLeft: "auto" }} onClick={() => setStep("upload")}>← Back</button>
          </div>

          {actions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-dim)", fontSize: 13 }}>
              No action items could be identified in this document.
            </div>
          ) : (<>
            <div style={{ fontSize: 11, color: "var(--text-dim)", marginBottom: 10 }}>
              Each selected action becomes a separate task. Sub-steps become subtasks. Tick to select which to create.
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {actions.map((a, i) => (
                <div key={i} className="ch-action-row" style={{ opacity: myActionsOnly && !a.isMyAction ? 0.45 : 1, flexDirection: "column", alignItems: "stretch", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <input type="checkbox" checked={!!selected[i]} onChange={e => setSelected(prev => ({...prev, [i]: e.target.checked}))}
                      style={{ marginTop: 3, flexShrink: 0 }} />
                    <div className="ch-action-info" style={{ flex: 1 }}>
                      <div className="ch-action-text">{a.action}</div>
                      <div className="ch-action-meta">
                        {a.dueDate && <span>📅 {a.dueDate}</span>}
                        {(a.subtasks||[]).length > 0 && <span>📋 {a.subtasks.length} sub-step{a.subtasks.length !== 1 ? "s" : ""}</span>}
                        <span className={`ch-action-priority ch-pri-${a.priority || "medium"}`}>{a.priority || "medium"}</span>
                      </div>
                    </div>
                  </div>
                  {selected[i] && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 26 }}>
                      <span style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>👤 Assign to:</span>
                      <select style={{ flex: 1, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, padding: "4px 8px", color: "var(--text)", fontSize: 12, outline: "none" }}
                        value={assigneeOverrides[i] ?? a.assignee ?? ""}
                        onChange={e => setAssigneeOverrides(prev => ({ ...prev, [i]: e.target.value }))}>
                        <option value="">- Unassigned -</option>
                        {jiraAssignees.map(name => <option key={name} value={name}>{name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {myActionsOnly && actions.some(a => !a.isMyAction) && (
              <button className="btn" style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 6 }}
                onClick={() => { const s = {}; actions.forEach((_, i) => { s[i] = true; }); setSelected(s); setMyActionsOnly(false); }}>
                Show all {actions.length} actions
              </button>
            )}

            {myActionsOnly && actions.some(a => !a.isMyAction) && (
              <button className="btn" style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 6 }}
                onClick={() => { const s = {}; actions.forEach((_, i) => { s[i] = true; }); setSelected(s); setMyActionsOnly(false); }}>
                Show all {actions.length} actions
              </button>
            )}

            {/* Parent task name */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 5 }}>
                Parent task name
              </label>
              <input style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 5, padding: "7px 10px", color: "var(--text)", fontSize: 13, outline: "none", fontFamily: "inherit" }}
                value={parentTaskName}
                onChange={e => setParentTaskName(e.target.value)}
                placeholder={context ? `[${context}] Actions from ${docName}` : `Actions from ${docName}`} />
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>
                Selected actions will become subtasks of this task
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
              <button className="btn" style={{ fontSize: 12 }} onClick={onClose}>Cancel</button>
              <button className="btn primary" style={{ fontSize: 12 }}
                disabled={!Object.values(selected).some(Boolean) || sending}
                onClick={createTasks}>
                {sending ? "Creating & notifying…" : `✓ Create task with ${Object.values(selected).filter(Boolean).length} action${Object.values(selected).filter(Boolean).length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </>)}
        </>)}
      </div>
    </div>
  );
}

// Manual tasks rendered in Gantt / Grid layouts (reuses roadmap CSS)
function ManualBoardView({ tasks, viewMode, onSelectTask, onUpdateTask }) {
  const STATUS_COLOUR = { "To Start": "#4a6070", "In Progress": "#F1901E", "Waiting Response": "#fbbf24", "Done": "#47ac4c" };
  const [expandedIds, setExpandedIds] = React.useState({});
  const toggleExpand = (id, e) => { if (e) e.stopPropagation(); setExpandedIds(prev => ({ ...prev, [id]: !prev[id] })); };
  // Measure the timeline pixel width so dependency arrows can use pixel coords (SVG paths cannot use % units)
  const timelineRef = React.useRef(null);
  const rowsContainerRef = React.useRef(null);
  const [timelineW, setTimelineW] = React.useState(0);
  const rowRefs = React.useRef({});
  const [rowGeom, setRowGeom] = React.useState({});
  // A signature that changes whenever anything affecting bar/row geometry changes,
  // so we re-measure the timeline width and redraw arrows in sync with the new layout.
  const geometrySig = JSON.stringify(tasks.map(t => [t.id, t.done, t.status, t.startDate, t.due, t.dependsOn, (t.subtasks || []).map(s => [s.id, s.done, s.startDate, s.due, s.dependsOn])]));
  React.useLayoutEffect(() => {
    const measure = () => {
      if (timelineRef.current) setTimelineW(timelineRef.current.offsetWidth);
      // Measure each visible row's BAR centre relative to the rows container.
      // Bars sit 18px below each row's top (task: top8+h20/2, subtask: top11+h14/2),
      // so this stays aligned regardless of how tall a row grows when its title wraps.
      const geom = {};
      Object.entries(rowRefs.current).forEach(([id, el]) => {
        if (el) geom[id] = el.offsetTop + 18;
      });
      setRowGeom(prev => {
        // Avoid needless state churn if nothing changed
        const keys = Object.keys(geom);
        if (keys.length === Object.keys(prev).length && keys.every(k => prev[k] === geom[k])) return prev;
        return geom;
      });
    };
    // Measure now, next frame, and after short delays - covers font/layout settling
    measure();
    const raf = requestAnimationFrame(measure);
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 250);
    // ResizeObserver catches any later reflow (wrapping resolving, panel resize, etc.)
    let ro;
    if (typeof ResizeObserver !== "undefined" && rowsContainerRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(rowsContainerRef.current);
      if (timelineRef.current) ro.observe(timelineRef.current);
    }
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(raf);
      clearTimeout(t1); clearTimeout(t2);
      if (ro) ro.disconnect();
    };
  }, [viewMode, expandedIds, geometrySig]);

  // Gantt setup - 9-month window from current month
  const ganttMonths = React.useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const months = [];
    for (let i = 0; i < 9; i++) {
      const d = new Date(start); d.setMonth(d.getMonth() + i);
      months.push({ label: d.toLocaleString("en-GB", { month: "short", year: "2-digit" }), start: new Date(d), end: new Date(d.getFullYear(), d.getMonth() + 1, 0) });
    }
    return months;
  }, []);
  const gStart = ganttMonths[0].start.getTime();
  const gEnd = ganttMonths[ganttMonths.length - 1].end.getTime();
  const gRange = gEnd - gStart;
  const todayPct = ((Date.now() - gStart) / gRange * 100).toFixed(2);

  const barFor = (dueStr, startStr, colour, depDueStr) => {
    if (!dueStr) return null;
    const due = new Date(dueStr).getTime();
    // Prefer an explicit start date; otherwise default to 7 days before due
    let start = startStr ? new Date(startStr).getTime() : due - 7 * 86400000;
    // If this item depends on another, it can't start before the predecessor's due date
    if (depDueStr) {
      const depDue = new Date(depDueStr).getTime();
      if (depDue > start) start = depDue;
    }
    const left = Math.max(0, (Math.min(start, due) - gStart) / gRange * 100);
    const right = Math.min(100, (due - gStart) / gRange * 100);
    return { left: left + "%", width: Math.max(right - left, 1) + "%", background: colour };
  };

  const taskById = React.useMemo(() => {
    const m = {};
    tasks.forEach(t => { m[String(t.id)] = t; });
    return m;
  }, [tasks]);

  // Order a list of items by dependency chain, assigning a depth (indent) level.
  // Items with no predecessor come first at depth 0; each dependent follows its
  // predecessor immediately, indented one level deeper. depKey is the field name
  // that holds the id of the item this one depends on.
  const orderByDependency = (items, depKey) => {
    const byId = {};
    items.forEach(it => { byId[String(it.id)] = it; });
    // children map: predecessorId -> [items that depend on it]
    const children = {};
    const roots = [];
    items.forEach(it => {
      const dep = it[depKey] != null ? String(it[depKey]) : null;
      if (dep && byId[dep]) {
        (children[dep] = children[dep] || []).push(it);
      } else {
        roots.push(it);
      }
    });
    const result = [];
    const visited = new Set();
    const walk = (item, depth) => {
      if (visited.has(String(item.id))) return; // guard against cycles
      visited.add(String(item.id));
      result.push({ item, depth });
      (children[String(item.id)] || []).forEach(child => walk(child, depth + 1));
    };
    roots.forEach(r => walk(r, 0));
    // Append any items not reached (e.g. part of a cycle) at depth 0
    items.forEach(it => { if (!visited.has(String(it.id))) result.push({ item: it, depth: 0 }); });
    return result;
  };

  const orderedTasks = React.useMemo(() => orderByDependency(tasks, "dependsOn"), [tasks]);

  if (viewMode === "gantt") {
    const ROW_H = 36;
    const LABEL_W = 380;
    // Build a flat list of visible rows with bar geometry for arrow drawing
    const visibleRows = [];
    orderedTasks.forEach(({ item: t, depth }) => {
      const stColour = STATUS_COLOUR[t.done ? "Done" : (t.status || "To Start")];
      const predecessor = t.dependsOn ? taskById[String(t.dependsOn)] : null;
      const bar = barFor(t.due, t.startDate || t.createdAt, stColour, predecessor?.due);
      const datedSubs = (t.subtasks || []).filter(s => s.due);
      const isExpanded = expandedIds[t.id];
      visibleRows.push({ kind: "task", id: String(t.id), item: t, depth, bar, predecessorId: predecessor ? String(predecessor.id) : null, stColour, datedSubs, isExpanded, predecessor });
      if (isExpanded) {
        orderByDependency(datedSubs, "dependsOn").forEach(({ item: s, depth: sDepth }) => {
          const subColour = STATUS_COLOUR[s.done ? "Done" : (s.status || "To Start")] || "#4a6070";
          const subPredecessor = s.dependsOn ? (t.subtasks || []).find(x => String(x.id) === String(s.dependsOn)) : null;
          const sbar = barFor(s.due, s.startDate || null, subColour, subPredecessor?.due);
          visibleRows.push({ kind: "subtask", id: "s_" + String(s.id), item: s, parentTask: t, depth: sDepth, bar: sbar, predecessorId: subPredecessor ? "s_" + String(subPredecessor.id) : null, subColour, subPredecessor });
        });
      }
    });
    // Index rows by id for arrow lookup
    const rowIndexById = {};
    visibleRows.forEach((r, i) => { rowIndexById[r.id] = i; });
    // Build arrow segments: from predecessor bar-end to dependent bar-start (pixel coords)
    // Y positions use MEASURED row centres (rowGeom) so wrapping/taller rows stay aligned.
    const arrows = [];
    if (timelineW > 0 && Object.keys(rowGeom).length > 0) {
      visibleRows.forEach((r) => {
        if (!r.predecessorId || !r.bar) return;
        const pIdx = rowIndexById[r.predecessorId];
        if (pIdx === undefined) return;
        const pRow = visibleRows[pIdx];
        if (!pRow.bar) return;
        const fromY = rowGeom[pRow.id];
        const toY = rowGeom[r.id];
        if (fromY === undefined || toY === undefined) return;
        const fromX = (parseFloat(pRow.bar.left) + parseFloat(pRow.bar.width)) / 100 * timelineW; // px end of predecessor
        const toX = parseFloat(r.bar.left) / 100 * timelineW; // px start of dependent
        arrows.push({ fromX, fromY, toX, toY, key: r.id });
      });
    }
    const totalHeight = visibleRows.length * ROW_H;

    return (
      <div className="rm-gantt">
        <div className="rm-gantt-header">
          <div className="rm-gantt-label-col">Task</div>
          <div className="rm-gantt-months" ref={timelineRef} style={{ position: "relative" }}>
            {ganttMonths.map(m => <div key={m.label} className="rm-gantt-month">{m.label}</div>)}
            {todayPct >= 0 && todayPct <= 100 && <div className="rm-gantt-today rm-gantt-today-label" style={{ left: todayPct + "%" }} />}
          </div>
        </div>
        <div ref={rowsContainerRef} style={{ position: "relative" }}>
          {/* Dependency arrows overlay (positioned over the timeline area only) */}
          {arrows.length > 0 && (
            <svg style={{ position: "absolute", top: 0, left: LABEL_W, width: timelineW, height: totalHeight, pointerEvents: "none", zIndex: 4, overflow: "visible" }}>
              {arrows.map(a => {
                // Elbow sits 12px to the left of the dependent bar's start, so the final
                // segment always runs left->right into the start and the arrowhead points right.
                const elbowX = a.toX - 12;
                const outX = Math.min(a.fromX + 8, elbowX); // step right from source, but not past the elbow
                return (
                  <g key={a.key}>
                    <polyline
                      points={`${a.fromX},${a.fromY} ${outX},${a.fromY} ${outX},${a.toY} ${a.toX},${a.toY}`}
                      fill="none" stroke="#2E75B6" strokeWidth="1.4" opacity="0.8" />
                    <polygon points={`${a.toX},${a.toY - 4} ${a.toX + 7},${a.toY} ${a.toX},${a.toY + 4}`} fill="#2E75B6" opacity="0.85" />
                  </g>
                );
              })}
            </svg>
          )}
          {visibleRows.map((r, i) => r.kind === "task" ? (() => {
            const t = r.item, depth = r.depth, bar = r.bar, predecessor = r.predecessor, datedSubs = r.datedSubs, isExpanded = r.isExpanded;
            return (
              <div key={r.id} ref={el => { rowRefs.current[r.id] = el; }} className="rm-gantt-row" onClick={() => onSelectTask(t.id)}>
                <div className="rm-gantt-row-label" style={{ display: "flex", alignItems: "flex-start", gap: 6, paddingLeft: 12 + depth * 18 }}>
                  {depth > 0 && <span style={{ color: "#2E75B6", flexShrink: 0, fontSize: 10, marginTop: 1 }}>↳</span>}
                  {datedSubs.length > 0 ? (
                    <button onClick={(e) => toggleExpand(t.id, e)}
                      style={{ background: "none", border: "none", color: "#2E75B6", cursor: "pointer", fontSize: 9, padding: 0, width: 12, flexShrink: 0, lineHeight: 1, marginTop: 2 }}
                      title={isExpanded ? "Collapse subtasks" : "Expand subtasks"}>
                      {isExpanded ? "▼" : "▶"}
                    </button>
                  ) : <span style={{ width: 12, flexShrink: 0 }} />}
                  <span className="rm-gantt-row-title" title={t.title}>{t.title}</span>
                  {datedSubs.length > 0 && <span style={{ fontSize: 9, color: "var(--text-dim)", flexShrink: 0, marginTop: 1 }}>({datedSubs.length})</span>}
                  {predecessor && <span style={{ fontSize: 9, color: "#2E75B6", flexShrink: 0, marginTop: 1 }} title={`Depends on: ${predecessor.title}`}>🔗</span>}
                </div>
                <div className="rm-gantt-timeline">
                  {todayPct >= 0 && todayPct <= 100 && <div className="rm-gantt-today" style={{ left: todayPct + "%" }} />}
                  {bar ? <div className="rm-gantt-bar" style={bar} title={predecessor ? `${t.status || "To Start"} · due ${t.due} · after "${predecessor.title}"` : `${t.status || "To Start"} · due ${t.due}`}>{t.done ? "✓" : ""}</div>
                       : <div style={{ height: "100%", display: "flex", alignItems: "center", paddingLeft: 8 }}><span style={{ fontSize: 9, color: "var(--text-dim)" }}>No due date{predecessor ? ` (after "${predecessor.title}")` : ""}</span></div>}
                </div>
              </div>
            );
          })() : (() => {
            const s = r.item, sDepth = r.depth, sbar = r.bar, subPredecessor = r.subPredecessor, t = r.parentTask;
            return (
              <div key={r.id} ref={el => { rowRefs.current[r.id] = el; }} className="rm-gantt-row" onClick={() => onSelectTask(t.id)} style={{ background: "var(--surface)" }}>
                <div className="rm-gantt-row-label" style={{ paddingLeft: 30 + sDepth * 16, display: "flex", alignItems: "flex-start", gap: 4 }}>
                  <span className="rm-gantt-row-title" style={{ fontSize: 10, color: "var(--text-muted)" }} title={s.text}>↳ {s.text}</span>
                  {subPredecessor && <span style={{ fontSize: 8, color: "#2E75B6", flexShrink: 0, marginTop: 1 }} title={`Depends on: ${subPredecessor.text}`}>🔗</span>}
                </div>
                <div className="rm-gantt-timeline">
                  {todayPct >= 0 && todayPct <= 100 && <div className="rm-gantt-today" style={{ left: todayPct + "%" }} />}
                  {sbar && <div className="rm-gantt-bar" style={{ ...sbar, height: 14, top: 11, opacity: 0.85 }} title={subPredecessor ? `${s.status || "To Start"} · due ${s.due} · after "${subPredecessor.text}"` : `${s.status || "To Start"} · due ${s.due}`}>{s.done ? "✓" : ""}</div>}
                </div>
              </div>
            );
          })())}
        </div>
      </div>
    );
  }

  // Grid
  return (
    <div className="rm-grid-wrap">
      <table className="rm-grid">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th><th style={{ maxWidth: 220 }}>Title</th><th>Status</th><th>Priority</th><th>Assignee</th><th>Start</th><th>Due</th><th>Subtasks</th><th>Created By</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map(t => {
            const subs = t.subtasks || [];
            const isExpanded = expandedIds[t.id];
            const stColour = STATUS_COLOUR[t.done ? "Done" : (t.status || "To Start")];
            return (
              <React.Fragment key={t.id}>
                <tr style={{ cursor: "pointer" }}>
                  <td style={{ textAlign: "center" }}>
                    {subs.length > 0 && (
                      <button onClick={(e) => toggleExpand(t.id, e)}
                        style={{ background: "none", border: "none", color: "#2E75B6", cursor: "pointer", fontSize: 9, padding: 0 }}
                        title={isExpanded ? "Collapse subtasks" : "Expand subtasks"}>
                        {isExpanded ? "▼" : "▶"}
                      </button>
                    )}
                  </td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell" style={{ maxWidth: 220, whiteSpace: "normal", wordBreak: "break-word" }}>{t.title}</div></td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell"><span className="rm-grid-status" style={{ background: stColour + "25", color: stColour }}>{t.done ? "Done" : (t.status || "To Start")}</span></div></td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell">{capitalise(t.priority)}</div></td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell">{t.assignedTo || ""}</div></td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell">{t.startDate ? formatDate(t.startDate) : ""}</div></td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell">{t.due ? formatDate(t.due) : ""}</div></td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell">{subs.length > 0 ? `${subs.filter(s => s.done).length}/${subs.length}` : ""}</div></td>
                  <td onClick={() => onSelectTask(t.id)}><div className="rm-grid-cell">{t.createdBy || ""}</div></td>
                </tr>
                {isExpanded && subs.map(s => {
                  const subColour = STATUS_COLOUR[s.done ? "Done" : (s.status || "To Start")] || "#4a6070";
                  const subPred = s.dependsOn ? subs.find(x => String(x.id) === String(s.dependsOn)) : null;
                  return (
                    <tr key={s.id} style={{ cursor: "pointer", background: "var(--surface)" }} onClick={() => onSelectTask(t.id)}>
                      <td></td>
                      <td><div className="rm-grid-cell" style={{ fontSize: 11, color: "var(--text-muted)", paddingLeft: 16 }}>↳ {s.text}{subPred && <span style={{ color: "#2E75B6", marginLeft: 5 }} title={`Depends on: ${subPred.text}`}>🔗</span>}</div></td>
                      <td><div className="rm-grid-cell"><span className="rm-grid-status" style={{ background: subColour + "25", color: subColour, fontSize: 9 }}>{s.jiraLinkStatus || s.status || (s.done ? "Done" : "To Start")}</span></div></td>
                      <td></td>
                      <td><div className="rm-grid-cell" style={{ fontSize: 11 }}>{s.assignedTo || ""}</div></td>
                      <td><div className="rm-grid-cell" style={{ fontSize: 11 }}>{s.startDate ? formatDate(s.startDate) : ""}</div></td>
                      <td><div className="rm-grid-cell" style={{ fontSize: 11 }}>{s.due ? formatDate(s.due) : ""}</div></td>
                      <td></td>
                      <td></td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── SLA Report View ──────────────────────────────────────────────────────────
// Ported from the standalone Jira SLA Report tool. Reuses the Task Manager's Jira
// connection (config) and proxy. Fetches SUPPORT tickets for a client + date range,
// classifies by status / priority / SLA, shows metric cards + ticket table, exports.
function parseSlaDetail(slaVal) {
  const result = { status: "Within SLA", breached: false, remainingMillis: null };
  if (!slaVal) return { status: "N/A", breached: false, remainingMillis: null };
  try {
    const completed = slaVal.completedCycles || [];
    const ongoing = slaVal.ongoingCycle || null;
    if (completed.length) {
      const last = completed[completed.length - 1];
      result.breached = !!last.breached;
      result.status = last.breached ? "Outside SLA" : "Within SLA";
      result.remainingMillis = last.remainingTime?.millis ?? null;
    } else if (ongoing) {
      result.breached = !!ongoing.breached;
      result.status = ongoing.breached ? "Outside SLA" : "Still Open";
      result.remainingMillis = ongoing.remainingTime?.millis ?? null;
    } else {
      result.status = "N/A";
    }
  } catch { result.status = "N/A"; }
  return result;
}

// Lightweight dependency-free SVG charts for the SLA report.
const SLA_CHART_COLOURS = ["#2E75B6", "#1B3A6B", "#47ac4c", "#f1901e", "#e32529", "#5b4fcf", "#00a499", "#768692", "#c47ef0", "#36454f", "#e6a817", "#8bc34a"];
function SlaDonut({ data, size = 160 }) {
  // data: [{label, value, colour}]
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const cx = size / 2, cy = size / 2, r = size / 2 - 6, inner = r * 0.62;
  let angle = -Math.PI / 2;
  const arcs = data.filter(d => d.value > 0).map((d, i) => {
    const frac = d.value / total;
    const a0 = angle, a1 = angle + frac * Math.PI * 2;
    angle = a1;
    const large = (a1 - a0) > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const xi1 = cx + inner * Math.cos(a1), yi1 = cy + inner * Math.sin(a1);
    const xi0 = cx + inner * Math.cos(a0), yi0 = cy + inner * Math.sin(a0);
    const dPath = `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
    return <path key={i} d={dPath} fill={d.colour} />;
  });
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{arcs}
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="20" fontWeight="800" fill="var(--text)">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="9" fill="var(--text-dim)">TOTAL</text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11 }}>
        {data.filter(d => d.value > 0).map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: d.colour, flexShrink: 0 }} />
            <span style={{ color: "var(--text-muted)" }}>{d.label}</span>
            <span style={{ color: "var(--text)", fontWeight: 600, marginLeft: "auto" }}>{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlaBarChart({ data, colour = "#2E75B6", height = 180, horizontal = false }) {
  // data: [{label, value}] (or value can be array for grouped -> use SlaGroupedBar)
  const max = Math.max(1, ...data.map(d => d.value));
  if (horizontal) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
            <span style={{ width: 130, textAlign: "right", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.label}>{d.label}</span>
            <div style={{ flex: 1, background: "var(--surface2)", borderRadius: 3, height: 16, position: "relative" }}>
              <div style={{ width: `${d.value / max * 100}%`, background: colour, height: "100%", borderRadius: 3, minWidth: 2 }} />
            </div>
            <span style={{ width: 30, color: "var(--text)", fontWeight: 600 }}>{d.value}</span>
          </div>
        ))}
      </div>
    );
  }
  const VW = 600, VH = height;
  const padL = 30, padR = 12, padT = 16, padB = 30;
  const plotW = VW - padL - padR, plotH = VH - padT - padB;
  const n = data.length;
  const slot = plotW / Math.max(n, 1);
  const bw = slot * 0.6;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
      {data.map((d, i) => {
        const bh = (d.value / max) * plotH;
        const x = padL + i * slot + (slot - bw) / 2;
        const y = padT + plotH - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={bw} height={bh} fill={colour} rx="2" />
            <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize="11" fill="var(--text)" fontWeight="700">{d.value}</text>
            <text x={x + bw / 2} y={VH - 10} textAnchor="middle" fontSize="11" fill="var(--text-dim)">{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function SlaGroupedBar({ periods, series, height = 200 }) {
  // periods: [labels], series: [{name, colour, values[]}]
  const allVals = series.flatMap(s => s.values);
  const max = Math.max(1, ...allVals);
  const VW = 600, VH = height;
  const padL = 30, padR = 12, padT = 16, padB = 28;
  const plotW = VW - padL - padR, plotH = VH - padT - padB;
  const n = Math.max(periods.length, 1);
  const groupW = plotW / n;
  const barW = (groupW * 0.8) / series.length;
  const labelStep = Math.ceil(n / 10);
  return (
    <div>
      <svg width="100%" height={height} viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
        {[0, 0.5, 1].map((g, gi) => {
          const gy = padT + (1 - g) * plotH;
          return <line key={gi} x1={padL} y1={gy} x2={VW - padR} y2={gy} stroke="var(--border)" strokeWidth="1" opacity="0.4" />;
        })}
        {periods.map((p, pi) => series.map((s, si) => {
          const bh = (s.values[pi] / max) * plotH;
          const x = padL + pi * groupW + (groupW * 0.1) + si * barW;
          const y = padT + plotH - bh;
          return <rect key={`${pi}-${si}`} x={x} y={y} width={barW * 0.9} height={bh} fill={s.colour} rx="1" />;
        }))}
        {periods.map((p, pi) => (pi % labelStep === 0 || pi === n - 1) ? (
          <text key={pi} x={padL + pi * groupW + groupW / 2} y={VH - 8} textAnchor="middle" fontSize="10" fill="var(--text-dim)">{p}</text>
        ) : null)}
      </svg>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 6, fontSize: 11 }}>
        {series.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.colour }} />
            <span style={{ color: "var(--text-muted)" }}>{s.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlaLineChart({ points, height = 200, colour = "#47ac4c" }) {
  // points: [{label, value}] value 0-100 or null
  const valid = (points || []).filter(p => p.value !== null && p.value !== undefined);
  if (!valid.length) return <div style={{ color: "var(--text-dim)", fontSize: 12, textAlign: "center", padding: 20 }}>No SLA trend data</div>;

  // Use a real (non-stretched) coordinate space so text renders at true proportions.
  const VW = 600, VH = height;
  const padL = 34, padR = 16, padT = 16, padB = 26;
  const plotW = VW - padL - padR;
  const plotH = VH - padT - padB;
  const max = 100, min = 0;
  const n = points.length;
  const xAt = (i) => n > 1 ? padL + (i / (n - 1)) * plotW : padL + plotW / 2;
  const yAt = (v) => padT + (1 - (v - min) / (max - min)) * plotH;

  const coords = points.map((p, i) => (p.value === null || p.value === undefined) ? null : { x: xAt(i), y: yAt(p.value), v: p.value, label: p.label });
  const linePts = coords.filter(Boolean);
  const line = linePts.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");
  const area = linePts.length ? `${line} L ${linePts[linePts.length - 1].x.toFixed(1)} ${(padT + plotH).toFixed(1)} L ${linePts[0].x.toFixed(1)} ${(padT + plotH).toFixed(1)} Z` : "";

  // Thin the x labels if there are many periods so they do not overlap
  const labelStep = Math.ceil(n / 8);

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg width="100%" height={height} viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
        {/* gridlines + y labels */}
        {[0, 25, 50, 75, 100].map(g => {
          const gy = yAt(g);
          return (
            <g key={g}>
              <line x1={padL} y1={gy} x2={VW - padR} y2={gy} stroke="var(--border)" strokeWidth="1" opacity="0.5" />
              <text x={padL - 6} y={gy + 3} textAnchor="end" fontSize="10" fill="var(--text-dim)">{g}%</text>
            </g>
          );
        })}
        {/* filled area */}
        {area && <path d={area} fill={colour} opacity="0.12" />}
        {/* line */}
        <path d={line} fill="none" stroke={colour} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* points + value labels */}
        {coords.filter(Boolean).map((c, i) => (
          <g key={i}>
            <circle cx={c.x} cy={c.y} r="3" fill={colour} />
            <text x={c.x} y={c.y - 8} textAnchor="middle" fontSize="10" fill="var(--text)" fontWeight="700">{c.v}%</text>
          </g>
        ))}
        {/* x axis labels (period), thinned to avoid overlap */}
        {points.map((p, i) => (i % labelStep === 0 || i === n - 1) ? (
          <text key={i} x={xAt(i)} y={VH - 8} textAnchor="middle" fontSize="10" fill="var(--text-dim)">{p.label}</text>
        ) : null)}
      </svg>
    </div>
  );
}

// ─── Jira Queue ──────────────────────────────────────────────────────────────
// Replaces the four Jira automation emails (ED Sign-off, Awaiting SDM, Awaiting PC,
// MIF Review) with a live, filterable, sortable, pivotable grid plus Excel export.
// Reuses the existing Jira connection (config) and /api/proxy passthrough.
function JiraQueueView({ config = {}, initialPreset = "ed-signoff", initialMe = false }) {
  const hasJira = !!(config.jiraUrl && config.jiraEmail && config.jiraToken);
  const base = (config.jiraUrl || "").replace(/\/$/, "");
  const auth = hasJira ? btoa(`${config.jiraEmail}:${config.jiraToken}`) : "";
  const PROXY_BASE = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "http://localhost:3001/api/proxy?url=" : "/api/proxy?url=";
  const myAccountId = localStorage.getItem("taskflow_jira_accountid") || "";
  const myName = localStorage.getItem("taskflow_jira_displayname") || "";

  // Preset definitions. personField: which user field the "Me / person / All" filter targets.
  const PRESETS = {
    "ed-signoff": {
      label: "ED Sign-off",
      // ED project tickets at "Waiting for Sign Off", filtered by SDM.
      statuses: [JQ_STATUS.edSignoff],
      projects: ["ED"],
      personField: "sdm", personDefault: "all",
      columns: ["key", "summary", "status", "sdm", "created", "updated", "assignee", "affectedClients"],
      group: ["status", "sdm"],
    },
    "awaiting-sdm": {
      label: "Awaiting SDM",
      statuses: [JQ_STATUS.sdm],
      personField: "sdm", personDefault: "me",
      columns: ["key", "summary", "created", "updated", "assignee", "affectedClients", "reason"],
      group: [],
    },
    "awaiting-pc": {
      label: "Awaiting PC",
      statuses: [JQ_STATUS.pc],
      personField: "pc", personDefault: "me",
      columns: ["key", "summary", "created", "updated", "assignee", "affectedClients", "reason"],
      group: [],
    },
    "mif-review": {
      label: "MIF Review",
      statuses: [JQ_STATUS.sdm],
      projects: ["MIF"],
      personField: "sdm", personDefault: "all",
      columns: ["key", "propertyRef", "mifType", "formName", "complianceModule", "affectedClients", "sdm"],
      group: [],
    },
  };

  // All available columns (key -> {label, field builder})
  const ALL_COLUMNS = [
    { id: "key", label: "Key" },
    { id: "summary", label: "Summary" },
    { id: "project", label: "Project" },
    { id: "status", label: "Status" },
    { id: "created", label: "Created" },
    { id: "updated", label: "Updated" },
    { id: "assignee", label: "Assignee" },
    { id: "sdm", label: "SDM" },
    { id: "pc", label: "Project Coordinator" },
    { id: "affectedClients", label: "Affected Clients" },
    { id: "reason", label: "Reason for SDM/PC Review" },
    { id: "propertyRef", label: "Property Reference" },
    { id: "mifType", label: "MIF Type" },
    { id: "formName", label: "Form Name" },
    { id: "complianceModule", label: "Compliance Module" },
  ];

  const [presetKey, setPresetKey] = React.useState(initialPreset in PRESETS ? initialPreset : "ed-signoff");
  const preset = PRESETS[presetKey];
  const [personMode, setPersonMode] = React.useState(initialMe ? "me" : (preset.personDefault || "all")); // me | all | <accountId>
  const [statusFilter, setStatusFilter] = React.useState(preset.statuses);
  const [projectFilter, setProjectFilter] = React.useState(preset.projects || []);
  const [visibleCols, setVisibleCols] = React.useState(preset.columns);
  const [showTime, setShowTime] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [sortKey, setSortKey] = React.useState("created");
  const [sortDir, setSortDir] = React.useState("desc");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [statusList, setStatusList] = React.useState([]);
  const [projectList, setProjectList] = React.useState([]);
  const [people, setPeople] = React.useState([]); // {accountId, displayName} seen in results
  const [showCols, setShowCols] = React.useState(false);
  const [pivotOpen, setPivotOpen] = React.useState(false);
  const [pivotRow, setPivotRow] = React.useState("status");
  const [pivotCol, setPivotCol] = React.useState("sdm");

  // When preset changes, reset dependent filters to its defaults
  const applyPreset = (key) => {
    const p = PRESETS[key];
    setPresetKey(key);
    setStatusFilter(p.statuses);
    setProjectFilter(p.projects || []);
    setVisibleCols(p.columns);
    setPersonMode(p.personDefault || "all");
    setSortKey("created"); setSortDir("desc");
  };

  const [roster, setRoster] = React.useState([]); // combined SDM + PC people {accountId, displayName}

  // Load status + project lists, and the combined SDM/PC roster, once
  React.useEffect(() => {
    if (!hasJira) return;
    const h = { Authorization: "Basic " + auth, Accept: "application/json" };
    fetch(PROXY_BASE + encodeURIComponent(`${base}/rest/api/3/status`), { headers: h })
      .then(r => r.ok ? r.json() : []).then(d => {
        const names = [...new Set((Array.isArray(d) ? d : []).map(s => s.name).filter(Boolean))].sort();
        setStatusList(names);
      }).catch(() => {});
    fetch(PROXY_BASE + encodeURIComponent(`${base}/rest/api/3/project/search?maxResults=100`), { headers: h })
      .then(r => r.ok ? r.json() : { values: [] }).then(d => {
        setProjectList((d.values || []).map(p => ({ key: p.key, name: p.name })));
      }).catch(() => {});

    // Build the SDM/PC roster: scan tickets that have an SDM or PC set and collect the
    // distinct people from both fields. This gives the full list of SDMs and PCs in use.
    (async () => {
      const rosterMap = {};
      const sdmNum = JQ_FIELDS.sdm.replace("customfield_", "");
      const pcNum = JQ_FIELDS.pc.replace("customfield_", "");
      const jql = `(cf[${sdmNum}] is not EMPTY OR cf[${pcNum}] is not EMPTY) ORDER BY updated DESC`;
      let nextPageToken = null;
      for (let page = 0; page < 10; page++) {
        const tok = nextPageToken ? `&nextPageToken=${encodeURIComponent(nextPageToken)}` : "";
        const url = `${base}/rest/api/3/search/jql?jql=${encodeURIComponent(jql)}&fields=${JQ_FIELDS.sdm},${JQ_FIELDS.pc}&maxResults=100${tok}`;
        let d;
        try {
          const r = await fetch(PROXY_BASE + encodeURIComponent(url), { headers: h });
          if (!r.ok) break; d = await r.json();
        } catch { break; }
        (d.issues || []).forEach(iss => {
          [iss.fields?.[JQ_FIELDS.sdm], iss.fields?.[JQ_FIELDS.pc]].forEach(u => {
            if (u && u.accountId) rosterMap[u.accountId] = u.displayName || u.accountId;
          });
        });
        nextPageToken = d.nextPageToken;
        if (!nextPageToken || !(d.issues || []).length) break;
      }
      setRoster(Object.entries(rosterMap).map(([accountId, displayName]) => ({ accountId, displayName })).filter(p => !jqExcluded(p.displayName)).sort((a, b) => a.displayName.localeCompare(b.displayName)));
    })();
  }, [hasJira, base, auth]);

  const quote = (s) => `"${String(s).replace(/"/g, '\\"')}"`;
  const buildJql = () => {
    const parts = [];
    if (statusFilter.length) parts.push(`status in (${statusFilter.map(quote).join(",")})`);
    if (projectFilter.length) parts.push(`project in (${projectFilter.map(quote).join(",")})`);
    // Person filter: the selected person is the SDM OR the PC on the ticket (combined).
    if (personMode !== "all") {
      const acct = personMode === "me" ? myAccountId : personMode;
      const sdmNum = JQ_FIELDS.sdm.replace("customfield_", "");
      const pcNum = JQ_FIELDS.pc.replace("customfield_", "");
      if (acct) parts.push(`(cf[${sdmNum}] = ${quote(acct)} OR cf[${pcNum}] = ${quote(acct)})`);
    }
    return parts.join(" AND ") + ` ORDER BY ${sortField(sortKey)} ${sortDir.toUpperCase()}`;
  };
  const sortField = (k) => {
    const map = { created: "created", updated: "updated", status: "status", assignee: "assignee", key: "key", summary: "summary", project: "project" };
    return map[k] || "created";
  };

  const FETCH_FIELDS = ["summary","status","project","created","updated","assignee",
    JQ_FIELDS.sdm, JQ_FIELDS.pc, JQ_FIELDS.affectedClients, JQ_FIELDS.reason,
    JQ_FIELDS.propertyRef, JQ_FIELDS.mifType, JQ_FIELDS.formName, JQ_FIELDS.complianceModule];

  // Normalise any Jira field value to a display string (or array for multi)
  const fieldText = (v) => {
    if (v === null || v === undefined) return "";
    if (Array.isArray(v)) return v.map(fieldText).filter(Boolean).join(", ");
    if (typeof v === "object") return v.displayName || v.value || v.name || v.key || "";
    return String(v);
  };
  const fieldList = (v) => { // multi-value as array
    if (v === null || v === undefined) return [];
    if (Array.isArray(v)) return v.map(x => fieldText(x)).filter(Boolean);
    const t = fieldText(v); return t ? [t] : [];
  };

  const runQuery = React.useCallback(async () => {
    if (!hasJira) { setError("Connect Jira in Config first."); return; }
    setLoading(true); setError("");
    try {
      const H = { Authorization: "Basic " + auth, Accept: "application/json" };
      let all = [];
      const jql = buildJql();
      let nextPageToken = null;
      for (let page = 0; page < 40; page++) {
        const tok = nextPageToken ? `&nextPageToken=${encodeURIComponent(nextPageToken)}` : "";
        const url = `${base}/rest/api/3/search/jql?jql=${encodeURIComponent(jql)}&fields=${encodeURIComponent(FETCH_FIELDS.join(","))}&maxResults=100${tok}`;
        const res = await fetch(PROXY_BASE + encodeURIComponent(url), { headers: H });
        if (!res.ok) throw new Error(`Jira ${res.status}: ${(await res.text()).slice(0, 180)}`);
        const d = await res.json();
        all = all.concat(d.issues || []);
        nextPageToken = d.nextPageToken;
        if (!nextPageToken || !(d.issues || []).length) break;
      }
      const peopleMap = {};
      const mapped = all.map(iss => {
        const f = iss.fields || {};
        const sdmObj = f[JQ_FIELDS.sdm], pcObj = f[JQ_FIELDS.pc];
        [sdmObj, pcObj, f.assignee].forEach(u => { if (u && u.accountId) peopleMap[u.accountId] = u.displayName || u.accountId; });
        return {
          key: iss.key,
          summary: f.summary || "",
          project: f.project?.name || f.project?.key || "",
          status: f.status?.name || "",
          created: f.created || "",
          updated: f.updated || "",
          assignee: f.assignee?.displayName || "",
          sdm: fieldText(sdmObj),
          pc: fieldText(pcObj),
          affectedClients: fieldList(f[JQ_FIELDS.affectedClients]),
          reason: fieldText(f[JQ_FIELDS.reason]),
          propertyRef: fieldText(f[JQ_FIELDS.propertyRef]),
          mifType: fieldText(f[JQ_FIELDS.mifType]),
          formName: fieldText(f[JQ_FIELDS.formName]),
          complianceModule: fieldText(f[JQ_FIELDS.complianceModule]),
        };
      });
      setPeople(Object.entries(peopleMap).map(([accountId, displayName]) => ({ accountId, displayName })).filter(p => !jqExcluded(p.displayName)).sort((a, b) => a.displayName.localeCompare(b.displayName)));
      setRows(mapped);
    } catch (e) { setError(e.message || "Query failed"); setRows([]); }
    finally { setLoading(false); }
  }, [hasJira, base, auth, statusFilter, projectFilter, personMode, presetKey, sortKey, sortDir]);

  React.useEffect(() => { runQuery(); /* live on load + when filters change */ }, [runQuery]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const mon = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
      const base2 = `${String(d.getDate()).padStart(2, "0")}/${mon}/${d.getFullYear()}`;
      return showTime ? `${base2} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : base2;
    } catch { return String(iso).slice(0, 10); }
  };
  const cellValue = (row, col) => {
    if (col === "created" || col === "updated") return fmtDate(row[col]);
    if (col === "affectedClients") return (row.affectedClients || []).join(", ");
    return row[col] ?? "";
  };

  // Client-side search + sort
  const displayRows = React.useMemo(() => {
    let list = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r => visibleCols.some(c => String(cellValue(r, c)).toLowerCase().includes(q)));
    }
    const arr = [...list].sort((a, b) => {
      let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      if (Array.isArray(av)) av = av.join(", "); if (Array.isArray(bv)) bv = bv.join(", ");
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [rows, search, sortKey, sortDir, visibleCols, showTime]);

  const toggleSort = (k) => { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };
  const toggleCol = (id) => setVisibleCols(cols => cols.includes(id) ? cols.filter(c => c !== id) : [...ALL_COLUMNS.map(c => c.id).filter(c => c === id || cols.includes(c))]);

  // Age bucket for pivot
  const ageBucket = (iso) => {
    if (!iso) return "Unknown";
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days <= 1) return "0-1 days"; if (days <= 3) return "2-3 days"; if (days <= 7) return "4-7 days";
    if (days <= 14) return "8-14 days"; if (days <= 30) return "15-30 days"; return "30+ days";
  };
  const PIVOT_DIMS = [
    { id: "project", label: "Project" }, { id: "sdm", label: "SDM" }, { id: "pc", label: "Project Coordinator" },
    { id: "status", label: "Status" }, { id: "assignee", label: "Assignee" }, { id: "affectedClients", label: "Affected Clients" },
    { id: "reason", label: "Reason" }, { id: "mifType", label: "MIF Type" }, { id: "formName", label: "Form Name" },
    { id: "complianceModule", label: "Compliance Module" }, { id: "ageCreated", label: "Age (Created)" }, { id: "ageUpdated", label: "Age (Updated)" },
  ];
  const dimValues = (row, dim) => {
    if (dim === "affectedClients") return row.affectedClients && row.affectedClients.length ? row.affectedClients : ["(none)"];
    if (dim === "ageCreated") return [ageBucket(row.created)];
    if (dim === "ageUpdated") return [ageBucket(row.updated)];
    const v = row[dim]; return [v ? String(v) : "(none)"];
  };
  const pivot = React.useMemo(() => {
    if (!pivotOpen) return null;
    const rowKeys = new Set(), colKeys = new Set(), cell = {};
    displayRows.forEach(r => {
      dimValues(r, pivotRow).forEach(rk => {
        rowKeys.add(rk);
        dimValues(r, pivotCol).forEach(ck => { colKeys.add(ck); const k = rk + "||" + ck; cell[k] = (cell[k] || 0) + 1; });
      });
    });
    return { rows: [...rowKeys].sort(), cols: [...colKeys].sort(), cell };
  }, [pivotOpen, pivotRow, pivotCol, displayRows]);

  const exportExcel = async () => {
    const ensure = () => new Promise((resolve, reject) => {
      if (window.XLSX) return resolve(window.XLSX);
      const s = document.createElement("script"); s.src = "https://cdn.sheetjs.com/xlsx-0.20.2/package/dist/xlsx.full.min.js";
      s.onload = () => resolve(window.XLSX); s.onerror = () => reject(new Error("Excel lib failed")); document.head.appendChild(s);
    });
    try {
      const XLSX = await ensure();
      const wb = XLSX.utils.book_new();
      const header = visibleCols.map(c => ALL_COLUMNS.find(x => x.id === c)?.label || c);
      const data = displayRows.map(r => visibleCols.map(c => cellValue(r, c)));
      const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
      ws["!cols"] = visibleCols.map(c => ({ wch: c === "summary" ? 50 : c === "affectedClients" ? 24 : 16 }));
      ws["!freeze"] = { xSplit: 0, ySplit: 1 };
      XLSX.utils.book_append_sheet(wb, ws, "Queue");
      if (pivot) {
        const pHeader = ["", ...pivot.cols, "Total"];
        const pData = pivot.rows.map(rk => {
          let total = 0; const cells = pivot.cols.map(ck => { const v = pivot.cell[rk + "||" + ck] || 0; total += v; return v; });
          return [rk, ...cells, total];
        });
        const colTotals = pivot.cols.map(ck => pivot.rows.reduce((s, rk) => s + (pivot.cell[rk + "||" + ck] || 0), 0));
        pData.push(["Total", ...colTotals, colTotals.reduce((a, b) => a + b, 0)]);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([pHeader, ...pData]), "Pivot");
      }
      const dstr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Jira_Queue_${preset.label.replace(/[^a-z0-9]/gi, "_")}_${dstr}.xlsx`);
    } catch (e) { alert("Excel export failed: " + e.message); }
  };

  const colLabel = (id) => ALL_COLUMNS.find(c => c.id === id)?.label || id;

  return (
    <div style={{ padding: 18, overflowY: "auto", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--text)" }}>📋 Jira Queue</h2>
        <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Live from Jira</span>
      </div>

      {!hasJira && (
        <div style={{ padding: 14, background: "rgba(227,37,41,0.08)", border: "1px solid #e3252940", borderRadius: 8, color: "var(--text)", marginBottom: 16 }}>
          Jira isn't connected. Open ⚙ Config and add your Jira URL, email and API token first.
        </div>
      )}

      {/* Preset tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {Object.keys(PRESETS).map(k => (
          <button key={k} className={`btn ${presetKey === k ? "primary" : ""}`} style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => applyPreset(k)}>
            {PRESETS[k].label}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12, padding: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>SDM/PC</label>
          <select className="form-input" value={personMode} onChange={e => setPersonMode(e.target.value)} style={{ minWidth: 180 }}>
            <option value="all">All</option>
            <option value="me">Me ({myName || "current user"})</option>
            {(roster.length ? roster : people).map(p => <option key={p.accountId} value={p.accountId}>{p.displayName}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 180 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>Search</label>
          <input className="form-input" placeholder="Filter visible rows…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <label style={{ fontSize: 11, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 5 }}>
          <input type="checkbox" checked={showTime} onChange={e => setShowTime(e.target.checked)} /> Show time
        </label>
        <button className="btn" style={{ fontSize: 12 }} onClick={() => setShowCols(s => !s)}>⚙ Columns</button>
        <button className="btn" style={{ fontSize: 12 }} onClick={() => setPivotOpen(o => !o)}>{pivotOpen ? "▦ Hide pivot" : "▦ Pivot"}</button>
        <button className="btn" style={{ fontSize: 12 }} onClick={runQuery} disabled={loading}>{loading ? "⏳" : "↻ Refresh"}</button>
        <button className="btn primary" style={{ fontSize: 12 }} onClick={exportExcel} disabled={!rows.length}>📊 Export Excel</button>
        <span style={{ fontSize: 11, color: "var(--text-dim)", alignSelf: "center", marginLeft: "auto" }}>{displayRows.length} ticket{displayRows.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Status + project multi-selects */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>Statuses</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4, maxHeight: 76, overflowY: "auto", padding: 6, border: "1px solid var(--border)", borderRadius: 6 }}>
            {(statusList.length ? statusList : statusFilter).map(s => (
              <label key={s} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 3, background: statusFilter.includes(s) ? "rgba(46,117,182,0.15)" : "transparent", border: "1px solid var(--border)", borderRadius: 10, padding: "1px 8px", cursor: "pointer" }}>
                <input type="checkbox" checked={statusFilter.includes(s)} onChange={() => setStatusFilter(f => f.includes(s) ? f.filter(x => x !== s) : [...f, s])} style={{ display: "none" }} />
                {s}
              </label>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>Projects</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4, maxHeight: 76, overflowY: "auto", padding: 6, border: "1px solid var(--border)", borderRadius: 6 }}>
            {projectList.map(p => (
              <label key={p.key} title={p.name} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 3, background: projectFilter.includes(p.key) ? "rgba(46,117,182,0.15)" : "transparent", border: "1px solid var(--border)", borderRadius: 10, padding: "1px 8px", cursor: "pointer" }}>
                <input type="checkbox" checked={projectFilter.includes(p.key)} onChange={() => setProjectFilter(f => f.includes(p.key) ? f.filter(x => x !== p.key) : [...f, p.key])} style={{ display: "none" }} />
                {p.key}
              </label>
            ))}
            {!projectList.length && <span style={{ fontSize: 11, color: "var(--text-dim)" }}>Loading…</span>}
          </div>
        </div>
      </div>

      {showCols && (
        <div style={{ marginBottom: 12, padding: 10, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {ALL_COLUMNS.map(c => (
            <label key={c.id} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, color: "var(--text-muted)" }}>
              <input type="checkbox" checked={visibleCols.includes(c.id)} onChange={() => setVisibleCols(cols => cols.includes(c.id) ? cols.filter(x => x !== c.id) : [...cols, c.id])} /> {c.label}
            </label>
          ))}
        </div>
      )}

      {error && <div style={{ padding: 12, background: "rgba(227,37,41,0.08)", border: "1px solid #e3252940", borderRadius: 8, color: "#e32529", marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {pivotOpen && pivot && (
        <div style={{ marginBottom: 16, padding: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Pivot</span>
            <label style={{ fontSize: 11, color: "var(--text-dim)" }}>Rows</label>
            <select className="form-input" value={pivotRow} onChange={e => setPivotRow(e.target.value)} style={{ fontSize: 12 }}>
              {PIVOT_DIMS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <label style={{ fontSize: 11, color: "var(--text-dim)" }}>Columns</label>
            <select className="form-input" value={pivotCol} onChange={e => setPivotCol(e.target.value)} style={{ fontSize: 12 }}>
              {PIVOT_DIMS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                <th style={{ padding: "5px 9px", textAlign: "left", background: "var(--surface2)", border: "1px solid var(--border)" }}></th>
                {pivot.cols.map(c => <th key={c} style={{ padding: "5px 9px", background: "var(--surface2)", border: "1px solid var(--border)", whiteSpace: "nowrap" }}>{c}</th>)}
                <th style={{ padding: "5px 9px", background: "var(--surface2)", border: "1px solid var(--border)", fontWeight: 700 }}>Total</th>
              </tr></thead>
              <tbody>
                {pivot.rows.map(rk => {
                  let tot = 0;
                  return (
                    <tr key={rk}>
                      <td style={{ padding: "5px 9px", border: "1px solid var(--border)", fontWeight: 600, whiteSpace: "nowrap" }}>{rk}</td>
                      {pivot.cols.map(ck => { const v = pivot.cell[rk + "||" + ck] || 0; tot += v; return <td key={ck} style={{ padding: "5px 9px", border: "1px solid var(--border)", textAlign: "center", color: v ? "var(--text)" : "var(--text-dim)" }}>{v || ""}</td>; })}
                      <td style={{ padding: "5px 9px", border: "1px solid var(--border)", textAlign: "center", fontWeight: 700 }}>{tot}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grid */}
      <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              {visibleCols.map(c => (
                <th key={c} onClick={() => toggleSort(c)} style={{ padding: "8px 10px", textAlign: "left", cursor: "pointer", whiteSpace: "nowrap", color: "var(--text-muted)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>
                  {colLabel(c)}{sortKey === c ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map(r => (
              <tr key={r.key} style={{ borderBottom: "1px solid var(--border)" }}>
                {visibleCols.map(c => (
                  <td key={c} style={{ padding: "6px 10px", whiteSpace: c === "summary" ? "normal" : "nowrap", maxWidth: c === "summary" ? 360 : undefined }}>
                    {c === "key"
                      ? <a href={`${base}/browse/${r.key}`} target="_blank" rel="noreferrer" style={{ color: "#4c9aff", fontFamily: "monospace" }}>{r.key}</a>
                      : cellValue(r, c)}
                  </td>
                ))}
              </tr>
            ))}
            {!displayRows.length && !loading && (
              <tr><td colSpan={visibleCols.length} style={{ padding: 24, textAlign: "center", color: "var(--text-dim)" }}>No tickets match this queue.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SLAReportView({ config = {} }) {
  const [clients, setClients] = React.useState([]);
  const [client, setClient] = React.useState("");
  const [startDate, setStartDate] = React.useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = React.useState(() => new Date().toISOString().split("T")[0]);
  const [extraJql, setExtraJql] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [report, setReport] = React.useState(null); // { tickets, counts, jql, total }
  const [notes, setNotes] = React.useState("");
  const [sortKey, setSortKey] = React.useState("created_iso");
  const [sortDir, setSortDir] = React.useState("desc");
  const [activePeriod, setActivePeriod] = React.useState("3M");

  // Quick period: end date is always the last day of the PREVIOUS month; start
  // date moves back N months from that end. E.g. in August, "3M" = 01 May to 31 July.
  const applyPeriod = (months) => {
    const now = new Date();
    // Last day of previous month
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    // Start: first day, going back (months-1) further from the start of the end month
    const start = new Date(end.getFullYear(), end.getMonth() - (months - 1), 1);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setStartDate(iso(start));
    setEndDate(iso(end));
    setActivePeriod(months + "M");
  };
  // Set the default 3M window on first mount
  React.useEffect(() => { applyPeriod(3); /* eslint-disable-line */ }, []);

  const hasJira = !!(config.jiraUrl && config.jiraEmail && config.jiraToken);
  const base = (config.jiraUrl || "").replace(/\/$/, "");
  const auth = hasJira ? btoa(`${config.jiraEmail}:${config.jiraToken}`) : "";
  const PROXY_BASE = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "http://localhost:3001/api/proxy?url=" : "/api/proxy?url=";

  // Populate the client dropdown from the authoritative client list (same source
  // as the Client Hub), so it isn't limited to whatever appears on recent tickets.
  React.useEffect(() => {
    const list = JIRA_CLIENT_LIST.filter(c => c !== "All Clients").sort((a, b) => a.localeCompare(b));
    setClients(list);
  }, []);

  const buildJql = () => {
    const parts = ['project = "SUPPORT"'];
    if (startDate) parts.push(`created >= "${startDate}"`);
    if (endDate) parts.push(`created <= "${endDate} 23:59"`);
    if (client) {
      const esc = client.replace(/"/g, '\\"');
      // Filter on the Affected Clients field (customfield_10435) only.
      parts.push(`cf[10435] = "${esc}"`);
    }
    if (extraJql.trim()) parts.push(`(${extraJql.trim()})`);
    return parts.join(" AND ") + " ORDER BY created DESC";
  };

  const SLA_FIELDS = ["key","summary","status","priority","created","resolutiondate","issuetype","reporter","assignee","resolution","duedate","organizations","customfield_10202","customfield_10207","customfield_10435","customfield_10054","customfield_10053"];

  const flattenCascading = (v) => {
    if (!v) return "";
    if (Array.isArray(v)) return v.map(flattenCascading).filter(Boolean).join(", ");
    if (typeof v === "object") {
      const parts = [];
      if (v.value) parts.push(String(v.value));
      if (v.child && v.child.value) parts.push(String(v.child.value));
      return parts.join(" - ");
    }
    return String(v);
  };
  const flattenMulti = (v) => {
    if (!v) return "";
    if (Array.isArray(v)) return v.map(i => (typeof i === "object" ? (i.name || i.value || i.displayName || "") : String(i))).filter(Boolean).join(", ");
    if (typeof v === "object") return v.name || v.value || v.displayName || "";
    return String(v);
  };
  const fmtDate = (val) => {
    if (!val) return "";
    try { return new Date(val).toLocaleDateString("en-GB"); } catch { return String(val).slice(0, 10); }
  };

  const runReport = async () => {
    if (!hasJira) { setError("Connect Jira in Config first."); return; }
    setLoading(true); setError(""); setReport(null);
    try {
      const jql = buildJql();
      let allIssues = [];
      let nextPageToken = null;
      for (let page = 0; page < 40; page++) {
        const tokenParam = nextPageToken ? `&nextPageToken=${encodeURIComponent(nextPageToken)}` : "";
        const url = `${base}/rest/api/3/search/jql?jql=${encodeURIComponent(jql)}&fields=${encodeURIComponent(SLA_FIELDS.join(","))}&maxResults=100${tokenParam}`;
        const res = await fetch(PROXY_BASE + encodeURIComponent(url), { headers: { Authorization: "Basic " + auth, Accept: "application/json" } });
        if (!res.ok) { throw new Error(`Jira ${res.status}: ${(await res.text()).slice(0, 200)}`); }
        const d = await res.json();
        allIssues = allIssues.concat(d.issues || []);
        nextPageToken = d.nextPageToken;
        if (!nextPageToken || !(d.issues || []).length) break;
      }

      const resolvedStatuses = ["resolved","done","closed","completed"];
      const withDev = "With Development team", awaitingClient = "Awaiting Client Response", triage = "Triage Waiting Information";
      const counts = { created:0, resolved:0, open:0, with_dev:0, awaiting_client:0, triage:0, p1:0, p2:0, p3:0, p4:0,
        fr_within:0, fr_violated:0, fr_open:0, fr_na:0, res_within:0, res_violated:0, res_open:0, res_na:0 };
      const tickets = [];

      allIssues.forEach(issue => {
        const f = issue.fields || {};
        const statusObj = f.status || {};
        const statusName = statusObj.name || "";
        const statusCat = (statusObj.statusCategory || {}).key || "";
        const priority = (f.priority || {}).name || "";
        const created = f.created || "";
        const resolved = f.resolutiondate || "";
        const issueType = (f.issuetype || {}).name || "";
        const assignee = (f.assignee || {}).displayName || "";
        const reporter = (f.reporter || {}).emailAddress || (f.reporter || {}).displayName || "";
        const changeFlag = flattenMulti(f.customfield_10207);
        const ticketType = changeFlag.toLowerCase() === "yes" ? "CHANGE" : "SOP";
        const category = flattenCascading(f.customfield_10202);
        const affectedClients = flattenMulti(f.customfield_10435);
        const organizations = flattenMulti(f.organizations);
        const resolutionName = (f.resolution || {}).name || "";
        const fr = parseSlaDetail(f.customfield_10054);
        const res = parseSlaDetail(f.customfield_10053);

        counts.created++;
        const isResolved = !!resolved || statusCat === "done" || resolvedStatuses.includes(statusName.toLowerCase());
        counts[isResolved ? "resolved" : "open"]++;
        if (statusName === withDev) counts.with_dev++;
        if (statusName === awaitingClient) counts.awaiting_client++;
        if (statusName === triage) counts.triage++;
        const p = priority.toLowerCase();
        if (["p1","critical","blocker","highest","1"].includes(p)) counts.p1++;
        else if (["p2","high","2"].includes(p)) counts.p2++;
        else if (["p3","medium","normal","3"].includes(p)) counts.p3++;
        else if (["p4","low","lowest","minor","trivial","4"].includes(p)) counts.p4++;
        [["fr",fr],["res",res]].forEach(([prefix, sla]) => {
          if (!sla || !sla.status || sla.status === "N/A") counts[`${prefix}_na`]++;
          else if (sla.status === "Within SLA") counts[`${prefix}_within`]++;
          else if (sla.status === "Outside SLA") counts[`${prefix}_violated`]++;
          else if (sla.status === "Still Open") counts[`${prefix}_open`]++;
          else counts[`${prefix}_na`]++;
        });

        tickets.push({
          key: issue.key || "", summary: f.summary || "", status: statusName, priority,
          created: fmtDate(created), created_iso: created ? created.slice(0, 10) : "",
          resolved: fmtDate(resolved), resolved_iso: resolved ? resolved.slice(0, 10) : "",
          is_resolved: isResolved, issue_type: issueType, type: ticketType,
          category, reporter, assignee, affected_clients: affectedClients, organizations,
          fr_sla: fr.status, res_sla: res.status,
          res_remaining_millis: res.remainingMillis, resolution_name: resolutionName,
        });
      });
      ["fr","res"].forEach(prefix => {
        const b = counts[`${prefix}_within`] + counts[`${prefix}_violated`] + counts[`${prefix}_open`];
        counts[`${prefix}_pct`] = b ? Math.round(counts[`${prefix}_within`] / b * 1000) / 10 : 0;
      });

      // ---- SLA Summary (SOP breakdown), ported from the original tool's renderSlaSummary ----
      const parseIso = (s) => { if (!s) return null; const d = new Date(s + "T00:00:00"); return isNaN(d) ? null : d; };
      const total = tickets.length;
      let sopOpen = 0, nonSop = 0, sameDay = 0, within2 = 0, paused = 0, noResp = 0, lapsed1 = 0, lapsed2 = 0;
      const nonSopCats = {};
      tickets.forEach(t => {
        if (!t.is_resolved && t.res_sla !== "Outside SLA") { sopOpen++; return; }
        const cat = String(t.category || "").toUpperCase();
        if (t.type === "CHANGE" || cat.includes("CHANGE REQUEST") || cat.includes("TEMPLATING")) {
          nonSop++;
          const label = t.category || t.type || "Non-SOP";
          nonSopCats[label] = (nonSopCats[label] || 0) + 1;
          return;
        }
        if (t.res_sla === "Outside SLA") {
          const rem = t.res_remaining_millis;
          if (rem !== null && rem !== undefined) {
            const breach = -rem;
            const businessDayMs = 8 * 3600 * 1000;
            if (breach > 0 && breach < businessDayMs) lapsed1++;
            else if (breach >= businessDayMs && breach < businessDayMs * 2) lapsed2++;
          }
          return;
        }
        const cd = parseIso(t.created_iso), rd = parseIso(t.resolved_iso);
        if (!cd || !rd) { paused++; return; }
        const deltaDays = Math.round((rd - cd) / 86400000);
        if (deltaDays === 0) sameDay++;
        else if (deltaDays <= 2) within2++;
        else if (String(t.resolution_name || "").toLowerCase().includes("autoclos")) noResp++;
        else paused++;
      });
      const sop = Math.max(total - sopOpen - nonSop, 0);
      const withinSop = sameDay + within2 + paused + noResp;
      const lapsedSop = Math.max(sop - withinSop, 0);
      const lapsedOther = Math.max(lapsedSop - lapsed1 - lapsed2, 0);
      const slaSummary = { total, open: sopOpen, nonSop, sop, sameDay, within2, paused, noResp,
        within: withinSop, lapsed: lapsedSop, lapsed1, lapsed2, lapsedOther,
        nonSopCats: Object.entries(nonSopCats).sort((a, b) => b[1] - a[1]) };

      // ---- Chart aggregations ----
      // Status distribution (top 12)
      const statusMap = {};
      tickets.forEach(t => { statusMap[t.status] = (statusMap[t.status] || 0) + 1; });
      const statusData = Object.entries(statusMap).sort((a, b) => b[1] - a[1]).slice(0, 12);
      // Category distribution (split on commas, top 10)
      const catMap = {};
      tickets.forEach(t => {
        String(t.category || "").split(",").map(c => c.trim()).filter(Boolean).forEach(c => { catMap[c] = (catMap[c] || 0) + 1; });
      });
      const categoryData = Object.entries(catMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
      // Created vs Resolved over time (by month YYYY-MM), plus SLA trend
      const periodMap = {}; // period -> {created, resolved, slaWithin, slaTotal}
      const monthKey = (iso) => iso ? iso.slice(0, 7) : null;
      tickets.forEach(t => {
        const pk = monthKey(t.created_iso);
        if (pk) {
          periodMap[pk] = periodMap[pk] || { created: 0, resolved: 0, slaWithin: 0, slaTotal: 0 };
          periodMap[pk].created++;
          // SLA trend: count this ticket toward its created period
          const frOk = t.fr_sla === "Within SLA", resOk = t.res_sla === "Within SLA";
          const frBad = t.fr_sla === "Outside SLA", resBad = t.res_sla === "Outside SLA";
          if (frOk || resOk || frBad || resBad) {
            periodMap[pk].slaTotal++;
            if (!frBad && !resBad) periodMap[pk].slaWithin++;
          }
        }
        const rk = monthKey(t.resolved_iso);
        if (rk) { periodMap[rk] = periodMap[rk] || { created: 0, resolved: 0, slaWithin: 0, slaTotal: 0 }; periodMap[rk].resolved++; }
      });
      const periods = Object.keys(periodMap).sort();
      const timeData = periods.map(p => ({
        period: p, created: periodMap[p].created, resolved: periodMap[p].resolved,
        slaPct: periodMap[p].slaTotal ? Math.round(periodMap[p].slaWithin / periodMap[p].slaTotal * 100) : null,
      }));

      setReport({ tickets, counts, jql, total: allIssues.length, slaSummary, statusData, categoryData, timeData });
    } catch (e) {
      setError(e.message || "Report failed");
    } finally {
      setLoading(false);
    }
  };

  const sortedTickets = React.useMemo(() => {
    if (!report) return [];
    const arr = [...report.tickets];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [report, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const exportExcel = async () => {
    if (!report) return;
    const ensure = () => new Promise((resolve, reject) => {
      if (window.XLSX) return resolve(window.XLSX);
      const s = document.createElement("script");
      s.src = "https://cdn.sheetjs.com/xlsx-0.20.2/package/dist/xlsx.full.min.js";
      s.onload = () => resolve(window.XLSX); s.onerror = () => reject(new Error("Excel lib failed"));
      document.head.appendChild(s);
    });
    try {
      const XLSX = await ensure();
      const c = report.counts;
      const wb = XLSX.utils.book_new();

      // ---- Summary sheet: title, meta, metric grid mirroring the on-screen cards ----
      const S = [];
      S.push(["C365Cloud Jira SLA Report"]);
      S.push([]);
      S.push(["Client", client || "All Clients"]);
      S.push(["Period", `${fmtDate(startDate)} to ${fmtDate(endDate)}`]);
      S.push(["Project", "SUPPORT"]);
      S.push(["Generated", new Date().toLocaleString("en-GB")]);
      S.push(["Total tickets", report.total]);
      S.push([]);
      // Metric grid, laid out as two rows of six label/value pairs like the screen
      S.push(["Volume & Status", "", "", "", "", ""]);
      S.push(["Created", "Resolved", "Open", "With Development", "Awaiting Client", "Triage"]);
      S.push([c.created, c.resolved, c.open, c.with_dev, c.awaiting_client, c.triage]);
      S.push([]);
      S.push(["Priority & SLA", "", "", "", "", ""]);
      S.push(["P1 Critical", "P2 High", "P3 Medium", "P4 Low", "First Response SLA", "Resolution SLA"]);
      S.push([c.p1, c.p2, c.p3, c.p4, c.fr_pct + "%", c.res_pct + "%"]);
      S.push([]);
      if (notes) { S.push(["Notes"]); S.push([notes]); S.push([]); }
      S.push(["C365Cloud | c365cloud.co.uk | Confidential"]);

      const wsSum = XLSX.utils.aoa_to_sheet(S);
      wsSum["!cols"] = [{ wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 18 }];
      // Merge the title and section headers across the grid width
      wsSum["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },   // title
        { s: { r: 8, c: 0 }, e: { r: 8, c: 5 } },   // Volume & Status header
        { s: { r: 12, c: 0 }, e: { r: 12, c: 5 } }, // Priority & SLA header
      ];
      // Style the cells SheetJS can style (title, section headers, metric labels/values)
      const NAVY = "1B3A6B", BLUE = "2E75B6";
      const styleCell = (addr, style) => { if (wsSum[addr]) wsSum[addr].s = style; };
      styleCell("A1", { font: { bold: true, sz: 16, color: { rgb: NAVY } } });
      ["A9", "A13"].forEach(a => styleCell(a, { font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: NAVY } } }));
      // Metric label rows (row 10 and 14, cols A-F) and value rows (11 and 15)
      ["A10","B10","C10","D10","E10","F10","A14","B14","C14","D14","E14","F14"].forEach(a =>
        styleCell(a, { font: { bold: true, sz: 9, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: BLUE } }, alignment: { horizontal: "center" } }));
      ["A11","B11","C11","D11","E11","F11","A15","B15","C15","D15","E15","F15"].forEach(a =>
        styleCell(a, { font: { bold: true, sz: 18, color: { rgb: NAVY } }, alignment: { horizontal: "center" } }));
      XLSX.utils.book_append_sheet(wb, wsSum, "Summary");

      // ---- Tickets sheet: full detail, frozen header, autofilter ----
      const header = ["Key","Summary","Status","Priority","Type","Category","Created","Resolved","Assignee","Reporter","Affected Clients","First Response SLA","Resolution SLA"];
      const rows = sortedTickets.map(t => [t.key,t.summary,t.status,t.priority,t.type,t.category,t.created,t.resolved,t.assignee,t.reporter,t.affected_clients,t.fr_sla,t.res_sla]);
      const wsT = XLSX.utils.aoa_to_sheet([header, ...rows]);
      wsT["!cols"] = [{wch:12},{wch:52},{wch:20},{wch:10},{wch:8},{wch:22},{wch:12},{wch:12},{wch:20},{wch:24},{wch:24},{wch:18},{wch:18}];
      wsT["!freeze"] = { xSplit: 0, ySplit: 1 };
      wsT["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: header.length - 1 } }) };
      // Style header row navy
      for (let i = 0; i < header.length; i++) {
        const a = XLSX.utils.encode_cell({ r: 0, c: i });
        if (wsT[a]) wsT[a].s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: NAVY } } };
      }
      XLSX.utils.book_append_sheet(wb, wsT, "Tickets");

      // ---- SLA Summary sheet (SOP breakdown), mirroring the on-screen section ----
      if (report.slaSummary) {
        const s = report.slaSummary;
        const pctOf = (n, d) => d ? (Math.round(n / d * 1000) / 10) + "%" : "0%";
        const SS = [
          ["SLA Summary (SOP Breakdown)"], [],
          ["Metric", "Count", "%"],
          ["Total Received In Period", s.total, "100%"],
          ["Non-SOP / Change", s.nonSop, pctOf(s.nonSop, s.total)],
          ["Total SOP", s.sop, pctOf(s.sop, s.total)],
          ["Total SOP Completed Within SLA", s.within, pctOf(s.within, s.sop || s.total)],
          ["  Same Day Completion", s.sameDay, pctOf(s.sameDay, s.sop || s.total)],
          ["  Within 2-Day SLA", s.within2, pctOf(s.within2, s.sop || s.total)],
          ["  Further Info Required / Paused", s.paused, pctOf(s.paused, s.sop || s.total)],
          ["  Completed - No Response", s.noResp, pctOf(s.noResp, s.sop || s.total)],
          ["Lapsed SLA", s.lapsed, pctOf(s.lapsed, s.sop || s.total)],
          ["  Completed +1 Business Day Late", s.lapsed1, pctOf(s.lapsed1, s.sop || s.total)],
          ["  Completed +2 Business Days Late", s.lapsed2, pctOf(s.lapsed2, s.sop || s.total)],
          ["  Other / Unclassified Lapsed SLA", s.lapsedOther, pctOf(s.lapsedOther, s.sop || s.total)],
          [],
          ["Non-SOP / Change Breakdown"],
          ["Category", "Count", "% of Non-SOP"],
          ...s.nonSopCats.map(([cat, n]) => [cat, n, pctOf(n, s.nonSop)]),
        ];
        const wsSS = XLSX.utils.aoa_to_sheet(SS);
        wsSS["!cols"] = [{ wch: 36 }, { wch: 10 }, { wch: 14 }];
        if (wsSS["A1"]) wsSS["A1"].s = { font: { bold: true, sz: 14, color: { rgb: NAVY } } };
        ["A3", "B3", "C3"].forEach(a => { if (wsSS[a]) wsSS[a].s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: NAVY } } }; });
        XLSX.utils.book_append_sheet(wb, wsSS, "SLA Summary");
      }

      // ---- Time Analysis sheet ----
      if ((report.timeData || []).length) {
        const TA = [["Time Analysis (by month)"], [], ["Period", "Created", "Resolved", "SLA %"]];
        report.timeData.forEach(t => TA.push([t.period, t.created, t.resolved, t.slaPct != null ? t.slaPct + "%" : ""]));
        const wsTA = XLSX.utils.aoa_to_sheet(TA);
        wsTA["!cols"] = [{ wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
        if (wsTA["A1"]) wsTA["A1"].s = { font: { bold: true, sz: 14, color: { rgb: NAVY } } };
        ["A3", "B3", "C3", "D3"].forEach(a => { if (wsTA[a]) wsTA[a].s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: NAVY } } }; });
        XLSX.utils.book_append_sheet(wb, wsTA, "Time Analysis");
      }

      // ---- Dashboards sheet: in-cell block-character bars for each chart ----
      {
        const NAVY2 = "1B3A6B", BLUE2 = "2E75B6";
        // Build a block-character bar scaled to `max` (width up to 40 cells wide)
        const bar = (value, max, width = 40) => {
          if (!max || max <= 0) return "";
          const filled = Math.round((value / max) * width);
          return "\u2588".repeat(Math.max(0, filled)) + "\u2591".repeat(Math.max(0, width - filled));
        };
        const D = [];
        const headerRows = []; // row indices to style as section headers
        const barRows = [];    // {r, colour} for bar cells
        const pushSection = (title) => { D.push([title, "", ""]); headerRows.push(D.length - 1); };
        const pushBars = (rows, colour) => {
          const max = Math.max(1, ...rows.map(r => r[1]));
          rows.forEach(([label, val]) => {
            D.push([label, val, bar(val, max)]);
            barRows.push({ r: D.length - 1, colour });
          });
        };

        D.push(["C365Cloud SLA Report - Dashboards"]); headerRows.push(0);
        D.push(["Client", client || "All Clients", ""]);
        D.push(["Period", `${fmtDate(startDate)} to ${fmtDate(endDate)}`, ""]);
        D.push([]);

        pushSection("Status Distribution");
        pushBars((report.statusData || []).map(d => [d[0], d[1]]), BLUE2);
        D.push([]);

        pushSection("Priority Breakdown");
        pushBars([["P1 Critical", c.p1], ["P2 High", c.p2], ["P3 Medium", c.p3], ["P4 Low", c.p4]], "e32529");
        D.push([]);

        pushSection("SLA: First Response vs Resolution");
        pushBars([
          ["First Response - Within", c.fr_within], ["First Response - Violated", c.fr_violated], ["First Response - Still Open", c.fr_open],
          ["Resolution - Within", c.res_within], ["Resolution - Violated", c.res_violated], ["Resolution - Still Open", c.res_open],
        ], BLUE2);
        D.push([]);

        pushSection("Top Categories");
        pushBars((report.categoryData || []).map(d => [d[0], d[1]]), "00a499");
        D.push([]);

        if ((report.timeData || []).length) {
          pushSection("Created vs Resolved (by month)");
          const tmax = Math.max(1, ...report.timeData.flatMap(t => [t.created, t.resolved]));
          report.timeData.forEach(t => {
            D.push([t.period + " - Created", t.created, bar(t.created, tmax)]); barRows.push({ r: D.length - 1, colour: BLUE2 });
            D.push([t.period + " - Resolved", t.resolved, bar(t.resolved, tmax)]); barRows.push({ r: D.length - 1, colour: "47ac4c" });
          });
          D.push([]);

          pushSection("SLA Trend (% within)");
          report.timeData.forEach(t => {
            const v = t.slaPct == null ? 0 : t.slaPct;
            D.push([t.period, (t.slaPct == null ? "" : v + "%"), bar(v, 100)]);
            barRows.push({ r: D.length - 1, colour: "47ac4c" });
          });
          D.push([]);
        }

        if (report.slaSummary) {
          const s = report.slaSummary;
          pushSection("SLA Summary (SOP) Distribution");
          pushBars([
            ["Same Day", s.sameDay], ["Within 2 Days", s.within2], ["Paused/Further Info", s.paused],
            ["No Response", s.noResp], ["Lapsed SLA", s.lapsed], ["Non-SOP", s.nonSop],
          ], "f1901e");
        }

        const wsD = XLSX.utils.aoa_to_sheet(D);
        wsD["!cols"] = [{ wch: 34 }, { wch: 10 }, { wch: 46 }];
        // Style title + section headers
        headerRows.forEach((r, idx) => {
          const a = XLSX.utils.encode_cell({ r, c: 0 });
          if (wsD[a]) wsD[a].s = idx === 0
            ? { font: { bold: true, sz: 14, color: { rgb: NAVY2 } } }
            : { font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: NAVY2 } } };
        });
        // Colour the bar cells to match each chart
        barRows.forEach(({ r, colour }) => {
          const a = XLSX.utils.encode_cell({ r, c: 2 });
          if (wsD[a]) wsD[a].s = { font: { name: "Consolas", sz: 9, color: { rgb: colour } } };
          const b = XLSX.utils.encode_cell({ r, c: 1 });
          if (wsD[b]) wsD[b].s = { alignment: { horizontal: "right" }, font: { bold: true } };
        });
        XLSX.utils.book_append_sheet(wb, wsD, "Dashboards");
      }

      XLSX.writeFile(wb, `C365Cloud_SLA_Report_${(client || "All").replace(/[^a-z0-9]/gi,"_")}_${endDate}.xlsx`);
    } catch (e) { alert("Excel export failed: " + e.message); }
  };

  const exportPdf = () => {
    if (!report) return;
    const c = report.counts;
    const NAVY = "#1B3A6B", BLUE = "#2E75B6";
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const rangeLabel = `${fmtDate(startDate)} to ${fmtDate(endDate)}`;
    const genStamp = new Date().toLocaleString("en-GB");

    // Metric card matching the on-screen tiles
    const card = (label, val, colour) => `
      <div style="flex:1 1 0;min-width:0;background:${colour};color:#fff;border-radius:8px;padding:12px 8px;text-align:center">
        <div style="font-size:22px;font-weight:800;line-height:1.05">${esc(val)}</div>
        <div style="font-size:8.5px;text-transform:uppercase;letter-spacing:.04em;opacity:.92;margin-top:3px">${esc(label)}</div>
      </div>`;

    const slaColour = (v) => v === "Outside SLA" ? "#e32529" : v === "Within SLA" ? "#2e8b57" : "#888";

    // ---- SVG chart generators (return markup strings) so the dashboard renders in the PDF ----
    const CHART_COLOURS = ["#2E75B6", "#1B3A6B", "#47ac4c", "#f1901e", "#e32529", "#5b4fcf", "#00a499", "#768692", "#c47ef0", "#36454f", "#e6a817", "#8bc34a"];
    const svgDonut = (data, size = 150) => {
      const items = data.filter(d => d.value > 0);
      const total = items.reduce((s, d) => s + d.value, 0) || 1;
      const cx = size / 2, cy = size / 2, r = size / 2 - 6, inner = r * 0.62;
      let angle = -Math.PI / 2;
      const arcs = items.map(d => {
        const frac = d.value / total, a0 = angle, a1 = angle + frac * Math.PI * 2; angle = a1;
        const large = (a1 - a0) > Math.PI ? 1 : 0;
        const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0), x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
        const xi1 = cx + inner * Math.cos(a1), yi1 = cy + inner * Math.sin(a1), xi0 = cx + inner * Math.cos(a0), yi0 = cy + inner * Math.sin(a0);
        return `<path d="M ${x0.toFixed(1)} ${y0.toFixed(1)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(1)} ${y1.toFixed(1)} L ${xi1.toFixed(1)} ${yi1.toFixed(1)} A ${inner} ${inner} 0 ${large} 0 ${xi0.toFixed(1)} ${yi0.toFixed(1)} Z" fill="${d.colour}"/>`;
      }).join("");
      const legend = items.map(d => `<div style="display:flex;align-items:center;gap:5px;font-size:9px;margin-bottom:2px"><span style="width:8px;height:8px;border-radius:2px;background:${d.colour};display:inline-block"></span><span style="color:#444">${esc(d.label)}</span><span style="margin-left:auto;font-weight:600;padding-left:8px">${d.value}</span></div>`).join("");
      return `<div style="display:flex;gap:12px;align-items:center">
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${arcs}
          <text x="${cx}" y="${cy - 1}" text-anchor="middle" font-size="18" font-weight="800" fill="#222">${total}</text>
          <text x="${cx}" y="${cy + 12}" text-anchor="middle" font-size="8" fill="#888">TOTAL</text></svg>
        <div style="min-width:120px">${legend}</div></div>`;
    };
    const svgVBars = (data, colour, w = 300, h = 170) => {
      const max = Math.max(1, ...data.map(d => d.value));
      const padL = 26, padB = 26, padT = 14, padR = 8;
      const plotW = w - padL - padR, plotH = h - padT - padB;
      const slot = plotW / Math.max(data.length, 1), bw = slot * 0.6;
      const bars = data.map((d, i) => {
        const bh = (d.value / max) * plotH, x = padL + i * slot + (slot - bw) / 2, y = padT + plotH - bh;
        return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${bh.toFixed(1)}" fill="${colour}" rx="2"/>
          <text x="${(x + bw / 2).toFixed(1)}" y="${(y - 3).toFixed(1)}" text-anchor="middle" font-size="9" font-weight="700" fill="#222">${d.value}</text>
          <text x="${(x + bw / 2).toFixed(1)}" y="${h - 9}" text-anchor="middle" font-size="9" fill="#888">${esc(d.label)}</text>`;
      }).join("");
      return `<svg width="100%" height="${h}" viewBox="0 0 ${w} ${h}">${bars}</svg>`;
    };
    const svgHBars = (data, colour, w = 300) => {
      const max = Math.max(1, ...data.map(d => d.value));
      return `<div style="display:flex;flex-direction:column;gap:4px">${data.map(d => `
        <div style="display:flex;align-items:center;gap:6px;font-size:9px">
          <span style="width:110px;text-align:right;color:#555;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(d.label)}</span>
          <div style="flex:1;background:#eef2f7;border-radius:3px;height:13px"><div style="width:${(d.value / max * 100).toFixed(1)}%;background:${colour};height:100%;border-radius:3px"></div></div>
          <span style="width:22px;font-weight:600">${d.value}</span></div>`).join("")}</div>`;
    };
    const svgGrouped = (periods, series, w = 340, h = 180) => {
      const allVals = series.flatMap(s => s.values), max = Math.max(1, ...allVals);
      const padL = 26, padB = 24, padT = 12, padR = 8;
      const plotW = w - padL - padR, plotH = h - padT - padB;
      const n = Math.max(periods.length, 1), groupW = plotW / n, barW = (groupW * 0.8) / series.length;
      const step = Math.ceil(n / 10);
      let out = "";
      periods.forEach((p, pi) => series.forEach((s, si) => {
        const bh = (s.values[pi] / max) * plotH, x = padL + pi * groupW + groupW * 0.1 + si * barW, y = padT + plotH - bh;
        out += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${(barW * 0.9).toFixed(1)}" height="${bh.toFixed(1)}" fill="${s.colour}" rx="1"/>`;
      }));
      periods.forEach((p, pi) => { if (pi % step === 0 || pi === n - 1) out += `<text x="${(padL + pi * groupW + groupW / 2).toFixed(1)}" y="${h - 7}" text-anchor="middle" font-size="8" fill="#888">${esc(p)}</text>`; });
      const legend = series.map(s => `<span style="display:inline-flex;align-items:center;gap:4px;font-size:9px;margin-right:10px"><span style="width:8px;height:8px;border-radius:2px;background:${s.colour};display:inline-block"></span>${esc(s.name)}</span>`).join("");
      return `<svg width="100%" height="${h}" viewBox="0 0 ${w} ${h}">${out}</svg><div style="text-align:center;margin-top:2px">${legend}</div>`;
    };
    const svgLine = (points, colour = "#47ac4c", w = 340, h = 180) => {
      const pts = points.filter(p => p.value !== null && p.value !== undefined);
      if (!pts.length) return `<div style="font-size:10px;color:#888;padding:16px;text-align:center">No SLA trend data</div>`;
      const padL = 30, padR = 12, padT = 14, padB = 24;
      const plotW = w - padL - padR, plotH = h - padT - padB, n = points.length;
      const xAt = (i) => n > 1 ? padL + (i / (n - 1)) * plotW : padL + plotW / 2;
      const yAt = (v) => padT + (1 - v / 100) * plotH;
      const coords = points.map((p, i) => (p.value == null) ? null : { x: xAt(i), y: yAt(p.value), v: p.value }).filter(Boolean);
      const line = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");
      const area = coords.length ? `${line} L ${coords[coords.length - 1].x.toFixed(1)} ${(padT + plotH).toFixed(1)} L ${coords[0].x.toFixed(1)} ${(padT + plotH).toFixed(1)} Z` : "";
      const grid = [0, 25, 50, 75, 100].map(g => `<line x1="${padL}" y1="${yAt(g).toFixed(1)}" x2="${w - padR}" y2="${yAt(g).toFixed(1)}" stroke="#e3e8ef" stroke-width="1"/><text x="${padL - 5}" y="${(yAt(g) + 3).toFixed(1)}" text-anchor="end" font-size="8" fill="#888">${g}%</text>`).join("");
      const dots = coords.map(c => `<circle cx="${c.x.toFixed(1)}" cy="${c.y.toFixed(1)}" r="2.5" fill="${colour}"/><text x="${c.x.toFixed(1)}" y="${(c.y - 6).toFixed(1)}" text-anchor="middle" font-size="8" font-weight="700" fill="#222">${c.v}%</text>`).join("");
      const step = Math.ceil(n / 8);
      const xlabels = points.map((p, i) => (i % step === 0 || i === n - 1) ? `<text x="${xAt(i).toFixed(1)}" y="${h - 7}" text-anchor="middle" font-size="8" fill="#888">${esc(p.label)}</text>` : "").join("");
      return `<svg width="100%" height="${h}" viewBox="0 0 ${w} ${h}">${grid}${area ? `<path d="${area}" fill="${colour}" opacity="0.12"/>` : ""}<path d="${line}" fill="none" stroke="${colour}" stroke-width="2"/>${dots}${xlabels}</svg>`;
    };

    // Build the chart panels HTML
    const priorityData = [{ label: "P1", value: c.p1 }, { label: "P2", value: c.p2 }, { label: "P3", value: c.p3 }, { label: "P4", value: c.p4 }];
    const statusChart = (report.statusData || []).map((d, i) => ({ label: d[0], value: d[1], colour: CHART_COLOURS[i % CHART_COLOURS.length] }));
    const categoryChart = (report.categoryData || []).map(d => ({ label: d[0], value: d[1] }));
    const chartPanel = (title, inner) => `<div style="flex:1 1 300px;min-width:280px;background:#fff;border:1px solid #e3e8ef;border-radius:8px;padding:12px;page-break-inside:avoid"><div style="font-size:11px;font-weight:700;color:${NAVY};margin-bottom:8px">${title}</div>${inner}</div>`;
    const chartsHtml = `
      <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px">
        ${chartPanel("Status Distribution", svgDonut(statusChart))}
        ${chartPanel("Priority Breakdown", svgVBars(priorityData, BLUE))}
        ${chartPanel("SLA: First Response vs Resolution", svgGrouped(["Within SLA", "Outside SLA", "Still Open"], [
          { name: "First Response", colour: "#2E75B6", values: [c.fr_within, c.fr_violated, c.fr_open] },
          { name: "Resolution", colour: "#1B3A6B", values: [c.res_within, c.res_violated, c.res_open] },
        ]))}
        ${chartPanel("Top Categories", svgHBars(categoryChart, "#00a499"))}
      </div>`;
    const timeHtml = (report.timeData || []).length ? `
      <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px">
        ${chartPanel("Created vs Resolved", svgGrouped(report.timeData.map(t => t.period), [
          { name: "Created", colour: "#2E75B6", values: report.timeData.map(t => t.created) },
          { name: "Resolved", colour: "#47ac4c", values: report.timeData.map(t => t.resolved) },
        ]))}
        ${chartPanel("SLA Trend (% within)", svgLine(report.timeData.map(t => ({ label: t.period, value: t.slaPct }))))}
      </div>` : "";

    const rowsHtml = sortedTickets.map((t, i) => `<tr style="background:${i % 2 ? "#f5f7fa" : "#fff"}">
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;font-family:'Courier New',monospace;white-space:nowrap;color:${BLUE};font-weight:600">${esc(t.key)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px">${esc(t.summary)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;white-space:nowrap">${esc(t.status)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;white-space:nowrap">${esc(t.priority)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;white-space:nowrap">${esc(t.type)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;white-space:nowrap">${esc(t.created)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;white-space:nowrap">${esc(t.assignee)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;white-space:nowrap;color:${slaColour(t.fr_sla)};font-weight:600">${esc(t.fr_sla)}</td>
      <td style="border-bottom:1px solid #e3e8ef;padding:5px 7px;white-space:nowrap;color:${slaColour(t.res_sla)};font-weight:600">${esc(t.res_sla)}</td>
    </tr>`).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>C365Cloud SLA Report - ${esc(client || "All Clients")}</title>
    <style>
      @page { size: A4 landscape; margin: 14mm 12mm 16mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { font-family: Arial, Helvetica, sans-serif; color: #222; margin: 0; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      svg { max-width: 100%; }
      .footer { position: fixed; bottom: 6mm; left: 0; right: 0; text-align: center; font-size: 8.5px; color: #98a2b3; }
    </style></head>
    <body>
      <!-- Header band -->
      <div style="background:${NAVY};color:#fff;border-radius:8px;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;margin-bottom:14px">
        <div>
          <div style="font-size:20px;font-weight:800;letter-spacing:.02em">Jira SLA Report</div>
          <div style="font-size:11px;opacity:.85;margin-top:2px">SUPPORT project &nbsp;&bull;&nbsp; C365Cloud</div>
        </div>
        <div style="text-align:right;font-size:11px;line-height:1.6">
          <div><span style="opacity:.7">Client:</span> <strong>${esc(client || "All Clients")}</strong></div>
          <div><span style="opacity:.7">Period:</span> ${esc(rangeLabel)}</div>
          <div style="opacity:.7">Generated ${esc(genStamp)}</div>
        </div>
      </div>

      <!-- Metric cards: row 1 -->
      <div style="display:flex;gap:6px;margin-bottom:6px">
        ${card("Created", c.created, "#36454f")}
        ${card("Resolved", c.resolved, "#47ac4c")}
        ${card("Open", c.open, "#768692")}
        ${card("With Development", c.with_dev, "#5b4fcf")}
        ${card("Awaiting Client", c.awaiting_client, "#f1901e")}
        ${card("Triage", c.triage, "#e32529")}
      </div>
      <!-- Metric cards: row 2 -->
      <div style="display:flex;gap:6px;margin-bottom:14px">
        ${card("P1 Critical", c.p1, "#e32529")}
        ${card("P2 High", c.p2, "#f1901e")}
        ${card("P3 Medium", c.p3, "#768692")}
        ${card("P4 Low", c.p4, "#47ac4c")}
        ${card("First Response SLA", c.fr_pct + "%", BLUE)}
        ${card("Resolution SLA", c.res_pct + "%", NAVY)}
      </div>

      ${notes ? `<div style="margin:0 0 14px;padding:10px 12px;background:#eef3fa;border-left:4px solid ${BLUE};border-radius:4px;font-size:11px;line-height:1.5"><strong style="color:${NAVY}">Notes:</strong> ${esc(notes).replace(/\n/g, "<br>")}</div>` : ""}

      ${chartsHtml}
      ${timeHtml}

      ${report.slaSummary ? (() => {
        const s = report.slaSummary;
        const pctOf = (n, d) => d ? (Math.round(n / d * 1000) / 10) + "%" : "0%";
        const row = (label, count, pctText, cls) => `<tr style="${cls === "head" ? "background:#eef3fa;font-weight:700" : cls === "warn" ? "background:#fff6e6;font-weight:700" : cls === "good" ? "background:#e9f7ec;font-weight:700" : ""}"><td style="padding:4px 8px;border-bottom:1px solid #eee;${cls === "indent" ? "padding-left:22px;color:#555" : ""}">${esc(label)}</td><td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${count}</td><td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right;color:#888">${pctText}</td></tr>`;
        const nonSopRows = s.nonSopCats.length ? s.nonSopCats.map(([cat, n]) => `<tr><td style="padding:3px 8px;border-bottom:1px solid #eee">${esc(cat)}</td><td style="padding:3px 8px;border-bottom:1px solid #eee;text-align:right">${n}</td><td style="padding:3px 8px;border-bottom:1px solid #eee;text-align:right;color:#888">${pctOf(n, s.nonSop)}</td></tr>`).join("") : `<tr><td colspan="3" style="padding:8px;text-align:center;color:#888">No Non-SOP / Change tickets</td></tr>`;
        const ssDonut = svgDonut([
          { label: "Same Day", value: s.sameDay, colour: "#47ac4c" },
          { label: "Within 2 Days", value: s.within2, colour: "#00a499" },
          { label: "Paused/Further Info", value: s.paused, colour: "#768692" },
          { label: "No Response", value: s.noResp, colour: "#5b4fcf" },
          { label: "Lapsed SLA", value: s.lapsed, colour: "#f1901e" },
          { label: "Non-SOP", value: s.nonSop, colour: "#d8d8d8" },
        ], 140);
        return `<div style="font-size:12px;font-weight:700;color:${NAVY};margin:4px 0 6px">SLA Summary (SOP Breakdown)</div>
        <div style="display:flex;gap:14px;flex-wrap:wrap;margin-bottom:14px;page-break-inside:avoid">
        <table style="flex:1 1 340px;border-collapse:collapse;font-size:10px;height:fit-content">
          ${row("Total Received In Period", s.total, "100%", "head")}
          ${row("Non-SOP / Change", s.nonSop, pctOf(s.nonSop, s.total), "indent")}
          ${row("Total SOP", s.sop, pctOf(s.sop, s.total), "head")}
          ${row("Total SOP Completed Within SLA", s.within, pctOf(s.within, s.sop || s.total), "good")}
          ${row("Same Day Completion", s.sameDay, pctOf(s.sameDay, s.sop || s.total), "indent")}
          ${row("Within 2-Day SLA", s.within2, pctOf(s.within2, s.sop || s.total), "indent")}
          ${row("Further Info Required / Paused", s.paused, pctOf(s.paused, s.sop || s.total), "indent")}
          ${row("Completed - No Response", s.noResp, pctOf(s.noResp, s.sop || s.total), "indent")}
          ${row("Lapsed SLA", s.lapsed, pctOf(s.lapsed, s.sop || s.total), "warn")}
          ${row("Completed +1 Business Day Late", s.lapsed1, pctOf(s.lapsed1, s.sop || s.total), "indent")}
          ${row("Completed +2 Business Days Late", s.lapsed2, pctOf(s.lapsed2, s.sop || s.total), "indent")}
          ${row("Other / Unclassified Lapsed SLA", s.lapsedOther, pctOf(s.lapsedOther, s.sop || s.total), "indent")}
        </table>
        <div style="flex:0 0 auto">${ssDonut}</div>
        </div>
        <div style="font-size:11px;font-weight:700;color:${NAVY};margin:0 0 4px">Non-SOP / Change Breakdown</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px;margin-bottom:14px">
          <thead><tr style="background:${NAVY};color:#fff"><th style="padding:4px 8px;text-align:left">Category</th><th style="padding:4px 8px;text-align:right">Count</th><th style="padding:4px 8px;text-align:right">% of Non-SOP</th></tr></thead>
          <tbody>${nonSopRows}</tbody>
        </table>` ;
      })() : ""}

      <div style="font-size:12px;font-weight:700;color:${NAVY};margin:0 0 6px">Ticket Detail <span style="font-weight:400;color:#888">(${report.total} tickets)</span></div>
      <table style="width:100%;border-collapse:collapse;font-size:9.5px">
        <thead><tr style="background:${NAVY};color:#fff">
          <th style="padding:6px 7px;text-align:left">Key</th>
          <th style="padding:6px 7px;text-align:left">Summary</th>
          <th style="padding:6px 7px;text-align:left">Status</th>
          <th style="padding:6px 7px;text-align:left">Priority</th>
          <th style="padding:6px 7px;text-align:left">Type</th>
          <th style="padding:6px 7px;text-align:left">Created</th>
          <th style="padding:6px 7px;text-align:left">Assignee</th>
          <th style="padding:6px 7px;text-align:left">First Resp</th>
          <th style="padding:6px 7px;text-align:left">Resolution</th>
        </tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="9" style="padding:14px;text-align:center;color:#888">No tickets in this period.</td></tr>`}</tbody>
      </table>

      <div class="footer">C365Cloud | c365cloud.co.uk | Confidential</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>
    </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
    else alert("Please allow pop-ups to export the PDF.");
  };

  const c = report?.counts;
  const metricCard = (label, value, bg) => (
    <div style={{ flex: "1 1 130px", minWidth: 120, background: bg, color: "#fff", borderRadius: 8, padding: "14px 12px", textAlign: "center" }}>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>{value ?? 0}</div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.9, marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ padding: 18, overflowY: "auto", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--text)" }}>📈 Jira SLA Report</h2>
        <span style={{ fontSize: 11, color: "var(--text-dim)" }}>SUPPORT project</span>
      </div>

      {!hasJira && (
        <div style={{ padding: 14, background: "rgba(227,37,41,0.08)", border: "1px solid #e3252940", borderRadius: 8, color: "var(--text)", marginBottom: 16 }}>
          Jira isn't connected. Open ⚙ Config and add your Jira URL, email and API token first.
        </div>
      )}

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16, padding: 14, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>Client</label>
          <select className="form-input" style={{ minWidth: 200 }} value={client} onChange={e => setClient(e.target.value)}>
            <option value="">All clients</option>
            {clients.map(cl => <option key={cl} value={cl}>{cl}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>From</label>
          <input type="date" className="form-input" value={startDate} onChange={e => { setStartDate(e.target.value); setActivePeriod(""); }} style={{ colorScheme: "dark" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>To</label>
          <input type="date" className="form-input" value={endDate} onChange={e => { setEndDate(e.target.value); setActivePeriod(""); }} style={{ colorScheme: "dark" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>Quick Select</label>
          <div style={{ display: "flex", gap: 4 }}>
            {[["1M",1],["3M",3],["6M",6],["12M",12]].map(([lbl, m]) => (
              <button key={lbl} className={`btn ${activePeriod === lbl ? "primary" : ""}`}
                style={{ fontSize: 11, padding: "8px 12px" }}
                onClick={() => applyPeriod(m)}
                title={`Last ${m} month(s), ending last day of previous month`}>{lbl}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 160 }}>
          <label style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, textTransform: "uppercase" }}>Extra JQL (optional)</label>
          <input className="form-input" placeholder='e.g. priority = P3' value={extraJql} onChange={e => setExtraJql(e.target.value)} />
        </div>
        <button className="btn primary" style={{ fontSize: 13, padding: "8px 18px" }} onClick={runReport} disabled={loading || !hasJira}>
          {loading ? "⏳ Fetching…" : "▶ Run Report"}
        </button>
        {report && (
          <>
            <button className="btn" style={{ fontSize: 12, padding: "8px 14px" }} onClick={exportExcel} title="Download as Excel">📊 Excel</button>
            <button className="btn" style={{ fontSize: 12, padding: "8px 14px" }} onClick={exportPdf} title="Download as PDF">📄 PDF</button>
          </>
        )}
      </div>

      {error && <div style={{ padding: 12, background: "rgba(227,37,41,0.08)", border: "1px solid #e3252940", borderRadius: 8, color: "#e32529", marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {report && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 14, padding: "10px 14px", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}>
            <span style={{ fontWeight: 700, color: "var(--text)" }}>📊 {report.total} tickets</span>
            <span style={{ color: "var(--text-muted)" }}>📅 {fmtDate(startDate)} to {fmtDate(endDate)}</span>
            <span style={{ background: "#2E75B6", color: "#fff", borderRadius: 20, padding: "2px 12px", fontWeight: 600 }}>🏢 {client || "All Clients"}</span>
            <span style={{ color: "var(--text-dim)", marginLeft: "auto" }}>Fetched {new Date().toLocaleString("en-GB")}</span>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {metricCard("Created", c.created, "#36454f")}
            {metricCard("Resolved", c.resolved, "#47ac4c")}
            {metricCard("Open", c.open, "#768692")}
            {metricCard("With Development", c.with_dev, "#5b4fcf")}
            {metricCard("Awaiting Client", c.awaiting_client, "#f1901e")}
            {metricCard("Triage", c.triage, "#e32529")}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {metricCard("P1 Critical", c.p1, "#e32529")}
            {metricCard("P2 High", c.p2, "#f1901e")}
            {metricCard("P3 Medium", c.p3, "#768692")}
            {metricCard("P4 Low", c.p4, "#47ac4c")}
            {metricCard("First Response SLA", c.fr_pct + "%", "#2E75B6")}
            {metricCard("Resolution SLA", c.res_pct + "%", "#1B3A6B")}
          </div>

          {/* First Response + Resolution SLA panels */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginBottom: 16 }}>
            {[
              { title: "First Response SLA", pct: c.fr_pct, within: c.fr_within, violated: c.fr_violated, open: c.fr_open, na: c.fr_na },
              { title: "Resolution SLA", pct: c.res_pct, within: c.res_within, violated: c.res_violated, open: c.res_open, na: c.res_na },
            ].map(panel => {
              const denom = panel.within + panel.violated + panel.open;
              const pctColour = panel.pct >= 95 ? "#47ac4c" : panel.pct >= 85 ? "#f1901e" : "#e32529";
              return (
                <div key={panel.title} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{panel.title}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#fff", background: pctColour, borderRadius: 20, padding: "2px 12px" }}>{panel.pct}%</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    {[["Within", panel.within, "#47ac4c"], ["Violated", panel.violated, "#e32529"], ["Still Open", panel.open, "#768692"]].map(([lbl, val, col]) => (
                      <div key={lbl} style={{ flex: 1, textAlign: "center", background: "var(--surface2)", borderRadius: 6, padding: "10px 4px" }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: col }}>{val}</div>
                        <div style={{ fontSize: 9, color: "var(--text-dim)", textTransform: "uppercase" }}>{lbl}</div>
                      </div>
                    ))}
                  </div>
                  <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                    <tbody>
                      {[["Within SLA", panel.within, "#47ac4c"], ["Violated", panel.violated, "#e32529"], ["Still Open", panel.open, "#768692"], ["N/A", panel.na, "#aaa"]].map(([lbl, val, col]) => (
                        <tr key={lbl} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "5px 4px", color: "var(--text-muted)" }}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: col, marginRight: 6 }} />{lbl}</td>
                          <td style={{ padding: "5px 4px", textAlign: "right", fontWeight: 600, color: "var(--text)" }}>{val}</td>
                          <td style={{ padding: "5px 4px", textAlign: "right", color: "var(--text-dim)" }}>{denom ? Math.round(val / denom * 100) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          {/* Charts row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Status Distribution</div>
              <SlaDonut data={(report.statusData || []).map((d, i) => ({ label: d[0], value: d[1], colour: SLA_CHART_COLOURS[i % SLA_CHART_COLOURS.length] }))} />
            </div>
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Priority Breakdown</div>
              <SlaBarChart data={[{ label: "P1", value: c.p1 }, { label: "P2", value: c.p2 }, { label: "P3", value: c.p3 }, { label: "P4", value: c.p4 }]} colour="#2E75B6" />
            </div>
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>SLA: First Response vs Resolution</div>
              <SlaGroupedBar periods={["Within", "Violated", "Open"]}
                series={[
                  { name: "First Response", colour: "#2E75B6", values: [c.fr_within, c.fr_violated, c.fr_open] },
                  { name: "Resolution", colour: "#1B3A6B", values: [c.res_within, c.res_violated, c.res_open] },
                ]} />
            </div>
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Top Categories</div>
              <SlaBarChart horizontal data={(report.categoryData || []).map(d => ({ label: d[0], value: d[1] }))} colour="#00a499" />
            </div>
          </div>

          {/* Time Analysis */}
          {(report.timeData || []).length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12, marginBottom: 16 }}>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Created vs Resolved (by month)</div>
                <SlaGroupedBar periods={report.timeData.map(t => t.period)}
                  series={[
                    { name: "Created", colour: "#2E75B6", values: report.timeData.map(t => t.created) },
                    { name: "Resolved", colour: "#47ac4c", values: report.timeData.map(t => t.resolved) },
                  ]} />
              </div>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>SLA Trend (% within)</div>
                <SlaLineChart points={report.timeData.map(t => ({ label: t.period, value: t.slaPct }))} />
              </div>
            </div>
          )}

          {/* SLA Summary (SOP breakdown) */}
          {report.slaSummary && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12, marginBottom: 16 }}>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>SLA Summary (SOP)</div>
                {(() => {
                  const s = report.slaSummary;
                  const pctOf = (v, base) => base ? Math.round(v / base * 100) + "%" : "";
                  const rows = [
                    ["Total Tickets", s.total, "", "bold"],
                    ["Non-SOP / Change", s.nonSop, pctOf(s.nonSop, s.total), "dim"],
                    ["Total SOP", s.sop, pctOf(s.sop, s.total), "bold"],
                    ["Total SOP Within SLA", s.within, pctOf(s.within, s.sop || s.total), "green"],
                    ["  Same Day Completion", s.sameDay, pctOf(s.sameDay, s.sop || s.total), ""],
                    ["  Within 2-Day SLA", s.within2, pctOf(s.within2, s.sop || s.total), ""],
                    ["  Further Info / Paused", s.paused, pctOf(s.paused, s.sop || s.total), ""],
                    ["  Completed - No Response", s.noResp, pctOf(s.noResp, s.sop || s.total), ""],
                    ["Lapsed SLA", s.lapsed, pctOf(s.lapsed, s.sop || s.total), "amber"],
                    ["  Lapsed +1 Day", s.lapsed1, pctOf(s.lapsed1, s.sop || s.total), ""],
                    ["  Lapsed +2 Days", s.lapsed2, pctOf(s.lapsed2, s.sop || s.total), ""],
                    ["  Other / Unclassified", s.lapsedOther, pctOf(s.lapsedOther, s.sop || s.total), ""],
                  ];
                  const rowStyle = (kind) => ({
                    bold: { fontWeight: 700, color: "var(--text)", background: "var(--surface2)" },
                    green: { fontWeight: 700, color: "#47ac4c" },
                    amber: { fontWeight: 700, color: "#f1901e" },
                    dim: { color: "var(--text-dim)" },
                    "": { color: "var(--text-muted)" },
                  }[kind]);
                  return (
                    <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
                      <tbody>
                        {rows.map(([lbl, val, pctv, kind]) => (
                          <tr key={lbl} style={{ borderBottom: "1px solid var(--border)" }}>
                            <td style={{ padding: "5px 6px", whiteSpace: "pre", ...rowStyle(kind) }}>{lbl}</td>
                            <td style={{ padding: "5px 6px", textAlign: "right", ...rowStyle(kind) }}>{val}</td>
                            <td style={{ padding: "5px 6px", textAlign: "right", color: "var(--text-dim)", fontWeight: 400 }}>{pctv}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>SLA Outcome Mix</div>
                <SlaDonut data={[
                  { label: "Same Day", value: report.slaSummary.sameDay, colour: "#47ac4c" },
                  { label: "Within 2 Days", value: report.slaSummary.within2, colour: "#00a499" },
                  { label: "Paused/Further Info", value: report.slaSummary.paused, colour: "#768692" },
                  { label: "No Response", value: report.slaSummary.noResp, colour: "#5b4fcf" },
                  { label: "Lapsed SLA", value: report.slaSummary.lapsed, colour: "#f1901e" },
                  { label: "Non-SOP", value: report.slaSummary.nonSop, colour: "#d8d8d8" },
                ]} />
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", margin: "16px 0 8px" }}>Non-SOP / Change Breakdown</div>
                <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                  <thead><tr style={{ color: "var(--text-dim)" }}>
                    <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Category</th>
                    <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>Count</th>
                    <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>% of Non-SOP</th>
                  </tr></thead>
                  <tbody>
                    {report.slaSummary.nonSopCats.length ? report.slaSummary.nonSopCats.map(([cat, cnt]) => (
                      <tr key={cat} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "4px 6px", color: "var(--text-muted)" }}>{cat}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right", color: "var(--text)", fontWeight: 600 }}>{cnt}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right", color: "var(--text-dim)" }}>{report.slaSummary.nonSop ? Math.round(cnt / report.slaSummary.nonSop * 100) : 0}%</td>
                      </tr>
                    )) : <tr><td colSpan={3} style={{ padding: 12, textAlign: "center", color: "var(--text-dim)" }}>No Non-SOP / Change tickets in this period</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <textarea className="form-input" style={{ width: "100%", minHeight: 60, marginBottom: 10, resize: "vertical" }}
            placeholder="Notes for the exported report…" value={notes} onChange={e => setNotes(e.target.value)} />

          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button className="btn" style={{ fontSize: 12 }} onClick={exportExcel}>📊 Export Excel</button>
            <button className="btn" style={{ fontSize: 12 }} onClick={exportPdf}>📄 Export PDF</button>
            <span style={{ fontSize: 11, color: "var(--text-dim)", alignSelf: "center", marginLeft: "auto" }}>{report.total} tickets</span>
          </div>

          <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--surface2)" }}>
                  {[["key","Key"],["summary","Summary"],["status","Status"],["priority","Priority"],["type","Type"],["created_iso","Created"],["assignee","Assignee"],["fr_sla","First Resp"],["res_sla","Resolution"]].map(([k, lbl]) => (
                    <th key={k} onClick={() => toggleSort(k)}
                      style={{ padding: "8px 10px", textAlign: "left", cursor: "pointer", whiteSpace: "nowrap", color: "var(--text-muted)", fontWeight: 600, borderBottom: "1px solid var(--border)" }}>
                      {lbl}{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedTickets.map(t => (
                  <tr key={t.key} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>
                      <a href={`${base}/browse/${t.key}`} target="_blank" rel="noreferrer" style={{ color: "#4c9aff" }}>{t.key}</a>
                    </td>
                    <td style={{ padding: "6px 10px", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.summary}>{t.summary}</td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{t.status}</td>
                    <td style={{ padding: "6px 10px" }}>{t.priority}</td>
                    <td style={{ padding: "6px 10px" }}>{t.type}</td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{t.created}</td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{t.assignee}</td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap", color: t.fr_sla === "Outside SLA" ? "#e32529" : t.fr_sla === "Within SLA" ? "#47ac4c" : "var(--text-dim)" }}>{t.fr_sla}</td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap", color: t.res_sla === "Outside SLA" ? "#e32529" : t.res_sla === "Within SLA" ? "#47ac4c" : "var(--text-dim)" }}>{t.res_sla}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!report && !loading && hasJira && (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text-dim)" }}>
          Select a client and date range, then Run Report.
        </div>
      )}
    </div>
  );
}

// ─── Roadmap View ─────────────────────────────────────────────────────────────

const RM_STATUSES = ["To Do","In Progress","Pending QA","In UAT","Deployed","Blocked","TBC"];
// Readiness: how well-defined/agreed the requirement is (separate from delivery status).
const RM_READINESS = ["Concept","Requested","Being written","Waiting for sign off","Fully specified & signed off"];
// Jira issue-type visual language (icon + accent colour), mirroring Jira so it is familiar.
const RM_TYPE_META = {
  epic: { icon: "\u25C6", colour: "#8b5cf6", label: "Epic" },
  story: { icon: "\u2261", colour: "#47ac4c", label: "Story" },
  task: { icon: "\u2611", colour: "#2E75B6", label: "Task" },
  bug: { icon: "\u25CF", colour: "#e32529", label: "Bug" },
  "sub-task": { icon: "\u21B3", colour: "#768692", label: "Sub-task" },
  subtask: { icon: "\u21B3", colour: "#768692", label: "Sub-task" },
};
const rmTypeMeta = (t) => RM_TYPE_META[(t || "").toLowerCase()] || { icon: "\u25CB", colour: "#768692", label: t || "" };
const RM_READINESS_COLOUR = {
  "Concept":"#768692","Requested":"#5b9bd5","Being written":"#F1901E",
  "Waiting for sign off":"#c47ef0","Fully specified & signed off":"#47ac4c",
};
// Left-to-right column order for the "By Jira status" board. Columns only appear when
// at least one card has that status; any status not listed here is appended at the end.
const RM_STATUS_BOARD_ORDER = [
  "Review","Blocked","Backlog","To Do","In Progress","Ready for Test","Pending Fix",
  "Code Complete","Pending QA","QA","QA Complete","On Hold","With DevOps","Rework","UAT",
  "Regression Test","Passed Regression","Pending Sign Off","Release","Release & Merged","Released","Done",
];
const RM_STATUS_CLASS = { "Deployed":"s-deployed","In Progress":"s-inprogress","In UAT":"s-inuat","Pending QA":"s-pendingqa","To Do":"s-todo","Blocked":"s-todo","TBC":"s-todo" };
const RM_STATUS_COLOUR = {
  // Not started / holding
  "Backlog":"#4a6070","To Do":"#4a6070","TBC":"#4a6070","On Hold":"#768692","Blocked":"#e32529",
  // In flight
  "In Progress":"#F1901E","Rework":"#F1901E","Pending Fix":"#e6a817","With DevOps":"#e6a817","Code Complete":"#5b9bd5",
  // Review / QA / test (blues)
  "Review":"#0078d4","Ready for Test":"#0078d4","Pending QA":"#0078d4","QA":"#0078d4","QA Complete":"#00a499",
  "Regression Test":"#0078d4","Passed Regression":"#00a499",
  // UAT / sign-off (purples)
  "In UAT":"#a78bfa","UAT":"#a78bfa","Pending Sign Off":"#c47ef0",
  // Release / done (greens)
  "Release":"#47ac4c","Release & Merged":"#47ac4c","Released":"#2e8b57","Deployed":"#47ac4c","Done":"#2e8b57",
};
const RM_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December","TBC"];

// Given a YYYY-MM-DD date, return the full month name (for roadmap release month).
function monthFromDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(String(dateStr).split("T")[0] + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  return RM_MONTHS[d.getMonth()];
}

function rmDateToMs(d) { return d ? new Date(d).getTime() : null; }

// UK (England & Wales) bank holidays for working-day duration maths. Update yearly.
const UK_BANK_HOLIDAYS = new Set([
  // 2026
  "2026-01-01","2026-04-03","2026-04-06","2026-05-04","2026-05-25","2026-08-31","2026-12-25","2026-12-28",
  // 2027
  "2027-01-01","2027-03-26","2027-03-29","2027-05-03","2027-05-31","2027-08-30","2027-12-27","2027-12-28",
  // 2028
  "2028-01-03","2028-04-14","2028-04-17","2028-05-01","2028-05-29","2028-08-28","2028-12-25","2028-12-26",
]);
const rmIsoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const rmIsWorkingDay = (d) => {
  const day = d.getDay();
  if (day === 0 || day === 6) return false;       // weekend
  if (UK_BANK_HOLIDAYS.has(rmIsoOf(d))) return false; // bank holiday
  return true;
};
// Count of working days from start to finish INCLUSIVE (both are working days counted).
const rmWorkingDaysBetween = (startIso, finishIso) => {
  if (!startIso || !finishIso) return null;
  const s = new Date(startIso + "T00:00:00"), f = new Date(finishIso + "T00:00:00");
  if (isNaN(s) || isNaN(f) || f < s) return null;
  let count = 0; const cur = new Date(s);
  while (cur <= f) { if (rmIsWorkingDay(cur)) count++; cur.setDate(cur.getDate() + 1); }
  return count;
};
// Finish date = start plus N working days (inclusive: 1 day means finish == start if start is a working day).
const rmAddWorkingDays = (startIso, days) => {
  if (!startIso || !days || days < 1) return "";
  let cur = new Date(startIso + "T00:00:00");
  // Move start to the first working day if it lands on a non-working day
  while (!rmIsWorkingDay(cur)) cur.setDate(cur.getDate() + 1);
  let counted = 1; // the start day counts as day 1
  while (counted < days) { cur.setDate(cur.getDate() + 1); if (rmIsWorkingDay(cur)) counted++; }
  return rmIsoOf(cur);
};
// Next working day strictly after the given date (used to start a dependent after a prerequisite).
const rmNextWorkingDay = (iso) => {
  if (!iso) return "";
  const cur = new Date(iso + "T00:00:00");
  do { cur.setDate(cur.getDate() + 1); } while (!rmIsWorkingDay(cur));
  return rmIsoOf(cur);
};

function RoadmapCardModal({ card, onSave, onDelete, onClose, onMove, moveTargets, knownAssignees = [] }) {
  const [form, setForm] = React.useState({ ...card });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Coupled scheduling: Days and Finish each drive the other, using working days.
  const setStart = (v) => setForm(f => {
    const start = v;
    // If we have a days count, recompute finish from the new start; else keep finish.
    if (f.durationDays && f.durationDays > 0) {
      return { ...f, start, finish: rmAddWorkingDays(start, parseInt(f.durationDays)) };
    }
    // If finish exists, recompute the days span from the new start/finish
    const days = rmWorkingDaysBetween(start, f.finish);
    return { ...f, start, durationDays: days || f.durationDays };
  });
  const setFinish = (v) => setForm(f => {
    const finish = v;
    const days = rmWorkingDaysBetween(f.start, finish);
    return { ...f, finish, durationDays: days != null ? days : f.durationDays };
  });
  const setDays = (v) => setForm(f => {
    const days = v === "" ? "" : Math.max(1, parseInt(v) || 1);
    if (days && f.start) return { ...f, durationDays: days, finish: rmAddWorkingDays(f.start, days) };
    return { ...f, durationDays: days };
  });

  return (
    <div className="rm-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rm-modal">
        <div className="rm-modal-header">
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "var(--teal,#00d4aa)", fontFamily: "monospace", fontWeight: 700, marginBottom: 4 }}>{form.jiraKey}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{form.title || "New card"}</div>
            {(form.epicKey || form.epicName) && (
              <div style={{ fontSize: 10, color: "#c47ef0", marginTop: 3 }}>
                ◈ Epic: {form.epicKey}{form.epicName ? " - " + form.epicName : ""}
              </div>
            )}
          </div>
          {form.jiraKey && (() => {
            const raw = String(form.jiraKey).trim();
            // Accept a full URL, or a bare key like SI-157; build a clean browse URL
            let href;
            if (/^https?:\/\//i.test(raw)) {
              href = raw;
            } else {
              const key = (raw.match(/[A-Z][A-Z0-9]+-\d+/i) || [raw.split(" ")[0]])[0];
              href = `https://c365online.atlassian.net/browse/${key}`;
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: "#4c9aff", textDecoration: "none", padding: "4px 8px", border: "1px solid #4c9aff40", borderRadius: 4, whiteSpace: "nowrap", flexShrink: 0 }}>
                ◆ Open in Jira
              </a>
            );
          })()}
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 20, padding: "0 0 0 8px" }}>✕</button>
        </div>
        <div className="rm-modal-body">
          <div className="rm-field-row single">
            <div className="rm-field">
              <label>Jira Key</label>
              <input className="rm-input" value={form.jiraKey} onChange={e => set("jiraKey", e.target.value)} placeholder="CLOUDDEV-0000" />
            </div>
          </div>
          <div className="rm-field-row single">
            <div className="rm-field">
              <label>Title</label>
              <input className="rm-input" value={form.title} onChange={e => set("title", e.target.value)} placeholder="Card title" />
            </div>
          </div>
          <div className="rm-field-row">
            <div className="rm-field">
              <label>Status</label>
              <select className="rm-select-input" value={form.status} onChange={e => set("status", e.target.value)}>
                {RM_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="rm-field">
              <label>Assignee</label>
              <input className="rm-input" list="rm-assignee-list" value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="Start typing a name…" />
              <datalist id="rm-assignee-list">
                {knownAssignees.map(a => <option key={a} value={a} />)}
              </datalist>
            </div>
          </div>
          <div className="rm-field-row single">
            <div className="rm-field">
              <label>Readiness</label>
              <select className="rm-select-input" value={form.readiness || ""} onChange={e => set("readiness", e.target.value)}>
                <option value="">-</option>
                {RM_READINESS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="rm-field-row">
            <div className="rm-field">
              <label>Start date</label>
              <input type="date" className="rm-input" value={(form.start || "").split("T")[0]} onChange={e => setStart(e.target.value)} style={{ colorScheme: "dark", cursor: "pointer" }} />
            </div>
            <div className="rm-field">
              <label>Days (working)</label>
              <input type="number" min="1" className="rm-input" value={form.durationDays ?? ""} onChange={e => setDays(e.target.value)} placeholder="e.g. 5" title="Working days (Mon-Fri, excl. UK bank holidays). Sets the finish date from the start." />
            </div>
            <div className="rm-field">
              <label>Finish date</label>
              <input type="date" className="rm-input" value={(form.finish || "").split("T")[0]} onChange={e => setFinish(e.target.value)} style={{ colorScheme: "dark", cursor: "pointer" }} />
            </div>
          </div>
          <div className="rm-field-row">
            <div className="rm-field">
              <label>Due month</label>
              <select className="rm-select-input" value={form.releaseMonth || ""} onChange={e => set("releaseMonth", e.target.value)}>
                <option value="">-</option>
                {RM_MONTHS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="rm-field">
              <label>Due date</label>
              <input type="date" className="rm-input" value={(form.releaseDate || "").split("T")[0]}
                onChange={e => {
                  const v = e.target.value;
                  set("releaseDate", v);
                  // Auto-fill Due month from the due date
                  if (v) {
                    try {
                      const mth = ["January","February","March","April","May","June","July","August","September","October","November","December"][new Date(v + "T00:00:00").getMonth()];
                      if (mth) set("releaseMonth", mth);
                    } catch {}
                  }
                }}
                style={{ colorScheme: "dark", cursor: "pointer" }} />
            </div>
          </div>
          <div className="rm-field-row single">
            <div className="rm-field">
              <label>Customers (Affected Clients)</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                {(Array.isArray(form.customers) ? form.customers : (form.customers ? [form.customers] : [])).map((cu, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 12, padding: "2px 8px" }}>
                    {cu}
                    <span style={{ cursor: "pointer", color: "var(--text-dim)" }} onClick={() => {
                      const cur = Array.isArray(form.customers) ? form.customers : (form.customers ? [form.customers] : []);
                      set("customers", cur.filter((_, idx) => idx !== i));
                    }}>✕</span>
                  </span>
                ))}
              </div>
              <select className="rm-select-input" value="" onChange={e => {
                if (!e.target.value) return;
                const cur = Array.isArray(form.customers) ? form.customers : (form.customers ? [form.customers] : []);
                if (!cur.includes(e.target.value)) set("customers", [...cur, e.target.value]);
              }}>
                <option value="">+ Add affected client…</option>
                {JIRA_CLIENT_LIST.filter(c => c !== "All Clients").map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="rm-field-row single">
            <div className="rm-field">
              <label>Dependencies (Jira keys, comma-separated)</label>
              <input className="rm-input" value={Array.isArray(form.dependencies) ? form.dependencies.join(", ") : form.dependencies || ""}
                onChange={e => set("dependencies", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                placeholder="e.g. CLOUDDEV-2481, CLOUDDEV-2485" />
            </div>
          </div>
          <div className="rm-field-row single">
            <div className="rm-field">
              <label>Comments</label>
              <textarea className="rm-textarea" value={form.comments || ""} onChange={e => set("comments", e.target.value)} placeholder="Notes, context..." />
            </div>
          </div>
        </div>
        <div className="rm-modal-footer">
          <button className="btn danger" style={{ fontSize: 12, marginRight: "auto" }} onClick={() => onDelete(card.id)}>🗑 Delete</button>
          {onMove && (moveTargets || []).length > 0 && (
            <select className="rm-select-input" style={{ fontSize: 12, maxWidth: 190 }} value=""
              title="Move this card to another roadmap"
              onChange={e => { if (e.target.value) onMove(card, e.target.value); }}>
              <option value="">↗ Move to roadmap…</option>
              {moveTargets.map(t => <option key={t.name} value={t.name}>{t.label}</option>)}
            </select>
          )}
          <button className="btn" style={{ fontSize: 12 }} onClick={onClose}>Cancel</button>
          <button className="btn primary" style={{ fontSize: 12 }} onClick={() => onSave(form)}>✓ Save</button>
        </div>
      </div>
    </div>
  );
}

function RoadmapView({ roadmapName = "CAFM Dev Roadmap", config = {}, displayLabel, onRename, onShare, onDelete, myRole = "owner", ownerless = false, allRoadmaps = [] }) {
  const [cards, setCards]           = React.useState([]);
  const [loading, setLoading]       = React.useState(true);
  const [viewMode, setViewMode]     = React.useState("board"); // board | gantt
  const [editCard, setEditCard]     = React.useState(null);
  const [filterStatus, setFilterStatus] = React.useState("");
  const [filterCustomer, setFilterCustomer] = React.useState("");
  const [filterOwner, setFilterOwner]   = React.useState("");
  const [dragCard, setDragCard]     = React.useState(null);
  const [dragOverLane, setDragOverLane] = React.useState(null);
  const [sortCol, setSortCol]       = React.useState("order");
  const [sortDir, setSortDir]       = React.useState("asc");
  const [inlineEdit, setInlineEdit] = React.useState(null); // { id, field }
  const [inlineVal, setInlineVal]   = React.useState("");
  const [gridDragId, setGridDragId] = React.useState(null);
  const [gridDragOver, setGridDragOver] = React.useState(null); // { id, pos: 'top'|'bottom' }
  // Board period mode: "month-name" (default, groups by releaseMonth name) or date-based
  // ("week" | "fortnight" | "month") which generate date-range columns from an anchor.
  const [periodMode, setPeriodMode] = React.useState(() => {
    try {
      // One-time migration: earlier builds defaulted the board to "month-name". Clear
      // that saved default once so the new "status" default takes effect; a value the
      // user deliberately picked (anything other than month-name) is preserved.
      const migrated = localStorage.getItem("taskflow_rm_period_migrated_v2");
      const saved = localStorage.getItem(`taskflow_rm_period_${roadmapName}`);
      if (!migrated && saved === "month-name") {
        localStorage.removeItem(`taskflow_rm_period_${roadmapName}`);
        return "status";
      }
      return saved || "status";
    } catch { return "status"; }
  });
  React.useEffect(() => { try { localStorage.setItem("taskflow_rm_period_migrated_v2", "1"); } catch {} }, []);
  const [periodAnchor, setPeriodAnchor] = React.useState(() => {
    try { return localStorage.getItem(`taskflow_rm_anchor_${roadmapName}`) || new Date().toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
  });
  const [periodEnd, setPeriodEnd] = React.useState(() => {
    try {
      const saved = localStorage.getItem(`taskflow_rm_end_${roadmapName}`);
      if (saved) return saved;
      const d = new Date(); d.setMonth(d.getMonth() + 2);
      return d.toISOString().slice(0, 10);
    } catch { const d = new Date(); d.setMonth(d.getMonth() + 2); return d.toISOString().slice(0, 10); }
  });
  React.useEffect(() => { try { localStorage.setItem(`taskflow_rm_period_${roadmapName}`, periodMode); } catch {} }, [periodMode, roadmapName]);
  React.useEffect(() => { try { localStorage.setItem(`taskflow_rm_anchor_${roadmapName}`, periodAnchor); } catch {} }, [periodAnchor, roadmapName]);
  React.useEffect(() => { try { localStorage.setItem(`taskflow_rm_end_${roadmapName}`, periodEnd); } catch {} }, [periodEnd, roadmapName]);

  React.useEffect(() => {
    setLoading(true);
    setCards([]);
    fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}`)
      .then(r => r.ok ? r.json() : { cards: [] })
      .then(d => { setCards(d.cards || []); })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, [roadmapName]);

  // Sync card statuses from Jira, and drop cards whose Jira status is Done
  const [syncingJira, setSyncingJira] = React.useState(false);
  const isDoneStatus = (statusName) => {
    if (!statusName) return false;
    const s = statusName.trim().toLowerCase();
    // Explicit list of genuinely-finished statuses. We do NOT use a loose "contains
    // complete" match, because mid-pipeline stages like "Code Complete" and
    // "QA Complete" are not done and must stay on the roadmap.
    const doneExact = new Set([
      "done","closed","resolved","complete","completed","deployed","live",
      "released","release & merged","cancelled","canceled","won't do","wont do",
    ]);
    if (doneExact.has(s)) return true;
    // Also treat clear "closed/resolved as ..." phrasings as done, but never anything
    // that is a Complete-stage (Code Complete, QA Complete, etc).
    if (/\bcomplete\b/.test(s) && !/(code|qa|dev|development|spec|build)\s+complete/.test(s)) {
      // e.g. "Completed" handled above; guard leaves stage-completes out
      return s === "complete" || s === "completed";
    }
    return false;
  };

  const syncFromJira = React.useCallback(async (cardList) => {
    const cfg = config || {};
    if (!cfg.jiraUrl || !cfg.jiraEmail || !cfg.jiraToken) return;
    const keyOf = (v) => { const m = String(v || "").match(/[A-Z][A-Z0-9]+-\d+/i); return m ? m[0].toUpperCase() : ""; };
    const keyed = cardList.filter(c => keyOf(c.jiraKey));
    if (!keyed.length) return;
    setSyncingJira(true);
    try {
      const auth = btoa(`${cfg.jiraEmail}:${cfg.jiraToken}`);
      const base = cfg.jiraUrl.replace(/\/$/, "");
      const keys = [...new Set(keyed.map(c => keyOf(c.jiraKey)))];
      const proxyBase = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
        ? "http://localhost:3001/api/proxy?url=" : "/api/proxy?url=";
      // Chunk into batches of 50 keys
      const infoByKey = {};
      for (let i = 0; i < keys.length; i += 50) {
        const chunk = keys.slice(i, i + 50);
        const jql = "issueKey in (" + chunk.map(k => '"' + k + '"').join(",") + ")";
        const url = `${base}/rest/api/3/search/jql?jql=${encodeURIComponent(jql)}&fields=status,duedate,customfield_10015,assignee,customfield_10435&maxResults=50`;
        const res = await fetch(proxyBase + encodeURIComponent(url), { headers: { Authorization: "Basic " + auth, Accept: "application/json" } });
        if (res.ok) {
          const d = await res.json();
          (d.issues || []).forEach(iss => {
            const flatC = (v) => {
              if (!v) return [];
              if (Array.isArray(v)) return v.map(x => (typeof x === "object" ? (x.value || x.name || x.displayName || "") : String(x))).filter(Boolean);
              if (typeof v === "object") return [v.value || v.name || v.displayName || ""].filter(Boolean);
              return [String(v)];
            };
            infoByKey[iss.key.toUpperCase()] = {
              status: iss.fields.status?.name || "",
              due: iss.fields.duedate || iss.fields.customfield_10015 || "",
              assignee: iss.fields.assignee?.displayName || "",
              customers: flatC(iss.fields.customfield_10435),
            };
          });
        }
      }
      // Apply updates: update status, dates, assignee, clients; drop if Done
      const toDelete = [];
      const updated = [];
      cardList.forEach(c => {
        const k = keyOf(c.jiraKey);
        const info = k ? infoByKey[k] : null;
        if (!info) return;
        const jiraStatus = info.status;
        if (jiraStatus && isDoneStatus(jiraStatus)) {
          toDelete.push(c);
          return;
        }
        const patch = {};
        if (jiraStatus && jiraStatus !== c.status) patch.status = jiraStatus;
        if (info.assignee && info.assignee !== c.owner) patch.owner = info.assignee;
        if (info.customers && info.customers.length && JSON.stringify(info.customers) !== JSON.stringify(c.customers || [])) patch.customers = info.customers;
        // Pull the Jira due/finish date through to the card's finish + release fields
        if (info.due) {
          const jDue = String(info.due).split("T")[0];
          if (jDue !== (c.finish || "").split("T")[0]) patch.finish = jDue;
          if (jDue !== (c.releaseDate || "").split("T")[0]) patch.releaseDate = jDue;
          const mth = monthFromDate(jDue);
          if (mth && mth !== c.releaseMonth) patch.releaseMonth = mth;
        }
        if (Object.keys(patch).length) updated.push({ ...c, ...patch });
      });
      // Persist deletions and updates
      for (const c of toDelete) {
        await fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}/cards/${c.id}`, { method: "DELETE" }).catch(() => {});
      }
      for (const c of updated) {
        await fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}/cards/${c.id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(c),
        }).catch(() => {});
      }
      if (toDelete.length || updated.length) {
        setCards(prev => prev
          .filter(c => !toDelete.find(d => d.id === c.id))
          .map(c => updated.find(u => u.id === c.id) || c));
        console.log(`[Roadmap] Jira sync: ${updated.length} updated, ${toDelete.length} dropped (Done)`);
      }
    } catch (e) {
      console.warn("[Roadmap] Jira sync failed:", e.message);
    } finally {
      setSyncingJira(false);
    }
  }, [config, roadmapName]);

  // Auto-sync from Jira once cards are loaded
  React.useEffect(() => {
    if (!loading && cards.length > 0) {
      syncFromJira(cards);
    }
  }, [loading, roadmapName]);

  const canEdit = myRole !== "view"; // owner and edit can modify; view is read-only

  const saveCard = async (updated) => {
    if (!canEdit) { setEditCard(null); return; }
    const isNew = !cards.find(c => c.id === updated.id);
    const base = `${ROADMAP_API}/${encodeURIComponent(roadmapName)}`;
    const extractKeyLocal = (v) => { const m = String(v || "").match(/[A-Z][A-Z0-9]+-\d+/i); return m ? m[0].toUpperCase() : ""; };

    // Build the post-save card list, then cascade: any card that depends on a card
    // whose finish changed gets its start snapped to the next working day after that
    // finish, its duration preserved (finish recomputed), and the ripple continues.
    let workingList = isNew ? [...cards, updated] : cards.map(c => c.id === updated.id ? updated : c);

    const cascadeChanges = [];
    const keyToCard = () => {
      const m = {};
      workingList.forEach(c => { const k = extractKeyLocal(c.jiraKey); if (k) m[k] = c; });
      return m;
    };
    // Iteratively resolve: repeat until no card needs moving (guards against cycles).
    let pass = 0;
    let changedThisPass = true;
    while (changedThisPass && pass < 50) {
      changedThisPass = false; pass++;
      const byKey = keyToCard();
      workingList = workingList.map(card => {
        const deps = card.dependencies || [];
        if (!deps.length || !card.start) return card;
        // Find the latest prerequisite finish among this card's dependencies
        let latestFinish = null;
        deps.forEach(dep => {
          const pre = byKey[extractKeyLocal(dep)];
          if (pre && pre.finish) { if (!latestFinish || pre.finish > latestFinish) latestFinish = pre.finish; }
        });
        if (!latestFinish) return card;
        const desiredStart = rmNextWorkingDay(latestFinish);
        if (desiredStart && desiredStart !== (card.start || "").split("T")[0]) {
          // Preserve duration: recompute finish from the new start using the card's day span
          const span = card.durationDays || rmWorkingDaysBetween(card.start, card.finish) || 1;
          const newCard = { ...card, start: desiredStart, finish: rmAddWorkingDays(desiredStart, span), durationDays: span };
          cascadeChanges.push(newCard);
          changedThisPass = true;
          return newCard;
        }
        return card;
      });
    }

    // Persist the edited card
    const url = isNew ? `${base}/cards` : `${base}/cards/${updated.id}`;
    const method = isNew ? "POST" : "PUT";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(updated) });
    let savedNew = null;
    if (res.ok && isNew) savedNew = await res.json();
    // Persist any cascaded dependents (dedupe by id, exclude the just-saved card)
    const uniqueCascade = [];
    const seen = new Set([updated.id]);
    for (let i = cascadeChanges.length - 1; i >= 0; i--) {
      const c = cascadeChanges[i];
      if (!seen.has(c.id)) { seen.add(c.id); uniqueCascade.push(c); }
    }
    for (const c of uniqueCascade) {
      await fetch(`${base}/cards/${c.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(c) }).catch(() => {});
    }

    // Update local state
    setCards(prev => {
      let next = isNew ? [...prev, savedNew || updated] : prev.map(c => c.id === updated.id ? updated : c);
      const cascadeMap = {}; uniqueCascade.forEach(c => { cascadeMap[c.id] = c; });
      next = next.map(c => cascadeMap[c.id] || c);
      return next;
    });
    setEditCard(null);
  };

  const deleteCard = async (id) => {
    if (!canEdit) return;
    await fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}/cards/${id}`, { method: "DELETE" });
    setCards(prev => prev.filter(c => c.id !== id));
    setEditCard(null);
  };

  // Move a card to another roadmap: create it on the target, then remove from this one.
  const moveCard = async (cardToMove, targetName) => {
    if (!canEdit || !targetName || targetName === roadmapName) return;
    try {
      const target = allRoadmaps.find(r => r.name === targetName);
      const payload = { ...cardToMove, id: "rm-" + Date.now() + "-" + Math.floor(Math.random() * 1000), order: 9999 };
      const res = await fetch(`${ROADMAP_API}/${encodeURIComponent(targetName)}/cards`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (!res.ok) { setEditCard(null); return; }
      // Remove from the current roadmap
      await fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}/cards/${cardToMove.id}`, { method: "DELETE" }).catch(() => {});
      setCards(prev => prev.filter(c => c.id !== cardToMove.id));
      setEditCard(null);
    } catch { setEditCard(null); }
  };

  const newCard = () => {
    if (!canEdit) return;
    setEditCard({
      id: "rm-" + Date.now(),
      jiraKey: "", title: "", status: "To Do", owner: "", readiness: "",
      start: "", finish: "", releaseMonth: "", releaseDate: "",
      customers: [], dependencies: [], comments: "", order: cards.length,
    });
  };

  const [importing, setImporting] = React.useState(false);

  const clearAll = async () => {
    if (!window.confirm(`Remove ALL ${cards.length} cards from this roadmap? This cannot be undone.`)) return;
    const snapshot = [...cards];
    setCards([]);
    for (const c of snapshot) {
      await fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}/cards/${c.id}`, { method: "DELETE" }).catch(() => {});
    }
  };

  // Import cards from Jira: accepts epic keys and/or individual issue keys.
  // Epics expand to all their child issues; individual keys are added directly.
  const importFromJira = async () => {
    const cfg = config || {};
    if (!cfg.jiraUrl || !cfg.jiraEmail || !cfg.jiraToken) {
      alert("Connect Jira in Config first.");
      return;
    }
    const input = window.prompt("Enter Jira epic key(s) and/or card key(s), separated by commas.\n\nEpics will pull in all their child issues. Example:\nCLOUDDEV-2776, CLOUDDEV-2500");
    if (!input || !input.trim()) return;
    // Extract every KEY-123 style token from the input, whether the user typed
    // bare keys, a full browse URL, or a mix. This is far more forgiving than
    // requiring each comma-separated token to be an exact key.
    const keys = [...new Set((input.toUpperCase().match(/[A-Z][A-Z0-9]+-\d+/g) || []))];
    if (!keys.length) { alert("No valid Jira keys found.\n\nEnter an epic key like SI-157, or paste a Jira URL."); return; }

    setImporting(true);
    try {
      const auth = btoa(`${cfg.jiraEmail}:${cfg.jiraToken}`);
      const base = cfg.jiraUrl.replace(/\/$/, "");
      const proxyBase = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
        ? "http://localhost:3001/api/proxy?url=" : "/api/proxy?url=";

      const fields = "summary,status,assignee,duedate,customfield_10015,customfield_10435,parent,issuetype";
      const errorsSeen = [];
      // Fetch helper that runs one JQL and returns issues; captures error detail
      const runJql = async (jql) => {
        try {
          let out = [];
          let startAt = 0;
          for (let page = 0; page < 10; page++) {
            const url = `${base}/rest/api/3/search/jql?jql=${encodeURIComponent(jql)}&fields=${encodeURIComponent(fields)}&maxResults=100&startAt=${startAt}`;
            const res = await fetch(proxyBase + encodeURIComponent(url), { headers: { Authorization: "Basic " + auth, Accept: "application/json" } });
            if (!res.ok) {
              let body = "";
              try { body = JSON.stringify(await res.json()); } catch { body = await res.text().catch(() => ""); }
              errorsSeen.push(`[${res.status}] ${jql} -> ${body.substring(0, 200)}`);
              console.warn("[Roadmap] JQL failed:", jql, res.status, body);
              return null;
            }
            const data = await res.json();
            const issues = data.issues || [];
            out = out.concat(issues);
            if (issues.length < 100) break;
            startAt += 100;
          }
          return out;
        } catch (e) {
          errorsSeen.push(`[ERR] ${jql} -> ${e.message}`);
          console.warn("[Roadmap] JQL threw:", jql, e.message);
          return null;
        }
      };

      const keyList = keys.map(k => `"${k}"`).join(",");
      const byId = {};
      const addIssues = (arr) => { (arr || []).forEach(iss => { byId[iss.key.toUpperCase()] = iss; }); };

      // Try every mechanism Jira uses to relate children to an epic; merge all results.
      addIssues(await runJql(`issuekey in (${keyList})`));                    // the keys themselves
      addIssues(await runJql(`parent in (${keyList})`));                      // team-managed epic children / subtasks
      addIssues(await runJql(`"Epic Link" in (${keyList})`));                 // company-managed (classic) epic children
      addIssues(await runJql(`parentEpic in (${keyList})`));                  // some instances
      addIssues(await runJql(`"Parent Link" in (${keyList})`));               // Advanced Roadmaps parent link
      // Per-key single-value form (some instances reject "in" on custom fields)
      for (const k of keys) {
        addIssues(await runJql(`"Epic Link" = ${k}`));
        addIssues(await runJql(`parent = ${k}`));
      }

      let allIssues = Object.values(byId);
      console.log(`[Roadmap] Import found ${allIssues.length} issue(s) for keys: ${keys.join(", ")}`, allIssues.map(i => `${i.key} (${i.fields.issuetype?.name})`));
      if (errorsSeen.length) console.warn("[Roadmap] Some import queries errored:\n" + errorsSeen.join("\n"));

      if (!allIssues.length) {
        alert(`No issues found for: ${keys.join(", ")}\n\n${errorsSeen.length ? "Some queries were rejected by Jira (see console F12 for details). This is usually a field-name or permissions issue." : "The epic may have no child issues, or they may all be Done."}`);
        setImporting(false);
        return;
      }

      // Skip epics themselves and issues already on the roadmap
      const existingKeys = new Set(cards.map(c => (c.jiraKey || "").toUpperCase()));
      // Use the same precise done check as Sync, so mid-pipeline stages like
      // "Code Complete" and "QA Complete" are NOT treated as done and are imported.
      let skippedEpic = 0, skippedExisting = 0, skippedDone = 0;
      // Build a lookup of epic key -> epic name from the fetched issues (the epics
      // themselves come back via the issuekey query) plus any child's parent field.
      const epicNames = {};
      allIssues.forEach(iss => {
        if ((iss.fields.issuetype?.name || "").toLowerCase() === "epic") {
          epicNames[iss.key.toUpperCase()] = iss.fields.summary || iss.key;
        }
        const p = iss.fields.parent;
        if (p && p.key) epicNames[p.key.toUpperCase()] = p.fields?.summary || p.key;
      });

      const newCards = [];
      allIssues.forEach((iss, i) => {
        const key = iss.key.toUpperCase();
        const type = iss.fields.issuetype?.name || "";
        if (type.toLowerCase() === "epic") { skippedEpic++; return; } // don't add the epic container itself
        if (existingKeys.has(key)) { skippedExisting++; return; }
        const status = iss.fields.status?.name || "To Do";
        if (isDoneStatus(status)) { skippedDone++; return; } // don't import already-done issues
        const due = iss.fields.duedate || iss.fields.customfield_10015 || "";
        // Determine the epic this child belongs to
        const parentKey = iss.fields.parent?.key ? iss.fields.parent.key.toUpperCase() : "";
        const epicKey = parentKey || (keys.length === 1 ? keys[0] : "");
        const epicName = (parentKey && epicNames[parentKey]) || (epicKey && epicNames[epicKey]) || "";
        // Affected Clients (customfield_10435) -> customers on the card
        const flatClients = (v) => {
          if (!v) return [];
          if (Array.isArray(v)) return v.map(x => (typeof x === "object" ? (x.value || x.name || x.displayName || "") : String(x))).filter(Boolean);
          if (typeof v === "object") return [v.value || v.name || v.displayName || ""].filter(Boolean);
          return [String(v)];
        };
        newCards.push({
          id: "rm-" + Date.now() + "-" + i,
          jiraKey: key,
          title: iss.fields.summary || key,
          issueType: type,
          epicKey,
          epicName,
          status,
          owner: iss.fields.assignee?.displayName || "",
          start: iss.fields.customfield_10015 || "",
          finish: due,
          releaseMonth: monthFromDate(due),
          releaseDate: due,
          customers: flatClients(iss.fields.customfield_10435),
          dependencies: [],
          comments: "",
          order: cards.length + newCards.length,
        });
        existingKeys.add(key);
      });

      if (!newCards.length) {
        alert(`Found ${allIssues.length} issue(s) but nothing new to add.\n\nSkipped: ${skippedEpic} epic(s), ${skippedExisting} already on roadmap, ${skippedDone} already Done.`);
        setImporting(false);
        return;
      }

      // Persist each new card
      const saved = [];
      for (const c of newCards) {
        try {
          const res = await fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}/cards`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(c),
          });
          if (res.ok) saved.push(await res.json());
          else saved.push(c);
        } catch { saved.push(c); }
      }
      setCards(prev => [...prev, ...saved]);
      alert(`Imported ${saved.length} card(s) from Jira.`);
    } catch (e) {
      console.warn("[Roadmap] Import failed:", e.message);
      alert("Import failed: " + e.message);
    } finally {
      setImporting(false);
    }
  };

  // Filtered cards
  const filtered = React.useMemo(() => {
    return cards
      .filter(c => !/not required/i.test(c.status || ""))
      .filter(c => !filterStatus || c.status === filterStatus)
      .filter(c => !filterCustomer || (c.customers || []).some(cu => cu.toLowerCase().includes(filterCustomer.toLowerCase())))
      .filter(c => !filterOwner || (c.owner || "").toLowerCase().includes(filterOwner.toLowerCase()))
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }, [cards, filterStatus, filterCustomer, filterOwner]);

  // Board - group by release month
  const lanes = React.useMemo(() => {
    const monthOrder = ["January","February","March","April","May","June","July","August","September","October","November","December","TBC",""];
    // Workflow order, least to most progressed. Higher rank = further along.
    // Rendered top-to-bottom, so we sort DESC to put Release at the top, Backlog at the bottom.
    const statusRank = (status) => {
      const s = (status || "").toLowerCase();
      if (/(release|released|deploy|deployed|live|ready for release)/.test(s)) return 6;
      if (/(qa|test|uat|review(?!ed))/.test(s)) return 5;
      if (/(rework)/.test(s)) return 4;
      if (/(in progress|progress|dev|development|build|code)/.test(s)) return 3;
      if (/(to do|todo|selected|open|new)/.test(s)) return 2;
      if (/(backlog)/.test(s)) return 1;
      return 0; // unknown statuses sink to the very bottom
    };
    const map = {};
    filtered.forEach(c => {
      const key = c.releaseMonth || "TBC";
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    // Sort each lane by status (most progressed first = top), then by title for stability
    Object.values(map).forEach(cards => {
      cards.sort((a, b) => {
        const diff = statusRank(b.status) - statusRank(a.status);
        if (diff !== 0) return diff;
        return (a.title || "").localeCompare(b.title || "");
      });
    });
    // Any month present in the data but not in the known order (safety net) gets appended
    const extra = Object.keys(map).filter(k => !monthOrder.includes(k));
    const order = [...monthOrder, ...extra];
    return order.filter(m => map[m]?.length).map(m => ({ month: m, cards: map[m] }));
  }, [filtered]);

  // ── Date-based period lanes (week / fortnight / month) ──
  const cardDateMs = (c) => {
    const d = c.releaseDate || c.finish || c.start;
    if (!d) return null;
    const t = new Date(String(d).split("T")[0] + "T00:00:00").getTime();
    return isNaN(t) ? null : t;
  };
  const startOfWeekMonday = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
  const fmtDayMon = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const datePeriods = React.useMemo(() => {
    if (periodMode === "month-name") return null;
    const periods = [];
    const endMs = new Date((periodEnd || periodAnchor) + "T23:59:59").getTime();
    let cursor;
    if (periodMode === "month") {
      const a = new Date(periodAnchor + "T00:00:00"); cursor = new Date(a.getFullYear(), a.getMonth(), 1);
    } else {
      cursor = startOfWeekMonday(new Date(periodAnchor + "T00:00:00"));
    }
    let guard = 0;
    while (cursor.getTime() <= endMs && guard < 260) {
      let start = new Date(cursor), end, label;
      if (periodMode === "month") {
        end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59);
        label = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      } else {
        const days = periodMode === "fortnight" ? 14 : 7;
        end = new Date(start); end.setDate(end.getDate() + days - 1); end.setHours(23, 59, 59, 999);
        label = `${fmtDayMon(start)} - ${fmtDayMon(end)}`;
        cursor = new Date(start); cursor.setDate(cursor.getDate() + days);
      }
      periods.push({ key: start.toISOString().slice(0, 10), label, startMs: start.getTime(), endMs: end.getTime(), cards: [] });
      guard++;
    }
    return periods;
  }, [periodMode, periodAnchor, periodEnd]);

  const dateLanes = React.useMemo(() => {
    if (!datePeriods) return null;
    const statusRank = (status) => {
      const s = (status || "").toLowerCase();
      if (/(release|released|deploy|deployed|live|ready for release)/.test(s)) return 6;
      if (/(qa|test|uat|review(?!ed))/.test(s)) return 5;
      if (/(rework)/.test(s)) return 4;
      if (/(in progress|progress|dev|development|build|code)/.test(s)) return 3;
      if (/(to do|todo|selected|open|new)/.test(s)) return 2;
      if (/(backlog)/.test(s)) return 1;
      return 0;
    };
    const periods = datePeriods.map(p => ({ ...p, cards: [] }));
    const unscheduled = [];
    filtered.forEach(c => {
      const ms = cardDateMs(c);
      if (ms === null) { unscheduled.push(c); return; }
      const p = periods.find(pp => ms >= pp.startMs && ms <= pp.endMs);
      if (p) p.cards.push(c); else unscheduled.push(c);
    });
    [...periods.map(p => p.cards), unscheduled].forEach(cardsArr => cardsArr.sort((a, b) => {
      const diff = statusRank(b.status) - statusRank(a.status);
      return diff !== 0 ? diff : (a.title || "").localeCompare(b.title || "");
    }));
    const out = periods.slice();
    if (unscheduled.length) out.push({ key: "__unscheduled__", label: "No date / TBC", cards: unscheduled, startMs: null, endMs: null });
    return out;
  }, [datePeriods, filtered]);

  // ── Status lanes (kanban grouped by the card's Jira status) ──
  const statusLanes = React.useMemo(() => {
    if (periodMode !== "status") return null;
    // Count cards per status (case-insensitive match against the defined order).
    const byStatus = {};
    filtered.forEach(c => {
      const s = (c.status || "").trim() || "TBC";
      if (!byStatus[s]) byStatus[s] = [];
      byStatus[s].push(c);
    });
    const norm = (s) => s.toLowerCase();
    const usedKeys = Object.keys(byStatus);
    // Columns in the defined left-to-right order, but only those that have cards.
    const ordered = RM_STATUS_BOARD_ORDER
      .map(name => {
        const matchKey = usedKeys.find(k => norm(k) === norm(name));
        return matchKey ? { key: matchKey, label: name, cards: byStatus[matchKey] } : null;
      })
      .filter(Boolean);
    // Any statuses on cards that are not in the defined order go at the end (also only if present).
    const orderedNormSet = new Set(RM_STATUS_BOARD_ORDER.map(norm));
    const extras = usedKeys
      .filter(k => !orderedNormSet.has(norm(k)))
      .sort()
      .map(k => ({ key: k, label: k, cards: byStatus[k] }));
    return [...ordered, ...extras];
  }, [periodMode, filtered]);

  // ── Owner lanes (kanban grouped by the card's owner) ──
  const ownerLanes = React.useMemo(() => {
    if (periodMode !== "owner") return null;
    const owners = [...new Set(filtered.map(c => c.owner).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const lanes = owners.map(owner => ({ key: owner, label: owner, cards: filtered.filter(c => c.owner === owner) }));
    const unassigned = filtered.filter(c => !c.owner);
    if (unassigned.length) lanes.push({ key: "__unassigned__", label: "Unassigned", cards: unassigned });
    return lanes;
  }, [periodMode, filtered]);

  // Lanes grouped by Jira issue type
  const typeLanes = React.useMemo(() => {
    if (periodMode !== "type") return null;
    const order = ["Epic", "Story", "Task", "Bug", "Sub-task"];
    const present = {};
    filtered.forEach(c => { const t = c.issueType || "Untyped"; present[t] = true; });
    const norm = (s) => s.toLowerCase();
    const ordered = order.filter(t => Object.keys(present).some(p => norm(p) === norm(t)));
    const extras = Object.keys(present).filter(p => !order.some(t => norm(t) === norm(p))).sort();
    return [...ordered, ...extras].map(t => ({
      key: t, label: t,
      cards: filtered.filter(c => norm(c.issueType || "Untyped") === norm(t)),
    })).filter(l => l.cards.length > 0);
  }, [periodMode, filtered]);

  // Drag handlers
  const onDragStart = (e, card) => { setDragCard(card); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e, month) => { e.preventDefault(); setDragOverLane(month); };
  const onDrop = async (e, month) => {
    e.preventDefault();
    if (!dragCard) return;
    const updated = { ...dragCard, releaseMonth: month };
    await saveCard(updated);
    setDragCard(null); setDragOverLane(null);
  };
  // Dropping onto a type lane changes the card's issue type
  const onDropType = async (e, typeKey) => {
    e.preventDefault();
    if (!dragCard || typeKey === "Untyped") { setDragCard(null); setDragOverLane(null); return; }
    const updated = { ...dragCard, issueType: typeKey };
    await saveCard(updated);
    setDragCard(null); setDragOverLane(null);
  };
  // Dropping onto an owner lane reassigns the card's owner
  const onDropOwner = async (e, ownerKey) => {
    e.preventDefault();
    if (!dragCard) return;
    const updated = { ...dragCard, owner: ownerKey === "__unassigned__" ? "" : ownerKey };
    await saveCard(updated);
    setDragCard(null); setDragOverLane(null);
  };
  // Dropping onto a status lane sets the card's status
  const onDropStatus = async (e, status) => {
    e.preventDefault();
    if (!dragCard) return;
    const updated = { ...dragCard, status };
    await saveCard(updated);
    setDragCard(null); setDragOverLane(null);
  };

  // Dropping onto a date-period lane sets the card's release date to that period's start
  const onDropDate = async (e, period) => {
    e.preventDefault();
    if (!dragCard) return;
    let updated;
    if (period.key === "__unscheduled__") {
      updated = { ...dragCard, releaseDate: "" };
    } else {
      const iso = new Date(period.startMs).toISOString().slice(0, 10);
      updated = { ...dragCard, releaseDate: iso, releaseMonth: monthFromDate(iso) };
    }
    await saveCard(updated);
    setDragCard(null); setDragOverLane(null);
  };

  // Gantt helpers
  // Weekly timeline. The range fits the actual work: from one week before the
  // earliest card date to one week after the latest, so it does not run on past the
  // real end of the plan. Falls back to a today-anchored window when no cards have dates.
  const ganttWeeks = React.useMemo(() => {
    const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
    // Gather all card dates (start, finish, releaseDate)
    const times = [];
    filtered.forEach(c => {
      [c.start, c.finish, c.releaseDate].forEach(v => { const t = rmDateToMs(v); if (t) times.push(t); });
    });
    let rangeStart, rangeEnd;
    // Start two weeks before today (a consistent, near-term lead-in) rather than
    // stretching all the way back to the earliest card. End at the latest card date
    // (plus a week of run-out), falling back to a short window when nothing is dated.
    const now = new Date();
    rangeStart = mondayOf(now);
    rangeStart.setDate(rangeStart.getDate() - 14);
    if (times.length) {
      rangeEnd = new Date(Math.max(...times));
      rangeEnd.setDate(rangeEnd.getDate() + 7);
      // Guard: never end before the start window
      if (rangeEnd.getTime() < rangeStart.getTime() + 14 * 86400000) {
        rangeEnd = new Date(rangeStart); rangeEnd.setDate(rangeEnd.getDate() + 56);
      }
    } else {
      rangeEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    }
    const weeks = [];
    let cursor = mondayOf(rangeStart);
    let guard = 0;
    while (cursor.getTime() <= rangeEnd.getTime() && guard < 200) {
      const wStart = new Date(cursor);
      const wEnd = new Date(cursor); wEnd.setDate(wEnd.getDate() + 6); wEnd.setHours(23, 59, 59, 999);
      weeks.push({
        start: wStart, end: wEnd,
        label: wStart.toLocaleDateString("en-GB", { day: "numeric" }), // day-of-month for the week
        monthKey: `${wStart.getFullYear()}-${wStart.getMonth()}`,
        monthLabel: wStart.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
      });
      cursor = new Date(cursor); cursor.setDate(cursor.getDate() + 7);
      guard++;
    }
    return weeks;
  }, [filtered]);

  // Group weeks into month spans for the header row above the weeks
  const ganttMonthSpans = React.useMemo(() => {
    const spans = [];
    ganttWeeks.forEach(w => {
      const last = spans[spans.length - 1];
      if (last && last.monthKey === w.monthKey) last.count++;
      else spans.push({ monthKey: w.monthKey, label: w.monthLabel, count: 1 });
    });
    return spans;
  }, [ganttWeeks]);

  const ganttStart = ganttWeeks.length ? ganttWeeks[0].start.getTime() : Date.now();
  const ganttEnd   = ganttWeeks.length ? ganttWeeks[ganttWeeks.length - 1].end.getTime() : Date.now();
  const ganttRange = Math.max(ganttEnd - ganttStart, 1);

  const barStyle = (card) => {
    let s = rmDateToMs(card.start) || rmDateToMs(card.releaseDate);
    let f = rmDateToMs(card.finish) || rmDateToMs(card.releaseDate);
    // If we only have one date, give the bar a one-week duration around it so
    // release-date-only (Jira imported) cards still show on the weekly timeline.
    const WEEK = 7 * 86400000;
    if (s && !f) f = s + WEEK;
    if (f && !s) s = f - WEEK;
    if (s && f && s === f) { f = s + WEEK; }
    if (!s || !f) return null;
    const left = Math.max(0, (s - ganttStart) / ganttRange * 100);
    const right = Math.min(100, (f - ganttStart) / ganttRange * 100);
    const width = Math.max(right - left, 1.5);
    return { left: left + "%", width: width + "%", background: RM_STATUS_COLOUR[card.status] || "#4a6070" };
  };

  const todayPct = ((Date.now() - ganttStart) / ganttRange * 100).toFixed(2);

  // Measure the gantt timeline pixel width so dependency arrows can use pixel coords
  // (SVG paths cannot use % for the path data reliably across browsers).
  const rmTimelineRef = React.useRef(null);
  const [rmTimelineW, setRmTimelineW] = React.useState(0);
  const ROW_H = 37; // .rm-gantt-row min-height 36px + 1px border-bottom
  const extractKey = (v) => { const m = String(v || "").match(/[A-Z][A-Z0-9]+-\d+/i); return m ? m[0].toUpperCase() : ""; };
  const rmGeomSig = JSON.stringify(filtered.map(c => [c.jiraKey, c.start, c.finish, c.releaseDate, c.dependencies]));

  // Shared row order for Grid AND Gantt, so both show cards in the same order.
  const sortedFiltered = React.useMemo(() => {
    const cols = { jiraKey: (c) => c.jiraKey, title: (c) => c.title, status: (c) => c.status,
      owner: (c) => c.owner, releaseMonth: (c) => RM_MONTHS.indexOf(c.releaseMonth), start: (c) => c.start || "", finish: (c) => c.finish || "",
      customers: (c) => (c.customers||[]).join(","), order: (c) => c.order ?? 999 };
    return [...filtered].sort((a, b) => {
      const va = cols[sortCol]?.(a) ?? ""; const vb = cols[sortCol]?.(b) ?? "";
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);
  React.useLayoutEffect(() => {
    const measure = () => { if (rmTimelineRef.current) setRmTimelineW(rmTimelineRef.current.offsetWidth); };
    measure();
    const raf = requestAnimationFrame(measure);
    const t1 = setTimeout(measure, 80);
    const t2 = setTimeout(measure, 260);
    let ro;
    if (typeof ResizeObserver !== "undefined" && rmTimelineRef.current) { ro = new ResizeObserver(measure); ro.observe(rmTimelineRef.current); }
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("resize", measure); cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); if (ro) ro.disconnect(); };
  }, [viewMode, rmGeomSig]);

  // Dependency arrows: from the end of a prerequisite bar to the start of the dependent bar.
  const ganttArrows = React.useMemo(() => {
    if (!rmTimelineW) return [];
    const rowIndex = {}, barPos = {};
    sortedFiltered.forEach((card, i) => {
      const key = extractKey(card.jiraKey);
      if (key) rowIndex[key] = i;
      const bs = barStyle(card);
      if (bs && key) {
        const left = parseFloat(bs.left) / 100 * rmTimelineW;
        const width = parseFloat(bs.width) / 100 * rmTimelineW;
        barPos[key] = { left, right: left + width };
      }
    });
    const arrows = [];
    sortedFiltered.forEach(card => {
      const toKey = extractKey(card.jiraKey);
      (card.dependencies || []).forEach(dep => {
        const fromKey = extractKey(dep);
        if (!fromKey || !barPos[fromKey] || !barPos[toKey]) return;
        if (rowIndex[fromKey] === undefined || rowIndex[toKey] === undefined) return;
        const y1 = rowIndex[fromKey] * ROW_H + ROW_H / 2;
        const y2 = rowIndex[toKey] * ROW_H + ROW_H / 2;
        arrows.push({ x1: barPos[fromKey].right, y1, x2: barPos[toKey].left, y2, fromKey, toKey });
      });
    });
    return arrows;
  }, [sortedFiltered, rmTimelineW, ganttStart, ganttRange]);

  const allCustomers = [...new Set(cards.flatMap(c => c.customers || []))].sort();
  const allOwners    = [...new Set(cards.map(c => c.owner).filter(Boolean))].sort();

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const startInline = (id, field, val) => { setInlineEdit({ id, field }); setInlineVal(val || ""); };
  const commitInline = async () => {
    if (!inlineEdit) return;
    const card = cards.find(c => c.id === inlineEdit.id);
    if (!card) return;
    const updated = { ...card, [inlineEdit.field]: inlineVal };
    await saveCard(updated);
    setInlineEdit(null);
  };

  const reorderGridRows = async (draggedId, targetId, pos) => {
    if (!canEdit) return;
    if (draggedId === targetId) return;
    // Build new order from current sortedFiltered
    const ids = sortedFiltered.map(c => c.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const newIds = [...ids];
    newIds.splice(fromIdx, 1);
    const insertAt = pos === "top" ? newIds.indexOf(targetId) : newIds.indexOf(targetId) + 1;
    newIds.splice(insertAt, 0, draggedId);
    // Update order field on each card and save
    const orderMap = {};
    newIds.forEach((id, i) => { orderMap[id] = i; });
    const updatedCards = cards.map(c => (orderMap[c.id] !== undefined ? { ...c, order: orderMap[c.id] } : c));
    setCards(updatedCards);
    // Manual reordering only makes sense in manual-order view, so switch to it so the
    // change is visible rather than being immediately re-sorted by another column.
    setSortCol("order"); setSortDir("asc");
    // Persist to server
    await fetch(`${ROADMAP_API}/${encodeURIComponent(roadmapName)}/reorder`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderedIds: newIds }),
    });
  };

  if (loading) return <div style={{ padding: 24, color: "var(--text-muted)" }}>Loading roadmap…</div>;

  const renderBoardCard = (card) => (
    <div key={card.id}
      className={`rm-card ${RM_STATUS_CLASS[card.status] || "s-todo"} ${dragCard?.id === card.id ? "dragging" : ""}`}
      draggable
      onDragStart={e => onDragStart(e, card)}
      onDragEnd={() => { setDragCard(null); setDragOverLane(null); }}
      onClick={() => setEditCard(card)}>
      <div className="rm-card-key" style={{ wordBreak: "break-word", whiteSpace: "normal", display: "flex", alignItems: "center", gap: 5 }}>
        {card.issueType && (() => { const tm = rmTypeMeta(card.issueType); return <span title={tm.label} style={{ color: tm.colour, fontWeight: 700, flexShrink: 0 }}>{tm.icon}</span>; })()}
        <span>{(() => {
          const raw = String(card.jiraKey || "");
          const m = raw.match(/[A-Z][A-Z0-9]+-\d+/i);
          return m ? m[0] : raw;
        })()}</span>
      </div>
      <div className="rm-card-title">{card.title}</div>
      {(card.epicKey || card.epicName) && (
        <div style={{ fontSize: 9, color: "var(--text-dim)", marginBottom: 5, display: "flex", alignItems: "center", gap: 3 }}
          title={`Epic: ${card.epicKey}${card.epicName ? " - " + card.epicName : ""}`}>
          <span style={{ color: "#c47ef0" }}>◈</span>
          <span style={{ fontWeight: 600, color: "#c47ef0" }}>{card.epicKey}</span>
          {card.epicName && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{card.epicName}</span>}
        </div>
      )}
      {card.releaseDate && (
        <div style={{ fontSize: 9, color: "var(--text-dim)", marginBottom: 4 }}>📅 {(() => { try { return new Date(card.releaseDate).toLocaleDateString("en-GB"); } catch { return card.releaseDate; } })()}</div>
      )}
      <div className="rm-card-meta">
        <span className={`rm-badge rm-badge-status`}>{card.status}</span>
        {card.owner && <span className="rm-badge rm-badge-owner">👤 {card.owner}</span>}
        {(card.customers || []).slice(0, 2).map(cu => (
          <span key={cu} className="rm-badge rm-badge-customer">{cu}</span>
        ))}
        {(card.dependencies || []).length > 0 && (
          <span style={{ fontSize: 9, color: "var(--amber)" }}>🔗 {card.dependencies.length} dep{card.dependencies.length !== 1 ? "s" : ""}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="rm-shell">
      {/* Top bar */}
      <div className="rm-topbar">
        <span className="rm-title">🗺 {displayLabel || roadmapName}</span>
        {myRole && myRole !== "owner" && !ownerless && (
          <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 10, background: "var(--surface2)", color: "var(--text-dim)", border: "1px solid var(--border)" }}>
            {myRole === "view" ? "👁 View only" : "✎ Can edit"}
          </span>
        )}
        {ownerless && onShare && (
          <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 10, background: "rgba(241,144,30,0.12)", color: "var(--amber)", border: "1px solid #f1901e50" }}
            title="This roadmap has no owner yet. Use Share to claim it and control access.">⚠ No owner</span>
        )}
        {onShare && (myRole === "owner" || ownerless) && (
          <button className="btn" style={{ fontSize: 11, padding: "2px 8px", borderColor: "#2E75B6", color: "#4c9aff" }}
            title={ownerless ? "Claim this roadmap and set who can access it" : "Share this roadmap"} onClick={onShare}>🔗 {ownerless ? "Claim & share" : "Share"}</button>
        )}
        {onRename && (myRole === "owner" || ownerless) && (
          <button className="btn" style={{ fontSize: 11, padding: "2px 8px" }}
            title="Rename this roadmap"
            onClick={() => {
              const newName = window.prompt("Rename roadmap:", displayLabel || roadmapName);
              if (newName && newName.trim()) onRename(newName.trim());
            }}>✎ Rename</button>
        )}
        {onDelete && (myRole === "owner" || ownerless) && (
          <button className="btn danger" style={{ fontSize: 11, padding: "2px 8px" }}
            title="Delete this roadmap" onClick={onDelete}>🗑 Delete</button>
        )}
        <div className="rm-view-toggle">
          <button className={`rm-view-btn ${viewMode === "board" ? "active" : ""}`} onClick={() => setViewMode("board")}>📋 Board</button>
          <button className={`rm-view-btn ${viewMode === "gantt" ? "active" : ""}`} onClick={() => setViewMode("gantt")}>📊 Gantt</button>
          <button className={`rm-view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")}>📄 Grid</button>
        </div>
        <div className="rm-filters">
          <select className="rm-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            {RM_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="rm-select" value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)}>
            <option value="">All customers</option>
            {allCustomers.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="rm-select" value={filterOwner} onChange={e => setFilterOwner(e.target.value)}>
            <option value="">All assignees</option>
            {allOwners.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {viewMode === "board" && (
            <>
              <select className="rm-select" value={periodMode} onChange={e => setPeriodMode(e.target.value)} title="How the board columns are grouped">
                <option value="month-name">By month (name)</option>
                <option value="status">By Jira status</option>
                <option value="type">By issue type</option>
                <option value="owner">By assignee</option>
                <option value="week">By week</option>
                <option value="fortnight">By fortnight</option>
                <option value="month">By month (dates)</option>
              </select>
              {(periodMode === "week" || periodMode === "fortnight" || periodMode === "month") && (
                <>
                  <span style={{ fontSize: 10, color: "var(--text-dim)", alignSelf: "center" }}>From</span>
                  <input type="date" className="rm-select" value={periodAnchor} onChange={e => setPeriodAnchor(e.target.value)} title="Board start date" style={{ colorScheme: "dark" }} />
                  <span style={{ fontSize: 10, color: "var(--text-dim)", alignSelf: "center" }}>To</span>
                  <input type="date" className="rm-select" value={periodEnd} min={periodAnchor} onChange={e => setPeriodEnd(e.target.value)} title="Board end date" style={{ colorScheme: "dark" }} />
                </>
              )}
            </>
          )}
          <span style={{ fontSize: 11, color: "var(--text-dim)", alignSelf: "center" }}>{filtered.length} cards</span>
          <button className="btn" style={{ fontSize: 12 }} onClick={importFromJira} disabled={importing}
            title="Import cards from a Jira epic (pulls all child issues) or individual card keys">
            {importing ? "⭳ Importing…" : "⭳ Import from Jira"}
          </button>
          {cards.length > 0 && (
            <button className="btn danger" style={{ fontSize: 12 }} onClick={clearAll}
              title="Remove all cards from this roadmap">🗑 Clear all</button>
          )}
          <button className="btn primary" style={{ fontSize: 12 }} onClick={newCard}>+ New card</button>
        </div>
      </div>

      <div className="rm-body">
        {/* ── Board view ── */}
        {viewMode === "board" && periodMode === "month-name" && (
          <div className="rm-board">
            {lanes.map(lane => (
              <div key={lane.month} className="rm-lane">
                <div className="rm-lane-header">
                  <div className="rm-lane-title">📅 {lane.month || "TBC"}</div>
                  <div className="rm-lane-meta">{lane.cards.length} card{lane.cards.length !== 1 ? "s" : ""}</div>
                </div>
                <div className={`rm-lane-cards ${dragOverLane === lane.month ? "drag-over" : ""}`}
                  onDragOver={e => onDragOver(e, lane.month)}
                  onDrop={e => onDrop(e, lane.month)}
                  onDragLeave={() => setDragOverLane(null)}>
                  {lane.cards.map(card => renderBoardCard(card))}
                </div>
              </div>
            ))}
          </div>
        )}
        {viewMode === "board" && (periodMode === "week" || periodMode === "fortnight" || periodMode === "month") && (
          <div className="rm-board">
            {(dateLanes || []).map(lane => (
              <div key={lane.key} className="rm-lane">
                <div className="rm-lane-header">
                  <div className="rm-lane-title">📅 {lane.label}</div>
                  <div className="rm-lane-meta">{lane.cards.length} card{lane.cards.length !== 1 ? "s" : ""}</div>
                </div>
                <div className={`rm-lane-cards ${dragOverLane === lane.key ? "drag-over" : ""}`}
                  onDragOver={e => onDragOver(e, lane.key)}
                  onDrop={e => onDropDate(e, lane)}
                  onDragLeave={() => setDragOverLane(null)}>
                  {lane.cards.map(card => renderBoardCard(card))}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === "board" && periodMode === "status" && (
          <div className="rm-board">
            {(statusLanes || []).map(lane => (
              <div key={lane.key} className="rm-lane">
                <div className="rm-lane-header">
                  <div className="rm-lane-title">
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 6, background: RM_STATUS_COLOUR[lane.key] || "#4a6070" }} />
                    {lane.label}
                  </div>
                  <div className="rm-lane-meta">{lane.cards.length} card{lane.cards.length !== 1 ? "s" : ""}</div>
                </div>
                <div className={`rm-lane-cards ${dragOverLane === lane.key ? "drag-over" : ""}`}
                  onDragOver={e => onDragOver(e, lane.key)}
                  onDrop={e => onDropStatus(e, lane.key)}
                  onDragLeave={() => setDragOverLane(null)}>
                  {lane.cards.map(card => renderBoardCard(card))}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === "board" && periodMode === "owner" && (
          <div className="rm-board">
            {(ownerLanes || []).length === 0 && (
              <div style={{ padding: 24, color: "var(--text-dim)", fontSize: 13 }}>No cards to group by owner.</div>
            )}
            {(ownerLanes || []).map(lane => (
              <div key={lane.key} className="rm-lane">
                <div className="rm-lane-header">
                  <div className="rm-lane-title">👤 {lane.label}</div>
                  <div className="rm-lane-meta">{lane.cards.length} card{lane.cards.length !== 1 ? "s" : ""}</div>
                </div>
                <div className={`rm-lane-cards ${dragOverLane === lane.key ? "drag-over" : ""}`}
                  onDragOver={e => onDragOver(e, lane.key)}
                  onDrop={e => onDropOwner(e, lane.key)}
                  onDragLeave={() => setDragOverLane(null)}>
                  {lane.cards.map(card => renderBoardCard(card))}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === "board" && periodMode === "type" && (
          <div className="rm-board">
            {(typeLanes || []).length === 0 && (
              <div style={{ padding: 24, color: "var(--text-dim)", fontSize: 13 }}>No cards to group by type.</div>
            )}
            {(typeLanes || []).map(lane => {
              const tm = rmTypeMeta(lane.key);
              return (
              <div key={lane.key} className="rm-lane">
                <div className="rm-lane-header">
                  <div className="rm-lane-title"><span style={{ color: tm.colour, marginRight: 6 }}>{tm.icon}</span>{lane.label}</div>
                  <div className="rm-lane-meta">{lane.cards.length} card{lane.cards.length !== 1 ? "s" : ""}</div>
                </div>
                <div className={`rm-lane-cards ${dragOverLane === lane.key ? "drag-over" : ""}`}
                  onDragOver={e => onDragOver(e, lane.key)}
                  onDrop={e => onDropType(e, lane.key)}
                  onDragLeave={() => setDragOverLane(null)}>
                  {lane.cards.map(card => renderBoardCard(card))}
                </div>
              </div>
            );})}
          </div>
        )}

        {/* ── Gantt view ── */}
        {viewMode === "gantt" && (
          <div className="rm-gantt">
            <div className="rm-gantt-header" style={{ alignItems: "stretch" }}>
              <div className="rm-gantt-label-col">Card</div>
              <div className="rm-gantt-months" style={{ display: "flex", flexDirection: "column", padding: 0 }}>
                {/* Month grouping row */}
                <div style={{ display: "flex", width: "100%", borderBottom: "1px solid var(--border)" }}>
                  {ganttMonthSpans.map((ms, i) => (
                    <div key={i} style={{ flex: ms.count, textAlign: "center", fontSize: 11, fontWeight: 700, color: "var(--text)", padding: "3px 0", borderRight: "1px solid var(--border)", whiteSpace: "nowrap" }}>
                      {ms.label}
                    </div>
                  ))}
                </div>
                {/* Week labels row */}
                <div style={{ display: "flex", width: "100%" }}>
                  {ganttWeeks.map((w, i) => (
                    <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 9, color: "var(--text-dim)", padding: "2px 0", borderRight: "1px solid var(--border)" }} title={`Week of ${w.start.toLocaleDateString("en-GB")}`}>
                      {w.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "relative" }}>
              {/* Dependency arrows overlay (offset past the 380px label column) */}
              {ganttArrows.length > 0 && rmTimelineW > 0 && (
                <svg style={{ position: "absolute", top: 0, left: 380, width: rmTimelineW, height: sortedFiltered.length * ROW_H, pointerEvents: "none", zIndex: 5, overflow: "visible" }}>
                  <defs>
                    <marker id="rm-dep-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#f1901e" />
                    </marker>
                  </defs>
                  {ganttArrows.map((a, i) => {
                    // Route: out from end of prerequisite, elbow, into start of dependent
                    const midX = Math.max(a.x1 + 12, a.x2 - 12);
                    const d = `M ${a.x1} ${a.y1} L ${midX} ${a.y1} L ${midX} ${a.y2} L ${a.x2 - 2} ${a.y2}`;
                    return <path key={i} d={d} fill="none" stroke="#f1901e" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#rm-dep-arrow)" opacity="0.85" />;
                  })}
                </svg>
              )}
              {sortedFiltered.map((card, rowI) => {
              const bs = barStyle(card);
              return (
                <div key={card.id} className="rm-gantt-row" onClick={() => setEditCard(card)}>
                  <div className="rm-gantt-row-label">
                    {(() => {
                      const raw = String(card.jiraKey || "");
                      const m = raw.match(/[A-Z][A-Z0-9]+-\d+/i);
                      const key = m ? m[0] : raw;
                      return key ? <span className="rm-gantt-row-key" style={{ marginRight: 6 }}>{key}</span> : null;
                    })()}
                    <span className="rm-gantt-row-title" title={card.title}>{card.title}</span>
                  </div>
                  <div className="rm-gantt-timeline" ref={rowI === 0 ? rmTimelineRef : undefined}>
                    {/* Week gridlines */}
                    {ganttWeeks.map((w, i) => (
                      <div key={i} style={{ position: "absolute", top: 0, bottom: 0, left: `${(i / ganttWeeks.length) * 100}%`, width: 1, background: "var(--border)", opacity: 0.4 }} />
                    ))}
                    {todayPct >= 0 && todayPct <= 100 && (
                      <div className="rm-gantt-today" style={{ left: todayPct + "%" }} />
                    )}
                    {bs && (
                      <div className="rm-gantt-bar" style={bs} title={`${card.status} · ${card.start || "?"} → ${card.finish || "?"}`}>
                        {parseFloat(bs.width) > 4 ? card.jiraKey?.split("-")[1] : ""}
                      </div>
                    )}
                    {!bs && (
                      <div style={{ height: "100%", display: "flex", alignItems: "center", paddingLeft: 8 }}>
                        <span style={{ fontSize: 9, color: "var(--text-dim)" }}>No dates</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}

        {/* ── Grid / spreadsheet view ── */}
        {viewMode === "grid" && (
          <div className="rm-grid-wrap">
            <table className="rm-grid">
              <thead>
                <tr>
                  <th style={{ width: 28, padding: "7px 4px" }}></th>
                  {[["jiraKey","Jira Key"],["title","Title"],["status","Status"],["readiness","Readiness"],["owner","Assignee"],
                    ["releaseMonth","Due Month"],["start","Start"],["finish","Finish"],
                    ["customers","Customers"],["comments","Comments"]].map(([col, label]) => (
                    <th key={col} className={sortCol === col ? "sorted" : ""} onClick={() => toggleSort(col)}>
                      {label} {sortCol === col ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                  ))}
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {sortedFiltered.map(card => (
                  <tr key={card.id}
                    className={[
                      editCard?.id === card.id ? "selected" : "",
                      gridDragId === card.id ? "row-dragging" : "",
                      gridDragOver?.id === card.id && gridDragOver?.pos === "top" ? "drag-over-top" : "",
                      gridDragOver?.id === card.id && gridDragOver?.pos === "bottom" ? "drag-over-bottom" : "",
                    ].filter(Boolean).join(" ")}
                    onDoubleClick={() => setEditCard(card)}
                    title="Double-click to open"
                    draggable={canEdit}
                    onDragStart={e => { if (!canEdit) return; setGridDragId(card.id); e.dataTransfer.effectAllowed = "move"; }}
                    onDragEnd={() => { setGridDragId(null); setGridDragOver(null); }}
                    onDragOver={e => {
                      if (!gridDragId) return;
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = e.clientY < rect.top + rect.height / 2 ? "top" : "bottom";
                      setGridDragOver(prev => (prev && prev.id === card.id && prev.pos === pos) ? prev : { id: card.id, pos });
                    }}
                    onDrop={e => {
                      e.preventDefault();
                      const targetPos = gridDragOver && gridDragOver.id === card.id ? gridDragOver.pos : "bottom";
                      if (gridDragId && gridDragId !== card.id) reorderGridRows(gridDragId, card.id, targetPos);
                      setGridDragId(null); setGridDragOver(null);
                    }}>
                    {/* Drag handle + open */}
                    <td style={{ width: 44, textAlign: "center", whiteSpace: "nowrap" }}>
                      <span className="rm-drag-handle" title="Drag to reorder">⠿</span>
                      <button className="btn" style={{ fontSize: 10, padding: "1px 5px", marginLeft: 3 }}
                        title="Open card" onClick={(e) => { e.stopPropagation(); setEditCard(card); }}>↗</button>
                    </td>
                    {/* Jira Key */}
                    <td style={{ minWidth: 130 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "jiraKey" ? (
                        <div className="rm-grid-cell editing">
                          <input autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={commitInline} onKeyDown={e => { if (e.key === "Enter") commitInline(); if (e.key === "Escape") setInlineEdit(null); }} />
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "jiraKey", card.jiraKey)}>
                          {card.issueType && (() => { const tm = rmTypeMeta(card.issueType); return <span title={tm.label} style={{ color: tm.colour, fontWeight: 700, marginRight: 4 }}>{tm.icon}</span>; })()}
                          <span className="rm-grid-key">{card.jiraKey || <span style={{ color: "var(--text-dim)" }}>-</span>}</span>
                        </div>
                      )}
                    </td>
                    {/* Title */}
                    <td style={{ minWidth: 280 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "title" ? (
                        <div className="rm-grid-cell editing">
                          <input autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={commitInline} onKeyDown={e => { if (e.key === "Enter") commitInline(); if (e.key === "Escape") setInlineEdit(null); }} />
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "title", card.title)} style={{ maxWidth: 320 }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: 310 }} title={card.title}>{card.title || "-"}</span>
                        </div>
                      )}
                    </td>
                    {/* Status */}
                    <td style={{ minWidth: 110 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "status" ? (
                        <div className="rm-grid-cell editing">
                          <select autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={commitInline}>
                            {RM_STATUSES.map(s => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "status", card.status)}>
                          <span className="rm-grid-status" style={{ background: (RM_STATUS_COLOUR[card.status] || "#4a6070") + "25", color: RM_STATUS_COLOUR[card.status] || "var(--text-muted)", border: `1px solid ${RM_STATUS_COLOUR[card.status] || "#4a6070"}40` }}>
                            {card.status || "-"}
                          </span>
                        </div>
                      )}
                    </td>
                    {/* Readiness */}
                    <td style={{ minWidth: 130 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "readiness" ? (
                        <div className="rm-grid-cell editing">
                          <select autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)} onBlur={commitInline}>
                            <option value="">-</option>
                            {RM_READINESS.map(r => <option key={r}>{r}</option>)}
                          </select>
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "readiness", card.readiness || "")}>
                          {card.readiness ? (
                            <span className="rm-grid-status" style={{ background: (RM_READINESS_COLOUR[card.readiness] || "#4a6070") + "25", color: RM_READINESS_COLOUR[card.readiness] || "var(--text-muted)", border: `1px solid ${RM_READINESS_COLOUR[card.readiness] || "#4a6070"}40` }}>
                              {card.readiness}
                            </span>
                          ) : <span style={{ color: "var(--text-dim)" }}>-</span>}
                        </div>
                      )}
                    </td>
                    {/* Owner */}
                    <td style={{ minWidth: 100 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "owner" ? (
                        <div className="rm-grid-cell editing">
                          <input autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={commitInline} onKeyDown={e => { if (e.key === "Enter") commitInline(); if (e.key === "Escape") setInlineEdit(null); }} />
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "owner", card.owner)}>
                          {card.owner || <span style={{ color: "var(--text-dim)" }}>-</span>}
                        </div>
                      )}
                    </td>
                    {/* Release Month */}
                    <td style={{ minWidth: 100 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "releaseMonth" ? (
                        <div className="rm-grid-cell editing">
                          <select autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)} onBlur={commitInline}>
                            {RM_MONTHS.map(m => <option key={m}>{m}</option>)}
                          </select>
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "releaseMonth", card.releaseMonth)}>
                          {card.releaseMonth || <span style={{ color: "var(--text-dim)" }}>-</span>}
                        </div>
                      )}
                    </td>
                    {/* Start */}
                    <td style={{ minWidth: 110 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "start" ? (
                        <div className="rm-grid-cell editing">
                          <input type="date" autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={commitInline} style={{ colorScheme: "dark" }} />
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "start", card.start)}>
                          {card.start ? formatDate(card.start.split("T")[0]) : <span style={{ color: "var(--text-dim)" }}>-</span>}
                        </div>
                      )}
                    </td>
                    {/* Finish */}
                    <td style={{ minWidth: 110 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "finish" ? (
                        <div className="rm-grid-cell editing">
                          <input type="date" autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={commitInline} style={{ colorScheme: "dark" }} />
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "finish", card.finish)}>
                          {card.finish ? formatDate(card.finish.split("T")[0]) : <span style={{ color: "var(--text-dim)" }}>-</span>}
                        </div>
                      )}
                    </td>
                    {/* Customers */}
                    <td style={{ minWidth: 140 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "customers" ? (
                        <div className="rm-grid-cell editing">
                          <input autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={() => { const updated = { ...card, customers: inlineVal.split(",").map(s => s.trim()).filter(Boolean) }; saveCard(updated); setInlineEdit(null); }}
                            onKeyDown={e => { if (e.key === "Enter") { const updated = { ...card, customers: inlineVal.split(",").map(s => s.trim()).filter(Boolean) }; saveCard(updated); setInlineEdit(null); } if (e.key === "Escape") setInlineEdit(null); }} />
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "customers", (card.customers||[]).join(", "))}>
                          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{(card.customers||[]).join(", ") || <span style={{ color: "var(--text-dim)" }}>-</span>}</span>
                        </div>
                      )}
                    </td>
                    {/* Comments */}
                    <td style={{ minWidth: 180 }}>
                      {inlineEdit?.id === card.id && inlineEdit?.field === "comments" ? (
                        <div className="rm-grid-cell editing">
                          <input autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                            onBlur={commitInline} onKeyDown={e => { if (e.key === "Enter") commitInline(); if (e.key === "Escape") setInlineEdit(null); }} />
                        </div>
                      ) : (
                        <div className="rm-grid-cell" onClick={() => startInline(card.id, "comments", card.comments)} style={{ maxWidth: 220 }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", fontSize: 11, color: "var(--text-muted)", maxWidth: 210 }}>{card.comments || <span style={{ color: "var(--text-dim)" }}>-</span>}</span>
                        </div>
                      )}
                    </td>
                    {/* Full edit */}
                    <td style={{ width: 40 }}>
                      <div className="rm-grid-cell" style={{ justifyContent: "center" }} onClick={() => setEditCard(card)}>
                        <span style={{ fontSize: 14, color: "var(--text-dim)" }}>✎</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="rm-grid-add-row" onClick={newCard}>
              <span style={{ fontSize: 16 }}>+</span> Add new card
            </div>
          </div>
        )}
      </div>

      {/* Card edit modal */}
      {editCard && (
        <RoadmapCardModal
          card={editCard}
          onSave={saveCard}
          onDelete={deleteCard}
          onMove={canEdit ? moveCard : undefined}
          moveTargets={allRoadmaps.filter(r => r.name !== roadmapName)}
          knownAssignees={[...new Set(cards.map(c => c.owner).filter(Boolean))].sort()}
          onClose={() => setEditCard(null)}
        />
      )}
    </div>
  );
}

export default function App() {
  const [tasks, setTasks] = useState(() => {
    try {
      const saved = localStorage.getItem("taskflow_tasks");
      const parsed = saved ? JSON.parse(saved) : [];
      const myName = localStorage.getItem("taskflow_jira_displayname") || "";
      return parsed
        .filter(t => {
          // Keep non-manual tasks (Jira, Outlook) always
          if (t.source !== "manual") return true;
          // Manual tasks are private: only show ones I created, am assigned, or have a
          // subtask assigned to me. Unowned (no createdBy) tasks are NOT shown to everyone.
          if (!myName) return false;
          if (t.createdBy === myName) return true;
          if (t.assignedTo === myName) return true;
          // Keep if a subtask is assigned to me
          if ((t.subtasks || []).some(s => s.assignedTo === myName)) return true;
          return false;
        })
        .map(t => ({
          ...t,
          jiraEffort: t.jiraEffort != null && typeof t.jiraEffort === "object" ? (t.jiraEffort.value ?? t.jiraEffort.name ?? null) : t.jiraEffort ?? null,
          jiraValue:  t.jiraValue  != null && typeof t.jiraValue  === "object" ? (t.jiraValue.value  ?? t.jiraValue.name  ?? null) : t.jiraValue  ?? null,
          jiraScore:  t.jiraScore  != null && typeof t.jiraScore  === "object" ? (t.jiraScore.value  ?? t.jiraScore.name  ?? null) : t.jiraScore  ?? null,
        }));
    } catch { return []; }
  });
  const [selected, setSelected] = useState(null);
  // Manual task reorder
  const dragId = useRef(null);
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(new Set()); // ids selected for bulk action
  const toggleBulkSelect = (id) => setBulkSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearBulk = () => setBulkSelected(new Set());

  // ── Activity Log ──────────────────────────────────────────────────────────
  const [activityLog, setActivityLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_activity_log") || "[]"); } catch { return []; }
  });
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [devCompletedTickets, setDevCompletedTickets] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_dev_completed") || "[]"); } catch { return []; }
  });
  const [lightMode, setLightMode] = useState(() => {
    try { return localStorage.getItem("taskflow_light_mode") === "true"; } catch { return false; }
  });
  const toggleLightMode = () => setLightMode(v => {
    const next = !v;
    try { localStorage.setItem("taskflow_light_mode", String(next)); } catch {}
    return next;
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [managerMode, setManagerMode] = useState(false);

  const logActivity = (action, taskTitle, detail) => {
    const entry = {
      id: Date.now() + Math.random(),
      ts: new Date().toLocaleString("en-GB", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }),
      action,
      title: taskTitle,
      detail: detail || "",
    };
    setActivityLog(prev => {
      const next = [entry, ...prev].slice(0, 200);
      try { localStorage.setItem("taskflow_activity_log", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const [filter, setFilter] = useState("dashboard");
  const [jiraQueuePreset, setJiraQueuePreset] = useState("ed-signoff");
  const [jiraQueueMe, setJiraQueueMe] = useState(false);
  const [search, setSearch] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [projects, setProjects] = useState(() => {
    // Seed from localStorage as immediate cache while server loads
    try { return JSON.parse(localStorage.getItem("taskflow_projects") || "[]"); } catch { return []; }
  });
  const projectsRef = useRef(projects);
  useEffect(() => { projectsRef.current = projects; }, [projects]);
  const [projectsSyncing, setProjectsSyncing] = useState(false);

  // Resolve current user's Jira accountId for project visibility
  const currentAccountId = useRef("");
  useEffect(() => {
    const stored = localStorage.getItem("taskflow_jira_accountid");
    if (stored) currentAccountId.current = stored;
  }, []);

  // Helper: fetch projects from server for this user
  const fetchSharedProjects = useCallback(async (accountId) => {
    try {
      const r = await fetch(PROJECTS_API, {
        headers: accountId ? { "X-User-AccountId": accountId } : {},
      });
      if (!r.ok) return null;
      return await r.json();
    } catch { return null; }
  }, []);

  // Helper: save projects to server
  const saveSharedProjects = useCallback(async (projs, accountId) => {
    try {
      await fetch(PROJECTS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountId: accountId || "", projects: projs }),
      });
    } catch (e) { console.warn("[Projects] Server save failed:", e.message); }
  }, []);

  // Load projects from server on mount (after accountId is available)
  useEffect(() => {
    const accountId = localStorage.getItem("taskflow_jira_accountid") || "";
    if (!accountId) return; // wait until we know who the user is
    setProjectsSyncing(true);
    fetchSharedProjects(accountId).then(serverProjects => {
      if (serverProjects && Array.isArray(serverProjects)) {
        // Server is the single source of truth - use server list exactly as returned.
        // Never merge back local-only projects: they may be deleted projects still cached
        // in localStorage that the server has already tombstoned.
        setProjects(serverProjects);
        try { localStorage.setItem("taskflow_projects", JSON.stringify(serverProjects)); } catch {}
      }
      setProjectsSyncing(false);
    });
  }, []); // eslint-disable-line

  // Persist projects - to both localStorage (cache) and server
  useEffect(() => {
    try { localStorage.setItem("taskflow_projects", JSON.stringify(projects)); } catch {}
    const accountId = localStorage.getItem("taskflow_jira_accountid") || "";
    if (accountId && projects.length > 0) {
      saveSharedProjects(projects, accountId);
    }
  }, [projects, saveSharedProjects]);

  // Refresh projects from server every 2 minutes (same as task sync)
  useEffect(() => {
    const interval = setInterval(() => {
      const accountId = localStorage.getItem("taskflow_jira_accountid") || "";
      if (!accountId) return;
      fetchSharedProjects(accountId).then(serverProjects => {
        if (!serverProjects || !Array.isArray(serverProjects)) return;
        // Only update if server has changes (compare by lastUpdatedAt)
        const serverIds = new Set(serverProjects.map(p => p.id));
        const localOnly = projectsRef.current.filter(p => !serverIds.has(p.id));
        const merged = [...serverProjects, ...localOnly];
        // Avoid triggering re-render if nothing changed
        if (JSON.stringify(merged.map(p => p.id + (p.lastUpdatedAt || ""))) !==
            JSON.stringify(projectsRef.current.map(p => p.id + (p.lastUpdatedAt || "")))) {
          setProjects(merged);
          try { localStorage.setItem("taskflow_projects", JSON.stringify(merged)); } catch {}
        }
      });
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSharedProjects]);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewJira, setShowNewJira] = useState(false);
  const [newJira, setNewJira] = useState({ summary: "", description: "", affectedClient: "", category: "", ticketType: "" });
  // Track Jira keys created via the + Jira button so they appear in "Created by me" sub-view
  const [appCreatedKeys, setAppCreatedKeys] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_app_created_jira") || "[]"); } catch { return []; }
  });
  const addAppCreatedKey = (key) => {
    setAppCreatedKeys(prev => {
      const next = prev.includes(key) ? prev : [...prev, key];
      try { localStorage.setItem("taskflow_app_created_jira", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const removeAppCreatedKey = (key) => {
    setAppCreatedKeys(prev => {
      const next = prev.filter(k => k !== key);
      try { localStorage.setItem("taskflow_app_created_jira", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [creatingJira, setCreatingJira] = useState(false);
  const [jiraAttachments, setJiraAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { filename: 0-100 }
  // Impl project Jira creation state
  const [showImplJira, setShowImplJira] = useState(false);
  const openImplJiraRef = useRef(null); // called by ImplementationsView to open modal
  const [implJiraTask, setImplJiraTask] = useState(null); // { task, moduleIndex, updateTaskFn }
  const [implJiraForm, setImplJiraForm] = useState({ summary: "", description: "", affectedClient: "", category: "", ticketType: "" });
  const [implJiraAttachments, setImplJiraAttachments] = useState([]);
  const implFileInputRef = useRef(null);
  const [creatingImplJira, setCreatingImplJira] = useState(false);
  const [implClientOptions, setImplClientOptions] = useState([]);
  const [implUploadProgress, setImplUploadProgress] = useState({});
  const [clientOptions, setClientOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const [emails, setEmails] = useState([]);
  const [config, setConfig] = useState(() => {
    try {
      const saved = localStorage.getItem("taskflow_config");
      const base = saved ? JSON.parse(saved) : { msToken: "", jiraUrl: "", jiraEmail: "", jiraToken: "", jiraProject: "" };
      // Load ms token from dedicated storage if not in config
      if (!base.msToken) base.msToken = getSavedMsToken() || "";
      return base;
    } catch {
      return { msToken: "", jiraUrl: "", jiraEmail: "", jiraToken: "", jiraProject: "" };
    }
  });
  // userIsManager reads from localStorage directly - safe to call after config is declared
  const userIsManager = MANAGER_NAMES.some(m =>
    (localStorage.getItem("taskflow_jira_displayname") || "").toLowerCase() === m.toLowerCase()
  );

  // Load shared team members from server
  const [serverTeamMembers, setServerTeamMembers] = React.useState([]);
  React.useEffect(() => {
    fetch(TEAM_MEMBERS_API)
      .then(r => r.ok ? r.json() : { members: [] })
      .then(d => {
        const members = d.members || [];
        setServerTeamMembers(members);
        window.__c365teamMembers__ = members; // make available to helper functions
      })
      .catch(() => {});
  }, []);

  // Build team member list from server + Jira tasks + dev tickets
  const allTeamMembers = React.useMemo(() => {
    const names = new Set();
    // From server team_members.json (authoritative list)
    serverTeamMembers.forEach(m => { if (m.name) names.add(m.name); });
    // From Jira task assignees (catches external/client contacts)
    tasks.forEach(t => { if (t.assignee) names.add(t.assignee); if (t.assignedTo) names.add(t.assignedTo); });
    (devCompletedTickets || []).forEach(t => { if (t.assignee) names.add(t.assignee); });
    names.delete(""); names.delete(null); names.delete(undefined);
    return [...names].sort();
  }, [tasks, devCompletedTickets, serverTeamMembers]);
  const [newTask, setNewTask] = useState({ title: "", priority: "medium", source: "manual", due: "", status: "To Start", recur: "" });
  const visionInputRef = React.useRef(null);
  const [visionBusy, setVisionBusy] = React.useState(false);
  const [visionReview, setVisionReview] = React.useState(null); // { title, items:[{text, kind}] }
  const [popoutTask, setPopoutTask] = useState(null);
  const [userSettings, setUserSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_user_settings") || "{}"); } catch { return {}; }
  });
  const [subFilter, setSubFilter] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_nav_collapsed") || "{}"); } catch { return {}; }
  });
  const [navManualMode, setNavManualMode] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_nav_manual") || "false"); } catch { return false; }
  });
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_sections_collapsed") || "{}"); } catch { return {}; }
  });
  const [viewMode, setViewModeRaw] = useState(() => {
    try { return localStorage.getItem("taskflow_view_mode") || "list"; } catch { return "list"; }
  });
  const setViewMode = (v) => {
    setViewModeRaw(v);
    try { localStorage.setItem("taskflow_view_mode", v); } catch {}
  };
  const [roadmapLabel, setRoadmapLabel] = useState(() => {
    try { return localStorage.getItem("taskflow_roadmap_label") || "Roadmap"; } catch { return "Roadmap"; }
  });
  // Registry of roadmaps beyond the built-in CAFM one. Each: { id, name (storage key), label }.
  // Roadmap registry is SHARED via the server (GET /api/roadmap lists all roadmaps),
  // so a roadmap one user creates is visible to everyone. We derive a stable nav id
  // from each roadmap's name. "CAFM Dev Roadmap" keeps its dedicated built-in slot.
  const rmNameToId = (name) => "roadmap-srv-" + name.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  const [roadmaps, setRoadmaps] = useState([]);
  const [cafmMeta, setCafmMeta] = useState({});
  const loadRoadmaps = React.useCallback(() => {
    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
    const myId = localStorage.getItem("taskflow_jira_accountid") || "";
    fetch(`${ROADMAP_API}?accountId=${encodeURIComponent(myId)}&user=${encodeURIComponent(myName)}`)
      .then(r => r.ok ? r.json() : { roadmaps: {} })
      .then(data => {
        const rmObj = data.roadmaps || {};
        if (rmObj["CAFM Dev Roadmap"]) {
          const c = rmObj["CAFM Dev Roadmap"];
          setCafmMeta({ visibility: c.visibility, owner: c.owner, ownerName: c.ownerName, shares: c.shares || [], myRole: c.myRole });
        }
        const list = Object.keys(rmObj)
          .filter(n => n !== "CAFM Dev Roadmap")
          .map(n => ({ id: rmNameToId(n), name: n, label: n,
            visibility: rmObj[n].visibility, owner: rmObj[n].owner, ownerName: rmObj[n].ownerName,
            shares: rmObj[n].shares || [], myRole: rmObj[n].myRole }));
        setRoadmaps(list);
      })
      .catch(() => {});
  }, []);
  React.useEffect(() => { loadRoadmaps(); }, [loadRoadmaps]);

  // Register this user in the shared registry so roadmap owners can share with them
  React.useEffect(() => {
    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
    const myId = localStorage.getItem("taskflow_jira_accountid") || "";
    const myEmail = localStorage.getItem("taskflow_jira_email") || "";
    if (!myName && !myId) return;
    const base = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:3001" : "";
    fetch(`${base}/api/users/register`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accountId: myId, name: myName, email: myEmail }) }).catch(() => {});
  }, []);

  const [shareDialog, setShareDialog] = useState(null); // { id, name, visibility, shares }
  const [allUsers, setAllUsers] = useState([]);
  const openShareDialog = (id) => {
    let name, meta;
    if (id === "roadmap-cafm") { name = "CAFM Dev Roadmap"; meta = cafmMeta; }
    else { const r = roadmaps.find(x => x.id === id); if (!r) return; name = r.name; meta = r; }
    // Legacy ownerless roadmaps were effectively visible to everyone; default the dialog
    // to that so claiming ownership does not silently hide it from current users.
    const defaultVis = meta.visibility || (!meta.owner ? "everyone" : "private");
    setShareDialog({ id, name, visibility: defaultVis, shares: meta.shares || [] });
    const base = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:3001" : "";
    fetch(`${base}/api/users`).then(r => r.ok ? r.json() : { users: [] }).then(d => setAllUsers(d.users || [])).catch(() => {});
  };
  const saveShareSettings = () => {
    if (!shareDialog) return;
    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
    const myId = localStorage.getItem("taskflow_jira_accountid") || "";
    fetch(`${ROADMAP_API}/${encodeURIComponent(shareDialog.name)}/share`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accountId: myId, name: myName, visibility: shareDialog.visibility, shares: shareDialog.shares }),
    })
      .then(r => r.json())
      .then(d => { if (d.error) { showToast(d.error, "error"); return; } showToast("Sharing updated"); setShareDialog(null); loadRoadmaps(); })
      .catch(() => showToast("Could not update sharing", "error"));
  };

  const addRoadmap = () => {
    const label = window.prompt("Name the new roadmap:");
    if (!label || !label.trim()) return;
    const name = label.trim();
    if (name === "CAFM Dev Roadmap" || roadmaps.some(r => r.name === name)) { showToast("A roadmap with that name already exists", "error"); return; }
    const id = rmNameToId(name);
    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
    const myId = localStorage.getItem("taskflow_jira_accountid") || "";
    fetch(`${ROADMAP_API}/create`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, accountId: myId, ownerName: myName, visibility: "private" }) })
      .then(() => { loadRoadmaps(); setFilter(id); showToast("Roadmap created (private). Use Share to give others access."); })
      .catch(() => { showToast("Could not create roadmap on the server", "error"); });
  };
  const renameRoadmap = (id, newLabel) => {
    setRoadmaps(prev => prev.map(r => r.id === id ? { ...r, label: newLabel } : r));
  };
  const deleteRoadmap = (id) => {
    const r = roadmaps.find(x => x.id === id);
    if (!r) return;
    if (!window.confirm(`Delete roadmap "${r.label}" for everyone? This removes the roadmap and all its cards. This cannot be undone.`)) return;
    fetch(`${ROADMAP_API}/${encodeURIComponent(r.name)}`, { method: "DELETE" })
      .then(() => { setRoadmaps(prev => prev.filter(x => x.id !== id)); if (filter === id) setFilter("dashboard"); showToast("Roadmap deleted"); loadRoadmaps(); })
      .catch(() => { showToast("Could not delete roadmap", "error"); });
  };

  const saveUserSettings = (updates) => {
    const updated = { ...userSettings, ...updates };
    setUserSettings(updated);
    localStorage.setItem("taskflow_user_settings", JSON.stringify(updated));
  };

  // Persist all tasks to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem("taskflow_tasks", JSON.stringify(tasks));
    } catch {}
  }, [tasks]);

  // Load shared manual tasks from server on mount, merging with local
  useEffect(() => {
    const myName = localStorage.getItem("taskflow_jira_displayname") || "";
    fetch(`${MANUAL_TASKS_API}?user=${encodeURIComponent(myName)}`)
      .then(r => r.ok ? r.json() : { tasks: [] })
      .then(data => {
        const serverTasks = data.tasks || [];
        const serverIds = new Set(serverTasks.map(t => t.id));

        // Migrate ONLY local tasks that don't exist on server yet AND were created by me
        const localManual = tasks.filter(t =>
          t.source === "manual" &&
          !serverIds.has(t.id) &&
          (!t.createdBy || t.createdBy === myName)
        );
        if (localManual.length) {
          localManual.forEach(t => {
            const withMeta = { ...t, createdBy: myName, createdAt: t.createdAt || new Date().toISOString(), localUpdatedAt: new Date().toISOString() };
            fetch(MANUAL_TASKS_API, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(withMeta),
            }).catch(() => {});
          });
          console.log(`[ManualTasks] Migrated ${localManual.length} local tasks to server`);
        }

        setTasks(prev => {
          if (!serverTasks.length) return prev;
          const localIds = new Set(prev.map(t => t.id));
          const newFromServer = serverTasks.filter(t => !localIds.has(t.id));
          const merged = prev.map(t => {
            const serverVersion = serverTasks.find(s => s.id === t.id);
            if (serverVersion && t.source === "manual") {
              const sTime = new Date(serverVersion.serverUpdatedAt || serverVersion.serverCreatedAt || 0);
              const lTime = new Date(t.localUpdatedAt || t.createdAt || 0);
              // Server done:true always wins (task was completed by someone else)
              if (serverVersion.done && !t.done) return serverVersion;
              return sTime > lTime ? serverVersion : t;
            }
            return t;
          });
          return [...merged, ...newFromServer];
        });
        console.log(`[ManualTasks] Loaded ${serverTasks.length} tasks for ${myName}`);
      })
      .catch(e => console.warn("[ManualTasks] Load failed:", e.message));
  }, []);

  // Periodic refresh of server manual tasks (every 60s) to catch updates from other users
  useEffect(() => {
    const refresh = () => {
      const myName = localStorage.getItem("taskflow_jira_displayname") || "";
      fetch(`${MANUAL_TASKS_API}?user=${encodeURIComponent(myName)}`)
        .then(r => r.ok ? r.json() : { tasks: [] })
        .then(data => {
          const serverTasks = data.tasks || [];
          if (!serverTasks.length) return;
          setTasks(prev => prev.map(t => {
            const serverVersion = serverTasks.find(s => s.id === t.id);
            if (!serverVersion || t.source !== "manual") return t;
            const sTime = new Date(serverVersion.serverUpdatedAt || serverVersion.serverCreatedAt || 0);
            const lTime = new Date(t.localUpdatedAt || t.createdAt || 0);
            // Server done:true always wins
            if (serverVersion.done && !t.done) return serverVersion;
            return sTime > lTime ? serverVersion : t;
          }));
        })
        .catch(() => {});
    };
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const interval = setInterval(async () => {
      const expiry = parseInt(localStorage.getItem("taskflow_ms_token_expiry") || "0");
      const refreshToken = localStorage.getItem("taskflow_ms_refresh");
      // Refresh if token expires within 10 minutes and we have a refresh token
      if (refreshToken && expiry && Date.now() > expiry - 10 * 60 * 1000) {
        const newToken = await refreshMsToken();
        if (newToken) {
          setConfig((c) => ({ ...c, msToken: newToken }));
        }
      }
    }, 60 * 1000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Parse OAuth token from URL on redirect back from Microsoft
  useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("code")) {
      handleMsCallback().then((token) => {
        if (token) {
          setConfig((c) => {
            const updated = { ...c, msToken: token };
            localStorage.setItem("taskflow_config", JSON.stringify(updated));
            return updated;
          });
        }
      });
    }
  });

  const msConnected = !!(config.msToken && getSavedMsToken());
  const jiraConnected = !!(config.jiraUrl && config.jiraEmail && config.jiraToken);
  // Expose config globally so MentionTextarea can call Jira user search
  // without threading config through 4 levels of props
  useEffect(() => { window.__c365config__ = config; }, [config]);

  // Client Hub → create tasks from extracted actions
  useEffect(() => {
    const handler = (e) => {
      const { tasks: newTasks } = e.detail || {};
      if (!newTasks?.length) return;
      const myName = localStorage.getItem("taskflow_jira_displayname") || "Unknown";
      const withMeta = newTasks.map(t => ({ ...t, createdBy: myName, createdAt: new Date().toISOString(), localUpdatedAt: new Date().toISOString() }));
      setTasks(prev => [...withMeta, ...prev]);
      showToast(`${withMeta.length} task${withMeta.length !== 1 ? "s" : ""} created from Client Hub`);
      setFilter("manual");
      // Sync to server
      withMeta.forEach(t => {
        fetch(MANUAL_TASKS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(t),
        }).catch(e => console.warn("[ManualTasks] CH task sync failed:", e.message));
      });
    };
    window.addEventListener("ch-create-tasks", handler);
    return () => window.removeEventListener("ch-create-tasks", handler);
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const sync = useCallback(async () => {
    if (!msConnected && !jiraConnected) { showToast("Configure connections first", "error"); return; }
    setSyncing(true);
    try {
      // Auto-refresh MS token if needed before syncing
      let activeConfig = config;
      if (msConnected) {
        const freshToken = await getValidMsToken();
        if (freshToken && freshToken !== config.msToken) {
          activeConfig = { ...config, msToken: freshToken };
          setConfig(activeConfig);
        }
      }
      const [outTasks, jiraTasks] = await Promise.all([
        msConnected ? fetchOutlookTasks(activeConfig) : Promise.resolve([]),
        jiraConnected ? fetchJiraIssues(activeConfig) : Promise.resolve([]),
      ]);

      // Store accountId for project sharing - resolve from /myself if connected
      if (jiraConnected && !localStorage.getItem("taskflow_jira_accountid")) {
        try {
          const { jiraUrl, jiraEmail, jiraToken } = activeConfig;
          const auth = btoa(`${jiraEmail}:${jiraToken}`);
          const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
          const mr = await fetch(proxyUrl(`${baseUrl}/rest/api/3/myself`), {
            headers: { Authorization: `Basic ${auth}`, Accept: "application/json" },
          });
          if (mr.ok) {
            const me = await mr.json();
            if (me.accountId) {
              localStorage.setItem("taskflow_jira_accountid", me.accountId);
              currentAccountId.current = me.accountId;
              // Now load shared projects with the resolved accountId
              fetchSharedProjects(me.accountId).then(serverProjects => {
                if (serverProjects && Array.isArray(serverProjects) && serverProjects.length > 0) {
                  const serverIds = new Set(serverProjects.map(p => p.id));
                  const localOnly = projectsRef.current.filter(p => !serverIds.has(p.id));
                  const merged = [...serverProjects, ...localOnly];
                  setProjects(merged);
                  try { localStorage.setItem("taskflow_projects", JSON.stringify(merged)); } catch {}
                }
              });
            }
          }
        } catch (e) { console.warn("[Sync] Could not resolve accountId:", e.message); }
      }
      // Keep manual tasks AND email-converted tasks from current state
      const manual = tasks.filter((t) => t.source === "manual" || t.id?.startsWith("email-"));
      const errors = [];
      if (outTasks?.error) errors.push(`Outlook: ${outTasks.error}`);
      if (jiraTasks?.error) errors.push(`Jira: ${jiraTasks.error}`);
      console.log("Sync - jiraConnected:", jiraConnected, "jiraTasks:", Array.isArray(jiraTasks) ? jiraTasks.length + " tasks" : JSON.stringify(jiraTasks)?.substring(0,100));

      // Email-converted tasks live locally only (id starts with "email-")
      const emailTasks = tasks.filter(t => t.id?.startsWith("email-"));
      const emailTaskTitles = new Set(emailTasks.map(t => t.title.trim().toLowerCase()));

      // Also load any previously suppressed Outlook task IDs from localStorage
      const suppressedIds = new Set(JSON.parse(localStorage.getItem("taskflow_suppressed_outlook") || "[]"));

      // Filter Outlook To-Do tasks:
      // - Remove any whose title matches an email-converted task
      // - Remove any explicitly suppressed IDs (previously duplicated)
      const filteredOutTasks = Array.isArray(outTasks)
        ? outTasks.filter(t => {
            if (suppressedIds.has(t.rawId)) return false;
            if (emailTaskTitles.has(t.title?.trim().toLowerCase())) {
              // Auto-suppress this Outlook task ID so it stays gone
              suppressedIds.add(t.rawId);
              localStorage.setItem("taskflow_suppressed_outlook", JSON.stringify([...suppressedIds]));
              return false;
            }
            return true;
          })
        : [];

      let flaggedEmailTasks = [];
      let replyMap = {};
      if (msConnected) {
        const em = await fetchOutlookEmails(config);
        const convertedIds = new Set(tasks.filter(t => t.id?.startsWith("email-")).map(t => t.id.replace("email-", "")));
        setEmails(em.filter(e => !convertedIds.has(e.id)));
        // Fetch flagged emails - these become tasks in the list
        flaggedEmailTasks = await fetchFlaggedEmails(config);

        // Check for replies on email-sourced tasks and subtasks
        const emailTasksToCheck = tasks.filter(t =>
          (t.id?.startsWith("email-") || (t.subtasks || []).some(s => s.stEmailId || s.text?.startsWith("✉ "))) && !t.done
        );
        if (emailTasksToCheck.length) {
          try {
            replyMap = await checkEmailReplies(config, emailTasksToCheck);
          } catch (e) {
            console.warn("[Sync] Reply detection failed:", e.message);
          }
        }
      }

      // Strip any locally-done Jira tasks from the main Jira results - they must only
      // come back through the controlled mention recheck below, which checks the latest
      // comment explicitly. Without this, the main mention query returns them and reopens
      // them on every sync regardless of whether the user was actually @mentioned again.
      const locallyDoneJiraIds = new Set(
        tasks.filter(t => t.source === "jira" && t.done && t.locallyDoneAt).map(t => t.id)
      );

      // ── "Created by me" - fetch BEFORE building newTasks so the Set is ready ──
      const createdByMeKeys = new Set();
      const extraReporterTasks = []; // reporter issues not already in jiraTasks
      const syncAccountId = localStorage.getItem("taskflow_jira_accountid") || "";
      if (jiraConnected && syncAccountId) {
        try {
          const { jiraUrl: jUrl2, jiraEmail: jEmail2, jiraToken: jToken2 } = activeConfig;
          const auth2 = btoa(`${jEmail2}:${jToken2}`);
          const base2 = jUrl2.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
          const jqlReporter = "reporter = " + '"' + syncAccountId + '"' + " AND statusCategory != Done AND updated >= -90d ORDER BY updated DESC";
          const rReporter = await fetch(
            proxyUrl(base2 + "/rest/api/3/search/jql?jql=" + encodeURIComponent(jqlReporter) + "&fields=status,summary,priority,duedate&maxResults=100"),
            { headers: { Authorization: "Basic " + auth2, Accept: "application/json" } }
          );
          if (rReporter.ok) {
            const dReporter = await rReporter.json();
            const reporterIssues = dReporter.issues || [];
            const jiraTaskKeys = new Set((Array.isArray(jiraTasks) ? jiraTasks : []).map(t => t.jiraKey));
            reporterIssues.forEach(issue => {
              createdByMeKeys.add(issue.key);
              if (!jiraTaskKeys.has(issue.key)) {
                const statusName = issue.fields.status?.name || "";
                extraReporterTasks.push({
                  id: "reporter-" + issue.key,
                  title: "[" + issue.key + "] " + (issue.fields.summary || issue.key),
                  source: "jira",
                  jiraKey: issue.key,
                  jiraStatus: statusName,
                  jiraUrl: base2 + "/browse/" + issue.key,
                  status: (() => {
                    const s = statusName.toLowerCase();
                    if (s.includes("progress") || s.includes("review") || s.includes("triage")) return "In Progress";
                    if (s === "with development team") return "Waiting Response";
                    if (s.includes("wait") || s.includes("hold") || s.includes("pending")) return "Waiting Response";
                    return "To Start";
                  })(),
                  done: false,
                  priority: (issue.fields.priority?.name || "Medium").toLowerCase(),
                  due: issue.fields.duedate || null,
                  description: "Reported by me",
                  assignedToMe: false, mentioned: false, isSDM: false, isPC: false,
                  waitingOn: null, waitingRole: null, supportStatus: null, devCardStatus: null,
                  appCreated: true,
                  fromImpl: false,
                  transitions: [],
                });
              }
            });
            console.log("[CreatedByMe]", createdByMeKeys.size, "open, adding", extraReporterTasks.length, "new to task list");
          }
        } catch (e) { console.warn("[CreatedByMe] reporter query error:", e.message); }
      }

      const newTasks = [
        ...filteredOutTasks,
        ...(Array.isArray(jiraTasks) ? jiraTasks.filter(t => !locallyDoneJiraIds.has(t.id)) : []),
        ...extraReporterTasks, // reporter issues not already in jiraTasks
        ...manual,
        ...flaggedEmailTasks,
      ];

      // Re-check locally-done Jira tasks for new @mentions.
      // These were stripped from the main Jira results above, so we check them separately.
      // ONLY resurface if the LATEST comment was posted AFTER locallyDoneAt AND explicitly @mentions the user.
      const locallyDoneJiraTasks = tasks.filter(t => t.source === "jira" && t.done && t.locallyDoneAt && t.jiraKey);

      if (jiraConnected && locallyDoneJiraTasks.length > 0) {
        try {
          const { jiraUrl, jiraEmail, jiraToken } = activeConfig;
          const auth = btoa(`${jiraEmail}:${jiraToken}`);
          const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
          const currentUserAccountId = await fetchJiraCurrentUserAccountId(activeConfig);
          const newlyMentionedKeys = new Set();

          if (currentUserAccountId) {
            await Promise.all(locallyDoneJiraTasks.map(async (existingTask) => {
              try {
                const commentsRes = await fetch(
                  proxyUrl(`${baseUrl}/rest/api/3/issue/${existingTask.jiraKey}/comment?maxResults=1&orderBy=-created`),
                  { headers: { "Authorization": `Basic ${auth}`, "Accept": "application/json" } }
                );
                if (!commentsRes.ok) return;
                const commentsData = await commentsRes.json();
                const latestComment = commentsData.comments?.[0];
                if (!latestComment) return;
                // Must be after we marked it done
                const commentDate = new Date(latestComment.created || 0);
                if (commentDate <= new Date(existingTask.locallyDoneAt)) return;
                // Must explicitly contain the user's accountId in the comment body
                const bodyText = JSON.stringify(latestComment.body || "");
                if (bodyText.includes(currentUserAccountId)) {
                  newlyMentionedKeys.add(existingTask.jiraKey);
                }
              } catch {}
            }));
          }

          // All locally-done tasks go back into newTasks:
          // - with _recheckMentioned:true if they have a qualifying new mention (will be resurfaced)
          // - with their existing done:true state if no new mention (stays in Completed)
          locallyDoneJiraTasks.forEach(existingTask => {
            const hasNewMention = newlyMentionedKeys.has(existingTask.jiraKey);
            newTasks.push({ ...existingTask, _recheckMentioned: hasNewMention });
          });
        } catch {
          locallyDoneJiraTasks.forEach(existingTask => newTasks.push({ ...existingTask, _recheckMentioned: false }));
        }
      } else {
        locallyDoneJiraTasks.forEach(existingTask => newTasks.push({ ...existingTask, _recheckMentioned: false }));
      }
      // Fetch available transitions for each Jira task
      // Preserve local-only fields (tags, subtasks, notes, senderName etc) from existing tasks
      const localTaskMap = {};
      tasks.forEach(t => { localTaskMap[t.id] = t; });

      const tasksWithTransitions = await Promise.all(newTasks.map(async (t) => {
        const existing = localTaskMap[t.id] || {};
        const merged = {
          ...t,
          // Preserve local fields that don't come from APIs
          tags: existing.tags?.length ? existing.tags : (t.tags || []),
          subtasks: (() => {
            // Use existing subtasks (preserves user edits) but overlay fresh Jira link data
            const base = existing.subtasks?.length ? existing.subtasks : (t.subtasks || []);
            // Build a map of fresh Jira link data from the incoming task's subtasks
            const freshMap = {};
            (t.subtasks || []).forEach(s => {
              if (s.jiraLinkStatus || s.jiraLinkAssignee) {
                freshMap[s.id] = { jiraLinkStatus: s.jiraLinkStatus, jiraLinkAssignee: s.jiraLinkAssignee };
              }
            });
            if (Object.keys(freshMap).length === 0) return base;
            return base.map(s => freshMap[s.id] ? { ...s, ...freshMap[s.id] } : s);
          })(),
          localNotes: existing.localNotes || t.localNotes || [],
          localNote: existing.localNote || t.localNote || "",
          senderName: t.senderName || existing.senderName,
          senderEmail: t.senderEmail || existing.senderEmail,
          receivedDate: t.receivedDate || existing.receivedDate,
          // Preserve manually set due date and webLink - never overwrite with API data
          due: existing.localDueOverride ? existing.due : (t.due || existing.due || null),
          localDueOverride: existing.localDueOverride || false,
          webLink: existing.webLink || t.webLink || "",
          // Preserve done/status for locally-completed Jira tasks and flagged emails
          // ONLY resurface a Jira task marked done locally if:
          // 1. The task is now assigned to the current user (wasn't before), OR
          // 2. The task now has a new @mention (mentioned flag is true and was not previously)
          // Generic Jira activity (comments by others, status changes etc) does NOT reopen the task
          done: (() => {
            if (!existing.done) return t.done || false;
            if (t.isFlaggedEmail) return true;
            if (t.source === "jira" && existing.locallyDoneAt) {
              // Reopen only if there is a new @mention after the user marked it done
              if (t._recheckMentioned === true) return false;
            }
            if (t.source === "jira") return true;
            return t.done || false;
          })(),
          status: (() => {
            if (!existing.done) return t.status || existing.status || "";
            if (t.isFlaggedEmail) return existing.status || "Done";
            if (t.source === "jira" && existing.locallyDoneAt) {
              if (t._recheckMentioned === true) return t.status || existing.status || "";
            }
            if (t.source === "jira") return existing.status || "Done";
            return t.status || existing.status || "";
          })(),
          locallyDoneAt: (() => {
            if (!existing.done || !existing.locallyDoneAt) return existing.locallyDoneAt || null;
            if (t.source === "jira") {
              if (t._recheckMentioned === true) return null;
            }
            return existing.locallyDoneAt;
          })(),
          // Preserve supportStatus and devCardStatus from fresh API
          supportStatus: t.supportStatus ?? existing.supportStatus ?? null,
          devCardStatus: t.devCardStatus ?? existing.devCardStatus ?? null,
          // Clear Jira link data if the webLink is no longer a Jira URL
          webLinkJiraStatus:   extractJiraKeyFromLink(existing.webLink || t.webLink || "") ? (t.webLinkJiraStatus ?? existing.webLinkJiraStatus ?? null) : null,
          webLinkJiraAssignee: extractJiraKeyFromLink(existing.webLink || t.webLink || "") ? (t.webLinkJiraAssignee ?? existing.webLinkJiraAssignee ?? null) : null,
          // appCreated: stamped from the reporter JQL query - survives the merge
          appCreated: (createdByMeKeys && createdByMeKeys.has(t.jiraKey)) || t.appCreated || false,
          // Flags always come from fresh API data - never OR with stale existing values.
          // This ensures tasks appear in exactly the right sub-filter for the current user.
          assignedToMe: t.assignedToMe ?? false,
          mentioned:    t.mentioned ?? false,
          isSDM:        t.isSDM ?? false,
          isPC:         t.isPC  ?? false,
          blockStatus:    t.blockStatus    ?? null,
          latestComment:  t.latestComment  ?? existing.latestComment ?? null,
          waitingOn:      t.waitingOn      ?? null,
          waitingRole:  t.waitingRole ?? null,
        };
        if (merged.source === "jira" && merged.jiraKey && jiraConnected) {
          try {
            const transitions = await fetchJiraTransitions(config, merged.jiraKey);
            return { ...merged, transitions };
          } catch { return merged; }
        }
        return merged;
      }));
      const resurfaced = tasksWithTransitions.filter(t => {
        const existing = localTaskMap[t.id];
        return existing?.done && !t.done && t.source === "jira" && existing?.locallyDoneAt;
      });
      // ── Sync main task webLink Jira status + assignee ───────────────────────
      // For manual tasks with a Jira URL in the webLink field, fetch status + assignee
      if (jiraConnected) {
        try {
          const { jiraUrl: jUrl4, jiraEmail: jEmail4, jiraToken: jToken4 } = activeConfig;
          const auth4 = btoa(`${jEmail4}:${jToken4}`);
          const base4 = jUrl4.replace(/\/+$/, "").replace(/^http:\/\//, "https://");

          const webLinkTasks = tasksWithTransitions.filter(t =>
            t.source === "manual" && t.webLink && extractJiraKeyFromLink(t.webLink)
          );

          if (webLinkTasks.length > 0) {
            const keys4 = [...new Set(webLinkTasks.map(t => extractJiraKeyFromLink(t.webLink)))];
            const jql4 = "issueKey in (" + keys4.map(k => '"' + k + '"').join(",") + ")";
            const r4 = await fetch(
              proxyUrl(base4 + "/rest/api/3/search/jql?jql=" + encodeURIComponent(jql4) + "&fields=status,assignee&maxResults=50"),
              { headers: { Authorization: "Basic " + auth4, Accept: "application/json" } }
            );
            if (r4.ok) {
              const d4 = await r4.json();
              const infoByKey = {};
              (d4.issues || []).forEach(i => {
                infoByKey[i.key] = {
                  status: i.fields.status?.name || "",
                  assignee: i.fields.assignee?.displayName || null,
                };
              });
              tasksWithTransitions.forEach(t => {
                const key = extractJiraKeyFromLink(t.webLink);
                if (!key || !infoByKey[key]) return;
                t.webLinkJiraStatus   = infoByKey[key].status;
                t.webLinkJiraAssignee = infoByKey[key].assignee;
              });
              console.log("[WebLinkSync] Updated", webLinkTasks.length, "task(s) with Jira status/assignee");
            }
          }
          // Clear stale Jira link data from tasks that no longer have a Jira webLink
          tasksWithTransitions.forEach(t => {
            if (t.source === "manual" && !extractJiraKeyFromLink(t.webLink)) {
              t.webLinkJiraStatus   = null;
              t.webLinkJiraAssignee = null;
            }
          });
        } catch(e) { console.warn("[WebLinkSync] error:", e.message); }
      }

      // ── Sync subtask statuses from linked Jira URLs ─────────────────────────
      // For any manual task subtask whose link is a Jira URL, fetch the current
      // Jira status and update the subtask status automatically
      if (jiraConnected) {
        try {
          const { jiraUrl: jUrl3, jiraEmail: jEmail3, jiraToken: jToken3 } = activeConfig;
          const auth3 = btoa(`${jEmail3}:${jToken3}`);
          const base3 = jUrl3.replace(/\/+$/, "").replace(/^http:\/\//, "https://");

          // Collect all subtask Jira links across all manual tasks
          const subtaskJiraLinks = []; // { taskId, subtaskId, jiraKey }
          tasksWithTransitions.forEach(t => {
            if (t.source !== "manual") return;
            (t.subtasks || []).forEach(s => {
              const key = extractJiraKeyFromLink(s.link);
              if (key) subtaskJiraLinks.push({ taskId: t.id, subtaskId: s.id, jiraKey: key });
            });
          });

          if (subtaskJiraLinks.length > 0) {
            const uniqueKeys = [...new Set(subtaskJiraLinks.map(x => x.jiraKey))];
            const jql3 = "issueKey in (" + uniqueKeys.map(k => '"' + k + '"').join(",") + ")";
            const r3 = await fetch(
              proxyUrl(base3 + "/rest/api/3/search/jql?jql=" + encodeURIComponent(jql3) + "&fields=status,assignee&maxResults=50"),
              { headers: { Authorization: "Basic " + auth3, Accept: "application/json" } }
            );
            if (r3.ok) {
              const d3 = await r3.json();
              const statusByKey = {};
              const assigneeByKey = {};
              (d3.issues || []).forEach(i => {
                statusByKey[i.key]   = i.fields.status?.name || "";
                assigneeByKey[i.key] = i.fields.assignee?.displayName || null;
              });
              // Update subtask statuses in tasksWithTransitions
              let anyUpdated = false;
              const changedTaskIds = new Set();
              tasksWithTransitions.forEach(t => {
                if (t.source !== "manual") return;
                const updated = (t.subtasks || []).map(s => {
                  const key = extractJiraKeyFromLink(s.link);
                  if (!key || !statusByKey[key]) return s;
                  const newStatus = jiraStatusToSubtaskStatus(statusByKey[key]);
                  const newAssignee = assigneeByKey[key] || null;
                  const newJiraStatus = statusByKey[key];
                  // Always update Jira link fields even if status unchanged
                  const statusChanged = newStatus && newStatus !== s.status;
                  const assigneeChanged = newAssignee !== s.jiraLinkAssignee;
                  const jiraStatusChanged = newJiraStatus !== s.jiraLinkStatus;
                  if (!statusChanged && !assigneeChanged && !jiraStatusChanged) return s;
                  anyUpdated = true;
                  return {
                    ...s,
                    ...(statusChanged ? { status: newStatus, done: newStatus === "Done" } : {}),
                    jiraLinkStatus: newJiraStatus,
                    jiraLinkAssignee: newAssignee || s.jiraLinkAssignee || null,
                  };
                });
                if (updated.some((s, i) => s !== (t.subtasks || [])[i])) {
                  t.subtasks = updated;
                  t.localUpdatedAt = new Date().toISOString();
                  changedTaskIds.add(t.id);
                }
              });
              if (anyUpdated) console.log("[SubtaskSync] Updated subtask statuses from Jira links");
              // Persist changed tasks to the server so the background refresh can't revert them
              changedTaskIds.forEach(id => {
                const t = tasksWithTransitions.find(x => x.id === id);
                if (t) {
                  fetch(`${MANUAL_TASKS_API}/${t.id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(t),
                  }).catch(e => console.warn("[SubtaskSync] Server persist failed:", e.message));
                }
              });
            }
          }
        } catch(e) { console.warn("[SubtaskSync] error:", e.message); }
      }

      // Reply detection results applied in post-sync setTasks below

      setTasks(sanitizeTasks(tasksWithTransitions));

      // Apply reopen results separately using live state to avoid stale closure issues
      if (Object.keys(replyMap).length) {
        setTimeout(() => {
          setTasks(prev => prev.map(t => {
            if (replyMap[t.id]) {
              return { ...t, replied: true, repliedAt: replyMap[t.id].repliedAt };
            }
            if (!t.subtasks?.length) return t;
            const hasUpdate = t.subtasks.some(s => replyMap[`${t.id}:${s.id}`]);
            if (!hasUpdate) return t;
            return {
              ...t,
              subtasks: t.subtasks.map(s => {
                const r = replyMap[`${t.id}:${s.id}`];
                if (!r) return s;
                if (r.reopen || r.newReply) {
                  // Don't auto-reopen - just flag that a new reply has arrived
                  console.log(`[Sync] New reply flagged on "${s.text?.substring(0,40)}" - user can reopen manually`);
                  return { ...s, newReply: true, newReplyAt: r.repliedAt };
                }
                if (r.unflagged && !s.done) {
                  return { ...s, done: true, status: "Done", replied: true, repliedAt: r.repliedAt, doneAt: new Date().toISOString() };
                }
                if (r.replied && !s.done && s.status !== "In Progress" && s.status !== "Done") {
                  return { ...s, replied: true, repliedAt: r.repliedAt, status: "In Progress" };
                }
                return s;
              })
            };
          }));
        }, 100);
      }

      // Auto-update implementation task statuses from linked Jira keys
      // Build done set from synced tasks
      const doneKeys = new Set(tasksWithTransitions.filter(t => t.done || t.status === "Done").map(t => t.jiraKey).filter(Boolean));

      // Collect all impl task Jira keys via ref (avoids stale closure)
      const implJiraKeys = [];
      projectsRef.current.forEach(proj => {
        proj.tasks.forEach(t => { if (t.jiraKey) implJiraKeys.push(t.jiraKey); });
        proj.modules.forEach(m => m.tasks.forEach(t => { if (t.jiraKey) implJiraKeys.push(t.jiraKey); }));
        (proj.moduleSetup || []).forEach(r => { if (r.jiraKey) implJiraKeys.push(r.jiraKey); });
        (proj.mobileForms || []).forEach(r => { if (r.jiraKey) implJiraKeys.push(r.jiraKey); });
        (proj.templates || []).forEach(r => { if (r.supportKey) implJiraKeys.push(r.supportKey); });
      });

      // Map from jiraKey → live status name
      const jiraStatusMap = {};
      // Map from SUPPORT key → { edKey, edStatus, templKey, templStatus }
      const templateLinksMap = {};

      if (jiraConnected && implJiraKeys.length > 0) {
        try {
          const { jiraUrl: jUrl, jiraEmail: jEmail, jiraToken: jToken } = config;
          const auth = btoa(`${jEmail}:${jToken}`);
          const baseUrl = jUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
          const jql = "issueKey in (" + implJiraKeys.slice(0, 50).map(k => '"' + k + '"').join(",") + ")";
          const r = await fetch(proxyUrl(baseUrl + "/rest/api/3/search/jql?jql=" + encodeURIComponent(jql) + "&fields=status,issuelinks&maxResults=50"), {
            headers: { Authorization: "Basic " + auth, Accept: "application/json" },
          });
          if (r.ok) {
            const d = await r.json();
            // Collect all ED- and TEMPL- keys we need to fetch
            const linkedKeys = new Set();
            (d.issues || []).forEach(i => {
              const statusName = i.fields.status?.name || "";
              const cat = i.fields.status?.statusCategory?.key || "";
              jiraStatusMap[i.key] = statusName;
              const nameLower = statusName.toLowerCase();
              if (cat === "done" || nameLower === "resolved" || nameLower === "done" || nameLower === "closed" || nameLower === "complete") {
                doneKeys.add(i.key);
              }
              // For SUPPORT- cards: find linked ED- and TEMPL- cards
              if (i.key.startsWith("SUPPORT-")) {
                const links = i.fields.issuelinks || [];
                let edKey = null, templKey = null;
                links.forEach(lnk => {
                  const linked = lnk.outwardIssue || lnk.inwardIssue;
                  if (!linked) return;
                  if (linked.key.startsWith("ED-")) { edKey = linked.key; linkedKeys.add(linked.key); }
                  if (linked.key.startsWith("TEMPL-")) { templKey = linked.key; linkedKeys.add(linked.key); }
                });
                templateLinksMap[i.key] = { edKey, templKey, edStatus: null, templStatus: null };
              }
            });

            // Fetch statuses for linked ED- and TEMPL- keys if any found
            if (linkedKeys.size > 0) {
              try {
                const linkedJql = "issueKey in (" + [...linkedKeys].map(k => '"' + k + '"').join(",") + ")";
                const lr = await fetch(proxyUrl(baseUrl + "/rest/api/3/search/jql?jql=" + encodeURIComponent(linkedJql) + "&fields=status&maxResults=50"), {
                  headers: { Authorization: "Basic " + auth, Accept: "application/json" },
                });
                if (lr.ok) {
                  const ld = await lr.json();
                  (ld.issues || []).forEach(li => {
                    jiraStatusMap[li.key] = li.fields.status?.name || "";
                  });
                  // Write ED and TEMPL statuses back into templateLinksMap
                  Object.keys(templateLinksMap).forEach(suppKey => {
                    const entry = templateLinksMap[suppKey];
                    if (entry.edKey) entry.edStatus = jiraStatusMap[entry.edKey] || null;
                    if (entry.templKey) entry.templStatus = jiraStatusMap[entry.templKey] || null;
                  });
                }
              } catch (e) { console.warn("[Impl] linked keys fetch error:", e.message); }
            }
          }
        } catch (e) { console.warn("[Impl] error:", e.message); }
      }

      // ── Feature: Impl Jira tickets + app-created tickets appear in Jira view ───
      // 1. Collect impl project jira keys not yet done
      const implJiraTasks = [];

      // createdByMeKeys populated earlier (before newTasks) - see above

      projectsRef.current.forEach(proj => {
        const allProjTasks = [
          ...proj.tasks,
          ...proj.modules.flatMap(m => m.tasks),
          ...(proj.moduleSetup || []),
          ...(proj.mobileForms || []),
          ...(proj.templates || []).map(r => ({ ...r, jiraKey: r.supportKey })),
        ];
        allProjTasks.forEach(t => {
          const key = t.jiraKey;
          if (!key) return;
          if (doneKeys.has(key)) return; // already done - don't surface
          // Don't duplicate - if already in main jira tasks skip
          if (tasksWithTransitions.some(x => x.jiraKey === key)) return;
          const liveStatus = jiraStatusMap[key] || t.jiraStatus || "In Progress";
          implJiraTasks.push({
            id: `impl-jira-${key}`,
            title: `[${key}] ${t.name || t.complianceType || key}`,
            source: "jira",
            jiraKey: key,
            jiraStatus: liveStatus,
            jiraUrl: `${config.jiraUrl ? config.jiraUrl.replace(/\/+$/, "") : "https://c365online.atlassian.net"}/browse/${key}`,
            status: (() => {
              const s = liveStatus.toLowerCase();
              if (s.includes("done") || s.includes("closed") || s.includes("resolved") || s.includes("complete")) return "Done";
              if (s.includes("progress") || s.includes("review") || s.includes("triage")) return "In Progress";
              if (s.includes("wait") || s.includes("hold") || s.includes("pending")) return "Waiting Response";
              return "To Start";
            })(),
            done: false,
            priority: "medium",
            due: null,
            description: `Project: ${proj.clientName}`,
            assignedToMe: false,
            mentioned: false,
            isSDM: false,
            isPC: false,
            waitingOn: null,
            waitingRole: null,
            fromImpl: true, // flag so we can filter/label these
            projectName: proj.clientName,
            transitions: [],
          });
        });
      });

      setProjects(prev => {
        const updated = prev.map(proj => ({
          ...proj,
          tasks: proj.tasks.map(t => {
            if (!t.jiraKey) return t;
            const liveStatus = jiraStatusMap[t.jiraKey];
            const isDone = doneKeys.has(t.jiraKey);
            return {
              ...t,
              jiraStatus: liveStatus || t.jiraStatus || "",
              status: isDone ? "Complete" : t.status,
            };
          }),
          modules: proj.modules.map(m => ({
            ...m,
            tasks: m.tasks.map(t => {
              if (!t.jiraKey) return t;
              const liveStatus = jiraStatusMap[t.jiraKey];
              const isDone = doneKeys.has(t.jiraKey);
              return {
                ...t,
                jiraStatus: liveStatus || t.jiraStatus || "",
                status: isDone ? "Complete" : t.status,
              };
            }),
          })),
          moduleSetup: (proj.moduleSetup || []).map(r => {
            if (!r.jiraKey) return r;
            const liveStatus = jiraStatusMap[r.jiraKey];
            return liveStatus ? { ...r, jiraStatus: liveStatus } : r;
          }),
          mobileForms: (proj.mobileForms || []).map(r => {
            if (!r.jiraKey) return r;
            const liveStatus = jiraStatusMap[r.jiraKey];
            return liveStatus ? { ...r, jiraStatus: liveStatus } : r;
          }),
          templates: (proj.templates || []).map(r => {
            if (!r.supportKey) return r;
            const links = templateLinksMap[r.supportKey];
            if (!links) return r;
            const edDone = links.edStatus && (links.edStatus.toLowerCase().includes("done") || links.edStatus.toLowerCase().includes("complete") || links.edStatus.toLowerCase().includes("closed"));
            return {
              ...r,
              edKey: links.edKey || r.edKey || "",
              templKey: links.templKey || r.templKey || "",
              edStatus: links.edStatus || r.edStatus || "",
              templStatus: links.templStatus || r.templStatus || (edDone && !links.templKey ? "Waiting on TEMPL" : !edDone ? "Waiting on ED" : r.templStatus || ""),
            };
          }),
        }));
        return updated;
      });
      // Merge impl jira tasks into main task list (deduped above)
      if (implJiraTasks.length > 0) {
        setTasks(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          // Remove stale impl-jira entries, re-add fresh ones
          const withoutStale = prev.filter(t => !t.id.startsWith("impl-jira-"));
          return [...withoutStale, ...implJiraTasks];
        });
      } else {
        // Remove any stale impl-jira tasks if no longer relevant
        setTasks(prev => prev.filter(t => !t.id.startsWith("impl-jira-")));
      }

      if (resurfaced.length > 0) {
        showToast(`${resurfaced.length} task${resurfaced.length > 1 ? "s" : ""} reopened due to new Jira activity`, "error");
      }
      // Don't clear selected - preserve expanded card across syncs
      if (errors.length) showToast(errors.join(" | "), "error");
      else showToast(`Synced ${tasksWithTransitions.length} tasks`);

      // ── Fetch recently completed dev tickets for Team Activity feed ─────────
      // Queries DEVOPS, CLOUDDEV, ED, TEMPL, TEMP2A for tickets moved to Done
      // in the last 7 days. No personal data - purely Jira ticket status changes.
      if (jiraConnected) {
        try {
          const { jiraUrl: jUrlA, jiraEmail: jEmailA, jiraToken: jTokenA } = activeConfig;
          const authA = btoa(`${jEmailA}:${jTokenA}`);
          const baseA = jUrlA.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
          const devSpaces = ["DEVOPS", "CLOUDDEV", "ED", "TEMPL", "TEMP2A"];
          const devDays  = parseInt(localStorage.getItem("taskflow_dev_days")  || "30");
          const devLimit = parseInt(localStorage.getItem("taskflow_dev_limit") || "500");
          const jqlA = `project in (${devSpaces.map(s => `"${s}"`).join(",")}) AND statusCategory = Done AND updated >= -${devDays}d ORDER BY updated DESC`;
          const fieldsA = "summary,status,assignee,updated,priority,customfield_10435";
          // Paginate using nextPageToken (Jira /search/jql cursor-based pagination)
          let allIssuesA = [];
          let nextPageToken = undefined;
          const pageSize = 100;
          while (allIssuesA.length < devLimit) {
            const pageParam = nextPageToken ? `&nextPageToken=${encodeURIComponent(nextPageToken)}` : "";
            const resA = await fetch(
              proxyUrl(`${baseA}/rest/api/3/search/jql?jql=${encodeURIComponent(jqlA)}&fields=${fieldsA}&maxResults=${pageSize}${pageParam}`),
              { headers: { Authorization: `Basic ${authA}`, Accept: "application/json" } }
            );
            if (!resA.ok) break;
            const dataA = await resA.json();
            const issues = dataA.issues || [];
            allIssuesA = [...allIssuesA, ...issues];
            if (dataA.isLast || !dataA.nextPageToken || issues.length < pageSize) break;
            nextPageToken = dataA.nextPageToken;
          }
          if (allIssuesA.length > 0) {
            const dataA = { issues: allIssuesA };
            const completed = (dataA.issues || []).slice(0, devLimit).map(i => ({
              id: i.id,
              key: i.key,
              summary: i.fields.summary,
              status: i.fields.status?.name || "Done",
              assignee: i.fields.assignee?.displayName || "Unassigned",
              updated: i.fields.updated,
              project: i.key.split("-")[0],
              affectedClient: (() => {
                const v = i.fields.customfield_10435;
                if (!v) return null;
                if (Array.isArray(v)) return v.map(x => typeof x === "object" ? (x.value ?? x.name) : x).join(", ");
                if (typeof v === "object") return v.value ?? v.name ?? null;
                return String(v);
              })(),
            }));
            setDevCompletedTickets(completed);
            try { localStorage.setItem("taskflow_dev_completed", JSON.stringify(completed)); } catch {}
            console.log(`[DevActivity] ${completed.length} completed dev tickets found (${devDays}d, limit ${devLimit})`);
          }
        } catch(e) { console.warn("[DevActivity] error:", e.message); }
      }
    } catch (e) {
      showToast(e.message, "error");
    }
    setSyncing(false);
  }, [config, msConnected, jiraConnected, tasks, projects]);

  // Auto-sync every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (msConnected || jiraConnected) {
        sync();
      }
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [sync, msConnected, jiraConnected]);

  // ── Browser tab notification badge ───────────────────────────────────────
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const overdue = tasks.filter(t => !t.done && t.due && t.due < today).length;
    const noReply = tasks.filter(t => isNoReply(t)).length;
    const urgent  = overdue + noReply;
    document.title = urgent > 0
      ? `(${urgent}) C365Cloud Task Manager`
      : "C365Cloud Task Manager";
  }, [tasks]);

  // Expose config and toast to detail panel components via window
  useEffect(() => {
    window.__c365config__ = config;
    window.__c365showToast__ = showToast;
    window.__c365closeDetail__ = () => { setSelected(null); setMobileDetailOpen(false); };
    window.__c365sync__ = sync;
  }, [config, sync]);

  // ── Version check - ping server every 10 mins to detect new deployments ──
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch("/version.json?t=" + Date.now());
        if (!res.ok) return;
        const data = await res.json();
        if (data.version && data.version !== APP_VERSION) {
          setNewVersionAvailable(true);
        }
      } catch {}
    };
    check();
    const interval = setInterval(check, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  // N = New task | J = New Jira | S = Sync | / = Focus search | Esc = Close modals
  useEffect(() => {
    const handler = (e) => {
      // Don't fire when typing in an input, textarea or contenteditable
      const tag = e.target.tagName;
      const inInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target.isContentEditable;
      if (e.key === "Escape") {
        setShowNewTask(false); setShowNewJira(false); setShowConfig(false);
        setSelected(null); setPopoutTask(null);
        return;
      }
      if (inInput) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); setShowNewTask(s => !s); }
      if (e.key === "j" || e.key === "J") { e.preventDefault(); setShowNewJira(s => !s); }
      if (e.key === "s" || e.key === "S") { e.preventDefault(); if (!syncing) sync(); }
      if (e.key === "/") { e.preventDefault(); document.querySelector(".search-box")?.focus(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [sync, syncing]);

  const handleVisionImage = async (file) => {
    setVisionBusy(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(",")[1]);
        r.onerror = reject; r.readAsDataURL(file);
      });
      const base = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:3001" : "";
      const res = await fetch(`${base}/api/vision/tasks`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64: b64, mediaType: file.type || "image/jpeg" }),
      });
      const data = await res.json();
      if (data.error) { showToast("Could not read image: " + data.error, "error"); setVisionBusy(false); return; }
      // Present a review: title editable, each subtask defaults to "subtask", user can change to task/skip
      setVisionReview({
        title: data.title || "Imported notes",
        transcription: data.transcription || "",
        items: (data.subtasks || []).map((t, i) => ({ id: i, text: t, kind: "subtask" })), // kind: subtask | task | skip
      });
    } catch (e) { showToast("Read image failed: " + e.message, "error"); }
    setVisionBusy(false);
  };

  const createFromVision = async () => {
    if (!visionReview) return;
    const myName = localStorage.getItem("taskflow_jira_displayname") || "Unknown";
    const now = new Date().toISOString();
    const kept = visionReview.items.filter(it => it.kind !== "skip" && it.text.trim());
    const subtaskItems = kept.filter(it => it.kind === "subtask");
    const standaloneTasks = kept.filter(it => it.kind === "task");

    const newTasks = [];
    // Main task with its subtasks
    if (visionReview.title.trim() || subtaskItems.length) {
      newTasks.push({
        id: `m${Date.now()}`, title: visionReview.title.trim() || "Imported notes",
        priority: "medium", source: "manual", due: "", status: "To Start",
        done: false, description: visionReview.transcription ? `From notes:\n${visionReview.transcription}` : "",
        createdBy: myName, createdAt: now, localUpdatedAt: now,
        subtasks: subtaskItems.map((it, i) => ({ id: Date.now() + i + 1, text: it.text.trim(), done: false, status: "To Start", due: null, link: null, assignedTo: "" })),
      });
    }
    // Any items the user promoted to standalone tasks
    standaloneTasks.forEach((it, i) => {
      newTasks.push({
        id: `m${Date.now() + 1000 + i}`, title: it.text.trim(), priority: "medium", source: "manual",
        due: "", status: "To Start", done: false, description: "", createdBy: myName, createdAt: now, localUpdatedAt: now, subtasks: [],
      });
    });

    setTasks(prev => [...newTasks, ...prev]);
    newTasks.forEach(t => {
      fetch(MANUAL_TASKS_API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(t) })
        .catch(e => console.warn("[ManualTasks] Vision create sync failed:", e.message));
    });
    logActivity("Created from notes", newTasks[0]?.title || "notes", `${subtaskItems.length} subtask(s), ${standaloneTasks.length} extra task(s)`);
    showToast(`Created ${newTasks.length} task${newTasks.length !== 1 ? "s" : ""} from notes`);
    setVisionReview(null);
  };

  const addTask = async () => {
    if (!newTask.title.trim()) return;
    const myName = localStorage.getItem("taskflow_jira_displayname") || "Unknown";
    const t = {
      ...newTask,
      id: `m${Date.now()}`,
      done: false,
      description: "",
      createdBy: myName,
      createdAt: new Date().toISOString(),
      localUpdatedAt: new Date().toISOString(),
    };
    setTasks((prev) => [t, ...prev]);
    setNewTask({ title: "", priority: "medium", source: "manual", due: "", status: "To Start", assignedTo: "", recur: "" });
    setShowNewTask(false);
    logActivity("Created", t.title, `Priority: ${t.priority}`);
    showToast(`Task added${t.assignedTo ? ` · assigned to ${t.assignedTo}` : ""}`);
    // Sync to server
    fetch(MANUAL_TASKS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(t),
    }).catch(e => console.warn("[ManualTasks] Create sync failed:", e.message));
    // Send notification email if assigned to someone
    if (t.assignedTo && config.msToken) {
      try {
        const email = await lookupJiraUserEmail(config, t.assignedTo);
        if (email) {
          await sendOutlookEmail(config, {
            to: email,
            subject: `Task assigned to you: ${t.title.substring(0, 60)}`,
            body: `
              <p>Hi ${t.assignedTo.split(" ")[0]},</p>
              <p>A task has been assigned to you in the C365Cloud Task Manager.</p>
              <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
                <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Task:</td><td style="padding:6px 12px">${t.title}</td></tr>
                ${t.due ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Due:</td><td style="padding:6px 12px">${t.due}</td></tr>` : ""}
                <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Priority:</td><td style="padding:6px 12px">${t.priority}</td></tr>
                ${t.description ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555;vertical-align:top">Description:</td><td style="padding:6px 12px">${t.description.replace(/\n/g, "<br>")}</td></tr>` : ""}
                ${(t.localNotes || []).length > 0 ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555;vertical-align:top">Notes:</td><td style="padding:6px 12px">${(t.localNotes || []).map(n => `<div style="margin-bottom:6px;padding:6px 8px;background:#f5f5f5;border-left:3px solid #1B3A6B;font-size:13px">${(n.text||"").replace(/\n/g,"<br>")}<span style="font-size:11px;color:#888;display:block;margin-top:3px">${n.date||""}${n.author ? " · " + n.author : ""}</span></div>`).join("")}</td></tr>` : ""}
              </table>
              <p style="color:#888;font-size:12px">This task has been logged in the C365Cloud Task Manager.</p>
            `,
          });
        }
      } catch (e) { console.warn("[addTask] Email notification failed:", e.message); }
    }
  };

  const handleCreateJira = async () => {
    if (!newJira.summary.trim() || !newJira.description.trim() || !newJira.affectedClient.trim() || !newJira.category || !newJira.ticketType) {
      showToast("Please fill in all fields before creating the issue", "error");
      return;
    }
    setCreatingJira(true);
    try {
      const result = await createJiraIssue(config, newJira.summary.trim(), newJira.description.trim(), newJira.affectedClient.trim(), newJira.category, newJira.ticketType);
      // Upload any queued attachments with progress tracking
      if (jiraAttachments.length > 0) {
        for (const file of jiraAttachments) {
          setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
          try {
            await addJiraAttachment(config, result.key, file, (pct) => {
              setUploadProgress(prev => ({ ...prev, [file.name]: pct }));
            });
            setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
          } catch (e) {
            showToast(`Attachment failed: ${e.message}`, "error");
          }
        }
        setUploadProgress({});
      }
      addAppCreatedKey(result.key);
      showToast(`✓ Created ${result.key}${jiraAttachments.length > 0 ? ` with ${jiraAttachments.length} attachment${jiraAttachments.length > 1 ? "s" : ""}` : ""}`);
      setNewJira({ summary: "", description: "", affectedClient: "", category: "", ticketType: "" });
      setJiraAttachments([]);
      setUploadProgress({});
      setShowNewJira(false);
      setTimeout(() => sync(), 1500);
    } catch (e) {
      showToast(e.message, "error");
    }
    setCreatingJira(false);
  };

  // Stable ref function - called by ImplementationsView button click
  // Using a ref avoids batching issues with setProjects from useEffect
  openImplJiraRef.current = (task, moduleIndex, projectClientName, updateTaskFn) => {
    const isTemplates = task.section === "Templates";
    const isMobileForms = task.section === "Mobile Forms";
    // Reset attachment state BEFORE flushSync so they don't interfere with subsequent updates
    setImplJiraAttachments([]);
    setImplUploadProgress({});
    // Use flushSync to force modal state updates synchronously before any re-render
    flushSync(() => {
      setImplJiraTask({ task, moduleIndex, updateTaskFn });
      setImplJiraForm({
        summary: projectClientName + " - " + task.name,
        description: "Implementation task for " + projectClientName + "\nSection: " + (task.section || "Module Setup") + "\nOwner: " + (task.owner || "C365"),
        affectedClient: projectClientName,
        category: isTemplates ? "Templating" : isMobileForms ? "OnSite" : "Implementations",
        ticketType: isTemplates ? "New Template" : isMobileForms ? "New Development Request" : "General",
      });
      setShowImplJira(true);
    });
    if (implClientOptions.length === 0) {
      fetchJiraFieldOptions(config, "customfield_10435")
        .then(opts => setImplClientOptions(opts.length > 0 ? opts : JIRA_CLIENT_LIST))
        .catch(() => setImplClientOptions(JIRA_CLIENT_LIST));
    }
  };

  const handleSubmitImplJira = async () => {
    if (!implJiraForm.summary.trim() || !implJiraForm.description.trim() || !implJiraForm.affectedClient.trim() || !implJiraForm.category || !implJiraForm.ticketType) {
      showToast("Please fill in all required fields", "error"); return;
    }
    setCreatingImplJira(true);
    try {
      const result = await createJiraIssue(config, implJiraForm.summary.trim(), implJiraForm.description.trim(), implJiraForm.affectedClient.trim(), implJiraForm.category, implJiraForm.ticketType);
      if (implJiraAttachments.length > 0) {
        for (const file of implJiraAttachments) {
          setImplUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
          try {
            await addJiraAttachment(config, result.key, file, (pct) => {
              setImplUploadProgress(prev => ({ ...prev, [file.name]: pct }));
            });
            setImplUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
          } catch (e) { showToast("Attachment: " + e.message, "error"); }
        }
        setImplUploadProgress({});
      }
      // Call the updateTask function stored with the task
      if (implJiraTask && implJiraTask.updateTaskFn) {
        implJiraTask.updateTaskFn(implJiraTask.task.id, { jiraKey: result.key }, implJiraTask.moduleIndex);
      }
      showToast("✓ Created " + result.key + (implJiraAttachments.length > 0 ? " with " + implJiraAttachments.length + " attachment(s)" : ""));
      setShowImplJira(false);
      setImplJiraTask(null);
      setImplJiraAttachments([]);
      setImplUploadProgress({});
      setTimeout(() => sync(), 1500);
    } catch (e) { showToast("Jira error: " + e.message, "error"); }
    setCreatingImplJira(false);
  };

  const toggleDone = async (task) => {
    const newDone = !task.done;
    setTasks((prev) => {
      const mapped = prev.map((t) => t.id === task.id ? {
        ...t,
        done: newDone,
        status: newDone ? "Done" : "open",
        locallyDoneAt: newDone && t.source === "jira" ? new Date().toISOString() : null,
      } : t);
      // Auto-create next occurrence for recurring manual tasks
      if (newDone && task.recur && task.source === "manual") {
        const nextDue = nextRecurDate(task.due, task.recur);
        if (nextDue) {
          const nextTask = {
            ...task,
            id: "m" + Date.now(),
            done: false,
            status: "To Start",
            due: nextDue,
            localNotes: [],
            subtasks: (task.subtasks || []).map(s => ({ ...s, done: false })),
          };
          setTimeout(() => showToast(`🔁 Next "${task.title}" created for ${formatDate(nextDue)}`), 100);
          return [...mapped, nextTask];
        }
      }
      return mapped;
    });
    // Close/deselect the task when marking it done (it moves to Completed view)
    if (newDone) {
      setSelected(null);
      setPopoutTask(null);
      logActivity("Completed", task.title);
    } else {
      logActivity("Reopened", task.title);
    }
    if (task.source === "outlook" && msConnected) {
      try { await completeOutlookTask(config, task.listId, task.rawId); } catch { }
    }
    // Jira tasks are marked done locally only - Jira has its own workflow
    // The done state is preserved across syncs so the task won't reappear
    if (task.source === "jira" && newDone) {
      showToast(`[${task.jiraKey}] marked done in Task Manager`);
    }
  };

  const emailToTask = (email) => {
    const senderName = email.from?.emailAddress?.name || email.from?.emailAddress?.address || "Unknown";
    const senderEmail = email.from?.emailAddress?.address || "";
    const emailDomain = formatEmailDomain(senderEmail);
    const receivedDate = email.receivedDateTime
      ? new Date(email.receivedDateTime).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
      : "";
    const titleParts = [senderName, emailDomain, email.subject].filter(Boolean);
    const t = {
      id: `email-${email.id}`,
      title: titleParts.join(" | "),
      source: "outlook",
      status: "To Start",
      done: false,
      priority: "medium",
      due: null,
      description: `From: ${senderName} <${senderEmail}>\nReceived: ${receivedDate}\n\n${email.bodyPreview}`,
      senderName,
      senderEmail,
      receivedDate,
      rawId: email.id,
      listId: "default"
    };
    setTasks((prev) => [t, ...prev]);
    setEmails((prev) => prev.filter((e) => e.id !== email.id));
    if (msConnected) {
      flagOutlookEmail(config, email.id).catch((e) => showToast(`Could not flag email: ${e.message}`, "error"));
    }
    showToast(`"${email.subject}" converted to task & flagged in Outlook`);
  };

  // Email → Client Hub: pre-populate a Client Hub entry from an Outlook email
  const [emailHubPrompt, setEmailHubPrompt] = React.useState(null);
  const [emailHubClient, setEmailHubClient] = React.useState("");
  const [emailHubSaving, setEmailHubSaving] = React.useState(false);
  const [emailSubtaskParentId, setEmailSubtaskParentId] = React.useState("");

  const logEmailToClientHub = async () => {
    if (!emailHubPrompt || !emailHubClient) return;
    setEmailHubSaving(true);
    const email = emailHubPrompt;
    const senderName = email.from?.emailAddress?.name || email.from?.emailAddress?.address || "";
    const senderEmail = email.from?.emailAddress?.address || "";
    const receivedDate = email.receivedDateTime ? email.receivedDateTime.split("T")[0] : new Date().toISOString().split("T")[0];

    // Fetch full body + attachments from Graph API - fall back to bodyPreview if it fails
    let bodyText = email.bodyPreview || "";
    let attachments = [];
    let skipped = [];
    try {
      const result = await fetchFullEmail(config, email.id);
      bodyText = result.bodyText;
      attachments = result.attachments;
      skipped = result.skipped;
    } catch (e) {
      console.warn("[ClientHub] Could not fetch full email, using preview:", e.message);
    }

    // Build content string - note any skipped oversized attachments
    let content = `From: ${senderName}${senderEmail ? ` <${senderEmail}>` : ""}\nReceived: ${receivedDate}\n\n${bodyText}`;
    if (skipped.length) {
      content += `\n\n[Attachments not saved - exceeded 20 MB limit: ${skipped.join(", ")}]`;
    }
    if (attachments.length) {
      content += `\n\n[${attachments.length} attachment${attachments.length !== 1 ? "s" : ""} saved to Documents: ${attachments.map(a => a.fileName).join(", ")}]`;
    }

    // Save the email entry
    const emailEntry = {
      clientName: emailHubClient,
      type: "email",
      subject: email.subject || "(no subject)",
      content,
      contactNames: senderName,
      loggedBy: "",
      date: receivedDate,
      createdAt: new Date().toISOString(),
    };

    try {
      const res = await fetch(CLIENT_HUB_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailEntry),
      });

      if (res.ok) {
        // Save each attachment as a separate document entry in Client Hub
        for (const att of attachments) {
          const docEntry = {
            clientName: emailHubClient,
            type: "document",
            subject: att.fileName,
            content: `Attachment from email: "${email.subject}" (${senderName}, ${receivedDate})`,
            contactNames: senderName,
            loggedBy: "",
            date: receivedDate,
            createdAt: new Date().toISOString(),
            fileName: att.fileName,
            fileSize: att.fileSize,
            fileMime: att.fileMime,
            fileUrl: att.fileUrl,
            fileId: att.fileId,
          };
          await fetch(CLIENT_HUB_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(docEntry),
          }).catch(e => console.warn("[ClientHub] Doc entry save failed:", e.message));
        }

        setEmails((prev) => prev.filter((e) => e.id !== email.id));
        const attMsg = attachments.length ? ` + ${attachments.length} attachment${attachments.length !== 1 ? "s" : ""}` : "";
        showToast(`"${email.subject}" logged to Client Hub for ${emailHubClient}${attMsg}`);
        setEmailHubPrompt(null);
        setEmailHubClient("");
      } else {
        showToast("Could not save to Client Hub - check proxy is running", "error");
      }
    } catch (e) {
      showToast(`Client Hub save error: ${e.message}`, "error");
    }
    setEmailHubSaving(false);
  };

  const updateTask = (taskId, updates) => {
    setTasks((prev) => prev.map((t) => {
      if (t.id !== taskId) return t;
      const merged = { ...t, ...updates, localUpdatedAt: new Date().toISOString() };
      // If subtasks were updated, recalculate due date from earliest undone subtask with a due date
      if (updates.subtasks && merged.source === "manual") {
        const undoneDates = merged.subtasks
          .filter(s => !s.done && s.due)
          .map(s => s.due)
          .sort();
        merged.due = undoneDates.length > 0 ? undoneDates[0] : null;
      }
      // Send email notification when a new note is added on a task assigned to someone else
      if (updates.localNotes && merged.assignedTo && config.msToken) {
        const myName = localStorage.getItem("taskflow_jira_displayname") || "";
        const prevNoteCount = (t.localNotes || []).length;
        const newNoteCount = (updates.localNotes || []).length;
        if (newNoteCount > prevNoteCount && merged.assignedTo !== myName) {
          const newNote = updates.localNotes[updates.localNotes.length - 1];
          lookupJiraUserEmail(config, merged.assignedTo).then(email => {
            if (email) sendOutlookEmail(config, {
              to: email,
              subject: `New comment on task: ${merged.title?.substring(0, 60)}`,
              body: `
                <p>Hi ${merged.assignedTo.split(" ")[0]},</p>
                <p>${myName || "Someone"} added a comment to a task assigned to you.</p>
                <table style="border-collapse:collapse;font-family:sans-serif;font-size:14px">
                  <tr><td style="padding:6px 12px;font-weight:bold;color:#555">Task:</td><td style="padding:6px 12px">${merged.title}</td></tr>
                  ${merged.due ? `<tr><td style="padding:6px 12px;font-weight:bold;color:#555">Due:</td><td style="padding:6px 12px">${merged.due}</td></tr>` : ""}
                  <tr><td style="padding:6px 12px;font-weight:bold;color:#555;vertical-align:top">Comment:</td>
                  <td style="padding:6px 12px"><div style="background:#f5f5f5;border-left:3px solid #1B3A6B;padding:8px 12px;font-size:13px">${(newNote.text||"").replace(/\n/g,"<br>")}</div>
                  <span style="font-size:11px;color:#888">${newNote.date||""} by ${myName}</span></td></tr>
                </table>
                <p style="color:#888;font-size:12px">This comment was added in the C365Cloud Task Manager.</p>
              `,
            }).catch(() => {});
          }).catch(() => {});
        }
      }
      // Sync manual tasks to server
      if (merged.source === "manual") {
        fetch(`${MANUAL_TASKS_API}/${merged.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(merged),
        }).catch(e => console.warn("[ManualTasks] Update sync failed:", e.message));
      }
      return merged;
    }));
  };

  const promoteSubtask = (parentTaskId, subtaskId, subtaskText) => {
    // Remove subtask from parent
    setTasks((prev) => prev.map((t) => {
      if (t.id !== parentTaskId) return t;
      return { ...t, subtasks: (t.subtasks || []).filter(s => s.id !== subtaskId) };
    }));
    // Create new manual task from the subtask text
    const newT = {
      id: `m${Date.now()}`,
      title: subtaskText,
      source: "manual",
      status: "To Start",
      done: false,
      priority: "medium",
      due: null,
      description: "",
      tags: [],
      subtasks: [],
      localNotes: [],
    };
    setTasks((prev) => [newT, ...prev]);
    showToast(`"${subtaskText}" promoted to task`);
  };

  const toggleSubtaskFromCard = (taskId, subtaskId) => {
    setTasks((prev) => prev.map((t) => {
      if (t.id !== taskId) return t;
      return { ...t, subtasks: (t.subtasks || []).map(s => s.id === subtaskId ? { ...s, done: !s.done } : s) };
    }));
  };

  const deleteTask = (task) => {
    setTasks((prev) => prev.filter(t => t.id !== task.id));
    setSelected(null);
    setPopoutTask(null);
    if (task.rawId) {
      const suppressed = new Set(JSON.parse(localStorage.getItem("taskflow_suppressed_outlook") || "[]"));
      suppressed.add(task.rawId);
      localStorage.setItem("taskflow_suppressed_outlook", JSON.stringify([...suppressed]));
    }
    // Sync deletion to server for manual tasks
    if (task.source === "manual") {
      fetch(`${MANUAL_TASKS_API}/${task.id}`, { method: "DELETE" })
        .catch(e => console.warn("[ManualTasks] Delete sync failed:", e.message));
    }
    showToast("Task deleted");
  };

  const dismissTask = (task) => {
    // Remove from task list and add to suppression list so it never syncs back
    setTasks((prev) => prev.filter(t => t.id !== task.id));
    if (task.rawId) {
      const suppressed = new Set(JSON.parse(localStorage.getItem("taskflow_suppressed_outlook") || "[]"));
      suppressed.add(task.rawId);
      localStorage.setItem("taskflow_suppressed_outlook", JSON.stringify([...suppressed]));
    }
    showToast("Task dismissed and won't sync back");
  };

  const handleStatusChange = (task, newStatus) => {
    const isDone = newStatus === "Done";
    setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: newStatus, done: isDone } : t));
    if (task.source === "outlook" && msConnected && isDone) {
      completeOutlookTask(config, task.listId, task.rawId).catch(() => {});
    }
    logActivity("Status changed", task.title, `→ ${newStatus}`);
    showToast(`Status updated to ${newStatus}`);
  };

  const handleJiraTransition = async (task, transitionId, transitionName) => {
    try {
      await transitionJiraIssue(config, task.jiraKey, transitionId, true);
      const isDone = ["done", "closed", "resolved"].includes(transitionName.toLowerCase());
      setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: transitionName, done: isDone } : t));
      if (isDone) { setSelected(null); setPopoutTask(null); }
      showToast(`[${task.jiraKey}] moved to ${transitionName}`);
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const completeFlaggedTask = async (task) => {
    // Mark flagged email task as done locally and update the email flag in Outlook
    setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, done: true, status: "Done" } : t));
    setSelected(null);
    setPopoutTask(null);
    if (msConnected && task.rawEmailId) {
      try {
        // Unflag the email in Outlook
        await fetch(`https://graph.microsoft.com/v1.0/me/messages/${task.rawEmailId}`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${config.msToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ flag: { flagStatus: "complete" } }),
        });
        showToast("Flagged email marked complete in Outlook");
      } catch (e) { showToast(`Marked done locally (could not update Outlook: ${e.message})`, "error"); }
    } else {
      showToast("Flagged email marked as done");
    }
  };

  const createOutlookFromJira = async (task) => {
    if (!msConnected) { showToast("Connect Outlook first", "error"); return; }
    try { await createOutlookTask(config, task.title, task.description); showToast("Outlook task created"); }
    catch (e) { showToast(e.message, "error"); }
  };

  const saveNote = async (task, noteOrSegments) => {
    const isSegments = Array.isArray(noteOrSegments);
    const plainText = isSegments ? noteOrSegments.map(s => s.type === "text" ? s.text : "@" + s.displayName).join("") : noteOrSegments;
    if (!plainText.trim()) return;
    logActivity("Note added", task.title, plainText.substring(0, 60) + (plainText.length > 60 ? "…" : ""));
    if (task.source === "jira" && jiraConnected) {
      try {
        // Pass segments array for ADF mentions, or plain string for simple comments
        await addJiraComment(config, task.jiraKey, isSegments ? noteOrSegments : plainText);
        showToast("Comment posted to Jira");
      } catch (e) { showToast(e.message, "error"); }
    } else {
      showToast("Note saved");
    }
  };

  const saveDue = async (task, dueDate) => {
    setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, due: dueDate, localDueOverride: true } : t));
    if (task.source === "outlook" && task.rawId && task.listId && msConnected) {
      try {
        await updateOutlookTaskDue(config, task.listId, task.rawId, dueDate);
        showToast("Due date saved to Outlook");
      } catch (e) { showToast(e.message, "error"); }
    } else if (task.source === "jira" && task.jiraKey && jiraConnected) {
      try {
        const { jiraUrl, jiraEmail, jiraToken } = config;
        const auth = btoa(`${jiraEmail}:${jiraToken}`);
        const baseUrl = jiraUrl.replace(/\/+$/, "").replace(/^http:\/\//, "https://");
        const body = dueDate ? { fields: { duedate: dueDate } } : { fields: { duedate: null } };
        const res = await fetch(proxyUrl(`${baseUrl}/rest/api/3/issue/${task.jiraKey}`), {
          method: "PUT",
          headers: { Authorization: `Basic ${auth}`, "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
        });
        if (res.ok || res.status === 204) showToast(`Due date saved to Jira [${task.jiraKey}]`);
        else showToast("Due date saved locally (Jira update failed)", "error");
      } catch (e) { showToast("Due date saved locally", "error"); }
    } else {
      showToast("Due date updated");
    }
  };

  

  // ── Saved searches ───────────────────────────────────────────────────────
  const [savedSearches, setSavedSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_saved_searches") || "[]"); } catch { return []; }
  });
  // ── Task templates ─────────────────────────────────────────────────────────
  const [taskTemplates, setTaskTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taskflow_task_templates") || "[]"); } catch { return []; }
  });
  const [showTemplates, setShowTemplates] = useState(false);

  const saveAsTemplate = (task) => {
    const name = window.prompt("Template name:", task.title);
    if (!name) return;
    const tmpl = {
      id: Date.now(),
      name: name.trim(),
      title: task.title,
      priority: task.priority,
      tags: task.tags || [],
      subtasks: (task.subtasks || []).map(s => ({ ...s, id: Date.now() + Math.random(), done: false })),
      recur: task.recur || "",
      description: task.description || "",
    };
    const updated = [...taskTemplates, tmpl];
    setTaskTemplates(updated);
    try { localStorage.setItem("taskflow_task_templates", JSON.stringify(updated)); } catch {}
    showToast(`Template "${name}" saved`);
  };

  const createFromTemplate = (tmpl) => {
    const t = {
      id: "m" + Date.now(),
      title: tmpl.title,
      source: "manual",
      status: "To Start",
      done: false,
      priority: tmpl.priority || "medium",
      due: null,
      description: tmpl.description || "",
      tags: [...(tmpl.tags || [])],
      subtasks: (tmpl.subtasks || []).map(s => ({ ...s, id: Date.now() + Math.random(), done: false })),
      recur: tmpl.recur || "",
    };
    setTasks(prev => [t, ...prev]);
    setShowTemplates(false);
    logActivity("Created", t.title, "From template");
    showToast(`Task created from template "${tmpl.name}"`);
  };

  const deleteTemplate = (id) => {
    const updated = taskTemplates.filter(t => t.id !== id);
    setTaskTemplates(updated);
    try { localStorage.setItem("taskflow_task_templates", JSON.stringify(updated)); } catch {}
  };

  const saveSearch = (name) => {
    if (!name.trim() || !search.trim()) return;
    const entry = { id: Date.now(), name: name.trim(), query: search };
    const updated = [...savedSearches, entry];
    setSavedSearches(updated);
    try { localStorage.setItem("taskflow_saved_searches", JSON.stringify(updated)); } catch {}
    showToast(`Search "${name}" saved`);
  };
  const deleteSavedSearch = (id) => {
    const updated = savedSearches.filter(s => s.id !== id);
    setSavedSearches(updated);
    try { localStorage.setItem("taskflow_saved_searches", JSON.stringify(updated)); } catch {}
  };

  // Manual task reorder - accepts (fromId, toId) for drag-drop
  const moveTask = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setTasks(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(t => t.id === fromId);
      const toIdx   = arr.findIndex(t => t.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  };

  const allTags = [...new Set(tasks.flatMap(t => t.tags || []))].sort();

  const filtered = tasks.filter((t) => {
    // Completed tasks only show in the "done" / "completed" view
    if (t.done && filter !== "done") return false;
    // Suppress SUPPORT cards at "With Development Team" from general views
    // Only show them in the dedicated with-dev-team sub-views (case-insensitive)
    if (t.source === "jira" && t.jiraStatus &&
        t.jiraStatus.toLowerCase() === "with development team" &&
        filter !== "jira" && filter !== "done") return false;
    if (filter === "open") return !t.done && (!t.jiraStatus || t.jiraStatus.toLowerCase() !== "with development team");
    if (filter === "done") return t.done;
    if (filter === "outlook") {
      if (!( t.source === "outlook")) return false;
      if (subFilter === "flagged") return t.isFlaggedEmail;
      if (subFilter === "tasks") return !t.isFlaggedEmail;
      return true;
    }
    if (filter === "jira") {
      if (t.source !== "jira") return false;
      // Suppress SUPPORT cards at "With Development Team" from all sub-views
      // except the with-dev-team sub-views - the linked dev card is the one to track
      const isWithDev = t.jiraStatus && t.jiraStatus.toLowerCase() === "with development team";
      const isWithDevSubFilter = subFilter && (subFilter === "with-dev-team" || subFilter.startsWith("dev-"));
      if (isWithDev && !isWithDevSubFilter && subFilter !== null) return false;
      // Also suppress from the general "all jira" view (no sub-filter) to avoid double-counting
      if (isWithDev && !subFilter) return false;
      if (subFilter === "jira-overdue") return !t.done && !!t.due && t.due < new Date().toISOString().split("T")[0];
      if (subFilter === "no-reply") return isNoReply(t);
      if (subFilter === "assigned")    return t.assignedToMe;
      if (subFilter === "mentioned")   return t.mentioned;
      if (subFilter === "waiting-sdm") return t.isSDM && t.jiraStatus === "Waiting on SDM";
      if (subFilter === "waiting-pc")  return t.isPC  && t.jiraStatus === "Waiting on PC";
      if (subFilter === "created-by-me") return !!t.appCreated;
      // SUPPORT direct statuses
      if (subFilter === "sup-triage")           return t.supportStatus === "Triage" || t.jiraStatus === "Triage";
      if (subFilter === "sup-triage-wait-info") return t.supportStatus === "Triage Waiting Information" || t.jiraStatus === "Triage Waiting Information";
      if (subFilter === "sup-triage-response")  return t.supportStatus === "Triage Response" || t.jiraStatus === "Triage Response";
      if (subFilter === "sup-tier3-triage")     return t.supportStatus === "Tier 3 Triage" || t.jiraStatus === "Tier 3 Triage";
      if (subFilter === "sup-new")              return t.supportStatus === "New" || t.jiraStatus === "New";
      if (subFilter === "sup-in-progress")      return t.supportStatus === "In Progress" || t.jiraStatus === "In Progress";
      if (subFilter === "sup-qa")               return t.supportStatus === "QA" || t.jiraStatus === "QA";
      if (subFilter === "sup-qa-completed")     return t.supportStatus === "QA Completed" || t.jiraStatus === "QA Completed";
      if (subFilter === "sup-on-hold")          return t.supportStatus === "On Hold" || t.jiraStatus === "On Hold" || t.blockStatus === "On Hold";
      if (subFilter === "sup-response")         return t.supportStatus === "Response" || t.jiraStatus === "Response";
      if (subFilter === "sup-reopened")         return t.supportStatus === "Reopened" || t.jiraStatus === "Reopened";
      // With Development Team - SUPPORT card + any dev card status sub-filter
      if (subFilter === "with-dev-team")           return t.jiraStatus === "With Development Team";
      if (subFilter === "dev-for-review")          return t.jiraStatus === "With Development Team" && t.devCardStatus === "For Review";
      if (subFilter === "dev-for-review-ip")       return t.jiraStatus === "With Development Team" && t.devCardStatus === "For Review - In Progress";
      if (subFilter === "dev-more-info")           return t.jiraStatus === "With Development Team" && t.devCardStatus === "More Info Required";
      if (subFilter === "dev-not-approved")        return t.jiraStatus === "With Development Team" && t.devCardStatus === "Not Approved";
      if (subFilter === "dev-not-required")        return t.jiraStatus === "With Development Team" && t.devCardStatus === "Not Required";
      if (subFilter === "dev-on-hold")             return t.jiraStatus === "With Development Team" && t.devCardStatus === "On Hold";
      if (subFilter === "dev-blocked")             return t.jiraStatus === "With Development Team" && t.devCardStatus === "Blocked";
      if (subFilter === "dev-backlog")             return t.jiraStatus === "With Development Team" && t.devCardStatus === "Backlog";
      if (subFilter === "dev-sprint-assigned")     return t.jiraStatus === "With Development Team" && t.devCardStatus === "Sprint Assigned";
      if (subFilter === "dev-to-do")               return t.jiraStatus === "With Development Team" && t.devCardStatus === "To Do";
      if (subFilter === "dev-in-progress")         return t.jiraStatus === "With Development Team" && t.devCardStatus === "In Progress";
      if (subFilter === "dev-qa")                  return t.jiraStatus === "With Development Team" && t.devCardStatus === "QA";
      if (subFilter === "dev-rework")              return t.jiraStatus === "With Development Team" && t.devCardStatus === "Rework";
      if (subFilter === "dev-response")            return t.jiraStatus === "With Development Team" && t.devCardStatus === "Response";
      if (subFilter === "dev-waiting-internal")    return t.jiraStatus === "With Development Team" && t.devCardStatus === "Waiting Internal";
      if (subFilter === "dev-waiting-external")    return t.jiraStatus === "With Development Team" && t.devCardStatus === "Waiting External";
      if (subFilter === "dev-to-publish")          return t.jiraStatus === "With Development Team" && t.devCardStatus === "To Publish";
      if (subFilter === "dev-waiting-sign-off")    return t.jiraStatus === "With Development Team" && t.devCardStatus === "Waiting for Sign Off";
      // Legacy blockStatus filters kept for backward compat
      if (subFilter === "blocked")              return t.blockStatus === "Blocked"              || t.jiraStatus === "Blocked";
      if (subFilter === "on-hold")              return t.blockStatus === "On Hold"              || t.jiraStatus === "On Hold";
      if (subFilter === "ed-on-hold")           return t.blockStatus === "Ed on Hold"           || t.jiraStatus === "Ed on Hold";
      if (subFilter === "awaiting-internal")    return t.blockStatus === "Awaiting Internal"    || t.jiraStatus === "Awaiting Internal";
      if (subFilter === "in-query")             return t.blockStatus === "In Query"             || t.jiraStatus === "In Query";
      if (subFilter === "waiting-internal")     return t.blockStatus === "Waiting Internal"     || t.jiraStatus === "Waiting Internal";
      if (subFilter === "waiting-external")     return t.blockStatus === "Waiting External"     || t.jiraStatus === "Waiting External";
      if (subFilter === "in-query-external")    return t.blockStatus === "In Query External"    || t.jiraStatus === "In Query External";
      if (subFilter === "waiting-sign-off")     return t.blockStatus === "Waiting for Sign Off"     || t.jiraStatus === "Waiting for Sign Off";
      if (subFilter === "ed-awaiting-sign-off") return t.blockStatus === "ED Awaiting Sign Off"     || t.jiraStatus === "ED Awaiting Sign Off";
      return true;
    }
    if (filter === "assigned-to-me") {
      const myName = localStorage.getItem("taskflow_jira_displayname") || "";
      if (t.done) return false;
      // Task assigned to me directly
      if (t.assignedTo && t.assignedTo === myName) return true;
      // Or a subtask is assigned to me (and not yet done)
      if ((t.subtasks || []).some(s => s.assignedTo === myName && !s.done)) return true;
      return false;
    }
    if (filter === "assigned-by-me") {
      const myName = localStorage.getItem("taskflow_jira_displayname") || "";
      if (t.done) return false;
      const owned = t.createdBy === myName || (!t.createdBy && t.source === "manual");
      if (!owned) return false;
      // Task assigned to someone other than me
      if (t.assignedTo && t.assignedTo !== myName) return true;
      // Or a subtask assigned to someone else - but ONLY if the main task isn't already assigned out
      if (!t.assignedTo && (t.subtasks || []).some(s => s.assignedTo && s.assignedTo !== myName && !s.done)) return true;
      return false;
    }
    if (filter === "manual") {
      if (t.source !== "manual") return false;
      if (subFilter && allTags.includes(subFilter)) return (t.tags || []).includes(subFilter);
      return true;
    }
    if (filter === "clients") {
      return t.source === "jira" && !t.done;
    }
    if (filter === "high") return t.priority === "high" && !t.done;
    if (filter === "saved-searches" && subFilter && subFilter.startsWith("saved-")) {
      const searchId = parseInt(subFilter.replace("saved-", ""));
      const saved = savedSearches.find(s => s.id === searchId);
      if (!saved) return false;
      const q = saved.query.toLowerCase();
      return !t.done && (
        t.title.toLowerCase().includes(q) ||
        (t.localNotes || []).some(n => (n.text || "").toLowerCase().includes(q)) ||
        (t.description || "").toLowerCase().includes(q)
      );
    }
    if (filter === "waiting") return t.waitingOn && !t.done;
    if (filter === "mentioned") return t.mentioned && !t.done;
    return true;
  }).filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (t.title.toLowerCase().includes(q)) return true;
    // Search note content
    if ((t.localNotes || []).some(n => (n.text || "").toLowerCase().includes(q))) return true;
    // Search description
    if ((t.description || "").toLowerCase().includes(q)) return true;
    return false;
  })
  .sort((a, b) => {
    // Manual tasks in list view preserve their manual order (drag-to-reorder)
    if (filter === "manual" && viewMode === "list") return 0;
    // All other views: overdue first, then by due date ascending, undated at end
    if (a.done && !b.done) return 1;
    if (!a.done && b.done) return -1;
    if (a.due && b.due) return new Date(a.due) - new Date(b.due);
    if (a.due && !b.due) return -1;
    if (!a.due && b.due) return 1;
    return 0;
  });

  const counts = {
    all: tasks.filter((t) => !t.done).length,
    open: tasks.filter((t) => !t.done).length,
    high: tasks.filter((t) => t.priority === "high" && !t.done).length,
    outlook: tasks.filter((t) => t.source === "outlook" && !t.done).length,
    outlookFlagged: tasks.filter((t) => t.source === "outlook" && t.isFlaggedEmail && !t.done).length,
    outlookTasks: tasks.filter((t) => t.source === "outlook" && !t.isFlaggedEmail && !t.done).length,
    jira: (() => {
      // Count unique visible tasks in the main Jira view (no sub-filter active)
      const seen = new Set();
      return tasks.filter(t => {
        if (t.source !== "jira") return false;
        if (t.done) return false;
        if (t.jiraStatus && t.jiraStatus.toLowerCase() === "with development team") return false;
        if (t.id?.startsWith("impl-jira-") || t.id?.startsWith("reporter-")) return false;
        if (seen.has(t.jiraKey || t.id)) return false;
        seen.add(t.jiraKey || t.id);
        return true;
      }).length;
    })(),
    jiraAssigned:  tasks.filter((t) => t.source === "jira" && t.assignedToMe && !t.done).length,
    jiraMentioned: tasks.filter((t) => t.source === "jira" && t.mentioned    && !t.done).length,
    jiraSDM:          tasks.filter((t) => t.source === "jira" && t.isSDM && t.jiraStatus === "Waiting on SDM" && !t.done).length,
    jiraPC:           tasks.filter((t) => t.source === "jira" && t.isPC  && t.jiraStatus === "Waiting on PC"  && !t.done).length,
    jiraCreatedByMe:     tasks.filter(t => t.source === "jira" && t.appCreated && !t.done && (!t.jiraStatus || t.jiraStatus.toLowerCase() !== "with development team")).length,
    jiraOverdue:         tasks.filter(t => t.source === "jira" && !t.done && t.due && t.due < new Date().toISOString().split("T")[0]).length,
    jiraNoReply:         tasks.filter(t => isNoReply(t)).length,
    // SUPPORT direct status counts
    jiraSupTriage:       tasks.filter(t => t.source === "jira" && (t.supportStatus === "Triage"                    || t.jiraStatus === "Triage")                    && !t.done).length,
    jiraSupTriageWait:   tasks.filter(t => t.source === "jira" && (t.supportStatus === "Triage Waiting Information" || t.jiraStatus === "Triage Waiting Information") && !t.done).length,
    jiraSupTriageResp:   tasks.filter(t => t.source === "jira" && (t.supportStatus === "Triage Response"            || t.jiraStatus === "Triage Response")            && !t.done).length,
    jiraSupTier3:        tasks.filter(t => t.source === "jira" && (t.supportStatus === "Tier 3 Triage"              || t.jiraStatus === "Tier 3 Triage")              && !t.done).length,
    jiraSupNew:          tasks.filter(t => t.source === "jira" && (t.supportStatus === "New"                        || t.jiraStatus === "New")                        && !t.done).length,
    jiraSupInProgress:   tasks.filter(t => t.source === "jira" && (t.supportStatus === "In Progress"                || t.jiraStatus === "In Progress")                && !t.done).length,
    jiraSupQA:           tasks.filter(t => t.source === "jira" && (t.supportStatus === "QA"                         || t.jiraStatus === "QA")                         && !t.done).length,
    jiraSupQACompleted:  tasks.filter(t => t.source === "jira" && (t.supportStatus === "QA Completed"               || t.jiraStatus === "QA Completed")               && !t.done).length,
    jiraSupOnHold:       tasks.filter(t => t.source === "jira" && (t.supportStatus === "On Hold"                    || t.jiraStatus === "On Hold")                    && !t.done).length,
    jiraSupResponse:     tasks.filter(t => t.source === "jira" && (t.supportStatus === "Response"                   || t.jiraStatus === "Response")                   && !t.done).length,
    jiraSupReopened:     tasks.filter(t => t.source === "jira" && (t.supportStatus === "Reopened"                   || t.jiraStatus === "Reopened")                   && !t.done).length,
    // With Development Team + dev card status counts
    jiraWithDev:         tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && !t.done).length,
    jiraDevForReview:    tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "For Review"              && !t.done).length,
    jiraDevForReviewIP:  tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "For Review - In Progress" && !t.done).length,
    jiraDevMoreInfo:     tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "More Info Required"       && !t.done).length,
    jiraDevNotApproved:  tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Not Approved"             && !t.done).length,
    jiraDevNotRequired:  tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Not Required"             && !t.done).length,
    jiraDevOnHold:       tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "On Hold"                  && !t.done).length,
    jiraDevBlocked:      tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Blocked"                  && !t.done).length,
    jiraDevBacklog:      tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Backlog"                  && !t.done).length,
    jiraDevSprint:       tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Sprint Assigned"          && !t.done).length,
    jiraDevToDo:         tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "To Do"                    && !t.done).length,
    jiraDevInProgress:   tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "In Progress"              && !t.done).length,
    jiraDevQA:           tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "QA"                       && !t.done).length,
    jiraDevRework:       tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Rework"                   && !t.done).length,
    jiraDevResponse:     tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Response"                 && !t.done).length,
    jiraDevWaitInt:      tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Waiting Internal"         && !t.done).length,
    jiraDevWaitExt:      tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Waiting External"         && !t.done).length,
    jiraDevToPublish:    tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "To Publish"               && !t.done).length,
    jiraDevWaitSignOff:  tasks.filter(t => t.source === "jira" && t.jiraStatus === "With Development Team" && t.devCardStatus === "Waiting for Sign Off"     && !t.done).length,
    jiraBlocked:        tasks.filter((t) => t.source === "jira" && (t.blockStatus === "Blocked"              || t.jiraStatus === "Blocked")              && !t.done).length,
    jiraOnHold:         tasks.filter((t) => t.source === "jira" && (t.blockStatus === "On Hold"              || t.jiraStatus === "On Hold")              && !t.done).length,
    jiraEdOnHold:       tasks.filter((t) => t.source === "jira" && (t.blockStatus === "Ed on Hold"           || t.jiraStatus === "Ed on Hold")           && !t.done).length,
    jiraAwaiting:       tasks.filter((t) => t.source === "jira" && (t.blockStatus === "Awaiting Internal"    || t.jiraStatus === "Awaiting Internal")    && !t.done).length,
    jiraInQuery:        tasks.filter((t) => t.source === "jira" && (t.blockStatus === "In Query"             || t.jiraStatus === "In Query")             && !t.done).length,
    jiraWaitInternal:   tasks.filter((t) => t.source === "jira" && (t.blockStatus === "Waiting Internal"     || t.jiraStatus === "Waiting Internal")     && !t.done).length,
    jiraWaitExternal:   tasks.filter((t) => t.source === "jira" && (t.blockStatus === "Waiting External"     || t.jiraStatus === "Waiting External")     && !t.done).length,
    jiraInQueryExt:     tasks.filter((t) => t.source === "jira" && (t.blockStatus === "In Query External"    || t.jiraStatus === "In Query External")    && !t.done).length,
    jiraWaitSignOff:    tasks.filter((t) => t.source === "jira" && (t.blockStatus === "Waiting for Sign Off" || t.jiraStatus === "Waiting for Sign Off") && !t.done).length,
    jiraEdSignOff:      tasks.filter((t) => t.source === "jira" && (t.blockStatus === "ED Awaiting Sign Off" || t.jiraStatus === "ED Awaiting Sign Off") && !t.done).length,
    jiraClients: tasks.filter((t) => t.source === "jira" && !t.done).length,
    waiting: tasks.filter((t) => t.waitingOn && !t.done).length,
    mentioned: tasks.filter((t) => t.mentioned && !t.done).length,
    manual: tasks.filter((t) => t.source === "manual" && !t.done).length,
    done: tasks.filter((t) => t.done).length,
  };

  const selectedTask = tasks.find((t) => t.id === selected);
  // On mobile, opening a task also opens the detail overlay
  const taskPanelRef = React.useRef(null);
  const handleSelectTask = (id) => {
    const t = tasks.find(x => x.id === id);
    setSelected(id);
    if (!t) return;
    // On mobile use the slide-in panel; on desktop open the full popout
    if (window.innerWidth <= 768) {
      setMobileDetailOpen(true);
      setTimeout(() => {
        if (taskPanelRef.current) taskPanelRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    } else {
      setPopoutTask(t);
    }
  };

  // Sub-nav items per filter
  const outlookSubItems = [
    { id: "flagged", label: "🚩 Flagged Email", count: counts.outlookFlagged },
    { id: "tasks", label: "☑ Tasks", count: counts.outlookTasks },
  ];
  const jiraSubItems = [
    // ── Role-based (5) ──────────────────────────────────────────────────────
    { id: "created-by-me",        label: "✚ Created by me",                   count: counts.jiraCreatedByMe },
    { id: "jira-overdue",         label: "⚠ Overdue",                          count: counts.jiraOverdue },
    { id: "no-reply",             label: "💬 No Reply (2+ days)",               count: counts.jiraNoReply },
    { id: "assigned",             label: "👤 Assigned to me",                 count: counts.jiraAssigned },
    { id: "mentioned",            label: "💬 Mentioned",                       count: counts.jiraMentioned },
    { id: "waiting-sdm",          label: "⏳ Waiting on SDM",                  count: counts.jiraSDM },
    { id: "waiting-pc",           label: "⏳ Waiting on PC",                   count: counts.jiraPC },
    // ── SUPPORT direct statuses (11) ────────────────────────────────────────
    { id: "sup-triage",           label: "🔎 Triage",                          count: counts.jiraSupTriage },
    { id: "sup-triage-wait-info", label: "🔎 Triage Waiting Information",      count: counts.jiraSupTriageWait },
    { id: "sup-triage-response",  label: "🔎 Triage Response",                 count: counts.jiraSupTriageResp },
    { id: "sup-tier3-triage",     label: "🔎 Tier 3 Triage",                   count: counts.jiraSupTier3 },
    { id: "sup-new",              label: "🆕 New",                             count: counts.jiraSupNew },
    { id: "sup-in-progress",      label: "▶ In Progress",                      count: counts.jiraSupInProgress },
    { id: "sup-qa",               label: "🧪 QA",                              count: counts.jiraSupQA },
    { id: "sup-qa-completed",     label: "✅ QA Completed",                    count: counts.jiraSupQACompleted },
    { id: "sup-on-hold",          label: "⏸ On Hold",                          count: counts.jiraSupOnHold },
    { id: "sup-response",         label: "💬 Response",                         count: counts.jiraSupResponse },
    { id: "sup-reopened",         label: "🔄 Reopened",                         count: counts.jiraSupReopened },
    // ── Directly assigned dev space statuses ───────────────────────────────
    { id: "blocked",              label: "🔴 Blocked",                          count: counts.jiraBlocked },
    // ── With Development Team - 18 dev card statuses (18) ───────────────────
    { id: "dev-for-review",       label: "🏗 For Review",                      count: counts.jiraDevForReview },
    { id: "dev-for-review-ip",    label: "🏗 For Review - In Progress",        count: counts.jiraDevForReviewIP },
    { id: "dev-more-info",        label: "🏗 More Info Required",               count: counts.jiraDevMoreInfo },
    { id: "dev-not-approved",     label: "🏗 Not Approved",                    count: counts.jiraDevNotApproved },
    { id: "dev-not-required",     label: "🏗 Not Required",                    count: counts.jiraDevNotRequired },
    { id: "dev-on-hold",          label: "🏗 On Hold",                         count: counts.jiraDevOnHold },
    { id: "dev-blocked",          label: "🏗 Blocked",                         count: counts.jiraDevBlocked },
    { id: "dev-backlog",          label: "🏗 Backlog",                         count: counts.jiraDevBacklog },
    { id: "dev-sprint-assigned",  label: "🏗 Sprint Assigned",                 count: counts.jiraDevSprint },
    { id: "dev-to-do",            label: "🏗 To Do",                           count: counts.jiraDevToDo },
    { id: "dev-in-progress",      label: "🏗 In Progress",                     count: counts.jiraDevInProgress },
    { id: "dev-qa",               label: "🏗 QA",                              count: counts.jiraDevQA },
    { id: "dev-rework",           label: "🏗 Rework",                          count: counts.jiraDevRework },
    { id: "dev-response",         label: "🏗 Response",                        count: counts.jiraDevResponse },
    { id: "dev-waiting-internal", label: "🏗 Waiting Internal",                count: counts.jiraDevWaitInt },
    { id: "dev-waiting-external", label: "🏗 Waiting External",                count: counts.jiraDevWaitExt },
    { id: "dev-to-publish",       label: "🏗 To Publish",                      count: counts.jiraDevToPublish },
    { id: "dev-waiting-sign-off", label: "🏗 Waiting for Sign Off",            count: counts.jiraDevWaitSignOff },
  ];
  const manualTagSubItems = allTags.map(tag => ({
    id: tag, label: `🏷 ${tag}`, count: tasks.filter(t => t.source === "manual" && !t.done && (t.tags||[]).includes(tag)).length
  }));

  const navItems = [
    { id: "dashboard", icon: "📊", label: "My Dashboard", section: "Views" },
    { id: "manual", icon: "✏", label: "Tasks", subItems: manualTagSubItems },
    { id: "clients", icon: "🏢", label: managerMode ? "All Clients (Manager)" : "By Client", count: counts.jiraClients },
    { id: "done", icon: "✓", label: "Completed" },
    { id: "implementations", icon: "🏗", label: "Implementations & Projects", section: "Reports & Tools", count: projects.length || undefined },
    { id: "sla-reports", icon: "📈", label: "SLA Reports" },
    { id: "jira-queue", icon: "📋", label: "Jira Queue" },
    { id: "roadmap-cafm", icon: "🗺", label: roadmapLabel, section: "Roadmaps" },
    ...roadmaps.map(r => ({ id: r.id, icon: "🗺", label: r.label })),
    { id: "__add_roadmap__", icon: "➕", label: "New roadmap" },
    { id: "client-hub", icon: "💬", label: "Client Hub", section: "Client Intelligence", badge: "New" },
    { id: "project-hub", icon: "🗂", label: "Project Hub" },
  ];

  return (
    <>
      <style>{STYLES}</style>
      <div className={`app${lightMode ? " light-mode" : ""}`}>
        <header className="header">
          <button className="mobile-nav-btn" onClick={() => setMobileSidebarOpen(v => !v)} title="Toggle navigation">☰</button>
          <div className="header-title">
            <span className="brand-c365">C365Cloud</span>&nbsp;<span>Task Manager</span>
            <span style={{ fontSize: 9, fontWeight: 400, color: "var(--text-dim)", marginLeft: 6, letterSpacing: 0.5 }}>v{APP_VERSION} ({APP_BUILD})</span>
          </div>
          {newVersionAvailable && (
            <div style={{ background: "var(--green)", color: "#fff", fontSize: 11, fontWeight: 600,
              padding: "4px 12px", borderRadius: 4, display: "flex", alignItems: "center", gap: 8 }}>
              🔄 New version available -
              <button onClick={() => window.location.reload(true)}
                style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
                  color: "#fff", borderRadius: 3, padding: "2px 8px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                Refresh now
              </button>
            </div>
          )}
          <div className="header-spacer" />
          <div className="header-badges">
            <div className={`badge outlook ${msConnected ? "connected" : "disconnected"}`} onClick={() => setShowConfig(true)}>
              <div className={`dot ${msConnected ? "pulse" : ""}`} />
              Outlook {msConnected ? "Connected" : "-"}
            </div>
            <div className={`badge jira ${jiraConnected ? "connected" : "disconnected"}`} onClick={() => setShowConfig(true)}>
              <div className={`dot ${jiraConnected ? "pulse" : ""}`} />
              Jira {jiraConnected ? "Connected" : "-"}
            </div>
            <button className="btn" onClick={sync} disabled={syncing} title="Sync (S)">
              <span className={syncing ? "syncing" : ""}>↻</span> {syncing ? "Syncing…" : "Sync"}
            </button>
            <button className="btn" title="Keyboard shortcuts: N=New task  J=New Jira  S=Sync  /=Search  Esc=Close"
              style={{ fontSize: 11, padding: "6px 10px", color: "var(--text-dim)" }}>⌨</button>
            <button className="btn" onClick={() => setShowExtractModal(true)}
              style={{ fontSize: 11, padding: "6px 10px", color: "#c47ef0", borderColor: "#c47ef040" }}
              title="Extract actions from a document">✦ Extract</button>
            {userIsManager && (
              <button className={`btn ${managerMode ? "primary" : ""}`}
                onClick={() => setManagerMode(v => !v)}
                style={{ fontSize: 11, padding: "6px 10px" }}
                title={managerMode ? "Back to personal view" : "Switch to manager view"}>
                👔 {managerMode ? "Manager ✓" : "Manager"}
              </button>
            )}
            <button className="btn" onClick={() => setShowConfig(true)}>⚙ Config</button>
          </div>
        </header>

        <div className={`main${filter === "implementations" ? " full-width" : ""}${selectedTask ? " detail-open" : ""}`}>
          {mobileSidebarOpen && <div className="mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />}
          <nav className={`sidebar${mobileSidebarOpen ? " mobile-open" : ""}`}>
            <button className="mobile-close-sidebar" onClick={() => setMobileSidebarOpen(false)}>{"✕"}</button>

            {/* ── Inbox at top ── */}
            {msConnected && (
              <>
                <div className="sidebar-section">
                  📬 Inbox
                  {emails.length > 0 && <span style={{ marginLeft: 6, background: "var(--red)", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{emails.length}</span>}
                </div>
                {emails.length === 0 ? (
                  <div style={{ fontSize: 11, color: "var(--green)", padding: "4px 12px 10px", fontStyle: "italic" }}>✓ No unread emails</div>
                ) : (
                  <>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", padding: "0 10px 6px", lineHeight: 1.4 }}>Click an email to convert to a task or log to Client Hub</div>
                    {emails.slice(0, 8).map((e) => (
                      <div key={e.id} className="nav-item" onClick={() => { setEmailHubPrompt(e); setEmailHubClient(""); }} title={`From: ${e.from?.emailAddress?.address}\nClick to action`} style={{ borderLeft: "3px solid var(--amber)", paddingLeft: 8 }}>
                        <span className="nav-icon" style={{ color: "var(--amber)" }}>✉</span>
                        <div style={{ overflow: "hidden", minWidth: 0 }}>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, color: "var(--text)" }}>{e.subject}</div>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10, color: "var(--text-muted)" }}>{e.from?.emailAddress?.name || e.from?.emailAddress?.address}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}

            {(() => {
              let currentSection = "Views";
              return navItems.map((n) => {
                if (n.section) currentSection = n.section;
                const sectionCollapsed = collapsedSections[n.section ? n.section : currentSection] && !n.section;
                const thisSection = currentSection;
                const isCollapsed = navManualMode && navCollapsed[n.id];
                const showSubs = n.subItems?.length > 0 && (filter === n.id || (navManualMode && !isCollapsed));
                return (
                <div key={n.id}>
                  {n.section && (
                    <div className="sidebar-section" style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 5, userSelect: "none" }}
                      onClick={() => {
                        const updated = { ...collapsedSections, [n.section]: !collapsedSections[n.section] };
                        setCollapsedSections(updated);
                        localStorage.setItem("taskflow_sections_collapsed", JSON.stringify(updated));
                      }}>
                      <span style={{ fontSize: 8 }}>{collapsedSections[n.section] ? "▶" : "▼"}</span>
                      {n.section}
                    </div>
                  )}
                  {!(collapsedSections[thisSection]) && (<>
                  <div className={`nav-item ${filter === n.id ? "active" : ""}`} onClick={() => { if (n.id === "__add_roadmap__") { addRoadmap(); setMobileSidebarOpen(false); return; } setFilter(n.id); setSubFilter(null); if (n.id === "manual" && viewMode === "board") setViewMode("list"); setMobileSidebarOpen(false); }}>
                    <span className="nav-icon">{n.icon}</span>
                    <span style={n.iconColor ? { color: n.iconColor, fontWeight: 600 } : undefined}>{n.label}</span>
                    {n.badge && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: "rgba(71,172,76,0.15)", border: "1px solid var(--green)", color: "var(--green)" }}>{n.badge}</span>}
                    {(n.count !== undefined || counts[n.id] !== undefined) && <span className="count">{n.count ?? counts[n.id]}</span>}
                    {navManualMode && n.subItems?.length > 0 && (
                      <button className="nav-collapse-btn" title={isCollapsed ? "Expand" : "Collapse"}
                        onClick={e => {
                          e.stopPropagation();
                          const updated = { ...navCollapsed, [n.id]: !isCollapsed };
                          setNavCollapsed(updated);
                          localStorage.setItem("taskflow_nav_collapsed", JSON.stringify(updated));
                        }}>{isCollapsed ? "▶" : "▼"}</button>
                    )}
                  </div>
                  {showSubs && (
                    <div className="nav-sub">
                      {n.subItems.filter(s => s.count >= 1).map(s => (
                        <div key={s.id} className={`nav-sub-item ${subFilter === s.id ? "active" : ""}`}
                          onClick={() => {
                            setFilter(n.id);
                            if (n.id === "manual" && viewMode === "board") setViewMode("list");
                            setSubFilter(subFilter === s.id ? null : s.id);
                          }}>
                          {s.label}
                          <span className="count">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  </>)}
                </div>
                );
              });
            })()}
          </nav>

          <div ref={taskPanelRef} className="task-panel" style={(filter === "implementations" || filter === "client-hub" || filter === "project-hub" || filter === "sla-reports" || filter === "jira-queue" || filter?.startsWith("roadmap-") || (filter === "manual" && (viewMode === "gantt" || viewMode === "grid" || viewMode === "calendar"))) ? { padding: 0, gap: 0 } : {}}>
            {filter !== "implementations" && filter !== "client-hub" && !filter?.startsWith("roadmap-") ? <>
            {bulkSelected.size > 0 && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", background: "var(--surface2)", border: "1px solid var(--green)40", borderRadius: 5, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "var(--green)", fontWeight: 600 }}>{bulkSelected.size} selected</span>
                <button className="btn primary" style={{ fontSize: 11, padding: "4px 10px" }}
                  onClick={() => { [...bulkSelected].forEach(id => { const t = tasks.find(x => x.id === id); if (t && !t.done) toggleDone(t); }); clearBulk(); }}>
                  ✓ Mark Done
                </button>
                <button className="btn" style={{ fontSize: 11, padding: "4px 10px", color: "var(--amber)", borderColor: "var(--amber)" }}
                  onClick={() => { setTasks(prev => prev.map(t => bulkSelected.has(t.id) ? { ...t, priority: "high" } : t)); clearBulk(); }}>
                  ▲ High Priority
                </button>
                {/* Bulk Jira transition - only shown when all selected tasks are Jira */}
                {[...bulkSelected].every(id => tasks.find(t => t.id === id)?.source === "jira") && bulkSelected.size > 0 && (() => {
                  const jiraTasks = [...bulkSelected].map(id => tasks.find(t => t.id === id)).filter(Boolean);
                  const commonTransitions = jiraTasks[0]?.transitions || [];
                  if (!commonTransitions.length) return null;
                  return (
                    <select className="form-select" style={{ fontSize: 11, background: "#fff", color: "#1a1a1a" }}
                      onChange={async e => {
                        if (!e.target.value) return;
                        const [transId, transName] = e.target.value.split("|");
                        let failed = 0;
                        await Promise.all(jiraTasks.map(async t => {
                          try { await transitionJiraIssue(config, t.jiraKey, transId, true); }
                          catch { failed++; }
                        }));
                        setTasks(prev => prev.map(t => bulkSelected.has(t.id) && t.source === "jira"
                          ? { ...t, status: transName, done: ["done","closed","resolved"].includes(transName.toLowerCase()) }
                          : t));
                        showToast(`${jiraTasks.length - failed} ticket(s) moved to ${transName}${failed ? `, ${failed} failed` : ""}`);
                        clearBulk();
                        e.target.value = "";
                      }}>
                      <option value="">🔷 Transition all...</option>
                      {commonTransitions.map(t => <option key={t.id} value={`${t.id}|${t.name}`}>{t.name}</option>)}
                    </select>
                  );
                })()}
                <button className="btn danger" style={{ fontSize: 11, padding: "4px 10px" }}
                  onClick={() => { if (window.confirm(`Delete ${bulkSelected.size} tasks?`)) { setTasks(prev => prev.filter(t => !bulkSelected.has(t.id))); clearBulk(); setSelected(null); } }}>
                  🗑 Delete
                </button>
                <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={clearBulk}>{"✕"} Clear</button>
              </div>
            )}
            <div className="toolbar">
              <input className="search-box" placeholder="Search tasks, notes… ( / to focus)"
                value={search} onChange={(e) => setSearch(e.target.value)} />
              {search.trim() && (
                <button className="btn" style={{ fontSize: 11, padding: "6px 10px", whiteSpace: "nowrap" }}
                  title="Save this search as a pinned filter"
                  onClick={() => {
                    const name = window.prompt("Name this saved search:", search);
                    if (name) saveSearch(name);
                  }}>🔖 Save</button>
              )}
              {filter === "manual" && (
                <div className="view-toggle">
                  <button className={`view-btn ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")} title="List view">☰ List</button>
                  <button className={`view-btn ${viewMode === "kanban" ? "active" : ""}`} onClick={() => setViewMode("kanban")} title="Kanban view">⊞ Kanban</button>
                  <button className={`view-btn ${viewMode === "gantt" ? "active" : ""}`} onClick={() => setViewMode("gantt")} title="Gantt view">📊 Gantt</button>
                  <button className={`view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")} title="Grid view">📄 Grid</button>
                  <button className={`view-btn ${viewMode === "calendar" ? "active" : ""}`} onClick={() => setViewMode("calendar")} title="Calendar view">📅 Calendar</button>
                </div>
              )}
              {filter === "done" && (
                <button className="btn danger" style={{ fontSize: 11, whiteSpace: "nowrap" }}
                  onClick={() => {
                    const count = tasks.filter(t => t.done).length;
                    if (count === 0) { showToast("No completed tasks to clear", "error"); return; }
                    if (window.confirm(`Permanently delete all ${count} completed task${count !== 1 ? "s" : ""}? This cannot be undone.`)) {
                      const toDelete = tasks.filter(t => t.done && t.source === "manual");
                      setTasks(prev => prev.filter(t => !t.done));
                      showToast(`${count} completed task${count !== 1 ? "s" : ""} deleted`);
                      // Sync deletions to server
                      toDelete.forEach(t => {
                        fetch(`${MANUAL_TASKS_API}/${t.id}`, { method: "DELETE" }).catch(() => {});
                      });
                    }
                  }}>
                  🗑 Clear all completed
                </button>
              )}
              {msConnected && <button className="btn" onClick={async () => {
                const em = await fetchOutlookEmails(config);
                const convertedIds = new Set(tasks.filter(t => t.id?.startsWith("email-")).map(t => t.id.replace("email-", "")));
                const filtered = em.filter(e => !convertedIds.has(e.id));
                setEmails(filtered);
                showToast(`${filtered.length} unread emails in inbox`);
              }}>📬 Inbox</button>}
              {taskTemplates.length > 0 && (
                <button className="btn" style={{ fontSize: 11 }} onClick={() => setShowTemplates(v => !v)} title="Create task from template">📋 Templates</button>
              )}
              <button className="btn primary" onClick={() => setShowNewTask((s) => !s)}>+ New</button>
              <button className="btn" style={{ borderColor: "#c47ef0", color: "#c47ef0" }} title="Create a task from a photo of notes"
                onClick={() => visionInputRef.current?.click()} disabled={visionBusy}>
                {visionBusy ? "📷 Reading…" : "📷 From notes"}
              </button>
              <input type="file" ref={visionInputRef} accept=".jpg,.jpeg,.png,.gif,.webp,.heic" style={{ display: "none" }}
                onChange={e => { if (e.target.files?.[0]) handleVisionImage(e.target.files[0]); e.target.value = ""; }} />
              {jiraConnected && (
                <button className="btn" style={{ borderColor: "#4c9aff", color: "#4c9aff" }}
                  onClick={async () => {
                    setShowNewJira(s => !s);
                    setShowNewTask(false);
                    if (clientOptions.length === 0 && jiraConnected) {
                      setLoadingClients(true);
                      const opts = await fetchJiraFieldOptions(config, "customfield_10435");
                      setClientOptions(opts.length > 0 ? opts : JIRA_CLIENT_LIST);
                      setLoadingClients(false);
                    }
                  }}>
                  🔷 + Jira
                </button>
              )}
            </div>
            </> : null}
            {showTemplates && taskTemplates.length > 0 && (
              <div style={{ background: "var(--surface)", border: "1px solid var(--green)40", borderRadius: 6, padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>📋 Task Templates</div>
                {taskTemplates.map(tmpl => (
                  <div key={tmpl.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                    background: "var(--surface2)", borderRadius: 4, border: "1px solid var(--border)" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{tmpl.name}</div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                        {capitalise(tmpl.priority)} · {(tmpl.subtasks||[]).length} subtask{(tmpl.subtasks||[]).length !== 1 ? "s" : ""}
                        {tmpl.recur ? ` · 🔁 ${tmpl.recur}` : ""}{(tmpl.tags||[]).length > 0 ? ` · ${tmpl.tags.join(", ")}` : ""}
                      </div>
                    </div>
                    <button className="btn primary" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => createFromTemplate(tmpl)}>Use</button>
                    <button className="btn" style={{ fontSize: 10, padding: "3px 8px", color: "var(--red)", borderColor: "var(--red)" }}
                      onClick={() => { if (window.confirm(`Delete template "${tmpl.name}"?`)) deleteTemplate(tmpl.id); }}>🗑</button>
                  </div>
                ))}
              </div>
            )}
            {showNewTask && filter !== "implementations" && filter !== "client-hub" && !filter?.startsWith("roadmap-") && (
              <div className="new-task-panel">
                <input className="form-input" placeholder="Task title…" value={newTask.title} onChange={(e) => setNewTask((f) => ({ ...f, title: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addTask()} autoFocus />
                <div className="new-task-row">
                  <select className="form-select" value={newTask.status} onChange={(e) => setNewTask((f) => ({ ...f, status: e.target.value }))}>
                    <option value="To Start">To Start</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Waiting Response">Waiting Response</option>
                  </select>
                  <select className="form-select" value={newTask.priority} onChange={(e) => setNewTask((f) => ({ ...f, priority: e.target.value }))}>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  <input className="form-input" type="date" value={newTask.due} onChange={(e) => setNewTask((f) => ({ ...f, due: e.target.value }))} placeholder="Due date" />
                  <select className="form-select" value={newTask.assignedTo || ""} onChange={e => setNewTask(f => ({ ...f, assignedTo: e.target.value }))}>
                    <option value="">👤 Assignee…</option>
                    {allTeamMembers.map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                  <select className="form-select" value={newTask.recur} onChange={e => setNewTask(f => ({ ...f, recur: e.target.value }))}>
                    {RECUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button className="btn primary" onClick={addTask}>Add</button>
                  <button className="btn" onClick={() => setShowNewTask(false)}>Cancel</button>
                </div>
              </div>
            )}
            {visionReview && (
              <div className="overlay" onClick={e => e.target === e.currentTarget && setVisionReview(null)}>
                <div className="rm-modal" style={{ maxWidth: 620, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
                  <div className="rm-modal-header">
                    <span className="rm-title">📷 Create task from notes</span>
                    <button className="btn" onClick={() => setVisionReview(null)}>✕</button>
                  </div>
                  <div className="rm-modal-body" style={{ overflowY: "auto" }}>
                    <div className="rm-field">
                      <label>Task title</label>
                      <input className="rm-input" value={visionReview.title} onChange={e => setVisionReview(v => ({ ...v, title: e.target.value }))} />
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", margin: "10px 0 6px", fontWeight: 600 }}>
                      Extracted items, choose what each becomes:
                    </div>
                    {visionReview.items.length === 0 && <div style={{ fontSize: 12, color: "var(--text-dim)", fontStyle: "italic" }}>No action items were detected. You can still create the task from the title above.</div>}
                    {visionReview.items.map((it, idx) => (
                      <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                        <input className="rm-input" style={{ flex: 1, opacity: it.kind === "skip" ? 0.45 : 1, textDecoration: it.kind === "skip" ? "line-through" : "none" }}
                          value={it.text} onChange={e => setVisionReview(v => ({ ...v, items: v.items.map((x, i) => i === idx ? { ...x, text: e.target.value } : x) }))} />
                        <select className="rm-select-input" style={{ width: 120, flexShrink: 0 }} value={it.kind}
                          onChange={e => setVisionReview(v => ({ ...v, items: v.items.map((x, i) => i === idx ? { ...x, kind: e.target.value } : x) }))}>
                          <option value="subtask">↳ Subtask</option>
                          <option value="task">◆ New task</option>
                          <option value="skip">✕ Skip</option>
                        </select>
                      </div>
                    ))}
                    {visionReview.transcription && (
                      <details style={{ marginTop: 12 }}>
                        <summary style={{ fontSize: 11, color: "var(--text-dim)", cursor: "pointer" }}>View full transcription</summary>
                        <div style={{ whiteSpace: "pre-wrap", fontSize: 12, color: "var(--text-muted)", marginTop: 6, padding: 10, background: "var(--surface2)", borderRadius: 6 }}>{visionReview.transcription}</div>
                      </details>
                    )}
                  </div>
                  <div className="rm-modal-footer">
                    <span style={{ fontSize: 11, color: "var(--text-dim)", marginRight: "auto" }}>
                      {visionReview.items.filter(i => i.kind === "subtask").length} subtask(s), {visionReview.items.filter(i => i.kind === "task").length} extra task(s)
                    </span>
                    <button className="btn" onClick={() => setVisionReview(null)}>Cancel</button>
                    <button className="btn primary" onClick={createFromVision}>Create</button>
                  </div>
                </div>
              </div>
            )}
            {showNewJira && (
              <div className="overlay" onClick={e => e.target === e.currentTarget && setShowNewJira(false)}>
                <div className="modal" style={{ maxWidth: 580, borderTop: "3px solid #4c9aff" }}>
                  <div className="modal-title" style={{ color: "#4c9aff" }}>🔷 New Jira Issue</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: -12 }}>
                    Project: <strong>SUPPORT</strong> &nbsp;·&nbsp; Status: <strong>Triage</strong>
                  </div>
                  <div className="modal-section">
                    <div className="form-row">
                      <label className="form-label">Summary <span style={{color:"var(--red)"}}>*</span></label>
                      <input className="form-input" placeholder="Issue title…" autoFocus
                        value={newJira.summary} onChange={e => setNewJira(f => ({ ...f, summary: e.target.value }))} />
                    </div>
                    <div className="form-row">
                      <label className="form-label">Description <span style={{color:"var(--red)"}}>*</span></label>
                      <textarea className="notes-area" placeholder="Describe the issue…" style={{ minHeight: 80 }}
                        value={newJira.description} onChange={e => setNewJira(f => ({ ...f, description: e.target.value }))} />
                    </div>
                    <div className="form-row">
                      <label className="form-label">Affected Client <span style={{color:"var(--red)"}}>*</span></label>
                      {loadingClients ? (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", padding: "8px 0" }}>⏳ Loading clients from Jira…</div>
                      ) : (
                        <select className="form-select" style={{ width: "100%", background: "#fff", color: newJira.affectedClient ? "#1a1a1a" : "#888" }}
                          value={newJira.affectedClient}
                          onChange={e => setNewJira(f => ({ ...f, affectedClient: e.target.value }))}>
                          <option value="">- Select client -</option>
                          {(clientOptions.length > 0 ? clientOptions : JIRA_CLIENT_LIST).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="form-row">
                      <label className="form-label">Category <span style={{color:"var(--red)"}}>*</span></label>
                      <select className="form-select" style={{ width: "100%", background: "#fff", color: "#1a1a1a" }}
                        value={newJira.category}
                        onChange={e => setNewJira(f => ({ ...f, category: e.target.value, ticketType: "" }))}>
                        <option value="">- Select category -</option>
                        <option value="Actions">Actions</option>
                        <option value="Compliance">Compliance</option>
                        <option value="Contractor Management">Contractor Management</option>
                        <option value="Documents">Documents</option>
                        <option value="Helpdesk">Helpdesk</option>
                        <option value="Notifications">Notifications</option>
                        <option value="OnSite">OnSite</option>
                        <option value="Pronto Forms">Pronto Forms</option>
                        <option value="Properties">Properties</option>
                        <option value="Reporting">Reporting</option>
                        <option value="System Config">System Config</option>
                        <option value="Tasks">Tasks</option>
                        <option value="Templating">Templating</option>
                        <option value="Users">Users</option>
                        <option value="Website Configuration">Website Configuration</option>
                        <option value="Other">Other</option>
                        <option value="Auto Closed">Auto Closed</option>
                        <option value="Blazer">Blazer</option>
                      </select>
                    </div>
                    {newJira.category && (
                      <div className="form-row">
                        <label className="form-label">Ticket Type <span style={{color:"var(--red)"}}>*</span></label>
                        <select className="form-select" style={{ width: "100%", background: "#fff", color: "#1a1a1a" }}
                          value={newJira.ticketType}
                          onChange={e => setNewJira(f => ({ ...f, ticketType: e.target.value }))}>
                          <option value="">- Select type -</option>
                          {newJira.category === "Actions" && <>
                            <option value="Add Actions">Add Actions</option>
                            <option value="Amend Actions">Amend Actions</option>
                            <option value="Close Actions">Close Actions</option>
                          </>}
                          {newJira.category === "Compliance" && <>
                            <option value="Add Module">Add Module</option>
                            <option value="Amend Module">Amend Module</option>
                            <option value="Remove Module">Remove Module</option>
                            <option value="Amend Previous Data">Amend Previous Data</option>
                            <option value="Completion of Inspection">Completion of Inspection</option>
                            <option value="Support Request">Support Request</option>
                            <option value="Suspend Compliance">Suspend Compliance</option>
                            <option value="Update Event Dates">Update Event Dates</option>
                          </>}
                          {newJira.category === "Contractor Management" && <>
                            <option value="Add">Add</option>
                            <option value="Amend">Amend</option>
                            <option value="Remove">Remove</option>
                          </>}
                          {newJira.category === "Documents" && <>
                            <option value="Add Document">Add Document</option>
                            <option value="Change Document">Change Document</option>
                            <option value="Document Upload">Document Upload</option>
                            <option value="Failed Upload">Failed Upload</option>
                            <option value="Remove Document">Remove Document</option>
                            <option value="Remove Attachment">Remove Attachment</option>
                          </>}
                          {newJira.category === "Helpdesk" && <>
                            <option value="Job Type Amendment">Job Type Amendment</option>
                            <option value="Invalid Document">Invalid Document</option>
                            <option value="Revert Job">Revert Job</option>
                            <option value="General Issue">General Issue</option>
                          </>}
                          {newJira.category === "Notifications" && <>
                            <option value="Add Notification">Add Notification</option>
                            <option value="Amend Notification">Amend Notification</option>
                            <option value="Remove Notification">Remove Notification</option>
                          </>}
                          {newJira.category === "OnSite" && <>
                            <option value="Add User">Add User</option>
                            <option value="Amend Form">Amend Form</option>
                            <option value="Amend Primary/Secondary User">Amend Primary/Secondary User</option>
                            <option value="Can't Capture Media">Can't Capture Media</option>
                            <option value="Can't Login">Can't Login</option>
                            <option value="Can't See Forms">Can't See Forms</option>
                            <option value="Can't Submit Form">Can't Submit Form</option>
                            <option value="Create Form">Create Form</option>
                            <option value="DataSet amendment">DataSet amendment</option>
                            <option value="Dispatch Form">Dispatch Form</option>
                            <option value="New Development Request">New Development Request</option>
                            <option value="Remove Form">Remove Form</option>
                            <option value="Software Bug">Software Bug</option>
                            <option value="User Management">User Management</option>
                          </>}
                          {newJira.category === "Pronto Forms" && <>
                            <option value="Can't Login">Can't Login</option>
                            <option value="Can't Capture Media">Can't Capture Media</option>
                            <option value="Can't See Forms">Can't See Forms</option>
                            <option value="Can't Submit Form">Can't Submit Form</option>
                            <option value="Add User">Add User</option>
                            <option value="Create Form">Create Form</option>
                            <option value="Amend Form">Amend Form</option>
                            <option value="User Management">User Management</option>
                            <option value="Amend Primary/Secondary User">Amend Primary/Secondary User</option>
                            <option value="Software Bug">Software Bug</option>
                            <option value="New Development Request">New Development Request</option>
                            <option value="Remove Form">Remove Form</option>
                            <option value="Dispatch Form">Dispatch Form</option>
                          </>}
                          {newJira.category === "Properties" && <>
                            <option value="Add Property">Add Property</option>
                            <option value="Amend Property">Amend Property</option>
                            <option value="Deactivate Property">Deactivate Property</option>
                          </>}
                          {newJira.category === "Reporting" && <>
                            <option value="Add User to Report">Add User to Report</option>
                            <option value="Amend Report">Amend Report</option>
                            <option value="Create Report">Create Report</option>
                            <option value="My Dashboard Error">My Dashboard Error</option>
                            <option value="Remove User from Report">Remove User from Report</option>
                            <option value="Report Error">Report Error</option>
                            <option value="Subscription Reporter Errors">Subscription Reporter Errors</option>
                          </>}
                          {newJira.category === "System Config" && <>
                            <option value="System Issue">System Issue</option>
                            <option value="Other">Other</option>
                          </>}
                          {newJira.category === "Tasks" && <>
                            <option value="Add Task">Add Task</option>
                            <option value="Amend Task">Amend Task</option>
                            <option value="Remove Task">Remove Task</option>
                            <option value="New Development Request">New Development Request</option>
                          </>}
                          {newJira.category === "Templating" && <>
                            <option value="New Template">New Template</option>
                            <option value="Amend Template">Amend Template</option>
                            <option value="Deactivate Template">Deactivate Template</option>
                          </>}
                          {newJira.category === "Users" && <>
                            <option value="Add User">Add User</option>
                            <option value="Amend User">Amend User</option>
                            <option value="Remove User">Remove User</option>
                            <option value="User Login Issue">User Login Issue</option>
                            <option value="User Config Issue">User Config Issue</option>
                            <option value="Add Contractor">Add Contractor</option>
                            <option value="Amend Contractor">Amend Contractor</option>
                            <option value="Remove Contractor">Remove Contractor</option>
                          </>}
                          {newJira.category === "Website Configuration" && <>
                            <option value="Other">Other</option>
                          </>}
                          {newJira.category === "Other" && <>
                            <option value="Other">Other</option>
                          </>}
                          {newJira.category === "Auto Closed" && <>
                            <option value="Non-Support Request">Non-Support Request</option>
                            <option value="Other">Other</option>
                          </>}
                          {newJira.category === "Blazer" && <>
                            <option value="Blazer">Blazer</option>
                          </>}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="modal-section">
                    <div className="modal-section-title">Attachments (optional)</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 12px", border: "1px dashed var(--border)", borderRadius: 5, fontSize: 11, color: "var(--text-muted)", width: "fit-content" }}>
                        📎 {uploadingAttachment ? "Uploading…" : "Add file (PDF, Word, Excel, images)"}
                        <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" multiple style={{ display: "none" }}
                          onChange={e => {
                            const files = Array.from(e.target.files || []);
                            setJiraAttachments(prev => [...prev, ...files]);
                            e.target.value = "";
                          }} />
                      </label>
                      {jiraAttachments.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                          {jiraAttachments.map((f, i) => {
                            const pct = uploadProgress[f.name];
                            const uploading = pct !== undefined && pct < 100;
                            const done = pct === 100;
                            return (
                              <div key={i} style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, padding: "6px 10px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                                  <span style={{ color: done ? "var(--green)" : "var(--text)" }}>
                                    {done ? "✓" : uploading ? "⏫" : "📄"} {f.name}
                                  </span>
                                  <span style={{ color: "var(--text-muted)", fontSize: 10 }}>({(f.size / 1024).toFixed(0)}KB)</span>
                                  {!uploading && !done && (
                                    <button onClick={() => setJiraAttachments(prev => prev.filter((_, j) => j !== i))}
                                      style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 12, padding: 0 }}>{"✕"}</button>
                                  )}
                                  {uploading && <span style={{ marginLeft: "auto", fontSize: 10, color: "#4c9aff" }}>{pct}%</span>}
                                </div>
                                {(uploading || done) && (
                                  <div style={{ height: 4, background: "var(--border)", borderRadius: 2, marginTop: 5, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: (pct || 0) + "%", background: done ? "var(--green)" : "#4c9aff", borderRadius: 2, transition: "width 0.2s" }} />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn" onClick={() => { setShowNewJira(false); setNewJira({ summary: "", description: "", affectedClient: "", category: "", ticketType: "" }); setJiraAttachments([]); }}>Cancel</button>
                    <button className="btn primary" style={{ background: "#0052cc", borderColor: "#0052cc" }}
                      onClick={handleCreateJira}
                      disabled={creatingJira || !newJira.summary.trim() || !newJira.description.trim() || !newJira.affectedClient.trim() || !newJira.category || !newJira.ticketType}>
                      {creatingJira ? "Creating…" : `Create in Jira${jiraAttachments.length > 0 ? ` + ${jiraAttachments.length} file${jiraAttachments.length > 1 ? "s" : ""}` : ""}`}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {filter === "manual" && viewMode === "kanban" ? (
              <KanbanBoard
                tasks={filtered}
                selected={selected}
                onSelect={(id) => handleSelectTask(id)}
                onToggle={toggleDone}
                onStatusChange={handleStatusChange}
              />
            ) : filter === "manual" && (viewMode === "gantt" || viewMode === "grid") ? (
              <ManualBoardView
                tasks={filtered}
                viewMode={viewMode}
                onSelectTask={(id) => handleSelectTask(id)}
                onUpdateTask={updateTask}
              />
            ) : filter === "manual" && viewMode === "calendar" ? (
              <CalendarView
                tasks={filtered}
                projects={[]}
                includeSubtasks={true}
                onSelectTask={(id) => handleSelectTask(id)}
              />
            ) : filter === "ticket-age" ? (
              <TicketAgeReport tasks={tasks} />
            ) : filter === "cross-gantt" ? (
              <CrossProjectGantt projects={projects} />
            ) : filter === "workload" ? (
              <WorkloadView tasks={tasks} projects={projects} />
            ) : filter === "activity-feed" ? (
              <SharedActivityFeed devCompletedTickets={devCompletedTickets} />
            ) : filter === "reporting" ? (
              <ReportingDashboard config={config} tasks={tasks} />
            ) : filter === "client-hub" ? (
              <ClientHubView jiraConnected={jiraConnected} />
            ) : filter === "project-hub" ? (
              <ProjectHubView />
            ) : filter?.startsWith("roadmap-") ? (
              (() => {
                const allRoadmapsList = [
                  { name: "CAFM Dev Roadmap", label: roadmapLabel },
                  ...roadmaps.map(r => ({ name: r.name, label: r.label })),
                ];
                if (filter === "roadmap-cafm") {
                  return <RoadmapView config={config} roadmapName={"CAFM Dev Roadmap"} displayLabel={roadmapLabel}
                    allRoadmaps={allRoadmapsList}
                    myRole={cafmMeta.myRole || "owner"}
                    ownerless={!cafmMeta.owner}
                    onShare={() => openShareDialog("roadmap-cafm")}
                    onRename={(newName) => { setRoadmapLabel(newName); try { localStorage.setItem("taskflow_roadmap_label", newName); } catch {} }} />;
                }
                const r = roadmaps.find(x => x.id === filter);
                if (!r) return <div style={{ padding: 24, color: "var(--text-muted)" }}>Roadmap not found.</div>;
                return <RoadmapView config={config} roadmapName={r.name} displayLabel={r.label}
                  myRole={r.myRole || "owner"}
                  ownerless={!r.owner}
                  allRoadmaps={allRoadmapsList}
                  onShare={() => openShareDialog(r.id)}
                  onDelete={() => deleteRoadmap(r.id)}
                  onRename={(newName) => renameRoadmap(r.id, newName)} />;
              })()
            ) : filter === "calendar" ? (
              <CalendarView
                tasks={tasks}
                projects={projects}
                onSelectTask={(id) => { setSelected(id); }}
              />
            ) : filter === "dashboard" ? (
              <DashboardView
                tasks={tasks}
                projects={projects}
                config={config}
                jiraConnected={jiraConnected}
                msConnected={msConnected}
                onNavigate={(f) => {
                  if (f === "jira-queue") { setJiraQueuePreset("awaiting-sdm"); setJiraQueueMe(true); setFilter("jira-queue"); setSubFilter(null); return; }
                  setFilter(f); setSubFilter(null);
                }}
                onOpenJiraQueue={() => { setJiraQueuePreset("awaiting-sdm"); setJiraQueueMe(true); setFilter("jira-queue"); setSubFilter(null); }}
                onSelect={(id) => { setSelected(id); handleSelectTask(id); }}
              />
            ) : filter === "sla-reports" ? (
              <SLAReportView config={config} />
            ) : filter === "jira-queue" ? (
              <JiraQueueView config={config} initialPreset={jiraQueuePreset} initialMe={jiraQueueMe} />
            ) : filter === "implementations" ? (
              <ImplementationsView
                projects={projects}
                onUpdateProjects={setProjects}
                config={config}
                jiraConnected={jiraConnected}
                showToast={showToast}
                sync={sync}
                tasks={tasks}
                onRaiseJira={(task, moduleIndex, projectClientName, updateTaskFn) => {
                  if (openImplJiraRef.current) openImplJiraRef.current(task, moduleIndex, projectClientName, updateTaskFn);
                }}
              />
            ) : filter === "clients" ? (
              <ClientDashboard
                tasks={tasks.filter(t => t.source === "jira" && !t.done)}
                allJiraTasks={tasks.filter(t => t.source === "jira")}
                managerMode={managerMode}
                config={config}
                jiraConnected={jiraConnected}
                selectedTask={selectedTask}
                onSelectTask={(id) => handleSelectTask(id)}
                onNavigate={(f) => { setFilter(f); setSubFilter(null); }}
              />
            ) : (
              <div className="task-list">
                {filtered.length === 0 ? (
                  <div className="empty-state">
                    <div className="icon">◈</div>
                    No tasks here.<br />
                    {(!msConnected && !jiraConnected) && "Configure connections and click Sync to load tasks."}
                  </div>
                ) : filter === "assigned-by-me" ? (() => {
                  // Build a flat list: whole task if assigned out, else individual assigned-out subtasks
                  const myName = localStorage.getItem("taskflow_jira_displayname") || "";
                  const items = [];
                  filtered.forEach(t => {
                    if (t.assignedTo && t.assignedTo !== myName) {
                      items.push({ key: t.id, type: "task", task: t });
                    } else {
                      (t.subtasks || []).forEach(s => {
                        if (s.assignedTo && s.assignedTo !== myName && !s.done) {
                          items.push({ key: t.id + "_" + s.id, type: "subtask", task: t, subtask: s });
                        }
                      });
                    }
                  });
                  if (items.length === 0) {
                    return <div className="empty-state"><div className="icon">📤</div>Nothing assigned out to others.</div>;
                  }
                  return items.map(item => item.type === "task" ? (
                    <div key={item.key} style={{ pointerEvents: "auto" }}>
                      <TaskCard
                        task={item.task}
                        selected={selected === item.task.id}
                        onClick={() => handleSelectTask(item.task.id)}
                        onToggle={() => toggleDone(item.task)}
                        onToggleSubtask={toggleSubtaskFromCard}
                        bulkChecked={bulkSelected.has(item.task.id)}
                        onBulkToggle={() => toggleBulkSelect(item.task.id)} />
                    </div>
                  ) : (
                    <div key={item.key} className="task-card" onClick={() => handleSelectTask(item.task.id)}
                      style={{ cursor: "pointer", borderLeft: "3px solid #F1901E" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <span style={{ color: "#F1901E", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>(S)</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 500, marginBottom: 4 }}>{item.subtask.text}</div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: 10, color: "var(--text-dim)" }}>in: {item.task.title}</span>
                            <span className="tag" style={{ fontSize: 9, color: "#47ac4c", borderColor: "#47ac4c50", background: "#47ac4c10", fontWeight: 600 }}>👤 {item.subtask.assignedTo}</span>
                            {item.subtask.status && (
                              <span className="tag" style={{ fontSize: 9,
                                ...(item.subtask.status === "Done" ? { color: "var(--green)", borderColor: "#47ac4c50", background: "#47ac4c10" }
                                  : item.subtask.status === "In Progress" ? { color: "#4c9aff", borderColor: "#4c9aff50", background: "#4c9aff10" }
                                  : item.subtask.status === "Waiting Response" ? { color: "var(--amber)", borderColor: "#F1901E50", background: "#F1901E10" }
                                  : { color: "var(--text-dim)", borderColor: "var(--border)", background: "var(--surface2)" }) }}>
                                {item.subtask.jiraLinkStatus || item.subtask.status}
                              </span>
                            )}
                            {item.subtask.due && (
                              <span className="tag due-tag" style={{ fontSize: 9 }}>📅 {formatDate(item.subtask.due)}</span>
                            )}
                            {(item.subtask.notes || []).length > 0 && (
                              <span className="tag" style={{ fontSize: 9, color: "#4c9aff", borderColor: "#4c9aff50", background: "#4c9aff10" }}>💬 {(item.subtask.notes || []).length}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ));
                })() : filtered.map((t) => (
                  <div key={t.id}
                    draggable={t.source === "manual" && filter === "manual" && viewMode === "list" ? "true" : "false"}
                    onDragStart={t.source === "manual" ? (e) => {
                      dragId.current = t.id;
                      setDraggingTaskId(t.id);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", t.id);
                    } : undefined}
                    onDragOver={t.source === "manual" ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = "move";
                    } : undefined}
                    onDrop={t.source === "manual" ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const fromId = dragId.current;
                      if (fromId && fromId !== t.id) moveTask(fromId, t.id);
                      dragId.current = null;
                      setDraggingTaskId(null);
                    } : undefined}
                    onDragEnd={() => {
                      dragId.current = null;
                      setDraggingTaskId(null);
                    }}
                    style={{
                      opacity: draggingTaskId === t.id ? 0.4 : 1,
                      outline: draggingTaskId && draggingTaskId !== t.id && t.source === "manual" ? "2px dashed var(--green)" : "none",
                      outlineOffset: -2,
                      borderRadius: 6,
                      transition: "outline 0.1s",
                    }}>
                    {/* pointer-events:none on inner content while ANY card is dragging
                        - this forces dragover to always hit the outer wrapper div,
                          which is the only way to guarantee drop fires */}
                    <div style={{ pointerEvents: draggingTaskId ? "none" : "auto" }}>
                      <TaskCard
                        task={t}
                        selected={selected === t.id}
                        onClick={() => handleSelectTask(t.id)}
                        onToggle={() => toggleDone(t)}
                        onDismiss={dismissTask}
                        onToggleSubtask={toggleSubtaskFromCard}
                        highlightAssignee={filter === "assigned-to-me" ? (localStorage.getItem("taskflow_jira_displayname") || "") : null}
                        markAssignedOutBy={filter === "assigned-by-me" ? (localStorage.getItem("taskflow_jira_displayname") || "") : null}
                        bulkChecked={bulkSelected.has(t.id)}
                        onBulkToggle={() => toggleBulkSelect(t.id)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {mobileDetailOpen && selectedTask && <div className="mobile-overlay" onClick={() => { setMobileDetailOpen(false); setSelected(null); }} />}
          {mobileDetailOpen && filter !== "implementations" && filter !== "client-hub" && !filter?.startsWith("roadmap-") && <DetailPanel
            task={selectedTask}
            onMobileClose={() => { setMobileDetailOpen(false); setSelected(null); }}
            onComplete={() => selectedTask && toggleDone(selectedTask)}
            onCompleteFlagged={() => selectedTask && completeFlaggedTask(selectedTask)}
            onCreateOutlook={() => selectedTask && createOutlookFromJira(selectedTask)}
            onSaveNote={saveNote}
            onSaveDue={saveDue}
            onTransition={handleJiraTransition}
            onStatusChange={handleStatusChange}
            onUpdateTask={updateTask}
            onDelete={deleteTask}
            onPopout={() => setPopoutTask(selectedTask)}
            onPromoteSubtask={promoteSubtask}
            onSaveTemplate={saveAsTemplate}
            jiraAssignees={allTeamMembers}
            config={config}
            allManualTasks={tasks.filter(t => t.source === "manual")}
            onCreateInJira={(task) => {
              setNewJira({
                summary: task.title || "",
                description: task.description || "",
                affectedClient: (task.tags?.find(t => t !== "client-hub") || task.title?.match(/\[([^\]]+)\]/)?.[1] || ""),
                category: "",
                ticketType: "",
              });
              setShowNewJira(true);
            }}
          />}
        </div>
      </div>


      {shareDialog && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShareDialog(null)}>
          <div className="rm-modal" style={{ maxWidth: 520, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
            <div className="rm-modal-header">
              <span className="rm-title">🔗 Share "{shareDialog.name}"</span>
              <button className="btn" onClick={() => setShareDialog(null)}>✕</button>
            </div>
            <div className="rm-modal-body" style={{ overflowY: "auto" }}>
              <div className="rm-field">
                <label>Who can access this roadmap</label>
                <select className="rm-select-input" value={shareDialog.visibility} onChange={e => setShareDialog(s => ({ ...s, visibility: e.target.value }))}>
                  <option value="private">🔒 Private (only me)</option>
                  <option value="everyone">🌐 Everyone in the team</option>
                  <option value="specific">👥 Specific people</option>
                </select>
              </div>
              {shareDialog.visibility === "specific" && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>People with access</div>
                  {(shareDialog.shares || []).length === 0 && <div style={{ fontSize: 12, color: "var(--text-dim)", fontStyle: "italic", marginBottom: 8 }}>No one added yet. Add people below.</div>}
                  {(shareDialog.shares || []).map((sh, idx) => {
                    const u = allUsers.find(x => (x.accountId && x.accountId === sh.user) || x.name === sh.user);
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid var(--border)" }}>
                        <span style={{ flex: 1, fontSize: 13, color: "var(--text)" }}>{u ? u.name : sh.user}</span>
                        <select className="rm-select-input" style={{ width: 130 }} value={sh.role}
                          onChange={e => setShareDialog(s => ({ ...s, shares: s.shares.map((x, i) => i === idx ? { ...x, role: e.target.value } : x) }))}>
                          <option value="view">👁 View</option>
                          <option value="edit">✎ View & edit</option>
                          <option value="owner">★ Owner</option>
                        </select>
                        <button className="btn danger" style={{ fontSize: 10, padding: "2px 6px" }}
                          onClick={() => setShareDialog(s => ({ ...s, shares: s.shares.filter((_, i) => i !== idx) }))}>Remove</button>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 11, color: "var(--text-dim)" }}>Add a person</label>
                    <select className="rm-select-input" value=""
                      onChange={e => {
                        if (!e.target.value) return;
                        const already = (shareDialog.shares || []).some(s => s.user === e.target.value);
                        if (!already) setShareDialog(s => ({ ...s, shares: [...(s.shares || []), { user: e.target.value, role: "view" }] }));
                      }}>
                      <option value="">Select a person…</option>
                      {allUsers
                        .filter(u => !(shareDialog.shares || []).some(s => s.user === (u.accountId || u.name)))
                        .map(u => <option key={u.accountId || u.name} value={u.accountId || u.name}>{u.name}{u.email ? ` (${u.email})` : ""}</option>)}
                    </select>
                    {allUsers.length === 0 && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>No other users have signed in yet. People appear here once they have used the Task Manager.</div>}
                  </div>
                </div>
              )}
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 14, lineHeight: 1.5 }}>
                <strong>View</strong> can see the board but not change it. <strong>View &amp; edit</strong> can move and edit cards. <strong>Owner</strong> can also manage sharing.
              </div>
            </div>
            <div className="rm-modal-footer">
              <button className="btn" onClick={() => setShareDialog(null)}>Cancel</button>
              <button className="btn primary" onClick={saveShareSettings}>Save sharing</button>
            </div>
          </div>
        </div>
      )}
      {showConfig && (
        <ConfigModal
          config={config}
          onSave={(c) => {
            // Ensure Jira URL always uses https
            if (c.jiraUrl) c.jiraUrl = c.jiraUrl.replace(/^http:\/\//, "https://").replace(/\/+$/, "");
            setConfig(c);
            localStorage.setItem("taskflow_config", JSON.stringify(c));
            setShowConfig(false);
            showToast("Config saved");
          }}
          onClose={() => setShowConfig(false)}
        />
      )}

      {/* ── Standalone Extract Actions modal ── */}
      {showExtractModal && <StandaloneExtractModal
        onClose={() => setShowExtractModal(false)}
        jiraAssignees={allTeamMembers}
        config={config}
        onCreateTasks={(tasks) => {
          setTasks(prev => [...tasks, ...prev]);
          showToast(`${tasks.length} task${tasks.length !== 1 ? "s" : ""} created`);
          setFilter("manual");
          setShowExtractModal(false);
        }}
      />}

      {emailHubPrompt && (() => {
        const alreadyTask = tasks.some(t => t.id === `email-${emailHubPrompt.id}` || t.rawId === emailHubPrompt.id);
        const alreadySubtask = tasks.some(t => (t.subtasks || []).some(s => s.stEmailId === emailHubPrompt.id));
        return (
        <div className="popout-overlay" onClick={e => e.target === e.currentTarget && setEmailHubPrompt(null)}>
          <div className="popout-modal" style={{ maxWidth: 480, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>What would you like to do with this email?</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", maxWidth: 380, lineHeight: 1.5 }}>
                  <span style={{ color: "var(--amber)" }}>✉</span> <strong style={{ color: "var(--text)" }}>{emailHubPrompt.subject || "(no subject)"}</strong>
                </div>
                <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 3 }}>
                  From: {emailHubPrompt.from?.emailAddress?.name || emailHubPrompt.from?.emailAddress?.address}
                </div>
                {(alreadyTask || alreadySubtask) && (
                  <div style={{ fontSize: 11, color: "var(--amber)", marginTop: 6, background: "rgba(241,144,30,0.1)", border: "1px solid var(--amber)40", borderRadius: 4, padding: "3px 8px" }}>
                    ⚠ This email already exists as a {alreadyTask ? "task" : "subtask"}
                  </div>
                )}
              </div>
              <button onClick={() => setEmailHubPrompt(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 0 0 12px" }}>✕</button>
            </div>

            {/* Option 1 - Convert to task (hidden if already a subtask) */}
            {!alreadySubtask && (
            <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 7, padding: "12px 14px", marginBottom: 10, cursor: "pointer", opacity: alreadyTask ? 0.5 : 1, pointerEvents: alreadyTask ? "none" : "auto" }}
              onClick={() => { emailToTask(emailHubPrompt); setEmailHubPrompt(null); }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 3 }}>✓ {alreadyTask ? "Already a task" : "Convert to task"}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{alreadyTask ? "This email has already been converted to a task" : "Add to your task list and flag the email in Outlook"}</div>
            </div>
            )}

            {/* Option 2 - Add as subtask */}
            <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 7, padding: "12px 14px", marginBottom: 10 }}
              onClick={() => {}}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 3 }}>➕ Add as subtask</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>Attach this email as a subtask to an existing task</div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 5 }}>Select parent task</label>
              <select
                style={{ width: "100%", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 5, padding: "7px 10px", color: "var(--text)", fontSize: 13, outline: "none", fontFamily: "Inter, sans-serif" }}
                value={emailSubtaskParentId}
                onChange={e => setEmailSubtaskParentId(e.target.value)}
                onClick={e => e.stopPropagation()}
              >
                <option value="">- Select a task -</option>
                {tasks.filter(t => t.source === "manual" && !t.done).map(t => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </select>
              <button
                className="btn primary"
                style={{ width: "100%", justifyContent: "center", marginTop: 10 }}
                disabled={!emailSubtaskParentId}
                onClick={e => {
                  e.stopPropagation();
                  const email = emailHubPrompt;
                  const senderName = email.from?.emailAddress?.name || email.from?.emailAddress?.address || "";
                  const senderEmail = email.from?.emailAddress?.address || "";
                  const domain = formatEmailDomain(senderEmail);
                  const subtaskText = "✉ " + [senderName, domain, email.subject].filter(Boolean).join(" | ");
                  const newSubtask = {
                    id: `st-email-${Date.now()}`,
                    text: subtaskText,
                    done: false,
                    due: null,
                    stEmailId: email.id,
                    note: `From: ${senderName}${senderEmail ? ` <${senderEmail}>` : ""}\n${email.bodyPreview || ""}`,
                  };
                  setTasks(prev => {
                    // Add subtask to parent
                    let updated = prev.map(t => {
                      if (t.id !== emailSubtaskParentId) return t;
                      return { ...t, subtasks: [...(t.subtasks || []), newSubtask] };
                    });
                    // Remove any existing main task created from the same email
                    updated = updated.filter(t => t.id !== `email-${email.id}` && t.rawId !== email.id);
                    return updated;
                  });
                  setEmails(prev => prev.filter(e => e.id !== email.id));
                  // Mark as read only - do NOT flag, as flagging would create a task on next sync
                  if (msConnected) {
                    fetch(`https://graph.microsoft.com/v1.0/me/messages/${email.id}`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${config.msToken}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ isRead: true }),
                    }).catch(() => {});
                  }
                  const parentTask = tasks.find(t => t.id === emailSubtaskParentId);
                  showToast(`Email added as subtask to "${parentTask?.title || "task"}"`);
                  setEmailHubPrompt(null);
                  setEmailSubtaskParentId("");
                  setSelected(emailSubtaskParentId);
                }}
              >
                ➕ Add as subtask
              </button>
            </div>

            {/* Option 3 - Log to Client Hub */}
            <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 7, padding: "12px 14px", cursor: "pointer" }}
              onClick={() => {}}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 3 }}>💬 Log to Client Hub</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>Save this email to a client's communication history</div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 5 }}>Select client</label>
              <select
                className="ch-select"
                value={emailHubClient}
                onChange={e => setEmailHubClient(e.target.value)}
                onClick={e => e.stopPropagation()}
              >
                <option value="">- Select a client -</option>
                {JIRA_CLIENT_LIST.filter(c => c !== "All Clients" && c !== "UNKNOWN").map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button
                className="btn primary"
                style={{ width: "100%", justifyContent: "center", marginTop: 10 }}
                disabled={!emailHubClient || emailHubSaving}
                onClick={e => { e.stopPropagation(); logEmailToClientHub(); }}
              >
                {emailHubSaving ? "Fetching & saving…" : "💬 Log to Client Hub"}
              </button>
            </div>

            <button className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 10, fontSize: 12 }}
              onClick={() => setEmailHubPrompt(null)}>
              Cancel - do nothing
            </button>
          </div>
        </div>
        );
      })()}

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      {showActivityLog && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowActivityLog(false)}>
          <div className="modal" style={{ maxWidth: 640, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div className="modal-title">📋 Activity Log</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" style={{ fontSize: 11 }}
                  onClick={() => { if (window.confirm("Clear activity log?")) { setActivityLog([]); localStorage.removeItem("taskflow_activity_log"); } }}>
                  🗑 Clear
                </button>
                <button className="btn" onClick={() => setShowActivityLog(false)}>✕ Close</button>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>Last {activityLog.length} actions (max 200)</div>
            <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              {activityLog.length === 0 && (
                <div style={{ color: "var(--text-dim)", fontSize: 12, textAlign: "center", padding: 24 }}>No activity recorded yet</div>
              )}
              {activityLog.map(e => {
                const actionColor = e.action === "Completed" ? "var(--green)" : e.action === "Created" ? "#4c9aff" : e.action === "Reopened" ? "var(--amber)" : e.action === "Note added" ? "#a78bfa" : "var(--text-muted)";
                return (
                  <div key={e.id} style={{ display: "flex", gap: 10, padding: "6px 8px", background: "var(--surface2)", borderRadius: 4, borderLeft: `3px solid ${actionColor}` }}>
                    <span style={{ fontSize: 10, color: "var(--text-dim)", whiteSpace: "nowrap", flexShrink: 0, marginTop: 1 }}>{e.ts}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: actionColor, whiteSpace: "nowrap", flexShrink: 0 }}>{e.action}</span>
                    <span style={{ fontSize: 11, color: "var(--text)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.title}>{e.title}</span>
                    {e.detail && <span style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap", flexShrink: 0, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }} title={e.detail}>{e.detail}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Impl Jira Creation Modal - rendered at App level to avoid z-index issues */}
      {showImplJira && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={e => e.target === e.currentTarget && setShowImplJira(false)}>
          <div className="modal" style={{ maxWidth: 580, borderTop: "3px solid #4c9aff", zIndex: 10000 }}>
            <div className="modal-title" style={{ color: "#4c9aff" }}>🔷 Raise Jira Issue</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: -12 }}>
              Project: <strong>SUPPORT</strong> &nbsp;·&nbsp; Status: <strong>Triage</strong>
            </div>
            <div className="modal-section">
              <div className="form-row">
                <label className="form-label">Summary <span style={{color:"var(--red)"}}>*</span></label>
                <input className="form-input" value={implJiraForm.summary}
                  onChange={e => setImplJiraForm(f => ({ ...f, summary: e.target.value }))} />
              </div>
              <div className="form-row">
                <label className="form-label">Description <span style={{color:"var(--red)"}}>*</span></label>
                <textarea className="notes-area" style={{ minHeight: 70 }} value={implJiraForm.description}
                  onChange={e => setImplJiraForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="form-row">
                <label className="form-label">Affected Client <span style={{color:"var(--red)"}}>*</span></label>
                <input className="form-input" style={{ background: "#fff", color: "#1a1a1a" }}
                  list="impl-client-list"
                  value={implJiraForm.affectedClient}
                  onChange={e => setImplJiraForm(f => ({ ...f, affectedClient: e.target.value }))}
                  placeholder="Type or select client…" />
                <datalist id="impl-client-list">
                  {(implClientOptions.length > 0 ? implClientOptions : JIRA_CLIENT_LIST).map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="form-row">
                <label className="form-label">Category <span style={{color:"var(--red)"}}>*</span></label>
                <select className="form-select" style={{ width: "100%", background: "#fff", color: "#1a1a1a" }}
                  value={implJiraForm.category}
                  onChange={e => setImplJiraForm(f => ({ ...f, category: e.target.value, ticketType: "" }))}>
                  <option value="">- Select category -</option>
                  <option value="Implementations">Implementations</option>
                  <option value="Actions">Actions</option>
                  <option value="Compliance">Compliance</option>
                  <option value="Contractor Management">Contractor Management</option>
                  <option value="Documents">Documents</option>
                  <option value="Helpdesk">Helpdesk</option>
                  <option value="Notifications">Notifications</option>
                  <option value="OnSite">OnSite</option>
                  <option value="Pronto Forms">Pronto Forms</option>
                  <option value="Properties">Properties</option>
                  <option value="Reporting">Reporting</option>
                  <option value="System Config">System Config</option>
                  <option value="Tasks">Tasks</option>
                  <option value="Templating">Templating</option>
                  <option value="Users">Users</option>
                  <option value="Website Configuration">Website Configuration</option>
                  <option value="Other">Other</option>
                  <option value="Auto Closed">Auto Closed</option>
                  <option value="Blazer">Blazer</option>
                </select>
              </div>
              {implJiraForm.category && (
                <div className="form-row">
                  <label className="form-label">Ticket Type <span style={{color:"var(--red)"}}>*</span></label>
                  <select className="form-select" style={{ width: "100%", background: "#fff", color: "#1a1a1a" }}
                    value={implJiraForm.ticketType}
                    onChange={e => setImplJiraForm(f => ({ ...f, ticketType: e.target.value }))}>
                    <option value="">- Select type -</option>
                    {implJiraForm.category === "Implementations" && <><option value="General">General</option><option value="New Project">New Project</option><option value="Module Setup">Module Setup</option><option value="Go-Live">Go-Live</option><option value="Issue">Issue</option></>}
                    {implJiraForm.category === "Actions" && <><option value="Add Actions">Add Actions</option><option value="Amend Actions">Amend Actions</option><option value="Close Actions">Close Actions</option></>}
                    {implJiraForm.category === "Compliance" && <><option value="Add Module">Add Module</option><option value="Amend Module">Amend Module</option><option value="Remove Module">Remove Module</option><option value="Amend Previous Data">Amend Previous Data</option><option value="Completion of Inspection">Completion of Inspection</option><option value="Support Request">Support Request</option><option value="Suspend Compliance">Suspend Compliance</option><option value="Update Event Dates">Update Event Dates</option></>}
                    {implJiraForm.category === "Contractor Management" && <><option value="Add">Add</option><option value="Amend">Amend</option><option value="Remove">Remove</option></>}
                    {implJiraForm.category === "Documents" && <><option value="Add Document">Add Document</option><option value="Change Document">Change Document</option><option value="Document Upload">Document Upload</option><option value="Failed Upload">Failed Upload</option><option value="Remove Document">Remove Document</option><option value="Remove Attachment">Remove Attachment</option></>}
                    {implJiraForm.category === "Helpdesk" && <><option value="Job Type Amendment">Job Type Amendment</option><option value="Invalid Document">Invalid Document</option><option value="Revert Job">Revert Job</option><option value="General Issue">General Issue</option></>}
                    {implJiraForm.category === "Notifications" && <><option value="Add Notification">Add Notification</option><option value="Amend Notification">Amend Notification</option><option value="Remove Notification">Remove Notification</option></>}
                    {implJiraForm.category === "OnSite" && <><option value="Add User">Add User</option><option value="Amend Form">Amend Form</option><option value="Amend Primary/Secondary User">Amend Primary/Secondary User</option><option value="Can't Capture Media">Can't Capture Media</option><option value="Can't Login">Can't Login</option><option value="Can't See Forms">Can't See Forms</option><option value="Can't Submit Form">Can't Submit Form</option><option value="Create Form">Create Form</option><option value="DataSet amendment">DataSet amendment</option><option value="Dispatch Form">Dispatch Form</option><option value="New Development Request">New Development Request</option><option value="Remove Form">Remove Form</option><option value="Software Bug">Software Bug</option><option value="User Management">User Management</option></>}
                    {implJiraForm.category === "Pronto Forms" && <><option value="Can't Login">Can't Login</option><option value="Can't Capture Media">Can't Capture Media</option><option value="Can't See Forms">Can't See Forms</option><option value="Can't Submit Form">Can't Submit Form</option><option value="Add User">Add User</option><option value="Create Form">Create Form</option><option value="Amend Form">Amend Form</option><option value="User Management">User Management</option><option value="Amend Primary/Secondary User">Amend Primary/Secondary User</option><option value="Software Bug">Software Bug</option><option value="New Development Request">New Development Request</option><option value="Remove Form">Remove Form</option><option value="Dispatch Form">Dispatch Form</option></>}
                    {implJiraForm.category === "Properties" && <><option value="Add Property">Add Property</option><option value="Amend Property">Amend Property</option><option value="Deactivate Property">Deactivate Property</option></>}
                    {implJiraForm.category === "Reporting" && <><option value="Add User to Report">Add User to Report</option><option value="Amend Report">Amend Report</option><option value="Create Report">Create Report</option><option value="My Dashboard Error">My Dashboard Error</option><option value="Remove User from Report">Remove User from Report</option><option value="Report Error">Report Error</option><option value="Subscription Reporter Errors">Subscription Reporter Errors</option></>}
                    {implJiraForm.category === "System Config" && <><option value="System Issue">System Issue</option><option value="Other">Other</option></>}
                    {implJiraForm.category === "Tasks" && <><option value="Add Task">Add Task</option><option value="Amend Task">Amend Task</option><option value="Remove Task">Remove Task</option><option value="New Development Request">New Development Request</option></>}
                    {implJiraForm.category === "Templating" && <><option value="New Template">New Template</option><option value="Amend Template">Amend Template</option><option value="Deactivate Template">Deactivate Template</option></>}
                    {implJiraForm.category === "Users" && <><option value="Add User">Add User</option><option value="Amend User">Amend User</option><option value="Remove User">Remove User</option><option value="User Login Issue">User Login Issue</option><option value="User Config Issue">User Config Issue</option><option value="Add Contractor">Add Contractor</option><option value="Amend Contractor">Amend Contractor</option><option value="Remove Contractor">Remove Contractor</option></>}
                    {implJiraForm.category === "Website Configuration" && <option value="Other">Other</option>}
                    {implJiraForm.category === "Other" && <option value="Other">Other</option>}
                    {implJiraForm.category === "Auto Closed" && <><option value="Non-Support Request">Non-Support Request</option><option value="Other">Other</option></>}
                    {implJiraForm.category === "Blazer" && <option value="Blazer">Blazer</option>}
                  </select>
                </div>
              )}
              <div className="form-row">
                <label className="form-label">Attachments (optional)</label>
                <input ref={implFileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" multiple style={{ display: "none" }}
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) setImplJiraAttachments(prev => [...prev, ...files]);
                    if (implFileInputRef.current) implFileInputRef.current.value = "";
                  }} />
                <button type="button" className="btn" style={{ fontSize: 11, padding: "7px 12px", border: "1px dashed var(--border)", color: "var(--text-muted)", width: "fit-content" }}
                  onClick={() => implFileInputRef.current && implFileInputRef.current.click()}>
                  📎 Add file (PDF, Word, Excel, images)
                </button>
                {implJiraAttachments.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 6 }}>
                    {implJiraAttachments.map((f, i) => {
                      const pct = implUploadProgress[f.name];
                      const uploading = pct !== undefined && pct < 100;
                      const done = pct === 100;
                      return (
                        <div key={i} style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 4, padding: "5px 8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                            <span style={{ color: done ? "var(--green)" : "var(--text)", flex: 1 }}>
                              {done ? "✓" : uploading ? "⏫" : "📄"} {f.name}
                              <span style={{ color: "var(--text-muted)", fontSize: 10, marginLeft: 6 }}>({(f.size/1024).toFixed(0)}KB)</span>
                            </span>
                            {uploading && <span style={{ fontSize: 10, color: "#4c9aff" }}>{pct}%</span>}
                            {!uploading && !done && (
                              <button onClick={() => setImplJiraAttachments(prev => prev.filter((_, j) => j !== i))}
                                style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 12, padding: 0 }}>{"✕"}</button>
                            )}
                          </div>
                          {(uploading || done) && (
                            <div style={{ height: 4, background: "var(--border)", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: (pct || 0) + "%", background: done ? "var(--green)" : "#4c9aff", borderRadius: 2, transition: "width 0.2s" }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => { setShowImplJira(false); setImplJiraAttachments([]); setImplUploadProgress({}); }}>Cancel</button>
              <button className="btn primary" style={{ background: "#0052cc", borderColor: "#0052cc" }}
                onClick={handleSubmitImplJira}
                disabled={creatingImplJira || !implJiraForm.summary.trim() || !implJiraForm.description.trim() || !implJiraForm.affectedClient || !implJiraForm.category || !implJiraForm.ticketType}>
                {creatingImplJira ? "Creating…" : "Create in Jira" + (implJiraAttachments.length > 0 ? " + " + implJiraAttachments.length + " file(s)" : "")}
              </button>
            </div>
          </div>
        </div>
      )}

      {popoutTask && (
        <PopoutModal
          task={tasks.find(t => t.id === popoutTask.id) || popoutTask}
          onClose={() => { setPopoutTask(null); setSelected(null); }}
          onComplete={() => { const t = tasks.find(x => x.id === popoutTask.id); if (t) toggleDone(t); }}
          onCompleteFlagged={() => { const t = tasks.find(x => x.id === popoutTask.id); if (t) completeFlaggedTask(t); }}
          onCreateOutlook={() => { const t = tasks.find(x => x.id === popoutTask.id); if (t) createOutlookFromJira(t); }}
          onSaveNote={saveNote}
          onSaveDue={saveDue}
          onTransition={handleJiraTransition}
          onStatusChange={handleStatusChange}
          onUpdateTask={updateTask}
          onDelete={deleteTask}
          onPromoteSubtask={promoteSubtask}
          jiraAssignees={allTeamMembers}
          config={config}
          allManualTasks={tasks.filter(t => t.source === "manual")}
          onCreateInJira={(task) => {
            setNewJira({
              summary: task.title || "",
              description: task.description || "",
              affectedClient: (task.tags?.find(t => t !== "client-hub") || task.title?.match(/\[([^\]]+)\]/)?.[1] || ""),
              category: "",
              ticketType: "",
            });
            setShowNewJira(true);
            setPopoutTask(null);
          }}
        />
      )}
    </>
  );
}
